import { appNow } from '../utils/appClock'
import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, memo } from 'react'
import { createPortal, flushSync } from 'react-dom'
import { useTradingContext } from './TradingContext'
import { TPO_LETTERS, tpoSessionKey, tpoNiceStep, tpoBuildProfile } from '../utils/tpoProfile'
const TPO_HIDE_KEYS = new Set(['solcheddar:maConfigs','solcheddar:showSmoketrail_v2','solcheddar:showVolumeProfile','solcheddar:showValueArea','solcheddar:showOptVol','solcheddar:showCvd','solcheddar:showDvp','solcheddar:showBodyVol','solcheddar:showVolumeBars','solcheddar:showFooterDelta','solcheddar:showPoc','solcheddar:showAbsorption','solcheddar:showArd','solcheddar:showAtrStop','solcheddar:showChurnZones','solcheddar:showProtraction','solcheddar:showSwings','solcheddar:showBotTrades','solcheddar:showBotOverlay','solcheddar:showCellFlip','cheddar_show_bookheat','cheddar_show_cbheat'])
import { useTradeDefaults } from './TradeDefaultsContext'
import OrderModal from './OrderModal'
import ChartContextMenu from './ChartContextMenu'
import { downloadSwapsCsv } from '../utils/swapExport'
import AVWAPSettingsMenu from './AVWAPSettingsMenu'
import ColorPicker from './ColorPicker'
import ATRTrailSettingsMenu from './ATRTrailSettingsMenu'
import MovingAverageSettingsMenu from './MovingAverageSettingsMenu'
import { MA_DEFAULTS, normalizeMaConfigs, updateMaCache, maValueAt } from '../utils/movingAverage'
import DVPSettingsMenu from './DVPSettingsMenu'
import CVDSettingsMenu, { CVD_DEFAULTS } from './CVDSettingsMenu'
import RangeSliderControl from './RangeSliderControl'
import ValueAreaSettingsMenu from './ValueAreaSettingsMenu'
import AbsorptionSettingsMenu from './AbsorptionSettingsMenu'
import AbsorbersPanel from './AbsorbersPanel'
import { fetchHoldPct } from '../utils/walletSources'
import { dbgOn, dbg } from '../utils/debugFlag'
import VolumeProfileSettingsMenu from './VolumeProfileSettingsMenu'
import VolumeSettingsMenu, { VOL_DEFAULTS, VOL_PCT_MIN, VOL_PCT_MAX } from './VolumeSettingsMenu'
import ArdSettingsMenu from './ArdSettingsMenu'
import SRLineSettingsMenu, { useTrendlines, drawTrendlines, indexOfX, timeOfIndexF, PHONE_HANDLE_HALF, clampHandleX, distToSegment } from './SRLineSettingsMenu'
import BotSettingsMenu, { BotBadge } from './BotSettingsMenu'
import Toast from './Toast'
import GateLoader from './GateLoader'
import usePriceAlerts from './hooks/usePriceAlerts'
import useVolumeAlerts, { fmtVol, roundVol } from './hooks/useVolumeAlerts'
import { getChartWallet, chartWalletVersion, onChartWalletChange, selectChartWallet,
         walletIdFor, buildWalletOverlay, getWalletStats } from './hooks/useChartData'
import useTokenDescriptions from './hooks/useTokenDescriptions'
import { useIsPhone, isPhoneDevice } from './hooks/useIsPhone'
import { positionStore } from './hooks/positionStore'
import * as positionSync from './hooks/positionSync'
import ChartMenu, { MenuSection, MenuRow, MenuItem, MenuItemRow, MenuToggle, MenuNumber, MenuSwatch, MenuButton, MenuDivider } from './menu/ChartMenu'
import { PriceAlertMenu, PriceAlertNotifications } from './PriceAlertUI'
import  useChartInteraction, { useStableCallback } from './hooks/useChartInteraction'
import { fetchTokenV2Data, normalizeTokenImage } from '../utils/jupiter'
import {
  IMBALANCE_RATIO, ROW_HEIGHT, RIGHT_PADDING,
  MIN_VIEW_WINDOW, MAX_VIEW_WINDOW, DEFAULT_VIEW_WINDOW,
  footprintWinForPlotW,
  MCAP_BUCKET_STEPS, MCAP_RATIO_STEPS, snapRatio, logStepIdx, logStepEdge, TOAST_DURATION, TOAST_ERROR_DURATION,
  AUTO_PADDING, FUTURE_PADDING, FIXED_OVERLAY_WIDTH,
  formatMcap, formatPrice, formatCompact, formatCellVol, formatCellVolExact, getTextSprite, getTextSpriteAtlas,
  atlasBucket, atlasText, atlasCssWidth, CELL_ATLAS_PX, CELL_ATLAS_FONT, CELL_ATLAS_FONT_HEAVY, CELL_ATLAS_PX_LARGE, CELL_ATLAS_FONT_LARGE,
  snapBucket, findBucketIndex, ceilBucket,
  mcapToY as mcapToYUtil, yToMcap as yToMcapUtil,
  latticeRowIndex,
  cmeInfo, stkInfo,
} from '../utils/chartUtils'
const OPTVOL_BASE = import.meta.env.VITE_HTTP_PROXY_BASE || 'https://history.cheddarterminal.app'   // optvol-20261103
import { detectSwings, detectStructure, computeAdaptiveLookback, computeATRTrailingStopLive } from '../utils/swingUtils'
import { registerChartBridge, makeChartBridge } from '../utils/grokDraw'
import { computeChurnZones, drawChurnZones, normalizeChurnCfg, buildChurnBgMap } from '../utils/churnZones'
import ChurnZonesSettingsMenu from './ChurnZonesSettingsMenu'
import { cmePosition, cmeUnrealized, cmeFlatten, CME_PAPER_EVENT } from '../utils/cmePaper'   // cmetags-20261101
import { normalizeProtractionCfg, protractionFor, drawProtraction } from '../utils/protraction'   // protraction-20261018
import ProtractionSettingsMenu from './ProtractionSettingsMenu'
import { computeAbsorbZones, hexA } from '../utils/absorbZones'   // absorb-zones-20261019
import { createHlBook, BOOK_WS_URL, HEAT_RING_MAX } from '../utils/hlBookFeed'
import { publishTank } from '../utils/meteoraTank'
import { scanCandlesMcapRange, stretchRangeForHighlight, bucketRowIndex, buildGroupedCells, headerTimeLabel } from '../utils/chartGeometry'
import {
  PACK_BUFFER_CANDLES,
  packCandle,
  buildDisplay,
  forEachLevel,
  candleTotalVolume,
  candleDelta,
  candleMaxLevelVolume,
  stepIdxOf,
  displayIsCurrent,
} from '../utils/candleHelpers'
import { setCtxFont } from '../utils/canvasFont'
import { FlipZoneDetector, drawFlipZones } from './FlipZoneDetector'
import { OrderflowBot, drawBotOverlay } from './OrderflowBot'
import BundleDetector from './BundleDetector'
import RemoteBot from './RemoteBot'
import ArdDetector from './ArdDetector'
import { openBotJournalReport, exportBotJournal, reportR } from './botJournalReport'
import { solPositionPnl, futuresPositionPnl } from '../utils/pnlUtils'
import useMeteoraBinLiquidity from './hooks/useMeteoraBinLiquidity'
import useCumulativeDelta from './hooks/useCumulativeDelta'
import CumulativeDeltaSubgraph from './CumulativeDeltaSubgraph'
import BodyVolSubgraph from './BodyVolSubgraph'
import DeltaVolumePro, { DVP_DEFAULTS } from './DeltaVolumePro'
import OrderflowTrail from './OrderflowTrail'
import LiquidityHeatLayer from './LiquidityHeatLayer'
import { buildSideLUTs, hexToRgb } from './themes'
import { FOLLOW_DAMP_K, FOLLOW_OFFSET_FRAC, FOLLOW_EDGE_FRAC, FP_CELL_LOD_MIN_PX, TF_OPTIONS } from './chartConstants'
import { requestFrame, cancelFrame, FRAME_INPUT, FRAME_ANIM, FRAME_DRAW, FRAME_LAYER } from '../utils/frameDriver'
import { registerDataHold } from '../utils/dataCommitGate'
import { xWalls, clampEndIdx, resolveEndIdx, startIdxFor, isLiveInView, rescaleFracPx, wheelAnchorIndex, placeWheelAnchor } from '../chart/machines/xWindow'
import ChartFooter from './ChartFooter'
import { isFootprintCellGLEnabled, createFootprintCellGL } from '../utils/footprintCellGL'
import { candleTier, aggregateCandleColumns, snapY } from '../utils/candleLod'
import { resolveLivePriceForY } from '../utils/chartProjector'
import useYCamera, { computeRangeBuffer } from '../chart/machines/useYCamera'
import { loadXView, loadXWin, loadXGap, saveXView, clearXView } from '../chart/machines/xZoomStore'
import { yfit, yfitOn } from '../chart/machines/yFitTrace'   // TEMP: localStorage.debugYFit='1' | '<chartId>'
import { createProjection } from '../chart/machines/projection'
import { placeCrosshairEl, placeCrosshairs, CROSSHAIR_DOM_STYLE } from '../utils/crosshair'
import { getVolBars, setVolBars, onVolBarsChange, VOL_SIZES, CME_VOL_SIZES, CME_VOL_DEFAULT } from '../utils/volBars'   // vol-bars-20261101
import { paperPosition, paperOpenPnl, paperSummary, paperMarkers, isPaperActive, PAPER_EVENT } from '../utils/paperTrade'   // paper-20261027

// AUTO_PADDING, FUTURE_PADDING, FIXED_OVERLAY_WIDTH live in chartUtils.js
// so this file and CumulativeDeltaSubgraph.jsx import the same source of
// truth. Previously they were duplicated as local consts in both files
// with "must match" comments, which silently drifted.

// FOLLOW_DAMP_K / FOLLOW_OFFSET_FRAC moved to ./chartConstants (imported above).

// ── Minimum rendered row height, in CSS px ──
// SINGLE SOURCE. This value had been written twice with two different
// numbers, and the gap between them was a bug you could scroll into:
//   - the grid memo capped row COUNT at containerHeight/3, i.e. "rows may
//     be as thin as 3px"
//   - dynamicRowHeight floored the rendered height at 8px
// So for any grid needing rows between 3px and 8px, the memo built a count
// the renderer then refused to honour. Row height got floored UP, canvas
// height (= length * dynamicRowHeight) overflowed the container, and since
// .footprint-body-scroll is overflow-y:auto with the scrollbar hidden AND
// the auto-scroll effect forces body.scrollTop = 0 on every grid change,
// the view pinned to the TOP of the price range with everything below
// silently clipped off the bottom - no scrollbar, no cue. That is the
// "scrolling back into a big drop pushes price below view" report: the
// deeper the drop, the more rows, the further below the fold price sat.
// Both sites now derive from this constant, so the renderer can always
// honour what the memo asks for and canvas height stays ~containerHeight.
const MIN_ROW_PX = 8

// Bitmap headroom below the viewport. The canvas is translated UP by
// residualPx (up to one row), so a viewport-tall surface leaves the bottom
// residualPx with no bitmap behind it — a hard cut through cells and VP.
// FIXED PX, never derived from RH: the surface must change only on a real
// window resize, or it becomes a second sizer and the realloc-clear
// tearing comes back.
const SURFACE_PAD_PX = 200   // diagnostic value; drop to ~64 once confirmed

// ============================================
// computeValueArea — POC + Value Area (VAH/VAL) from a volume-by-price profile.
// profile: { [mcapBucketKey]: { buy, sell, total } } (the chart's volumeProfile).
// Standard method: start at the Point of Control (highest-volume level) and
// expand outward, each step adding whichever adjacent level carries more
// volume, until the captured volume reaches vaPct% of the total. Returns
// { poc, vah, val } in mcap units, or null when there's nothing to profile.
// ============================================
// ─── Unified recent views (sol tokens + futures pairs) ───
// One localStorage list shared by every chart slot, replacing the sol-only
// data.tokenHistory as the dropdown's source. Entries:
//   { kind:'sol', address, name }  |  { kind:'fut', coin, name }
// Recording happens chart-side (effect below) so BOTH modes enter recents
// the moment they're viewed; selection flows through onHistorySelect —
// sol rows keep the legacy {address,name} shape byte-compatible, fut rows
// add kind/coin and need the small App-side branch to actually switch.
// L2 book heatmap service (hl_book_capture.py, droplet :8767) — same
// host-resolution pattern as the main WS on :8765.
// Env-or-hostname, matching the main socket's own shape in chartUtils.
// BOOK_WS_URL / HEAT_RING_MAX moved to utils/hlBookFeed (the data slice's
// one owner); imported above because the CB effect still uses both.

const RECENT_VIEWS_KEY = 'cheddar_recent_views'
const RECENT_VIEWS_MAX = 12
// hold-pct-big-20261016: one bold '% hold' per churn/absorption zone.
// L = [text, x, y, alpha] with x/y already in DEVICE px (recorded when the
// zone was drawn), drawn with an identity transform so the camera X scale
// can't stretch it. sy = device px per CSS px. Centered on the zone.
// zindex-20261102: chart layering. Footprint = index 0. An item's `index`
// (from its settings menu): < 0 paints on the BELOW layer (under the cells),
// 0 / missing = main canvas (unchanged), > 0 on the ABOVE layer (over the
// cells AND the forming candle). Layers mirror the main canvas size, CSS and
// transform; prepared lazily, only on full passes that actually use them.
function zLayerPrep(L, canvas, ctx) {
  if (L.width !== canvas.width) L.width = canvas.width
  if (L.height !== canvas.height) L.height = canvas.height
  if (L.style.width !== canvas.style.width) L.style.width = canvas.style.width
  if (L.style.height !== canvas.style.height) L.style.height = canvas.style.height
  if (L.style.transform !== canvas.style.transform) L.style.transform = canvas.style.transform
  const c = L.getContext('2d')
  c.setTransform(1, 0, 0, 1, 0, 0)
  c.clearRect(0, 0, L.width, L.height)
  c.setTransform(ctx.getTransform())
  return c
}
function zLayerClear(L) {
  if (!L || !L.width || !L.height) return
  const c = L.getContext('2d')
  c.setTransform(1, 0, 0, 1, 0, 0)
  c.clearRect(0, 0, L.width, L.height)
}

function drawZoneHoldLabels(ctx, L, sy) {
  if (!L.length) return
  ctx.save()
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.globalAlpha = 1
  ctx.font = `800 ${Math.round(14 * sy)}px "JetBrains Mono", monospace`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.lineWidth = Math.max(2, 3 * sy)
  ctx.lineJoin = 'round'
  ctx.strokeStyle = 'rgba(0,0,0,0.9)'
  ctx.fillStyle = '#ffffff'   // hold-label-style-20261016: white
  for (let i = 0; i < L.length; i += 4) {
    ctx.globalAlpha = L[i + 3]   // hold-hover-alpha-20261016: 0.5 while hovered
    ctx.strokeText(L[i], L[i + 1], L[i + 2])
    ctx.fillText(L[i], L[i + 1], L[i + 2])
  }
  ctx.restore()
}

// abs-liq-20261017 (v2 absorb-liq2): liquidity reaction label beside each
// absorption cell, e.g. 'Liq ↑6s'. Same idea as the bot badge's liq number
// (OrderflowBot _updateLiqReaction): how many seconds until price leaves the
// level by a clear amount. All tuning lives in ABS_LIQ.
//   row     the cell's PAINTED row [m, m+W): m = the level forEachLevel gave
//           the absorption memo (display lattice when built), W = that row's
//           width (display bucket / log step, else the native bucket)
//   level   row centre L = m + W/2
//   t0      the last tape print inside the row during the cell's candle
//           (data.rawTradesRef, ~3h buffer; older cells: no label). No print
//           there (lattice mismatch) -> the candle's close time.
//   thr     atrMult x ATR(atrLen) of the chart bars ending at the cell's candle;
//           fewer than 2 bars -> pctFallback x L. Never less than one row.
//   result  the FIRST side to reach L +/- thr after t0 wins:
//           up -> 'Liq ↑Ns' LEFT of the cell, down -> 'Liq ↓Ns' RIGHT of it
//   weak    neither side reached within the window -> no label (final)
//   window  max(windowSec, windowBars x timeframe) after t0
//   pending window still open on the tape (live / newest cell) -> no label yet
// v1 bug (no labels ever): "choppy" fired on any print one NATIVE bucket
// either side of the level - inside the painted row itself - so ~90-100% of
// cells were cached as choppy; plus a 2-bar window was too short on big bars.
const ABS_LIQ = { atrLen: 5, atrMult: 0.5, pctFallback: 0.01, windowSec: 120, windowBars: 3, minRowPx: 6 }

// -> { dir: 'up'|'down', sec } | null (final: weak / no data at all)
//    | undefined (not resolvable yet: tape doesn't cover it / window open)
function absLiqReaction(buf, candles, idx, m, tfSec) {
  const c = candles[idx]
  if (!buf || !buf.length || !c || !(tfSec > 0) || !Number.isFinite(m)) return undefined
  const ivMs = tfSec * 1000
  const cs = c.startTime, ce = cs + ivMs
  if (!(buf.oldestTime() <= cs)) return undefined
  const live = buf.newestTime() < ce   // absorb-liq7: forming candle -> only a hit counts, else pending
  // painted row width
  let W = 0
  if (c._display && c._displayRatio > 0) W = m * (c._displayRatio - 1)
  else if (c._display && c._displayBucket > 0) W = c._displayBucket
  if (!(W > 0)) W = c._nativeBucket > 0 ? c._nativeBucket : 0
  if (!(W > 0)) {
    const ks = []
    forEachLevel(c, (lm) => { ks.push(lm) })
    ks.sort((x, y) => x - y)
    let g = Infinity
    for (let k = 1; k < ks.length; k++) { const d = ks[k] - ks[k - 1]; if (d > 0 && d < g) g = d }
    W = Number.isFinite(g) ? g : 0
  }
  const L = m + W / 2
  // ATR(atrLen) of the bars ending at the cell's candle
  let trSum = 0, trN = 0
  for (let j = Math.max(1, idx - ABS_LIQ.atrLen + 1); j <= idx; j++) {
    const b = candles[j], pb = candles[j - 1]
    if (!b || !pb) continue
    const h = b.high_mcap, l = b.low_mcap, pc = pb.close_mcap
    if (!Number.isFinite(h) || !Number.isFinite(l) || !(h >= l)) continue
    trSum += Number.isFinite(pc) ? Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc)) : h - l
    trN++
  }
  let thr = trN >= 2 ? ABS_LIQ.atrMult * trSum / trN : ABS_LIQ.pctFallback * L
  if (!(thr > W)) thr = W
  if (!(thr > 0)) return null
  const win = Math.max(ABS_LIQ.windowSec * 1000, ABS_LIQ.windowBars * ivMs)
  // first index with time >= candle start (buffer is chronological)
  let lo = 0, hi = buf.length
  while (lo < hi) { const md = (lo + hi) >> 1; if (buf.timeAt(md) < cs) lo = md + 1; else hi = md }
  let t0 = -1, i0 = -1, iEnd = lo
  for (let i = lo; i < buf.length; i++) {
    const t = buf.timeAt(i)
    if (t >= ce) break
    iEnd = i
    const px = buf.mcapAt(i)
    if (px >= m && px < m + W) { t0 = t; i0 = i }
  }
  if (i0 < 0) { if (live) return undefined; t0 = ce; i0 = iEnd }   // no print in the row: start at candle close
  for (let i = i0 + 1; i < buf.length; i++) {
    const t = buf.timeAt(i)
    if (t < t0) continue
    if (t - t0 > win) return null
    const px = buf.mcapAt(i)
    if (!(px > 0)) continue
    if (px >= L + thr) return { dir: 'up', sec: (t - t0) / 1000, at: t }
    if (px <= L - thr) return { dir: 'down', sec: (t - t0) / 1000, at: t }
  }
  return !live && buf.newestTime() - t0 > win ? null : undefined
}

// L = [text, x, y, alpha, color, align, colWidth] in DEVICE px (identity
// transform, like drawZoneHoldLabels). px = cell-number font size in CSS px
// (floored at 9). absorb-liq5: drawn at full size, never squeezed; a label
// wider than its candle column is skipped instead (zoomed far out).
function drawAbsLiqLabels(ctx, L, sy, px) {
  if (!L.length) return
  ctx.save()
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.font = `700 ${Math.round(Math.max(9, px) * sy)}px "JetBrains Mono", monospace`
  ctx.textBaseline = 'middle'
  ctx.lineWidth = Math.max(2.5, 3 * sy)
  ctx.lineJoin = 'round'
  ctx.strokeStyle = 'rgba(0,0,0,0.9)'
  for (let i = 0; i < L.length; i += 7) {
    ctx.globalAlpha = L[i + 3]
    ctx.textAlign = L[i + 5]
    ctx.fillStyle = L[i + 4]
    if (ctx.measureText(L[i]).width > L[i + 6]) continue   // column too narrow: hide
    ctx.strokeText(L[i], L[i + 1], L[i + 2])
    ctx.fillText(L[i], L[i + 1], L[i + 2])
  }
  ctx.restore()
}

function readRecentViews() {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_VIEWS_KEY) || '[]')
    return Array.isArray(v) ? v : []
  } catch { return [] }
}
function pushRecentView(entry) {
  try {
    const id = entry.kind === 'fut' ? `fut:${entry.coin}` : entry.address
    if (!id) return
    const rest = readRecentViews().filter(e =>
      (e.kind === 'fut' ? `fut:${e.coin}` : e.address) !== id)
    localStorage.setItem(RECENT_VIEWS_KEY, JSON.stringify([entry, ...rest].slice(0, RECENT_VIEWS_MAX)))
  } catch {}
}

// ─── (wick) Wick colour — ONE helper for every wick draw ───
// Normal spacing: candle.wickCandle (candle look) / candle.wickFootprint
// (slim overlay over cells), then the caller's fallback. Tight spacing
// (visual candle spacing < WICK_TIGHT_CW CSS px — the 3px black halo alone is
// wider than the pitch there): every candle is ONE opaque stick in
// candle.wickTight, no halo, no body. That stops coloured bodies and grey
// cores interleaving into pink/purple stripes when zoomed far out.
// All three keys are per-theme (themes.js) and user-overridable through
// resolveTheme — localStorage 'solcheddar_theme_overrides' →
// { [themeId]: { 'candle.wickTight': '#rrggbb' } }. A picker plugs in by
// adding the path to THEME_COLOR_GROUPS; nothing here changes.
const WICK_TIGHT_CW = 3
// TF SWITCH WINDOW (see tfWinRef). Opens when the new-interval candles are
// in hand and anchored; lasts TF_WIN_MS, pushed out by TF_WIN_EXTEND_MS on
// every structural publish (reconcile, slabs) up to TF_WIN_MAX_MS.
const TF_WIN_MS = 3000
const TF_WIN_EXTEND_MS = 800
const TF_WIN_MAX_MS = 6000
function wickColorOf(TH, tight, candleStyled, fallback) {
  const c = (TH && TH.candle) || {}
  if (tight) return c.wickTight || c.wickCandle || '#ffffff'
  return (candleStyled ? c.wickCandle : c.wickFootprint) || fallback || c.wickCandle || '#ffffff'
}

function computeValueArea(profile, bucket, vaPct, ratio) {
  // profile: Map<stepIdx, [buy, sell, total]> (integer-keyed — see the
  // volumeProfile memo). (va) A step is a LOG index when the profile carries
  // a ratio — step * bucket was not a price there (the VA drew far off the
  // data) — so steps map back exactly as the VP draw does: logStepEdge in
  // log, step * bucket in linear.
  if (!ratio && !(bucket > 0)) return null
  const edge = ratio ? (st) => logStepEdge(st, ratio) : (st) => st * bucket
  const rows = []
  for (const [step, e] of profile) {
    const v = e[2] || 0
    if (v > 0) rows.push({ step, vol: v })
  }
  if (rows.length === 0) return null
  rows.sort((a, b) => a.step - b.step)
  let total = 0, pocIdx = 0
  for (let i = 0; i < rows.length; i++) {
    total += rows[i].vol
    if (rows[i].vol > rows[pocIdx].vol) pocIdx = i
  }
  if (total <= 0) return null
  const target = total * (Math.max(1, Math.min(100, vaPct)) / 100)
  let running = rows[pocIdx].vol, lo = pocIdx, hi = pocIdx
  while (running < target && (lo > 0 || hi < rows.length - 1)) {
    const volDown = lo > 0 ? rows[lo - 1].vol : -1
    const volUp   = hi < rows.length - 1 ? rows[hi + 1].vol : -1
    if (volUp >= volDown) { hi++; running += rows[hi].vol }
    else { lo--; running += rows[lo].vol }
  }
  // (va) Row-edge mapping: a level key (edge(step)) is its row's BOTTOM edge
  // on screen — row i covers [mcaps[i], mcaps[i]+B) and yOf(mcaps[i]) is
  // row i's bottom. POC = centre of its row (step + 0.5); VAL = bottom of
  // the lowest row (step); VAH = top of the highest row (step + 1).
  return { poc: edge(rows[pocIdx].step + 0.5), val: edge(rows[lo].step), vah: edge(rows[hi].step + 1) }
}

// ============================================
// useChartScroll — scroll/pan/drag (tightly coupled to DOM refs)
// ============================================
function useChartScroll({
  setAutoScrollEnabled, autoScrollRef, chartData,
  visibleSortedMcaps, visibleSortedMcapsRef, dynamicRowHeight,
  containerHeight, bodyScrollRef, yAxisRef, viewWindowSizeRef,
  containerWidthRef, viewEndIndex, setViewEndIndex, viewEndIndexRef,
  pinchActiveRef,
  crosshairModeRef, crosshairArmedRef,
  drawCanvasRef,
  // (scrollperf) FPCC's coalesced scheduleCanvasDraw, via ref. Optional.
  scheduleDrawRef = null,
  // Slot/xWin frac when provided — one write owner. Local fallback for tests.
  fractionalOffsetPxRef: fractionalOffsetPxRefProp = null,
  // Body-wheel park. The trade/resize latch must not write follow -1 over it.
  wheelParkRef = null,
  // Parent drag flag. Written on pointerdown, before React re-renders, so the
  // grid memo already sees the drag on the first column change. Setting it
  // in the parent render is one memo too late — that memo stamp-fits, then
  // the ease chases the jump.
  isDraggingChartRef = null,
  // (drag-jump) FPCC's wheel-glide freeze: stops an X zoom glide at the
  // position currently on screen. Called on pointerdown before the anchor.
  freezeXGlideRef = null,
  // kickYFollowRef. Seed / settle / abort run from the gesture, never from
  // the parent render: flushSync during render commits a tree the in-progress
  // render then overwrites.
  onDragEdgeRef = null,
  // (yexplore) Body-drag Y: (phase 'start'|'move'|'end', clientY). Called from
  // the REAL pointer only — the X coast never calls it, so Y has no coast.
  onYDragRef = null,
}) {
  const total = chartData.candles.length
  const resolvedEndIndex = resolveEndIdx(viewEndIndex, total)
  const resolvedEndIndexRef = useRef(resolvedEndIndex)
  // (chart2-bounce) Identity of THIS chart's scroll on the window-level
  // 'solcheddar-redraw' event, so the other chart ignores it.
  const redrawSrcRef = useRef(null)
  if (redrawSrcRef.current === null) redrawSrcRef.current = {}
  resolvedEndIndexRef.current = resolvedEndIndex

  // ── bodyScrollTop cache ──────────────────────────────────────────────
  // drawCanvas needs the body's scrollTop on EVERY draw (regionPx = px + scr,
  // the compensation that cancels the region's physical scroll against the
  // pinned canvas). Reading el.scrollTop is a LAYOUT read, and drawCanvas
  // writes transforms to five elements immediately after it — so each draw
  // read layout that the previous draw had dirtied, forcing a synchronous
  // reflow. The Aug 2026 profile attributed both `Recalculate style` and
  // `Layout` to that one line.
  //
  // scrollTop only changes when a scroll happens, so it is cached and the
  // DOM is read only then. Every writer below updates this in the SAME
  // statement as the DOM write, which closes the staleness window entirely:
  // scroll EVENTS are async, so a cache fed only by the listener could be
  // one frame behind a programmatic scroll.
  //
  // It lives here rather than in the main component on purpose. The body
  // element mounts behind the reveal gate, and this file already documents
  // what that costs ("a once-on-mount effect could attach to null and
  // silently never block") — so this reuses the scroll listener that is
  // already proven to attach, instead of adding a second one with the same
  // timing hazard. Main reaches it via `scroll.`, the same way it already
  // reaches isDraggingRef and dragStartRef.
  const bodyScrollTopRef = useRef(0)

  const setScrollTop = useCallback((target) => {
    const body = bodyScrollRef.current
    const yAxis = yAxisRef.current
    if (!body) return
    // Reserve the bottom-pad rows that the memo adds to cover yResidualPx
    // smoothing — programmatic scroll (auto-scroll-to-highlight etc.) must
    // NOT consume them or the gap that the pad exists to hide comes back.
    // User-initiated wheel/drag pan bypasses this helper and works freely.
    const visCount = visibleSortedMcaps.visibleCount ?? visibleSortedMcaps.length
    const padPx = (visibleSortedMcaps.length - visCount) * dynamicRowHeight
    const maxScroll = Math.max(0, body.scrollHeight - body.clientHeight - padPx)
    const clamped = Math.max(0, Math.min(maxScroll, target))
    body.scrollTop = clamped
    bodyScrollTopRef.current = clamped
    if (yAxis) yAxis.scrollTop = clamped
  }, [bodyScrollRef, yAxisRef, visibleSortedMcaps, dynamicRowHeight])

  const scrollRafRef = useRef(0)
  const stopGlideRef = useRef(null)
  const scrollToLatest = useCallback((mode) => {
    // 'latch' is the trade/resize keeper for the init sentinel (-1).
    // A body-wheel park owns end/frac until a real follow request.
    const latch = mode === 'latch'
    if (latch && wheelParkRef?.current) return
    if (!latch && wheelParkRef) wheelParkRef.current = false
    // End the glide first: otherwise the next glide frame pans from the old
    // anchor and hauls the chart straight back off the live edge.
    // 'abort' — X is jumping to the live edge. Ease the Y window off; do not
    // ease it toward the pre-jump span.
    stopGlideRef.current?.('abort')
    if (scrollRafRef.current) cancelFrame(scrollRafRef.current)
    scrollRafRef.current = requestFrame(() => {
      // Wheel notch after this was queued re-parks. Do not snap to the pad.
      if (wheelParkRef?.current) return
      setViewEndIndex(-1)
      scrollRafRef.current = 0
    }, FRAME_INPUT)
  }, [setViewEndIndex, wheelParkRef])

  // Scroll sync
  useEffect(() => {
    const body = bodyScrollRef.current
    if (!body) return
    // Seed on attach: user scroll is not the only way a container arrives
    // non-zero (restored position, browser scroll anchoring), and until the
    // first scroll event the cache would otherwise assert 0.
    bodyScrollTopRef.current = body.scrollTop
    const onScroll = () => {
      // The one place the DOM is read. Everything else — including
      // drawCanvas, once per frame — reads the cache.
      bodyScrollTopRef.current = body.scrollTop
      // (scrollTop assignment on the overflow:visible axis body is a
      // no-op, kept for history; the real sync is regionPx in drawCanvas.)
      if (yAxisRef.current) yAxisRef.current.scrollTop = body.scrollTop
      // Region layers ride px - scrollTop — recompute on every scroll.
      // (scrollperf) Through FPCC's shared, coalesced scheduler when present:
      // a raw rAF here was a SECOND full synchronous drawCanvas on any frame
      // that also had a glide/pan draw queued. Same frame, one pass.
      const sched = scheduleDrawRef?.current
      if (sched) sched()
      else requestAnimationFrame(() => drawCanvasRef.current?.())
    }
    body.addEventListener('scroll', onScroll, { passive: true })
    return () => body.removeEventListener('scroll', onScroll)
  }, [bodyScrollRef, yAxisRef])

  // Auto-scroll Y effects
  useEffect(() => {
    if (!autoScrollRef.current) return
    const body = bodyScrollRef.current
    if (!body) return
    // (scrollperf) Only when not already at 0. Assigning scrollTop forces a
    // synchronous layout even when the value is unchanged, and this runs on
    // every lattice change — every few frames of a Y-wheel glide.
    if (bodyScrollTopRef.current !== 0) {
      body.scrollTop = 0
      if (yAxisRef.current) yAxisRef.current.scrollTop = 0
    }
    bodyScrollTopRef.current = 0
  }, [visibleSortedMcaps, bodyScrollRef, yAxisRef, autoScrollRef])

  // Cached scroller geometry for the auto-scroll effect below — see the
  // note inside it on why these are not read on every tick.
  const autoScrollGeomRef = useRef({ key: '', ch: 0, sh: 0 })
  // Keyed on the ROW, not the price: this effect only uses the row index,
  // and highlightMcap is the raw last print (changes every trade).
  const _asBs = visibleSortedMcaps.length >= 2 ? Math.round((visibleSortedMcaps[0] - visibleSortedMcaps[1]) * 1e8) / 1e8 : 1
  const highlightIdx = findBucketIndex(visibleSortedMcaps, chartData.highlightMcap, _asBs)
  useEffect(() => {
    if (!autoScrollRef.current) return
    const body = bodyScrollRef.current
    if (!body) return
    if (highlightIdx === -1) return
    const rowTop = highlightIdx * dynamicRowHeight
    // GEOMETRY IS CACHED, NOT RE-READ PER TICK. This effect is keyed on
    // chartData.highlightMcap, so it ran on every price update — and
    // clientHeight/scrollHeight are layout reads, so each tick forced a
    // synchronous layout of the whole document. Profiled at 351.7ms of self
    // time in Layout attributed to this line.
    //
    // Neither value can change without one of: a resize (containerHeight,
    // already tracked by the ResizeObserver), a different row count, or a
    // different row height. Key the cache on exactly those three and the
    // read happens when the geometry actually moves instead of 30x a second.
    // Still the REAL DOM values — this defers the read, it doesn't guess it.
    // (scrollperf) EARLY OUT BEFORE ANY LAYOUT READ. This effect re-runs on
    // every lattice / row-height change — i.e. every frame of a Y-wheel glide —
    // and geomKey below changes with dynamicRowHeight, so it read clientHeight
    // + scrollHeight (forced sync layout) and then setScrollTop read them again
    // and wrote scrollTop (another forced layout + a scroll event + a full
    // draw from the scroll listener). This scroller's clientHeight is its own
    // box (containerHeight comes from the same element), so cache it on that
    // alone. If the target clamps to 0 and we are already at 0 — the normal
    // follow case, live row above the 66% line — the original path ends in
    // setScrollTop(0) on a scroller already at 0: a no-op. Skip it.
    {
      const g0 = autoScrollGeomRef.current
      if (g0.chKey !== containerHeight || !(g0.ch > 0)) {
        g0.chKey = containerHeight
        g0.ch = body.clientHeight
      }
      if (rowTop - g0.ch * 0.66 <= 0 && bodyScrollTopRef.current === 0) return
    }
    const geomKey = `${containerHeight}|${visibleSortedMcaps.length}|${dynamicRowHeight}`
    if (autoScrollGeomRef.current.key !== geomKey) {
      autoScrollGeomRef.current = {
        key: geomKey,
        ch: body.clientHeight,
        sh: body.scrollHeight,
        chKey: containerHeight,
      }
    }
    const { ch: containerH, sh: scrollH } = autoScrollGeomRef.current
    const target = rowTop - containerH * 0.66
    setScrollTop(Math.max(0, Math.min(scrollH - containerH, target)))
  }, [visibleSortedMcaps, dynamicRowHeight, highlightIdx, bodyScrollRef, autoScrollRef, setScrollTop, containerHeight])

  // Treadmill: do NOT snap to the live edge on every tick. Forward tracking is
  // handled by the candle-arrival effect (which holds the dragged gap). Only
  // latch to the default rest while still at the init sentinel (-1), i.e.
  // before the user/treadmill has established a concrete offset.
  useEffect(() => {
    if (wheelParkRef?.current) return
    if (autoScrollRef.current && viewEndIndex === -1) scrollToLatest('latch')
  }, [autoScrollRef, chartData.highlightMcap, chartData.tradeCount, scrollToLatest, viewEndIndex, wheelParkRef])

  // Container resize: only re-latch if we're at the init sentinel. A concrete
  // offset is in candle units and survives a pixel resize unchanged, so don't
  // reset it.
  useEffect(() => {
    if (wheelParkRef?.current) return
    if (viewEndIndex === -1) scrollToLatest('latch')
  }, [containerHeight, viewEndIndex, wheelParkRef])

  // Drag
  const [isDragging, setIsDragging] = useState(false)
  // Ref mirror for the treadmill effect: it must know a drag is live WITHOUT
  // re-running on every drag start/stop (its deps stay candle-count only).
  const isDraggingRef = useRef(false)
  const fractionalOffsetPxRefLocal = useRef(0)   // ← Claude fix #2: ref instead of state
  const fractionalOffsetPxRef = fractionalOffsetPxRefProp || fractionalOffsetPxRefLocal

  const dragStartRef = useRef({ x: 0, y: 0, scrollTop: 0, viewEndIndex: 0 })

  // ── X momentum ───────────────────────────────────────────────────────
  // A flick keeps gliding after release and decays to a stop. It runs at
  // FRAME_INPUT on the shared driver, so the position it produces is read by
  // the draw LATER IN THE SAME FRAME — cells, axis, trail, heat and tags all
  // paint from one frame's geometry, exactly as during a drag.
  const GLIDE_START_V  = 0.15   // px/ms to start — below this a release stops
  const GLIDE_STOP_V   = 0.02   // px/ms to end
  const GLIDE_DECAY    = 0.94   // per 16.67ms
  const GLIDE_STALE_MS = 80     // parked finger → no flick
  const velRef   = useRef({ v: 0, x: 0, ts: 0 })
  const glideRef = useRef({ id: 0, v: 0, x: 0, ts: 0 })
  const isGlidingRef = useRef(false)
  const [isGliding, setIsGliding] = useState(false)

  const stopGlide = useStableCallback((reason) => {
    const was = !!(glideRef.current.id || isGlidingRef.current)
    if (glideRef.current.id) { cancelFrame(glideRef.current.id); glideRef.current.id = 0 }
    glideRef.current.v = 0
    if (isGlidingRef.current) { isGlidingRef.current = false; setIsGliding(false) }
    // No reason: pointerdown is catching the coast and will seed a new drag.
    // Settling in between would release the window the seed is about to own.
    // 'end' finishes the Y ease on the window the coast stopped on.
    // 'abort' drops that window immediately — X is about to jump (scroll to
    // live) and easing the pre-jump span would fight it.
    if (!was) return
    if (reason === 'abort') onDragEdgeRef?.current?.({ abort: true })
    else if (reason === 'end') onDragEdgeRef?.current?.({ settle: true })
  })

  const glideStep = useStableCallback(() => {
    const g = glideRef.current
    g.id = 0
    // The anchor is priced at the window size the drag started with. If a
    // pinch or footer zoom changes it mid-glide, the same virtual travel
    // means a different number of candles and the chart teleports — the drag
    // guards this with a rebase; a glide has no finger to rebase to.
    if (pinchActiveRef?.current
        || dragStartRef.current.windowSize !== viewWindowSizeRef.current) {
      stopGlide('end'); return
    }
    const now = performance.now()
    const dt = Math.min(50, Math.max(1, now - g.ts))   // clamp a tab-switch gap
    g.ts = now
    g.x += g.v * dt
    g.v *= Math.pow(GLIDE_DECAY, dt / 16.67)
    const endBefore  = viewEndIndexRef.current
    const fracBefore = fractionalOffsetPxRef.current
    applyPanFromX(g.x)
    // Wall: applyPanFromX clamped and nothing moved. Keep gliding against it
    // and the chart sits still while the timer runs down.
    const pinned = viewEndIndexRef.current === endBefore
      && fractionalOffsetPxRef.current === fracBefore
    if (Math.abs(g.v) < GLIDE_STOP_V || pinned) { stopGlide('end'); return }
    g.id = requestFrame(glideStep, FRAME_INPUT)
  })

  stopGlideRef.current = stopGlide
  useEffect(() => stopGlide, [stopGlide])

  // (drag-jump) TEMP debug: localStorage.debugDrag = '1'.
  const dragDbgOn = () => { try { return localStorage.getItem('debugDrag') === '1' } catch { return false } }
  const dragFirstMoveRef = useRef(false)
  // The end the PAINTER is showing. A body-wheel park may sit past the
  // forward wall (cursor zoom-out with the pointer left of the live edge
  // solves the end far into the future pad); the painters read it unclamped
  // while the park holds, so the drag anchor must too. Clamping it here is
  // what jumped the chart right on the first drag frame — by the whole
  // overshoot, hundreds of candles at a wide zoom.
  const resolveDragEnd = () => resolveEndIdx(viewEndIndexRef.current, chartData.candles.length,
    !(wheelParkRef?.current))

  const handlePointerDown = useStableCallback((e) => {
    if (e.button !== 0) return
    stopGlide()                     // a new touch catches the chart
    velRef.current.v = 0
    velRef.current.x = e.clientX
    velRef.current.ts = performance.now()
    // Touch pinch in progress (or fingers not all lifted yet) — the pinch
    // effect owns the gesture; starting a pan here would fight it.
    if (pinchActiveRef?.current) return
    // Phone crosshair mode: the finger scrubs the crosshair — never pans.
    if (crosshairModeRef?.current || crosshairArmedRef?.current) return
    if (e.target.closest('.y-axis-body')) return
    const _dbg = dragDbgOn()
    const _pre = _dbg ? { end: viewEndIndexRef.current, win: viewWindowSizeRef.current,
      frac: fractionalOffsetPxRef.current, park: !!wheelParkRef?.current } : null
    setIsDragging(true)
    isDraggingRef.current = true
    // Before setState's render. The grid memo reads this ref; if the flag
    // flips only after that memo, the first drag frame stamp-fits.
    if (isDraggingChartRef) isDraggingChartRef.current = true
    // (drag-jump) A wheel zoom glide still easing must stop WHERE IT IS ON
    // SCREEN, before the anchor below is read — otherwise it keeps publishing
    // its cursor-anchored end over the drag, or lands on its target mid-drag.
    const _froze = freezeXGlideRef?.current?.(e.clientX) || null
    onDragEdgeRef?.current?.({ seed: true })
    const container = bodyScrollRef.current
    if (!container) return
    container.setPointerCapture?.(e.pointerId)
    const tot = chartData.candles.length
    // Capture the EFFECTIVE displayed endIdx — including AUTO_PADDING in
    // autoscroll mode. Prefer the IMPERATIVE viewEndIndexRef (footer/pinch
    // land writes end+frac there before React re-renders). Using the React
    // resolvedEndIndex after a phone footer pan armed from a stale -1 /
    // AUTO_PADDING end while the canvas already showed the anchored end —
    // first chart pan jumped.
    // Forward-wall clamp ON — same law as the painters. Leaving this raw let
    // the pan anchor disagree with paint by the overshoot after total shrinks
    // (TF fold / token / prune), which jumped the first drag pixel.
    // (drag-jump) Clamp only when not wheel-parked — see resolveDragEnd.
    const resolvedEnd = resolveDragEnd()
    // Preserve the chart's current sub-pixel position across drag
    // boundaries. The previous drag may have left a non-zero frac in
    // fractionalOffsetPxRef (e.g., 30px of sub-candle offset). The chart
    // is currently drawn with that 30px shift baked in. If we capture
    // x: e.clientX naively, the first pointermove with dx=0 produces
    // rawDelta=0, indexDelta=0, frac=0 — overwriting the 30 with 0 and
    // snapping the chart by -30px. By shifting the start x backward by
    // frac0, the first pointermove with no real movement produces an
    // effective rawDelta of frac0/pxPerCandle, which (since |frac0| <
    // pxPerCandle) gives indexDelta=0 and frac=frac0 — the chart's
    // displayed position is reproduced exactly. Real movement on top
    // of this composes additively as expected.
    const frac0 = fractionalOffsetPxRef.current
    dragStartRef.current = { x: e.clientX - frac0, y: e.clientY, scrollTop: container.scrollTop, viewEndIndex: resolvedEnd, windowSize: viewWindowSizeRef.current }
    dragFirstMoveRef.current = true
    if (_dbg) {
      const { endWall } = xWalls(tot, viewWindowSizeRef.current)
      dbg('[dragdbg] down', { pre: _pre, glide: _froze,
        after: { end: viewEndIndexRef.current, win: viewWindowSizeRef.current, frac: frac0,
          park: !!wheelParkRef?.current },
        anchorEnd: resolvedEnd, endWall, total: tot, clientX: e.clientX })
    }
    onYDragRef?.current?.('start', e.clientY)
  })

  const handlePointerMove = useStableCallback((e) => {
    if (!isDragging) return
    if (pinchActiveRef?.current) return
    // Crosshair mode can arm MID-gesture (long-press while a drag is already
    // live). Checking here — not just at pointerdown — freezes that drag the
    // instant the mode arms, so scrubbing never pans the chart.
    if (crosshairModeRef?.current || crosshairArmedRef?.current) return
    const container = bodyScrollRef.current
    if (!container) return
    // MID-DRAG WINDOW-SIZE REBASE. rawDelta below re-prices the WHOLE
    // accumulated dx at the CURRENT pxPerCandle. If viewWindowSize changes
    // while this pan is live (phone: footer x-axis drag or pinch on the
    // other thumb), the same finger travel suddenly means dx·(W_new/W_old)
    // candles — the chart teleports (hard-left fling on footer-expand +
    // hold-pan). Rebase the anchor at the new scale so motion composes
    // from the current position instead of re-integrating history.
    if (dragStartRef.current.windowSize !== viewWindowSizeRef.current) {
      dragStartRef.current.windowSize = viewWindowSizeRef.current
      dragStartRef.current.x = e.clientX - fractionalOffsetPxRef.current
      // Forward-wall clamp ON — same law as the painters / drag path above.
      dragStartRef.current.viewEndIndex = resolveDragEnd()
    }
    // Velocity for the release glide: px/ms, EMA-smoothed. A long gap means
    // the finger parked, so the sample restarts rather than averaging a
    // stale burst into a flick that never happened.
    {
      const nowMs = performance.now()
      const dtv = nowMs - velRef.current.ts
      if (dtv > 0) {
        const inst = (e.clientX - velRef.current.x) / dtv
        velRef.current.v = dtv > 120 ? inst : velRef.current.v * 0.4 + inst * 0.6
      }
      velRef.current.x = e.clientX
      velRef.current.ts = nowMs
    }
    const _dbgFirst = dragFirstMoveRef.current && dragDbgOn()
    const _endBefore = _dbgFirst ? viewEndIndexRef.current : null
    applyPanFromX(e.clientX)
    if (dragFirstMoveRef.current) {
      dragFirstMoveRef.current = false
      if (_dbgFirst) {
        dbg('[dragdbg] first move', { endBefore: _endBefore, end: viewEndIndexRef.current,
          win: viewWindowSizeRef.current, frac: fractionalOffsetPxRef.current,
          dx: e.clientX - dragStartRef.current.x, anchorEnd: dragStartRef.current.viewEndIndex,
          park: !!wheelParkRef?.current })
      }
    }
    // (yexplore) Y rides the same pointer move, after X. Not in
    // applyPanFromX: the coast replays that with a virtual x and must not
    // move Y.
    onYDragRef?.current?.('move', e.clientY)
  })

  // Pan to a pointer x against the drag anchor. The drag calls it with the
  // real pointer; the glide calls it with a virtual one. Walls, the
  // floor-not-trunc correction and the frac contract live here ONCE — a
  // second integrator for position is how the halves of this chart drift.
  const applyPanFromX = useStableCallback((clientX) => {
    const dx = clientX - dragStartRef.current.x
    // X only. Body-drag Y (explore) is onYDragRef, from handlePointerMove.
    // Treadmill model: dragging does NOT disable autoscroll. It repositions
    // the horizontal gap (viewEndIndex below); the candle-arrival effect then
    // holds that new gap as data flows. (Previously this flipped autoscroll
    // off so a drag-back could hold history — now the treadmill holds any
    // offset, forward or backward, without a mode switch.)
    const plotWidth = Math.max(1, containerWidthRef.current - RIGHT_PADDING - FIXED_OVERLAY_WIDTH)
    const pxPerCandle = plotWidth / Math.max(1, viewWindowSizeRef.current)
    const rawDelta = dx / pxPerCandle
    // Math.trunc, NOT Math.floor. Math.floor is asymmetric around zero:
    // floor(-0.025) = -1 but floor(+0.025) = 0. So a single pixel of
    // leftward mouse movement immediately bumps indexDelta by a full
    // candle while a single pixel of rightward movement does nothing —
    // visible as the chart snapping to the mouse on drag start (and
    // every time leftward motion crosses an integer-candle threshold).
    // Math.trunc rounds toward zero in both directions: trunc(-0.025) =
    // trunc(+0.025) = 0, trunc(-1.25) = -1 just like trunc(+1.25) = +1.
    // frac becomes signed (negative for leftward drags), and the
    // downstream renderers (canvas, AVWAP, marker positions) all
    // consume it as a plain additive offset on candle X, so a signed
    // frac is fine.
    const tot = chartData.candles.length
    // Hard wall on both ends. The end index can move within
    // [startWall, endWall]; convert those to the min/max rawDelta the drag
    // is allowed to produce, then CLAMP rawDelta before deriving indexDelta
    // and frac. Clamping the continuous delta (not just the integer newEnd)
    // means that at a wall the fractional offset goes to 0 smoothly and the
    // canvas simply stops — instead of the integer pinning while frac snaps
    // back a column (the forward-pan "pop"). startWall=0, endWall includes
    // FUTURE_PADDING so forward panning stops exactly at the padding edge.
    // LEFT WALL — the viewport edge (xWalls, soft edge): the drag may pull
    // history right until the oldest candle sits at the plot's right edge;
    // the columns left of it render blank. At either wall the continuous
    // delta clamps, frac goes to 0 and the canvas simply stops (the coast
    // sees "pinned" and ends — no spring).
    const { startWall, endWall: endWallLaw } = xWalls(tot, viewWindowSizeRef.current)
    const startV = dragStartRef.current.viewEndIndex
    // (drag-jump) A drag that starts past the forward wall (wheel park) may
    // not push further out, but must not snap back to the wall either: its
    // own start end is the forward limit. Otherwise the law is unchanged.
    const endWall = Math.max(endWallLaw, startV)
    // newEnd = startV - indexDelta, so: indexDelta ∈ [startV-endWall, startV-startWall]
    const minDelta = startV - endWall
    const maxDelta = startV - startWall
    const rawDeltaClamped = Math.max(minDelta, Math.min(maxDelta, rawDelta))
    // FLOOR, not trunc. frac is the sub-candle remainder and the renderer
    // requires it in [0, cw): candleX = plotWidth - (idxFromRight+1)*cw + frac.
    // Math.trunc rounds TOWARD ZERO, so a negative delta leaves a NEGATIVE
    // remainder (-1.86 → trunc -1 → frac -0.86) and the whole render shifts
    // up to one full candle width left — the last footprint then vanishes a
    // candle early instead of sliding under the axis, and only one candle
    // advances per step. Math.floor rounds DOWN, so the remainder is always
    // positive (-1.86 → floor -2 → frac +0.14) and newEnd absorbs the extra
    // index, which is the paired half of the same correction.
    //
    // Sign-dependent by construction, which is why it only ever reproduced
    // one way: dragging in the direction that yields POSITIVE deltas has
    // trunc === floor and looks perfect.
    const indexDelta = Math.floor(rawDeltaClamped)
    const rawNewEnd = startV - indexDelta
    const newEnd = Math.max(startWall, Math.min(endWall, rawNewEnd))
    const frac = (rawDeltaClamped - indexDelta) * pxPerCandle

    fractionalOffsetPxRef.current = frac                       // ref update
    viewEndIndexRef.current = newEnd                           // sync imperatively, same tick

    window.dispatchEvent(new CustomEvent('solcheddar-redraw', { detail: { src: redrawSrcRef.current } }))
    // Past the law's forward wall only when the drag started there.
    setViewEndIndex(newEnd, newEnd > endWallLaw ? { clampFuture: false } : undefined)
  })

const handlePointerUp = useStableCallback((e) => {
  setIsDragging(false)
  isDraggingRef.current = false
  // Flick → glide. Replays the drag with a virtual pointer, so it stops at
  // the same walls the drag does. Crosshair mode never glides.
  const vAge = performance.now() - velRef.current.ts
  const v0 = vAge <= GLIDE_STALE_MS ? velRef.current.v : 0
  const startingGlide = Math.abs(v0) >= GLIDE_START_V
      && !crosshairModeRef?.current && !crosshairArmedRef?.current
      && !pinchActiveRef?.current
  if (startingGlide) {
    glideRef.current.v  = v0
    glideRef.current.x  = velRef.current.x
    glideRef.current.ts = performance.now()
    if (!isGlidingRef.current) { isGlidingRef.current = true; setIsGliding(true) }
    glideRef.current.id = requestFrame(glideStep, FRAME_INPUT)
  }
  // (yexplore) Y has no coast: the drag's Y ends here.
  onYDragRef?.current?.('end', e?.clientY)
  const container = bodyScrollRef.current
  if (container && e.pointerId != null && container.releasePointerCapture) {
    try { container.releasePointerCapture(e.pointerId) } catch {}
  }
  // Settle at the EXACT drag-end position. handlePointerMove dispatches this
  // redraw on every move to sync the main component's fractionalOffsetPxRef to
  // the scroll hook's `frac` (the sub-candle offset). On release we must do it
  // once more: the gesture leaves a non-zero frac, and without a final sync the
  // next unrelated render recomputes position from the integer viewEndIndex
  // alone — dropping that sub-candle residual and snapping the canvas. This is
  // the "chart moves on release" jump; the final redraw lands it where the drag
  // visually ended.
  window.dispatchEvent(new CustomEvent('solcheddar-redraw', { detail: { src: redrawSrcRef.current } }))
  // After the redraw retargets Y at the release position. A coast keeps the
  // drag flag; stopGlide('end') settles when the coast actually stops.
  // Settling before the redraw would release, then the redraw would arm a
  // new window with no settle and the fit would stick.
  if (!startingGlide) onDragEdgeRef?.current?.({ settle: true })
})

  useEffect(() => {
    if (!isDragging) return
    window.addEventListener('pointermove', handlePointerMove, { passive: true })
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('pointercancel', handlePointerUp)
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerUp)
    }
  }, [isDragging, handlePointerMove, handlePointerUp])

  // isDraggingRef + dragStartRef are exposed for the treadmill autoscroll in
  // the MAIN component: when it shifts viewEndIndex on candle arrival, it must
  // shift the live drag's anchor by the same delta or the two writers fight
  // (the mid-drag sawtooth). They live here — main reaches them via `scroll.`.
  // isDragging reports MOTION, not "finger down": the glide keeps
  // beginChartInteract armed, so the trail and heat stay on their shift fast
  // path and endChartInteract fires when the chart actually stops. Pointer
  // semantics stay on isDraggingRef.

  // edgescroll-20261102: pan by dx px through the same integrator (walls,
  // frac contract) as a drag. dx > 0 reveals older candles.
  const panByPx = (dxPx) => {
    const save = dragStartRef.current
    const frac0 = fractionalOffsetPxRef.current || 0
    dragStartRef.current = { ...(save || {}), x: 0, viewEndIndex: viewEndIndexRef.current }
    try { applyPanFromX(frac0 + dxPx) } finally { dragStartRef.current = save }
  }
  return { resolvedEndIndexRef, isDragging: isDragging || isGliding, isDraggingRef, dragStartRef, fractionalOffsetPxRef, handlePointerDown, scrollToLatest, bodyScrollTopRef, stopGlide, redrawSrcRef, panByPx }
}

// ============================================
// PnL Sparkline — small inline SVG line chart for the History tab
// ============================================
// Timeframe selector — replaces the native <select> with a custom inline
// expander. Shows the current TF + a ▸ caret; clicking slides the options out
// to the RIGHT as a horizontal row of buttons within the rail. Picking one
// sets the TF and collapses. (TF_OPTIONS moved to ./chartConstants.)
// Desktop pin (same 📌/📍 toggle as the bot settings menu): while pinned the
// row stays expanded — picking a TF or clicking elsewhere doesn't collapse
// it. One global pin, persisted in localStorage. Phone ignores it.
const TF_PIN_KEY = 'solcheddar:tfMenuPinned'
function TimeframeSelect({ value, onChange, chartId = 'main', cmeRoot = null }) {
  const isPhone = useIsPhone()
  // vol-bars-20261101: chart type set from the context menu (Chart type ->
  // Volume). In Vol the option row lists bar sizes (SOL per candle) instead of timeframes.
  const [vb, setVb] = useState(() => getVolBars(chartId))
  useEffect(() => {
    setVb(getVolBars(chartId))
    return onVolBarsChange((id, v) => { if (id === chartId) setVb(v) })
  }, [chartId])
  const isVol = vb.type === 'vol'
  const [open, setOpen] = useState(false)
  const [pinned, setPinned] = useState(() => {
    try { return localStorage.getItem(TF_PIN_KEY) === '1' } catch { return false }
  })
  const pinOn = pinned && !isPhone
  const shown = open || pinOn
  const togglePin = () => {
    const next = !pinned
    setPinned(next)
    setOpen(true)   // unpinning leaves it open; normal collapse applies from here
    try { localStorage.setItem(TF_PIN_KEY, next ? '1' : '0') } catch {}
  }
  const ref = useRef(null)
  // Phone drop-up anchor. The menu is absolutely positioned against
  // .footprint-wrapper rather than against this button, because the phone rail
  // is overflow-x:auto — anchoring to the button would clip the menu to a 28px
  // sliver of rail. The cost of escaping that clip is that CSS alone can't know
  // where the button IS, so the left offset is measured here at open time and
  // written as an inline style. Desktop ignores it (the desktop menu is a
  // static horizontal slide-out, unpositioned).
  const [menuLeft, setMenuLeft] = useState(null)
  useEffect(() => {
    if (!open || pinOn) return
    const onOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    // touchstart too: on phone the menu floats over the chart (CSS drop-up,
    // .is-phone scoped), and the chart's touch handlers preventDefault —
    // which suppresses the synthetic mousedown a tap would otherwise fire,
    // so a chart tap would never dismiss the menu with mousedown alone.
    document.addEventListener('mousedown', onOutside)
    document.addEventListener('touchstart', onOutside)
    return () => {
      document.removeEventListener('mousedown', onOutside)
      document.removeEventListener('touchstart', onOutside)
    }
  }, [open, pinOn])

  // Measure on open: put the menu's left edge under the trigger, then clamp so
  // a button near the right edge doesn't push the menu off-screen. Runs in a
  // layout effect so the position is set before the first painted frame —
  // otherwise the menu appears at left:8 and slides across on open.
  useLayoutEffect(() => {
    // NOT cleared on close. The menu fades out over ~200ms (opacity +
    // visibility transition), and dropping the inline `left` mid-fade sends it
    // back to the CSS default of left:8 — so it visibly flashed leftward on
    // every dismiss and every selection. Keeping the last measured value means
    // it fades out exactly where it was; the next open re-measures before
    // paint anyway, so a stale value is never seen.
    if (!open) return
    const el = ref.current
    const menu = el?.querySelector('.tf-select-options')
    const box = el?.closest('.footprint-wrapper')     // the positioned ancestor
    if (!el || !menu || !box) return
    const b = box.getBoundingClientRect()
    const t = el.getBoundingClientRect()
    // Everything measured in screen px. The phone rail is CSS-zoomed (1.3),
    // and the menu lives inside it, so its `left` is in zoomed units: divide
    // the screen-px result by the rail's zoom. Desktop zoom is 1 (no change).
    const rail = el.closest('.chart-rail')
    // Effective zoom of the menu = rail zoom x the menu's own counter-zoom
    // (phone CSS sets the menu back to 1x, so this is ~1 there too).
    const z = ((rail && parseFloat(getComputedStyle(rail).zoom)) || 1) *
              (parseFloat(getComputedStyle(menu).zoom) || 1)
    const w = menu.getBoundingClientRect().width || 0
    const MARGIN = 8
    const max = Math.max(MARGIN, b.width - w - MARGIN)
    setMenuLeft(Math.round(Math.min(Math.max(t.left - b.left, MARGIN), max) / z))
  }, [open])   // phone-only positioning; pin is desktop-only so `open` is enough

  const current = TF_OPTIONS.find(o => o.value === value)
  const autoSz = (typeof window !== 'undefined' && window.__cheddarVolBar?.auto) ? window.__cheddarVolBar.size : null
  const cmeSz = cmeRoot ? ((vb.cmeSize && vb.cmeSize[cmeRoot]) || 0) : 0   // cmevol-20261102
  const curLabel = (isVol && cmeRoot)
    ? (cmeSz > 0 ? `${cmeSz} ct` : `Auto ${CME_VOL_DEFAULT[cmeRoot] || 500} ct`)
    : isVol
    ? (vb.size > 0 ? `${vb.size} SOL` : `Auto${autoSz ? ' ' + autoSz : ''} SOL`)
    : (current ? current.label : value)
  const closeIfUnpinned = () => { if (!pinOn) setOpen(false) }
  return (
    <span
      className={`tf-select${shown ? ' open' : ''}${pinOn ? ' pinned' : ''}`}
      ref={ref}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <button
        className="tf-select-current"
        onClick={() => setOpen(o => !o)}
        title={isVol ? (cmeRoot ? 'Volume bars: each candle closes after this many contracts' : 'Volume bars: each candle closes after this much SOL') : 'Timeframe'}
      >
        {curLabel}
        <span className="tf-select-caret">▸</span>
      </button>
      <span
        className="tf-select-options"
        style={menuLeft == null ? undefined : { left: menuLeft }}
      >
        {!isVol && TF_OPTIONS.map(o => (
          <button
            key={o.value}
            className={`tf-select-opt${o.value === value ? ' active' : ''}`}
            onClick={() => { onChange(o.value); closeIfUnpinned() }}
          >{o.label}</button>
        ))}
        {isVol && cmeRoot && [0, ...(CME_VOL_SIZES[cmeRoot] || [])].map(sz => (
          <button
            key={'c' + sz}
            className={`tf-select-opt${cmeSz === sz ? ' active' : ''}`}
            title={sz ? `New candle every ${sz} contracts traded` : `Auto: ${CME_VOL_DEFAULT[cmeRoot] || 500} contracts per candle`}
            onClick={() => { setVolBars(chartId, { cmeSize: { ...(vb.cmeSize || {}), [cmeRoot]: sz } }); closeIfUnpinned() }}
          >{sz ? `${sz} ct` : 'Auto'}</button>
        ))}
        {isVol && !cmeRoot && [0, ...VOL_SIZES].map(sz => (
          <button
            key={'v' + sz}
            className={`tf-select-opt${vb.size === sz ? ' active' : ''}`}
            title={sz ? `New candle every ${sz} SOL traded` : 'Auto: about one candle per minute of this coin\'s recent flow'}
            onClick={() => { setVolBars(chartId, { size: sz }); closeIfUnpinned() }}
          >{sz ? `${sz}◎` : 'Auto'}</button>
        ))}
        {!isPhone && (
          <button
            className={`tf-select-pin${pinned ? ' active' : ''}`}
            onClick={togglePin}
            title={pinned ? 'Unpin timeframes' : 'Pin timeframes open'}
          >{pinned ? '📌' : '📍'}</button>
        )}
      </span>
    </span>
  )
}

// points: [{ time, pnl }] in chronological order. Renders a green/red line
// based on whether the final pnl is positive, with a fill underneath.
function PnlSparkline({ points, width = 500, height = 300, hoverTime = null, unit = 'usd' }) {
  if (!width || !height || width < 20 || height < 20) return null
  if (!points || points.length < 2) {
    return (
      <div style={{
        width, height, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 10, color: '#444',
      }}>
        not enough trades to chart
      </div>
    )
  }

  const pad = { top: 8, right: 6, bottom: 8, left: 6 }
  const w = width - pad.left - pad.right
  const h = height - pad.top - pad.bottom

  const xs = points.map(p => p.time)
  const ys = points.map(p => p.pnl)
  const minX = xs[0]
  const maxX = xs[xs.length - 1]
  const minY = Math.min(...ys, 0)  // always include zero so the baseline is visible
  const maxY = Math.max(...ys, 0)
  const rangeX = maxX - minX || 1
  const rangeY = (maxY - minY) || 1

  const xScale = (t) => pad.left + ((t - minX) / rangeX) * w
  const yScale = (v) => pad.top + (1 - (v - minY) / rangeY) * h

  const finalPnl = ys[ys.length - 1]
  const positive = finalPnl >= 0
  const color = positive ? '#66bb6a' : '#ef5350'
  const fillColor = positive ? 'rgba(102,187,106,0.12)' : 'rgba(239,83,80,0.12)'

  // Build the line path
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xScale(p.time).toFixed(1)} ${yScale(p.pnl).toFixed(1)}`).join(' ')
  // Build a fill path that closes down to the zero line (or chart bottom if zero is off-chart)
  const zeroY = yScale(0)
  const fillPath = `${linePath} L ${xScale(maxX).toFixed(1)} ${zeroY.toFixed(1)} L ${xScale(minX).toFixed(1)} ${zeroY.toFixed(1)} Z`

  const fmt = (n) => {
    const abs = Math.abs(n)
    const sign = n >= 0 ? '+' : '-'
    if (unit === 'sol') {
      if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(2)}k ◎`
      return `${sign}${abs.toFixed(2)} ◎`
    }
    if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}k`
    return `${sign}$${abs.toFixed(2)}`
  }

  // ─── Hover marker: find the nearest point to hoverTime ───
  let hoverPoint = null
  if (hoverTime != null && hoverTime >= minX && hoverTime <= maxX) {
    let best = points[0], bestDist = Math.abs(points[0].time - hoverTime)
    for (let i = 1; i < points.length; i++) {
      const d = Math.abs(points[i].time - hoverTime)
      if (d < bestDist) { best = points[i]; bestDist = d }
    }
    hoverPoint = best
  }

  return (
    <div style={{ position: 'relative', width, height, background: 'transparent' }}>
      <svg width={width} height={height} style={{ display: 'block' }}>
        {/* Zero baseline */}
        <line x1={pad.left} x2={width - pad.right} y1={zeroY} y2={zeroY}
          stroke="#222" strokeWidth="1" strokeDasharray="2 3" />
        {/* Fill */}
        <path d={fillPath} fill={fillColor} stroke="none" />
        {/* Line */}
        <path d={linePath} fill="none" stroke={color} strokeWidth="1.5"
          strokeLinejoin="round" strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 3px ${color}66)` }} />
        {/* Hover crosshair (horizontal + vertical) + dot */}
        {hoverPoint && (() => {
          const hx = xScale(hoverPoint.time)
          const hy = yScale(hoverPoint.pnl)
          const hoverColor = hoverPoint.pnl >= 0 ? '#66bb6a' : '#ef5350'
          return (
            <g>
              {/* Vertical line */}
              <line x1={hx} x2={hx} y1={pad.top} y2={height - pad.bottom}
                stroke="#fff" strokeWidth="1" strokeDasharray="3 2" opacity="0.4" />
              {/* Horizontal line */}
              <line x1={pad.left} x2={width - pad.right} y1={hy} y2={hy}
                stroke="#fff" strokeWidth="1" strokeDasharray="3 2" opacity="0.4" />
              <circle cx={hx} cy={hy} r="3.5" fill={hoverColor} stroke="#fff" strokeWidth="1" />
            </g>
          )
        })()}
        {/* Final dot */}
        <circle cx={xScale(maxX)} cy={yScale(finalPnl)} r="2.5" fill={color} />
      </svg>
      {/* Trade count — top right (resting state) */}
      {!hoverPoint && (
        <>
          <div style={{
            position: 'absolute', top: 4, right: 8, fontSize: 11, fontWeight: 700,
            color, fontFamily: "'JetBrains Mono', monospace",
            textShadow: '0 0 4px rgba(0,0,0,0.8)',
          }}>
            {fmt(finalPnl)}
          </div>
          <div style={{
            position: 'absolute', top: 18, right: 8, fontSize: 8, fontWeight: 600,
            color: '#555', letterSpacing: '0.05em', textTransform: 'uppercase',
            textAlign: 'right',
          }}>
            {points.length} trades
          </div>
        </>
      )}
      {/* Hover labels — P/L floats to the right of the crosshair, time sits at its base */}
      {hoverPoint && (() => {
        const hx = xScale(hoverPoint.time)
        const hy = yScale(hoverPoint.pnl)
        const hoverColor = hoverPoint.pnl >= 0 ? '#66bb6a' : '#ef5350'
        // Flip the P/L label to the left side when crosshair is too close to the right edge
        const labelOnLeft = hx > width - 60
        const fmtTime = (t) => {
          const d = new Date(t)
          return `${(d.getMonth()+1)}/${d.getDate()} ${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`
        }
        return (
          <>
            {/* P/L — right of crosshair dot */}
            <div style={{
              position: 'absolute', top: hy - 8,
              [labelOnLeft ? 'right' : 'left']: labelOnLeft ? (width - hx + 8) : (hx + 8),
              fontSize: 11, fontWeight: 700, color: hoverColor,
              fontFamily: "'JetBrains Mono', monospace",
              textShadow: '0 0 4px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.9)',
              pointerEvents: 'none', whiteSpace: 'nowrap',
            }}>
              {fmt(hoverPoint.pnl)}
            </div>
            {/* Time — at base of vertical line */}
            <div style={{
              position: 'absolute', bottom: 2,
              left: Math.max(4, Math.min(width - 90, hx - 45)),
              fontSize: 8, fontWeight: 600, color: '#aaa',
              fontFamily: "'JetBrains Mono', monospace",
              textShadow: '0 0 4px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.9)',
              pointerEvents: 'none', whiteSpace: 'nowrap',
            }}>
              {fmtTime(hoverPoint.time)}
            </div>
          </>
        )
      })()}
    </div>
  )
}

// ── Y-axis label measurement ──
// One shared offscreen 2D context measures label glyphs in the axis's real
// font (the old 7.2px/char estimate under-measured Nova Square, so 7+ char
// labels overflowed the column). Two taste constants define the whole
// axis geometry; everything else derives from the measured text width.
const AXIS_LABEL_FONT = '14px "Nova Square", system-ui, sans-serif'
const AXIS_LETTER_SPACING_PX = 0.56   // body letter-spacing 0.04em @ 14px
const AXIS_PAD_R   = 4                // label glyphs inset from screen edge (per side)
const AXIS_BAR_GAP = 4                // gap between bar-ends and glyphs
let _axisMeasureCtx = null
function measureAxisLabel(text) {
  if (!text) return 0
  if (!_axisMeasureCtx) _axisMeasureCtx = document.createElement('canvas').getContext('2d')
  const ctx = _axisMeasureCtx
  setCtxFont(ctx, AXIS_LABEL_FONT)   // (perf3) once per canvas, not per measure
  let tracked = false
  if ('letterSpacing' in ctx) {
    ctx.letterSpacing = `${AXIS_LETTER_SPACING_PX}px`
    tracked = ctx.letterSpacing !== ''
  }
  const w = ctx.measureText(text).width
  return tracked ? w : w + text.length * AXIS_LETTER_SPACING_PX
}

// ── Cell LOD thresholds, in CSS px of COLUMN width ────────────────────
// SINGLE SOURCE, same reason MIN_ROW_PX is one (see its note): these were
// local to drawCanvas, and the cell budget below has to agree with them about
// what counts as "a cell on screen". Two copies of a threshold is the bug
// that comment describes, so there is one.
// Text and cells are SEPARATE: text drops sooner; cells linger into candles.
//   cw > FP_LOD_TEXT_PX              -> footprint + volume text
//   FP_LOD_FADE_START < cw <= TEXT   -> cells still solid, text already off
//   CELLS < cw <= FADE_START         -> cells fading into candle bodies
//   cw <= FP_LOD_CELLS_PX            -> no cells, candles only
// FOLLOW_BAND_FRAC (the deadzone) moved to the Y machine — it is only ever
// read by the centre policy. See src/chart/machines/useYCamera.js.

// ── TOTAL FIT ───────────────────────────────────────────────────────────
// A range of zero, or of nothing, is not an answer. Pad a single known price
// into a real window so the y fit can ALWAYS produce one.
//
// This is the rule the y axis was missing. When the visible window scanned
// nothing, the fit returned the LAST GOOD lattice instead — which preserves
// the state that produced no data rather than replacing it, so the view has
// no way back and the chart appears to stick. A fit that answers every input
// cannot stick, and needs no fallback, because there is no failure case to
// fall back from.
function paddedRange(price) {
  const pad = Math.abs(price) * 0.1 || 1
  return { min: price - pad, max: price + pad }
}

// Exponential approach. tau is a TIME CONSTANT, not a duration — visible
// settle is roughly 3x it. dt clamped so a backgrounded tab does not jump.
function easeToward(cur, target, dtMs, tauMs) {
  if (!Number.isFinite(cur) || !Number.isFinite(target)) return target
  if (!(tauMs > 0) || !(dtMs > 0)) return target
  return cur + (target - cur) * (1 - Math.exp(-Math.min(dtMs, 64) / tauMs))
}

const _yaxisProbe = { sig: '', bkt: 0 }

// ── ROW HEIGHT FROM THE SPAN, NOT THE COUNT ─────────────────────────────
// RH = H / count looks equivalent and is not. rangeMax = ceilBucket(top) and
// rangeMin = floorBucket(bottom) snap INDEPENDENTLY, so whenever the span is
// not a whole number of buckets the two ends cross their boundaries at
// different moments and `count` flips between N and N+1 as the centre slides
// — with the bucket and the half-range both perfectly still. H/count turns
// that into a 1-2% height change on EVERY row at once, which is the shimmer.
//
// Deriving from the span instead makes RH continuous in halfRange, so a count
// flip changes nothing: the extra row simply appears at the rim. Measured at
// bucket 0.6 / span 44.57 buckets, H/count gives 20.00 vs 19.57px; this gives
// 19.33px at both counts.
//
// The trade is that count*RH lands a little under containerHeight (a sub-row
// sliver, ~1-3%) instead of exactly filling it. That is a fixed offset, not a
// shimmer, and it is what the SOLID build shipped. It was changed to H/count
// to stop the wheel Y-camera overshooting the short lattice mid-ease; that
// camera no longer exists.
//
// Reads halfRange/step off the lattice, which are ALREADY stamped and are
// price-space values — so this does not put a pixel quantity on the array and
// cannot remint latticeId on a resize, which is why rowH itself stays off it.
// bot-tag-20261025: trade tag = dark circle ringed in the side color with a
// glyph inside, plus a 45° tail whose tip touches the traded half-cell.
// BUY: circle below-left, tail up-right. SELL: circle above-right, tail
// down-left. glyph 'bot' = BotBadge robot head (24×24 viewBox units),
// text = your own fill's size (fmtTagSize).
function tradeTagTip(frame, idx, mcap, isBuy, RH, yRow, nRows, noSplit) {
  // Cells: buy bars grow LEFT of the column centre, sell bars RIGHT.
  const cx = frame.xMid(idx)
  const cw = frame.cw
  let ry = frame.yOf(mcap)
  const r = frame.rowOfPrice(mcap)
  if (!noSplit && r >= 0 && r < nRows) ry = (yRow(r) + yRow(r + 1)) / 2
  const s = isBuy ? 1 : -1                   // buy: target left half, tip on its lower-left edge
  if (noSplit) return { x: cx - s * 2, y: ry + s * 2 }
  const hx = cx - s * cw / 4
  const d = Math.max(1, Math.min(cw / 4, RH / 2) - 1)
  return { x: hx - s * d, y: ry + s * d }
}

// Compact size for a tag: 250, 8.5k, 850k, 1.2m, 3b (lowercase suffixes).
function fmtTagSize(v) {
  const a = Math.abs(+v || 0)
  const f = (x, u) => (x < 10 ? (Math.round(x * 10) / 10) : Math.round(x)) + u
  if (a >= 1e9) return f(a / 1e9, 'b')
  if (a >= 1e6) return f(a / 1e6, 'm')
  if (a >= 1e3) return f(a / 1e3, 'k')
  if (a >= 10) return String(Math.round(a))
  if (a >= 1) return String(Math.round(a * 10) / 10)
  return String(Math.round(a * 100) / 100).replace(/^0/, '')   // .25
}

// glyph: 'bot' = BotBadge robot head in a circle; any other string = that
// text in a pill (circle when it fits) — used for your own fills' size.
function drawTradeTag(ctx, tipX, tipY, isBuy, R, color, glyph) {
  const s = isBuy ? 1 : -1                   // away from tip: buy (-1,+1), sell (+1,-1)
  const L = Math.max(6, R * 1.2)             // visible tail length
  const off = (L + R) / Math.SQRT2
  let ox = tipX - s * off, oy = tipY + s * off
  const ring = Math.max(1.25, R * 0.16)
  const isText = glyph !== 'bot'
  let hw = R                                 // half width (pill grows sideways for text)
  let fpx = 0
  if (isText) {
    fpx = Math.max(8, Math.round(R * 0.95))
    ctx.save()
    ctx.font = `700 ${fpx}px 'JetBrains Mono', monospace`
    const tw = ctx.measureText(glyph).width
    ctx.restore()
    hw = Math.max(R, tw / 2 + ring + 2)
    ox -= s * (hw - R)                       // grow AWAY from the tail so its base stays put
  }
  const shape = (rad) => {
    ctx.beginPath()
    if (hw > R && ctx.roundRect) ctx.roundRect(ox - hw - (rad - R), oy - rad, 2 * (hw + rad - R), 2 * rad, rad)
    else if (hw > R) ctx.rect(ox - hw - (rad - R), oy - rad, 2 * (hw + rad - R), 2 * rad)
    else ctx.arc(ox, oy, rad, 0, Math.PI * 2)
  }
  // Tail: wedge from the near end of the shape to the tip (shape covers its base).
  const bx = ox + s * (hw - R) + s * R / Math.SQRT2, by = oy - s * R / Math.SQRT2   // tag-nocircle-20261025: base at old circle edge
  const w = R * 0.35                                // tag-nocircle-20261025: thin tail, no circle
  ctx.save()
  ctx.lineJoin = 'round'
  ctx.beginPath()
  ctx.moveTo(tipX, tipY)
  ctx.lineTo(bx + w, by + w)
  ctx.lineTo(bx - w, by - w)
  ctx.closePath()
  ctx.lineWidth = 2
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)'
  ctx.stroke()
  ctx.fillStyle = color
  ctx.fill()
  // tag-nocircle-20261025: circle removed; glyph + tail only.
  ctx.lineCap = 'round'
  if (!isText) {
    // BotBadge head: antenna dot (12,2), stem to (12,5), head rect (4,7,16,12)
    // rx 3, eyes r1.4 at (9,13)/(15,13). Ink spans ~u1..u19 → centre u10.
    const k = (R - ring) / 11.6
    const U = (u) => ox + (u - 12) * k
    const V = (u) => oy + (u - 13) * k
    const lw = Math.max(1, 2 * k)
    ctx.lineWidth = lw
    ctx.strokeStyle = color
    ctx.beginPath()
    if (ctx.roundRect) ctx.roundRect(U(4), V(7), 16 * k, 12 * k, 3 * k)
    else ctx.rect(U(4), V(7), 16 * k, 12 * k)
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(U(12), V(2))
    ctx.lineTo(U(12), V(5))
    ctx.stroke()
    ctx.fillStyle = color
    ctx.beginPath()
    ctx.arc(U(12), V(2), Math.max(0.9, k), 0, Math.PI * 2)
    ctx.fill()
    for (const eu of [9, 15]) {
      ctx.beginPath()
      ctx.arc(U(eu), V(13), Math.max(1, 1.4 * k), 0, Math.PI * 2)
      ctx.fill()
    }
  } else {
    ctx.font = `700 ${fpx}px 'JetBrains Mono', monospace`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = color
    ctx.fillText(glyph, ox, oy + 0.5)
  }
  ctx.restore()
}

function rowHeightFor(mcaps, containerHeight, fallbackCount) {
  // CAMERA LAW: the price window 2·halfRange maps onto H exactly.
  // RH = H·step/(2·half). Do NOT pack by visibleCount — quiet/pad used to
  // inflate count and then H/n reopened the visible price window while the
  // pin still held halfRange (bottom gap at max expand). Zoom lives in
  // halfRange (which changes n). Count is derived from the span, never the
  // other way around.
  const step = mcaps?.step
  // (yexplore) A tick-refined lattice is LINEAR rows under an ln camera:
  // its step is a price, so RH reads the matching price half (rowHalf).
  const half = (mcaps?.rowHalf > 0) ? mcaps.rowHalf : mcaps?.halfRange
  if (containerHeight > 0 && step > 0 && Number.isFinite(half) && half > 0) {
    return Math.max(MIN_ROW_PX, (containerHeight * step) / (2 * half))
  }
  const n = Number.isFinite(mcaps?.visibleCount) && mcaps.visibleCount > 0
    ? mcaps.visibleCount
    : (fallbackCount > 0 ? fallbackCount : 0)
  if (containerHeight > 0 && n > 0) return Math.max(MIN_ROW_PX, containerHeight / n)
  return ROW_HEIGHT
}

// (perf4) OPT-IN FRAME COUNTER. localStorage.cheddar_debug_frames = '1', then
// reload (or wait ~1s). Logs once a second while anything is counted:
//   full/live   drawCanvas passes, and the per-frame histogram of FULL draws
//               over frames where the Y glide stepped (0 = a HELD frame —
//               the glide moved but nothing was painted; 2+ = a double draw)
//   ystep       Y glide steps (2+/frame would be two walkers)
//   yCam.frame  camera runs: memo (fresh) / inplace, and memo hand-off reuses
//   rows-commit glide frames whose rows changed (React rebuilds the lattice)
//   renders     FootprintChart renders; long = frames > 25ms apart
// A frame closes at the end of the frame driver's tick (FRAME_LAYER), so a
// bucket is exactly what the next paint shows. Costs one flag read per
// second when off; the rAF only runs while there is something to count.
const _fd = { on: false, chk: 0, cur: null, acc: null, armed: false, last: 0, idle: 0 }
function fdOn() {
  const n = Date.now()
  if (n - _fd.chk > 1000) {
    _fd.chk = n
    try { _fd.on = localStorage.getItem('cheddar_debug_frames') === '1' } catch { _fd.on = false }
  }
  return _fd.on
}
function fdNewAcc() {
  return { t0: performance.now(), frames: 0, full: 0, live: 0, ystep: 0, dstep: 0,
    camMemo: 0, camInPlace: 0, reuse: 0, inPlaceOk: 0, rowsCommit: 0, render: 0,
    long: 0, dispBuild: 0, gf: [0, 0, 0] }
}
function fdCount(k) {
  if (!fdOn()) return
  if (!_fd.cur) _fd.cur = {}
  if (!_fd.acc) _fd.acc = fdNewAcc()
  _fd.cur[k] = (_fd.cur[k] | 0) + 1
  _fd.idle = 0
  if (!_fd.armed) { _fd.armed = true; requestFrame(fdClose, FRAME_LAYER) }
}
function fdClose() {
  _fd.armed = false
  const c = _fd.cur || {}
  _fd.cur = {}
  const a = _fd.acc || (_fd.acc = fdNewAcc())
  const now = performance.now()
  a.frames++
  if (_fd.last && now - _fd.last > 25) a.long++
  _fd.last = now
  for (const k of ['full', 'live', 'ystep', 'camMemo', 'camInPlace', 'reuse', 'inPlaceOk', 'rowsCommit', 'render', 'dispBuild']) a[k] += c[k] | 0
  if ((c.ystep | 0) > 0) a.gf[Math.min(2, c.full | 0)]++
  if ((c.ystep | 0) >= 2) a.dstep++
  const any = Object.keys(c).length > 0
  _fd.idle = any ? 0 : _fd.idle + 1
  if (now - a.t0 >= 1000 || (_fd.idle > 30 && a.frames > _fd.idle)) {
    if (a.full + a.live + a.ystep + a.render > 0) {
      dbg(`[frames] ${a.frames}f/${Math.round(now - a.t0)}ms`
        + ` | full ${a.full} (glide frames by full draws 0:${a.gf[0]} 1:${a.gf[1]} 2+:${a.gf[2]})`
        + ` | live ${a.live}`
        + ` | ystep ${a.ystep} (2+/frame ${a.dstep})`
        + ` | yCam.frame memo ${a.camMemo} inplace ${a.camInPlace} (reuse ${a.reuse})`
        + ` | inplace-ok ${a.inPlaceOk} rows-commit ${a.rowsCommit}`
        + ` | renders ${a.render} | long>25ms ${a.long} | dispBuild ${a.dispBuild}`)
    }
    _fd.acc = fdNewAcc()
  }
  if (fdOn() && _fd.idle <= 30) { _fd.armed = true; requestFrame(fdClose, FRAME_LAYER) }
  else { _fd.acc = null; _fd.last = 0 }
}

// (perf3) VOLUME PROFILE — PER-CANDLE BINNED LEVELS. The VP memo re-runs on
// every X-scroll frame (visibleCandles is a new slice each step) and re-binned
// every native level of every visible candle: forEachLevel + logStepIdx
// (Math.log) per level, ~10^4 per frame. A PACKED candle's _native is
// immutable (packCandle builds it once; late ticks unpack into _liveLevels
// and null _native), so its binned levels are a pure function of
// (_native identity, _nativeBucket, ratio | bucket). Cached per candle, in
// the ORIGINAL level order with the ORIGINAL buy/sell doubles, so the memo's
// bump sequence — and therefore every sum and max — is bit-identical to the
// fresh walk. Returns null whenever forEachLevel(c, fn, null, null) would
// take a different path (live levels, a current _display): caller walks.
const _vpLevelCache = new WeakMap()
function vpBinnedLevels(c, ratio, bucketSize) {
  if (c._liveLevels && c._liveLevels.size > 0) return null
  const a = c._native
  if (!a) return null
  if (c._display && c._display.length > 0 && displayIsCurrent(c, null, null)) return null
  const b = c._nativeBucket
  const bkt = ratio ? 0 : bucketSize
  const hit = _vpLevelCache.get(c)
  if (hit && hit.a === a && hit.b === b && hit.ratio === ratio && hit.bkt === bkt) return hit.lv
  const lv = new Float64Array(a.length)
  for (let i = 0; i < a.length; i += 3) {
    const m = a[i] * b                       // forEachLevel's native mcap, verbatim
    lv[i] = ratio ? (m > 0 ? logStepIdx(m, ratio) : NaN) : stepIdxOf(m, bucketSize)
    lv[i + 1] = a[i + 1]
    lv[i + 2] = a[i + 2]
  }
  _vpLevelCache.set(c, { a, b, ratio, bkt, lv })
  return lv
}

// (perf2) ROW-ANCHORED OVERLAY TRANSFORMS. One formatter for the JSX and for
// drawCanvas's imperative writes, so both produce the identical string.
// Overlays ride `transform` (no layout), never `top`/`height`.
//   yTagTf  — plain translate (alert anchors, in-flow layers, the chip)
//   yTagTfC — translate + the -50% centring the tag CSS used to carry
//   yBarTf  — meteora bar: 1px box translated to its row and scaled to RH
// (perf3) 2D translateY, NOT translate3d: any 3D transform function — even a
// trivial one — is a direct compositing reason in Chrome, so every tag, layer
// and the chip became its own compositor layer (Layerize 130 → 537ms). 2D
// transforms repaint in their parent layer, the same as before perf2.
const yTagTf = (y) => `translateY(${y}px)`
const yTagTfC = (y) => `translateY(${y}px) translateY(-50%)`
const yBarTf = (top, h) => `translateY(${top}px) scaleY(${h})`
// Write-on-change keyed on what WE last wrote AND what the element holds now
// (React may have rewritten the prop on a render). Inline-style reads are
// CSSOM only — no layout.
function writeTf(el, t) {
  if (el.__yTf === t && el.__yTfSeen === el.style.transform) return
  el.style.transform = t
  el.__yTf = t
  el.__yTfSeen = el.style.transform
}

// (y2a) THE LATTICE GEOMETRY, PURE. Lifted verbatim out of the grid memo
// (visibleSortedMcaps, section 5) so the Y glide can evaluate the SAME rows
// for a zoom without a React render. No refs, no state, no side effects other
// than the existing cheddar_debug_scale console probe. Three substitutions
// only: yCam.isPinned() -> `pinned`, containerWidthRef.current -> `containerW`,
// and the count<=0 bail returns null (the memo maps null back to
// chartData.sortedMcaps). Everything else is the memo's text.
// Inputs: centre / halfRange from the camera frame, the working-space step,
// the pin AFTER frame(), and the viewport (H, window, container width).
function latticeGeomFor({ center, halfRange, bucketSize, pinned, containerHeight, viewWindowSize, containerW, holdQuiet, log = false, minStep = 0, refinePx = 0, targetPx = 0 }) {
  const minQuietRows = 24
  // holdQuiet: a price-bucket commit kept the previous half-span. Do not
  // raise the quiet floor in that frame (or while that window is still held).
  if (!pinned && !holdQuiet && bucketSize > 0) {
    const minHalf = (minQuietRows * bucketSize) / 2
    if (halfRange < minHalf) halfRange = minHalf
  }
  const liveHalfRange = halfRange
  // Desired (unsnapped) top of the grid from the current center.
  const desiredTop = center + halfRange
  // Hysteretic snap: reuse the last committed rangeMax unless the desired
  // top has moved more than one bucket above it, or the bottom row would
  // clip the data (desiredTop dropped a full bucket below it). Recomputing
  // ceilBucket every frame produced a fractionally different snap as
  // dataRange/center wiggled, and that re-snap fed back into the next
  // frame's range — the loop on new highs and the jump-up at lows.
  const wantSnap = ceilBucket(desiredTop, bucketSize)
  // SYMMETRIC REBUCKET: rangeMax is always the plain ceil of desiredTop, on
  // both a bucket change and steady state. Previously a bucket change ran a
  // top-anchored price-PIVOT (recompute rangeMax so the anchor price keeps its
  // old row index) — but that routed the correction through rangeMax, so its
  // magnitude scaled with the anchor's distance BELOW the top: tiny at highs,
  // large at lows, and it fell back to a plain ceil ("out of window") more
  // often at lows, which read as a jump on new lows specifically. That was the
  // last place highs and lows were handled differently. The smooth-positioning
  // job the pivot was reaching for is already done symmetrically by the
  // residual glide below (residualBuckets = (rangeMax - desiredTop)/gridBucket,
  // in [0,bucket) because rangeMax = ceil(desiredTop)) together with the
  // symmetric bottom snap. So: one rule for both ends, no distance-scaled
  // pivot, no low-side fallback. (The old pivot block is in git if the
  // rebucket "clunk" it was damping returns.)
  let rangeMax = wantSnap
  // lastRangeMaxRef stamped AFTER y-fit lerp so the target auto-fit does not
  // latch before the glide paints (that was the snap-then-glide).
  // ── SYMMETRIC BOTTOM SNAP (make the bottom like the top) ──
  // The top (rangeMax) snaps to a hard bucket boundary via ceilBucket; the
  // bottom used to be a DERIVED value (rangeMax - count*bucket), never snapped
  // to anything. That asymmetry is the new-lows bug: a new high moves the
  // anchored top to a clean boundary and the rows below keep their positions,
  // but a new low extended the floating bottom, which changed the row COUNT
  // and re-spaced every row off the single top anchor — the row farthest from
  // that anchor (a fresh low, by definition) jumped the most.
  // Fix: snap the bottom too. rangeMin = floorBucket(desiredBottom) on the
  // SAME bucket lattice as the top. Now both ends are hard boundaries on one
  // grid (top=ceil, bottom=floor, same bucketSize → lockstep phase, so the
  // old "independent rangeMin snap oscillates ±1 row on pan" concern doesn't
  // apply — they can't drift relative to each other). count falls out of the
  // two snapped ends. A new low snaps the bottom exactly like a new high
  // snaps the top: clean, symmetric, no whole-grid re-spacing.
  // ── COUNT COMES FROM THE SPAN, THE BOTTOM COMES FROM THE COUNT. ──────
  // The note above says ceil(top) and floor(bottom) are on one grid so they
  // "can't drift relative to each other". They can, and this is the bump on
  // an x scroll. Whenever the span is not a whole number of buckets the two
  // ends cross their boundaries at DIFFERENT moments, so a sliding centre
  // flips count between N and N+1 with the bucket and the span both frozen.
  // Measured: bucket 0.6, span 44.57 buckets -> count 45/46. RH is H/count,
  // so every row resizes by 1/N on each flip — 4% at 25 rows, right as
  // candles enter and leave.
  //
  // The span does not care where the centre is, so deriving count from it
  // removes the flip entirely: pan all you like, count holds, RH holds. The
  // top stays hard-snapped (that is the anchor the residual slides against)
  // and the bottom is derived, so both ends still land on the one grid.
  // The +1 guarantees the bottom covers desiredBottom for any phase.
  //
  // A genuine new low still changes the span and therefore count — one
  // re-space, on real data, which is the behaviour the symmetric snap was
  // written for. What it cannot do any more is flip back and forth while
  // nothing but the centre moves.
  let gridBucket = bucketSize
  const desiredBottom = center - halfRange
  let count = Math.max(1, Math.ceil((desiredTop - desiredBottom) / gridBucket) + 1)
  let rangeMin = rangeMax - count * gridBucket

  // Quiet floor moved above (halfSpan camera floor when unpinned).
  // Count is derived from the camera window only — never padded here.
  if (count <= 0) return null
  // EMERGENCY COARSEN — committed bucket vs data range can be degenerate
  // during load (sizer commits off a tiny restored range, then history
  // prepends a huge one). The old code bailed to the STALE previous grid
  // here, freezing the chart until the next dep change re-ran this memo —
  // on a quiet token that's the next trade, seconds or minutes away.
  // Build a valid grid at a coarsened local bucket instead; drawCanvas
  // derives its bucket from this array and buildDisplay groups to match,
  // so the frame is self-consistent and the sizer converges next pass.
  // TWO CAPS: what the container can DRAW, and what it can draw FAST.
  // The geometry one is keyed on height, the budget one on columns.
  const _plotW = Math.max(1, containerW - RIGHT_PADDING - FIXED_OVERLAY_WIDTH)
  const _cw = _plotW / Math.max(1, viewWindowSize)
  // Full-footprint only. Below TEXT_PX (incl. the fade band) keep fine
  // vertical resolution — same lattice candles used, so VP bar height and
  // Y scale don't jump while cells dissolve in.
  const _cols = _cw > FP_LOD_FADE_START ? Math.round(viewWindowSize) : 0
  // QUANTISED to CELL_ROW_STEP — see prior note: continuous budget → new
  // bucket every column of an X zoom.
  const _budget = cellBudget(_plotW, containerHeight || 0)
  const _rawBudgetRows = _cols > 0 ? Math.floor(_budget / _cols) : Infinity
  const _budgetRows = Number.isFinite(_rawBudgetRows)
    ? Math.max(CELL_ROW_STEP, Math.floor(_rawBudgetRows / CELL_ROW_STEP) * CELL_ROW_STEP)
    : Infinity
  // ── GEOMETRY CAP: ROWS THE CONTAINER CAN ACTUALLY DRAW. ──────────────
  // Without this, count grows past what the height can show, RH hits its
  // MIN_ROW_PX floor, and count*RH exceeds containerHeight. The lattice is
  // top-anchored (mcapToY puts row 0 at rangeMax), so ALL of that excess
  // goes off the bottom — the range stays symmetric about dataCenter in
  // PRICE while being pushed below the fold in PIXELS. That is the dip, and
  // it appears suddenly because H/count crossing MIN_ROW_PX is a hard edge.
  //
  // Cap count instead and the floor never binds: count <= H/MIN_ROW_PX - 2
  // means H/count >= MIN_ROW_PX, so count*RH <= H by construction and there
  // is no overflow to centre or scroll.
  //
  // QUANTISED, unlike the version this is restored from. Raw, it is a
  // continuous function of containerHeight, and the coarsen branch below
  // derives gridBucket from MAX_GRID_ROWS — an arbitrary value, not a ladder
  // rung — so a window drag reminted the bucket on every pixel: new
  // latticeId, heat/cell/atlas invalidation, per frame. That is why it was
  // removed rather than fixed. Snapping to CELL_ROW_STEP gives the cap in a
  // handful of discrete jumps across a resize instead of once per pixel,
  // exactly as _budgetRows already does for an X zoom.
  const _rawFitRows = Math.floor((containerHeight || 0) / MIN_ROW_PX) - 2
  const _fitRows = _rawFitRows > 0
    ? Math.max(CELL_ROW_STEP, Math.floor(_rawFitRows / CELL_ROW_STEP) * CELL_ROW_STEP)
    : Infinity
  const MAX_GRID_ROWS = Math.max(20, Math.min(_fitRows, _budgetRows))
  try {
    if (typeof localStorage !== 'undefined'
        && localStorage.getItem('cheddar_debug_scale') === '1') {
      dbg('[cells]', _budgetRows < _fitRows ? 'budget binding' : 'fit binding',
        '— cols', viewWindowSize,
        '| rows →', MAX_GRID_ROWS,
        '| fitRows', _fitRows, '| budgetRows', _budgetRows,
        '| budget', _budget, '| cw', _cw.toFixed(1))
    }
  } catch {}
  // ── (yexplore) REFINE TO THE TICK. ─────────────────────────────────
  // Only with a user Y span (caller passes minStep > 0 only then). When the
  // committed bucket's rows would be taller than refinePx, switch to LINEAR
  // rows on multiples of the instrument's smallest step: minStep * 2^k, k >= 0,
  // the smallest such step whose rows are >= targetPx (so rows sit in
  // [targetPx, 2*targetPx)). (ytickcap) k = 0 only at the span floor, where a
  // one-tick row is exactly targetPx — single-tick rows never stretch past
  // the target row height (yCam.frame enforces the floor). Every row then holds
  // exactly 2^k ticks (k = 0: one tick per row), and k changing keeps the
  // surviving rows (power-of-two subset, same as the coarsen below). Under
  // log the camera stays in ln; the view's ln window is mapped to price once
  // here — at a span this narrow ln and linear agree to well under a pixel.
  if (minStep > 0 && refinePx > 0 && targetPx > 0 && containerHeight > 0 && halfRange > 0
      && (containerHeight * bucketSize) / (2 * halfRange) > refinePx) {
    const topP = log ? Math.exp(desiredTop) : desiredTop
    const botP = log ? Math.exp(desiredBottom) : desiredBottom
    const linHalf = (topP - botP) / 2
    // Committed bucket in price units at the top of the view.
    const bucketP = log ? topP * (1 - Math.exp(-bucketSize)) : bucketSize
    if (linHalf > 0 && Number.isFinite(linHalf) && minStep < bucketP) {
      // Step whose rows are exactly targetPx. At the floor want == minStep
      // (to float noise — the epsilon) → k = 0.
      const want = (2 * linHalf * targetPx) / containerHeight
      let k = Math.ceil(Math.log2(want / minStep) - 1e-9)
      if (!(k >= 0)) k = 0
      let step = minStep * Math.pow(2, k)
      let n = Math.max(1, Math.ceil((2 * linHalf) / step) + 1)
      // Row cap still binds (cell budget can sit below H / row height).
      while (n > MAX_GRID_ROWS && step < 2 * linHalf) {
        step *= 2
        n = Math.max(1, Math.ceil((2 * linHalf) / step) + 1)
      }
      if (step < bucketP) {
        const top = Math.ceil(topP / step) * step
        return {
          halfRange, liveHalfRange, desiredTop, desiredBottom,
          rangeMax: top, rangeMin: top - n * step, count: n, gridBucket: step,
          MAX_GRID_ROWS, lin: true, linHalf, slideTop: topP,
        }
      }
    }
  }
  if (count > MAX_GRID_ROWS) {
    // ── QUANTISE THE INPUT, NOT THE OUTPUT. ─────────────────────────────
    // Snapping the OUTPUT to a ladder rung was tried and is a trap: the
    // ladder steps by decades, so the chosen rung can be far coarser than
    // the span, rangeMax and rangeMin snap to the same multiple, count goes
    // to 0 and the lattice is empty. Blank chart, no error, no early return
    // — it just rolls in and out as you cross the cap. The divisor has to
    // come from the cap so count is bounded by construction.
    //
    // ── COARSEN HARMONICALLY. step' = bucketSize * 2^n. ─────────────────
    // _qHalf quantised halfRange to an octave so the divisor only moved
    // once an octave. That fixed the spin and it is kept in spirit — n
    // below is also an octave function, so the step still holds for a
    // whole octave of halfRange.
    //
    // What _qHalf did NOT give is any relationship between the coarse step
    // and the committed one. (2*_qHalf)/(MAX-2) is an arbitrary real, so
    // every coarse rung was a NEW price and the crossing re-derived the
    // entire grid. Heat adopts a row SHIFT for free (uniform integer
    // offset, probed from its own stored prices) but cannot adopt a
    // re-spacing — it falls back to the budgeted 8-entries-a-frame
    // re-projection, which is the ribbon crawling for tens of frames.
    //
    // A power-of-two multiple makes the coarse rungs a strict SUBSET of
    // the fine ones: every surviving row keeps its exact price and the
    // crossing drops every other row instead of re-spacing all of them.
    //
    // n comes from the row cap directly, so count <= MAX_GRID_ROWS by
    // construction (the -1 pays for the +1 row count carries).
    const _needStep = (2 * halfRange) / (MAX_GRID_ROWS - 1)
    const _n = Math.max(1, Math.ceil(Math.log2(_needStep / bucketSize)))
    gridBucket = bucketSize * Math.pow(2, _n)
    // ANCHOR FROM desiredTop, NOT FROM THE FINE rangeMax. slide() measures
    // ceil(desiredTop/step)*step - desiredTop; ceil'ing the already-ceil'd
    // fine rangeMax can land one coarse bucket higher than that, and the
    // residual then cannot cancel it — the grid sits one row low. Reachable
    // whenever desiredTop is within bucketSize below a coarse boundary,
    // i.e. 1 crossing in 2^n. Same expression as slide's, so they agree by
    // construction. Still a multiple of bucketSize: the anchor lands on a
    // rung that already existed.
    rangeMax = Math.ceil(desiredTop / gridBucket) * gridBucket
    // Same rule as the normal path: span -> count -> bottom.
    count = Math.max(1, Math.ceil((2 * halfRange) / gridBucket) + 1)
    rangeMin = rangeMax - count * gridBucket
  }
  return { halfRange, liveHalfRange, desiredTop, desiredBottom, rangeMax, rangeMin, count, gridBucket, MAX_GRID_ROWS, lin: false, linHalf: 0, slideTop: desiredTop }
}

// (yexplore) THE TICK FLOOR — the smallest meaningful row, in PRICE units.
// Hyperliquid perps: a price has at most 5 significant figures and at most
// (6 - szDecimals) decimals; whole numbers are always valid (so the tick
// never exceeds 1). Never finer than the native storage bucket either — rows
// below it would be empty. Solana mcap has no venue tick: the native storage
// bucket IS the floor. 0 = unknown (no refine; the Y span floor falls back).
function hlPriceTick(price, szDecimals) {
  if (!(price > 0) || !Number.isFinite(szDecimals)) return 0
  const sig = Math.pow(10, Math.floor(Math.log10(price)) - 4)
  const dec = Math.pow(10, -Math.max(0, 6 - szDecimals))
  return Math.min(1, Math.max(sig, dec))
}
function yTickFloor(candles, price, isHl, szDecimals) {
  let native = 0
  if (candles && candles.length) {
    for (let i = candles.length - 1, n = 0; i >= 0 && n < 8; i--, n++) {
      const nb = +candles[i]?._nativeBucket
      if (nb > 0) { native = nb; break }
    }
  }
  if (!isHl) return native
  return Math.max(native, hlPriceTick(price, szDecimals))
}
// Rows refine (halve toward the tick) once they would be taller than this
// many target rows. Also sets the Y span floor: at the floor, one-tick rows
// sit at exactly this height.
const Y_REFINE_ROW_MULT = 2
// Body drag: vertical travel before a drag counts as having a Y component
// (enters explore). Keeps an ordinary sideways drag from stopping follow.
const Y_EXPLORE_ARM_PX = 6

// Pin translate: sub-bucket slide ONLY. Letterbox centre was reverted —
// it fought residual and made the edge wander. Viewport fill is owned by
// rowHeightFor (2·half → H), not by a second translate.
function yPinResidualPx(mcaps, RH, _containerHeight) {
  const rb = mcaps?.residualBuckets
  return (Number.isFinite(rb) && Number.isFinite(RH)) ? rb * RH : 0
}

// ── THE STATELESS HALF OF THE FIT ───────────────────────────────────────
// Scan a candle slice into a price window. PURE: no refs, no side effects, no
// dependence on which window is currently displayed — so it is safe to call
// for a HYPOTHETICAL window as many times per frame as you like, which is the
// whole point. A glide that wants to know where it is going has to be able to
// ask "what does the fit say at window W" without moving anything.
//
// What is deliberately NOT in here is the centre policy — the held target, the
// FOLLOW_EDGE_FRAC breach test and the FOLLOW_DAMP_K damper. Those are
// stateful and they read the CURRENT visible half-range, so evaluating them at
// a window you are not at yet would both test a breach against the wrong
// window and advance the held centre as a side effect. Destination evaluation
// would corrupt the present. They stay in the memo, running once a frame at
// the real window.
//
// getFallbackPrice is a function, not a value: it walks candles backwards and
// is only needed when the scan comes up empty.
function scanFitRange(rangeCandles, wantLog, getFallbackPrice, allCandles = null) {
  const scanned = scanCandlesMcapRange(rangeCandles)
  let min = scanned.min
  let max = scanned.max
  // Empty window (scrolled past live): frame the last real candles. The ±10%
  // pad is ln 0.20 — 10-20x a futures window, so the axis blew out.
  if ((!Number.isFinite(min) || !Number.isFinite(max)) && allCandles?.length) {
    const t = scanCandlesMcapRange(allCandles.slice(-20))
    if (Number.isFinite(t.min) && Number.isFinite(t.max)) { min = t.min; max = t.max }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    const kp = getFallbackPrice()
    if (!(kp > 0)) return null
    const r = paddedRange(kp)
    min = r.min
    max = r.max
  }
  // ln of a non-positive price is not a number — fall back to linear for this
  // frame rather than produce NaN rows.
  const log = !!wantLog && min > 0
  if (log) {
    min = Math.log(min)
    max = Math.log(max)
  }
  return { min, max, log }
}

// THE LEGAL X VIEW (xWalls / clampEndIdx / resolveEndIdx) moved to the X
// machine, imported above: src/chart/machines/xWindow.js. Every mover in this
// file and in useChartInteraction now clamps through the same definition.

const FP_LOD_TEXT_PX = 32
const FP_LOD_FADE_START = 20
const FP_LOD_FADE_PX = 10
// Hard cell→candle switch. Midpoint of the old FADE_START..FADE_START-FADE_PX
// ramp, i.e. where the crossfade used to sit at 50%.
const FP_LOD_SWITCH = FP_LOD_FADE_START - FP_LOD_FADE_PX * 0.5   // 15, going DOWN
// HYSTERESIS, NOT A CROSSFADE. The old 11-16px band drew cells AND candles
// alpha-blended on the non-axis-aligned path — it bought smoothness with a
// double draw in exactly the range that was already the slowest. A dead zone
// buys the same stability for nothing: nothing is drawn twice, nothing renders
// at partial alpha. Coming back UP, cells do not return until 18, so two
// adjacent wheel notches cannot sit either side of one threshold and flip the
// whole render mode back and forth.
const FP_LOD_HYST_PX = 3
const FP_LOD_RESTORE = FP_LOD_SWITCH + FP_LOD_HYST_PX            // 18, going UP
const FP_LOD_CELLS_PX = FP_LOD_FADE_START - FP_LOD_FADE_PX   // 10

// ── Cell budget ──────────────────────────────────────────────
// The chart bounded its two axes INDEPENDENTLY and never looked at their
// product. Rows were capped by containerHeight / MIN_ROW_PX — a purely
// vertical question. Cells stopped drawing below FP_LOD_CELLS_PX of column
// width — a purely horizontal one. Neither knew about the other, so the cell
// COUNT was whatever the two happened to multiply out to: ~110 rows x ~78
// cell-bearing columns ≈ 8.5k cells, each costing a fill, a sprite lookup and
// a blit per side. Which is why the lag only cleared when you scaled down far
// enough for cells to vanish, or zoomed in far enough that few remained.
//
// So budget the PRODUCT. Rows are the lever, because coarsening the mcap
// bucket is already how the grid sheds rows and the sizer already owns it.
// Wide view with many columns -> fewer rows allowed -> coarser bucket ->
// fewer cells. Zoomed in with few columns -> the row cap is never the binding
// constraint and nothing changes.
//
// Budget only when cells are FULLY on (cw > FP_LOD_FADE_START). Gating at
// FP_LOD_CELLS_PX rebuckeed mid-fade; always-on made zoomed-out VP/grid bars
// fat (few budgeted rows → large RH) then thinner on zoom-in. Fade band
// (CELLS..FADE_START) keeps the fine candle lattice so morph and VP stay stable.
//
// STATIC, deliberately. An adaptive budget driven by measured frame cost
// would be a second controller fighting the bucket hysteresis for the same
// output — the "one hysteresis, one owner" deadlock the sizer's own notes
// warn about. This is a fixed ceiling: same inputs, same grid, every time.
//
// TUNE IT LIVE, no rebuild:
//   localStorage.cheddar_cell_budget = '3000'   // then pan/zoom once
//   localStorage.removeItem('cheddar_cell_budget')
// Watch the effect with localStorage.cheddar_debug_scale = '1', which now
// prints the projected cell count and whether the budget is binding.
// Row-cap quantum. Bigger = fewer rebuckets while zooming, coarser jumps in
// price resolution when the budget bites. 16 gives ~6 distinct caps across
// the binding range on a 900px chart.
const CELL_ROW_STEP = 16
const CELL_BUDGET_DESKTOP = 4500
const CELL_BUDGET_PHONE   = 1800
// SCREEN-SIZED. A fixed 4500 was sized for a ~900px chart; on a big panel
// Fine's rows × the ~50 columns at the gate (cw = FP_LOD_FADE_START) blew
// through it, so the budget coarsened rows 128→80 while zooming out and let
// go at the gate — the dip. Budgeting per PIXEL instead scales with the
// plot: one cell per CELL_AREA_PX of plot. At the gate a column is 20px
// wide, so rows allowed = area/(CELL_AREA_PX·cols) = H·20/CELL_AREA_PX —
// with 160 that's H/8, exactly the geometry cap (MIN_ROW_PX = 8). So the
// budget never binds at the gate on ANY screen size (no dip), and only
// bites if a tiny plot would otherwise go under the device floor.
// The device constants remain the FLOOR (phones keep their own cap).
const CELL_AREA_PX = 160
function cellBudget(plotW = 0, plotH = 0) {
  try {
    const o = localStorage.getItem('cheddar_cell_budget')
    if (o) { const n = parseInt(o, 10); if (Number.isFinite(n) && n > 200) return n }
  } catch {}
  const floor = isPhoneDevice() ? CELL_BUDGET_PHONE : CELL_BUDGET_DESKTOP
  const byArea = Math.floor((plotW * plotH) / CELL_AREA_PX)
  return Math.max(floor, byArea)
}

// ── Y-axis tier model ─────────────────────────────────────────────────────
// Which rows get a full label, a dim minor label, a lone dash, or nothing.
// Lived inline in the y-axis row map while the labels were DOM; the row map
// no longer renders labels, so this is now the painter's alone. Logic is
// byte-for-byte what the map did — see the long note there in git history
// on why spacing is solved in PIXELS (one modulo can't serve three
// densities) and anchored to the PRICE lattice (index anchoring slid a row
// on every top-edge snap).
const Y_LABEL_TARGET_PX  = 90
const Y_MINOR_MIN_GAP_PX = 34
const Y_TICK_MIN_ROW_PX  = 9
function axisTierOf(mcaps, RH) {
  const gridBucket = mcaps.length > 1 ? Math.abs(mcaps[0] - mcaps[1]) : 0
  const rawStep = Math.max(1, Math.ceil(Y_LABEL_TARGET_PX / Math.max(1, RH)))
  const labelStep = rawStep <= 2 ? rawStep : Math.max(4, Math.round(rawStep / 4) * 4)
  const minorStep = labelStep >= 2 ? labelStep / 2 : 0
  const tickOffset = minorStep && minorStep % 2 === 0 ? minorStep / 2 : -1
  return (mcap, i) => {
    // Under log the lattice is geometric: the row index that keeps label
    // spacing regular in ROWS is the ln-step index, not mcap/gridBucket
    // (which would space labels by absolute price and bunch them at the top).
    const k = mcaps.log
      ? logStepIdx(mcap, mcaps[0] / mcaps[1])
      : (gridBucket > 0 ? Math.round(mcap / gridBucket) : i)
    if (k % labelStep === 0) return 'major'
    if (minorStep && k % minorStep === 0
        && minorStep * RH >= Y_MINOR_MIN_GAP_PX) return 'minor'
    // ONE dash per gap, at its midpoint — not one per empty row.
    if (tickOffset > 0 && ((k % minorStep) + minorStep) % minorStep === tickOffset) return 'tick'
    return 'blank'
  }
}

// Measure a DOM node's content rect with ResizeObserver. Returns [ref, {width, height}].
function useElementSize() {
  const ref = useRef(null)
  const [size, setSize] = useState({ width: 0, height: 0 })
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cr = entry.contentRect
        setSize({ width: cr.width, height: cr.height })
      }
    })
    ro.observe(el)
    // Initial measure
    const rect = el.getBoundingClientRect()
    setSize({ width: rect.width, height: rect.height })
    return () => ro.disconnect()
  }, [])
  return [ref, size]
}

// PnlSparkline that auto-fits its parent container.
function ResponsiveSparkline({ points, hoverTime = null, unit = 'usd' }) {
  const [ref, size] = useElementSize()
  return (
    <div ref={ref} style={{ width: '100%', height: '100%' }}>
      <PnlSparkline points={points} width={Math.floor(size.width)} height={Math.floor(size.height)} hoverTime={hoverTime} unit={unit} />
    </div>
  )
}

// ============================================
// ============================================
// MiniCandleSpark — small OHLC candle stick spark
// ============================================
//
// Same layout contract as ScanSparkline (left/top/width/height + openY),
// so the watchlist's spark sits in exactly the same screen real estate as
// the scan tab's spark and pivots on the same structural dashed divider.
//
// Renders one bull/bear-colored candle per OHLC entry: wick line from
// high to low at the column center, body rect from open to close. Bullish
// (close >= open) green, bearish red. y-axis anchors on the FIRST candle's
// open price (matches the scan spark's pivot semantic — "where did this
// series start?") with independent above/below scaling so both halves
// of the panel fill out regardless of how much each direction moved.
const MiniCandleSpark = memo(function MiniCandleSpark({ candles, left, top, width, height, openY }) {
  if (!Array.isArray(candles) || candles.length < 1 || width <= 0 || height <= 0) return null

  const open = candles[0].o
  let max = open, min = open
  for (const c of candles) {
    if (c.h > max) max = c.h
    if (c.l < min) min = c.l
  }
  const aboveRange = max - open
  const belowRange = open - min

  const oy = Math.max(0, Math.min(height, openY))
  const aboveH = oy
  const belowH = height - oy

  const yFor = (p) => {
    if (p > open && aboveRange > 0) return oy - ((p - open) / aboveRange) * aboveH
    if (p < open && belowRange > 0) return oy + ((open - p) / belowRange) * belowH
    return oy
  }

  const stepX = width / candles.length
  const bodyW = Math.max(1.2, Math.min(stepX * 0.65, 6))

  return (
    <svg
      className="jup-scan-spark-bg"
      style={{ left, top, width, height }}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {candles.map((c, i) => {
        const cx = i * stepX + stepX / 2
        const yH = yFor(c.h)
        const yL = yFor(c.l)
        const yO = yFor(c.o)
        const yC = yFor(c.c)
        const bull = c.c >= c.o
        const bodyTop = Math.min(yO, yC)
        const bodyH = Math.max(1, Math.abs(yC - yO))
        const color = bull ? '#b0b0b0' : '#555'
        return (
          <g key={i}>
            <line x1={cx} y1={yH} x2={cx} y2={yL} stroke={color} strokeWidth="0.8" />
            <rect x={cx - bodyW / 2} y={bodyTop} width={bodyW} height={bodyH} fill={color} />
          </g>
        )
      })}
    </svg>
  )
})

// ─── Plain-text token info line ───
// Mirror of the WatchlistCard/scan "story" construction, flattened to a
// string for the chart watermark sub-line (shown when the charted token has
// no Metaplex description). Same fields, same thresholds.
function buildTokenInfoText(t) {
  if (!t) return null
  const created = t.firstPool?.createdAt
  let ageStr = null
  if (created) {
    const ms = Date.now() - new Date(created).getTime()
    if (ms > 0) {
      const h = ms / 3600000, d = h / 24
      if (h < 1)        ageStr = `${Math.max(1, Math.round(ms / 60000))}m`
      else if (h < 24)  ageStr = `${Math.floor(h)}h`
      else if (d < 30)  ageStr = `${Math.floor(d)}d`
      else if (d < 365) ageStr = `${Math.floor(d / 30)}mo`
      else              ageStr = `${Math.floor(d / 365)}y`
    }
  }
  const n = t.mcap || t.fdv
  const mcapStr = !n ? null
    : n >= 1e9 ? `$${(n / 1e9).toFixed(2)}B`
    : n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M`
    : n >= 1e3 ? `$${(n / 1e3).toFixed(0)}K`
    : `$${Math.round(n)}`
  const hc = t.holderCount
  const hldrsStr = !hc ? null
    : hc >= 1e6 ? `${(hc / 1e6).toFixed(1)}M`
    : hc >= 1e3 ? `${(hc / 1e3).toFixed(1)}K`
    : `${hc}`
  const lp = t.launchpad
  const ch24 = t.stats24h?.priceChange

  const parts = []
  if (lp && ageStr)      parts.push(`${ageStr}-old ${lp} launch.`)
  else if (ageStr)       parts.push(`Launched ${ageStr} ago.`)
  else if (lp)           parts.push(`${lp} launch.`)
  if (mcapStr && hldrsStr) parts.push(`${mcapStr} mcap, ${hldrsStr} holders.`)
  else if (mcapStr)        parts.push(`${mcapStr} mcap.`)
  else if (hldrsStr)       parts.push(`${hldrsStr} holders.`)
  if (typeof ch24 === 'number') {
    if (ch24 >= 50)       parts.push(`Up ${Math.round(ch24)}% in 24h.`)
    else if (ch24 <= -30) parts.push(`Down ${Math.round(Math.abs(ch24))}% in 24h.`)
  }
  return parts.length > 0 ? parts.join(' ') : null
}

// ============================================
//
// Same Jupiter-card layout as LiveScanCard, but driven by data persisted
// in the watchlist entry rather than a live WS stream. Renders a
// MiniCandleSpark off the dashed divider when the entry has cached OHLC
// chart data. Also draws the absolute-positioned `×` remove button.
//
// Layout measurement is identical to LiveScanCard's so the spark sits in
// exactly the same screen region; only the data path differs.
const WatchlistCard = memo(function WatchlistCard({ entry, index, onSelect, onRemove, description }) {
  const SPARK_INSET_LEFT   = 8
  const SPARK_INSET_RIGHT  = 40
  const SPARK_INSET_TOP    = 2
  const SPARK_INSET_BOTTOM = 6

  const cardRef  = useRef(null)
  const nameRef  = useRef(null)
  const storyRef = useRef(null)
  const headRef  = useRef(null)
  const [geo, setGeo] = useState(null)

  useLayoutEffect(() => {
    const card  = cardRef.current
    const story = storyRef.current
    const head  = headRef.current
    if (!card) return

    const cardRect = card.getBoundingClientRect()
    const sparkLeft = cardRect.width / 2

    const sparkTop = head
      ? (head.getBoundingClientRect().bottom - cardRect.top) + SPARK_INSET_TOP
      : 32

    let dividerY
    if (story) {
      dividerY = story.getBoundingClientRect().top - cardRect.top
    } else {
      dividerY = sparkTop + 18
    }

    const sparkHeight = cardRect.height - sparkTop - SPARK_INSET_BOTTOM
    const sparkWidth  = cardRect.width - sparkLeft - SPARK_INSET_RIGHT
    const openY       = dividerY - sparkTop

    setGeo({
      left:   sparkLeft,
      top:    sparkTop,
      width:  Math.max(0, sparkWidth),
      height: Math.max(0, sparkHeight),
      openY,
    })
  }, [entry.address, entry.symbol, entry.name, description, entry.chart])

  // Data extraction — same fields as the Jupiter scan tab uses for tokens/v2.
  const t      = entry
  const addr   = t.address
  const score  = t.organicScore || 0
  const tier   = score >= 70 ? 'tier-high' : score >= 40 ? 'tier-mid' : 'tier-low'
  const ch24   = t.stats24h?.priceChange
  const usd    = t.usdPrice
  const name   = t.symbol || t.name || `${addr.slice(0, 6)}...`
  const candles = Array.isArray(t.chart) ? t.chart : null
  const hasSpark = candles && candles.length >= 1
  const hasOrganic = typeof t.organicScore === 'number'

  // Price label — micro-priced memecoin handling (subscript-zero notation).
  let priceLabel = '···'
  if (usd && usd > 0) {
    if (usd >= 1)         priceLabel = '$' + usd.toFixed(usd >= 100 ? 2 : 4)
    else if (usd >= 0.01) priceLabel = '$' + usd.toFixed(5)
    else if (usd >= 1e-4) priceLabel = '$' + usd.toFixed(7)
    else {
      const s = usd.toFixed(12)
      const m = s.match(/^0\.0+/)
      if (m) {
        const zeros = m[0].length - 2
        const rest  = s.slice(m[0].length).slice(0, 4).replace(/0+$/, '') || '0'
        const sub   = '\u2080\u2081\u2082\u2083\u2084\u2085\u2086\u2087\u2088\u2089'[zeros]
        priceLabel  = `$0.0${sub || `(${zeros})`}${rest}`
      } else {
        priceLabel = '$' + usd.toExponential(2)
      }
    }
  }

  // Story narrative — same construction as the scan tab.
  const fmtAge = (createdAt) => {
    if (!createdAt) return null
    const ms = Date.now() - new Date(createdAt).getTime()
    if (ms <= 0) return null
    const h = ms / 3600000, d = h / 24
    if (h < 1)        return `${Math.max(1, Math.round(ms / 60000))}m`
    if (h < 24)       return `${Math.round(h)}h`
    if (d < 30)       return `${Math.floor(d)}d`
    if (d < 365)      return `${Math.floor(d / 30)}mo`
    return `${Math.floor(d / 365)}y`
  }
  const fmtMcap = (n) => {
    if (!n) return null
    if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`
    if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`
    return `$${Math.round(n)}`
  }
  const fmtHldrs = (n) => {
    if (!n) return null
    if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`
    if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`
    return `${n}`
  }

  const ageStr   = fmtAge(t.firstPool?.createdAt)
  const mcapStr  = fmtMcap(t.mcap || t.fdv)
  const hldrsStr = fmtHldrs(t.holderCount)
  const lp       = t.launchpad
  const tags     = Array.isArray(t.tags) ? t.tags : []
  const isVerified = tags.includes('verified')

  const sentences = []
  if (lp && ageStr)      sentences.push(<><strong>{ageStr}</strong>-old <strong>{lp}</strong> launch.</>)
  else if (ageStr)       sentences.push(<>Launched <strong>{ageStr}</strong> ago.</>)
  else if (lp)           sentences.push(<><strong>{lp}</strong> launch.</>)

  if (mcapStr && hldrsStr) sentences.push(<><strong>{mcapStr}</strong> mcap, <strong>{hldrsStr}</strong> holders.</>)
  else if (mcapStr)        sentences.push(<><strong>{mcapStr}</strong> mcap.</>)
  else if (hldrsStr)       sentences.push(<><strong>{hldrsStr}</strong> holders.</>)

  if (typeof ch24 === 'number') {
    if (ch24 >= 50)       sentences.push(<><span className="up">Up {Math.round(ch24)}%</span> in 24h.</>)
    else if (ch24 <= -30) sentences.push(<><span className="down">Down {Math.round(Math.abs(ch24))}%</span> in 24h.</>)
  }

  const badges = []
  if (isVerified)                          badges.push({ key: 'verif',  label: '✓ Verified', tone: 'info' })
  if (t.audit?.mintAuthorityDisabled)      badges.push({ key: 'mint',   label: '✓ Mint',     tone: 'good' })
  if (t.audit?.freezeAuthorityDisabled)    badges.push({ key: 'freeze', label: '✓ Freeze',   tone: 'good' })
  if (t.audit?.topHoldersPercentage > 30) {
    badges.push({ key: 'top', label: `${Math.round(t.audit.topHoldersPercentage)}% top`, tone: 'warn' })
  }

  const showDesc  = !!description
  const showStory = !showDesc && sentences.length > 0

  return (
    <div
      ref={cardRef}
      className={`jup-scan-row ${hasOrganic ? tier : ''}`}
      onClick={() => onSelect(addr)}
      title={addr}
      style={{ position: 'relative' }}
    >
      {/* Absolute-positioned remove button — fades in on row hover */}
      <button
        className="watchlist-card-remove"
        title="Remove from watchlist"
        onClick={(e) => { e.stopPropagation(); onRemove(addr) }}
      >×</button>

      {/* OHLC spark — pivoted on the structural dashed divider via geo measurement */}
      {hasSpark && geo && (
        <MiniCandleSpark
          candles={candles}
          left={geo.left}
          top={geo.top}
          width={geo.width}
          height={geo.height}
          openY={geo.openY}
        />
      )}

      <div className="jup-scan-head" ref={headRef}>
        <span className="jup-scan-rank">{index + 1}</span>
        {(t.icon || t.logoURI)
          ? <img className="jup-scan-logo" src={t.icon || t.logoURI} alt="" loading="lazy" onError={(e) => { e.target.style.visibility = 'hidden' }} />
          : <div className="jup-scan-logo jup-scan-logo-placeholder" />}
        <span className="jup-scan-name"><span ref={nameRef}>{name}</span></span>
        {hasOrganic && (
          <div className={`jup-scan-organic ${tier}`}>
            <div className="jup-scan-organic-value">{Math.round(score)}</div>
            <div className="jup-scan-organic-label">ORG</div>
          </div>
        )}
      </div>
      <div className="jup-scan-price-row">
        <span className={`jup-scan-price ${usd ? '' : 'pending'}`}>{priceLabel}</span>
        {typeof ch24 === 'number' && ch24 !== 0 && (
          <span className={`jup-scan-change ${ch24 >= 0 ? 'up' : 'down'}`}>
            {ch24 >= 0 ? '+' : ''}{ch24.toFixed(2)}%
          </span>
        )}
      </div>
      {(showDesc || showStory || badges.length > 0) && (
        <div className="jup-scan-story" ref={storyRef}>
          {showDesc && (
            <div className="jup-scan-desc" title={description}>{description}</div>
          )}
          {showStory && (
            <div className="jup-scan-story-text">
              {sentences.map((s, k) => (
                <span key={k}>{k > 0 && ' '}{s}</span>
              ))}
            </div>
          )}
          {badges.length > 0 && (
            <div className="jup-scan-badges">
              {badges.map(b => (
                <span key={b.key} className={`jup-scan-badge ${b.tone}`}>{b.label}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
})


// Base58 Solana mint detector for chat — renders matches as clickable chips.

// VolumeProfileBars (DOM) — REMOVED. The volume profile now paints onto a
// dedicated canvas inside drawCanvas (search "Volume profile layer"). The
// memo here was a no-op against data ticks — the profile Map's identity
// changes per tick, so hundreds of absolutely-positioned divs re-did
// style+layout+paint at WS cadence, and it was the last hundreds-of-nodes
// element in the rebucket cascade (HANDOFF §10).

// ============================================
// FootprintChart Component
// (scanCandlesMcapRange / stretchRangeForHighlight / bucketRowIndex moved
// to utils/chartGeometry.js — pure geometry, imported above.)

// ============================================
// Cross-chart crosshair sync. The vertical (time) line is mirrored between
// chart instances by broadcasting the hovered candle's startTime — NOT a
// pixel x, since each chart has its own zoom/scroll and the same x lands on
// different candles. Each chart projects the broadcast time back to its own
// x via its own viewport math. Module-level (not React state/context) so it
// costs nothing per frame and needs no shared parent: a tiny pub/sub keyed
// by an emitter id so a chart ignores its own echo.
// Primary input device is coarse (finger) — hardware truth, independent of how
// the browser labels individual events. Samsung Internet / Chromium desktop-site
// mode can report touch as pointerType 'mouse'; this doesn't lie. Touchscreen
// laptops (primary = mouse/trackpad) evaluate false, so desktop is unaffected.
const COARSE_POINTER = typeof window !== 'undefined' &&
  !!window.matchMedia?.('(pointer: coarse)')?.matches

// ─── Volume-bar DOM layer (memoized) ───
// This was inline JSX in the main component: every React commit re-diffed up
// to `visibleCount` divs (~400 at a footer-expanded window) even when the
// commit had nothing to do with volume (menu opens, hover ids, drag flags…).
// As a memo child it re-renders only when the candle slice, bar width, count,
// or scale actually change — data ticks still update it, chrome commits don't.
const VolumeBarsLayer = memo(function VolumeBarsLayer({ visibleCandles, visibleCount, pxPerCandle, maxTotalVolume }) {
  return (
    <div className="volume-layer">
      {Array.from({ length: visibleCount }).map((_, i) => {
        const candle = visibleCandles[i]
        if (!candle) return (<div key={`ghost-${i}`} className="volume-bar-cell" style={{ width: `${pxPerCandle}px`, minWidth: `${pxPerCandle}px` }} />)
        const totalVol = candleTotalVolume(candle)
        const barHeight = maxTotalVolume > 0 ? Math.min(120, (totalVol / maxTotalVolume) * 120) : 0
        return (<div key={candle.startTime} className="volume-bar-cell" style={{ width: `${pxPerCandle}px`, minWidth: `${pxPerCandle}px` }}><div className="volume-bar" style={{ height: `${barHeight}px` }} /></div>)
      })}
    </div>
  )
})

const crosshairBus = {
  listeners: new Set(),
  emit(payload) { for (const fn of this.listeners) fn(payload) },
  subscribe(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn) },
}

// ─── Wallet badge ───
// A filled circle carrying the selected wallet's TOTAL volume at this cell,
// with its print count as a shallow card-stack behind it. Number = how much,
// depth = how many; the two compose (12 over three cards = three prints
// averaging 4) and neither claims anything about the aggregate cell it sits
// beside. Deck capped at 5 — past that the offset stack is noise at cell
// scale and the count lives in the tracker's tooltip. Text is dark-on-colour
// so it reads on both the green and the red at the 8–16px cell font.
const BADGE_MAX_CARDS = 5
// The badge uses formatCellVol — THE CELL'S OWN FORMATTER, not one of its
// own. `amount` is the token amount, so cell values run to millions, and
// formatCellVol renders a bare mantissa: 1,500,000 -> "2", 938,000 -> "938".
// A badge with independent notation printed "99k" next to a cell reading
// "938" — two numbers describing the same trades in units that cannot be
// compared, which is the one thing this badge exists to let you do. Sharing
// the formatter makes them comparable by construction rather than by my
// remembering to keep two functions in step.
// PILL, not circle, MEASURED to its label — sizing to content is the only
// shape that cannot lie about its own number. `side` anchors it at the
// column edge and grows it OUTWARD, so a wide pill can never reach into the
// neighbouring candle; the old centre-anchored placement did exactly that
// on narrow candles.
function drawWalletBadge(ctx, xEdge, y, r, vol, n, color, fontPx, side, exact) {
  const label = exact ? formatCellVolExact(vol) : formatCellVol(vol)
  ctx.save()
  setCtxFont(ctx, `500 ${fontPx}px "JetBrains Mono", monospace`)   // (perf3)
  const w = Math.max(r * 2, ctx.measureText(label).width + r)
  const h = r * 2
  const x = side > 0 ? xEdge : xEdge - w
  const rad = Math.min(h / 2, w / 2)
  const cards = Math.min(BADGE_MAX_CARDS, Math.max(1, n | 0))
  // Stroked, not just filled. Same-colour pills overlapping at 1.5px offsets
  // merge into one blob and the stack stops being readable as a count — the
  // dark outline is what separates card from card. Drawn per pill rather
  // than once around the group so every edge in the stack is visible.
  const pill = (px, py, alpha) => {
    ctx.beginPath()
    if (ctx.roundRect) ctx.roundRect(px, py, w, h, rad)
    else {
      ctx.moveTo(px + rad, py)
      ctx.arcTo(px + w, py, px + w, py + h, rad); ctx.arcTo(px + w, py + h, px, py + h, rad)
      ctx.arcTo(px, py + h, px, py, rad);         ctx.arcTo(px, py, px + w, py, rad)
    }
    ctx.globalAlpha = alpha
    ctx.fillStyle = color
    ctx.fill()
    ctx.globalAlpha = Math.min(1, alpha + 0.35)
    ctx.strokeStyle = '#000'
    ctx.lineWidth = 1
    ctx.stroke()
  }
  // Back to front, so each card's outline is overdrawn by the one in front
  // and the deck reads as depth rather than as concentric rings.
  for (let k = cards - 1; k >= 1; k--) pill(x + side * k * 1.5, y - h / 2 - k * 1.5, 0.45)
  pill(x, y - h / 2, 1)
  ctx.globalAlpha = 1
  ctx.fillStyle = '#0b0b0b'
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
  ctx.fillText(label, x + w / 2, y + 0.5)
  ctx.restore()
}



// ── Window + range → bucket (one mapping) ─────────────────────────────
// Sizer and wheel DEST both call this. No second ladder loop.
// computeRangeBuffer now lives in the Y machine and is imported above: the
// camera and the sizer cannot disagree about the buffer if there is one copy.

function rowHeightForStep(dataRange, zoom, heightPx, step) {
  const half = (dataRange / 2) * zoom + computeRangeBuffer(dataRange, step)
  return (heightPx * step) / Math.max(1e-12, 2 * half)
}

/** Nearest ladder rung for a price span. Returns commit value (ratio or $ step). */
// Tick-quantized instruments (CME: every trade prints on a 0.25 lattice).
// The generic ladder has rungs like 0.3 / 0.35 / 0.45 that are NOT whole
// ticks: a 0.3 row holds one tick here and two ticks there, so volume groups
// unevenly, labels read 7649.3, and refine (tick·2^k) vs ladder rungs flip the
// lattice under a Y drag (the "expand contracts"). On a tick instrument every
// rung must be a whole number of ticks.
const TICK_LADDER_MULTS = [1, 2, 3, 4, 5, 6, 8]
const _tickLadderCache = new Map()
function tickLadder(tick) {
  let L = _tickLadderCache.get(tick)
  if (!L) {
    L = []
    for (let e = 0; e <= 6; e++) for (const m of TICK_LADDER_MULTS) L.push(Math.round(tick * m * Math.pow(10, e) * 1e8) / 1e8)
    _tickLadderCache.set(tick, L)
  }
  return L
}
function isTickMultiple(step, tick) {
  if (!(tick > 0)) return true
  const k = step / tick
  return k >= 1 - 1e-9 && Math.abs(k - Math.round(k)) < 1e-6
}

function pickBucketForRange({ dataRange, zoom, heightPx, targetRowH, log, tick = 0 }) {
  if (!(dataRange > 0) || !(heightPx > 0) || !(targetRowH > 0)) return null
  const LADDER = log ? MCAP_RATIO_STEPS.map(r => Math.log(r)) : (tick > 0 ? tickLadder(tick) : MCAP_BUCKET_STEPS)
  let best = null, bestDist = Infinity
  for (let i = 0; i < LADDER.length; i++) {
    const S = LADDER[i]
    const rh = rowHeightForStep(dataRange, zoom, heightPx, S)
    const dist = Math.abs(rh - targetRowH)
    if (dist < bestDist) { bestDist = dist; best = S }
  }
  if (best == null) return null
  return log ? snapRatio(Math.exp(best)) : best
}

function FootprintChart({
  data,
  dataSource,
  chartId = 'main',   // slot identity — scopes per-slot+mode settings below
  // Bottom ticker feed — supplied by App, sourced from ScanPanel while its
  // Ticker toggle is on; null/[] otherwise (ticker hidden, zero re-renders
  // from scan updates).
  tickerTokens = null,
  // Focus system: false on the unarmed chart — dims the canvas-drawn
  // current-price band to neutral (the DOM chip dims via CSS; the band
  // is canvas paint, so it needs to know).
  isActiveChart = true,
  setDataSource,
  hlPair,
  setHlPair,
  timeInterval,
  setTimeInterval,
  mcapBucketSize,
  setMcapBucketSize,
  // chartMode/bucketDensity now come from the slot (per-chart). Suffixed Prop
  // so the component can fall back to internal state when used standalone.
  chartMode: chartModeProp,
  setChartMode: setChartModeProp,
  bucketDensity: bucketDensityProp,
  setBucketDensity: setBucketDensityProp,
  // Dual-chart-in-one-cell header:
  //  headerless    — render WITHOUT the chart-rail (the non-primary chart).
  //  secondChart   — when present on the primary chart, the rail grows a 2nd
  //                  symbol chip for the other chart; the inactive chip is
  //                  grayed and clicking it arms that chart.
  //  headerControls— bindings the shared rail controls (timeframe/type/density)
  //                  drive: the ACTIVE slot's setters, so the rail acts on
  //                  whichever chart is armed regardless of which instance owns
  //                  the rail.
  headerless = false,
  secondChart = null,
  headerControls = null,
  // Dual-chart-in-one-cell switching (injected by Workspace via cloneElement):
  //  coLocated  — true when chart + chart2 share a cell. Only then does the
  //               primary chart show the SECOND dropdown.
  //  cellSwitch — (panelKey) => set which chart is the visible one in the cell
  //               (Workspace node.active). The dropdowns are the switcher, so a
  //               chip click both makes its chart visible AND arms it.
  coLocated = false,
  cellSwitch = null,
  autoScrollEnabled,
  setAutoScrollEnabled,
  // X: consume ONLY through xWin when present. Loose props = null-xWin fallback
  // (tests / dual path) so the chart does not crash without a slot handle.
  xWin = null,
  viewWindowSize: viewWindowSizeProp,
  setViewWindowSize: setViewWindowSizeProp,
  viewEndIndex: viewEndIndexProp,
  setViewEndIndex: setViewEndIndexRawProp,
  viewEndIndexRef: viewEndIndexRefProp = null,
  commitEnd: commitEndProp = null,
  commitWindow: commitWindowProp = null,
  bodyScrollRef,
  viewWindowSizeRef: viewWindowSizeRefProp,
  timeIntervalRef,
  publicKeyRef,
  hlWallet,
  publicKey,
  setTokenInput,
  onHistorySelect,
  theme,
  themeId,
  setThemeId,
  addMessage,
  tradeFilterMin,
  tokenWatchlist,
  setTokenWatchlist,
}) {
  const { defaults } = useTradeDefaults()
  const trading = useTradingContext()

  // Prefer slot xWin when present — one X owner. Loose props only if xWin is null.
  // (xref) GLIDE WINDOW, HELD LOCALLY. The slot's windowSize is App state, and
  // the X glide used to commit it on every integer candle step — so every step
  // re-rendered all of App. During a glide the per-step int now lives HERE
  // (glideWin): this chart re-renders exactly as before (the fit memo /
  // lattice / visibleCandles must see each step — drawCanvas paints the window
  // the lattice was fit for), but App does not. flushGlideWin commits the
  // value to the slot at landing / abort. Any direct setViewWindowSize (TF /
  // token reset, footer or axis reset, desktop pinch) drops the override first,
  // so a discrete write is never shadowed. Refs (viewWindowSizeRef, xWin.snap)
  // are still written every frame by commitWindow, as before.
  const [glideWin, setGlideWin] = useState(null)
  const glideWinRef = useRef(null)
  const slotWindowSize = xWin ? xWin.windowSize : viewWindowSizeProp
  const slotWindowSizeRef = useRef(slotWindowSize)
  slotWindowSizeRef.current = slotWindowSize
  const viewWindowSize = glideWin != null ? glideWin : slotWindowSize
  const setViewWindowSizeBase = (xWin && xWin.setWindowSize) || setViewWindowSizeProp
  const setViewWindowSizeBaseRef = useRef(setViewWindowSizeBase)
  setViewWindowSizeBaseRef.current = setViewWindowSizeBase
  // Stable (was the slot's useState setter, also stable).
  const setViewWindowSize = useCallback((v) => {
    if (glideWinRef.current != null) { glideWinRef.current = null; setGlideWin(null) }
    const f = setViewWindowSizeBaseRef.current
    if (typeof f === 'function') f(v)
  }, [])
  // Glide frame: hold the committed int locally (only when it changes).
  const setGlideWinLive = useCallback((w) => {
    const cur = glideWinRef.current != null ? glideWinRef.current : slotWindowSizeRef.current
    if (w === cur) return
    glideWinRef.current = w
    // (xflash) Same as Y rows-commit: lattice / header / viewportInfo must
    // see this window BEFORE FRAME_DRAW, or the X camera stretches the wrong
    // mesh for a frame (flash / double look).
    flushSync(() => setGlideWin(w))
  }, [])
  // Land / abort: one slot commit, override released in the same batch.
  const flushGlideWin = useCallback(() => {
    const w = glideWinRef.current
    if (w == null) return
    glideWinRef.current = null
    const f = setViewWindowSizeBaseRef.current
    if (typeof f === 'function') f(prev => (prev === w ? prev : w))
    setGlideWin(null)
  }, [])
  const viewEndIndex = xWin ? xWin.endIndex : viewEndIndexProp
  const setViewEndIndexRaw = (xWin && xWin.setEndIndex) || setViewEndIndexRawProp
  const viewWindowSizeRef = (xWin && xWin.windowSizeRef) || viewWindowSizeRefProp
  const commitEnd = (xWin && xWin.commitEnd) || commitEndProp
  const commitWindow = (xWin && xWin.commitWindow) || commitWindowProp

  // --- Viewport / UI state (local only) ---
  // (yexplore) No Y zoom / Y pan state. The Y camera (useYCamera) owns the
  // user span and the explore centre in refs; a React pass is requested via
  // yFitTick only when the lattice rows actually change.
  // ── LOG PRICE SCALE ──
  // One global setting, persisted. A geometric lattice: each row is a
  // constant RATIO of price rather than a constant amount, so a memecoin that
  // runs 40k → 900k gets rows that mean the same thing at both ends, and
  // dataRange — which in linear terms swings with every extreme that scrolls
  // into view — becomes stable in ln-space. See the memo for the mechanism:
  // log scale IS linear scale on ln(price), which is why almost nothing below
  // the input transform had to change.
  //
  // ONE setting, not per token: stays wherever you last switched it, across
  // tokens and reloads. Toggle from the console: __cheddarLogScale(true|false).
  const LOG_SCALE_KEY = 'solcheddar:logScaleOn'
  const [logScale, setLogScale] = useState(() => {
    try {
      localStorage.removeItem('solcheddar:logScale')   // retired per-token map
      const v = localStorage.getItem(LOG_SCALE_KEY)
      return v == null ? true : v === '1'
    } catch { return true }
  })
  const logScaleRef = useRef(logScale)
  logScaleRef.current = logScale
  // The sizer's committed row RATIO when log is on — the geometric counterpart
  // of mcapBucketSize, kept SEPARATE so nothing that reads mcapBucketSize as a
  // price (pickStorageBucket, the cell budget, snapBucket) ever sees a 1.01.
  const [mcapRatio, setMcapRatio] = useState(1.01)
  const mcapRatioRef = useRef(1.01)
  mcapRatioRef.current = mcapRatio

  const dataSourceRef = useRef(dataSource)
  useEffect(() => {
    dataSourceRef.current = dataSource
    window.dispatchEvent(new CustomEvent('solcheddar-datasource', { detail: { dataSource } }))
  }, [dataSource])
  // Format Y-axis: price for futures, mcap for Solana
  // Solana axis uses its OWN formatter, not the shared formatMcap (which
  // still serves alerts, the journal, swing labels and the order modal at
  // full precision). formatMcap is fixed-2 in every tier, so a scam mint at
  // 1.7e13 renders "17001.34B" — six digits of which two are noise, and the
  // axis column goes wide to hold them.
  //
  // Precision scales to magnitude instead of dropping outright: at >=100
  // units the decimals cannot disambiguate adjacent rows so they go; below
  // 10 they are load-bearing. Removing them unconditionally would let 6000
  // and 6050 BOTH print "6K" — an axis asserting two different rows are the
  // same number, which is the kind of false claim we don't ship.
  const formatMcapAxis = useCallback((val) => {
    const abs = Math.abs(val)
    const scaled = (v, unit, suffix) => {
      const n = v / unit
      const d = Math.abs(n) >= 100 ? 0 : Math.abs(n) >= 10 ? 1 : 2
      return n.toFixed(d) + suffix
    }
    if (abs >= 1e9) return scaled(val, 1e9, 'B')
    if (abs >= 1e6) return scaled(val, 1e6, 'M')
    if (abs >= 1e3) return scaled(val, 1e3, 'K')
    return val.toFixed(0)
  }, [])
  const formatYAxis = useCallback((val, rowStep = null) => {
    return dataSource === 'hyperliquid' ? formatPrice(val, rowStep) : formatMcapAxis(val)
  }, [dataSource, formatMcapAxis])

  // --- Swing detection settings ---
  // ─── Quick-trade rail (beaker-toggled) ───
  // One-click market Buy/Sell + a SOL-qty input living in THIS chart's rail.
  // Per-chart by the armed-chart convention: clicking anything in a chart
  // arms it before onClick runs, and executeMarketOrder trades the armed
  // context's token/pair. Sol buys pass the input via buyAmountSol; sells
  // use the default sell%; FUTURES sizing stays on the futures defaults by
  // executeMarketOrder's own design (no per-order fut override exists), so
  // ─── Per-slot, per-mode indicator settings ───
  // Four namespaces: (slot main|B) x (mode sol|fut). A setting saved on
  // chart 1 futures touches nothing else — each chart carries its own
  // indicator list per mode. Keys become `<base>.<slot>.<mode>`; the first
  // read falls back to the legacy unscoped key so nobody's current setup
  // resets, after which the scoped key owns it. Values are JSON (legacy raw
  // strings parse through the catch path: '1'->1 truthy, '0.9'->0.9).
  // Switching a slot's mode re-reads that scope's values live.
  const settingsScope = `${chartId}.${dataSource === 'hyperliquid' ? 'fut' : 'sol'}`
  // tpohide-20261103: single gate - in TPO mode indicator toggles read as off
  // (render only; stored values untouched, so switching back restores them).
  const _tpoHide = (chartModeProp ?? (() => { try { return localStorage.getItem('solcheddar_chart_mode') } catch { return null } })()) === 'tpo'
  const useScopedSetting = (base, def) => {
    const key = `${base}.${settingsScope}`
    const read = () => {
      try {
        const s = localStorage.getItem(key)
        if (s != null) { try { return JSON.parse(s) } catch { return s } }
        const legacy = localStorage.getItem(base)
        if (legacy != null) { try { return JSON.parse(legacy) } catch { return legacy } }
      } catch {}
      return def
    }
    const [val, setVal] = useState(read)
    const keyRef = useRef(key)
    useEffect(() => {
      if (keyRef.current === key) return
      keyRef.current = key
      setVal(read())
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key])
    const set = useCallback((v) => {
      setVal(prev => {
        const nv = typeof v === 'function' ? v(prev) : v
        try { localStorage.setItem(key, JSON.stringify(nv)) } catch {}
        return nv
      })
    }, [key])
    if (_tpoHide && TPO_HIDE_KEYS.has(base)) return [base === 'solcheddar:maConfigs' ? (Array.isArray(val) ? val.map(c => ({ ...c, on: false })) : []) : false, set]
    return [val, set]
  }

  // Scoped AFTER the hook exists — see the wake-reconnect TDZ scar.
  const [showSwings, setShowSwings] = useScopedSetting('solcheddar:showSwings', true)
  // the qty input hides in futures mode rather than lie about applying.
  const [showQuickTrade, setShowQuickTrade] = useScopedSetting('cheddar_show_quicktrade', false)
  const [quickQty, setQuickQty] = useScopedSetting('cheddar_quicktrade_sol', '0.5')
  // ─── L2 liquidity heatmap (beaker-toggled, futures only) ───
  // Store events, derive views: the ring holds raw book snapshots
  // ({ts,b,a}, time-ascending); the draw pass derives per-column heat at
  // render, so TF changes, pans, and bucket changes need no refold. No
  // React state per frame — refs only; a 1Hz tick repaints quiet tape.
  const [showBookHeat, setShowBookHeat] = useScopedSetting('cheddar_show_bookheat', false)
  // (cmeheat) HL book heat means nothing on a CME chart: never fetched/drawn there; the saved toggle is untouched.
  const _bookHeatCme = dataSource === 'hyperliquid' && !!cmeInfo(hlPair?.base)
  // cmeexact-20261102: CME cells/profile/badges print exact contract counts.
  const cmeExactRef = useRef(false)
  cmeExactRef.current = _bookHeatCme
  const _fcv = (v) => (cmeExactRef.current ? formatCellVolExact(v) : formatCellVol(v))
  // (cmeheat2) Heat sources per market. A future CME book feed slots in as a
  // new key here (and in the Heatmap menu) without touching the crypto path.
  const heatSrc = _bookHeatCme ? { hl: false, cb: false } : { hl: true, cb: true }
  const anyHeatSource = heatSrc.hl || heatSrc.cb
  const showBookHeatLive = showBookHeat && heatSrc.hl
  // Coinbase liquidity: SEPARATE stream, separate toggle, drawn AMBER so
  // whose size it is stays legible. Normalizes off its OWN cbMaxSzRef
  // (max-based, untouched by the HL percentile change) — the two books'
  // brightness scales are independent by design.
  const [showCbHeat, setShowCbHeat] = useScopedSetting('cheddar_show_cbheat', false)
  const showCbHeatLive = showCbHeat && heatSrc.cb
  const cbSnapsRef = useRef([])
  // Per-book normalization: HL perp sizes dwarf CB spot top-20, so a SHARED
  // max squashed every amber level to ~0.06 alpha — painted, invisible.
  // Each book scales against its own session max; the slider window applies
  // to each book's own range, which is the comparison that's actually legible.
  const cbMaxSzRef = useRef(1)
  // The HL book data slice — WS, ring, ingest, walls, flow, slot fold —
  // lives in utils/hlBookFeed now (one owner; see its header for why two
  // folds remain and why they are different jobs, not duplication). One
  // book PER CHART INSTANCE: workspace slots can watch different coins.
  const hlBookRef = useRef(null)
  if (!hlBookRef.current) hlBookRef.current = createHlBook()
  // Sensitivity window (dual slider in the beaker menu): fractions of the
  // session-max LOG range. Below lo -> not drawn; lo..hi -> blue->white-hot
  // ramp; above hi -> saturated white. Draw reads the REFS (no re-render
  // per drag frame); state exists for the slider UI + persistence.
  const [heatLo, setHeatLo] = useScopedSetting('cheddar_heat_lo', 0)   // default: FULL range; the slider is the filter
  const [heatHi, setHeatHi] = useScopedSetting('cheddar_heat_hi', 1.0)
  // Extend the CURRENT book forward N columns into the future-padding
  // region: where today's walls sit projected ahead of price. 0 = off.
  const [heatExtend, setHeatExtend] = useScopedSetting('cheddar_heat_extend', 0)
  const [showHeatValues, setShowHeatValues] = useScopedSetting('cheddar_heat_values', false)
  const showHeatValuesRef = useRef(false)
  useLayoutEffect(() => { showHeatValuesRef.current = !!showHeatValues }, [showHeatValues])
  // Adaptive significance (sigma / persistence) runs always in the heat
  // pass — the old cheddar_heat_auto scoped toggle had no reader left.
  // ─── Heat render-side refs (restored) ───
  // The Phase-2 ref cleanup's slice took these along with its six intended
  // targets; the residue sweep only checked the names being REMOVED, and
  // esbuild doesn't flag unresolved identifiers, so it shipped as a runtime
  // ReferenceError. The sweep is now completeness-based: every heat/cb/book
  // identifier used in code must have a declaration.
  //
  // Display normalizer: EMA-smoothed p25/p97 of drawn row totals in log
  // space, published by the main pass one frame behind, read by BOTH the
  // canvas pass and the flow layer so seam brightness stays continuous.
  // span 0 = UNSEEDED: the first fold SNAPS (no glide-from-dark fade-in);
  // EMA breathes only after the seed. Render-side stat — deliberately NOT
  // in hlBookFeed: it is a property of what this component draws.
  const heatNormRef = useRef({ lo: 0, span: 0 })
  // Draw-coupled hook: FPCC calls the flow layer's paint at the tail of
  // every full draw so zoom/pan and the layer land in the same frame.
  const heatLayerPaintRef = useRef(null)
  const trailLayerPaintRef = useRef(null)
  // THE PAINTED GEOMETRY. What the last drawCanvas actually drew with --
  // not what the refs hold now. Every price->pixel consumer reads this so
  // a frozen canvas cannot be paired with a live row height. Empty until
  // the first paint; readers fall back to the live refs for that one frame.
  const paintedGeomRef = useRef({ mcaps: null, RH: 0, residualPx: 0 })
  // The frame: one geometry snapshot per pass. Written in drawCanvas.
  const lastFrameRef = useRef(null)
  // Header (time labels) and footer (delta) viewports. Their cells are laid
  // out by React; placeEdgeRows slides them by the painted frame's X.
  const headerVpRef = useRef(null)
  const footerVpRef = useRef(null)
  // ─── Trendlines ───
  // State, hit-testing, drag and painting all live in SRLineSettingsMenu
  // alongside the toolbar that styles them — a trendline is an S/R line
  // with two anchors, so they belong together. This file keeps only the
  // scoped setting, the hook, one draw call and three handler taps.
  // Keyed on the INSTRUMENT, not the chart slot: a trendline drawn on ETH
  // has no meaning on a memecoin, and useScopedSetting keys on
  // `chartId.mode` — so they used to follow you across every token. Same
  // pseudo-address the watchlist star uses, so futures pairs and Solana
  // mints share one identity scheme. The hook owns load/save and bounds
  // total storage (see the drawing store in SRLineSettingsMenu).
  const futWatchKey = dataSource === 'hyperliquid' && hlPair?.base ? `fut:${hlPair.base}` : null   // hoisted: the drawing token below needs it, and this evaluates immediately (not a deferred closure)
  const drawingToken = futWatchKey || data.currentToken || null
  const trend = useTrendlines(drawingToken, {
    getRect: () => canvasRef.current?.getBoundingClientRect() || null,
    redraw: () => drawCanvasRef.current?.(),
  })
  // Bid/ask colors. A book has two sides and merging them into one hue threw
  // away the most basic thing about a level: is it support or resistance.
  // The thermal ramp's editable stops, low -> high. Replaces the bid/ask
  // pair: the map is one continuous palette and side reads from the book's
  // shape, so the user-facing colors are the ramp LEVELS themselves.
  const HEAT_STOP_LABELS = ['empty', 'low', 'mid', 'high', 'wall', 'hot', 'max']
  const HEAT_RAMP_DEFAULT = ['#0a1428', '#1b3a6b', '#4a7fc9', '#cfe8ff', '#ffe066', '#ff8c1a', '#ff2d1a']
  const [heatRamp, setHeatRamp] = useScopedSetting('cheddar_heat_ramp', HEAT_RAMP_DEFAULT)
  const heatRampRef = useRef(HEAT_RAMP_DEFAULT)
  const [heatGamma, setHeatGamma] = useScopedSetting('cheddar_heat_gamma', 1)
  const heatGammaRef = useRef(1)
  useLayoutEffect(() => { heatGammaRef.current = Number.isFinite(heatGamma) ? heatGamma : 1 }, [heatGamma])
  useLayoutEffect(() => {
    heatRampRef.current = Array.isArray(heatRamp) && heatRamp.length === 7 ? heatRamp : HEAT_RAMP_DEFAULT
  }, [heatRamp])
  // How far back the FORMING column aggregates. 5 minutes deliberately: it
  // is the window the 5m chart already had, which read correctly — so every
  // timeframe now behaves at the live edge the way that one does. Closed
  // columns are unaffected.
  const HEAT_LIVE_MS = 300_000
  const heatExtendRef = useRef(0)
  useLayoutEffect(() => { heatExtendRef.current = Math.max(0, Math.min(40, Number(heatExtend) || 0)) }, [heatExtend])
  const heatLoRef = useRef(heatLo); const heatHiRef = useRef(heatHi)
  // Scope swaps (slot mode change) re-read heatLo/Hi; the draw reads REFS,
  // so mirror state -> refs whenever it changes for any reason.
  useEffect(() => { heatLoRef.current = heatLo; heatHiRef.current = heatHi }, [heatLo, heatHi])
  const setHeatRange = (lo, hi) => {
    lo = Math.max(0, Math.min(0.98, lo)); hi = Math.max(lo + 0.02, Math.min(1, hi))
    heatLoRef.current = lo; heatHiRef.current = hi
    setHeatLo(lo); setHeatHi(hi)   // scoped setters persist to this slot+mode
  }
  // (the .fp-heat-range CSS injector lived here — dead once the dual
  //  <input type=range> control was replaced by RangeSliderControl)
  const [, setHeatTick] = useState(0)
  useEffect(() => {
    const coin = hlPair?.base
    const active = showBookHeat && dataSource === 'hyperliquid' && coin && !cmeInfo(coin) && !stkInfo(coin)
    const book = hlBookRef.current
    // The percentile normalizer is a RENDER-side stat (fed by this
    // component's heat pass), so its reset stays here rather than moving
    // into the data module with everything else.
    if (!active) { book.watch(null); heatNormRef.current = { lo: 0, span: 0 }; return }
    book.watch(coin)
    heatNormRef.current = { lo: 0, span: 0 }
    const tick = setInterval(() => setHeatTick(t => t + 1), 1000)
    return () => {
      clearInterval(tick)
      book.watch(null)
      heatNormRef.current = { lo: 0, span: 0 }
    }
  }, [showBookHeat, dataSource, hlPair?.base])

  // Coinbase book stream — namespaced coin "CB:<BASE>" through the same
  // service/tunnel. Majors only in practice; unknown products just never
  // reply and the ring stays empty (server logs the CB error side).
  useEffect(() => {
    const coin = hlPair?.base
    if (!showCbHeat || dataSource !== 'hyperliquid' || !coin || cmeInfo(coin) || stkInfo(coin)) { cbSnapsRef.current = []; return }
    const key = `CB:${coin}`
    let ws = null, closed = false, retry = null
    const connect = () => {
      try { ws = new WebSocket(BOOK_WS_URL) } catch { retry = setTimeout(connect, 5000); return }
      ws.onopen = () => ws.send(JSON.stringify({ action: 'book_sub', coin: key, historyMin: 1440 }))
      ws.onmessage = (ev) => {
        try {
          const m = JSON.parse(ev.data)
          if (m.coin !== key) return
          const ing = (sn) => {
            const ring = cbSnapsRef.current
            ring.push(sn)
            if (ring.length > HEAT_RING_MAX) ring.splice(0, ring.length - HEAT_RING_MAX)
            let mx = cbMaxSzRef.current
            for (const [, sz] of sn.b) if (sz > mx) mx = sz
            for (const [, sz] of sn.a) if (sz > mx) mx = sz
            cbMaxSzRef.current = mx
          }
          if (m.type === 'book_history') {
            cbSnapsRef.current = []
            for (const sn of (m.snaps || [])) ing(sn)
            dbg(`[CbHeat] history: ${cbSnapsRef.current.length} snaps for ${coin}`)
          } else if (m.type === 'book') ing({ ts: m.ts, b: m.b, a: m.a })
        } catch {}
      }
      ws.onclose = () => { if (!closed) retry = setTimeout(connect, 5000) }
    }
    connect()
    return () => { closed = true; clearTimeout(retry); try { ws?.close() } catch {}; cbSnapsRef.current = [] }
  }, [showCbHeat, dataSource, hlPair?.base])
  const [qtPending, setQtPending] = useState(null)   // 'buy' | 'sell' | null
  // paperwallet-20261027: paper is now a wallet (the "+" in the wallet menu).
  // BUY/SELL route through executeMarketOrder, which simulates while it is active.
  const [qtPaper, setQtPaper] = useState(isPaperActive)
  const [paperTick, setPaperTick] = useState(0)
  useEffect(() => {
    const h = () => { setQtPaper(isPaperActive()); setPaperTick(n => n + 1) }
    window.addEventListener(PAPER_EVENT, h)
    return () => window.removeEventListener(PAPER_EVENT, h)
  }, [])
  const fireQuick = async (side) => {
    if (qtPending) return
    setQtPending(side)
    try {
      const opts = dataSource === 'hyperliquid'
        ? {}
        : (side === 'buy' ? { buyAmountSol: Math.max(0.001, parseFloat(quickQty) || 0) || undefined } : {})
      await trading.executeMarketOrder(side, opts)   // errors toast inside the context
    } catch {} finally { setQtPending(null) }
  }
  const [showATRStop, setShowATRStop] = useScopedSetting('solcheddar:showAtrStop', false)
  const [showChurnZones, setShowChurnZones] = useScopedSetting('solcheddar:showChurnZones', false)
  const showChurnZonesRef = useRef(false)
  const churnZonesRef = useRef([])
  const churnBgRef = useRef(null)   // { map: idx->colour, key } in Background mode
  const [churnCfgStored, setChurnCfg] = useScopedSetting('solcheddar:churnZonesConfig', {})
  const churnCfg = useMemo(() => normalizeChurnCfg(churnCfgStored), [churnCfgStored])
  const churnCfgRef = useRef(churnCfg)
  churnCfgRef.current = churnCfg
  const [churnSettings, setChurnSettings] = useState({ show: false, x: 0, y: 0, openLeft: false })
  // protraction-20261018: open -> wick extreme made against the close direction, per candle.
  const [showProtraction, setShowProtraction] = useScopedSetting('solcheddar:showProtraction', false)
  const showProtractionRef = useRef(false)
  showProtractionRef.current = !!showProtraction
  const [protCfgStored, setProtCfg] = useScopedSetting('solcheddar:protractionConfig', {})
  const protCfg = useMemo(() => normalizeProtractionCfg(protCfgStored), [protCfgStored])
  const protCfgRef = useRef(protCfg)
  protCfgRef.current = protCfg
  const protCacheRef = useRef({ key: '', map: new Map() })
  const [protSettings, setProtSettings] = useState({ show: false, x: 0, y: 0, openLeft: false })
  const [atrTrailCfgStored, setAtrTrailCfg] = useScopedSetting('solcheddar:atrTrailConfig', {})
  const atrTrailCfg = useMemo(() => ({ atrPeriod: 5, hhvPeriod: 10, mult: 2.5, bullCol: '#26de81', bearCol: '#fc5c65', weight: 2.5, ...(atrTrailCfgStored || {}) }), [atrTrailCfgStored])
  const [atrSettings, setAtrSettings] = useState({ show: false, x: 0, y: 0, openLeft: false })
  const atrStyleRef = useRef(atrTrailCfg)
  useLayoutEffect(() => { atrStyleRef.current = atrTrailCfg }, [atrTrailCfg])
  // ─── Moving Average overlay ───
  // Same per-chart/per-mode scoped storage as every other indicator config.
  // Stored as an ARRAY of configs so more MAs can be added later; ships
  // with one (id 'ma1'). Values computed in the draw pass from an
  // incremental cache (utils/movingAverage), hover lives in a ref.
  const [maConfigsStored, setMaConfigsStored] = useScopedSetting('solcheddar:maConfigs', MA_DEFAULTS)
  const maConfigs = useMemo(() => normalizeMaConfigs(maConfigsStored), [maConfigsStored])
  const maConfigsRef = useRef(maConfigs)
  maConfigsRef.current = maConfigs
  const updateMa = useCallback((id, patch) => {
    setMaConfigsStored(prev => normalizeMaConfigs(prev).map(c => (c.id === id ? { ...c, ...patch } : c)))
  }, [setMaConfigsStored])
  const [maSettings, setMaSettings] = useState({ show: false, x: 0, y: 0, openLeft: false, id: 'ma1' })
  const maCacheRef = useRef(new Map())     // id -> incremental cache
  const maPointsRef = useRef([])           // [{ id, points }] as last painted (canvas px)
  const maHoverIdRef = useRef(null)        // hovered MA id — ref only, no React state
  const maHoverPtRef = useRef(null)        // newest {clientX, clientY}
  const maHoverRafRef = useRef(0)
  const swingsRef     = useRef({ swingHighs: [], swingLows: [], structureEvents: [] })
  const showSwingsRef = useRef(true)
  const atrStopRef    = useRef([])
  const atrCacheRef   = useRef({ ver: -1, base: null })   // incremental ATR cache (see computeATRTrailingStopLive)
  const showATRStopRef = useRef(false)

  // ─── Draw-loop caches (B1 + B2) ──────────────────────────────────────
  // lastHistIdxCache: incrementally maintained "last historical candle"
  // index. The original scanned all candles every frame; this caches the
  // result and only re-scans when the candles array reference or length
  // changes. Backward scan from end means even invalidations are O(live)
  // not O(total), since historicals cluster at the front.
  // avwapAccumCache: per-AVWAP cumulative price-volume arrays. Closed
  // candles are immutable, so cumPVAt[i]/cumVAt[i] persist across frames
  // — only the live candle is recomputed each frame. Anchor or mode
  // changes invalidate the cache for that AVWAP only.
  // Map<vwap.id, { anchorTime, mode, anchorIdx, candlesRef,
  //                cumPVAt: Float64Array, cumVAt: Float64Array,
  //                lastClosedIdx }>
  const avwapAccumCacheRef = useRef(new Map())
  // packCache (B3): tracks the high-water-mark of "candles already packed
  // into Float32Array form" so we don't re-walk the array every frame
  // looking for unpacked candles. Reset on candles-array swap (token
  // change). Pack pass runs on every frame but is O(0) in steady state.
  const packCacheRef = useRef({ lastPackedIdx: -1, candlesRef: null })
  // Last lattice key the display pass fully warmed. When this changes we
  // sync-rebuild every visible candle BEFORE paint — see display pass.
  const dispPaintKeyRef = useRef(null)
  // Prior full-draw [startIdx,endIdx] — rim candles that just entered sync-build
  // (budget skip left them blank until idle warm caught up).
  const dispRangeRef = useRef({ lo: -1, hi: -1 })

  // ─── Theme (footprint + candle colors) ───
  // Owned here rather than threaded as a prop, so the brush picker in
  // trades-footer can drive it directly. themeRef gives the canvas
  // draw loop a stable reference that updates synchronously on switch.
  // Theme is owned by App now (app-level footer hosts the ThemePicker) and
  // passed down as props: `theme` (value, drives the draw loop via themeRef /
  // cellLut) plus `themeId`/`setThemeId` (still threaded for any in-chart use).
  const themeRef = useRef(theme)
  themeRef.current = theme
  // The 256-entry cell color LUT only depends on the theme. Building it on
  // every drawCanvas (60+/s, and on the heavy first paint) was pure waste —
  // memoize per theme and let drawCanvas read it through a ref.
  // Per-side ramps: the buy and sell halves of a cell can carry different
  // gradients (theme picker -> Footprint cells). When a side hasn't been
  // split, buildSideLUTs hands back the SAME cached LUT object for both,
  // so the untouched case costs nothing extra.
  const cellLuts = useMemo(() => buildSideLUTs(theme), [theme])
  const cellLutRef = useRef(cellLuts)
  cellLutRef.current = cellLuts
  // ────────────────────────────────────────────────────────────────────
  const [showFlipZones, setShowFlipZones] = useState(false)
  const showFlipZonesRef = useRef(false)
  // Flipzone detector — tightened retention to reduce stale-zone clutter
  // and the per-frame cost of drawing/pruning. Defaults were maxZones=10,
  // maxAgeBars=500. With 500-bar candle history, the default age threshold
  // never actually evicted anything, so old session zones lingered.
  const flipDetectorRef = useRef(new FlipZoneDetector({
    maxZones:    6,
    maxAgeBars:  120,
  }))
  const flipZonesRef = useRef([])

  // --- Meteora DLMM bin overlay toggle ---
  // Defaults ON since the bins have been visible by default. Toggle-off hides
  // the bid/ask heatmap on the right side; the underlying liquidity hook
  // keeps running so the bot still has bin data when needed.
  const [showMeteora, setShowMeteora] = useState(false)
  const showMeteoraRef = useRef(false)

  // --- Chart mode (footprint vs candle) ---
  // Local state with localStorage persistence — no parent wiring needed.
  // Mirrored to ref so the canvas draw loop reads the latest value without
  // forcing a closure rebuild every toggle.
  const [chartModeInternal, setChartModeInternal] = useState(
    () => localStorage.getItem('solcheddar_chart_mode') || 'footprint'
  )
  // chartMode is now owned by the slot (so the shared header can route it to
  // the active chart). Fall back to internal state if rendered standalone
  // without the prop.
  const chartMode = chartModeProp ?? chartModeInternal
  const setChartMode = setChartModeProp ?? setChartModeInternal
  const chartModeRef = useRef(chartMode)
  // tpo-20261103: TPO chart type state read by the draw pass.
  const [tpoLetters, setTpoLetters] = useState(() => { try { return localStorage.getItem('cheddar_tpo_letters') !== '0' } catch { return true } })
  const tpoLettersRef = useRef(tpoLetters); tpoLettersRef.current = tpoLetters
  const tpoKindRef = useRef('crypto')
  tpoKindRef.current = (dataSource === 'hyperliquid' && hlPair?.base) ? (stkInfo(hlPair.base) ? 'stk' : cmeInfo(hlPair.base) ? 'cme' : 'crypto') : 'crypto'
  // Beaker indicator menu — viewport-clamped fixed dropdown, same anchor
  // pattern as TapePanel's filter menus. Holds the toggles that moved out
  // of the right-click menu (which keeps only positional actions).
  const [indicatorMenuPos, setIndicatorMenuPos] = useState(null)
  const indicatorMenuRef = useRef(null)
  const indicatorMenuPanelRef = useRef(null)   // portaled panel (outside indicatorMenuRef)
  // Token-history dropdown anchored to the chart-rail symbol chip. Click
  // the chip → recent tokens (this chart's own data.tokenHistory) → pick
  // to switch. Reuses App's handleHistorySelect (onHistorySelect prop) so
  // there's one token-switch path. Solana only — futures pairs aren't in
  // tokenHistory and the chip shows the HL pair instead.
  const [chipHistoryOpen, setChipHistoryOpen] = useState(false)
  const [chipTab, setChipTab] = useState('recent')   // 'recent' | 'watch'
  const chipHistoryRef = useRef(null)
  // Phone: the chart rail sits at the bottom AND clips its overflow
  // (overflow-x:auto forces overflow-y to clip), so an in-rail dropdown is cut
  // off whichever way it opens. Break it OUT of the rail with position:fixed,
  // anchored just above the chip and clamped to the viewport — no chart
  // ancestor has a transform, so fixed positions against the screen and the
  // rail's clip can't reach it. Desktop keeps the CSS default (downward).
  // Device test, not a width test — see hooks/useIsPhone. The old version was
  // a live (max-width: 768px) subscription, so rotating the phone to landscape
  // (~852px) silently put the chip menu back on the desktop path while the
  // rail it has to escape was still there.
  const isPhone = useIsPhone()
  // Phone Focus v2 — html.chart-focus + chart-focus-target (CSS). Body+Y
  // screen-max remints containerHeight via existing body ResizeObserver.
  const CHART_FOCUS_KEY = 'solcheddar_chart_focus'
  // Phone Focus mode PARKED (replaced by Expand = app fullscreen). Flip to
  // true to bring it back; while false chartFocus is always false, the saved
  // localStorage value is ignored and html.chart-focus is never applied.
  const FOCUS_ENABLED = false
  // vol-ctx-20261101: volume-bar chart type, toggled from the context menu.
  const [volOn, setVolOn] = useState(() => getVolBars(chartId).type === 'vol')
  useEffect(() => {
    setVolOn(getVolBars(chartId).type === 'vol')
    return onVolBarsChange((id, v) => { if (id === chartId) setVolOn(v.type === 'vol') })
  }, [chartId])
  const [chartFocus, setChartFocus] = useState(() => {
    if (!FOCUS_ENABLED) return false
    try { return localStorage.getItem(CHART_FOCUS_KEY) === '1' } catch { return false }
  })
  const chartFocusRef = useRef(chartFocus)
  chartFocusRef.current = chartFocus
  const toggleChartFocus = useCallback((on) => {
    if (!FOCUS_ENABLED) return false
    const next = on === undefined ? !chartFocusRef.current : !!on
    try { localStorage.setItem(CHART_FOCUS_KEY, next ? '1' : '0') } catch {}
    setChartFocus(next)
    return next
  }, [])
  useEffect(() => {
    if (typeof document === 'undefined') return
    const on = !!(FOCUS_ENABLED && chartFocus && isPhone)
    document.documentElement.classList.toggle('chart-focus', on)
    return () => { document.documentElement.classList.remove('chart-focus') }
  }, [chartFocus, isPhone])
  // Fixed-position style for the phone chip dropdown, computed from the chip's
  // on-screen rect at open time (null on desktop → CSS default downward).
  const [chipMenuStyle, setChipMenuStyle] = useState(null)
  const toggleChipHistory = () => {
    const willOpen = !chipHistoryOpen
    if (willOpen && isPhone && chipHistoryRef.current) {
      const r = chipHistoryRef.current.getBoundingClientRect()
      // The phone rail is CSS-zoomed 1.2x, but this menu counter-zooms back to
      // 1x in CSS (same text size as desktop), so these are plain screen px.
      // Portrait: full screen width, edge to edge. Landscape: 260px card, centered.
      const portrait = window.innerHeight > window.innerWidth
      const menuW = portrait ? window.innerWidth : Math.min(260, window.innerWidth - 16)
      const left = Math.round((window.innerWidth - menuW) / 2)   // centered on screen
      setChipMenuStyle({
        position: 'fixed',
        left,
        top: 'auto',
        bottom: window.innerHeight - r.top + 4,   // sit just above the chip
        width: menuW,
        // At most half the screen (and never past the top); scroll if taller.
        maxHeight: Math.max(120, Math.min(r.top - 12, window.innerHeight * 0.5)),
        overflowY: 'auto',
        zIndex: 95,
      })
    } else {
      setChipMenuStyle(null)
    }
    setChipHistoryOpen(willOpen)
  }
  useEffect(() => {
    if (!chipHistoryOpen) return
    const handler = (e) => {
      if (chipHistoryRef.current && !chipHistoryRef.current.contains(e.target)) setChipHistoryOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [chipHistoryOpen])
  // Cap the chip dropdown to the chart: desktop opens DOWN -> stop at the
  // chart's bottom; phone opens UP (fixed, above the chip) -> stop at its top.
  // Measured after render (real top/bottom), re-run on resize. Written
  // straight to style; divided by the element's CSS zoom (phone counter-zoom).
  const chipMenuRef = useRef(null)
  useLayoutEffect(() => {
    if (!chipHistoryOpen) return
    const fit = () => {
      const el = chipMenuRef.current, chart = wrapperRef.current
      if (!el || !chart) return
      el.style.maxHeight = ''
      const r = el.getBoundingClientRect(), c = chart.getBoundingClientRect()
      const zoom = el.offsetHeight > 0 ? (r.height / el.offsetHeight) || 1 : 1
      const avail = isPhone && chipMenuStyle ? r.bottom - Math.max(0, c.top) - 4
                                             : Math.min(c.bottom, window.innerHeight) - r.top - 4
      el.style.maxHeight = Math.max(80, Math.floor(avail / zoom)) + 'px'
    }
    fit()
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [chipHistoryOpen, chipMenuStyle, chipTab, isPhone])
  // Beaker outside-click / Esc: ChartMenu (ignoreRefs = the beaker button).
  useEffect(() => {
    chartModeRef.current = chartMode
    localStorage.setItem('solcheddar_chart_mode', chartMode)
  }, [chartMode])

  // ─── View-window cap + candle-mode auto-switch ───
  // Allow up to 2× the base max candles on screen. Local override of the
  // imported MAX_VIEW_WINDOW so the change lives here, not in chartUtils.
  const MAX_VIEW_WINDOW_X = MAX_VIEW_WINDOW * 2
  // Past this many candles on screen, footprint cells are too thin to read,
  // so auto-switch to candle mode; switch back to footprint when zoomed
  // back under it. Sits at the original (non-doubled) max so the extra
  // room the doubling adds is candle-mode territory. Tune freely.
  const CANDLE_MODE_THRESHOLD = 100
  // Tracks which side of the threshold we were on last frame, so the auto-
  // switch fires only on a CROSSING — not every render. That lets a manual
  // mode pick stick until the next time the count crosses the line.
  const lastThresholdSideRef = useRef(null)

  // Auto-switch chart mode on threshold crossings made DURING the session.
  // Above the threshold → candle (cells unreadable); back under → footprint.
  // Crossing-only, and first run just records the side without switching —
  // so the persisted chartMode is respected on load and a manual override
  // between crossings isn't immediately undone. Acts only when the count
  // actively crosses the line.
  useEffect(() => {
    const side = viewWindowSize > CANDLE_MODE_THRESHOLD ? 'above' : 'below'
    const prevSide = lastThresholdSideRef.current
    lastThresholdSideRef.current = side
    if (prevSide == null || prevSide === side) return // first run or no crossing
    if (chartModeRef.current === 'tpo') return // tpo-20261103: TPO is a manual pick, zoom never overrides it
    if (side === 'above' && chartModeRef.current !== 'candle') setChartMode('candle')
    else if (side === 'below' && chartModeRef.current !== 'footprint') setChartMode('footprint')
  }, [viewWindowSize, CANDLE_MODE_THRESHOLD])

  // Bucket-size density preset. Biases the auto-bucket selector by changing
  // the target row height it solves for: smaller target → more, finer
  // buckets; larger → fewer, coarser. Stays fully adaptive across tokens and
  // zoom (it does NOT pin an absolute bucket size), so all the edge/scaling
  // behavior is unchanged — only the density target shifts. Read via ref in
  // the draw/memo hot paths so changing it doesn't rebuild closures.
  const BUCKET_DENSITY_ROWH = { fine: 12, normal: 20, coarse: 32 }
  const [bucketDensityInternal, setBucketDensityInternal] = useState(
    () => localStorage.getItem('solcheddar_bucket_density') || 'normal'
  )
  // bucketDensity is now owned by the slot (so the shared header can route it
  // to the active chart). Fall back to internal state if rendered standalone.
  const bucketDensity = bucketDensityProp ?? bucketDensityInternal
  const setBucketDensity = setBucketDensityProp ?? setBucketDensityInternal
  const targetRowHRef = useRef(BUCKET_DENSITY_ROWH[bucketDensity] || 20)
  const densityFirstRunRef = useRef(true)
  // F/N/C must pick the rung nearest the new targetRowH — the legibility
  // band inside computeIdealBucket would otherwise keep the old bucket when
  // current RH still sits inside the NEW density's band (common N↔F).
  const forceNearestBucketRef = useRef(false)
  const densityForceRetryRafRef = useRef(0)
  useEffect(() => {
    targetRowHRef.current = BUCKET_DENSITY_ROWH[bucketDensity] || 20
    localStorage.setItem('solcheddar_bucket_density', bucketDensity)
    // FRAME TRIGGER for F/N/C.
    //
    // targetRowH is a REF read inside computeIdealBucket, whose useCallback
    // deps are [scanVisibleMcapRange, containerHeight] — bucketDensity is not
    // among them, and this effect is PASSIVE, so it lands after render and
    // after the layout-effect redraw. Switching F↔N↔C therefore mutated a ref
    // that nothing re-read: the bucket kept its old row-height target until
    // some unrelated event (a trade tick, a zoom, a resize) happened to
    // re-run the sizer. That's the "switch does nothing until the chart
    // moves" behavior.
    //
    // bucketForceTick is already the house mechanism for forcing a bucket
    // re-derive (dblclick override) and is a dep of the sizer effect, so
    // bumping it re-runs the sizer against the NEW target row height and
    // takes the wasForced path that bypasses the hysteresis latches.
    // Skip the mount run: the sizer already runs on mount via its own deps,
    // and forcing there would commit an extra bucket during first paint.
    if (densityFirstRunRef.current) { densityFirstRunRef.current = false; return }
    forceNearestBucketRef.current = true
    setBucketForceTick(t => t + 1)
    // If the sizer settles on the SAME bucket (common — F/N/C changes row
    // height, which only sometimes crosses a bucket boundary), no state
    // changes downstream and nothing repaints, yet row geometry still needs
    // to reflect the new density. Schedule one coalesced draw so the grid
    // updates either way.
    scheduleCanvasDraw()
    // scheduleCanvasDraw is deliberately NOT in the dep array. This effect is
    // declared ~4200 lines above its `const scheduleCanvasDraw = useCallback`,
    // and a dep array is evaluated DURING RENDER — naming it here throws
    // "Cannot access before initialization" (TDZ). Calling it inside the
    // effect body is safe because the body runs after render completes, by
    // which point the const is initialized. It's useCallback([]) — stable for
    // the component's lifetime — so omitting it changes no behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucketDensity])

  // --- Orderflow Bot ---
  const botRef = useRef(null)
  if (!botRef.current) {
    const saved = localStorage.getItem('solcheddar_bot_config')
    botRef.current = saved ? OrderflowBot.deserialize(saved) : new OrderflowBot()
  }
  // Bundle detector — SHADOW mode. Watches the charted token's tape for
  // coordinated-buying bursts (many wallets, uniform sizes, tight window) and
  // console-logs would-fires. No UI yet — this is the calibration pass.
  // NOTE: this wiring was silently LOST once already — the crosshair rebuild
  // started from an upload that predated the detector deploy, and shadow mode
  // logs nothing when absent, same as when quiet. If [bundle] logs ever vanish
  // after a rebuild of this file, check here first.
  const bundleRef = useRef(null)
  if (!bundleRef.current) bundleRef.current = new BundleDetector()
  // Strategy ticks are gated to ~4Hz. bot.update + bundle.update used to run
  // on EVERY redraw — which hits rAF rate (60Hz) during pans, scrubs and the
  // follow glide — dragging four full-candle-array passes (tape heat, churn,
  // reactivity, base) with them. 60Hz for a state machine that acts on 250ms
  // horizons is pure main-thread waste that grows with data volume. 250ms
  // adds at most 250ms to an exit's detection; the fill-at-stop model books
  // the stop price regardless, so the R accounting is unaffected.
  const strategyTickAtRef = useRef(0)
  // Bot trade markers (◄ arrows) — rebuilt from the bot journal on the 4Hz
  // strategy tick, drawn every frame. [{ t: candleStartMs, mcap, side }]
  const botMarkersRef = useRef([])
  // Why-are-there-no-markers diagnostic. Read from the console via
  // window.__cheddarBotMarkers() — tells you in one call whether the
  // journal is empty (data problem), whether entries were filtered out by
  // token (charting a different coin than the bot traded), or whether they
  // built but landed outside the loaded candle range (history problem).
  const botMarkerDiagRef = useRef({
    mode: null, journalLen: 0, built: 0, tokenFiltered: 0, mapped: 0, drawn: 0, token: null,
  })
  // ── ARD shadow detector (client-side) ──
  // Phase-1 Aggression-Response Divergence, run in the browser off the same
  // rawSwaps buffer the bundle detector uses. It never trades; its onWouldFire
  // pushes hollow markers we paint alongside the solid ◄ trade markers so the
  // would-have-beens are visible against actual fills. The runner runs its OWN
  // instance server-side and journals — this is the on-chart shadow only.
  const ardRef = useRef(null)
  if (!ardRef.current) ardRef.current = new ArdDetector()
  // [{ t, mcap, dir: 'LONG'|'PROTECT', x, sol }] — bounded ring; key de-dups
  // repeat fires within a candle bucket so one strike = one marker.
  const ardMarkersRef = useRef([])
  const ardSeenRef = useRef(new Set())
  const ardTokenRef = useRef(null)
  // ARD one-shot arm (WalletHoldings). While armed, the detector keeps
  // ticking and its markers keep painting even with the shadow overlay
  // toggled off — the user queued a trade on the next triangle, so the
  // triangle must both happen and be visible. Fresh would-fires are
  // re-broadcast as a window event; the panel owns the actual execution.
  const ardArmedRef = useRef(false)
  useEffect(() => {
    const onArm = (e) => { ardArmedRef.current = !!e.detail?.armed }
    window.addEventListener('solcheddar-ard-arm', onArm)
    return () => window.removeEventListener('solcheddar-ard-arm', onArm)
  }, [])
  // Wire the callback ONCE. It stamps the fire into the current-timeframe
  // candle bucket so markers survive TF switches, same convention as the
  // trade markers. onDecline is intentionally left null on the client — the
  // decline vocabulary is server-side tuning telemetry, not chart chrome.
  if (!ardRef.current.onWouldFire) {
    ardRef.current.onWouldFire = (sig) => {
      const px = sig.mcap
      if (!(px > 0)) return
      const intervalMs = (timeIntervalRef.current || 60) * 1000
      const t = Math.floor(appNow() / intervalMs) * intervalMs
      const key = `${sig.dir}:${t}:${Math.round(px)}`
      if (ardSeenRef.current.has(key)) return
      ardSeenRef.current.add(key)
      const arr = ardMarkersRef.current
      arr.push({ t, mcap: px, dir: sig.dir, x: sig.event?.strengthX || sig.event?.x || 0, sol: sig.event?.sol || 0 })
      if (arr.length > 200) arr.shift()
      if (ardSeenRef.current.size > 400) ardSeenRef.current.clear()
      // Announce the FRESH triangle (post de-dup, so one strike = one event).
      // WalletHoldings' ARD arm listens for this to fire its queued order;
      // token rides along so a second chart slot can't trigger it.
      window.dispatchEvent(new CustomEvent('solcheddar-ard-triangle', {
        detail: { dir: sig.dir, mcap: px, token: ardTokenRef.current || null },
      }))
    }
  }
  // Fresh Liq reaction (OrderflowBot._updateLiqReaction reclaim) as a window
  // event for WalletHoldings' long-press arm. clientAt/tapeAt let the panel
  // put `at` (tape time) on its own clock. token (the one the bot ticked
  // on) guards other chart slots.
  // liq3s-anytf-20261019: one spot for on-chart indicators to drive armed
  // trades. kind 'liq' -> 'solcheddar-liq-reaction' (WalletHoldings arm).
  // A future indicator adds its kind here plus a listener in WalletHoldings.
  const emitIndicatorSignal = useCallback((kind, r) => {
    window.dispatchEvent(new CustomEvent(`solcheddar-${kind}-reaction`, {
      detail: {
        dir: r.dir, sec: r.sec, at: r.at,
        tapeAt: r.now, clientAt: Date.now(),
        token: r.token || null,
      },
    }))
  }, [])
  const emitLiqReaction = useCallback((r) => emitIndicatorSignal('liq', r), [emitIndicatorSignal])
  const [botEnabled, setBotEnabled] = useState(() => botRef.current.enabled)
  const botEnabledRef = useRef(botEnabled)

  // --- Server bot (remote proxy over the app WS) ---
  const [botMode, setBotMode] = useState(() =>
    localStorage.getItem('solcheddar_bot_mode') || 'local')
  useEffect(() => { localStorage.setItem('solcheddar_bot_mode', botMode) }, [botMode])
  const remoteBotRef = useRef(null)
  if (!remoteBotRef.current) {
    // Seed with the local bot's (complete) config so the menu's knob rows
    // have real numbers before the server's first status broadcast.
    remoteBotRef.current = new RemoteBot(() => data.wsRef?.current, botRef.current.config)
  }
  // (Re)attach the status listener whenever the socket instance changes.
  useEffect(() => {
    if (data.wsRef?.current) remoteBotRef.current.attach(data.wsRef.current)
  }, [data.wsRef?.current, data.status]) // eslint-disable-line react-hooks/exhaustive-deps
  // Keep the proxy's watch context on the charted token + timeframe.
  useEffect(() => {
    remoteBotRef.current.setContext({ token: data.currentToken, interval: timeInterval })
  }, [data.currentToken, timeInterval])
  const activeBot = botMode === 'server' ? remoteBotRef.current : botRef.current
  // Ref mirror of activeBot for the canvas draw loop. drawCanvas is a big
  // useCallback that must NOT re-create on a mode flip, so it reads the
  // journal source through this ref — the same pattern as botEnabledRef /
  // showBotTradesRef. Without it the marker rebuild grabbed botRef.current
  // (the LOCAL bot) in both modes, so in server mode it read an empty local
  // journal and drew nothing while the server's real trades sat in
  // RemoteBot._journal. That was the "no bot markers in server mode" bug.
  const activeBotRef = useRef(activeBot)
  useEffect(() => { activeBotRef.current = activeBot }, [activeBot])
  const [botMenu, setBotMenu] = useState({ show: false, x: 0, y: 0 })
  const [botWaiting, setBotWaiting] = useState(false)

  // Toast
  const [toast, setToast] = useState({ message: '', type: '', show: false })

  // ─── Messages log (session-only) ───
  // ─── Messages & lobby chat moved to App ───
  // addMessage is received as a prop; App owns the scrollback, chat hook,
  // and popup. The chart only pushes order/DEX/alert events via addMessage.

  // ─── Token-name registry for messages ───
  // mint → display name. Populated from every source that knows a token's
  // name (watchlist, DEX boost messages, the charted token). Alert triggers
  // and DEX alerts resolve names through this so a message keeps a real
  // name even if, at trigger time, the symbol map hasn't been populated or a
  // different token is charted. Ref (not state) — read at event time, no
  // re-render needed.
  const tokenNameRegistryRef = useRef({})
  const rememberTokenName = useCallback((mint, name) => {
    if (!mint || !name) return
    // Don't overwrite a good name with a bare mint-slice fallback.
    const looksLikeMintSlice = typeof name === 'string' && name.includes('…') && name.length <= 8
    const existing = tokenNameRegistryRef.current[mint]
    if (existing && looksLikeMintSlice) return
    tokenNameRegistryRef.current[mint] = name
  }, [])
  const resolveTokenName = useCallback((mint, fallback) => {
    if (mint && tokenNameRegistryRef.current[mint]) return tokenNameRegistryRef.current[mint]
    if (fallback) return fallback
    return mint ? `${mint.slice(0, 6)}…` : 'token'
  }, [])

  const showToast = useCallback((message, type) => {
    const duration = type === 'sell' ? TOAST_ERROR_DURATION : TOAST_DURATION
    setToast({ message, type, show: true })
    setTimeout(() => setToast(t => ({ ...t, show: false })), duration)
    // Mirror into the messages log. type 'sell' is used for both errors and
    // some neutral dismissals; tag by content so the panel can color them.
    addMessage(message, type === 'sell' ? 'error' : 'order')
  }, [addMessage])

  // Layout refs (local only)
  const yAxisRef = useRef(null)
  const canvasRef = useRef(null)
  // M1 WebGL cell-fill underlay (batched fills only; text/wicks/strokes stay 2D)
  const cellGlCanvasRef = useRef(null)
  // Last size handed to the cell GL controller (chart-perf): skip unchanged resizes.
  const glSizeMemoRef = useRef({ gl: null, w: 0, h: 0, dpr: 0 })
  const cellGlRef = useRef(null) // cellGL controller | false (create failed) | null (not tried)
  const livePriceForYRef = useRef(0) // body tip / dashed line / gold chip shared Y
  // The chip element. React renders it and owns its text; drawCanvas owns
  // its position, from the same projection the candles use.
  const currentChipRef = useRef(null)
  // ── LIVE-LAYER SPLIT (stage 1) ──
  // The anim pump used to repaint the ENTIRE main canvas 30-40x/s to animate
  // a few cells on ONE candle (measured: ~28-40ms rAF baseline, pure raster,
  // zero script). The forming candle now paints on its own canvas (z3); the
  // pump repaints only that. The main canvas repaints on structural events
  // (data publish, zoom, rebucket, theme, toggles) exactly as before.
  const liveLayerRef = useRef(null)
  const zAboveLayerRef = useRef(null)   // zindex-20261102
  const zBelowLayerRef = useRef(null)   // zindex-20261102
  const zLayerDirtyRef = useRef({ a: false, b: false })
  const zoneLabelLayerRef = useRef(null)   // hold-label-front-20261016: topmost canvas for zone hold %
  const liveDrawScheduledRef = useRef(false) // pump flag — SEPARATE from canvasDrawScheduledRef so a pending live frame can never suppress a scheduled full draw (latent collision in the old shared flag)
  const liveCanvasSizeRef = useRef({ w: 0, h: 0 })
  const liveCssRef = useRef({ w: '', h: '' })
  const containerWidthRef = useRef(800)
  // Live viewport H — written synchronously in ResizeObserver (same pattern as
  // width). drawCanvas / camera read THIS so a resize paint never uses the
  // stale React state from the previous box size (that was the dip-then-snap).
  const containerHeightRef = useRef(600)
  const [containerHeight, setContainerHeight] = useState(600)
  // (resize-flash) Width as STATE too, only so a width resize re-renders the
  // children that take their size as a render-time prop (heat, trail). The
  // ref stays the source every reader uses.
  const [, setContainerWidthState] = useState(800)
  // (resize-flash) Synchronous paint hooks the subgraphs register, so the
  // resize callback can draw them in the same frame as the chart.
  const dvpPaintNowRef = useRef(null)
  const cvdPaintNowRef = useRef(null)
  const heightFromBoxRef = useRef(false)

  // Width-derived footprint X framing. True after home/token/auto-fit; cleared
  // on manual X zoom (wheel / footer / pinch) so resize does not fight the user.
  // (xzoom) A stored user zoom (xZoomStore) starts the chart off auto-fit,
  // so the resize observer does not refit over the restored count.
  const xAutoFitRef = useRef(loadXWin(chartId) == null)
  const fitFootprintXRef = useRef(() => false)
  const fitFootprintX = useCallback((force = false) => {
    if (!force && !xAutoFitRef.current) return false
    const cw = containerWidthRef.current || 0
    if (!(cw > 0)) return false
    const plotW = Math.max(1, cw - RIGHT_PADDING - FIXED_OVERLAY_WIDTH)
    const win = footprintWinForPlotW(plotW)
    xAutoFitRef.current = true
    if (viewWindowSizeRef.current !== win) {
      setViewWindowSize(win)
      viewWindowSizeRef.current = win
    }
    return true
  }, [setViewWindowSize])
  fitFootprintXRef.current = fitFootprintX
  // (xzoom) Token switch: the user's stored candle count if there is one,
  // else the width-derived default fit. Same writes as fitFootprintX.
  const restoreOrFitXRef = useRef(() => false)
  restoreOrFitXRef.current = () => {
    const win = loadXWin(chartId)
    if (win == null) return fitFootprintX(true)
    xAutoFitRef.current = false
    if (viewWindowSizeRef.current !== win) {
      setViewWindowSize(win)
      viewWindowSizeRef.current = win
    }
    return true
  }

  // Entering footprint (manual toggle or threshold cross back): if still on
  // auto framing OR still at candle-overview width, fit columns to plot width.
  // A user who zoomed under the threshold already chose their W — leave it.
  const prevChartModeForFitRef = useRef(chartMode)
  useEffect(() => {
    const prev = prevChartModeForFitRef.current
    prevChartModeForFitRef.current = chartMode
    if (chartMode !== 'footprint' || prev === 'footprint') return
    const w = +viewWindowSizeRef.current || 0
    if (xAutoFitRef.current || w >= CANDLE_MODE_THRESHOLD) fitFootprintX(true)
  }, [chartMode, fitFootprintX])

  // Viewport refs for imperative access (sync with props)
  const mcapBucketSizeRef = useRef(mcapBucketSize)
  // (yexplore) Y-scale glide target: an ABSOLUTE half-span (working space).
  // The glide walks yCam's user span toward it; rows unchanged → the lattice
  // updates in place (no render), rows changed → one yFitTick pass.
  // Declared early: grid memo / coneLive read these during RENDER (ydiag).
  const yHalfTargetRef = useRef(null)
  const yGlideRafRef = useRef(0)
  // One yCam.frame() per pass: the glide tick runs the frame; if that frame
  // needs a lattice rebuild (or lands), the memo reuses THIS result instead of
  // calling frame() a second time for the same inputs.
  const yCamHandoffRef = useRef(null)
  // ── User y-scale gesture window ──
  // Timestamp (performance.now() ms) until which a HUMAN is actively scaling
  // the price axis — wheel over the y-axis, or a y-axis finger drag. Both
  // paths stamp it; the bucket sizer reads it to pick its commit mode.
  // WHY: the sizer's stability gate is trailing-edge (see BUCKET_STABLE_MS)
  // because during a pair/TF load the y-range lurches on every backfill
  // landing, and committing each intermediate ideal cost ~26s of regroup
  // across a 50s load. That's the right call for MACHINE-driven ideal churn.
  // It is the wrong call for a user gesture: the ideal changes as you drag,
  // the timer resets on every change, and nothing commits until you STOP —
  // so the grid renders at the stale bucket with the new halfRange, rows
  // compress continuously (RH = H*B/(2*half+2*B)), and the deferred commit
  // lands as a visible pop. Asymmetric by construction: expanding drives RH
  // DOWN toward MIN_ROW_PX where cells stop being legible, while narrowing
  // drives it UP, where a stale-coarse bucket just reads as bigger cells.
  // Committing live during a gesture is affordable because computeIdealBucket
  // returns a LADDER value (MCAP_BUCKET_STEPS) behind a sticky margin — the
  // ideal changes on rung crossings, not per frame, so a full expand costs a
  // handful of regroups, not sixty a second.
  // ── THE Y MACHINE ───────────────────────────────────────────────────
  // Sole writer of mode / centre / halfSpan / residual. Nothing else in this
  // file may write those four — read them off the frame() result in the grid
  // memo. src/chart/machines/useYCamera.js.
  // TEMP yfit tag: which chart, at which TF / height. Both charts of a dual
  // cell stay mounted, so every trace line must say whose it is.
  const yTraceRef = useRef({ id: chartId, tf: timeInterval, H: 0 })
  yTraceRef.current.id = chartId
  yTraceRef.current.tf = timeInterval
  yTraceRef.current.H = containerHeight
  const yfitC = (reason, d = {}) => yfit(reason, { ...yTraceRef.current, ...d })
  const yCam = useYCamera(yTraceRef)
  const markYScaleGesture = yCam.markGesture
  // (yexplore) Venue size decimals for the futures tick floor (healed onto
  // hlPair by App when the pair list lands). null until known.
  const hlSzDecRef = useRef(null)
  // (yclamp) Full loaded-data price extremes, raw price. Extended incrementally
  // (tail rescan) while the series only grows; full rescan on prepend/reset.
  const fullRangeRef = useRef({ first: null, n: 0, lo: Infinity, hi: -Infinity })
  hlSzDecRef.current = Number.isFinite(+hlPair?.szDecimals) ? +hlPair.szDecimals : null
  const viewEndIndexRefLocal = useRef(viewEndIndex)
  const viewEndIndexRef = (xWin && xWin.endIndexRef) || viewEndIndexRefProp || viewEndIndexRefLocal
  // Candle count for clamp at write time — filled each render once chartData exists.
  const candlesLenRef = useRef(0)
  // Sole X-end write from FPCC: slot commitEnd when provided, else local clamp+sync.
  // STABLE identity: must NOT list viewWindowSize. The wheel effect deps include
  // setViewEndIndex; recreating it mid X-zoom glide re-ran cleanup and cancelled
  // the glide rAF ("lost my glide on x"). Read size from the ref only.
  const commitEndRef = useRef(commitEnd)
  commitEndRef.current = commitEnd
  const setViewEndIndex = useCallback((endIdx, opts) => {
    const total = candlesLenRef.current
    const commit = commitEndRef.current
    if (typeof commit === 'function') {
      commit(endIdx, total, opts)
      return
    }
    const w = (viewWindowSizeRef && viewWindowSizeRef.current) || 1
    const next = (endIdx === -1) ? -1 : clampEndIdx(endIdx, total, w, !(opts && opts.clampFuture === false))
    viewEndIndexRef.current = next
    setViewEndIndexRaw(next)
  }, [setViewEndIndexRaw, viewWindowSizeRef, viewEndIndexRef])
  const autoScrollRef = useRef(autoScrollEnabled)
  const isActiveChartRef = useRef(isActiveChart)
  useEffect(() => {
    isActiveChartRef.current = isActiveChart
    // Repaint immediately on arm/disarm — the band color lives in canvas
    // paint, not CSS, so a focus flip must schedule a draw. (Effects run
    // post-render, so the later-declared scheduleCanvasDraw binding is
    // initialized by the time this fires.)
    scheduleCanvasDraw()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveChart])
  const visibleSortedMcapsRef = useRef([])
  // autoCenter / followTarget / followZoom / fitHold / yPinnedHalf /
  // yPinnedFactor all moved into the Y machine (yCam, above). They were the
  // camera's private state and every one of them had two or more writers here.
  // One frame after a glide lands: use the hold as the range EXACTLY.
  // Union-with-scan on that frame is the end bounce — the scan can be one
  // candle wider/narrower than the eased target.
  const holdExactRef = useRef(false)
  // True while chart is being drag-scrolled. Read by visibleSortedMcaps
  // memo to freeze the mcap RANGE (min/max from visibleCandles) during
  // drag so panning horizontally doesn't resize candles vertically.
  const isDraggingChartRef = useRef(false)
  // True while pan/zoom/glide/pinch/y-axis-drag is live. LiquidityHeatLayer
  // freezes full paints and only keeps translateY(REG) in sync until this
  // clears — then one catch-up full paint.
  // Wheel burst coalescer: notches summed by the handler, applied once a
  // frame. See the note in the wheel handler — wheel events are neither
  // passive nor coalesced by the browser.
  const wheelBurstRef = useRef({ notches: 0, yDelta: 0, raf: 0, yRaf: 0 })
  // localStorage read ONCE, not per wheel event. getItem is synchronous and
  // was running inside a non-passive handler 20+ times a flick.
  const chartInteractingRef = useRef(false)
  // (yaudit) Dead zoomPaint/Freeze/Destination/LandGrid/LandMute/BucketHold
  // removed — never armed after X-cone owned framing. See bak-yaudit.
  const interactWheelTimerRef = useRef(0)
  const yAxisZoomDragActiveRef = useRef(false)
  // Assigned after glide refs exist; call sites use .current so order is safe.
  const beginChartInteractRef = useRef(() => { chartInteractingRef.current = true })
  const endChartInteractRef = useRef(() => {})
  const lastViewWindowSizeRef = useRef(0)
  // Bucket-switch confirmation. When the ideal bucket diverges from the
  // current one, candidate must persist for N consecutive memo runs before
  // we actually switch. Stops scroll+tick coincidences from triggering a
  // grid relayout on a single jittered frame.
  // Latches the last bucket value the sizer actually committed via
  // setMcapBucketSize. Cleared once state settles (ideal === current).
  // This is NOT a speed governor — it only stops the same value from being
  // re-requested before React reflects it in state, which would otherwise
  // be a literal infinite render (max-update-depth). The bucket still moves
  // as fast as the data trips the rule.
  const lastRequestedBucketRef = useRef(null)
  // Sub-bucket pixel residual from snapping rangeMax to a bucket boundary.
  // The grid is bucket-aligned by necessity; this lets us shift it by the
  // exact sub-bucket amount via CSS transform so the snap is visually
  // smooth instead of jumping by one row every boundary crossing.
  const yResidualPxRef = useRef(0)
  // ── The grid the MAIN CANVAS PIXELS were last painted against ──
  // A live frame repaints ONE candle; every other cell on screen is pixels
  // from the last FULL draw. So a live frame must render against the grid
  // those pixels were drawn on, not the newest one — otherwise the forming
  // candle sits on grid B while the bulk sits on grid A, and they slide by
  // different amounts. Advanced only by a full repaint, which is the only
  // moment all layers are redrawn together. null until the first full draw.
  const committedGridRef = useRef(null)
  // Monotonic frame counter for derived committed.latticeId (`${array.latticeId}:c${n}`).
  const commitSeqRef = useRef(0)
  // ── THE PAINT CAMERA, PUBLISHED ────────────────────────────────────────
  // Mid X-zoom: ONE lattice (FROM) for axis + canvas + layers; camera stays
  // identity (X owns the glide). DEST installs at land — no mid-glide
  // FROM→DEST flip and no Y-morph warp under fast wheel.
  // Identity when idle (scale 1, ty 0); consumers apply unconditionally.
  const cameraRef = useRef({ scale: 1, ty: 0, active: false, scaleX: 1, ax: 0 })
  // X ease ownership: live sub-candle scale via zoomGlide.win vs painted int W.
  // (yaudit) xPaintFreeze arm/clear stubs removed — never assigned.
  const committedXRef = useRef(null)
  // Latched cell/candle LOD mode. See FP_LOD_HYST_PX — the band is a dead
  // zone, so this is the only place the current side of it is remembered.
  const lodCandlesRef = useRef(false)
  // DECLARED HERE, NOT NEXT TO THE DRIVER. The grid memo reads
  // glideHalfRef.current during RENDER, thousands of lines above where the
  // glide is wired — a const declared down there is in its temporal dead zone
  // at that point and throws "Cannot access before initialization". Same trap
  // the scheduleCanvasDraw dep-array note describes. Refs read during render
  // belong with the other early refs; only the driver needs to be near its
  // call sites.
  // ── ZOOM GLIDE ────────────────────────────────────────────────────────
  // ONE glide, TWO axes, one `t`. x eases toward the target window; y eases
  // toward whatever the fit says AT THE CURRENT EASED WINDOW. Because both
  // move off the same tick they arrive together, so the path is straight —
  // left, right, or any of the four diagonals, depending on where the fit
  // ends up. Nothing decides the direction; it falls out.
  //
  // THE TARGET IS RECOMPUTED EVERY FRAME, NEVER CAPTURED. That is the whole
  // difference from armZoomPaint. A captured destination is stale the moment
  // a print lands, so arrival needs a reconciliation step — the "land". Here
  // the target IS the live fit, so when the window reaches its target the
  // eased half-range already equals what the fit would say, and handing back
  // is a no-op. There is nothing to snap to because nothing was frozen.
  const zoomTargetWinRef = useRef(null)
  const zoomGlideRef = useRef(null)      // { win, half } — the eased state
  // (drag-jump) Set by the wheel effect: freeze an X zoom glide at the frame
  // on screen. useChartScroll calls it on pointerdown.
  const freezeXGlideRef = useRef(null)
  // X camera as used by the last FULL paint. Live-only passes reuse it, so the
  // live candle sits on the same stretch as the mesh it is drawn over.
  const xCamFullRef = useRef({ active: false, scale: 1, ax: 0 })
  const zoomGlideRafRef = useRef(0)
  const glideHalfRef = useRef(null)      // published eased halfRange, or null
  // so land === the destination known before the glide moved.
  const pendingLandYRef = useRef(null)
  // The frame AFTER land used to call yCam.frame on the scan. That scan is
  // one candle off the eased dest, and the scale bumps. Hold the landed
  // window until the scan actually leaves it.
  const yLandExactRef = useRef(null)
  // Centre eases too, or the motion is not straight. halfRange is the SPAN and
  // centre is the MIDPOINT; easing one while the other jumps gives you a path
  // that stretches smoothly around a point that keeps hopping. dataCenter is
  // (max+min)/2 straight off the scan — undamped since the follow damper was
  // removed — so it steps every time an extreme enters or leaves the window.
  // Both off the same tick, same tau, and the path is a line.
  const glideCenterRef = useRef(null)
  // Y-ONLY EASE STEPS NEED AN INVALIDATION CHANNEL. When the window is not
  // moving (a drag pan, a live print) the eased centre/half still change, and
  // a ref write cannot re-run the grid memo — the same trap as the original
  // wheel bug. During a zoom the window is state and carries it; here nothing
  // else changed, so this tick is the honest dep. It only advances while the
  // ease is actually converging, then stops.
  const [yFitTick, setYFitTick] = useState(0)
  const kickYFollowRef = useRef(() => {})
  // (yexplore) Body-drag Y handler (useChartScroll → onYDragRef). Assigned
  // after the in-place camera path below.
  const yBodyDragRef = useRef(() => {})
  // (ydragslide) X-drag Y fit: one destination window (visible hi+lo). Centre
  // and half both ease toward it on Y_DRAG_FIT_TAU_MS — neither snaps.
  // Wheel cone / pin / Y-gesture still win absolutely. X coast is untouched.
  const dragFitInPlaceRef = useRef(() => false)   // (drag-smooth) assigned beside yCamInPlace
  const yDragFitDestRef = useRef(null)   // { half, centre, min, max } pan-free
  const yDragFitRafRef = useRef(0)
  const yUnsnappedPaintRef = useRef(false)   // (drag-shimmer) last candle paint skipped Y snap
  const yDragFitLastTsRef = useRef(0)
  const yDragFitSettleRef = useRef(false)
  // Time constant, not a duration. Visible settle is ~3x (~1.2s). The old
  // drag ease was 100ms (~1/3s), which read as a late jump.
  const Y_DRAG_FIT_TAU_MS = 55
  const ZOOM_GLIDE_TAU_MS = 55
  // Wheel-only X tau: 2x the shared 55ms so the scroll-wheel zoom takes ~2x as
  // long to settle. Phone pinch and footer drag keep ZOOM_GLIDE_TAU_MS (they
  // trail a finger/drag, and a slower ease there reads as lag). (wheelslow)
  const ZOOM_GLIDE_TAU_WHEEL_MS = 110
  // Wheel-only X tail (wheelslow2). commitWindow rounds W, so the paint only
  // moves on whole-candle crossings. With the pure tau-110 exponential the last
  // candle crossed after a ~116ms stall, then ~250ms more of invisible
  // sub-candle creep held the interact flag before land (volume/heat/sizer
  // catch-up): that was the catch. So: floor the speed at 0.02 candles/ms
  // (= the exponential's own speed ~2.2 candles out, so the handoff is
  // continuous: one candle every 50ms to the end), and land as soon as
  // round(win) already equals the target (0.5). Pinch/footer keep 55ms/0.05.
  const ZOOM_GLIDE_MIN_RATE_WHEEL = 0.02   // candles per ms
  // 0.5 was right when the paint only moved on whole candles (landing within
  // half a candle was invisible). With the X camera the paint shows the
  // FRACTIONAL window, so a 0.5 land threw away up to half a candle of
  // motion in one frame — the left-to-right "readjust" at the end of a
  // wheel glide. 0.01: the min-rate floor (0.02 candles/ms) still finishes
  // the last half candle in ~25ms, and the final snap is sub-pixel.
  const ZOOM_GLIDE_LAND_WHEEL = 0.01       // candles
  // (ease25) Wheel only: share of the glide's total time spent slowing down.
  // Constant deceleration (speed ramps linearly to 0) over the last
  // f/(2-f) of the distance takes f of the total time. 0 = no ease-out.
  const GLIDE_EASE_FRAC = 0.25
  // Row height the cell mesh was last built at. See the deadline in drawCanvas.
  const meshRHRef = useRef(0)
  const volumeProfileLayerRef = useRef(null)
  const hoverColRef = useRef(-1)
  // Frac lives on the slot xWin when present (geom snap needs it beside win/end).
  const fractionalOffsetPxRefLocal = useRef(0)
  const fractionalOffsetPxRef = (xWin && xWin.fracPxRef) || fractionalOffsetPxRefLocal
  const cursorLabelRef = useRef(null)
  const hHighlightRef = useRef(null)  // horizontal cursor crosshair (dashed), aligned with price label
  const isOverYAxisRef = useRef(false)
  const mouseXRef = useRef(-1)   // ← exact mouse X for precise dashed line
  const crosshairRef = useRef(null)  // DOM dashed line — updated imperatively on mouse move
  const crosshairXRef = useRef(-1)   // last crosshair plot-x (px); -1 = hidden. Anchor for relative scrub.
  // The crosshair HORIZONTAL line's current price — stamped where the cursor
  // label computes it (same yToMcap source, clamped). Alerts commit HERE, not
  // at the setting-press's finger position (see setCommitMcapResolver).
  const crosshairMcapRef = useRef(0)
  const crosshairYRef = useRef(-1)   // last crosshair plot-y (px) we synthesized; -1 = none yet.
                                     // X had this via updateCrosshair; Y never did, which is why the
                                     // horizontal line kept riding the real finger while X went relative.
  // Phone RELATIVE scrub (TradingView-style): the crosshair does NOT ride under
  // the finger. First touch places it (absolute, one snap); every move after
  // shifts it by the finger's DELTA, so it drifts out from under your thumb and
  // you can see the price it's on. { fingerX0, crossX0 } captured at scrub start.
  const scrubRef = useRef(null)
  // Last touch activity (ms). Touchscreens dispatch SYNTHETIC mouse events
  // ~300ms after a tap, at the tap point — see the ghost-mouse guard in
  // handleBodyMouseMove. Stamped by the chart's touch handlers.
  const lastTouchTsRef = useRef(0)
  // Coalesced hover: latest mousemove payload + the single pending frame.
  // Declared with the other pointer refs so the unmount cleanup above the
  // handler can cancel the frame without a forward reference.
  const hoverEvRef = useRef(null)
  const hoverRafRef = useRef(0)
  const syncCrosshairRef = useRef(null)  // mirror of the OTHER chart's time line
  // Stable per-instance id so a chart ignores its own bus echo.
  const chartInstanceId = useRef(`ch_${Math.random().toString(36).slice(2)}`).current

  // Project a remote chart's hovered startTime to THIS chart's x and move
  // the sync line. Same x formula as the canvas; if the time isn't in this
  // chart's candle array (different token, or scrolled out of view) the
  // line hides. Subscribes once; ignores its own emissions.
  // (xhair-sync) THE REMOTE TIME IS MATCHED BY SPAN, NOT BY EXACT startTime,
  // and re-placed on every paint. It used to look for a candle whose
  // startTime EQUALLED the broadcast one. That only works when this chart's
  // candles are at least as fine as the sender's: a 1m chart's times all
  // exist on a 15s chart (so chart 2 always matched), but a 15s chart's times
  // mostly fall INSIDE a 1m candle, so the receiver found no match and hid
  // the line on 3 of every 4 sender candles — the flicker. It was also placed
  // once per remote mousemove, so this chart's own treadmill, pan or zoom left
  // it on a stale x until the remote mouse moved again.
  // Now: remember the remote payload, map the CENTRE of the sender's candle
  // to the candle here whose span contains it (spans run to the next
  // candle's start, so mixed-lattice history works too), and place it from
  // the painted frame — on receipt and after every paint — until the sender
  // broadcasts null (its mouse left).
  const syncRemoteRef = useRef(null)   // { startTime, ivSec } | null
  const placeSyncLineRef = useRef(() => {})
  placeSyncLineRef.current = (f) => {
    const el = syncCrosshairRef.current
    if (!el) return
    const hide = () => { if (el.style.display !== 'none') el.style.display = 'none' }
    const r = syncRemoteRef.current
    if (!r || r.startTime == null) return hide()
    const candleArr = chartDataRef.current?.candles
    if (!f || !(f.cw > 0) || !candleArr || !candleArr.length) return hide()
    const t = r.startTime + (r.ivSec > 0 ? r.ivSec * 500 : 0)
    // Last candle with startTime <= t.
    let lo = 0, hi = candleArr.length - 1, idx = -1
    while (lo <= hi) {
      const mid = (lo + hi) >> 1
      const st = candleArr[mid]?.startTime
      if (st != null && st <= t) { idx = mid; lo = mid + 1 } else hi = mid - 1
    }
    if (idx < 0) return hide()
    const nextSt = candleArr[idx + 1]?.startTime
    const ownIvMs = (timeIntervalRef?.current || timeInterval || 60) * 1000
    const spanEnd = nextSt != null ? nextSt : candleArr[idx].startTime + ownIvMs
    if (!(t < spanEnd)) return hide()
    const x = f.xMid(idx)
    if (!Number.isFinite(x) || x < 0 || x > f.plotWidth) return hide()
    if (el.style.display !== 'block') el.style.display = 'block'
    el.style.transform = `translateX(${x}px)`
  }
  useEffect(() => {
    const unsub = crosshairBus.subscribe(({ from, startTime, ivSec }) => {
      if (from === chartInstanceId) return
      syncRemoteRef.current = startTime == null ? null : { startTime, ivSec: +ivSec || 0 }
      placeSyncLineRef.current(lastFrameRef.current)
    })
    return unsub
  }, [chartInstanceId])
  // Imperative crosshair updater. Called from mouse handlers instead of
  // triggering a full canvas redraw. Keeps the line at 60fps regardless
  // of how heavy the canvas is to draw.
  const updateCrosshair = (x) => {
    const el = crosshairRef.current
    if (!el) return
    if (x < 0) {
      el.style.display = 'none'
      crosshairXRef.current = -1
    } else {
      el.style.display = 'block'
      placeCrosshairEl(el, x)   // utils/crosshair: same snap/width/dash as the subgraph lines
      crosshairXRef.current = x   // remembered so the phone RELATIVE scrub has an anchor
    }
    // Subgraph (CVD / DVP) lines: same x, same synchronous call — no rAF lag.
    placeCrosshairs(mouseXRef, x)
    placeXCrossLabels(x)
  }

  // Header crosshair chip — the X counterpart of the Y-axis price label,
  // same chip (.price-label-text). Imperative like the line: no React state
  // per move. Follows the vertical line exactly (it doesn't snap) and reads
  // the candle whose painted column holds it, using the painted frame's own
  // column mapping (frame.xLeft inverse), so the chip agrees with the column
  // under the line and with placeEdgeRows. Shows that candle's time in the
  // header format; future slots extrapolate the time. The row shares the
  // plot's x origin (same assumption placeEdgeRows makes). Clamped inside
  // [0, plotWidth] so the chip never runs under the y-axis. Width is measured
  // only when the text changes, so steady hover does no layout reads.
  const headerXLabelRef = useRef(null)
  const xLabelsOnRef = useRef(false)   // chip showing; placeEdgeRows refreshes only then
  const placeXCrossLabels = (x) => {
    xLabelsOnRef.current = x >= 0
    const hEl = headerXLabelRef.current
    if (!hEl) return
    const f = lastFrameRef.current
    const candles = chartDataRef.current?.candles
    let hTxt = ''
    if (x >= 0 && f && f.cw > 0 && candles && candles.length) {
      const i = Math.floor(f.endIdx + 1 - (f.plotWidth + f.panOffsetPx - x) / f.cw)
      const iv = timeIntervalRef?.current || timeInterval || 60
      const c = i >= 0 ? candles[i] : null
      if (c && c.startTime != null) {
        hTxt = headerTimeLabel(c.startTime, iv)
      } else if (i >= candles.length) {
        const last = candles[candles.length - 1]
        if (last && last.startTime != null) {
          hTxt = headerTimeLabel(last.startTime + (i - candles.length + 1) * iv * 1000, iv)
        }
      }
    }
    const maxX = f && f.plotWidth > 0 ? f.plotWidth
      : containerWidthRef.current - RIGHT_PADDING - FIXED_OVERLAY_WIDTH
    const put = (el, txt) => {
      if (!el) return
      if (!txt) { if (el.style.display !== 'none') el.style.display = 'none'; return }
      let span = el.firstElementChild
      if (!span) { span = document.createElement('span'); span.className = 'price-label-text'; el.appendChild(span) }
      if (el.style.display !== 'flex') { el.style.display = 'flex'; el._xw = 0 }
      if (span.textContent !== txt) { span.textContent = txt; el._xw = 0 }
      if (!el._xw) el._xw = span.offsetWidth || 0
      const w = el._xw
      const cx = Math.max(w / 2, Math.min(x, maxX - w / 2))
      const tf = `translateX(${Math.round(cx - w / 2)}px)`
      if (el.style.transform !== tf) el.style.transform = tf
    }
    put(hEl, hTxt)
  }

  // Dynamic row height ref (created early so useChartInteraction gets the real ref)
  const dynamicRowHeightRef = useRef(ROW_HEIGHT)

  // Canvas draw scheduling
  const drawCanvasRef = useRef(null)
  const lastDrawTsRef = useRef(0)   // stamped by drawCanvas; the render-nudge below skips if a draw just happened
  const gridLayerRef = useRef(null)
  // Column plates (cellSlotBg) — z0 under the order trail (z1); cells stay z2+.
  const plateLayerRef = useRef(null)
  const volLaneRef = useRef(null)   // VOLUME LANE (canvas)
  // Peak-hold for volume-lane scale during X interact/ease. Live-window
  // maxVol is the rest SoT; mid-gesture round(winSize) edges were dropping
  // max when a tall candle left the set and rescaling every bar in one frame.
  const volMaxHoldRef = useRef(0)
  const trailCandleVolRef = useRef(0)   // trailvol-20261103: typical visible candle volume
  // Signature of the last-drawn grid (dpr|w|h|RH|rows). The grid block in
  // drawCanvas skips its clearRect+stroke when nothing it draws from has
  // changed — which is every animation-pumped frame between data ticks.
  const gridSigRef = useRef('')

  // ─── Live cell flip animations ───
  // When the rightmost (live) footprint candle's buy/sell value at a level
  // changes, we kick off a 300ms scale+flip animation for that half-cell.
  // Two separate Maps per side, keyed by NUMERIC mcap (not a string template
  // like `${mcap}|buy`). Number keys avoid the ~N string allocations per
  // frame the previous `${String(mcap)}|side` keys cost; Map handles
  // numbers natively. Value: startTime (number) — single field is enough.
  // prevLiveBuy/Sell track last-drawn buy/sell values per level to detect
  // changes between frames. All four are cleared when the live candle
  // rolls forward (new bar).
  const cellAnimsBuyRef  = useRef(new Map())   // mcap (number) → startTime
  const cellAnimsSellRef = useRef(new Map())
  const prevLiveBuyRef   = useRef(new Map())   // mcap (number) → last buy vol
  const prevLiveSellRef  = useRef(new Map())
  const lastLiveCandleStartRef = useRef(null)
  const lastCanvasSizeRef = useRef({ w: 0, h: 0 })
  // Grow-immediately / shrink-with-hysteresis paint height. Lattice
  // totalHeight can jump (fine density × many rows → ~4k) then settle;
  // tracking it 1:1 reallocs multi-MB 2D+GL buffers. Half-pixel
  // containerHeight jitter is absorbed by Math.round + this ratchet.
  // Cache the last-written transform px and CSS width/height strings so the
  // per-frame draw only TOUCHES the DOM when they actually change. Re-writing
  // identical style.transform / style.width / --chart-y-residual every frame
  // invalidates layout for the whole footprint subtree (DevTools: "forced
  // reflow", "116 nodes need layout, root #document") — the main paint cost.
  // pinPx (.px) / regionPx: the two transform families (pinned vs in-flow). glTf/glOp/
  // glVis/zIdx: the layer-inducing writes on the GL + main canvases, gated
  // here because re-assigning opacity, visibility or z-index relayerizes even
  // when the value is unchanged (Layerize was 23.9% of a 35s profile).
  // pinPx (= .px): translate for PINNED layers (canvas/grid/live/VP/GL/heat/trail).
  // regionPx (= pinPx + scrollTop): translate / --chart-y-residual for IN-FLOW layers.
  // One publisher: drawCanvas transform block writes both. Consumers must read
  // these — never re-derive from live residual refs mid-frame.
  const lastStyleRef = useRef({ px: NaN, pinPx: NaN, regionPx: NaN, axisSolid: false, w: '', h: '', glTf: null, glOp: null, glVis: null, zIdx: null })
  // (y1) LATTICE-HEIGHT LAYERS, WRITTEN FROM THE PAINTED FRAME. Six layers were
  // React-rendered at `height: canvasHeight` (rows x RH), which changes on every
  // Y-zoom frame — a React style diff + layout per frame, and one render behind
  // the canvas during a freeze. drawCanvas now writes frame.mcaps.length x
  // frame.RH (the same lattice the tags/axis already read) to all six, only
  // when the string changes. Before the first painted frame the React value
  // seeds them (layout effect after canvasHeight), so first paint is unchanged.
  // Callback refs seed late-mounting layers (magnifier, meteora, remounts).
  const alertLayerElRef = useRef(null)
  const posTagsLayerElRef = useRef(null)
  const limitTagsLayerElRef = useRef(null)
  const magLayerElRef = useRef(null)
  const dlmmLayerElRef = useRef(null)
  const layerHRef = useRef('')        // last `${H}px` drawCanvas wrote; '' = no frame yet
  const canvasHeightRef = useRef(0)   // React canvasHeight — pre-first-frame seed only
  const layerHNow = () => layerHRef.current
    || (canvasHeightRef.current > 0 ? `${canvasHeightRef.current}px` : '')
  const seedLayerH = (el) => {
    if (!el) return
    const h = layerHNow()
    if (h && el.style.height !== h) el.style.height = h
  }
  const layerHEls = () => [alertLayerElRef.current, posTagsLayerElRef.current,
    limitTagsLayerElRef.current, yAxisRef.current, magLayerElRef.current, dlmmLayerElRef.current]
  const writeLayerHeights = (h) => {
    if (h === layerHRef.current) return
    layerHRef.current = h
    for (const el of layerHEls()) if (el) el.style.height = h
  }
  // (perf2) The four in-flow tag layers take regionPx as an inline transform
  // (was the --chart-y-residual var on the wrapper). A layer that mounts
  // between draws (magnifier on long-press, remounts) is seeded here from the
  // last published regionPx, exactly what the var would have given it.
  const seedLayerTf = (el) => {
    if (!el) return
    const r = lastStyleRef.current.regionPx
    if (Number.isFinite(r)) writeTf(el, yTagTf(r))
  }
  const alertLayerRefCb = useCallback((el) => { alertLayerElRef.current = el; seedLayerH(el); seedLayerTf(el) }, [])
  const posTagsLayerRefCb = useCallback((el) => { posTagsLayerElRef.current = el; seedLayerH(el); seedLayerTf(el) }, [])
  const limitTagsLayerRefCb = useCallback((el) => { limitTagsLayerElRef.current = el; seedLayerH(el); seedLayerTf(el) }, [])
  const magLayerRefCb = useCallback((el) => { magLayerElRef.current = el; seedLayerH(el); seedLayerTf(el) }, [])
  const dlmmLayerRefCb = useCallback((el) => { dlmmLayerElRef.current = el; seedLayerH(el) }, [])
  const yAxisBodyRefCb = useCallback((el) => { yAxisRef.current = el; seedLayerH(el) }, [])
  // (y2b) ROW-ANCHORED OVERLAYS FOLLOW THE PAINTED FRAME. Alert / position /
  // limit tags, the magnifier marker and meteora bars were positioned only by
  // React (priceToY / idx*RH at render). A Y-glide frame that updates the
  // lattice in place has no render, so drawCanvas re-applies the SAME formulas
  // (same clamp, same lattice, same `${y}px` strings) to the mounted elements.
  // The JSX keeps rendering them exactly as before; data-* attributes carry the
  // price. When a tag's off-screen state (arrow / opacity / class) or a
  // mount/unmount condition would change, one render is requested instead.
  const [, setYOverlayTick] = useState(0)
  const yOverlayRenderAskedRef = useRef(false)
  const yChipWantAskedRef = useRef(null)   // last chip-mount state we asked a render for
  yOverlayRenderAskedRef.current = false   // a render is happening now
  fdCount('render')   // (perf4) debug counter; no-op unless enabled
  // Header/footer rows ride the painted X. The cells are packed so endIdx's
  // right edge sits at plotWidth (data-xe = that endIdx, data-xpad = cell
  // width past it). frame.xLeft is the footprint's own column mapping, so the
  // rows get the same sub-candle pan offset and slide off the edges with the
  // columns instead of stepping a whole candle at a time. Refs only, so a
  // stale closure inside drawCanvas is fine.
  const placeEdgeRows = (f) => {
    if (!f || typeof f.xLeft !== 'function' || !(f.cw > 0)) return
    for (const el of [headerVpRef.current, footerVpRef.current]) {
      if (!el) continue
      const e = +el.dataset.xe
      if (!Number.isFinite(e)) continue
      const tx = f.xLeft(e) + f.cw - f.plotWidth + (+el.dataset.xpad || 0)
      el.style.setProperty('--edge-x', `${Number.isFinite(tx) ? tx : 0}px`)
    }
    // Chart moved under a still crosshair (pan, coast, new candle): refresh
    // the header chip's candle. Same frame, so they stay in step.
    if (xLabelsOnRef.current && crosshairXRef.current >= 0) placeXCrossLabels(crosshairXRef.current)
  }
  // pos-tag-20261023: position tags pin to the VISIBLE plot (below the header
  // row, above any footer row), not the lattice ends. Layer y maps to screen
  // y + pinPx; header/footer overlap is measured at most every 500ms.
  const posInsetRef = useRef({ t: 0, top: 0, bot: 0 })
  const posTagBounds = (RH, n) => {
    const now = performance.now(), ins = posInsetRef.current
    const body = bodyScrollRef.current
    if (body && now - ins.t > 500) {
      ins.t = now
      try {
        const b = body.getBoundingClientRect()
        const hr = headerVpRef.current?.parentElement?.getBoundingClientRect()
        const fr = footerVpRef.current?.parentElement?.getBoundingClientRect()
        ins.top = hr && hr.height > 0 && hr.top < b.top + b.height / 2 ? Math.max(0, hr.bottom - b.top) : 0
        ins.bot = fr && fr.height > 0 && fr.top > b.top + b.height / 2 ? Math.max(0, b.bottom - fr.top) : 0
        ins.h = b.height
      } catch {}
    }
    const H = ins.h || containerHeightRef.current || n * RH
    const pin = Number.isFinite(lastStyleRef.current.pinPx) ? lastStyleRef.current.pinPx : 0
    const HALF = 14   // tag is 24px, centred
    let lo = ins.top + HALF - pin, hi = H - ins.bot - HALF - pin
    if (!(hi > lo)) { lo = RH / 2; hi = (n - 0.5) * RH }
    return [lo, hi]
  }
  const repositionYOverlays = (f) => {
    if (!f || !(f.RH > 0) || !f.mcaps || typeof f.yOf !== 'function') return
    const RH = f.RH, n = f.mcaps.length
    let ask = false
    // (perf2) Positions ride transform (see yTagTf). data-ytag: 'a' alert
    // anchor (plain translate), 'p' position tag and 'l' limit / magnifier
    // (translate + -50% centring, as their JSX renders it).
    const pb = posTagBounds(RH, n)
    const clampTag = (el) => {           // alert + position: clamp + offScreen
      if (el.dataset.ydrag) return        // dragged alert rides the pointer
      let y = f.yOf(parseFloat(el.dataset.ym))
      let off = ''
      if (el.dataset.ytag === 'p') {      // position: pin inside the visible plot
        if (!Number.isFinite(y) || y < pb[0]) { y = pb[0]; off = 'above' }
        else if (y > pb[1]) { y = pb[1]; off = 'below' }
      } else if (!Number.isFinite(y) || y < 0) { y = RH / 2; off = 'above' }
      else if (y > n * RH) { y = (n - 0.5) * RH; off = 'below' }
      if (off !== (el.dataset.yoff || '')) ask = true
      writeTf(el, el.dataset.ytag === 'p' ? yTagTfC(y) : yTagTf(y))
    }
    const clampLine = (el) => {          // limit + magnifier: clamp, no offScreen
      if (el.dataset.ydrag) return        // dragged limit tag rides the pointer
      let y = f.yOf(parseFloat(el.dataset.ym))
      if (!Number.isFinite(y)) { ask = true; return }   // JSX would unmount it
      if (y < 0) y = RH / 2
      else if (y > n * RH) y = (n - 0.5) * RH
      writeTf(el, yTagTfC(y))
    }
    const each = (layer, fn) => {
      if (!layer) return
      for (const c of layer.children) if (c.dataset && c.dataset.ytag) fn(c)
    }
    // Current-price chip: drawCanvas already positions it; only its MOUNT is
    // React's. Same test as the JSX. Ask once per flip (never loops if the
    // JSX declines to mount it for another reason).
    {
      const lp = livePriceForYRef.current
      const pY = lp > 0 ? lp : (chartDataRef.current?.highlightMcap || 0)
      const yC = pY > 0 ? f.yOf(pY) : NaN
      const want = (pY > 0) && Number.isFinite(yC) && !(yC < -RH) && !(yC > n * RH + RH)
      if (want !== !!currentChipRef.current) {
        if (yChipWantAskedRef.current !== want) { yChipWantAskedRef.current = want; ask = true }
      } else {
        yChipWantAskedRef.current = null
      }
    }
    each(alertLayerElRef.current, clampTag)
    each(posTagsLayerElRef.current, clampTag)
    each(limitTagsLayerElRef.current, clampLine)
    each(magLayerElRef.current, clampLine)
    const dl = dlmmLayerElRef.current
    if (dl) {
      // Same source the JSX used: dynamicRowHeight (its ref is the same value).
      const RHd = dynamicRowHeightRef.current
      for (const c of dl.children) {
        const i = c.dataset ? c.dataset.yi : undefined
        if (i == null) continue
        writeTf(c, yBarTf((+i) * RHd, RHd))   // (perf2) no top/height writes
      }
    }
    if (ask && !yOverlayRenderAskedRef.current) {
      yOverlayRenderAskedRef.current = true
      setYOverlayTick((t) => t + 1)
    }
  }
  const canvasDrawScheduledRef = useRef(false)

  // Keep refs in sync
  // State-mirroring refs are useLayoutEffect (not useEffect) so they're
  // up-to-date by the time the drawCanvas useLayoutEffect runs. Order
  // matters: these are declared first, so they fire before drawCanvas's
  // useLayoutEffect — drawCanvas reads fresh values.
  useLayoutEffect(() => { mcapBucketSizeRef.current = mcapBucketSize }, [mcapBucketSize])
  useLayoutEffect(() => {
    // SINGLE WRITER for the x window. State lands here first, so by the time
    // this runs the fit memo has already rebuilt the lattice for this exact
    // value — then the ref is published and the draw scheduled. Nothing can
    // paint a window fit has not seen, and the heat / trail rAFs (which read
    // this ref live) cannot run ahead of the chart.
    viewWindowSizeRef.current = viewWindowSize
    // (xflash) Mid X-glide the FRAME_DRAW re-queue already paints each step
    // after the anim tick. flushSync → this layout → scheduleCanvasDraw was a
    // SECOND full paint the same frame (the flash). Ref still published so
    // heat/trail/readers stay current. Land clears zoomGlideRef before
    // publishWin, so landing still schedules here.
    if (zoomGlideRef.current == null && zoomTargetWinRef.current == null) {
      scheduleCanvasDraw()
    }
    // scheduleCanvasDraw is deliberately NOT in the dep array — same TDZ
    // reason as the bucketDensity effect above: the dep array is evaluated
    // during render, ~6800 lines before its `const ... = useCallback`. It is
    // useCallback([]), stable for the component's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewWindowSize])
  useLayoutEffect(() => {
    // Copying follow state (-1) onto the ref snaps a body-wheel park back
    // to the live pad. While parked, commitWindow / commitEnd own the ref.
    if (xWin?.wheelParkRef?.current) return
    viewEndIndexRef.current = viewEndIndex
  }, [viewEndIndex, xWin])
  // Keep clamp total fresh for setViewEndIndex writes (`data` is the chart prop).
  candlesLenRef.current = data?.candles?.length || 0
  useLayoutEffect(() => { autoScrollRef.current = autoScrollEnabled }, [autoScrollEnabled])
  useLayoutEffect(() => { timeIntervalRef.current = timeInterval }, [timeInterval])

  // --- Persist timeframe across refresh ---
  // timeInterval/setTimeInterval are props (state lives in the parent), so
  // persist here: restore once on mount, then write on every change. Keyed
  // per data source so crypto/HL don't clobber each other's last TF.
  // (chart2-bounce) PER SLOT. One key for both charts meant chart 2 wrote its
  // TF over chart 1's and a reload opened both on whichever changed last.
  // Main keeps the legacy key; other slots get their own, seeded from the
  // legacy one on first read so nothing resets.
  const _tfBase = `solcheddar_timeframe_${dataSource || 'sol'}`
  const _tfKey = chartId === 'main' ? _tfBase : `${_tfBase}.${chartId}`
  const _tfRestored = useRef(false)
  useEffect(() => {
    if (_tfRestored.current) return
    _tfRestored.current = true
    try {
      const saved = parseInt(localStorage.getItem(_tfKey) ?? localStorage.getItem(_tfBase), 10)
      if (Number.isFinite(saved) && saved > 0 && saved !== timeIntervalRef.current) {
        setTimeInterval(saved)
      }
    } catch {}
  }, [_tfKey, setTimeInterval])
  useEffect(() => {
    // Don't overwrite storage until after the restore attempt, or the
    // default initial value would stomp the saved one on first paint.
    if (!_tfRestored.current) return
    try { localStorage.setItem(_tfKey, String(timeInterval)) } catch {}
  }, [timeInterval, _tfKey])
  useEffect(() => { publicKeyRef.current = publicKey ? publicKey.toString() : null }, [publicKey])
  // ─── Timeframe hotkeys (inside FootprintChart, not TimeframeSelect) ──
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Armed chart only — every canvas instance registers this window
      // listener, so ungated, one keypress retimeframed BOTH charts in dual
      // mode. Ref (not prop) so arm handoff needs no re-registration.
      if (!isActiveChartRef.current) return;
      const active = document.activeElement;
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)) return;
      if (trading?.orderModal?.show) return;

      let newTF = null;

      if (e.shiftKey) {
        if (e.key === '1') newTF = 15;
        else if (e.key === '2') newTF = 30;
      } else {
        switch (e.key) {
          case '1': newTF = 60; break;
          case '2': newTF = 300; break;
          case '3': newTF = 1800; break;
          case '4': newTF = 14400; break;
          case '5': newTF = 86400; break;
        }
      }

      if (newTF !== null && newTF !== timeIntervalRef.current) {
        setTimeInterval(newTF);
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setTimeInterval]);

  // Push wallet pubkey to server on every change (unlock, switch, lock).
  // The subscribe action also carries wallet, but only fires on token
  // change — without this, unlocking the wallet after the chart has
  // subscribed leaves cs.wallet=None on the server, which breaks
  // wallet_activity broadcasts AND price alerts (server rejects add_alert).
  useEffect(() => {
    if (!data.setServerWallet) return
    data.setServerWallet(publicKey ? publicKey.toString() : null)
  }, [publicKey, data.setServerWallet])

  // --- Switch reveal gate ---
  // When the token or timeframe changes, several independent effects (viewport
  // memo -> y-axis scaling -> scroll sync -> draw loop) settle across multiple
  // frames, and fresh data may take extra frames to arrive over the wire. We
  // hide the scroll container with visibility:hidden (layout still computes so
  // plotWidth / viewport math stay correct) and reveal only once all readiness
  // signals are in place — see the disarm effect below `visibleSortedMcaps`.
  const [isSwitching, setIsSwitching] = useState(true)
  // Rect of the VISIBLE chart body, measured when a switch starts, expressed as
  // an offset FROM .footprint-wrapper — the GateLoader's holder is absolute
  // inside that wrapper.
  //
  // It used to be a position:FIXED holder at the body's viewport rect, and that
  // was the bug: fixed is welded to the VIEWPORT, so on the phone (where the
  // workspace column scrolls) the chart slid away underneath while the loader
  // stayed glued to the screen. Absolute-inside-the-chart scrolls WITH the chart,
  // which is the whole point.
  //
  // The original reason for going fixed still holds and is still handled: the
  // wrapper's box runs taller than the chart you actually see on phone, so
  // GateLoader's own `bottom: 18px` landed below the visible area. The fix isn't
  // to escape the wrapper — it's to stop using `bottom` against it. This holder
  // carries an EXPLICIT top/left/width/height cut from the measured body, so how
  // tall the wrapper happens to be stops mattering entirely.
  //
  // Safe to sit inside the wrapper and stay sharp: the freeze-frame blur is on
  // the inner canvas/overlay div, NOT on .footprint-wrapper (see the filter
  // below — the rail and footer stay sharp for the same reason).
  const wrapperRef = useRef(null)

  // Whitelist for crosshair-cursor clicks. Deliberately a short, explicit
  // selector list rather than "anything clickable": the crosshair spans the
  // full chart, so a generic redirect would fire whatever it happened to
  // cross. Add entries here only for controls that are already
  // pointerEvents:auto and already handle a real click.
  const CROSSHAIR_CLICK_TARGETS = [
    '.alert-lines-layer [data-xh]',   // alert × / drag chip
    '.position-tags-layer [data-xh]', // position + limit tags
  ].join(',')

  // ─── Crosshair exit: tap anywhere OFF the chart ───
  // Mirrors the old in-chart quick-tap teardown exactly (touch never fires
  // mouseleave, so the label and highlight strip must be hidden by hand or
  // they stick). Document-level, capture phase: capture so a panel that
  // stops propagation on its own taps still dismisses the crosshair, and no
  // preventDefault so the tapped control still does its job — exiting the
  // mode should not eat the tap that exits it.
  const exitCrosshairMode = useStableCallback(() => {
    if (!crosshairModeRef.current) return
    crosshairModeRef.current = false
    updateCrosshair(-1)
    if (cursorLabelRef.current) cursorLabelRef.current.style.display = 'none'; placeXCrossLabels(-1)
    if (hHighlightRef.current)  hHighlightRef.current.style.display = 'none'
  })
  // (phone-xhair) No off-chart tap exit any more: the header button is the
  // only switch, so tapping the button itself can't race an exit listener.
  const [switchBodyRect, setSwitchBodyRect] = useState(null)
  useLayoutEffect(() => {
    if (!isSwitching) { setSwitchBodyRect(null); return }
    const r = bodyScrollRef?.current?.getBoundingClientRect?.()
    const w = wrapperRef.current?.getBoundingClientRect?.()
    if (r && w && r.width > 0 && r.height > 0) {
      // Wrapper-relative. Scroll moves both rects together, so the delta is
      // stable — no scroll listener, nothing to go stale.
      setSwitchBodyRect({ left: r.left - w.left, top: r.top - w.top, width: r.width, height: r.height })
    } else {
      setSwitchBodyRect(null)   // cold boot before layout — loader just skips
    }
  }, [isSwitching])
  // Mirror in a ref so drawCanvas (a stable useCallback) can gate on it without
  // taking it as a dep. During a switch the draw loop is suppressed so the
  // canvas HOLDS the outgoing token's last frame (canvas pixels persist until
  // the next draw); the CSS blur over it is the freeze-frame. When the switch
  // disarms, data has already populated, so the normal draw trigger repaints
  // the new token in the same commit that lifts the blur — no sharp flash of
  // the stale frame.
  const isSwitchingRef = useRef(true)
  // useLayoutEffect (not useEffect) + declared ABOVE the draw-trigger layout
  // effect so that, in the commit where isSwitching flips off, this runs first
  // and the subsequent draw-trigger sees the fresh value and paints the new
  // token. A post-paint useEffect would update too late and block the reveal.
  useLayoutEffect(() => { isSwitchingRef.current = isSwitching }, [isSwitching])
  // Tracks the candles reference captured at the last successful reveal.
  // We only update this ref from inside the reveal callback itself — never
  // from a separate effect — because a plain useEffect maintaining it would
  // fire with stale closure values right after a switch commits and corrupt
  // the comparison baseline. During normal live viewing the ref sits frozen
  // at "candles at last reveal", which is fine: it's only consulted during
  // switching, and by then the live stream has long since mutated the
  // reference so candlesChanged will be true as soon as real new data lands.
  // Baseline captured at ARM time, not last-reveal time. Live ticks replace
  // the candles reference constantly, so comparing against the last-reveal
  // reference passes immediately with the OLD token's data still in place —
  // the cover dropped one frame after arming, flashing the stale chart, then
  // the reset emptied it and the new chart painted in pieces uncovered.
  const switchBaselineRef = useRef(null)
  const gateArmedAtRef = useRef(0)   // gate breadcrumbs: elapsed-at-reveal
  // ─── The gate's IDENTITY ───
  // This was `data.currentToken`, which is blind to futures. selectHlPair
  // nulls currentToken on every pair switch (useChartData: the anti-
  // contamination null that makes late Solana backfill frames fail their
  // identity check), so a futures→futures switch is null → null — an
  // unchanged dep. The arm effect never re-ran and the chart rebuilt with no
  // cover at all. Only the FIRST entry into futures armed it, because that
  // one is <mint> → null and does change.
  //
  // Same shape as futWatchKey above: whichever mode is live supplies the
  // identity, and the pair/mint is what actually distinguishes one load from
  // the next. Empty-but-present values ('' at mount) are fine — they're a
  // distinct key from any real selection, so the real one still arms.
  const gateKey = dataSource === 'hyperliquid'
    ? `fut:${hlPair?.base || ''}`
    : `sol:${data.currentToken || ''}`
  // Is there anything to load yet? At mount the mint/pair is '' — a key that
  // can NEVER satisfy readiness, because readiness needs candles and nothing
  // is subscribed. Arming on it ran a whole gate cycle against that: the
  // 1500ms fallback fired, revealed an EMPTY chart, and then the real token
  // armed a second gate behind it. The cover already starts up (isSwitching
  // defaults true), so "nothing selected" is a hold, not an arm.
  const gateHasIdentity = dataSource === 'hyperliquid'
    ? !!(hlPair?.base) : !!data.currentToken
  // Empty slot: gate stays armed (draws suppressed) but nothing is loading —
  // no loader, no blur, clean watermark.
  const showSwitchCover = isSwitching && gateHasIdentity
  // Token changes always pass through an empty candles array (useChartData
  // resets synchronously on token switch). Require observing that reset
  // before the gate can pass, so in-flight old-token frames can't satisfy
  // it. Timeframe changes rebuild in place — no reset — so the flag starts
  // satisfied when the token didn't change.
  const sawResetRef = useRef(true)
  const prevGateKeyRef = useRef(null)
  // historyPending arms in a passive effect (after paint) while the gate
  // arms in a layout effect (before paint) — one paint window exists where
  // the gate's conditions pass before pending is visible. Mirror into a
  // ref and re-check at the moment of reveal.
  useLayoutEffect(() => {
    // Nothing selected: stay covered, arm nothing. Leaving prevGateKeyRef
    // untouched is deliberate — the first REAL key then reads as a change,
    // so it still waits for the reset that a token switch produces.
    if (!gateHasIdentity) { setIsSwitching(true); return }
    switchBaselineRef.current = chartData.candles
    sawResetRef.current = prevGateKeyRef.current === gateKey
    prevGateKeyRef.current = gateKey
    gateArmedAtRef.current = Date.now()
    // GATE BREADCRUMBS — one line per gate transition so a stuck-switch
    // episode names its layer from the console alone: no arm line = the
    // switch never started (subscribe path); armed but no reveal line = the
    // gate wedged (and the fallback line says what it saw). Cheap, permanent.
    dbg(`[GATE] armed → ${gateKey}`)
    setIsSwitching(true)
    // Identity changes only — TF changes rebuild in place without a cover.
  }, [gateKey, gateHasIdentity])

  const footerDragRef = useRef(null)

  // Trade filter + wallet menu moved to TapePanel; tradeFilterMin is a prop.

  // Wallet watchlist state lifted to App (shared with TapePanel). Received as
  // Wallet watchlist state lifted to App; the Wallets tab moved to WalletsPanel
  // and trade highlighting moved to TapePanel. The chart no longer references
  // watchedWallets directly.

  // Token watchlist state
  // tokenWatchlist + setTokenWatchlist are props from App (App owns the state
  // and its persistence). The chart keeps the enrichment, name registry,
  // organic cache, and the context-menu star — all reading via the props.

  // Feed watchlist names into the message token-name registry.
  useEffect(() => {
    for (const t of tokenWatchlist) {
      const nm = t.symbol || t.name
      if (t.address && nm) rememberTokenName(t.address, nm)
    }
  }, [tokenWatchlist, rememberTokenName])

  // ─── Organic score cache for currentToken footer badge ───
  // Map of address → score|null (null = fetched but no score available).
  // Watchlist entries are preferred (always-fresh via existing enrichment);
  // this cache only covers tokens NOT in the watchlist. Minimal deps so we
  // don't re-fetch every time the watchlist ticks.
  const [fetchedOrganic, setFetchedOrganic] = useState({})
  useEffect(() => {
    if (dataSource === 'hyperliquid' || !data.currentToken) return
    const addr = data.currentToken
    if (addr in fetchedOrganic) return
    // Watchlist already has it? skip fetch — derivation below will pick it up
    const wl = tokenWatchlist.find(t => t.address === addr)
    if (wl && typeof wl.organicScore === 'number') return

    let cancelled = false
    fetchTokenV2Data(addr).then(v2 => {
      if (cancelled) return
      setFetchedOrganic(prev => ({
        ...prev,
        [addr]: (v2 && typeof v2.organicScore === 'number') ? v2.organicScore : null
      }))
    }).catch(() => {
      if (!cancelled) setFetchedOrganic(prev => ({ ...prev, [addr]: null }))
    })
    return () => { cancelled = true }
  }, [data.currentToken, dataSource]) // eslint-disable-line react-hooks/exhaustive-deps

  // One-time lazy enrichment for legacy entries — anything without a
  // fetchedAt timestamp predates the rich-data migration. Fire a batch of
  // background fetches on mount; once they resolve, the persistence effect
  // above writes them to localStorage so subsequent loads skip this entirely.
  // Sequential (not Promise.all) to avoid hammering the rate limit.
  const legacyEnrichRanRef = useRef(false)
  useEffect(() => {
    if (legacyEnrichRanRef.current) return
    legacyEnrichRanRef.current = true
    const needsV2    = tokenWatchlist.filter(t => !t.fetchedAt)
    // Sparks retired — no more GT ohlcv fetches (were 429ing, doubled per
    // mounted chart). Strip stored spark arrays once: they were most of
    // token_watchlist's bytes and pushed it past the 32KB sync cap.
    if (tokenWatchlist.some(t => t.chart)) {
      setTokenWatchlist(prev => prev.map(t => {
        if (!t.chart) return t
        const { chart, ...rest } = t   // eslint-disable-line no-unused-vars
        return rest
      }))
    }
    if (needsV2.length === 0) return
    let cancelled = false
    ;(async () => {
      // V2 metadata first (cheap, often instant via cache).
      for (const t of needsV2) {
        if (cancelled) return
        try {
          const v2 = await fetchTokenV2Data(t.address)
          if (cancelled || !v2) continue
          setTokenWatchlist(prev => prev.map(p =>
            p.address === t.address
              ? { ...v2, address: t.address, name: v2.symbol || v2.name || p.name, fetchedAt: Date.now() }
              : p
          ))
        } catch {}
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Tab state for trade tape panel
  const [tapeTab, setTapeTab] = useState('trades') // 'trades' | 'tokens' | 'wallets' | 'history' (scan moved to ScanPanel)
  // History sub-view: 'wallet' (Helius on-chain swaps) | 'bot' (bot journal)
  const [historyView, setHistoryView] = useState('wallet')
  // Live tick to refresh bot journal display (bot writes to localStorage; this nudges re-render)
  const [, setBotJournalTick] = useState(0)
  useEffect(() => {
    if (tapeTab !== 'history' || historyView !== 'bot') return
    const id = setInterval(() => setBotJournalTick(t => t + 1), 2000)
    return () => clearInterval(id)
  }, [tapeTab, historyView])

  // Trade history / HL fills moved to App (JournalPanel owns the wallet/futures
  // history view). The chart keeps only the bot-journal analysis view.

  // --- Meteora DLMM bin liquidity (Solana mode only) ---
  // Backend pushes `{ type: "meteora_pool", mint, pool }` when it observes a
  // DLMM swap for the subscribed mint; we cache the pubkey and feed it to
  // the hook. The hook also falls back to DexScreener if the backend hasn't
  // pushed one yet.
  const [meteoraPoolByMint, setMeteoraPoolByMint] = useState({})
  useEffect(() => {
    const handler = (e) => {
      const { mint, pool } = e.detail || {}
      if (!mint || !pool) return
      setMeteoraPoolByMint((prev) => (
        prev[mint] === pool ? prev : { ...prev, [mint]: pool }
      ))
    }
    window.addEventListener('solcheddar-meteora-pool', handler)
    return () => window.removeEventListener('solcheddar-meteora-pool', handler)
  }, [])

  // Client-side Meteora disabled. The DLMM library makes ~86 RPC calls
  // per token load (getMultipleAccountsInfo per bin) and ignores the
  // injected rpcUrl for some internal calls, falling back to public
  // Solana RPC. Moving to server-side aggregation: backend subscribes
  // once and broadcasts bin data via WS, clients consume from there.
  // Flip back to true when the WS feed is wired up.
  const meteoraEnabled = false
  const meteoraLiquidity = useMeteoraBinLiquidity({
    mint: data.currentToken,
    pool: meteoraPoolByMint[data.currentToken] || null,
    supply: data.tokenSupplyRef?.current || 0,
    rpcUrl: import.meta.env.VITE_RPC_URL || 'https://api.mainnet-beta.solana.com',
    enabled: meteoraEnabled,
  })
  const meteoraBinsRef = useRef([])
  useEffect(() => {
    meteoraBinsRef.current = meteoraLiquidity.bins
    // Publish to the mint-keyed tank registry so sibling panels can read the
    // bid-side depth WITHOUT running a second RPC poller for the same fact.
    // This hook stays the single owner of the fetch.
    publishTank(data.currentToken, meteoraLiquidity.bins, data.chartData?.highlightMcap || 0)
  }, [meteoraLiquidity.bins, data.currentToken])
  useEffect(() => {
    // Trigger canvas redraw when liquidity updates
    if (drawCanvasRef.current) drawCanvasRef.current()
  }, [meteoraLiquidity.bins])

  // ─── DexScreener boosts ───
  // Server polls DexScreener every 45s and broadcasts boost_new for newly
  // appearing boosts. The latest/top snapshot feeds live in ScanPanel now;
  // here we keep the live toast queue + messages-log mirror (global —
  // fires whether or not the Scan panel is open).
  // boostFeeds (the latest/top snapshots) moved to ScanPanel — the chart
  // keeps only the toast + messages-log mirror for live boost_new events.
  const [boostToasts, setBoostToasts] = useState([])         // [{id, mint, icon, amount, ts}]

  // Listen for boost_new / boosts_list on the main data WS (broadcasts go
  // to every client, not just scan-subscribed ones).
  useEffect(() => {
    const ws = data.wsRef?.current
    if (!ws) return
    const handler = (e) => {
      let msg
      try { msg = JSON.parse(e.data) } catch { return }
      if (!msg || !msg.type) return
      if (msg.type === 'boost_new') {
        // Queue a toast (feed snapshots live in ScanPanel)
        setBoostToasts(prev => [
          ...prev,
          {
            id:     `bt_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`,
            mint:   msg.mint,
            // Straight off the wire — the boost feed relays DexScreener's
            // icon URL verbatim, so this door needs the same normalization
            // as the scan payloads.
            icon:   normalizeTokenImage(msg.icon),
            amount: msg.amount,
            url:    msg.url,
            ts:     Date.now(),
          },
        ])
        // Mirror into the messages log.
        const amtStr = typeof msg.amount === 'number' ? `$${msg.amount}` : (msg.amount || '')
        // Remember any name this message carries, then resolve (registry
        // first) so the log keeps a real name.
        if (msg.mint && (msg.symbol || msg.name)) rememberTokenName(msg.mint, msg.symbol || msg.name)
        const sym = resolveTokenName(msg.mint, msg.symbol || msg.name)
        addMessage(`💸 DEX Paid ${amtStr} — ${sym}`, 'dex')
      }
    }
    ws.addEventListener('message', handler)
    return () => ws.removeEventListener('message', handler)
  }, [data.wsRef?.current, data.status]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-dismiss boost toasts after 14s (a hair longer than alert toasts;
  // these aren't time-critical so the user has more time to act).
  useEffect(() => {
    if (boostToasts.length === 0) return
    const timers = boostToasts.map(t => {
      const remaining = Math.max(0, 14000 - (Date.now() - t.ts))
      return setTimeout(() => setBoostToasts(prev => prev.filter(x => x.id !== t.id)), remaining)
    })
    return () => timers.forEach(clearTimeout)
  }, [boostToasts])

  // Scan state/controls moved to ScanPanel. The bottom ticker stays here
  // (chart-resident UI) and is fed by the `tickerTokens` prop — null/empty
  // whenever the Scan panel's Ticker toggle is off or the panel is closed,
  // so scan updates no longer re-render the chart unless the ticker is on.
  const tickerDragRef = useRef({ active: false, startX: 0, startOffset: 0, moved: false })
  const tickerBeltRef = useRef(null)
  const limitDragRef = useRef(null)

  // Description fetch for the CHARTED token only (watermark sub-line).
  // ScanPanel runs its own useTokenDescriptions over its visible mints;
  // the hook's module-level cache dedupes across the two instances, so a
  // token that's both charted and on a scan card is fetched once.
  const descMints = useMemo(() => {
    if (dataSource !== 'hyperliquid' && data.currentToken) return [data.currentToken]
    return []
  }, [data.currentToken, dataSource])
  const tokenDescriptions = useTokenDescriptions(descMints)

  // Jupiter tokens/v2 object for the charted token — the watermark info
  // fallback needs the same fields the cards have (age, mcap, holders,
  // launchpad). One search per token switch.
  const [currentTokenInfo, setCurrentTokenInfo] = useState(null)
  useEffect(() => {
    const mint = data.currentToken
    setCurrentTokenInfo(null)
    if (!mint || dataSource === 'hyperliquid') return
    let dead = false
    ;(async () => {
      try {
        const res = await fetch(`https://lite-api.jup.ag/tokens/v2/search?query=${mint}`)
        if (!res.ok) return
        const arr = await res.json()
        const t = Array.isArray(arr)
          ? (arr.find(x => (x.id || x.address) === mint) || null)
          : null
        if (!dead && t) setCurrentTokenInfo(t)
      } catch {}
    })()
    return () => { dead = true }
  }, [data.currentToken, dataSource])

  // Watermark sub-line: Metaplex description first, card-style info second.
  // CRITICAL: read the description DURING RENDER, not inside the memo with
  // the Map in deps. useTokenDescriptions returns one stable Map for the
  // whole session — its identity never changes, so it's useless as a memo
  // dependency: the memo froze on null even after the cache filled. (It
  // only ever worked when the Jupiter fallback's setState happened to
  // re-key the memo after the batch landed — a race fresh pump launches
  // lose, since they're not in Jupiter's index yet.) The hook re-renders
  // this component when the cache grows, so a render-time .get() is
  // always fresh, and the memo keys on the resolved string.
  const chartedTokenDesc = (dataSource !== 'hyperliquid' && data.currentToken)
    ? (tokenDescriptions.get(data.currentToken) || null)
    : null
  const watermarkInfo = useMemo(() => {
    if (dataSource === 'hyperliquid' || !data.currentToken) return null
    if (chartedTokenDesc) return chartedTokenDesc
    return buildTokenInfoText(currentTokenInfo)
  }, [dataSource, data.currentToken, chartedTokenDesc, currentTokenInfo])

  // Container width/height tracking
  // (resize-flash) ONE CALLBACK, BEFORE PAINT. ResizeObserver fires after
  // layout and before the browser paints, at most once per frame. This used
  // to only note the size there and push the real work to the NEXT frame
  // (requestFrame apply -> setContainerHeight -> a later React render), so on
  // every resize step the browser painted:
  //   frame 0  the new box with the OLD bitmaps (stale/blank strip),
  //   frame 1  a draw at the new size with the OLD Y fit (the memo had not
  //            re-run) — and the heat/trail layers, whose size is a render-
  //            time prop, still at the old size (width never caused a render
  //            at all, so they caught up on the next DATA commit),
  //   frame 2+ the React render: Y refit, layers resized and repainted.
  // That is the flash-then-rescale. Now, in the observer callback itself:
  // write the refs, flushSync the size state (Y fit memo + every layer's
  // size prop re-run now), then draw every layer synchronously. The browser's
  // next paint is the finished frame at the new size. The resize-skip guard
  // in the GL path still skips unchanged sizes.
  useEffect(() => {
    const body = bodyScrollRef.current
    if (!body) return
    let settleTimer = 0
    const sync = (fromEffect) => {
      const w = body.clientWidth
      const h = body.clientHeight
      // Hidden (display:none in a dual cell): keep the last real box.
      if (!(w > 0) && !(h > 0)) return
      if (w > 0) containerWidthRef.current = w
      // Keep ~TARGET_FOOTPRINT_CANDLE_PX (or NARROW 52px under FOOTPRINT_NARROW_PLOT_W) columns while auto-fit is live.
      if (w > 0 && xAutoFitRef.current && chartModeRef.current === 'footprint') {
        fitFootprintXRef.current()
      }
      if (h > 0) {
        containerHeightRef.current = h
        heightFromBoxRef.current = true
        const mcaps = visibleSortedMcapsRef.current
        const n = mcaps?.visibleCount ?? mcaps?.length ?? 0
        if (n > 0) {
          const RH = rowHeightFor(mcaps, h, n)
          dynamicRowHeightRef.current = RH
          yResidualPxRef.current = yPinResidualPx(mcaps, RH, h)
        }
      }
      // Y fit for the new height, and the heat / trail size props, BEFORE
      // the draw. Unchanged values bail out without rendering.
      const setBox = () => {
        if (h > 0) setContainerHeight(prev => (prev === h ? prev : h))
        if (w > 0) setContainerWidthState(prev => (prev === w ? prev : w))
      }
      // The mount call runs inside this effect, where flushSync is not
      // allowed (React defers it and warns): plain setState + a queued draw.
      if (fromEffect) {
        setBox()
        if (scheduleCanvasDraw) scheduleCanvasDraw('full')
        return
      }
      try { flushSync(setBox) } catch { setBox() }
      // The paint, same callback. A queued draw for this frame is now
      // redundant — drop it, unless a glide owns the per-frame draw chain.
      if (drawRafRef.current && !yGlideRafRef.current && !zoomGlideRafRef.current) {
        cancelFrame(drawRafRef.current)
        drawRafRef.current = 0
        canvasDrawScheduledRef.current = false
        pendingInvalidateRef.current = 0
      }
      // Full pass: main + live + y-axis + volume + GL cells, then it forces
      // heat and trail itself (end of drawCanvas).
      drawCanvasRef.current?.()
      // Subgraphs (DVP / CVD) draw on their own rAF, which already ran this
      // frame — paint them now too.
      dvpPaintNowRef.current?.()
      cvdPaintNowRef.current?.()
    }
    const measure = (fromEffect) => {
      beginChartInteractRef.current()
      if (settleTimer) clearTimeout(settleTimer)
      settleTimer = setTimeout(() => {
        settleTimer = 0
        endChartInteractRef.current()
        // One clean paint after settle (trail pops back + final size).
        if (scheduleCanvasDraw) scheduleCanvasDraw('full')
        else if (drawCanvasRef.current) drawCanvasRef.current()
      }, 120)
      sync(fromEffect === true)
    }
    measure(true)
    const ro = new ResizeObserver(() => measure(false))
    ro.observe(body)
    return () => {
      ro.disconnect()
      if (settleTimer) clearTimeout(settleTimer)
    }
  }, [])

  // Block context menu on y-axis
  useEffect(() => {
    const yAxis = yAxisRef.current
    if (!yAxis) return
    const blockContextMenu = (e) => { if (isOverYAxisRef.current) e.preventDefault() }
    const handleMouseEnter = () => { isOverYAxisRef.current = true }
    const handleMouseLeave = () => { isOverYAxisRef.current = false }
    yAxis.addEventListener('mouseenter', handleMouseEnter)
    yAxis.addEventListener('mouseleave', handleMouseLeave)
    window.addEventListener('contextmenu', blockContextMenu, true)
    return () => {
      yAxis.removeEventListener('mouseenter', handleMouseEnter)
      yAxis.removeEventListener('mouseleave', handleMouseLeave)
      window.removeEventListener('contextmenu', blockContextMenu, true)
    }
  }, [])

  // Close dropdowns on click outside — moved to TapePanel along with the
  // trade-filter and wallet-menu dropdowns.

  // ============================================
  // Wire up hooks (data comes from props)
  // ============================================

  // Wire HL wallet WS ref. Depends on data.status because that's real
  // state that flips to 'connected' when the WS opens — refs don't trigger
  // re-renders, so depending on data.wsRef?.current alone would miss the
  // open event and leave the HL wallet pointed at a null ws indefinitely.
  // That manifests as the HL balance always showing 0 even after unlock,
  // because sendAuth() reads a null wsRef and silently bails.
  useEffect(() => {
    if (data.wsRef) hlWallet.setWsRef(data.wsRef.current)
  }, [data.wsRef, data.status, hlWallet])

  // Effective token key for S/R lines, AVWAPs, etc. — HL pair base when in futures mode
  const effectiveToken = dataSource === 'hyperliquid' && hlPair ? `hl_${hlPair.base}` : data.currentToken

  // --- Limit order tag drag (cancel + replace at new price) ---
  const handleLimitDragStart = useCallback((e, lo) => {
    if (e.target.closest('.limit-order-tag-close')) return
    if (dataSource === 'hyperliquid' && hlWallet.isLocked) { showToast('Connect first', 'sell'); return }
    if (dataSource === 'solana' && !publicKey) { showToast('Connect wallet first', 'sell'); return }

    e.stopPropagation()
    const tag = e.currentTarget
    const mcapSpan = tag.querySelector('.limit-order-tag-mcap')
    const startY = e.clientY
    // (perf2) The tag sits by transform now (top is 0), so its Y is re-derived
    // with the SAME clamp the JSX / drawCanvas place it with.
    let originalTop = NaN
    {
      const f = lastFrameRef.current
      if (f && f.RH > 0 && f.mcaps && typeof f.yOf === 'function') {
        const n = f.mcaps.length
        let y = f.yOf(lo.targetMcap)
        if (Number.isFinite(y)) {
          if (y < 0) y = f.RH / 2
          else if (y > n * f.RH) y = (n - 0.5) * f.RH
          originalTop = y
        }
      }
      if (!Number.isFinite(originalTop)) originalTop = 0
    }
    limitDragRef.current = { lo, originalTop, delta: 0, started: false }
    const releaseTag = () => {   // hand the tag back to drawCanvas, snap to price
      if (!tag.dataset.ydrag) return
      delete tag.dataset.ydrag
      scheduleCanvasDrawRef.current?.()
    }

    // Helper: convert pixel top → price
    const topToPrice = (top) => {
      const mcaps = visibleSortedMcapsRef.current
      const RH = dynamicRowHeightRef.current
      if (mcaps.length < 2 || RH <= 0) return null
      const topM = mcaps[0]
      const bottomM = mcaps[mcaps.length - 1]
      const totalH = mcaps.length * RH
      const fraction = Math.max(0, Math.min(1, top / totalH))
      return topM - fraction * (topM - bottomM)
    }

    const onMove = (ev) => {
      if (!limitDragRef.current) return
      const delta = ev.clientY - startY
      limitDragRef.current.delta = delta
      // Only apply drag styling on first real movement
      if (!limitDragRef.current.started && Math.abs(delta) > 2) {
        limitDragRef.current.started = true
        tag.style.cursor = 'grabbing'
        tag.style.zIndex = '100'
        tag.style.opacity = '0.8'
        tag.style.transition = 'none'
        tag.dataset.ydrag = '1'   // (perf2) drawCanvas leaves it to the pointer
      }
      if (limitDragRef.current.started) {
        const newTop = limitDragRef.current.originalTop + delta
        writeTf(tag, yTagTfC(newTop))
        if (mcapSpan) {
          const livePrice = topToPrice(newTop)
          if (livePrice && livePrice > 0) mcapSpan.textContent = formatYAxis(livePrice)
        }
      }
    }

    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)

      if (!limitDragRef.current) return
      const drag = limitDragRef.current
      limitDragRef.current = null

      // Reset styles only if drag actually started
      if (drag.started) {
        tag.style.cursor = ''
        tag.style.zIndex = ''
        tag.style.opacity = ''
        tag.style.transition = ''
      }

      // Click without drag → open modal to edit this order
      if (!drag.started) {
        trading.setOrderModal({ show: true, side: drag.lo.side, mcap: drag.lo.targetMcap })
        return
      }

      const finalTop = drag.originalTop + drag.delta
      const newPrice = topToPrice(finalTop)

      if (!newPrice || newPrice <= 0 || !isFinite(newPrice)) {
        if (mcapSpan) mcapSpan.textContent = formatYAxis(drag.lo.targetMcap)
        releaseTag()
        return
      }

      // Skip if dropped at same row (sub-pixel noise)
      if (Math.abs(drag.delta) < dynamicRowHeightRef.current * 0.3) {
        if (mcapSpan) mcapSpan.textContent = formatYAxis(drag.lo.targetMcap)
        releaseTag()
        return
      }

      trading.handleMoveLimitOrder(drag.lo, newPrice)
      // (perf2) Stay at the drop point while the cancel+replace lands (the old
      // tag unmounts when the order list updates). If this element is still
      // around after that (move failed / same id kept), give it back.
      setTimeout(() => { if (tag.isConnected) releaseTag() }, 4000)
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
  }, [trading, visibleSortedMcapsRef, dynamicRowHeightRef, dataSource, hlWallet, publicKey, showToast, formatYAxis])

  // (scrollperf) Filled right after scheduleCanvasDraw is defined (below).
  // Declared here so useChartInteraction can schedule hover redraws with it.
  const scheduleCanvasDrawRef = useRef(null)
  // (srline-trend-20261019) Alerts set on an S/R line follow it when it moves.
  // Filled once priceAlerts exists (below).
  const srLineMovedRef = useRef(null)
  const interaction = useChartInteraction({
    srLineMovedRef,
    chartData: data.chartData,
    currentToken: effectiveToken,
    visibleSortedMcapsRef,
    dynamicRowHeightRef,
    viewEndIndexRef, viewWindowSizeRef, containerWidthRef,
    bodyScrollRef, fractionalOffsetPxRef, canvasRef, drawCanvasRef, timeIntervalRef, yAxisRef,
    scheduleDrawRef: scheduleCanvasDrawRef,
    showToast,
    saveSRLines: (token, lines) => {
      try { localStorage.setItem(`solcheddar_srlines_${token}`, JSON.stringify(lines)) } catch {}
      interaction.setSrLines(lines)
    },
  })

  // Grok drawing bridge (utils/grokDraw.js): only the active/armed chart answers.
  const grokDrawRef = useRef(null)
  grokDrawRef.current = {
    isActive: () => !!isActiveChartRef.current, chartId, tf: timeInterval,
    candles: data.chartData?.candles, interaction, trend,
    swingsRef, viewEndIndexRef, viewWindowSizeRef, drawCanvasRef,
  }
  useEffect(() => registerChartBridge(makeChartBridge(() => grokDrawRef.current)), [])

  // Wire own-buy tape detection to trading.addPosition
  // When our trade hits the gRPC tape, addPosition fires with exact bucket mcap.
  // The 'tape' source flag is the authoritative marker — refineEntryFromTx will skip
  // any position with source='tape', and addPosition will REPLACE (not average) an
  // 'instant' placeholder when tape upgrades it.
  useEffect(() => {
    if (data.onOwnBuyDetectedRef) {
      data.onOwnBuyDetectedRef.current = (token, entryMcap, tokenAmount, supply) =>
        trading.addPosition(token, entryMcap, tokenAmount, supply, { source: 'tape' })
    }
  }, [data.onOwnBuyDetectedRef, trading.addPosition])

  // --- Bot signal wiring ---
  useEffect(() => {
    botEnabledRef.current = botEnabled
    const bot = botRef.current
    if (!bot) return

    bot.onSignal = async (signal) => {
      // The token this order is FOR. Read before any await: a TOKEN_SWITCH
      // exit fires inside bot.update() while _currentToken is still the old
      // mint. Solana-only — the HL branch trades the armed pair.
      const sigToken = signal.token || bot._currentToken || undefined
      // ── OrderflowBot v2 vocabulary. The legacy handler below only knew
      // BUY/SHORT/SELL/PARTIAL_SELL, so v2 signals executed NOTHING.
      if (signal.type === 'PILOT_OPEN' || signal.type === 'LOAD_ADD') {
        const isShort = signal.direction === 'SHORT'
        if (isShort && dataSourceRef.current !== 'hyperliquid') return
        if (!isShort && !publicKey && dataSourceRef.current !== 'hyperliquid') {
          showToast('🤖 BOT entry blocked — connect wallet first', 'sell')
          bot.disable(); bot.enable()
          return
        }
        const legSol = signal.type === 'PILOT_OPEN' ? signal.sizeSol : signal.addSizeSol
        showToast(`🤖 BOT ${signal.type === 'PILOT_OPEN' ? 'PILOT' : 'LOAD'} ${isShort ? 'SHORT' : 'LONG'} — ${signal.reason}`, isShort ? 'sell' : 'buy')
        setBotWaiting(true)
        try {
          await trading.executeMarketOrder(isShort ? 'sell' : 'buy', { buyAmountSol: legSol, token: sigToken })
        } catch {
          showToast(`🤖 BOT ${signal.type === 'PILOT_OPEN' ? 'PILOT' : 'LOAD'} failed — resetting`, 'sell')
          bot.disable(); bot.enable()
        }
        setBotWaiting(false)
        return
      }
      if (signal.type === 'EXIT') {
        const dirLabel = signal.direction === 'SHORT' ? 'COVER' : 'CLOSE'
        showToast(`🤖 BOT ${dirLabel} (${(signal.pnlPct * 100).toFixed(1)}%) — ${signal.reason}`, signal.pnlPct >= 0 ? 'buy' : 'sell')
        setBotWaiting(true)
        const closeSide = signal.direction === 'SHORT' ? 'buy' : 'sell'
        // A stop MUST get out — one retry, then scream.
        try {
          await trading.executeMarketOrder(closeSide, { sellFraction: 1, token: sigToken })
        } catch {
          await new Promise(r => setTimeout(r, 1500))
          try { await trading.executeMarketOrder(closeSide, { sellFraction: 1, token: sigToken }) }
          catch { showToast('🤖 BOT EXIT FAILED TWICE — CLOSE MANUALLY', 'sell') }
        }
        setBotWaiting(false)
        return
      }
      if (signal.type === 'ADOPT' || signal.type === 'RELEASE') return  // management-only

      // ── Legacy vocabulary (RemoteBot / older builds) ──
      if (signal.type === 'BUY') {
        if (!publicKey && dataSourceRef.current !== 'hyperliquid') {
          showToast('🤖 BOT BUY blocked — connect wallet first', 'sell')
          bot.disable(); bot.enable()
          return
        }
        showToast(`🤖 BOT LONG — ${signal.reason}`, 'buy')
        setBotWaiting(true)
        try {
          await trading.executeMarketOrder('buy', { token: sigToken })
        } catch {
          showToast('🤖 BOT LONG failed — resetting', 'sell')
          bot.disable(); bot.enable()
        }
        setBotWaiting(false)
      } else if (signal.type === 'SHORT') {
        if (dataSourceRef.current !== 'hyperliquid') {
          // Can't short on Solana — skip
          return
        }
        showToast(`🤖 BOT SHORT — ${signal.reason}`, 'sell')
        setBotWaiting(true)
        try {
          await trading.executeMarketOrder('sell', { token: sigToken })
        } catch {
          showToast('🤖 BOT SHORT failed — resetting', 'sell')
          bot.disable(); bot.enable()
        }
        setBotWaiting(false)
      } else if (signal.type === 'SELL') {
        const pnl = (signal.pnlPct * 100).toFixed(1)
        const dirLabel = signal.direction === 'SHORT' ? 'COVER' : 'CLOSE'
        showToast(`🤖 BOT ${dirLabel} (${pnl}%) — ${signal.reason}`, signal.pnlPct >= 0 ? 'buy' : 'sell')
        setBotWaiting(true)
        try {
          // Close: reverse direction — sell to close long, buy to close short
          const closeSide = signal.direction === 'SHORT' ? 'buy' : 'sell'
          await trading.executeMarketOrder(closeSide, { token: sigToken })
        } catch {}
        setBotWaiting(false)
      } else if (signal.type === 'PARTIAL_SELL') {
        // Scale out a fraction of the REMAINING position. Bot keeps managing
        // the runner — do NOT disable bot or clear entry on failure.
        const pct = Math.round((signal.sizePct || 0) * 100)
        const dirLabel = signal.direction === 'SHORT' ? 'COVER' : 'TRIM'
        const rStr = signal.currentR != null ? ` @ ${signal.currentR.toFixed(2)}R` : ''
        showToast(`🤖 BOT ${dirLabel} ${pct}%${rStr} — P${signal.level}`, 'buy')
        try {
          const closeSide = signal.direction === 'SHORT' ? 'buy' : 'sell'
          // NOTE: requires trading.executeMarketOrder to honor { sellFraction }
          // (fraction of current tokenBalance for longs, of position size for shorts)
          await trading.executeMarketOrder(closeSide, { sellFraction: signal.sizePct, token: sigToken })
        } catch {
          showToast(`🤖 PARTIAL P${signal.level} failed`, 'sell')
        }
      }
    }
    bot.onStateChange = () => {
      try { localStorage.setItem('solcheddar_bot_config', JSON.stringify(bot.serialize())) } catch {}
    }

    if (botEnabled && !bot.enabled) bot.enable()
    else if (!botEnabled && bot.enabled) bot.disable()
  }, [botEnabled, showToast, trading, publicKey])

  // Last STRUCTURALLY-VALID grid the memo produced. When the user scrolls the
  // data entirely off-screen the visible-candle scan yields nothing, and the
  // memo used to fall back to chartData.sortedMcaps — which can itself be
  // empty/degenerate mid-load, leaving the y-axis with no range and the scale
  // going unbounded (rows spanning to zero / exploding). Holding the last good
  // grid here lets an empty window reuse the previous real price range: the
  // screen stays level at the last data extent instead of collapsing. Declared
  // up here (not with the other memo caches) because the viewport-reset effect
  // below clears it on token change so token A's range never pins token B.

  // Reset the VIEWPORT on a genuine token change. viewEndIndex / the Y
  // camera span / viewWindowSize all persist across a token switch (they're
  // slot/local state, not keyed to the token) — so if the user had scrolled
  // BACK into history on token A (viewEndIndex pinned to some old candle,
  // yPanOffset nonzero) then picked token B, token B inherited A's scroll
  // position. B's candle array is a different length, so that stale end-index
  // landed B's data far off to the left, and the stale y-pan stacked on top of
  // B's price range ("added to the current position"). Initial load looked
  // right only because the viewport was still at its defaults. Snap back to
  // the live edge on every real switch; first mount is skipped so the initial
  // load path is untouched.
  const viewportResetTokenRef = useRef(null)
  // ── CHART IDENTITY, valid in BOTH modes ──
  // The futures path deliberately runs setCurrentToken(null) (the Tier-A
  // poisoning fix: every late Solana frame must fail its identity check), so
  // in futures mode `data.currentToken` is null and any effect guarded by it
  // goes dark. That made these viewport resets fire only for Solana→Solana
  // switches: Solana→futures returned on the null, futures→SAME-token found
  // the ref still holding that token (never updated while dark) and matched,
  // and futures pair switches (ETH→BTC) never ran the effect at all — y-zoom,
  // y-pan, window size, grid hold and axis width all carried across the mode
  // boundary. Key on an identity that exists in both modes instead; `hl:`
  // matches resolveTokenKey's convention and can never collide with a base58
  // mint. Cheap ternary on purpose — string/null compares by value in deps.
  const chartIdentity = dataSource === 'hyperliquid'
    ? (hlPair?.base ? `hl:${hlPair.base}` : null)
    : data.currentToken
  useEffect(() => {
    if (!chartIdentity) return   // transient null between switch phases — adopt nothing
    if (viewportResetTokenRef.current === null) {
      viewportResetTokenRef.current = chartIdentity   // first mount: no reset
      return
    }
    if (viewportResetTokenRef.current === chartIdentity) return
    viewportResetTokenRef.current = chartIdentity
    setViewEndIndex(-1)            // follow the live edge (concretizes at the stored live gap)
    yHalfTargetRef.current = null  // a Y-scale glide must not carry over
    restoreOrFitXRef.current()     // (xzoom) stored candle count, else width-derived fit
    volMaxHoldRef.current = 0      // don't inherit token A's volume scale
    committedGridRef.current = null
    yCam.reset()                   // pin + hold + damped follow trail
    zoomGlideRef.current = null
    glideHalfRef.current = null
    glideCenterRef.current = null
    yDragFitDestRef.current = null
    axisHoldRef.current.textW = 0  // release the axis-width session hold
    setForceBucketRecompute(c => c + 1)
  }, [chartIdentity])

  // When token changes, load positions + S/R lines + detect existing position.
  // S/R lines are per-canvas state — every chart loads its own. The trading
  // CONTEXT is a shared singleton, so only the ARMED chart may drive it:
  // un-gated, BOTH canvases called loadPositions/detectExistingPosition on
  // their own token changes, and the non-armed chart switching tokens stomped
  // the context with ITS token's positions — the armed chart's tag filter
  // then rejected every entry and its tags vanished ("timed out") until the
  // next reload. isActiveChart in the deps re-loads the context on arm
  // handoff, so the newly-armed chart immediately owns it (and recovers
  // from any historical stomp).
  useEffect(() => {
    if (!data.currentToken) return
    interaction.loadSRLines(data.currentToken)
    if (!isActiveChart) return
    trading.loadPositions(data.currentToken)
    trading.detectExistingPosition(data.currentToken, data.tokenSupplyRef.current)
    // Broadcast token change for WalletHoldings order entry — armed chart
    // only, since order entry follows the armed chart.
    window.dispatchEvent(new CustomEvent('solcheddar-token-changed', { detail: { token: data.currentToken } }))
  }, [data.currentToken, isActiveChart])

  // Remember the charted token's name for the messages registry. Separate
  // from the token-load effect above so status changes (reconnects) don't
  // re-trigger position/SR loading. The connection status carries the
  // symbol as token:SYMBOL once the stream is live.
  useEffect(() => {
    if (data.currentToken && data.status && data.status.startsWith('token:')) {
      rememberTokenName(data.currentToken, data.status.slice(6))
    }
  }, [data.currentToken, data.status, rememberTokenName])

  // When futures pair changes, load S/R lines for the pair
  useEffect(() => {
    if (dataSource !== 'hyperliquid' || !hlPair) return
    interaction.loadSRLines(`hl_${hlPair.base}`)
    // Broadcast pair change for WalletHoldings order entry
    window.dispatchEvent(new CustomEvent('solcheddar-token-changed', { detail: { token: hlPair.base, isFutures: true } }))
  }, [hlPair, dataSource])

  // Listen for buy events — purely for any consumers that want to refresh on completion.
  // Position creation and tape upgrade are handled in useTrading.js + the create-position
  // handler above. We no longer create fallback positions from current chart mcap here
  // (that was the "tag jumps way out below" bug — by 5s the bucket has moved).
  useEffect(() => {
    const handler = (e) => {
      const { token } = e.detail || {}
      // Own token only — the other chart's buy must not be sized with THIS
      // chart's supply.
      if (!token || token !== data.currentToken) return
      // Defensive: if neither tape nor refine produced a position after 6s, try detectExistingPosition
      // to at least pull something from the chain. This is rare — only fires if everything else missed.
      setTimeout(() => {
        const hasPos = positionStore.get(token)
        if (hasPos.length > 0) return
        trading.detectExistingPosition(token, data.tokenSupplyRef.current)
      }, 6000)
    }
    window.addEventListener('solcheddar-buy-executed', handler)
    return () => window.removeEventListener('solcheddar-buy-executed', handler)
  }, [trading, data.tokenSupplyRef, data.currentToken])

  // Instant position tag from buy click (chart OR wallet) — placeholder at current chart mcap.
  // Tape detection upgrades this to source='tape', or refine fills it in if tape misses.
  useEffect(() => {
    const handler = (e) => {
      const { token, solAmount } = e.detail || {}
      if (!token || token !== data.currentToken) return
      if (isPaperActive()) return   // paperclose-20261027: paper buys make no chain tag
      // Skip if a position already exists for this token (placeholder or upgraded)
      const existing = trading.positions.find(p => p.token === token)
      if (existing) return
      const entryMcap = data.lastBucketMcapRef?.current || 0
      const supply = data.tokenSupplyRef.current
      if (entryMcap > 0 && supply > 0) {
        const tokenAmount = solAmount && entryMcap > 0 ? (solAmount * supply) / entryMcap : 0
        trading.addPosition(token, entryMcap, tokenAmount, supply, { source: 'instant' })
      }
    }
    window.addEventListener('solcheddar-create-position', handler)
    return () => window.removeEventListener('solcheddar-create-position', handler)
  }, [trading, data.currentToken, data.tokenSupplyRef, data.lastBucketMcapRef])

  // ─── Per-chart position display (two-independent-chart model) ───
  // The ARMED chart renders the live shared trading context (optimistic
  // updates, tape detection, reconciliation). A NON-armed chart shows its OWN
  // token's positions, loaded from localStorage and refreshed on the same
  // position-change events the context broadcasts — so both charts display
  // their own positions at once, each with P/L from its own mcap. The arm
  // handoff is seamless because the context always writes localStorage on save.
  // (Limit orders + HL futures positions are server/account-backed and remain
  // armed-only for now.)
  const [ownPositions, setOwnPositions] = useState([])
  useEffect(() => {
    if (isActiveChart) { setOwnPositions([]); return }   // armed chart uses the live context
    const tok = data.currentToken
    if (!tok) { setOwnPositions([]); return }
    // Same store the armed view reads — re-read whenever OUR token is written.
    setOwnPositions(positionStore.get(tok))
    positionSync.want(tok)   // positions-20261017: balance poll + entry backfill for this chart's token
    return positionStore.subscribe((t) => { if (t === tok || t === '*') setOwnPositions(positionStore.get(tok)) })
  }, [isActiveChart, data.currentToken])

  // Position-tag source: each chart shows its OWN token's positions.
  // paperaudit-20261027: in Paper the tags are the paper position only (real
  // tags hidden, no leakage either way). Units are SOL/mcap, so x supply = tokens.
  const _paperPos = (qtPaper && dataSource !== 'hyperliquid') ? paperPosition(data.currentToken) : null
  // cmetags-20261101: CME paper position for the charted root (cmePaper.js).
  const _cmeRoot = (dataSource === 'hyperliquid' && hlPair?.base && cmeInfo(hlPair.base)) ? String(hlPair.base).toUpperCase() : null
  const [cmePaperTick, setCmePaperTick] = useState(0)
  useEffect(() => {
    const bump = () => setCmePaperTick(t => t + 1)
    const onStorage = (e) => { if (!e.key || e.key === 'cheddar_cme_paper_v1') bump() }
    window.addEventListener(CME_PAPER_EVENT, bump)
    window.addEventListener('storage', onStorage)
    return () => { window.removeEventListener(CME_PAPER_EVENT, bump); window.removeEventListener('storage', onStorage) }
  }, [])
  const _cmePos = useMemo(() => (_cmeRoot ? cmePosition(_cmeRoot) : null), [_cmeRoot, cmePaperTick])
  const renderedPositions = qtPaper
    ? (_paperPos ? [{ id: `paper:${data.currentToken}`, token: data.currentToken, paper: true,
        entryMcap: _paperPos.entryMcap, tokenAmount: _paperPos.units * (Number(data.tokenSupplyRef?.current) || 0) }] : [])
    : (isActiveChart ? trading.positions : ownPositions)

  // Reset AVWAP + viewport state on new token — keyed on chartIdentity (not
  // currentToken) for the same reason as the viewport reset above: futures
  // mode has no currentToken, and these must fire across the mode boundary
  // and on futures pair switches too.
  useEffect(() => {
    if (!chartIdentity) return
    setAutoScrollEnabled(true)
    setViewEndIndex(-1)
    yHalfTargetRef.current = null
    yCam.clearPin()
    interaction.setAvwaps([])
  }, [chartIdentity])

  // Trade filter sync
  // tradeFilterMin → data sync moved to App (owns the filter state now).


  // ============================================
  // Viewport computations
  // ============================================

  const { chartData } = data

  // Mirror chartData into a ref so drawCanvas can read the latest data WITHOUT
  // listing chartData in its useCallback deps. That keeps drawCanvas's identity
  // stable across data ticks, so the synchronous pre-paint layout-effect (which
  // depends on drawCanvas) no longer re-fires on every trade/candle — only the
  // RAF-scheduled data effect redraws. Assigned during render so it's current
  // by the time any draw runs this frame.
  const chartDataRef = useRef(chartData)
  chartDataRef.current = chartData

  // The ONE accessor for layers that paint outside drawCanvas and so cannot
  // see the `frame` binding. Same object drawCanvas published; never a
  // second lattice. Pre-first-paint only, falls back to the live refs.
  const priceToY = useCallback((mcap) => {
    const f = lastFrameRef.current
    if (f) return f.yOf(mcap)
    return mcapToYUtil(mcap, visibleSortedMcapsRef.current, dynamicRowHeightRef.current)
  }, [])

  // --- Cumulative Delta subgraph ---
  // Toggle persisted to localStorage so reload preserves the user's choice.
  // Hook runs even when subgraph is hidden so wick observations accumulate;
  // toggling on later shows real history rather than starting blank.
  const [showCvdSubgraph, setShowCvdSubgraph] = useScopedSetting('solcheddar:showCvd', false)
  const [showDvp, setShowDvp] = useScopedSetting('solcheddar:showDvp', false)
  const [showBodyVol, setShowBodyVol] = useScopedSetting('solcheddar:showBodyVol', false)   // bodyvol-20261029
  const [dvpConfigStored, setDvpConfig] = useScopedSetting('solcheddar:dvpConfig', {})
  const [dvpHeightStored, setDvpHeight] = useScopedSetting('solcheddar:dvpHeight', 110)
  const dvpHeight = Math.max(60, Math.min(320, Number(dvpHeightStored) || 110))
  const dvpConfig = useMemo(() => ({ ...DVP_DEFAULTS, ...(dvpConfigStored || {}) }), [dvpConfigStored])
  // Regular CVD style (Candle / Histogram) + colors — CVDSettingsMenu (cdhist-20261025).
  const [cvdConfigStored, setCvdConfig] = useScopedSetting('solcheddar:cvdConfig', {})
  const cvdConfig = useMemo(() => ({ ...CVD_DEFAULTS, ...(cvdConfigStored || {}) }), [cvdConfigStored])
  // Orderflow trail (ATAS-style live flow into the right gutter).
  // (Migration also fixed a latent bug: the old persist effect depended on
  // showCvdSubgraph, so the smoketrail toggle only saved when CVD toggled.)
  const [showOrderflowTrail, setShowOrderflowTrail] = useScopedSetting('solcheddar:showSmoketrail_v2', true)

  // ARD shadow overlay (hollow would-fire arrows). Off by default — it's a
  // research overlay. Ref mirror so drawCanvas (identity-stable, reads refs)
  // can gate the detector run + paint without being recreated; toggling the
  // state re-renders → the universal render-nudge repaints the same frame.
  // Bot trade markers (journal-driven ▲/▼ + bot glyph). Default ON.
  const [showBotTrades, setShowBotTrades] = useScopedSetting('solcheddar:showBotTrades', true)
  // own-tag-20261025: own fills collected from recentTrades (a short rolling
  // buffer) so the chart tags outlive it; reset on token switch. Read by 9c.
  const ownTagsRef = useRef({ token: null, ids: new Set(), list: [] })
  {
    const o = ownTagsRef.current
    const tok = data.currentToken || null
    if (o.token !== tok) { o.token = tok; o.ids = new Set(); o.list = [] }
    const rt = data.recentTrades || []
    for (let i = 0; i < rt.length; i++) {
      const t = rt[i]
      if (!t || !t.isOwn || !(t.mcap > 0) || !Number.isFinite(t.ts) || o.ids.has(t.id)) continue
      o.ids.add(t.id)
      o.list.push({ ts: t.ts, mcap: t.mcap, side: t.side, amount: t.amount })
      if (o.list.length > 200) o.list.shift()
    }
  }
  // paperaudit-20261027: in Paper the yellow own-fill tags come from the paper
  // fills (persisted in cheddar_paper_v1) instead — never mixed with real fills.
  const paperMarksRef = useRef({ key: '', list: [] })
  {
    const pm = paperMarksRef.current
    const key = qtPaper ? `${paperTick}|${data.currentToken || ''}` : ''
    if (pm.key !== key) { pm.key = key; pm.list = qtPaper ? paperMarkers(data.currentToken) : [] }
  }
  const qtPaperRef = useRef(qtPaper)
  qtPaperRef.current = qtPaper
  const showBotTradesRef = useRef(showBotTrades)
  useEffect(() => {
    showBotTradesRef.current = showBotTrades
  }, [showBotTrades])

  // Bot overlay (base range band + PILOT / BASIS / STOP lines). Purely a
  // DISPLAY toggle — it no longer rides on whether the local bot is armed.
  // It used to: the overlay was gated on botEnabledRef, so the only way to
  // see the bot's range was to arm the local brain, which in server mode
  // meant a second bot on the same wallet. Now arming and drawing are two
  // separate switches and this one draws whichever bot is live.
  const [showBotOverlay, setShowBotOverlay] = useScopedSetting('solcheddar:showBotOverlay', true)
  const showBotOverlayRef = useRef(showBotOverlay)
  useEffect(() => {
    showBotOverlayRef.current = showBotOverlay
  }, [showBotOverlay])

  // INDEPENDENT BOTS (by choice). Server and each chart's local bot arm on
  // their own — they may run different timeframes. Known hazard: they share
  // one wallet, and EXIT sells sellFraction:1, so two bots on the SAME token
  // can dump each other's position. Both arm call sites still route here.
  const toggleLocalBotArm = useCallback(() => {
    setBotEnabled(v => !v)
  }, [])

  // Console probe for "why don't I see bot markers?". One call answers it:
  //   toggle   false  → the Bot Markers menu item is off
  //   journalLen 0    → no journal reached the client (server mode: the
  //                     runner never replied to {cmd:'journal'})
  //   built 0 w/ journal + tokenFiltered > 0 → the bot traded other coins
  //   mapped 0 w/ built > 0 → trades predate the loaded candle history
  //   drawn 0 w/ mapped > 0 → they're off-screen; scroll/zoom out
  // ── window.__cheddarLattice() ──
  // What the price→row mapping is doing right now. Two jobs.
  //
  // ONE: it is the deploy check. latticeRowIndex is an identifier, so the
  // minifier renames it and the bundle-grep that verified ack-no-rearm cannot
  // work here. If this function exists in the console, the code is live.
  //
  // TWO: it is the instrument for turning log scale on. The lattice is uniform
  // in PIXELS either way; what changes is whether a row is a constant
  // DIFFERENCE in price or a constant RATIO. `mode` says which, `step` is that
  // difference or ratio, and `spread` is how far the top and bottom rows are
  // apart in price terms — the number that makes a linear lattice a bad fit
  // when it gets large.
  //
  // `roundTrip` is the correctness check: take the price at the top edge of
  // each sampled row and ask the mapper which row it belongs to. Every answer
  // must equal the row it came from. `ok:false` means the lattice and the
  // mapper disagree, which is the class of bug that once drifted volume bars a
  // row off the highlight chip — catch it here rather than by eye.
  // One toggle, three entry points (LOG/LIN button, context menu, console
  // __cheddarLogScale). Persists the single global setting.
  const toggleLogScale = useCallback((on) => {
    const next = on === undefined ? !logScaleRef.current : !!on
    try { localStorage.setItem(LOG_SCALE_KEY, next ? '1' : '0') } catch {}
    setLogScale(next)
    return { log: next }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.__cheddarLogScale = (on) => (on === undefined ? { log: logScaleRef.current } : toggleLogScale(on))
    return () => { try { delete window.__cheddarLogScale } catch {} }
  }, [toggleLogScale])

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.__cheddarLattice = () => {
      const m = visibleSortedMcapsRef.current
      if (!m || m.length < 2) return { error: 'no lattice yet' }
      const log = !!m.log
      const top = m[0], bot = m[m.length - 1]
      const sample = []
      let ok = true
      for (let k = 0; k < 8; k++) {
        const i = Math.floor(k * (m.length - 1) / 7)
        const got = findBucketIndex(m, m[i], mcapBucketSizeRef.current)
        if (got !== i) ok = false
        sample.push({ row: i, price: m[i], mapsTo: got })
      }
      return {
        mode: log ? 'log' : 'linear',
        rows: m.length,
        rowHeightPx: dynamicRowHeightRef.current,
        top, bottom: bot,
        spread: +(top / bot).toFixed(3) + 'x',
        step: log ? +(m[0] / m[1]).toFixed(6) + ' (ratio)'
                  : +(m[0] - m[1]).toPrecision(6) + ' (difference)',
        committedBucket: mcapBucketSizeRef.current,
        roundTrip: { ok, sample },
      }
    }
    return () => { try { delete window.__cheddarLattice } catch {} }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.__cheddarBotMarkers = () => ({
      toggle: showBotTradesRef.current,
      ...botMarkerDiagRef.current,
      markers: botMarkersRef.current.slice(0, 20),
    })
    return () => { try { delete window.__cheddarBotMarkers } catch {} }
  }, [])

  // Same idea for "Bot Overlay is checked but I see no range". The overlay
  // can be silent for three unrelated reasons and they look identical on
  // screen, so name them:
  //   toggle false     → the Bot Overlay menu item is off
  //   source null      → no bot wired at all
  //   armed false      → the active bot is disarmed; drawBotOverlay bails
  //                      early by design (nothing is running, so there's
  //                      no range to draw). NOT a bug.
  //   activeBase null  → armed and scanning, but no base has formed yet
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.__cheddarBotOverlay = () => {
      const b = activeBotRef.current || botRef.current
      const s = b?.getStatus?.() || null
      return {
        toggle: showBotOverlayRef.current,
        source: b ? (b === remoteBotRef.current ? 'server' : 'local') : null,
        armed: !!b?.enabled,
        state: s?.state ?? null,
        activeBase: s?.activeBase ?? null,
        pilot: s?.pilot ?? null,
        load: s?.load ?? null,
      }
    }
    return () => { try { delete window.__cheddarBotOverlay } catch {} }
  }, [])

  const [showArd, setShowArd] = useScopedSetting('solcheddar:showArd', false)
  const showArdRef = useRef(showArd)
  useEffect(() => {
    showArdRef.current = showArd
  }, [showArd])

  // ARD shadow tuning — partial-config overrides applied LIVE to the on-chart
  // detector (ardRef). Persisted to localStorage; the server runner has its
  // own instance and is unaffected. { show,x,y,openLeft } drives the gear popup.
  const [ardConfig, setArdConfig] = useScopedSetting('solcheddar:ardConfig', {})
  const [ardSettings, setArdSettings] = useState({ show: false, x: 0, y: 0, openLeft: false })
  const [heatSettings, setHeatSettings] = useState({ show: false, x: 0, y: 0, openLeft: false })
  const [dvpSettings, setDvpSettings] = useState({ show: false, x: 0, y: 0, openLeft: false })
  const [cvdSettings, setCvdSettings] = useState({ show: false, x: 0, y: 0, openLeft: false })
  // Delta subgraph panes (CVD + Delta Volume Pro) sit outside the footprint
  // body, so the body's wheel listener never saw them. Their wheel is
  // forwarded to THAT handler (bodyWheelHandlerRef, set by the body-wheel
  // effect): same X glide zoom, same burst coalescing, anchored on the
  // pointer's clientX against the main canvas (the panes share its x map).
  const [dvpPaneEl, setDvpPaneEl] = useState(null)
  const [cvdPaneEl, setCvdPaneEl] = useState(null)
  const bodyWheelHandlerRef = useRef(null)
  useEffect(() => {
    // Mutate the live detector's config in place — it reads this.config each
    // tick, so retuned gates take effect on the next swap without a rebuild.
    // Persist is useScopedSetting's job — do not stamp the legacy unscoped key.
    if (ardRef.current) Object.assign(ardRef.current.config, ardConfig)
  }, [ardConfig])

  // ─── Absorption: outline cells where heavy one-sided volume was rejected ───
  // DEFINITION (kept narrow on purpose). Absorption is not "a big cell". It is
  // aggressive volume at a level that FAILED to move price through that level
  // — someone passive stood there and ate it. So the score needs both terms:
  //   bid absorb  = sellVol × how far price closed ABOVE that level
  //   ask absorb  = buyVol  × how far price closed BELOW that level
  // normalised by the candle's own range, so a level is only marked when the
  // rejection is meaningful relative to that bar rather than in raw ticks.
  //
  // Volume gate is a MULTIPLE OF THE MEDIAN visible cell, not an absolute:
  // absolute thresholds are meaningless across a 156-mcap mint and ETH.
  //
  // Only ONE cell per candle is ever marked — the strongest. Outlining every
  // qualifying level would paint half the chart and the word "highest" in the
  // request would stop meaning anything.
  const ABS_DEFAULTS = { buyColor: '#38e08b', sellColor: '#ff5c5c', weight: 2, minMult: 3, showLiq: true, zones: false, zoneColor: '#ffd166' }   // showLiq: absorb-liq4
  const POC_COLOR = '#ffffff'
  const [showAbsorption, setShowAbsorption] = useScopedSetting('solcheddar:showAbsorption', false)
  // Footer Δ strip: per-candle delta text on/off (right-click the footer).
  const [showFooterDelta, setShowFooterDelta] = useScopedSetting('solcheddar:showFooterDelta', true)
  const [footerMenuPos, setFooterMenuPos] = useState(null)
  // Footprint POC: the highest-total-volume level of EACH bar, outlined.
  // Distinct from the Value Area POC above, which is one line across the
  // whole visible profile.
  const [showPoc, setShowPoc] = useScopedSetting('solcheddar:showPoc', false)
  const [absConfigStored, setAbsConfig] = useScopedSetting('solcheddar:absConfig', {})
  const absConfig = useMemo(() => ({ ...ABS_DEFAULTS, ...(absConfigStored || {}) }), [absConfigStored])
  const [absSettings, setAbsSettings] = useState({ show: false, x: 0, y: 0, openLeft: false })
  const showAbsorptionRef = useRef(showAbsorption)
  const showPocRef = useRef(showPoc)
  const absConfigRef = useRef(absConfig)
  const absorptionRef = useRef(null)
  const absCellCacheRef = useRef({ key: '', map: new Map() })   // liq-repaint-20261023: startTime -> { mcap, side } | null
  const absLiqCacheRef = useRef({ key: '', map: new Map() })   // abs-liq-20261017: startTime -> { mcap, res }
  // Painted Absorption outlines, recorded by the draw pass in the painted
  // canvas's DEVICE px (ctx transform applied: dpr + camera X scale), keyed by
  // candle startTime. Hit-test converts the pointer into the same space via
  // the canvas's own bounding rect. (absorbers-rect-20261016)
  const absRectsRef = useRef(new Map())
  // hold-pct-big-20261016: '% hold' centered in each churn/absorption zone.
  // "t0:t1:side" -> { hold, held, n, partial } from /api/wallet/holdpct
  // (server keeps incremental per-box state). zoneHoldVisRef = the zones the
  // last full paint showed (what the poller asks for).
  const holdPctRef = useRef({ mint: '', map: new Map() })
  const zoneHoldVisRef = useRef([])
  // hold-label-style-20261016: zone rects (canvas DEVICE px) from the last full
  // paint, and the zone the MOUSE is over (its label is hidden). Touch: no-op.
  const zoneHoldRectsRef = useRef([])
  const zoneHoverKeyRef = useRef(null)
  // abs-cell-hover-20261017: startTime of the absorption cell under the MOUSE
  // (its outline is highlighted). Touch: never set.
  const absHoverKeyRef = useRef(null)
  const holdFetchRef = useRef({ key: '', at: 0, busy: false })
  useEffect(() => { showAbsorptionRef.current = showAbsorption }, [showAbsorption])
  useEffect(() => { showPocRef.current = showPoc }, [showPoc])
  // Flip Animation indicator: off = live cells count in realtime, no pop/flip.
  const [showCellFlip, setShowCellFlip] = useScopedSetting('solcheddar:showCellFlip', true)
  const showCellFlipRef = useRef(showCellFlip)
  useEffect(() => {
    showCellFlipRef.current = showCellFlip
    cellAnimsBuyRef.current.clear(); cellAnimsSellRef.current.clear(); prevLiveBuyRef.current.clear(); prevLiveSellRef.current.clear()
  }, [showCellFlip])
  useEffect(() => { absConfigRef.current = absConfig }, [absConfig])
  // ─── Volume (bottom bars) indicator: toggle + settings for the existing
  // VOLUME LANE canvas. On by default and VOL_DEFAULTS = the original look
  // (fixed 120px, same colours). Refs assigned in render so the render-nudge
  // draw after a toggle/edit already sees the new values. (volume-ind-20261024)
  const [showVolumeBars, setShowVolumeBars] = useScopedSetting('solcheddar:showVolumeBars', true)
  const [volConfigStored, setVolConfig] = useScopedSetting('solcheddar:volConfig', {})
  const volConfig = useMemo(() => ({ ...VOL_DEFAULTS, ...(volConfigStored || {}) }), [volConfigStored])
  const [volSettings, setVolSettings] = useState({ show: false, x: 0, y: 0, openLeft: false })
  const volConfigRef = useRef(volConfig)
  volConfigRef.current = volConfig
  // ─── Volume alerts (volalert-20261028) ───
  // Right-click / long-press a volume bar -> Volume menu -> Set alert (starts
  // at that bar's volume). Bell chip + dashed line sit on the lane like the
  // price-alert tags (same chip, drag, x-cancel, toast, bell sound); the
  // alert fires when a LATER bar's volume reaches the level, then removes
  // itself. Local per instrument + timeframe (hooks/useVolumeAlerts).
  const volAlerts = useVolumeAlerts(drawingToken, timeInterval, {
    redraw: () => drawCanvasRef.current?.(),
    getLabel: () => (dataSource === 'hyperliquid' ? (hlPair?.base || '') : resolveTokenName(data.currentToken)),
    onTrigger: (n) => addMessage?.(`🔔 vol ≥ ${fmtVol(n.level)} (bar ${fmtVol(n.vol)})`, 'alert',
      (dataSource !== 'hyperliquid' && data.currentToken) ? { mint: data.currentToken, name: resolveTokenName(data.currentToken) } : undefined),
  })
  const volAlertsRef = useRef(volAlerts)
  volAlertsRef.current = volAlerts
  const volLaneGeomRef = useRef(null)     // last painted lane { LANE_H, maxVol, cols, lineEnd }
  const volAlertLayerRef = useRef(null)
  const volAlertDragRef = useRef(null)    // { id, level, moved } while dragging
  const [volAlertDragId, setVolAlertDragId] = useState(null)
  // Volume pane scale (volalert2): max(visible bars, every alert level on
  // this chart x headroom). The dragged alert counts at its live drag level.
  const VOL_ALERT_HEADROOM = 1.08
  // volalert3: the scale fits the bars only, EXCEPT while an alert is
  // selected for editing (click/tap the bell) -> stretches to that alert's
  // level x headroom. Select/deselect eases the scale over ~200ms.
  const volAlertSelRef = useRef(null)     // id of the alert selected for edit
  const volScaleAnimRef = useRef(null)    // { from, t0 } scale ease
  const volScaleRafRef = useRef(0)
  const volLaneScaleTarget = useStableCallback((barsMax) => {
    const list = volAlertsRef.current?.alertsRef.current || []
    const d = volAlertDragRef.current
    const sel = volAlertSelRef.current
    let top = 0
    for (const a of list) {
      if (a.id !== sel && !(d && d.id === a.id)) continue
      const lv = (d && d.id === a.id) ? d.level : a.level
      if (lv > top) top = lv
    }
    return Math.max(barsMax || 0, top * VOL_ALERT_HEADROOM)
  })
  const volLaneScale = useStableCallback((barsMax) => {
    const target = volLaneScaleTarget(barsMax)
    const an = volScaleAnimRef.current
    if (!an) return target
    const p = (performance.now() - an.t0) / 200
    if (p >= 1 || !(an.from > 0)) { volScaleAnimRef.current = null; return target }
    if (!volScaleRafRef.current) volScaleRafRef.current = requestAnimationFrame(() => { volScaleRafRef.current = 0; drawCanvasRef.current?.() })
    const e = 1 - Math.pow(1 - p, 3)
    return an.from + (target - an.from) * e
  })
  const setVolAlertSel = useStableCallback((id) => {
    if (volAlertSelRef.current === id) return
    const layer = volAlertLayerRef.current
    if (layer) for (const el of layer.children) {
      const b = el.querySelector('.va-bell')
      if (b) b.classList.toggle('va-sel', el.dataset.vaid === id)
    }
    volAlertSelRef.current = id
    const g = volLaneGeomRef.current
    volScaleAnimRef.current = g && g.maxVol > 0 ? { from: g.maxVol, t0: performance.now() } : null
    drawCanvasRef.current?.()
  })
  // Click / tap anywhere outside the selected bell -> deselect, scale back to bars.
  useEffect(() => {
    const onDown = (ev) => {
      const sel = volAlertSelRef.current
      if (!sel) return
      const host = ev.target?.closest?.('[data-vaid]')
      if (host && host.dataset.vaid === sel) return
      setVolAlertSel(null)
    }
    const onKey = (ev) => { if (ev.key === 'Escape' && volAlertSelRef.current) setVolAlertSel(null) }
    document.addEventListener('pointerdown', onDown, true)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('pointerdown', onDown, true); document.removeEventListener('keydown', onKey) }
  }, [setVolAlertSel])
  // volalert3: ↑/× hover bridge — one hover area (bell + bridge + controls)
  // with a 300ms hide delay; × is armed only once it has been visible 250ms.
  const vaShowCtl = (b) => { if (!b) return; clearTimeout(b._vaHT); if (!b.classList.contains('va-hover')) { b.classList.add('va-hover'); b._vaShownAt = performance.now() } }
  const vaHideCtl = (b) => { if (!b) return; clearTimeout(b._vaHT); b._vaHT = setTimeout(() => b.classList.remove('va-hover'), 300) }
  // Position the DOM bell/line nodes from the last painted lane geometry.
  // Called by the full draw and after every render; refs only.
  const placeVolAlerts = useStableCallback(() => {
    const layer = volAlertLayerRef.current
    if (!layer) return
    const g = volLaneGeomRef.current
    const list = volAlertsRef.current?.alertsRef.current || []
    if (g && layer.style.height !== `${g.LANE_H}px`) layer.style.height = `${g.LANE_H}px`
    const drag = volAlertDragRef.current
    for (const el of layer.children) {
      const id = el.dataset.vaid
      const a = list.find(x => x.id === id)
      const cols = g?.cols
      if (!a || !cols || !cols.length || !(g.maxVol > 0)) { el.style.display = 'none'; continue }
      const dragging = !!(drag && drag.id === id)
      const level = dragging ? drag.level : a.level
      // Always centred on its OWN anchor column (pan / zoom / drag / pinned);
      // drag only moves it vertically. Anchor scrolled out of view -> hidden.
      const offLeft = false
      const c = a.t < cols[0].t ? null : cols.find(c => c.t >= a.t)
      if (!c) { el.style.display = 'none'; continue }
      const x = (c.x0 + c.x1) / 2
      if (x >= g.lineEnd) { el.style.display = 'none'; continue }
      let y = g.LANE_H - (level / g.maxVol) * g.LANE_H
      const above = y < 0
      y = Math.max(6, Math.min(g.LANE_H, y))
      el.style.display = ''
      el.style.transform = `translate(${x}px, ${y}px)`
      el.style.opacity = (dragging || volAlertSelRef.current === id) ? '1' : ((above || offLeft) ? '0.35' : '0.7')
      const up = el.querySelector('[data-va-up]')
      if (up) up.style.display = above ? '' : 'none'
      const val = el.querySelector('[data-va-val]')
      if (val) { val.style.display = dragging ? '' : 'none'; if (dragging) val.textContent = fmtVol(level) }
      const chip = el.querySelector('[data-va-chip]')
      if (chip) chip.title = `Volume alert ≥ ${fmtVol(level)}\nDrag to move · right-click to edit`
    }
  })
  useLayoutEffect(() => { placeVolAlerts() })
  const beginVolAlertDrag = useStableCallback((a, e) => {
    if (e.button != null && e.button !== 0) return   // right button -> contextmenu (edit menu)
    e.preventDefault(); e.stopPropagation()
    const layer = volAlertLayerRef.current
    const g0 = volLaneGeomRef.current
    if (!layer || !g0 || !(g0.maxVol > 0)) return
    const rect = layer.getBoundingClientRect()
    setVolAlertSel(a.id)    // click/tap = select for edit -> scale includes this alert
    volAlertDragRef.current = { id: a.id, level: a.level, moved: false }
    setVolAlertDragId(a.id)
    // Show ↑/× while touched/dragged (phone has no hover); linger 2.5s after.
    const bellEl = e.currentTarget?.closest?.('.va-bell')
    if (bellEl) { clearTimeout(bellEl._vaT); bellEl.classList.add('va-active'); vaShowCtl(bellEl) }
    // Scale frozen at drag start inside the pane (bell tracks the cursor);
    // ABOVE the pane the level keeps rising, S*exp(-y/H) — same value and
    // slope at the top edge, so it's continuous — and the lane scale grows
    // to follow (draw uses max(bars, level*headroom)), no jumps.
    // Map against the EDIT scale (stretched to this alert), not the eased frame.
    const S = volLaneScaleTarget(g0.barsMax ?? g0.maxVol) || g0.maxVol, H0 = g0.LANE_H
    const y0 = e.clientY
    let rafPending = false
    const onMove = (ev) => {
      const d = volAlertDragRef.current
      if (!d) return
      if (!d.moved && Math.abs(ev.clientY - y0) < 3) return   // a click selects; doesn't nudge the level
      const ly = Math.min(H0, ev.clientY - rect.top)
      const raw = ly >= 0 ? ((H0 - ly) / H0) * S : S * Math.exp(-ly / H0)
      const lv = roundVol(raw)
      if (!(lv > 0)) return
      d.level = lv; d.moved = true
      const g = volLaneGeomRef.current || g0
      const want = volLaneScaleTarget(g.barsMax ?? g.maxVol)
      if (Math.abs(want - g.maxVol) > g.maxVol * 1e-3) {
        if (!rafPending) { rafPending = true; requestAnimationFrame(() => { rafPending = false; drawCanvasRef.current?.() }) }
      } else placeVolAlerts()
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      const d = volAlertDragRef.current
      volAlertDragRef.current = null
      setVolAlertDragId(null)
      if (bellEl) { clearTimeout(bellEl._vaT); bellEl._vaT = setTimeout(() => bellEl.classList.remove('va-active'), 2500) }
      if (d && d.moved && d.level !== a.level) volAlertsRef.current.update(d.id, d.level)
      else drawCanvasRef.current?.()
    }
    window.addEventListener('pointermove', onMove, { passive: true })
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
  })
  const openVolAlertMenu = useStableCallback((a, e) => {
    e.preventDefault(); e.stopPropagation()
    setVolSettings({ show: true, x: e.clientX + 4, y: Math.max(6, Math.min(e.clientY, window.innerHeight - 240)), openLeft: e.clientX > window.innerWidth - 420, alert: { mode: 'edit', id: a.id } })
  })
  // ─── Absorbers: click / tap an Absorption outline -> who absorbed (sol only) ───
  // No per-frame bookkeeping: the still click is mapped back to (candle, row)
  // with the same x math as the crosshair time label and yToMcap, then checked
  // against absorptionRef. Only the real Absorption outlines - not churn
  // background, not ARD markers.
  const [absorbersAt, setAbsorbersAt] = useState(null)
  const absTapRef = useRef(null)
  const closeAbsorbers = useCallback(() => setAbsorbersAt(null), [])
  const tryOpenAbsorbers = (cx, cy, dry) => {
    // [Absorbers] traces: localStorage.cheddarDebug = '1' (absorbers-debug-20261016)
    const _tag = dry ? '[Absorbers] hit-test(down)' : '[Absorbers] open(up)'
    const no = (why, extra) => { if (dbgOn()) dbg(`${_tag} NO: ${why} ` + (extra ? JSON.stringify(extra) : '')); return false }
    if (!showAbsorptionRef.current) return no('absorption indicator off')
    if (dataSource === 'hyperliquid') return no('hyperliquid chart')
    if (!data.currentToken) return no('no current token')
    const cells = absorptionRef.current
    if (!cells || !cells.size) return no('no absorption cells computed', { cells: cells ? cells.size : null })
    const canvas = canvasRef.current, f = lastFrameRef.current
    const candles = chartDataRef.current?.candles
    const mcaps = visibleSortedMcapsRef.current, RH = dynamicRowHeightRef.current
    if (!canvas || !f || !(f.cw > 0) || !candles?.length || !(mcaps?.length >= 2) || !(RH > 0)) {
      return no('geometry not ready', { canvas: !!canvas, frame: !!f, cw: f?.cw, candles: candles?.length, mcaps: mcaps?.length, RH })
    }
    // 1) Painted-rect hit-test (exact: same space the outline was drawn in).
    {
      const PAD = 6
      let best = null, bestD = Infinity
      for (const R of absRectsRef.current.values()) {
        const el = R.el
        if (!el || !el.isConnected || !(el.width > 0)) continue
        const br = el.getBoundingClientRect()
        const sx = el.width / br.width, sy = el.height / br.height
        const px = (cx - br.left) * sx, py = (cy - br.top) * sy
        const padX = PAD * sx, padY = PAD * sy
        const x0 = Math.min(R.x, R.x + R.w), x1 = Math.max(R.x, R.x + R.w)
        const y0 = Math.min(R.y, R.y + R.h), y1 = Math.max(R.y, R.y + R.h)
        if (px < x0 - padX || px > x1 + padX || py < y0 - padY || py > y1 + padY) continue
        const d = Math.hypot(px - (x0 + x1) / 2, py - (y0 + y1) / 2)
        if (d < bestD) { bestD = d; best = { R, rowCss: (y1 - y0) / sy } }
      }
      if (best) {
        const { R } = best
        const c = R.candle
        const ci = candles.indexOf(c)
        const iv0 = timeIntervalRef?.current || timeInterval || 60
        const nxt = ci >= 0 ? candles[ci + 1] : null
        const t0 = c.startTime
        const t1 = (nxt && nxt.startTime > t0 ? nxt.startTime : t0 + iv0 * 1000) - 1
        // price band: neighbouring visible rows around the outlined level
        let stepR = 0
        for (let k = 0; k < mcaps.length - 1; k++) {
          const a = mcaps[k], b = mcaps[k + 1]
          if ((a - R.mcap) * (b - R.mcap) <= 0) { stepR = Math.abs(a - b); break }
        }
        if (!(stepR > 0)) stepR = Math.abs(mcaps[0] - mcaps[mcaps.length - 1]) / (mcaps.length - 1)
        if (dbgOn()) dbg(`${_tag} HIT(rect) ` + JSON.stringify({ startTime: t0, side: R.side, mcap: R.mcap, rects: absRectsRef.current.size }))
        if (dry) return true
        setAbsorbersAt({ mint: data.currentToken, t0, t1, side: R.side, lo: R.mcap - stepR, hi: R.mcap + 2 * stepR, x: cx, y: cy })
        return true
      }
      if (dbgOn()) dbg(`${_tag} rect miss, falling back ` + JSON.stringify({ rects: absRectsRef.current.size }))
    }
    // 2) Fallback: lattice math (old path).
    const r = canvas.getBoundingClientRect()
    const x = cx - r.left, y = cy - r.top
    const i = Math.floor(f.endIdx + 1 - (f.plotWidth + f.panOffsetPx - x) / f.cw)
    const c = i >= 0 ? candles[i] : null
    if (!c || c.startTime == null) return no('no candle under x', { x, y, i, endIdx: f.endIdx, cw: f.cw, n: candles.length })
    let hit = cells.get(c)
    let byTime = false
    if (!hit) for (const [k, v] of cells) { if (k && k.startTime === c.startTime) { hit = v; byTime = true; break } }
    if (!hit) {
      // nearest outlined candle, to show whether x is off by a column or more
      let near = null
      for (let d = 1; d <= 3 && !near; d++) for (const j of [i - d, i + d]) {
        const cj = candles[j]; if (!cj) continue
        if (cells.get(cj) || [...cells.keys()].some(k => k && k.startTime === cj.startTime)) { near = { dIdx: j - i }; break }
      }
      return no('no outline cell for candle', { i, startTime: c.startTime, x: Math.round(x), cells: cells.size, nearestOutline: near })
    }
    // row step at the hit level (log lattice: steps vary by height)
    let step = 0
    for (let k = 0; k < mcaps.length - 1; k++) {
      const a = mcaps[k], b = mcaps[k + 1]
      if ((a - hit.mcap) * (b - hit.mcap) <= 0) { step = Math.abs(a - b); break }
    }
    if (!(step > 0)) step = Math.abs(mcaps[0] - mcaps[mcaps.length - 1]) / (mcaps.length - 1)
    const m = yToMcapUtil(y, mcaps, RH)
    const okLattice = Math.abs(m - hit.mcap) <= step * 2
    // Painted-space check: the outline is drawn through the frame projection
    // (camera scale/ty + residual), which the plain lattice inverse above
    // ignores. Accept a tap within 2 painted rows of the drawn outline too.
    let yPainted = null, okPainted = false
    try { yPainted = f.yOf ? f.yOf(hit.mcap) : null } catch { yPainted = null }
    const rowPx = RH * (f.camScale || 1)
    if (Number.isFinite(yPainted)) okPainted = Math.abs(y - yPainted) <= Math.max(2 * rowPx, 10)
    if (!okLattice && !okPainted) {
      return no('tap too far from outline row', { y: Math.round(y), yPainted: yPainted != null ? Math.round(yPainted) : null, rowPx: +rowPx.toFixed(1),
        tapMcap: m, outlineMcap: hit.mcap, rowsAway: +(Math.abs(m - hit.mcap) / step).toFixed(2), limitRows: 2, side: hit.side })
    }
    const iv = timeIntervalRef?.current || timeInterval || 60
    const nxt = candles[i + 1]
    const t0 = c.startTime
    const t1 = (nxt && nxt.startTime > t0 ? nxt.startTime : t0 + iv * 1000) - 1
    if (dbgOn()) dbg(`${_tag} HIT(fallback) ` + JSON.stringify({ i, startTime: c.startTime, byTime, side: hit.side, mcap: hit.mcap, okLattice, okPainted }))
    if (dry) return true // hit-test only (pointerdown)
    setAbsorbersAt({ mint: data.currentToken, t0, t1, side: hit.side, lo: hit.mcap - step, hi: hit.mcap + 2 * step, x: cx, y: cy })
    return true
  }
  useEffect(() => { setAbsorbersAt(null) }, [data.currentToken, dataSource, showAbsorption])
  // hold-pct-big-20261016: batch-fetch hold % for the zones on screen.
  // Every 5 s: refetch if the visible set changed, else every 60 s (live).
  useEffect(() => {
    if (!showChurnZones || dataSource === 'hyperliquid' || !data.currentToken) return
    const mint = data.currentToken
    if (holdPctRef.current.mint !== mint) { holdPctRef.current = { mint, map: new Map() }; holdFetchRef.current = { key: '', at: 0, busy: false } }
    let alive = true
    const tick = () => {
      const F = holdFetchRef.current
      if (!alive || F.busy || document.hidden) return
      const boxes = zoneHoldVisRef.current.slice(-40)
      if (!boxes.length) return
      const key = boxes.map(b => b.key).join(',')
      if (key === F.key && Date.now() - F.at < 60_000) return
      F.busy = true
      fetchHoldPct({ mint, boxes })
        .then(d => {
          if (!alive || holdPctRef.current.mint !== mint) return
          const m = holdPctRef.current.map
          for (const [k, v] of Object.entries(d?.boxes || {})) m.set(k, v)
          if (m.size > 400) for (const k of [...m.keys()].slice(0, m.size - 400)) m.delete(k)
          F.key = key; F.at = Date.now()
          scheduleCanvasDrawRef.current?.()
        })
        .catch(e => { F.at = Date.now() - 45_000; if (dbgOn()) dbg('[HoldPct] fetch failed ' + (e?.message || e)) })   // retry in ~15 s
        .finally(() => { F.busy = false })
    }
    const first = setTimeout(tick, 1500)
    const id = setInterval(tick, 5000)
    return () => { alive = false; clearTimeout(first); clearInterval(id) }
  }, [data.currentToken, dataSource, showChurnZones])
  // hold-label-style-20261016: hide a zone's label while the mouse is over it.
  useEffect(() => {
    if (!showChurnZones) { zoneHoverKeyRef.current = null; return }
    const setKey = (k) => {
      if (k === zoneHoverKeyRef.current) return
      zoneHoverKeyRef.current = k
      scheduleCanvasDrawRef.current?.()
    }
    const onMove = (e) => {
      if (e.pointerType !== 'mouse') return
      const R = zoneHoldRectsRef.current, cv = canvasRef.current
      if (!R.length || !cv || !(cv.width > 0)) { if (zoneHoverKeyRef.current) setKey(null); return }
      const br = cv.getBoundingClientRect()
      if (!(br.width > 0) || !(br.height > 0)) return
      const px = (e.clientX - br.left) * cv.width / br.width, py = (e.clientY - br.top) * cv.height / br.height
      let k = null
      for (const r of R) if (px >= r.x0 && px <= r.x1 && py >= r.y0 && py <= r.y1) { k = r.key; break }
      setKey(k)
    }
    const onOut = (e) => { if (!e.relatedTarget) setKey(null) }   // mouse left the window
    window.addEventListener('pointermove', onMove, { passive: true })
    window.addEventListener('pointerout', onOut, { passive: true })
    return () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerout', onOut); zoneHoverKeyRef.current = null }
  }, [showChurnZones])
  // abs-cell-hover-20261017: highlight the outline of the absorption cell under
  // the mouse. Hit-tests the painted rects (absRectsRef, same as the absorbers
  // click) and redraws only when the hovered cell changes.
  useEffect(() => {
    if (!showAbsorption) { absHoverKeyRef.current = null; return }
    const setKey = (k) => {
      if (k === absHoverKeyRef.current) return
      absHoverKeyRef.current = k
      scheduleCanvasDrawRef.current?.()
    }
    const onMove = (e) => {
      if (e.pointerType !== 'mouse') return
      if (e.buttons) { if (absHoverKeyRef.current != null) setKey(null); return }   // dragging
      const rects = absRectsRef.current
      if (!rects || !rects.size) { if (absHoverKeyRef.current != null) setKey(null); return }
      const brs = new Map()
      let k = null
      for (const R of rects.values()) {
        const el = R.el
        if (!el || !el.isConnected || !(el.width > 0)) continue
        let br = brs.get(el)
        if (!br) { br = el.getBoundingClientRect(); brs.set(el, br) }
        if (!(br.width > 0) || !(br.height > 0)) continue
        const sx = el.width / br.width, sy = el.height / br.height
        const px = (e.clientX - br.left) * sx, py = (e.clientY - br.top) * sy
        const x0 = Math.min(R.x, R.x + R.w), x1 = Math.max(R.x, R.x + R.w)
        const y0 = Math.min(R.y, R.y + R.h), y1 = Math.max(R.y, R.y + R.h)
        if (px >= x0 && px <= x1 && py >= y0 && py <= y1) { k = R.candle?.startTime ?? null; break }
      }
      setKey(k)
    }
    const onOut = (e) => { if (!e.relatedTarget) setKey(null) }   // mouse left the window
    window.addEventListener('pointermove', onMove, { passive: true })
    window.addEventListener('pointerout', onOut, { passive: true })
    return () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerout', onOut); absHoverKeyRef.current = null }
  }, [showAbsorption, data.currentToken])
  // [Absorbers] debug: the REAL pointer target of every press (capture phase,
  // runs before any stopPropagation). Shows if an overlay sits over the body.
  useEffect(() => {
    const onCap = (e) => {
      if (!dbgOn()) return
      const t = e.target
      const cls = typeof t?.className === 'string' ? t.className : (t?.className?.baseVal || '')
      dbg('[Absorbers] window capture pointerdown ' + JSON.stringify({
        target: `${t?.tagName || '?'}.${cls}`,
        inBody: !!bodyScrollRef.current?.contains(t),
        topAtPoint: (() => { const el = document.elementFromPoint(e.clientX, e.clientY); return el ? `${el.tagName}.${typeof el.className === 'string' ? el.className : ''}` : null })(),
      }))
    }
    window.addEventListener('pointerdown', onCap, true)
    return () => window.removeEventListener('pointerdown', onCap, true)
  }, [])

  // Volume Profile Value Area overlay (POC / VAH / VAL + band) + settings popup.
  const VA_DEFAULTS = { vaPct: 70, showPOC: true, showBounds: true, showBand: true, pocColor: '#ffaa00', bandColor: '#5b8def', weight: 1, vpBuy: null, vpSell: null, vpSide: 'right', vpMode: 'profile', vpText: true }
  // Volume Profile is now a toggleable indicator like the rest. It used to be
  // unconditional — the lane simply always existed — so there was no way to
  // turn it off or to say which edge it hangs from.
  const [showVolumeProfile, setShowVolumeProfile] = useScopedSetting('solcheddar:showVolumeProfile', true)
  const [vpSettings, setVpSettings] = useState({ show: false, x: 0, y: 0, openLeft: false })
  const [showValueArea, setShowValueArea] = useScopedSetting('solcheddar:showValueArea', false)
  const [vaConfigStored, setVaConfig] = useScopedSetting('solcheddar:vaConfig', {})
  const vaConfig = useMemo(() => ({ ...VA_DEFAULTS, ...(vaConfigStored || {}) }), [vaConfigStored])
  const [vaSettings, setVaSettings] = useState({ show: false, x: 0, y: 0 })
  const showValueAreaRef = useRef(showValueArea)
  const vaConfigRef = useRef(vaConfig)
  const valueAreaRef = useRef(null)
  // Which VA line the cursor is over ('poc' | 'vah' | 'val' | null). Set by
  // the desktop hover path (handleBodyMouseMove); read by the draw loop to
  // highlight the hovered line and by onPointerDown to open its settings.
  const hoveredVaLineRef = useRef(null)
  // REPAINT ON VP SETTINGS CHANGE. The draw effect keys on view / layout /
  // data / theme, not vaConfig, so a mode, colour, side or text flip from the
  // settings menu only reached the canvas on the next data tick — instant on
  // a live mint, a visible hang on a quiet futures chart, and outright broken
  // for a Profile↔Footprint toggle that has to be seen to be believed. Same
  // TDZ shape as the isActiveChart effect: scheduleCanvasDraw is declared
  // ~4100 lines below, so it's called in the body (post-render, initialised)
  // and kept OUT of the dep array. drawCanvasRef is assigned inline during
  // render, so the deferred rAF can't fire against a null ref.
  useEffect(() => {
    scheduleCanvasDraw()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vaConfig])
  // optvol-20261103: Opt Vol Levels -- top 3 call/put strikes by volume on the
  // nearest expiry. Server (/stk/optvol, via the Schwab relay) ranks; we poll 60s.
  const [showOptVol, setShowOptVol] = useScopedSetting('solcheddar:showOptVol', false)
  const optVolRef = useRef(null)
  const _ovInfo = dataSource === 'hyperliquid' && hlPair?.base ? stkInfo(hlPair.base) : null
  const optVolSym = _ovInfo && !_ovInfo.opt ? _ovInfo.sym : null
  useEffect(() => {
    optVolRef.current = null
    scheduleCanvasDraw()
    if (!showOptVol || !optVolSym) return
    let dead = false
    const load = async () => {
      try {
        const r = await fetch(`${OPTVOL_BASE}/stk/optvol?symbol=${encodeURIComponent('STK:' + optVolSym)}`)
        const j = await r.json()
        if (!dead && r.ok && !j.error) { optVolRef.current = j; scheduleCanvasDraw() }
      } catch {}
    }
    load()
    const t = setInterval(load, 60000)
    return () => { dead = true; clearInterval(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOptVol, optVolSym])
  // VP bar-colour overrides are theme-scoped — switching themes (ThemePicker)
  // clears them so the picked theme's colours take over. Keyed on a string of
  // id + the theme's two VP colours (primitive dep, per project convention) so
  // an in-theme VP colour EDIT clears them too — otherwise picking a Volume
  // profile colour in the theme menu looks like a dead control while
  // vaConfig.vpBuy/vpSell are set.
  const themeVpKey = `${themeId}|${theme?.volumeProfile?.buy || ''}|${theme?.volumeProfile?.sell || ''}`
  const vaThemeIdRef = useRef(themeVpKey)
  useEffect(() => {
    if (vaThemeIdRef.current !== themeVpKey) {
      vaThemeIdRef.current = themeVpKey
      setVaConfig(c => (c.vpBuy == null && c.vpSell == null) ? c : { ...c, vpBuy: null, vpSell: null })
    }
  }, [themeVpKey])

  // Smoketrail flow direction ('left' | 'right'), toggled from the right-click
  // menu. 'left' (default) ages trades into the chart body; 'right' ages them
  // toward the y-axis gutter. Read by OrderflowTrail's RAF loop each frame, so
  // the toggle takes effect on the next frame with no remount.
  const [trailFlowDir, setTrailFlowDir] = useScopedSetting('solcheddar:trailFlowDir', 'left')
  // Hover state for the trail's live-edge handle (AVWAP-style glow) and the
  // little flow-direction menu opened by right-clicking that handle.
  const [trailHovered, setTrailHovered] = useState(false)
  const [trailFlowMenu, setTrailFlowMenu] = useState(null) // {x,y} | null
  // Trail colours. null = follow the theme's candle up/down (the old
  // behaviour); THEME in the menu resets to that.
  const [trailColors, setTrailColors] = useScopedSetting('solcheddar:trailColors', { up: null, down: null })
  // Menu frame / fit / outside-click / Esc: ChartMenu (render site below).
  const trailHoveredRef = useRef(false)
  trailHoveredRef.current = trailHovered

  // Resizable subgraph height. Persisted to localStorage so the user's
  // chosen size sticks across reloads. Clamped 60–400px — any wider eats
  // the main chart, any shorter and bars become unreadable.
  const CVD_MIN_HEIGHT = 60
  const CVD_MAX_HEIGHT = 400
  const [cvdHeightStored, setCvdHeight] = useScopedSetting('solcheddar:cvdHeight', 100)
  const cvdHeight = Math.max(CVD_MIN_HEIGHT, Math.min(CVD_MAX_HEIGHT, Number(cvdHeightStored) || 100))
  // Drag state for the resize handle. Captured on pointerdown, tracked
  // on window pointermove (so it works even if the cursor leaves the
  // 4px-tall handle). Released on pointerup. Triggers a bucket recompute
  // on release because the main chart's containerHeight just shifted.
  const cvdResizeRef = useRef(null) // { startY, startHeight }
  const [isCvdResizing, setIsCvdResizing] = useState(false)
  const onCvdResizeStart = (e) => {
    e.preventDefault()
    cvdResizeRef.current = { startY: e.clientY, startHeight: cvdHeight }
    setIsCvdResizing(true)
  }
  useEffect(() => {
    if (!isCvdResizing) return
    const onMove = (e) => {
      const ref = cvdResizeRef.current
      if (!ref) return
      // Drag UP = grow subgraph (it's anchored at the bottom of the
      // chart body, so dragging the handle upward extends it taller).
      const dy = ref.startY - e.clientY
      const next = Math.max(CVD_MIN_HEIGHT, Math.min(CVD_MAX_HEIGHT, ref.startHeight + dy))
      setCvdHeight(next)
    }
    const onUp = () => {
      cvdResizeRef.current = null
      setIsCvdResizing(false)
      // Main chart's containerHeight just changed (ResizeObserver will
      // pick it up), but force a bucket recompute too so row sizing
      // settles immediately rather than waiting for the next user
      // interaction to trip the auto-rule.
      setForceBucketRecompute(c => c + 1)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [isCvdResizing])
  const cvd = useCumulativeDelta(chartData.candles, { enabled: showCvdSubgraph, structuralVersion: chartData.structuralVersion })

  // Treadmill base: the candle count the treadmill last applied (layout
  // effect below). Declared here because viewportInfo reads it.
  const lastTotalRef = useRef(chartData.candles.length)
  // Armed on a real TF change (not mount). Cleared once the new-interval
  // candles are in hand and the view end sits on the live column.
  const tfLiveAnchorRef = useRef(false)
  // Mirror of scroll.isDragging. `scroll` is declared below useChartScroll,
  // so reading scroll.isDragging here would TDZ. The mirror is updated by a
  // tiny effect placed AFTER useChartScroll. rangeCandles reads it (the
  // left sliver column is ranged while a drag can slide it into view).
  const [isDraggingMirror, setIsDraggingMirror] = useState(false)
  const viewportInfo = useMemo(() => {
    const total = chartData.candles.length
    if (total === 0) return { startIdx: 0, endIdx: -1, visibleCount: 0, pxPerCandle: 0 }

    // Inclusive SoT: winSize = N candles on screen → startIdx = endIdx - N + 1.
    // Same formula as drawCanvas / cols / getSeamX (rounded int win). pxPerCandle
    // still divides by the React int; paint geometry uses the float ref.
    const mainPlotWidth = Math.max(1, containerWidthRef.current - RIGHT_PADDING - FIXED_OVERLAY_WIDTH)
    // ── READ THE LIVE WINDOW, NOT THE COMMITTED ONE ──────────────────────
    // viewWindowSize is React state, committed only when the wheel ease
    // lands. The ease moves viewWindowSizeRef, a float, every tick. Reading
    // the state meant this returned the OLD window for the whole gesture, so
    // everything downstream — rangeCandles, the grid memo, fit — rescanned
    // the same candles and produced the same answer. That is why fit only
    // caught up when you stopped scrolling, and why bumping the memo's
    // invalidation tick alone changed nothing: it re-ran a function of a
    // value that had not moved.
    //
    // Pan never had the problem because it moves viewEndIndex, which IS
    // state and IS a dep.
    //
    // READ THE STATE, NOT THE REF. This memo runs during render; the layout
    // effect publishes viewWindowSizeRef AFTER render. Reading the ref here
    // gave last commit's window while state already held this one's — fit
    // computed one frame behind the columns it was supposed to be framing.
    // State is the source now; the ref is its post-render mirror.
    //
    // Rounded for indices (columns are integers) and kept float for
    // pxPerCandle, which is the same split drawCanvas already makes.
    const _winLive = Math.max(1, +viewWindowSize || 1)
    const _winInt = Math.max(1, Math.round(_winLive))
    const pxPerCandle = mainPlotWidth / _winLive

    // Body-wheel park: state may still be the follow sentinel (-1) while the
    // ref already holds the cursor's end. Resolving -1 here fit the lattice
    // to the live pad and the right edge fought the glide.
    const _gWheel = zoomGlideRef.current
    const _parkedEnd = (_gWheel && _gWheel.wheelAnchor && Number.isFinite(_gWheel.placedEnd))
      ? _gWheel.placedEnd
      : null
    const _endSrc = _parkedEnd != null
      ? _parkedEnd
      : ((xWin?.wheelParkRef?.current && viewEndIndexRef.current !== -1)
          ? viewEndIndexRef.current
          : viewEndIndex)
    // ATOMIC TREADMILL. A length change (live column appended OR older
    // history prepended — slabs, reconcile, backfill) shifts every index by
    // the delta; the treadmill layout effect moves viewEndIndex by the same
    // delta, but only AFTER this render. For that one pass the stale end
    // sliced different, older candles: the Y scan saw off-screen history,
    // the expand-only fit widened (and held), the sizer rebucketed, and the
    // next pass flipped back — the visible lo..hi alternation and the
    // "scales on off-screen data". Apply the pending delta here, under the
    // exact conditions the treadmill will, so every pass slices the same
    // candles (tail-relative, i.e. the same candles by time for a prepend).
    // When the effect then commits, its delta is 0 against this result.
    let _endUse = _endSrc
    // Same for the follow sentinel's first concretization: the treadmill
    // turns -1 into total-1 + rest gap on a count change; do it here too.
    if (_endSrc === -1 && total >= 2 && autoScrollEnabled
        && !(_gWheel && _gWheel.wheelAnchor) && !tfLiveAnchorRef.current
        && !(xWin?.wheelParkRef?.current && viewEndIndexRef.current === -1)
        && total !== lastTotalRef.current) {
      _endUse = total - 1 + loadXGap(chartId, viewWindowSizeRef.current, AUTO_PADDING)
    }
    if (_endSrc !== -1 && total >= 2 && autoScrollEnabled
        && !(_gWheel && _gWheel.wheelAnchor) && !tfLiveAnchorRef.current
        && !(xWin?.wheelParkRef?.current && viewEndIndexRef.current === -1)) {
      const _dTm = total - lastTotalRef.current
      if (_dTm !== 0 && lastTotalRef.current >= 2) {
        const { startWall: _lw, endWall: _fw } = xWalls(total, viewWindowSizeRef.current)
        _endUse = Math.max(_lw, Math.min(_fw, _endSrc + _dTm))
      }
    }
    // Parked body-wheel may sit past the live edge. Do not pull it back.
    const endIdx = resolveEndIdx(_endUse, total, !(_parkedEnd != null || (xWin?.wheelParkRef?.current && _endUse !== -1)))
    const startIdx = startIdxFor(endIdx, _winInt)
    const visibleCount = endIdx - startIdx + 1

    return { startIdx, endIdx, visibleCount, pxPerCandle }
    // viewWindowSize is a FLOAT mid-ease and a dep, so this re-runs every
    // frame of a wheel — the same thing a drag gets from viewEndIndex.
  }, [chartData.candles.length, viewEndIndex, viewWindowSize, autoScrollEnabled])

  // ── Treadmill autoscroll ──
  // A new column shifts viewEndIndex by the same count, so the candle that
  // was on screen stays in that screen slot and the new live column takes
  // the place the previous one held. viewEndIndex === -1 is the init/reset
  // sentinel (resolves to the default AUTO_PADDING rest); the first real
  // candle below converts it to a concrete index so the gap is well-defined
  // from then on. Dragging changes the gap (handlePointerMove) and does NOT
  // disable autoscroll, so the treadmill continues from the new offset.
  // (lastTotalRef is declared above viewportInfo, which reads it.)
  // Whether the live (newest) candle is within the visible window — drives
  // vertical follow vs fit in the memo AND the bucket sizer's stretch, kept in
  // a ref so the sizer (a ref-reading callback) sees the same decision the
  // memo renders. True when at/ahead of the last real candle, or at init (-1).
  const liveInViewRef = useRef(true)
  useEffect(() => {
    const total = chartData.candles.length
    liveInViewRef.current = isLiveInView(viewEndIndexRef.current, total)
  }, [viewEndIndex, chartData.candles.length])
  // LAYOUT effect, and the ref is written in the same tick as the state: this
  // runs before paint, so the first frame drawn with a new candle count (a TF
  // switch DOWN adds hundreds at once) already uses the matching end index.
  // As a passive effect it ran after that paint — one frame drew the new,
  // longer array against the old end index and the chart flashed left.
  useLayoutEffect(() => {
    const total = chartData.candles.length
    const prev = lastTotalRef.current
    lastTotalRef.current = total
    if (total < 2) {
      // TOTAL COLLAPSED (reconnect resetChart / rebuild wipe). The old bare
      // return kept the stale concrete viewEndIndex while SWALLOWING the
      // wipe's huge negative delta (lastTotalRef is updated above, so the
      // next growth tick applies only its own +delta). The stale index then
      // clamped to the FORWARD wall (total-1+FUTURE_PADDING = +40): the view
      // sat 40 future-slots ahead of a 2-candle chart, all data off-screen
      // LEFT at winSize < 40 — the post-reconnect blank chart that "takes
      // forever" to drag back. A wiped chart has no gap worth preserving:
      // re-arm the -1 autoscroll sentinel so every draw resolves to the live
      // edge while the backfill rebuild streams in.
      if (viewEndIndexRef.current !== -1) {
        viewEndIndexRef.current = -1
        setViewEndIndex(-1)
        // Mid-drag wipe: the anchor must not keep the stale index either —
        // park it at the live-edge equivalent; the drag walls re-clamp.
        if (scroll?.isDraggingRef?.current) scroll.dragStartRef.current.viewEndIndex = Math.max(0, total - 1 + AUTO_PADDING)
      }
      return
    }
    if (zoomGlideRef.current?.wheelAnchor) {
      // Active body-wheel glide owns end. A column that lands mid-glide
      // must not fight the cursor anchor. lastTotalRef is already caught
      // up, so the gap is not eaten in one jump when the glide lands.
      // A finished park is not this: the view still steps on the next column.
      return
    }
    if (xWin?.wheelParkRef?.current && viewEndIndexRef.current === -1) {
      // Park is a concrete end written on the ref. Do not turn a still--1
      // ref back into the live pad on top of it.
      return
    }
    // Timeframe fold owns the end index. A length delta here is a rebin,
    // not a new column — one assignment happens when the new series lands.
    if (tfLiveAnchorRef.current) return
    if (!autoScrollEnabled) {
      // Autoscroll off (shouldn't happen in the always-on model, but kept
      // defensive): legacy "keep up only if pinned to the latest" behavior.
      if (viewEndIndexRef.current !== -1 && viewEndIndexRef.current === total - 2) {
        setViewEndIndex(total - 1)
      }
      return
    }
    const delta = total - prev
    if (delta === 0) return
    const cur = viewEndIndexRef.current
    if (cur === -1) {
      // First concretization: adopt the rest gap — the user's stored live
      // slot (xZoomStore) or the default AUTO_PADDING. viewportInfo applies
      // the same value in this render (atomic treadmill).
      const _rest = total - 1 + loadXGap(chartId, viewWindowSizeRef.current, AUTO_PADDING)
      viewEndIndexRef.current = _rest
      setViewEndIndex(_rest)
      // If a drag is somehow live at concretization (candle landed between
      // pointerdown and the first move), the anchor must agree with the value
      // we just wrote, or the first move computes from a different world.
      // NOTE: the refs live INSIDE useChartScroll — reached via `scroll`,
      // whose const is declared BELOW this effect. That's legal: the effect
      // CALLBACK runs post-commit when the binding exists. (Bare references
      // here shipped a ReferenceError once — esbuild checks syntax, not
      // bindings.) Optional chaining as the belt: this line must never throw.
      if (scroll?.isDraggingRef?.current) scroll.dragStartRef.current.viewEndIndex = _rest
    } else {
      // Hold the screen slot: viewEndIndex moves with the candle count.
      // Clamp to the same walls every other site uses (xWalls: soft left
      // edge at 0, forward wall at FUTURE_PADDING).
      const { startWall: leftWall, endWall: fwdWall } = xWalls(total, viewWindowSizeRef.current)
      const next = Math.max(leftWall, Math.min(fwdWall, cur + delta))
      if (next !== cur) { viewEndIndexRef.current = next; setViewEndIndex(next) }
      // MID-DRAG REBASE — the "something catches the scroll" fix. The
      // pointermove math derives newEnd from the anchor captured at
      // pointerdown (dragStartRef.viewEndIndex). The treadmill and that math
      // were two uncoordinated writers: every candle arriving mid-drag, the
      // treadmill shifted the view (+delta → content lurches toward the
      // forward gutter), then the very next finger move recomputed from the
      // STALE anchor and yanked it back — a per-candle sawtooth the finger
      // feels as catching, with net escapes past the live edge that cost
      // double the distance to drag home. When the treadmill moves the view,
      // it must move the anchor by the SAME delta: the next move then lands
      // exactly where the treadmill left off, gap held, no fight.
      if (scroll?.isDraggingRef?.current) scroll.dragStartRef.current.viewEndIndex += delta
    }
  }, [chartData.candles.length, autoScrollEnabled])

  // ── New footprint candle refits Y ──
  // Runs after the treadmill (declared above it), so the end already sits on
  // the new column. Drops the expand-only fit hold and the land hold, then
  // one extra pass (yFitTick) lets the scan of the visible window decide the
  // range, including shrinking. The same-TF rebucket hold is not touched.
  // Follow (pinned) has no fit hold, so its centre and span stay the user's.
  // ── TF SWITCH WINDOW: ONE Y OWNER ─────────────────────────────────────
  // null = normal. { phase:'wait' } from the TF change until the new-interval
  // candles are in hand and the X anchor is applied; then { phase:'hold' }
  // for TF_WIN_MS (extended by structural publishes — reconcile, slabs — up
  // to TF_WIN_MAX_MS). Inside it, exactly one thing sets Y: frame() fitting
  // the visible candles' raw hi/lo, expand-only from the first new-interval
  // scan (the old interval's hold is dropped at the start, not carried).
  // Suppressed for the window: the fitGen refit, the rebucket/quiet hold
  // (and its carry of the previous lattice half), the follow-band recentre,
  // the in-place camera path, a drag-fit ease still in flight, the land
  // holds. The painted half is held expand-only once the new-interval
  // rebucket has landed. A user gesture (X drag / wheel / glide, Y scale)
  // ends the window — the user owns Y from there. A user span keeps its
  // half; frame() only clamps its centre. After the window the ordinary
  // expand-only idle hold simply continues, so nothing jumps at the end.
  const tfWinRef = useRef(null)

  const yFitGenRef = useRef(chartData.fitGen)
  useLayoutEffect(() => {
    if (chartData.fitGen === yFitGenRef.current) return
    yFitGenRef.current = chartData.fitGen
    // Inside a TF switch window the fit is the only Y writer and is
    // expand-only; a candle boundary there must not drop the hold (on 1m a
    // boundary lands mid-load about one switch in twenty).
    if (tfWinRef.current) { yfitC('fitGen suppressed (tf window)'); return }
    yCam.clearFitHold('fitGen')
    yLandExactRef.current = null
    setYFitTick((t) => t + 1)
  }, [chartData.fitGen, yCam])

  const visibleCandles = useMemo(() => {
    if (viewportInfo.visibleCount === 0) return []
    return chartData.candles.slice(viewportInfo.startIdx, viewportInfo.endIdx + 1)
  }, [chartData.candles, viewportInfo.startIdx, viewportInfo.endIdx, viewportInfo.visibleCount])

  // ─── Draw-aligned range slice: visibleCandles PLUS pan-overdraw column ───
  // startIdx is inclusive (endIdx - winSize + 1) everywhere — viewportInfo and
  // drawCanvas agree. Paint STILL draws one extra left column via `_loopLo =
  // startIdx - 1` so a fractional pan can slide that candle into view
  // (panOffsetPx ∈ [0, cw); named overdraw, NOT part of winSize).
  //
  // Range scans must include that same column: otherwise the sliver is painted
  // but never ranged, and its high/low rows fall off the grid (pop-in at the
  // left edge). This is NOT a viewportInfo/paint +1 disagreement patch.
  //
  // Deliberately NOT a replacement for visibleCandles: positional consumers
  // (VolumeBarsLayer, header groups) assume index 0 is the first whole slot.
  //
  // Only when that column can actually show: its right edge sits at x = frac,
  // so at rest with frac < 0.5px it is entirely off screen. Ranging it then
  // let a newly loaded off-screen neighbour (history landing just left of
  // the window) widen Y and move the sizer. A drag can slide it in without
  // a re-render (frac is a ref), so while dragging it is always ranged; the
  // drag-end mirror flip re-runs this with the resting frac. The wheel glide
  // re-runs it every frame (viewWindowSize dep) and reads the live frac.
  const rangeCandles = useMemo(() => {
    const sIdx = viewportInfo.startIdx
    if (sIdx <= 0 || visibleCandles.length === 0) return visibleCandles
    if (!isDraggingMirror && !((+fractionalOffsetPxRef.current || 0) >= 0.5)) return visibleCandles
    return chartData.candles.slice(sIdx - 1, viewportInfo.endIdx + 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData.candles, viewportInfo.startIdx, viewportInfo.endIdx, visibleCandles, isDraggingMirror, viewWindowSize])

  // Shared range scan — both visibleSortedMcaps and computeIdealBucket need
  // the data's mcap span. Returns { min, max } or null on no data.
  // Historical candles use stored high/low; live candles scan their bucket
  // keys (which is what the drawer iterates anyway).
  const scanVisibleMcapRange = useCallback(() => {
    if (visibleCandles.length === 0) return null
    // Delegates to the module-scope scanCandlesMcapRange — the SAME loop
    // the grid memo uses, by construction. Validity policy stays local.
    // rangeCandles, not visibleCandles: the sizer must measure what the
    // painter paints, including the leading sliver (see rangeCandles note).
    const { min, max } = scanCandlesMcapRange(rangeCandles)
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return null
    return { min, max }
  }, [visibleCandles, rangeCandles])

  // Buffer formula lives at module scope (computeRangeBuffer) — one with pickBucketForRange.

  // Output-stability cache for visibleSortedMcaps. The memo allocates a
  // fresh array on every run (new Array(extCount)), so even on frames
  // where the grid is structurally identical to the previous frame — the
  // common case during auto-scroll, where ticks update price within
  // existing bucket boundaries — every downstream consumer sees a new
  // reference and re-runs (ref syncs, dependent memos, layout effects,
  // bucket-sizer effect via .length, JSX consumers). By comparing the
  // new result against the previous and returning the previous when
  // structurally identical, downstream consumers skip when nothing
  // actually changed. ~60Hz cascade collapses to "fire only when the
  // grid genuinely shifts."
  const prevMcapsRef = useRef(null)
  // Monotonic owner-id for lattice arrays. Minted inside the stability cache
  // on NEW arrays only; cached `prev` keeps its id so idle slides that return
  // the same reference do not flip heat's lattice fingerprint.
  const latticeIdSeqRef = useRef(0)
  // Hysteresis for the top-of-grid snap. Holds the last committed rangeMax
  // (and the bucket it was computed at). rangeMax only re-snaps when the
  // desired top crosses a FULL bucket beyond the held value, or the bottom
  // would clip data — otherwise the held value is reused. This is what
  // stops the scaling↔rebucketing feedback loop on new highs and the
  // upward "phantom jump" at lows (both came from rangeMax recomputing a
  // fractionally-different snap every frame).
  const lastRangeMaxRef = useRef(null)
  // Linear price and ln(price) are different spaces. autoCenter /
  // followTarget / fitHold / halfSpan pin / lastRangeMax all hold values in
  // the CURRENT space — if log flips and those are kept, follow damps from
  // e.g. 2500 toward ln(2500)≈7.8 (or expand-only merges across spaces) and
  // the axis shows extremes / Infinity. yCam.frame(logChanged) drops hold,
  // pin, and trail before the hold merge so the next frame snaps clean.
  const lastLogModeRef = useRef(null)

  // TF re-fit guards — declared ABOVE visibleSortedMcaps so the grid memo can
  // clear expand-only fit hold on the same frame new-interval candles arrive
  // (refs used from a later hook would TDZ during that memo).
  const tfRebucketPendingRef = useRef(false)
  // New-interval data is in hand when the data layer tags its candles with
  // this interval. Array identity was not that signal: every tick publishes
  // a fresh array, so on an async (futures) fold the guards fired on old
  // candles and the real series landed unguarded.
  const tfDataIn = chartData.tf == null || chartData.tf === timeInterval
  // (tfLiveAnchorRef is declared above viewportInfo, which reads it.)
  const tfLiveAnchorSkipRef = useRef(true)
  // Columns the resolved end sat past the LIVE candle at the flip. On
  // arrival: end = newLiveIdx + this, so the new live candle takes the
  // screen slot the old one had. One assignment, no glide.
  const tfLiveSlotRef = useRef(0)
  const tfCenterRef = useRef(null)
  const visibleSortedMcaps = useMemo(() => {
    // (yaudit) Dead zoomLandGrid early-return + zoomFreeze FROM-hold removed
    // (never armed; cone owns framing via glideHalf/Centre → latticeGeomFor).

    // ───────── 0. Edge cases ─────────
    // ONE KNOWN PRICE, in priority order: the live price, then the newest
    // candle that has one. This is what an empty scan is answered WITH.
    const knownPrice = () => {
      const hl = +chartData.highlightMcap || 0
      if (hl > 0) return hl
      const cs = chartData.candles
      for (let i = cs.length - 1; i >= 0; i--) {
        const v = +(cs[i]?.close_mcap ?? cs[i]?.close) || 0
        if (v > 0) return v
      }
      return 0
    }

    // `visibleCandles.length === 0` IS NO LONGER AN EXIT. It used to return
    // the last good lattice, which held the state that produced no data and
    // gave the view no way back. The scan below answers an empty window with
    // ±Infinity, and the range synthesis turns that into a real window around
    // the known price — so the fit is total and the chart recomputes its way
    // out instead of freezing.
    //
    // The only genuine early return left is having no price at all: first
    // paint, token switch, before any data. That is not a stuck view, it is
    // an empty chart, and an empty lattice is the correct answer to it.
    if (chartData.sortedMcaps.length === 0 && !(knownPrice() > 0)) {
      return chartData.sortedMcaps
    }

    // ───────── 1. Mode decision ─────────
    // Two modes:
    //   follow — the live candle is in view (at/ahead of the forward edge):
    //            anchor on highlightMcap (live price) with a viewport deadzone.
    //   fit    — dragged BACK into history (live candle scrolled off the right):
    //            center on the visible-candle midpoint and fit their highs/lows.
    // TREADMILL DECOUPLING: vertical follow is NO LONGER tied to autoScrollEnabled
    // (which is now always-on for the horizontal treadmill). If it were, dragging
    // back to study history would keep the y-scale pinned to LIVE price while you
    // look at OLD candles — misaligned. Instead, follow only when the newest
    // candle is actually within the visible window; once you pan back past it,
    // fit the window you're viewing. "Live in view" = viewEndIndex reaches the
    // last real candle (gap >= 0 to tot-1).
    // Mode is owned by the Y pin, NOT by yZoomFactor crossing 0.99 — that
    // silent flip fought the user mid-gesture and on land.
    const total = chartData.candles.length
    const xMoving = !!(
      isDraggingChartRef.current
      || zoomGlideRef.current != null
      || zoomTargetWinRef.current != null
    )
    const liveInView = isLiveInView(viewEndIndexRef.current, total)
    // X does not switch follow/fit. That mode flip on a pan toward live was a
    // second Y policy landing on top of the scan — the bounce.
    // Mode is the machine's (it is owned by the pin, and the pin lives there).
    // xMoving + liveInView are the only two things it needs from this scope;
    // both are fed to frame() below.

    // ───────── 2. Range (min/max mcap) ─────────
    // Shared scan + shared highlight stretch (module-scope helpers, also
    // used by the bucket sizer via scanVisibleMcapRange/computeIdealBucket)
    // so the renderer and the sizer measure the SAME dataRange by
    // construction. The why-comments for the no-snap scan and the
    // whole-bucket margin / lattice / half-bucket-tolerance stretch live
    // on the helpers.
    let bucketSize = mcapBucketSize
    let minMcap, maxMcap
    // LOG SCALE = LINEAR SCALE ON ln(PRICE). Every line of range math below —
    // dataRange, the buffer, halfRange, centre, top/bottom snap, row count, the
    // coarsen branch, the residual — is a difference, a floor/ceil by a step,
    // or a ratio of differences. All of those are correct in ln-space
    // unchanged, because ln turns a price RATIO into a DIFFERENCE. So the whole
    // memo runs on ln(price) with bucketSize = ln(ratio), and exp() is applied
    // once at the output. That is the entire log implementation of the grid.
    //
    // Requires min > 0: ln of a non-positive price is not a number. Fall back
    // to linear for that frame rather than produce NaN rows.
    let LOG = logScaleRef.current
    {
      // rangeCandles, not visibleCandles: the grid must contain rows for the
      // leading sliver the painter draws, or its cells skip at the edge.
      //
      // (Highlight-stretch removed — it pulled the range toward the live price,
      // which sits at the LOW when price is making new lows, asymmetrically
      // inflating the bottom and making it behave differently from the top.
      // Raw scan min/max only: highs and lows now feed dataRange identically.)
      //
      // TOTAL: an unscannable window is answered, not abandoned — scanFitRange
      // returns null only when there is no price anywhere, which is an empty
      // chart rather than a stuck view.
      //
      // One call, and the same call the glide will make at a target window.
      const fit = scanFitRange(rangeCandles, LOG, knownPrice, chartData.candles)
      if (!fit) return chartData.sortedMcaps
      minMcap = fit.min; maxMcap = fit.max; LOG = fit.log
    }

    // ── Y IS THE SCAN. ──────────────────────────────────────────────────
    // Visible candles → min/max. That is the scale.
    //   x moving — stamp it (exact, every frame). Land is a no-op because
    //              idle starts from the same pair.
    //   idle     — expand-only, so ranging does not breathe.
    //   follow   — no hold; centre is price.
    // Nothing eases this. Easing was a second function of the same inputs
    // and it never agreed with idle on the last frame.
    const logChanged = lastLogModeRef.current !== LOG
    if (logChanged) {
      lastLogModeRef.current = LOG
      // Lattice-side cross-space state only. The camera drops its own trail
      // off the same flag, inside frame().
      lastRangeMaxRef.current = null
      committedGridRef.current = null
    }
    // scanFitRange already applied ln() to min/max when LOG survived; the
    // bucket has to follow it into the same working space.
    if (LOG) bucketSize = Math.log(mcapRatio)

    // ── ONE CAMERA FRAME ─────────────────────────────────────────────────
    // Fit hold, halfSpan pin, follow deadzone and centre, in that order, once
    // per grid pass. `step` goes IN — the camera never picks a bucket, which
    // is what keeps zoom rung-neutral and the axis labels still.
    // Timeframe switch window (see tfWinRef).
    {
      const W = tfWinRef.current
      const _yGest = !!(yGlideRafRef.current || yHalfTargetRef.current != null
        || yAxisZoomDragActiveRef.current || yCam.isGesturing())
      if (W && (xMoving || _yGest)) {
        tfWinRef.current = null
        yfitC(`tf window END (user ${xMoving ? 'x' : 'y'} gesture)`, { lo: minMcap, hi: maxMcap })
      } else if (W && W.phase === 'wait' && tfDataIn && !tfLiveAnchorRef.current) {
        const t = Date.now()
        W.phase = 'hold'; W.startAt = t; W.endAt = t + TF_WIN_MS
        W.sv = chartData.structuralVersion; W.half = 0
        W.lo = minMcap; W.hi = maxMcap
        yCam.clearFitHold('tf-start')
        yCam.clearTrail('tf-start')
        yLandExactRef.current = null
        pendingLandYRef.current = null
        yCamHandoffRef.current = null
        yfitC('tf window START', { lo: minMcap, hi: maxMcap })
      } else if (W && W.phase === 'hold') {
        const t = Date.now()
        if (chartData.structuralVersion !== W.sv) {
          W.sv = chartData.structuralVersion
          W.endAt = Math.min(W.startAt + TF_WIN_MAX_MS, Math.max(W.endAt, t + TF_WIN_EXTEND_MS))
        }
        if (t > W.endAt) {
          tfWinRef.current = null
          // The load has settled. If the visible hi/lo is narrower than the
          // widest the window saw (a transient that has since gone), refit to
          // what is on screen NOW — the one allowed shrink. If it is not
          // narrower, the hold already IS the fit: do nothing, Y stays put.
          // A user span is the user's — left alone.
          const _eps = Math.abs(W.hi - W.lo) * 1e-6
          const _narrowed = minMcap > W.lo + _eps || maxMcap < W.hi - _eps
          if (_narrowed && !yCam.isPinned()) yCam.clearFitHold('tf-end refit (visible narrowed)')
          yfitC(`tf window END (settled +${t - W.startAt}ms${_narrowed ? ', refit' : ', no-op'})`, { lo: minMcap, hi: maxMcap, extra: `seen=${W.lo}..${W.hi}` })
        } else {
          if (minMcap < W.lo) W.lo = minMcap
          if (maxMcap > W.hi) W.hi = maxMcap
        }
      }
    }
    const tfWin = tfWinRef.current
    const tfHold = !!(tfWin && tfWin.phase === 'hold')
    // (yexplore) The live candle's footprint, working space — what follow
    // keeps inside the central band. NaN = none (follow uses the live price).
    let liveLo = NaN
    let liveHi = NaN
    if (liveInView && total > 0) {
      const lc = chartData.candles[total - 1]
      if (lc) {
        const r = scanCandlesMcapRange([lc])
        if (Number.isFinite(r.min) && Number.isFinite(r.max) && (!LOG || r.min > 0)) {
          liveLo = LOG ? Math.log(r.min) : r.min
          liveHi = LOG ? Math.log(r.max) : r.max
        }
      }
    }
    // (ytickcap) Tick floor, priced at the scan midpoint, computed BEFORE the
    // camera: frame() floors the user span at one tick per target-height row
    // (minHalfLin, price units), and the lattice refines against the SAME step.
    let _fullLo = NaN, _fullHi = NaN
    {
      const cs = chartData.candles || []
      const n = cs.length
      const fr = fullRangeRef.current
      const first = n ? cs[0]?.startTime : null
      let from = 0
      if (n && fr.first === first && n >= fr.n && fr.n > 0) from = fr.n - 1
      else { fr.lo = Infinity; fr.hi = -Infinity }
      if (n > from) {
        const r = scanCandlesMcapRange(from ? cs.slice(from) : cs)
        if (Number.isFinite(r.min) && r.min < fr.lo) fr.lo = r.min
        if (Number.isFinite(r.max) && r.max > fr.hi) fr.hi = r.max
      }
      fr.first = first; fr.n = n
      if (Number.isFinite(fr.lo) && Number.isFinite(fr.hi) && (!LOG || fr.lo > 0)) {
        _fullLo = LOG ? Math.log(fr.lo) : fr.lo
        _fullHi = LOG ? Math.log(fr.hi) : fr.hi
      }
    }
    const _targetPx = targetRowHRef.current || 20
    const _yMinStep = yTickFloor(chartData.candles,
      LOG ? Math.exp((minMcap + maxMcap) / 2) : (minMcap + maxMcap) / 2,
      dataSource === 'hyperliquid', hlSzDecRef.current)
    const _camIn = {
      min: minMcap,
      max: maxMcap,
      step: bucketSize,
      highlight: chartData.highlightMcap,
      log: LOG,
      xMoving,
      liveInView,
      logChanged,
      liveLo,
      liveHi,
      // (cmeydrag) CME: the floor is one tick per 6 target
      // rows. At 0.25 per row the plain floor sits ABOVE the default fit on a
      // quiet ES window, so arming a user span snapped the view OUT and every
      // "expand" drag clamped up to it. Crypto keeps the plain floor.
      clampMin: _fullLo,
      clampMax: _fullHi,
      minHalfLin: (_yMinStep > 0 && containerHeight > 0)
        ? (containerHeight * _yMinStep) / (2 * _targetPx
          * ((dataSource === 'hyperliquid' && cmeInfo(hlPair?.base)?.tick) ? 6 : 1)) : 0,
      tfHold,
    }
    // (ycone2) Cone owns Y framing for the whole X-wheel glide. Do NOT call
    // yCam.frame — that stamps fitHold to the ceil(W) scan, recomputes
    // wantHalf/centre from the live window, and fights the lerped cone every
    // publishWin remint / live tick. Pin still wins absolutely.
    // (ydiag) Diagonal: if Y scale is live, cone must NOT own framing —
    // the user span / glideYZoom own halfSpan via frame().
    const yScaleLive = !!(
      yGlideRafRef.current
      || yHalfTargetRef.current != null
      || yAxisZoomDragActiveRef.current
      || yCam.isGesturing()
    )
    const coneLive = !!(
      zoomGlideRef.current?.cone
      && !yScaleLive
      && !yCam.isPinned()
      && Number.isFinite(glideHalfRef.current)
      && Number.isFinite(glideCenterRef.current)
    )
    let cam
    let halfRange
    let center
    // Set when the rebucket hold must NOT carry the previous lattice half
    // (see the carry below); the lattice then applies its floor normally.
    let _dropQuiet = false
    // (ydragslide) X-drag owns framing the same way cone does: published
    // glide half+centre is already the destination. No yCam.frame stamp.
    const dragFitLive = !!(
      !coneLive
      && yDragFitDestRef.current
      && Number.isFinite(glideHalfRef.current)
      && Number.isFinite(glideCenterRef.current)
      && !yScaleLive
      && !yCam.isPinned()
    )
    if (coneLive) {
      // (yview) Mid-glide camera = straight lerp to the armed land, published
      // by stepXCone. Never yCam.frame / never live-window fit mid-flight.
      // Fall through to latticeGeomFor so the lattice covers that plan (freeze
      // of START rows blanked cells when new columns entered).
      yCamHandoffRef.current = null
      halfRange = glideHalfRef.current
      center = glideCenterRef.current
      cam = {
        mode: 'fit',
        centre: center,
        fitCentre: center,
        halfSpan: halfRange,
        dataRange: maxMcap - minMcap,
        scaleMoving: false,
      }
      fdCount('reuse')
      yfitC('memo cone', { lo: minMcap, hi: maxMcap, c: center, h: halfRange })
    } else if (dragFitLive) {
      yCamHandoffRef.current = null
      halfRange = glideHalfRef.current
      center = glideCenterRef.current
      cam = {
        mode: 'fit',
        centre: center,
        fitCentre: center,
        halfSpan: halfRange,
        dataRange: maxMcap - minMcap,
        scaleMoving: false,
      }
      fdCount('reuse')
      yfitC('memo dragFit', { lo: minMcap, hi: maxMcap, c: center, h: halfRange })
    } else {
      const _land = pendingLandYRef.current
      if (_land && Number.isFinite(_land.half) && Number.isFinite(_land.centre)
          && !yCam.isPinned()) {
        // (yknow2) Land remint = arm dest (pendingLandY), not a fresh frame().
        pendingLandYRef.current = null
        yCamHandoffRef.current = null
        halfRange = _land.half
        center = _land.centre
        cam = {
          mode: 'fit',
          centre: center,
          fitCentre: _land.centre,
          halfSpan: halfRange,
          dataRange: (_land.max - _land.min),
          scaleMoving: false,
          holdQuiet: true,
        }
        fdCount('reuse')
        yfitC('memo land', { lo: minMcap, hi: maxMcap, c: center, h: halfRange })
      } else {
        const _exact = yLandExactRef.current
        const _pad = _exact ? Math.max(1e-9, _exact.half * 1e-4) : 0
        if (_exact && (
          yCam.isPinned() || xMoving || logChanged
          || minMcap < _exact.min - _pad || maxMcap > _exact.max + _pad
        )) yLandExactRef.current = null
        const _keep = yLandExactRef.current
        if (_keep && !yScaleLive) {
          pendingLandYRef.current = null
          yCamHandoffRef.current = null
          halfRange = _keep.half
          center = _keep.centre
          cam = {
            mode: 'fit',
            centre: center,
            fitCentre: _keep.centre,
            halfSpan: halfRange,
            dataRange: Math.max(0, _keep.max - _keep.min),
            scaleMoving: false,
            holdQuiet: true,
          }
          fdCount('reuse')
          yfitC('memo landExact', { lo: minMcap, hi: maxMcap, c: center, h: halfRange })
        } else {
        pendingLandYRef.current = null
        const _ho = yCamHandoffRef.current
        yCamHandoffRef.current = null
        const _hoOk = !!_ho
          && (performance.now() - _ho.at) < 250
          && yCam.peekFitHold() === _ho.holdAfter
          && yCam.isPinned() === _ho.pinnedAfter
          && Object.keys(_camIn).every((k) => Object.is(_camIn[k], _ho.inputs[k]))
        cam = _hoOk ? _ho.cam : yCam.frame(_camIn)
        fdCount(_hoOk ? 'reuse' : 'camMemo')   // (perf4) debug counter
        // let: unpinned quiet floors the CAMERA window (not lattice count).
        // Pin owns halfSpan absolutely — never inflate it while pinned.
        halfRange = cam.halfSpan      // the user span, or the fit + buffer
        center = cam.centre           // fit / follow / explore centre
        // Bucket handoff: the painted half is what is on screen. The camera
        // half is pre-quiet, so taking the previous lattice half stops this
        // frame from either subtracting a rung or lifting the quiet floor.
        //
        // EXCEPT a half the 24-row quiet floor inflated. That floor is
        // 12 × the OLD bucket; carrying it into a finer bucket kept a window
        // several times the data on screen until the scan next changed — the
        // TF squeeze (lower TF: old coarse bucket), and again after the
        // TF-window end refit when the sizer's settle commit landed. Not
        // carried: the fit half goes to the lattice with its floor applied
        // at the NEW bucket, which is what a fresh fit would paint.
        if (cam.holdQuiet) {
          const prevLat = prevMcapsRef.current
          if (prevLat && prevLat.floorRaised) {
            _dropQuiet = true
            yfitC('memo quiet carry DROPPED (prev half was floor-inflated)', { lo: minMcap, hi: maxMcap, c: center, h: halfRange })
          } else if (prevLat && prevLat.halfRange > halfRange) {
            halfRange = prevLat.halfRange
            yfitC('memo quiet carry (prev lattice half)', { lo: minMcap, hi: maxMcap, c: center, h: halfRange })
          }
        }
        // TF window: the painted half is expand-only once the new interval's
        // rebucket has landed (before it, the stale bucket's quiet floor
        // would be recorded and carried — the old squeeze). Pin owns its
        // span; the window never touches it.
        if (tfHold && !yCam.isPinned() && !tfRebucketPendingRef.current
            && tfWin.half > halfRange) {
          halfRange = tfWin.half
          yfitC('memo tf expand-only half', { lo: minMcap, hi: maxMcap, c: center, h: halfRange })
        }
        }
      }
    }
    const dataRange = cam.dataRange     // post-hold span (probe only)

    // ───────── 3. Bucket size — OWNED BY THE EFFECT ─────────
    // The memo no longer derives its own bucket candidate. The single
    // source of truth is computeIdealBucket + the bucket-sizer effect,
    // which commits mcapBucketSize. The memo renders the grid at whatever
    // bucket was committed — `bucketSize` stays = mcapBucketSize for the
    // remainder of this memo. This removes the second, disagreeing sizer
    // (raw dataRange + 5x guard) that fought the effect's renderedRange
    // formula and caused the two-bucket ping-pong / max-update-depth loop.

    // ───────── 4. Center (hold-until-edge, damped recenter) ─────────
    // Third generation of this policy, and the history matters:
    //   v1  hold-until-escape that anchored price AT THE EDGE on correction —
    //       every new low re-breached instantly, so it edge-walked (lurch per
    //       boundary cross).
    //   v2  continuous critically-damped follow — smooth, but NEVER STILL:
    //       the center drifted on every tick, which reads as constant
    //       fidgeting when price is just ranging.
    //   v3  (this) the damper stays, but its TARGET is a step function. The
    //       target FREEZES between breaches — the damper converges and the
    //       chart sits perfectly still. When price wanders past
    //       FOLLOW_EDGE_FRAC of the visible half-range from the held center,
    //       the target steps to a FULL recenter (price back at
    //       FOLLOW_OFFSET_FRAC — v1's fatal flaw was recentring only to the
    //       edge) and the existing FOLLOW_DAMP_K glide carries it there.
    //       Free property: during a violent run the edge re-breaches while
    //       gliding, so this degrades gracefully into v2's continuous follow
    //       — still when ranging, tracking when trending.
    // ── ONE FIT. THE DRAG-PAN ONE. ────────────────────────────────────────
    // Center is the midpoint of the range currently on screen. Nothing held,
    // nothing damped, nothing to converge — the same line pan has always run,
    // now on every mode.
    //
    // WHY THIS IS NOT v2 (see the history above — v2 was continuous follow and
    // "NEVER STILL", fidgeting whenever price ranged):
    // v2 damped toward the LIVE PRICE, which ticks many times a second, so the
    // center could never settle. This follows the SCANNED RANGE, which only
    // moves when an extreme enters or leaves the window. Different input, and
    // it is the whole difference. Flat tape, unchanged extremes, identical
    // midpoint — bit-for-bit still, not damped-to-nearly-still.
    //
    // Both properties fall out rather than being aimed at:
    //   AGGRESSIVE — a new high lands, the midpoint moves that frame. The
    //   damper used to grow halfRange instantly while the center crawled
    //   toward it at FOLLOW_DAMP_K, which is what dropped the bottom of the
    //   chart out of the viewport on a new high.
    //   WAITING — nothing entered or left, the midpoint is the same number,
    //   so the scale does not move at all.
    //
    // stretchRangeForHighlight still extends the range when price pushes past
    // the recorded extremes, so a genuine breakout is tracked — but its
    // half-bucket tolerance means ordinary ticks inside the printed range do
    // not move min/max, which is what keeps this still.
    //
    // ── THE CONSUMER OF `mode`. ──────────────────────────────────────────
    // `mode` was computed at 4440 and nothing branched on it, so every frame
    // took the fit path regardless of the y wheel. This is the missing half.
    //
    //   fit    — resting state, and where double-click returns you. Centre on
    //            the visible-candle midpoint and frame their highs and lows.
    //   follow — you narrowed with the y wheel. The span is now smaller than
    //            the data, so data runs past the viewport BY DESIGN; anchor on
    //            the live price so the part you care about stays framed.
    //
    // The y wheel IS the switch: below 0.99 you follow price, at 1 you fit.
    // ── PROBE 2. Logs the memo's INPUTS, not just its output. ─────────────
    // Probe 1 showed the fit producing one line per ease instead of one per
    // frame, and the suspected cause was viewportInfo reading viewWindowSize
    // (committed int) instead of viewWindowSizeRef (live float). That is now
    // fixed and the symptom is unchanged, so the hold is somewhere else.
    //
    // These four answer where, together:
    //   win / ref   — do they diverge during the ease? If ref moves and win
    //                 does not, viewportInfo IS seeing the float.
    //   startIdx/endIdx — does the WINDOW actually move? If the indices are
    //                 static while ref moves, viewportInfo is not recomputing
    //                 (its deps still gate on state, and a ref read inside a
    //                 memo does not invalidate it — the memo must re-run for
    //                 the ref read to matter).
    //   nCandles    — is rangeCandles changing? That is what the scan reads.
    // Behind the same flag as [cells]/[scale]: unconditional, this logged on
    // EVERY glide frame (the fit now re-runs per frame) and the console work
    // itself made the glide stutter.
    if (typeof window !== 'undefined' && (() => { try { return localStorage.getItem('cheddar_debug_scale') === '1' } catch { return false } })()) {
      const _f = window.__fit || (window.__fit = { k: '' })
      const _k = `${cam.fitCentre.toFixed(3)}|${viewportInfo.startIdx}|${viewportInfo.endIdx}|${rangeCandles.length}`
      if (_k !== _f.k) {
        _f.k = _k
        dbg(`[fit] c=${cam.fitCentre.toFixed(2)} r=${dataRange.toFixed(2)} ` +
          `win=${viewWindowSize} ref=${(+viewWindowSizeRef.current || 0).toFixed(2)} ` +
          `idx=${viewportInfo.startIdx}..${viewportInfo.endIdx} n=${rangeCandles.length} ` +
          `win=${viewWindowSize}`)
      }
    }
    // ───────── 5. Build the row grid ─────────
    // Buffer additive, NOT multiplied by yZoom. One row of constant
    // visual padding above/below data at any zoom. At yZoom=1 this is
    // identical to the old `((dataRange/2) + buffer) * yZoom`; at higher
    // zoom it stops the buffer (which is `max(bucketSize, dataRange*0.005)`)
    // from being amplified by yZoom on every bucket change.
    // ── SCALE THE SPAN ABOUT THE CENTRE. ─────────────────────────────────
    // One multiplication: centre comes from the fit, the factor sets the span,
    // and they compose. Below 1 the window shrinks inside the data, above 1 it
    // widens past it — symmetric, because the centre never moves.
    //
    // This used to be floored at dataRange/2, so a factor under 1 did NOTHING
    // and the axis could only ever widen. The floor was there because y-zoom
    // was follow-only and clipping data had no way back: an off-screen view
    // made the fit return the last good lattice, which held the state that
    // produced it. That fallback is gone — the fit is total now, every input
    // yields a legal window — so clipping is recoverable, and zooming in past
    // the data is just the user zooming in, which is not a bug to prevent.
    //
    // The floors that remain are about NUMERICAL validity, not about keeping
    // data in frame: the user span is clamped (tick floor .. 5x fit) at both
    // writers, yCam.frame() clamps the centre to the visible data, and
    // minQuietRows below guarantees a usable row count whatever the span.
    //
    // buffer stays ADDITIVE (see above) — constant visual padding at any zoom
    // is a deliberate choice and multiplying it here would undo it.
    // ── ZOOM WIDENS THE PRICE WINDOW. THAT IS WHAT BUILDS NEW ROWS. ──────
    // Rows are generated as `rangeMax - i*bucket`, so a wider window IS more
    // rows. Zoom out and the lattice grows past the data to fill the frame;
    // the rows that already existed keep their prices because `bucket` is
    // fixed, and the new ones are appended at the ends.
    //
    // I briefly moved zoom out of here and into RH instead, to stop the axis
    // labels moving on a y scroll. That did stop the labels moving — but it
    // also removed the only thing that generated rows, so zooming out left
    // the lattice SHORTER than the viewport with blank above and below.
    //
    // The labels were a separate bug and have a separate fix: computeIdealBucket
    // no longer reads yZoomFactor, so `bucket` is zoom-neutral. With the rung
    // pinned, count can grow freely without re-deriving a single price. Both
    // properties, no trade.
    //
    // buffer stays ADDITIVE — constant visual padding at any zoom, deliberate.
    // While a glide is running it owns the half-range; otherwise this is the
    // live fit. The glide's target IS this expression evaluated at the eased
    // window, so the two agree at arrival and the handover is a no-op.
    // ── Y WORKS LIKE X. ──────────────────────────────────────────────────
    // The lattice covers the DATA. The viewport is a window onto it, and the
    // window may be smaller than the lattice — exactly as x shows a window of
    // candles with more of them off the left and right.
    //
    // Zoom does two different things and they are not the same knob:
    //   OUT (factor > 1) widens the SPAN, which generates rows, so the frame
    //       stays full instead of running out of lattice.
    //   IN  (factor < 1) magnifies ROW HEIGHT and leaves the span alone, so
    //       the lattice stands TALLER than the viewport and the extremes go
    //       off-screen — still present, still each on its own row.
    //
    // Span tracks the user span both ways. Zoom-in shrinks the window about
    // centre (follow); zoom-out widens and generates rows. Rungs stay put
    // because computeIdealBucket scores at zoom=1.
    // Do NOT fold a centrePx camera into residual — that translateY rolls
    // the pinned canvas up instead of scaling about mid.
    // halfRange already set above (pin / wantHalf).
    // ── Quiet = camera floor when UNPINNED, never a count pad. ─────────
    // Old path inflated count to minQuietRows then packed RH = H/count,
    // which reopened the visible price window while the pin held halfSpan
    // (bottom gap + bounce at max expand). Quiet is a FIT policy: grow
    // the camera window so a brand-new token's one candle sits small in
    // empty rows. Pin owns halfSpan — skip entirely while pinned.
    // (y2a) Section 5 geometry lives in latticeGeomFor (module scope) —
    // identical text, now shared with the Y glide's in-place path.
    // (yexplore) Tick floor only with a user span — the default fit never
    // refines (the sizer keeps its rows inside the legibility band).
    const _userSpan = yCam.isPinned()
    const _refinePx = Y_REFINE_ROW_MULT * _targetPx
    const _geom = latticeGeomFor({
      center, halfRange, bucketSize,
      // (ycone2) Quiet floor would inflate halfRange every remint and fight
      // the cone lerp. Quiet is already baked into planLandY dest / seed.
      pinned: _userSpan || coneLive || dragFitLive,
      holdQuiet: !!cam.holdQuiet && !_dropQuiet,
      containerHeight, viewWindowSize,
      containerW: containerWidthRef.current,
      log: LOG, minStep: _userSpan ? _yMinStep : 0, refinePx: _refinePx,
      targetPx: _targetPx,
    })
    if (!_geom) return chartData.sortedMcaps
    const _floorRaised = _geom.halfRange > halfRange
    if (yfitOn(chartId) && _geom.halfRange > halfRange) {
      yfitC('lattice quiet-floor raise', { lo: minMcap, hi: maxMcap, c: center, h: _geom.halfRange, extra: `bucket=${bucketSize}` })
    }
    halfRange = _geom.halfRange
    if (tfHold && !_userSpan && !coneLive && !dragFitLive && !tfRebucketPendingRef.current
        && halfRange > tfWin.half) tfWin.half = halfRange
    const { desiredTop } = _geom
    let { rangeMax, count, gridBucket } = _geom
    // Tick-refined lattice: linear rows (exact ticks) even under log.
    const LOGR = LOG && !_geom.lin
    // The destination lattice is installed only after the zoom camera lands.
    lastRangeMaxRef.current = { value: rangeMax, bucket: bucketSize }
    // Pad by 1 row at the bottom. The drawCanvas transform pulls the canvas
    // UP by yResidualPx ∈ [0, RH) to smooth the rangeMax bucket-snap, which
    // would otherwise expose a gap of that height below the canvas (most
    // visible in follow mode, where rangeMax snaps up by a bucket every
    // time price advances past the top edge). The extra row sits just
    // outside the viewport when residual=0 and slides into the gap as
    // residual grows. `visibleCount` is stamped so dynamicRowHeight reads
    // the real row count, not the padded length — keeping RH and
    // dynamicRowHeight in lockstep (both = containerHeight / count).
    const BOTTOM_PAD = 1
    const extCount = count + BOTTOM_PAD
    const mcaps = new Array(extCount)
    // exp() on the way out is the only place the log lattice differs from the
    // linear one in shape. Tag the array: latticeRowIndex / bucketRowIndex /
    // findBucketIndex read `.log` to pick the ln-space membership formula.
    if (LOGR) { for (let i = 0; i < extCount; i++) mcaps[i] = Math.exp(rangeMax - i * gridBucket) }
    else      { for (let i = 0; i < extCount; i++) mcaps[i] = rangeMax - i * gridBucket }
    mcaps.log = LOGR
    mcaps.visibleCount = count
    // Price half for RH when the rows are linear under an ln camera.
    mcaps.rowHalf = _geom.lin ? _geom.linHalf : undefined

    // ───────── 6. Price-space residual (viewport applies × RH) ─────────
    // Lattice owns residual in BUCKETS — never pixels. RH = f(containerHeight)
    // lives in the viewport layer below so a window drag cannot remint this
    // array / latticeId / heat projection.
    // ── PROBE [yaxis]. localStorage.cheddar_debug_yaxis = '1'. Delete after. ──
    // Logs only when something a LABEL depends on moves. If the axis is
    // spinning, one of these is changing every frame:
    //   top    — rangeMax. Steps by one bucket per boundary crossing. Labels
    //            are rangeMax - i*bucket, so this moving IS the labels moving.
    //   bkt    — gridBucket. If this changes, EVERY label changes at once.
    //            A '*' means it changed since the last line: that is the
    //            sizer walking the ladder, and it should be rare.
    //   n      — row count. Flipping N/N+1 is the ceil/floor breathing; with
    //            span-derived RH it should no longer change row heights.
    //   rh     — row height. Should now be near-constant while bkt holds.
    //   res    — residualBuckets, the sub-bucket slide. Should ramp 0->1 and
    //            wrap each time `top` steps. If `top` steps while res is NOT
    //            near a wrap, the canvas and the axis are out of lockstep.
    //   ctr    — the eased centre feeding desiredTop.
    if (typeof localStorage !== 'undefined' && localStorage.getItem('cheddar_debug_yaxis') === '1') {
      const _rb = (rangeMax - desiredTop) / gridBucket
      const _rh = (containerHeight * gridBucket) / (2 * halfRange)
      const _bktMoved = _yaxisProbe.bkt !== gridBucket
      // Signature covers EVERYTHING that can move a pixel, not just the label
      // set: row height and lattice identity included. A shimmer with `top`,
      // `bkt` and `n` all held means it is rh or a remint.
      const _sig = `${rangeMax}|${gridBucket}|${count}|${_rh.toFixed(4)}|${prevMcapsRef.current?.latticeId ?? -1}`
      if (_sig !== _yaxisProbe.sig) {
        _yaxisProbe.sig = _sig
        _yaxisProbe.bkt = gridBucket
        dbg('[yaxis]'
          + ` top=${rangeMax.toFixed(4)}`
          + ` bkt=${gridBucket.toFixed(6)}${_bktMoved ? '*' : ' '}`
          + ` n=${count}`
          + ` rh=${_rh.toFixed(3)}`
          + ` res=${_rb.toFixed(3)}`
          + ` ctr=${center.toFixed(5)}`
          + ` half=${halfRange.toFixed(5)}`
          + ` lat=${prevMcapsRef.current?.latticeId ?? -1}`
          + ` glide=${Number.isFinite(glideCenterRef.current) ? 'y' : 'n'}`)
      }
    }

    // ── SUB-BUCKET ONLY. ────────────────────────────────────────────────
    // Measured against the ceil of desiredTop, NOT against rangeMax.
    //
    // rangeMax is not simply ceil(desiredTop): minQuietRows pads it up by
    // `addEach` WHOLE buckets, and the coarsen re-ceils it to a new bucket.
    // Dividing that difference by gridBucket gives `addEach + fraction`, and
    // the translate then drags the whole pinned stack up by that many ROWS.
    // Narrowing y is what pushes count under minQuietRows, so the more you
    // narrow the further it drags — and it tracks price because desiredTop
    // does. At rest count is over the threshold, there is no padding, and the
    // residual stays under 1, which is why it only ever showed when zoomed in.
    //
    // Whole buckets are ROWS. Rows get drawn, not translated. This term exists
    // only to slide the grid the fractional part of a bucket so the top edge
    // moves smoothly instead of stepping, so it is in [0,1) by construction.
    // gridBucket, not bucketSize: the slide is measured on the FINAL step,
    // after any coarsen. The machine also owns the scaleMoving snap-off.
    const residualBuckets = yCam.slide(_geom.slideTop, gridBucket)
    // NO ZOOM-OUT CEILING. There was one here, capping the Y zoom at the
    // point where count would pass MAX_GRID_ROWS or RH would fall under
    // MIN_ROW_PX — and it is why the y narrowed but would not widen.
    //
    //     _byCap = MAX_GRID_ROWS * bucket / (2 * dataHalf)  ==  cap / count@1
    //
    // The sizer already targets a row height, so count at rest sits close to
    // the cap and that ratio comes out near 1. Clamped to max(1, ...), the
    // ceiling was 1: every zoom-out notch was discarded.
    //
    // Neither invariant needs a ceiling, because the coarsen branch enforces
    // both. It holds count <= MAX_GRID_ROWS by construction, and the geometry
    // cap keeps MAX_GRID_ROWS <= containerHeight/MIN_ROW_PX - 2, so
    // RH = H/count >= MIN_ROW_PX falls out. Zoom out as far as you like: the
    // span widens, the bucket coarsens, more price on screen. No dead travel,
    // because every notch still changes the span.
    mcaps.halfRange = halfRange
    mcaps.floorRaised = _floorRaised   // quiet floor set this half (see the carry)
    mcaps.step = gridBucket           // working-space step (ln when log)
    mcaps.residualBuckets = residualBuckets
    mcaps.fitCenter = cam.fitCentre

    // ───────── 7. Cache for next frame ─────────
    lastViewWindowSizeRef.current = viewWindowSize
    // (y2b) The pass inputs, stamped on the lattice this pass returns. The Y
    // glide re-runs ONLY the camera + geometry against these while none of
    // them has moved (X still, same candles / price / pan / bucket / viewport);
    // anything else falls back to a React render, exactly as before.
    const _yPass = {
      min: minMcap, max: maxMcap, LOG, bucketSize,
      highlight: chartData.highlightMcap, liveInView, liveLo, liveHi, fullLo: _fullLo, fullHi: _fullHi,
      minStep: _yMinStep, minHalfLin: _camIn.minHalfLin, targetPx: _targetPx,
      H: containerHeight, win: viewWindowSize, containerW: containerWidthRef.current,
      candles: chartData.candles, end: viewEndIndexRef.current,
      logScale: logScaleRef.current, mcapBucketSize, mcapRatio,
    }
    // ───────── 8. Output stability ─────────
    // If the new grid matches the previous one structurally, return the
    // previous reference. Downstream memos/effects that depend on
    // `visibleSortedMcaps` (ref identity) then skip — they don't see a
    // new array, so React doesn't invalidate them. We compare length,
    // visibleCount, and the first/last entries. The entries are
    // arithmetically derived from (rangeMax, bucketSize, i) — if both
    // ends match and length matches, every intermediate entry matches
    // too. Exact equality is fine; the arithmetic is deterministic.
    const prev = prevMcapsRef.current
    // `.log` IS PART OF THE IDENTITY. length/count/step/halfRange/ends can
    // match between a LINEAR and a LOG lattice that span the same range with
    // the same row count, while every row BETWEEN the ends differs. Without
    // this, the frame after a toggle returned the STALE array: the tag never
    // reached the draw, dispRatio stayed null, and cells, the volume profile
    // and the current-price chip were all still placed by the linear mapping.
    // rowH is NOT identity — it is viewport (containerHeight) and must not
    // remint latticeId on resize.
    //
    // NEITHER IS halfRange, for exactly the same reason, and it used to be in
    // here. The rows ARE the identity: log + length + step + both ends pin
    // every row between them. halfRange is the price span the viewport asked
    // for, and rangeMax/rangeMin are bucket-SNAPPED off it — so a small change
    // in halfRange usually yields a lattice that matches row for row, and this
    // check would reject reuse for it anyway.
    //
    // That was survivable while halfRange only moved when an extreme entered
    // or left the window. It is not survivable now that it is a continuously
    // eased value: it changes EVERY FRAME of a glide or a drag-follow, so every
    // frame minted a new latticeId and invalidated heat, cells and the atlas
    // against a lattice that had not actually changed. That is the shimmer.
    //
    // It also explains why the lows suffered and the highs did not: a remint
    // re-derives RH, the grid is anchored at rangeMax, and displacement is
    // rowIdx * dRH — zero at the top anchor, a full row at the bottom. The
    // lowest rows are the farthest from the anchor, so they are where the
    // movement shows.
    if (prev
        && prev.log === mcaps.log
        && prev.length === mcaps.length
        && prev.visibleCount === mcaps.visibleCount
        && prev.step === mcaps.step
        && prev[0] === mcaps[0]
        && prev[prev.length - 1] === mcaps[mcaps.length - 1]) {
      // Same PRICE lattice — keep prev.latticeId. residualBuckets can move
      // without reminting; copy onto the stable ref for the viewport layer.
      prev.residualBuckets = mcaps.residualBuckets
      // Copied, not compared: rowHeightFor derives RH from halfRange, so the
      // reused array has to carry the CURRENT span or row height goes stale.
      prev.halfRange = mcaps.halfRange
      prev.floorRaised = mcaps.floorRaised
      prev.rowHalf = mcaps.rowHalf
      prev.fitCenter = mcaps.fitCenter
      // THE WINDOW THIS LATTICE WAS FIT FOR. Stamped even on the reuse path:
      // the scan produced the same range, so the new window is still fully
      // covered, and the painter must move with it.
      prev.winSizeF = viewWindowSize
      prev.yPass = _yPass
      return prev
    }
    // NEW array path: mint monotonic owner id.
    mcaps.latticeId = ++latticeIdSeqRef.current
    mcaps.winSizeF = viewWindowSize
    mcaps.yPass = _yPass
    prevMcapsRef.current = mcaps
    return mcaps
  }, [visibleCandles, rangeCandles, mcapBucketSize, mcapRatio, logScale, chartData.sortedMcaps, autoScrollEnabled, viewEndIndex, chartData.highlightMcap, viewWindowSize, bucketDensity, yFitTick, yCam, containerHeight, dataSource, hlPair?.szDecimals, viewportInfo.endIdx])

  useLayoutEffect(() => { visibleSortedMcapsRef.current = visibleSortedMcaps }, [visibleSortedMcaps])

  // ── VIEWPORT Y: pixels onto the price lattice ──
  // RH and residualPx are the only things a window drag may change. Lattice
  // identity (latticeId / step / ends) is untouched here.
  const dynamicRowHeight = useMemo(() => {
    if (visibleSortedMcaps.length === 0) return ROW_HEIGHT
    // yFitTick is a dep even when the lattice array is reused: halfRange/
    // row stamps mutate on the stable ref (Y glide / explore in place), and
    // without it RH goes stale on the pass that lands them.
    // (ycone2) halfRange too: X-cone / reuse mutates half on the same array
    // identity — without it layout syncs dynamicRowHeightRef from a stale
    // memo and fights the in-place cone RH every publishWin.
    return rowHeightFor(visibleSortedMcaps, containerHeight,
      visibleSortedMcaps.visibleCount ?? visibleSortedMcaps.length)
  }, [visibleSortedMcaps, visibleSortedMcaps?.halfRange, visibleSortedMcaps?.rowHalf, containerHeight, yFitTick])

  const heightStateMatchesBox = () => {
    const liveH = bodyScrollRef.current?.clientHeight || 0
    return !(liveH > 0 && liveH !== containerHeight)
  }
  useLayoutEffect(() => {
    const RH = dynamicRowHeight
    if (!Number.isFinite(RH)) return
    if (!heightStateMatchesBox()) return
    yResidualPxRef.current = yPinResidualPx(
      visibleSortedMcaps, RH, containerHeight)
  }, [visibleSortedMcaps, dynamicRowHeight, containerHeight])

  useLayoutEffect(() => {
    if (!heightStateMatchesBox()) return
    dynamicRowHeightRef.current = dynamicRowHeight
  }, [dynamicRowHeight, containerHeight])
  useLayoutEffect(() => {
    if (!heightStateMatchesBox()) return
    containerHeightRef.current = containerHeight
  }, [containerHeight])

  // Disarm the switch reveal gate once all scaling signals are in place:
  //   1. candles reference has changed since the last stable (non-switching)
  //      render — proves the parent delivered data for the new inputs
  //   2. y-axis mcap range has been computed
  //   3. current price / highlight mcap is known
  //   4. row height is resolved
  //   5. if auto-scroll is on, viewEndIndex must be -1 (snap-to-latest
  //      committed) — otherwise scrollToLatest's own rAF can fire after our
  //      reveal and shift the chart one frame later
  // Once ready, wait one rAF for downstream effects (scroll sync, draw effect)
  // to commit, then synchronously redraw and reveal.
  useLayoutEffect(() => {
    if (!isSwitching) return
    if (chartData.candles.length === 0) sawResetRef.current = true
    const candlesChanged =
      chartData.candles !== switchBaselineRef.current &&
      sawResetRef.current
    const autoScrollSettled = !autoScrollEnabled || viewEndIndex === -1
    const ready =
      candlesChanged &&
      chartData.candles.length > 0 &&
      visibleSortedMcaps.length > 0 &&
      chartData.highlightMcap > 0 &&
      // ≥6px rows: above the emergency-coarsen floor (~3px), below every
      // real density target (Fine=12), so degenerate load/TF-change frames
      // stay covered while any settled grid reveals. The 1500ms fallback
      // still force-reveals if the sizer never converges.
      dynamicRowHeight >= 6 &&
      // Token switch only: hold through the subscribe history phase. (TF
      // changes never arm this gate — they refold locally and paint at once.)
      !data.historyPending &&
      autoScrollSettled
    if (!ready) return
    let raf1 = 0, raf2 = 0
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        // Final synchronous check: a TF/token change may have armed
        // pending AFTER this effect's readiness check but BEFORE this
        // callback fires (hook effects run after the gate's layout
        // effect). The ref is set synchronously at arm time, so reading
        // it here closes the one-paint flash window.
        if (data.historyPendingRef?.current) return
        if (drawCanvasRef.current) drawCanvasRef.current()
        dbg(`[GATE] reveal (ready) +${Date.now() - gateArmedAtRef.current}ms`)
        setIsSwitching(false)
      })
    })
    return () => {
      // Raw rAF ids — this double-rAF is a layout-settle deferral that must
      // keep browser frame semantics, so it is deliberately NOT on the shared
      // driver. cancelFrame would silently no-op on a raw id.
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
    }
  }, [isSwitching, chartData.candles, chartData.highlightMcap, visibleSortedMcaps.length, dynamicRowHeight, autoScrollEnabled, viewEndIndex, data.historyPending])

  // Safety fallback: if readiness never comes (dead token, no data, network
  // stall), reveal anyway after arming so the user isn't left staring at a
  // blank container. History-aware: while the server backfill is still in
  // flight (historyPending armed at subscribe, cleared by the 'backfill'
  // frame), firing would reveal the pre-backfill frame — one live candle,
  // degenerate axis, then the whole history slamming in as a column. So if
  // pending is still set when the timer fires, hold the cover and re-check,
  // up to a hard cap that preserves the dead-token escape hatch. Dead/fresh
  // tokens clear pending almost instantly (the server always sends the
  // backfill frame, empty for uncaptured mints), so they still reveal at
  // ~1500ms; only genuinely heavy backfills (large captured histories)
  // extend the covered wait.
  //
  // KEYED ON gateKey AS WELL AS isSwitching. It used to be isSwitching alone,
  // and the arm effect's setIsSwitching(true) is a no-op whenever the cover is
  // ALREADY up — same value, so the dep never changed and this effect never
  // re-ran. Any re-arm mid-cover therefore inherited the REMAINDER of the
  // previous arm's clock instead of a fresh 1500ms, and HARD_CAP_MS counted
  // from the stale armedAt too. Mild at boot (mount arms at t=0, the real
  // selection lands ~600ms later, and the reveal fires 900ms after it — the
  // "+895ms" fallback line, which is 1500 minus the second arm). Sharp when
  // switching token B while A is still covered: B could inherit near-zero
  // grace and force-reveal into exactly the partial paint this gate exists to
  // hide. gateKey changes on every arm by construction — the arm effect is
  // keyed on it — so this now gets one fresh timer per arm, no new state.
  useEffect(() => {
    if (!isSwitching) return
    // Nothing selected — no arm, so no escape. Without this the timer still
    // ran on the empty key and force-revealed a chart with no token.
    if (!gateHasIdentity) return
    // Catastrophic escape only. Must comfortably exceed the worst honest
    // backfill (heavy coins: multi-second build + ~40MB transfer + parse) —
    // an 8s cap raced real loads and force-revealed the pre-backfill frame.
    const HARD_CAP_MS = 20000
    // Second cap, for the case the first one can't distinguish: the 20s
    // escape assumes revealing SOMETHING beats holding, which is true when
    // candles have landed and only the deep history is still folding. It is
    // false when the fold hasn't produced a single candle yet — then the cap
    // reveals an empty grid with a bare y-axis, which is strictly worse than
    // the covered pre-load look it replaced (that state shows no axis at
    // all, and reads as "loading" rather than as "this token has no data").
    // Heavy mints hit exactly this: 169k raw swaps fold client-side and blow
    // past 20s with candles still at 0.
    //
    // So the escape now asks whether there is anything to show. With candles
    // on screen the 20s cap stands unchanged. With NOTHING on screen, keep
    // holding to EMPTY_CAP_MS — the cover is the better frame, and it is
    // honest: the data really hasn't arrived. The longer cap still exists so
    // a genuinely dead token can't hold the gate forever.
    const EMPTY_CAP_MS = 60000
    const armedAt = Date.now()
    let t
    const fire = () => {
      const pending = !!data.historyPendingRef?.current
      const nothingToShow = (chartDataRef.current?.candles?.length ?? 0) === 0
      const cap = nothingToShow ? EMPTY_CAP_MS : HARD_CAP_MS
      if (pending && Date.now() - armedAt < cap) {
        t = setTimeout(fire, 500)   // history in flight — hold, re-check
        return
      }
      if (drawCanvasRef.current) drawCanvasRef.current()
      // Fallback fired = readiness never came. The payload here IS the
      // diagnosis of the next stuck episode: elapsed says whether it was the
      // 1.5s path or the 20s history-hold, historyPending says whether the
      // backfill frame ever arrived, candles/sawReset say which readiness
      // condition starved.
      //
      // Candle count comes off chartDataRef, NOT the closed-over chartData.
      // This effect's deps deliberately exclude chartData, so the closure
      // holds the array as it stood at ARM time — where the count is 0 almost
      // by definition, since the reset that precedes a switch is what emptied
      // it. The old line reported that stale 0 no matter what had landed since,
      // which made every fallback look like a starved load.
      const liveCandles = chartDataRef.current?.candles?.length ?? 0
      dbg(`[GATE] reveal (FALLBACK) +${Date.now() - gateArmedAtRef.current}ms | key=${gateKey} | historyPending=${!!data.historyPendingRef?.current} | candles=${liveCandles} | sawReset=${sawResetRef.current}`)
      setIsSwitching(false)
    }
    t = setTimeout(fire, 1500)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSwitching, gateKey, gateHasIdentity])

  // Shared bucket-size resolver — one source of truth for both wheel and effect
  // CME tick (0 for everything else — crypto/HL keep the generic ladder).
  const cmeTickRef = useRef(0)
  cmeTickRef.current = (dataSource === 'hyperliquid' && hlPair && cmeInfo(hlPair.base)?.tick) || 0
  const idealBucketForRange = useCallback((rangeMin, rangeMax) => {
    if (containerHeight === 0) return null
    if (!Number.isFinite(rangeMin) || !Number.isFinite(rangeMax) || rangeMax <= rangeMin) return null
    const range = { min: rangeMin, max: rangeMax }
    const hl = chartDataRef.current?.highlightMcap || 0
    // ── ZOOM IS NOT AN INPUT TO THE DATA MODEL. ──────────────────────────
    // This read the Y zoom, so scrolling the y axis changed `half`, which
    // changed the scored RH, which changed which rung won — and a different
    // rung is a DIFFERENT LATTICE. Every price level got re-derived, so the
    // numbers on the axis moved instead of the scale moving over them. You
    // were not scaling a window over fixed prices; you were recomputing the
    // prices from the scale.
    //
    // The bucket is sized for the FIT — the range the data actually occupies,
    // at zoom 1 — and the user span then moves the viewport over that lattice
    // (latticeGeomFor refines toward the tick when rows get too tall).
    // Zoom in: fewer of the same rows, taller. Zoom out: more of the same
    // rows, shorter. The rungs never change, so neither do the labels.
    //
    // RH deliberately leaves targetRowH when you zoom. That is what zooming
    // IS; chasing targetRowH back was the re-bucket.
    const zoom = 1
    const targetRowH = targetRowHRef.current
    // Self-consistent sizing: evaluate each candidate bucket UNDER ITSELF.
    // The old form measured the range under the CURRENT bucket (stretch
    // margin quantized to it, buffer floored at 1x it, the stretch
    // trigger's deadband at currentBucket/2) and then picked a DIFFERENT
    // bucket -- so committing the pick changed the measurement that
    // produced it: lattice re-anchors, stretch toggles off, measured range
    // collapses by the margin, sizer undoes its own commit, stretch
    // re-triggers. A bistable loop, parked exactly at new lows where the
    // stretch is live (a real move shifting dataRange is what "kicked it
    // out"). Scoring each step with ITS OWN stretch + buffer + the exact
    // continuous-RH formula the grid memo stamps (RH = H*B/(2*half+2*B),
    // half = (dataRange/2)*zoom + buffer(dataRange,B); buffer NOT zoomed)
    // has a fixed point: the chosen bucket re-evaluates to itself, and
    // sizer/renderer lockstep holds by construction instead of by comment.
    // targetRowH pairing with the rowHealthy band below still applies:
    // the band's lower bound MUST match targetRowH (22) or the sizer
    // would re-bucket every tick even when sized correctly.
    let best = null
    let bestDist = Infinity
    // STRETCH ONCE, with a FIXED reference bucket — not per-candidate.
    // Previously each step S called stretchRangeForHighlight(min,max,hl,S),
    // so whether the highlight stretch fired depended on S's half-bucket
    // deadband: coarse S (big deadband) saw the UN-stretched range, fine S
    // (small deadband) saw the STRETCHED, larger range. Same price → a
    // different dataRange per candidate → the scorer was biased toward coarse
    // at new lows, committed coarse, the stretch then turned off under the
    // coarse deadband, the range collapsed, and the scorer swung back. The
    // discontinuity in the objective MOVED with the candidate, so the search
    // had no stable fixed point exactly when price sat within ~half a bucket
    // of the low — i.e. ~every approach to a new low (the pre-empt→retrack
    // →jump→loop reported for months). Computing the stretched range ONCE
    // against the COMMITTED bucket makes the stretch decision constant across
    // candidates; the RH scoring stays self-consistent per S (the term the
    // older comment below is about — that's preserved). DO NOT restore the
    // per-candidate stretch: it is what reintroduces the low-side loop.
    // (Highlight-stretch removed here too — same as the grid memo. The bucket
    // sizer now measures the raw symmetric range, so highs and lows feed it
    // identically. This was the asymmetric "extra stuff" that pulled the range
    // toward the live price — the low when watching price drop — and jittered
    // the bucket tie at lows specifically.)
    // LOG: the same scoring loop on ln(price). The candidate "buckets" are
    // ln(ratio) for each rung of MCAP_RATIO_STEPS, the range is ln(max/min),
    // and the committed value is ln(mcapRatio). The row-height formula and the
    // hysteresis band are unchanged — they were always about pixels. `best`
    // is mapped back to a RATIO on return, so the commit path stores a ratio
    // in mcapRatio and never a 1.01 in mcapBucketSize.
    const LOGS = logScaleRef.current && range.min > 0
    const sMin = LOGS ? Math.log(range.min) : range.min
    const sMax = LOGS ? Math.log(range.max) : range.max
    const sDataRange = sMax - sMin
    const committed = LOGS ? Math.log(mcapRatioRef.current) : mcapBucketSizeRef.current
    const H = containerHeightRef.current || containerHeight
    // Working-space step for RH / band (same formula pickBucket uses).
    const committedStep = committed
    const committedRh = (committedStep && Number.isFinite(committedStep))
      ? rowHeightForStep(sDataRange, zoom, H, committedStep)
      : null
    const picked = pickBucketForRange({
      dataRange: sDataRange, zoom, heightPx: H, targetRowH, log: LOGS,
      tick: LOGS ? 0 : cmeTickRef.current,
    })
    // Keep `best` in working space until the LOGS snap below (legacy path).
    best = picked == null ? null : (LOGS ? Math.log(picked) : picked)
    bestDist = (best != null && committedRh != null)
      ? Math.abs(rowHeightForStep(sDataRange, zoom, H, best) - targetRowH)
      : Infinity
    const committedDist = committedRh != null ? Math.abs(committedRh - targetRowH) : Infinity
    // STICKY SELECTION (minimal). The commit pipeline downstream is built on
    // "one hysteresis, one owner" — and THIS is that owner. With NO sticky,
    // computeIdealBucket returns the raw nearest bucket, which flips between two
    // adjacent steps when the ideal row height lands between them; the commit
    // effect then sees ideal !== current every frame and oscillates A→B→A→B (a
    // rebucket loop). So keep the committed bucket unless a candidate is
    // decisively better. The margin is small (0.15, the pre-cleanup value): its
    // ONLY job now is breaking the two-adjacent-step tie. The larger 0.35 was
    // over-damping to fight the low-side wobble — and that wobble is now handled
    // at the source by the symmetric bottom snap, so the heavy margin isn't
    // needed, just enough to stop the flip-flop.
    // ── LEGIBILITY BAND (replaces the point-target sticky margin) ──
    // The old rule optimized toward a POINT: keep whichever rung sits nearest
    // targetRowH, with a 0.15 margin only to break the two-adjacent-step tie.
    // Any drift off that point made a neighbouring rung score better, so the
    // chart rebucketed on essentially every rung crossing — and a rebucket
    // re-bins every cell, which is the pop. Rows were always near-perfectly
    // sized, and the price of that was a regroup every ~1.2x of zoom.
    //
    // TradingView glides because its y-axis is continuous — price→pixel is a
    // scalar, nothing to re-bin. A footprint ladder can't have that. But it
    // does NOT need the point: what a reader actually requires is that rows
    // stay LEGIBLE, which is a range, not a value. So hold the committed
    // bucket as long as its row height is inside the band, and only re-pick
    // when it leaves. Inside the band, zooming just scales pixels — no
    // regroup, no pop, actual glide. Crossings drop from once-per-rung to
    // roughly once per big zoom.
    //
    // On exit we still pick the rung nearest targetRowH, so the new bucket
    // lands mid-band with headroom in both directions rather than against the
    // edge it just crossed (which would re-trigger immediately).
    //
    // This SUBSUMES the old sticky margin: the band is far wider than 0.15,
    // so the adjacent-step tie it existed to break can no longer occur — a
    // freshly committed bucket sits at the middle of the band by construction.
    //
    // Trade, stated plainly: row height now varies across the band instead of
    // hugging the target. At normal density you'll sometimes read 13px rows
    // where before you'd always get ~20px. That variance IS the glide — the
    // regroups it removes are the pops. Floor never goes below MIN_ROW_PX.
    const BAND_LO_FRAC = 0.65   // ↓ = wider band, fewer regroups, thinner rows
    const BAND_HI_FRAC = 1.75   // ↑ = wider band, fewer regroups, fatter rows
    const bandLo = Math.max(MIN_ROW_PX, targetRowH * BAND_LO_FRAC)
    const bandHi = targetRowH * BAND_HI_FRAC
    // forceNearest: F/N/C (and any caller that armed the ref) wants the
    // rung nearest targetRowH, not "still legible under the old bucket."
    if (!forceNearestBucketRef.current
        && committed && committedRh !== null
        && (LOGS || isTickMultiple(committed, cmeTickRef.current))
        && committedRh >= bandLo && committedRh <= bandHi) {
      best = committed
    }
    // Map the ln-step back to the ratio the rest of the chart speaks. Snap to
    // the ladder rather than exp(best) directly so float noise in the ln
    // round-trip cannot manufacture an off-ladder ratio (which would break
    // the `ideal === current` equality the commit effect depends on).
    if (LOGS && best != null) best = snapRatio(Math.exp(best))
    // ── Scale probe (localStorage.cheddar_debug_scale='1') ──
    // Logs only when the chosen bucket differs from committed — the way to
    // catch any residual re-bucketing.
    try {
      if (best !== committed && typeof localStorage !== 'undefined'
          && localStorage.getItem('cheddar_debug_scale') === '1') {
        dbg('[scale]',
          'bucket', committed, '→', best,
          '| span', (range.max - range.min).toFixed(0), 'strSpan', (sMax - sMin).toFixed(0),
          '| hl', hl.toFixed(0), 'liveInView', liveInViewRef.current,
          '| targetRowH', targetRowH, 'zoom', zoom.toFixed(4),
          '| bestDist', bestDist.toFixed(2), 'commDist', committedDist.toFixed(2))
      }
    } catch {}
    return best
  }, [containerHeight])

  // Visible window → bucket (the sizer effect and the TF flip).
  const computeIdealBucket = useCallback(() => {
    const r = scanVisibleMcapRange()
    return r ? idealBucketForRange(r.min, r.max) : null
  }, [scanVisibleMcapRange, idealBucketForRange])

  // (glidebucket) Wheel glide: commit the DEST window's bucket when a notch
  // arms, not after the land. The sizer is held for the whole cone, so it
  // used to rebucket ~15ms after land, and the land plan (centre, buffer,
  // 24-row floor) had been made on the OLD bucket. Same band rule as the
  // sizer, so the land kick finds nothing to change. Refs are written now so
  // planLandY in the same arm reads the new bucket. Prices on screen hold:
  // the cone owns half/centre mid-glide, this only changes the rows.
  const commitArmBucketRef = useRef(null)
  commitArmBucketRef.current = (rawMin, rawMax) => {
    const ideal = idealBucketForRange(rawMin, rawMax)
    if (ideal == null) return
    const LOGC = logScaleRef.current
    const current = LOGC ? mcapRatioRef.current : mcapBucketSizeRef.current
    if (ideal === current) return
    yfitC(`rebucket (wheel arm) ${current} → ${ideal}`)
    lastRequestedBucketRef.current = ideal
    if (LOGC) { mcapRatioRef.current = ideal; setMcapRatio(ideal) }
    else { mcapBucketSizeRef.current = ideal; setMcapBucketSize(ideal) }
  }

  // ============================================
  // Bucket size pipeline (unified)
  // ============================================
  // One effect, one rule. Bucket recomputes when any of:
  //   - candle data changes
  //   - viewport (zoom/pan) changes
  //   - container size changes ONLY if RH leaves a hard legibility band
  //     (ordinary window drag is viewport-only — see resize gate above)
  //   - timeframe changes (1m ↔ 5m)
  //   - subgraph toggle (chart body shrinks/grows)
  //   - external "force" trigger (footer dblclick)
  //
  // Decision logic:
  //   1. While the user is dragging, never adjust (avoid grid-fight).
  //   2. If `forceFlag` is set, always recompute regardless of band.
  //   3. Otherwise only recompute when current row height is OUTSIDE the
  //      16-32 healthy band, OR when current bucket is more than 2x off
  //      ideal (catches cases where row count happens to land in band but
  //      the actual range/bucket pairing is bad — e.g. timeframe switch).
  //
  // The 2x ideal-vs-current check is the second condition that previously
  // required ad-hoc "force" triggers from timeframe / subgraph / dblclick.
  // With this in place those external triggers become redundant; the
  // forceFlag remains as a manual override for cases where the user
  // explicitly resets viewport state (dblclick) and wants a clean rebucket
  // even if the math says "close enough."
  const [bucketForceTick, setBucketForceTick] = useState(0)
  // Bumped when an X/Y gesture lands so the sizer re-runs after refs that
  // blocked mid-gesture (drag / zoomGlide) clear — ref clears are invisible
  // to the effect deps, so without this kick the new ideal never commits.
  const [sizerKick, setSizerKick] = useState(0)
  const kickSizer = useCallback(() => {
    setSizerKick((k) => k + 1)
  }, [])

  // (isDraggingMirror is declared above rangeCandles, which reads it.)

  // Bucket-commit coalescer (the zoom-out freeze fix). The sizer effect below
  // re-runs EVERY FRAME during a drag (visibleCandles/visibleSortedMcaps tick),
  // and on a fast small→wide zoom the ideal bucket walks a chain (6→7.5→15→…).
  // Each commit is a React re-render plus a full display regroup downstream —
  // synchronous, range-proportional work — so the chain stacked several of
  // them back-to-back on the main thread: the phone freeze ("sometimes pulls
  // out" = the chain settled; "reload" = it thrashed). Rate-limit the
  // COMMITTER, not the chooser: first divergence commits immediately, further
  // steps inside the window collapse into ONE trailing commit at the final
  // ideal. The sizing rule and its hysteresis are untouched.
  const bucketCommitTimerRef = useRef(null)
  const pendingBucketRef = useRef(null)
  const lastBucketCommitAtRef = useRef(0)
  const dragRungHoldRef = useRef(null)   // (drag-smooth) { ideal, at } pending 1-rung step mid-drag
  const sizerLastRangeRef = useRef({ lo: 0, hi: 0 })   // (pan-flash) range at last sizer run
  const lastSizerRunAtRef = useRef(0)
  const dragFreezeAtRef = useRef(0)   // (wheel-drag) glide frozen by a drag start: hold ALL rung steps 150ms
  const warmIdleRef = useRef(0)
  // Resize-vs-market fingerprint for the sizer: window drag must not rebucket
  // unless RH leaves a HARD legibility band (viewport absorbs ordinary resize).
  const sizerMarketKeyRef = useRef('')
  const sizerLastHRef = useRef(0)
  useEffect(() => () => clearTimeout(bucketCommitTimerRef.current), [])

  // useLayoutEffect, NOT useEffect — ORDERING, not prediction.
  //
  // visibleSortedMcaps is a useMemo: the lattice is built DURING render from
  // the bucket in state. This sizer used to run as a passive effect, i.e.
  // AFTER the browser had already painted. So scrolling back until a big
  // previous high entered the range went:
  //   render → lattice built with the OLD (too-fine) bucket over a suddenly
  //   much larger range → PAINT (current price squashed) → effect → commit
  //   the coarser bucket → re-render → pop back into place.
  // The squashed frame was real, committed, and visible — the "clunk". The
  // range was never unknown (all the candles are already loaded and the ideal
  // bucket is computable from them in that same frame); it was simply applied
  // one paint too late. No averaging or prediction can fix a scheduling
  // order — and none is needed.
  //
  // A layout effect runs after DOM mutation but BEFORE paint, so the setState
  // here flushes a synchronous re-render and the browser only ever paints the
  // CORRECTED scale. Same work, same commit, same hysteresis — one frame
  // earlier, and the wrong frame is never shown.
  //
  // Cost: this already ran every drag frame; it now runs at the same rate but
  // ahead of paint, and it REMOVES a wasted paint (the wrong one). Layout
  // effects run in declaration order, and this sits above the drawCanvas
  // layout effect, so the redraw below always sees the committed bucket.
  // (The coalescer's trailing commit still lands via setTimeout and stays
  // post-paint by nature — but the FIRST divergence, which is exactly the
  // scroll-back case, commits inline and is now pre-paint.)
  useLayoutEffect(() => {
    // ── Interaction throttle (drag smoothness) ──
    // This body scans the visible candles and scores the whole ladder, and as
    // a LAYOUT effect it runs BEFORE paint — i.e. directly in the input-to-
    // pixel path. Once per frame that is the single biggest non-drawing cost
    // of a drag, and it grows with visible candle count, which is why deep
    // history felt heavy.
    //
    // Scale does not need 60Hz to look continuous — adjacent ladder rungs are
    // only 1.2x apart and the sizer has hysteresis, so re-evaluating at ~12Hz
    // while the pointer is down is visually identical and costs ~5x less per
    // frame. The drag keeps its live rescale (NOT the old drag-freeze); it
    // just stops re-deciding on frames where the answer cannot have changed
    // meaningfully.
    //
    // Only throttled DURING a drag. At rest every run is pre-paint and inline,
    // which is what keeps the scroll-back pop fixed.
    //
    // Tail case, stated honestly: if the FINAL drag frame lands inside a
    // throttle window it is skipped, and this effect does not re-run on
    // release (isDraggingMirror is not a dep). The scale can therefore reflect
    // where the chart was up to ~80ms before you let go, until the next dep
    // change re-runs it — which any trade tick does (2-3/s). Adjacent rungs
    // are 1.2x apart, so an 80ms drag tail almost never crosses one. If it
    // ever shows, the fix is a trailing re-run on release, not a shorter
    // window.
    // Read the REF, not isDraggingMirror state: that state is deliberately
    // absent from this effect's dep array (see the note at the bottom), so a
    // state read here would depend on closure-refresh timing. The ref is
    // written every drag frame and is always current.
    // (pan-flash) Skip a throttled drag run ONLY while the visible DATA range
    // (candles' real high/low) is still where the last run saw it (within 5%
    // of span). A new extreme runs NOW, pre-paint, so a scale step never
    // paints one frame at the old bucket. (drag-smooth) Data range, NOT the
    // lattice ends: those move with the drag-fit ease / padding and re-tripped
    // the gate every frame. min/max come from the grid memo's own scan of the
    // same candles (yPass), so this costs no extra scan.
    {
      const yp = visibleSortedMcaps && visibleSortedMcaps.yPass
      const ok = !!(yp && Number.isFinite(yp.min) && Number.isFinite(yp.max) && yp.max > yp.min)
      const lo = ok ? yp.min : 0
      const hi = ok ? yp.max : 0
      const prev = sizerLastRangeRef.current
      if (isDraggingChartRef.current) {
        const nowMs = performance.now()
        const span = Math.max(1e-12, prev.hi - prev.lo)
        const still = ok && prev.hi > 0
          && Math.abs(lo - prev.lo) <= span * 0.05 && Math.abs(hi - prev.hi) <= span * 0.05
        if (still && nowMs - lastSizerRunAtRef.current < 80) return
        lastSizerRunAtRef.current = nowMs
      }
      sizerLastRangeRef.current = { lo, hi }
    }
    // (ycone2) Cone owns Y framing — a mid-glide bucket commit remints the
    // lattice under the lerp and fights it. The dest bucket is committed at
    // arm (commitArmBucketRef); the land kickSizer then finds it settled.
    if (zoomGlideRef.current?.cone) return
    // (Drag-freeze removed — the bucket sizer now runs live during the chart
    // drag, so the scale adjusts as you scroll through history instead of
    // holding frozen until release. It re-runs every drag frame because
    // visibleCandles/visibleSortedMcaps change as the window moves. This was
    // gated behind `if (isDraggingChartRef.current) return` defensively, back
    // when committing a bucket mid-drag could trigger the bucket-swap loop —
    // which the symmetric snap + highlight-stretch removal now prevent at the
    // source.)
    if (visibleSortedMcaps.length === 0 || containerHeight === 0) return

    // DEGENERATE SAMPLE GUARD — mid-swipe at the window edge the view can
    // hold 1-2 candles of a much larger chart (data off-screen left, live
    // candle just entering). Sizing off that sliver commits a micro bucket,
    // and until the swipe brings the real range back the whole chart
    // renders under it — the VP key union exploded to ~100K rows/DOM nodes
    // and locked the phone. Hold the committed bucket until the sample is
    // meaningful; genuinely tiny charts (fresh token, <3 candles TOTAL)
    // still size normally.
    if (visibleCandles.length < 3 && chartData.candles.length >= 3) return

    // ── TRACK LIVE. DO NOT DEFER THE RUNG TO ARRIVAL. ────────────────────
    // A hold used to sit here, skipping the sizer for the WHOLE gesture and
    // releasing on endChartInteract — so the rebucket landed after you
    // stopped: the glide finishes, the hold lifts, the rung changes, and the
    // chart readjusts under you. That is the settling on an x scroll, and the
    // second pass when the y wheel crosses 0.99 into follow, where the mode
    // flip moves the centre mid-gesture and the rung change queues behind it.
    //
    // It was written because walking intermediate rungs mid-glide shuffled the
    // lattice. Both causes of that are fixed elsewhere now: the coarsen
    // quantises its INPUT (one bucket per octave of halfRange, not one per
    // frame), and count comes from the SPAN so a sliding centre cannot flip
    // it. Tracking live costs a rung change or two across a gesture instead of
    // a shuffle — and you arrive already correct, with nothing to settle.
    //
    // The force-tick retry that lived inside the hold goes with it: it existed
    // only so an F/N/C density press during a gesture was not swallowed by the
    // early return. With no early return it is consumed normally below.

    // ── RESIZE GATE (lattice/viewport split) ──
    // containerHeight is still a dep so extreme resizes can rebucket, but a
    // normal window drag only changes RH in the viewport layer. Market key
    // ignores H; if it is unchanged and RH is still inside a HARD band, skip.
    // F/N/C and bucketForceTick bypass (explicit display-setting change).
    {
      const lid = visibleSortedMcaps.latticeId != null ? visibleSortedMcaps.latticeId : ''
      const marketKey = `${lid}|${visibleSortedMcaps.length}|${Math.round(viewWindowSize)}|${mcapBucketSize}|${mcapRatio}|${logScale ? 1 : 0}|${bucketForceTick}|${targetRowHRef.current}`
      const prevKey = sizerMarketKeyRef.current
      const prevH = sizerLastHRef.current
      const hDelta = Math.abs(containerHeight - prevH)
      const hOnly = prevKey !== '' && prevKey === marketKey && prevH > 0 && hDelta > 0.5
      if (hOnly && bucketForceTick === 0 && !forceNearestBucketRef.current) {
        const RH = dynamicRowHeightRef.current
        const target = targetRowHRef.current || 20
        // Harder than computeIdealBucket's 0.65–1.75 glide band — resize must
        // stretch further before it earns a ladder remint.
        const hardLo = MIN_ROW_PX
        const hardHi = target * 2.5
        if (Number.isFinite(RH) && RH >= hardLo && RH <= hardHi) {
          sizerLastHRef.current = containerHeight
          return
        }
      }
      sizerMarketKeyRef.current = marketKey
      sizerLastHRef.current = containerHeight
    }

    const ideal = computeIdealBucket()
    if (ideal == null) {
      // Consume density force even on a null ideal so forceNearest can't
      // stay armed and skip the legibility band on later ordinary runs.
      if (bucketForceTick !== 0) {
        setBucketForceTick(0)
        forceNearestBucketRef.current = false
        if (densityForceRetryRafRef.current) {
          cancelAnimationFrame(densityForceRetryRafRef.current)
          densityForceRetryRafRef.current = 0
        }
      }
      return
    }
    // Compare against live STATE, not mcapBucketSizeRef. The ref is synced
    // in a useLayoutEffect, so mid-burst it can lag the committed
    // mcapBucketSize and defeat this guard — the effect would re-commit the
    // same value, .length flips, and it loops. State always reflects what
    // React last committed.
    // Mode-aware: in log the sizer returns a RATIO and the committed value is
    // mcapRatio; in linear it is mcapBucketSize. Mixing them would make
    // `ideal === current` never true and commit on every tick.
    const LOGC = logScaleRef.current
    const current = LOGC ? mcapRatio : mcapBucketSize
    const commit = (v) => { yfitC(`rebucket (sizer) ${current} → ${v}`); return LOGC ? setMcapRatio(v) : setMcapBucketSize(v) }

    // Always consume the force flag, whether or not we end up changing the
    // bucket. Otherwise a no-op force (dblclick when bucket is already
    // ideal) would leave the tick > 0 and bypass the rule check on the
    // next ordinary trigger.
    const wasForced = bucketForceTick !== 0
    if (wasForced) {
      setBucketForceTick(0)
      forceNearestBucketRef.current = false
      if (densityForceRetryRafRef.current) {
        cancelAnimationFrame(densityForceRetryRafRef.current)
        densityForceRetryRafRef.current = 0
      }
    }

    if (ideal === current) {
      // Settled. Clear the request latch so the next genuine divergence
      // isn't blocked by a stale request — and KILL any pending trailing
      // commit: under trailing-edge gating a churn chain can end back at
      // the current bucket (range lurched out and back during a load), and
      // a timer armed mid-chain would otherwise fire afterwards and move a
      // now-correct bucket to a stale ideal.
      lastRequestedBucketRef.current = null
      clearTimeout(bucketCommitTimerRef.current)
      pendingBucketRef.current = null
      return
    }

    // Request latch: don't re-request a value we just committed before React
    // has reflected it in state (current). Prevents the same-value re-fire
    // that fed the max-update-depth loop.
    if (!wasForced && lastRequestedBucketRef.current === ideal) return

    // (drag-smooth) Rung hysteresis during a BODY drag: a 1-rung step must
    // hold ~150ms before it commits (a >=2-rung step commits now). Each
    // commit remints the grid and regroups every visible candle, so walking
    // single rungs back and forth mid-drag read as jagged. Release commits
    // whatever is still pending: endChartInteract -> kickSizer re-runs this
    // effect with the drag flag clear.
    if (!wasForced && isDraggingChartRef.current) {
      const LAD = LOGC ? MCAP_RATIO_STEPS : (cmeTickRef.current > 0 ? tickLadder(cmeTickRef.current) : MCAP_BUCKET_STEPS)
      const iI = LAD.indexOf(ideal), iC = LAD.indexOf(current)
      const rungs = (iI >= 0 && iC >= 0) ? Math.abs(iI - iC) : 99
      if (rungs < 2 || performance.now() - dragFreezeAtRef.current < 150) {
        const h = dragRungHoldRef.current
        const nowMs = performance.now()
        if (!h || h.ideal !== ideal) { dragRungHoldRef.current = { ideal, at: nowMs }; return }
        if (nowMs - h.at < 150) return
      }
    }
    dragRungHoldRef.current = null

    // Commit whatever computeIdealBucket returns. It ALREADY owns the
    // hysteresis (the sticky margin keeps the committed bucket unless a
    // candidate is decisively better), so if ideal !== current here the bucket
    // is genuinely wrong and must move. The old rowHealthy/farOff band was a
    // SECOND hysteresis using a DIFFERENT criterion (is rowH in [target,
    // target*1.6]) than the sticky's (is dist-to-target decisively better).
    // The two deadlocked: the sizer wanted 7.5 (committed 11 scored 5.83 off
    // target) but the band saw bucket 11's rowH sitting in-band and blocked
    // the commit — so computeIdealBucket re-requested the same change every
    // frame without it ever taking, then the bucket lurched two steps when the
    // range finally pushed rowH out of band. One hysteresis, one owner.
    lastRequestedBucketRef.current = ideal
    // ── Trailing-edge stability gate ──
    // The old gate was leading-edge with a 160ms COOLDOWN: quiet-lately →
    // commit now, busy → defer the remainder. That's the right shape for a
    // lone user-driven step, but during a pair/TF load the y-range lurches
    // on every backfill dump and deep-merge landing, ideals arrive a few
    // hundred ms apart, each one passes the quiet check, and each commit
    // invalidates EVERY visible candle's display cache — measured as ~26s
    // of per-frame buildDisplay regroup across a 50s load (the "sluggish
    // while it's working in the background" window). Trailing-edge instead:
    // a bucket commits only after its ideal has HELD STILL for
    // BUCKET_STABLE_MS. A churning chain collapses into ONE regroup at the
    // end; a lone steady-state step commits 300ms later than before, which
    // is imperceptible next to the regroup it pays for anyway. The timer
    // measures stability of the VALUE, not silence of the effect: the
    // request latch above swallows same-ideal re-fires (data ticks), so
    // only a genuinely NEW ideal resets the clock. wasForced (dblclick)
    // still bypasses entirely — user intent stays instant.
    const BUCKET_STABLE_MS = 300
    // Source split. The trailing gate above is correct for MACHINE-driven
    // ideal churn (load lurches). A user gesture is the opposite case: they
    // are watching the axis move and want it adjusting under the finger, and
    // the ladder + sticky margin already bound how often `ideal` can actually
    // change during one. So gesture → commit live; everything else → trailing.
    // Body x-pan is included: pan changes visible range under a stale bucket
    // and RH stretches until the trailing commit — same fat-cell expand as
    // zoom without markYScaleGesture. isDraggingChartRef covers that path.
    // Commit live during X pan / X zoom / Y scale. The old early-return on
    // isDraggingChartRef || zoomGlideRef left ideal computed but never
    // committed for the whole gesture — and when the ref cleared, the effect
    // did not re-run (refs are not deps), so rebucket looked "broken" on X
    // and Y until a random trade tick. Y halfSpan is pinned separately now,
    // so a bucket commit is not a Y jump.
    // ── COMMIT LIVE. NEVER RENDER THE REBUCKET, ONLY ITS RESULT. ─────────
    // The trailing gate used to defer this by BUCKET_STABLE_MS for anything
    // that was not a gesture. That delay IS the rebucket being rendered: for
    // 300ms the chart paints the OLD bucket against a range it no longer fits,
    // so RH stretches and the cells go fat — the note above calls it out. The
    // wait bought nothing visual; it only postponed the same swap.
    //
    // The swap itself is already atomic. A bucket change makes latticeFlipped
    // true, which sets the rebuild deadline to Infinity, so every candle's
    // display is regrouped in ONE frame instead of arriving in budgeted
    // pieces. You see the old lattice, then the new one — never a partial.
    //
    // What the gate was really guarding is machine-driven churn during a load
    // lurch, and that is bounded elsewhere: the bucket ladder is discrete and
    // the sticky margin holds a rung until the range moves meaningfully off
    // it, so `ideal` cannot oscillate per frame. If a load ever does chatter,
    // the fix belongs on that margin, not on a timer that makes every honest
    // rebucket wait 300ms.
    clearTimeout(bucketCommitTimerRef.current)
    bucketCommitTimerRef.current = 0
    pendingBucketRef.current = null
    lastBucketCommitAtRef.current = Date.now()
    commit(ideal)
  }, [
    visibleCandles,                    // data ticks
    visibleSortedMcaps.length,         // viewport recomputed
    mcapBucketSize, mcapRatio, logScale,   // read directly as `current`; on
                                       // commit, state changes → effect
                                       // re-runs → ideal === current path
                                       // clears the latches and settles
    containerHeight,                   // resize — gated above; only hard RH band rebuckets
    timeInterval,                      // 1m ↔ 5m
    showCvdSubgraph,                   // body shrinks/grows
    showBodyVol,
    bucketForceTick,                   // manual override (dblclick)
    sizerKick,                         // post-gesture kick (drag/glide land)
    viewWindowSize,                    // same story for x-zoom; visibleCandles
                                       // ref usually catches it, but listing
                                       // it explicitly is defensive against
                                       // identity-stable slices
    // (isDraggingMirror removed from deps — it existed ONLY to fire a bucket
    // recompute on drag-END to undo the old freeze. Now that the bucket runs
    // live during drag, that extra on-release recompute is redundant and fired
    // against settling state, picking a possibly-different bucket than the last
    // drag frame → rebucket-pivot → grid shifted on release. The effect already
    // re-runs throughout the drag via visibleCandles/visibleSortedMcaps.)
    computeIdealBucket,                // recreated when visibleCandles or
                                       // containerHeight changes (NOT on zoom)
  ])

  // Public-ish trigger for explicit user resets where we want a guaranteed
  // recompute even if the rule above says "close enough."
  const setForceBucketRecompute = (_) => setBucketForceTick(t => t + 1)

  // ── Synchronous rebucket on timeframe change (kills the one-frame reposition) ──
  // The passive bucket sizer above reacts to the new-interval candles one
  // render LATE. On a TF flip the data layer republishes rebinned candles;
  // visibleCandles changes on that frame, but the sizer's PASSIVE effect runs
  // after the paint and its setMcapBucketSize only takes on the FOLLOWING
  // frame. So the first post-flip frame draws the new candles against the
  // bucket that was sized for the OLD interval's (narrower) range — then snaps
  // the next frame, with an emergency-coarsen intermediate when the old bucket
  // is far off the new range. That settle is the reposition.
  // Fix: on the exact frame the new-interval data arrives, commit the ideal
  // bucket SYNCHRONOUSLY in a layout effect. setMcapBucketSize here forces a
  // pre-paint re-render, so the grid (and the draw effect below) recompute at
  // the correct bucket before anything is painted — no stale-bucket frame.
  // computeIdealBucket derives from the raw scanned range (scanVisibleMcapRange)
  // independent of the committed bucket, so it returns the right answer for the
  // new data immediately. Gated by a pending flag so it fires exactly once per
  // flip; on the forced re-render the passive sizer sees ideal===current and
  // no-ops, so there's no second sizer fighting this one.
  useLayoutEffect(() => {
    tfRebucketPendingRef.current = true
    if (tfLiveAnchorSkipRef.current) tfLiveAnchorSkipRef.current = false
    else {
      // Open the TF switch window (tfWinRef): one Y owner until the load
      // settles. Stand down every other writer now — a drag-fit ease or a
      // wheel land still in flight would otherwise plan Y on the OLD candles.
      tfWinRef.current = { phase: 'wait', armedAt: Date.now(), startAt: 0, endAt: 0, sv: null, half: 0 }
      if (yDragFitDestRef.current) kickYFollowRef.current({ abort: true })
      yLandExactRef.current = null
      pendingLandYRef.current = null
      yCamHandoffRef.current = null
      yfitC(`tf window ARM → ${timeInterval}`)
      // Flip render: the old interval is still on screen. Store how far the
      // end sits past the old live candle.
      tfLiveAnchorRef.current = true
      const prevCs = chartData.candles || []
      const end = prevCs.length > 0 ? resolveEndIdx(viewEndIndexRef.current, prevCs.length) : 0
      tfLiveSlotRef.current = prevCs.length > 0 ? end - (prevCs.length - 1) : 0
      // (xzoom) Live candle on screen: anchor at the user's stored live slot
      // (normally the same value — the writer keeps it current). Scrolled
      // back into history: keep that offset, as before.
      const _w = Math.max(1, Math.round(+viewWindowSizeRef.current || 1))
      if (prevCs.length > 0 && tfLiveSlotRef.current >= 0 && tfLiveSlotRef.current <= _w - 1) {
        tfLiveSlotRef.current = loadXGap(chartId, _w, tfLiveSlotRef.current)
      }
      // (tfcenter) Remember the time at the horizontal centre. Only when the
      // centre sits on a real candle; in the live gap the slot rule above wins.
      tfCenterRef.current = null
      if (prevCs.length > 0) {
        const _half = (_w - 1) / 2
        const _ci = Math.round(end - _half)
        if (_ci >= 0 && _ci <= prevCs.length - 1 && prevCs[_ci] && prevCs[_ci].startTime != null) {
          tfCenterRef.current = { t: prevCs[_ci].startTime, half: _half }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeInterval])
  // New-interval candles in hand: first the X anchor, then the rung.
  // The treadmill does not add the length delta (a rebin, not a new column).
  // Anchor: one assignment, new live index + the stored slot offset. If that
  // moves the end, return — the next pass (viewEndIndex dep) sizes the rung
  // and the memo refits Y against the anchored window, not the stale one.
  //
  // Rung: commit the ideal bucket SYNCHRONOUSLY so the first painted frame
  // of the new interval is already on the right ladder (the passive sizer
  // runs one render late — that settle was the reposition / scale flicker).
  useLayoutEffect(() => {
    if (!tfRebucketPendingRef.current) return
    if (!tfDataIn) return
    const cs = chartData.candles
    if (!cs || cs.length === 0) return
    if (tfLiveAnchorRef.current) {
      tfLiveAnchorRef.current = false
      const _c = tfCenterRef.current
      tfCenterRef.current = null
      let _end = (cs.length - 1) + (tfLiveSlotRef.current || 0)
      if (_c) {
        // (tfcenter) Last candle starting at or before the centre time.
        let lo = 0, hi = cs.length - 1, k = 0
        while (lo <= hi) { const m = (lo + hi) >> 1; if (cs[m].startTime <= _c.t) { k = m; lo = m + 1 } else hi = m - 1 }
        const { startWall, endWall } = xWalls(cs.length, viewWindowSizeRef.current)
        _end = Math.max(startWall, Math.min(endWall, Math.round(k + _c.half)))
      }
      setViewEndIndex(_end)
      // The memo opens the TF window on the first pass with the anchor
      // applied; an unchanged end re-renders nothing, so give it that pass.
      if (tfWinRef.current && tfWinRef.current.phase === 'wait') setYFitTick((t) => t + 1)
      if (viewEndIndexRef.current !== viewEndIndex) return
    }
    if (visibleSortedMcaps.length === 0 || containerHeight === 0) return
    const ideal = computeIdealBucket()
    if (ideal == null) return
    tfRebucketPendingRef.current = false
    yfitC(`tf rebucket (sync) ideal=${ideal}`)
    if (logScaleRef.current) { if (ideal !== mcapRatioRef.current) setMcapRatio(ideal) }
    else                     { if (ideal !== mcapBucketSizeRef.current) setMcapBucketSize(ideal) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData.candles, chartData.tf, visibleCandles, visibleSortedMcaps, containerHeight, computeIdealBucket, viewEndIndex, setViewEndIndex])

  const canvasHeight = useMemo(() => visibleSortedMcaps.length * dynamicRowHeight, [visibleSortedMcaps.length, dynamicRowHeight])
  // (y1) Seed only: before drawCanvas has painted a frame the layers take
  // React's value, exactly as the JSX did. After that drawCanvas owns them.
  canvasHeightRef.current = canvasHeight
  useLayoutEffect(() => {
    if (layerHRef.current) return
    const h = `${canvasHeight}px`
    for (const el of layerHEls()) if (el && el.style.height !== h) el.style.height = h
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasHeight])

  // Stable y-axis column width. The label column was auto-width, so it
  // resized (and pushed the plot in/out) whenever a value gained or lost a
  // digit — e.g. crossing 999→1000 or 1999.99→2000.00. Compute width from
  // the longest label CURRENTLY VISIBLE, mapped from char count, so the
  // column only changes when the widest label's length actually changes
  // (a real magnitude-bracket shift), not on every value tick. Sampling
  // every 3rd row matches which rows actually render a label.
  // ONE glyph edge, everything derives from it. Rows label every 3rd row;
  // measure exactly those. The LIVE PRICE is deliberately not measured —
  // its formatted length changes tick-to-tick and used to re-size the
  // whole column (and the volume profile anchored to it); the chip
  // overhangs the plot instead (TradingView-style).
  //   yAxisWidth     — the column box (region/body/header width)
  //   profileRightPx — bars' right edge: AXIS_BAR_GAP left of the widest
  //                    label's glyphs, which right-align at AXIS_PAD_R.
  // Session hold: the axis never NARROWS while charting the same token.
  // Reset on token switch (viewport-reset effect below).
  const axisHoldRef = useRef({ textW: 0 })
  // Live copy of yAxisWidth for drawCanvas (which reads refs, not state):
  // the canvas paints the solid axis backdrop at THIS width so the painted
  // strip and the DOM column are always the same surface.
  const axisWRef = useRef(0)

  const { yAxisWidth, profileRightPx } = useMemo(() => {
    let maxW = 0
    for (let i = 0; i < visibleSortedMcaps.length; i += 3) {
      const w = measureAxisLabel(formatYAxis(visibleSortedMcaps[i]))
      if (w > maxW) maxW = w
    }
    // DECADE HEADROOM: also reserve for the top label at 10x the current
    // range top. Without this, a token pumping through a digit boundary
    // (9.99K → 10.05K: 5 chars → 6) lengthened the labels mid-move and
    // stepped the whole axis+profile left — "the text pushed it out."
    // Reserving one decade up means a 10x pump changes NOTHING; only a
    // >10x run past the session's initial reserve takes one step, then
    // parks again.
    if (visibleSortedMcaps.length > 0 && visibleSortedMcaps[0] > 0) {
      const w10 = measureAxisLabel(formatYAxis(visibleSortedMcaps[0] * 10))
      if (w10 > maxW) maxW = w10
    }
    let textW = Math.ceil(maxW)
    const hold = axisHoldRef.current
    if (textW > hold.textW) hold.textW = textW      // grow: remember it
    else textW = hold.textW                          // never narrow in-session
    // Tight column: AXIS_PAD_R per side. Centered text then sits exactly
    // AXIS_PAD_R from each edge; bars end AXIS_BAR_GAP before the glyphs
    // (= flush with the column/backdrop edge, by construction).
    const w = Math.max(48, Math.min(140, textW + AXIS_PAD_R * 2))
    return {
      yAxisWidth: w,
      profileRightPx: textW > 0 ? Math.min(w, textW + AXIS_PAD_R + AXIS_BAR_GAP) : w,
    }
  }, [visibleSortedMcaps, formatYAxis])

  // The axis column exists only when it has something to label. Before the
  // first data of a session that is already true (the width memo floors at
  // 48px, but there are no rows and axisHold has never grown), which is the
  // clean empty look. After that the column persisted through every empty
  // state — token switch, reset, a load that hasn't landed — leaving a bare
  // 48px strip, and worse, one still showing the PREVIOUS token's numbers
  // until the new ladder arrives. An axis printing another coin's mcaps is a
  // false claim about this coin. Collapse it instead, so every empty state
  // looks like the first one.
  const hasAxisValues = visibleSortedMcaps.length > 0
  const axisColW = hasAxisValues ? yAxisWidth : 0

  // Publish the column width for the draw loop (universal render-nudge
  // repaints after every commit, so the backdrop resizes the same frame).
  useEffect(() => {
    axisWRef.current = yAxisWidth
    // Publish the LIVE axis width so the header/footer viewport masks clip the
    // time/delta labels at the REAL axis edge. The x-mapping reserves a fixed
    // 60px (FIXED_OVERLAY_WIDTH) so labels stay aligned with the candles, but a
    // large-cap axis is wider than that — without this the labels render in the
    // gap on top of the axis (footer is z6, axis z5). Clipping, not moving, keeps
    // candle alignment intact.
    wrapperRef.current?.style.setProperty('--axis-w', `${yAxisWidth}px`)
  }, [yAxisWidth])

  // ── Y-AXIS PAINTER (canvas) ──────────────────────────────────────────────
  // Pass one of moving the price axis off the DOM. Labels, tier dashes, the
  // hover band and the current-row look are drawn here; the .y-axis-row divs
  // below are now empty hit-targets (see the note at the row map).
  //
  // WHY. The axis was ~110 row divs, each with text, spans, an inline height
  // and a class that changed on hover AND on price. hoverState moves on every
  // mousemove during a hold; highlightMcap moves on every tick. So ~110
  // elements were reconciled and restyled several times a second — measured
  // as 61.5% pre-paint / layout-shift, 16.3% layerize, 10.5% recalculate
  // style in the desktop profile, against 12.9% for drawImage. This is the
  // same reason the volume lane went DOM → canvas.
  //
  // PARITY. Same inputs the DOM rows read, same nearest-row mapping for the
  // current row (bucketRowIndex), same font as measureAxisLabel so the text
  // fits the column that was sized for it. Rows are drawn at i*RH on a
  // canvas that is a child of .y-axis-body, so it rides the body's per-frame
  // residual transform (drawCanvas sets yAxisRef.style.transform) for free —
  // no second alignment term to keep in sync.
  //
  // Z. The canvas sits ABOVE the rows (z7 vs 6, pointer-events:none). In an
  // inactive chart host, `.chart-host-inactive .y-axis-row.current` painted a
  // solid row background; at a lower z that would cover the label. Drawing
  // that look here instead and dropping `.current` from the row class is
  // one fewer DOM write per row-crossing and one fewer thing to disagree.
  //
  // useLayoutEffect, not useEffect: paint in the same frame the row heights
  // commit, so a rebucket never shows one frame of new rows under old text.
  // Sticky font-step state, one per surface that sizes text off row height.
  // Separate refs: the cell loop and the VP ladder have different width
  // terms, so a shared ref would let one drag the other across a step.
  const yAxisCanvasRef = useRef(null)
  // Last font string written to the axis canvas. Canvas 2D keeps its state
  // between paints, so this makes ctx.font a one-time cost instead of a
  // per-paint style resolution. Reset it if the canvas is ever recreated.
  const yAxisFontRef = useRef('')
  // ── Hit-test: pointer → axis row ──
  // The inverse of the painter's `y = i * RH`. Pass two removed the row divs,
  // so the body is the only event surface and every handler on it derives its
  // row from geometry instead of from which element fired.
  //
  // offsetY, not clientY − rect.top: it is the pointer's position in the
  // target's OWN coordinate space, which for a translateY'd body is exactly
  // the pre-transform space the rows were painted in — and it costs no layout
  // read. React's synthetic event doesn't expose it; nativeEvent does. Falls
  // back to the rect only if the target isn't the body itself (the canvas is
  // pointer-events:none, so today that cannot happen; the guard is for the
  // day something else gets mounted inside).
  //
  // isCurrent uses the same nearest-row mapping as the painter and the chip
  // overlay (bucketRowIndex), so all three agree on which row is "current".
  const yAxisHoverRowRef = useRef(-1)
  const axisRowAt = (e) => {
    // Same lattice the painter used, or the axis hit-tests rows it never drew.
    const f = lastFrameRef.current
    const RH = f && f.RH > 0 ? f.RH : dynamicRowHeight
    const mcaps = f ? f.mcaps : visibleSortedMcaps
    if (!(RH > 0) || !mcaps || mcaps.length === 0) return null
    let y
    const ne = e.nativeEvent || e
    if (e.target === e.currentTarget && typeof ne.offsetY === 'number') y = ne.offsetY
    else y = e.clientY - e.currentTarget.getBoundingClientRect().top
    const i = Math.floor(y / RH)
    if (i < 0 || i >= mcaps.length) return null
    const _raw = bucketRowIndex(mcaps, (f && f.livePrice > 0) ? f.livePrice : chartData.highlightMcap)
    const hlIdx = (_raw >= 0 && _raw < mcaps.length) ? _raw : -1
    return { i, mcap: mcaps[i], isCurrent: i === hlIdx }
  }
  const yAxisPaintRef = useRef(null)
  const axisPaintKeyRef = useRef({ mcaps: null, RH: 0, row: NaN })
  const axisBandArmedRef = useRef(false)
  // Row of the live price — the only thing the painter needs from it.
  const axisHlRow = bucketRowIndex(visibleSortedMcaps, chartData.highlightMcap)
  useLayoutEffect(() => {
    const paint = () => {
    const cv = yAxisCanvasRef.current
    if (!cv) return
    // PAINTED lattice — same array and RH the canvas used this pass.
    const f = lastFrameRef.current
    const mcaps = f ? f.mcaps : visibleSortedMcaps
    const RH = f && f.RH > 0 ? f.RH : dynamicRowHeight
    // (y1) Bitmap height from the PAINTED frame — the same rows x RH drawCanvas
    // writes to .y-axis-body, so bitmap and box always agree.
    const W = axisColW, H = (f && f.RH > 0 && f.mcaps) ? f.mcaps.length * f.RH : canvasHeight
    const ctx = cv.getContext('2d')
    if (!ctx) return
    if (!(W > 0) || !(H > 0) || !(RH > 0) || mcaps.length === 0) {
      // Nothing to label. Clear rather than skip: a canvas keeps its last
      // frame, and the previous token's numbers on this coin's axis would be
      // a false claim (the same reason axisColW collapses to 0 here).
      if (cv.width && cv.height) ctx.clearRect(0, 0, cv.width, cv.height)
      return
    }
    const dpr = window.devicePixelRatio || 1
    const needW = Math.round(W * dpr), needH = Math.round(H * dpr)
    // ASSIGNING width/height RESETS THE ENTIRE 2D STATE — font, alpha,
    // transform, the lot. The font cache below assumes state persists across
    // paints, which is true only until this fires, so invalidate it here or
    // the axis renders in the default 10px sans after any resize (and stays
    // that way, because the cache says the font is already set).
    if (cv.width !== needW || cv.height !== needH) {
      cv.width = needW; cv.height = needH
      yAxisFontRef.current = ''
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, W, H)

    const tierOf = axisTierOf(mcaps, RH)
    // The row the CHIP occupies — one live price, or the axis skips a
    // different row than the chip covers.
    const _raw = bucketRowIndex(mcaps, (f && f.livePrice > 0) ? f.livePrice : chartData.highlightMcap)
    const hlIdx = (_raw >= 0 && _raw < mcaps.length) ? _raw : -1
    const hMcap = interaction.magnifier.visible ? interaction.magnifier.mcap
      : (interaction.hoverState.rows.length > 0 ? interaction.hoverState.rows[0] : null)
    const cMcap = chartData.highlightMcap

    // Band meets the dashed live line (yOf), not the row under it.
    // The old fill walked whole rows and skipped hlIdx. bucketRowIndex is
    // ceil on a descending ladder, so that skipped cell is the one under
    // the price: green stopped 0.5-1.5 rows below the line and the gap
    // changed as price moved inside the step. Red's next row already
    // reached the line, which is why only green showed a gap.
    if (hMcap != null && hMcap > 0) {
      const liveMcap = (f && f.livePrice > 0) ? f.livePrice : cMcap
      if (liveMcap > 0 && liveMcap !== hMcap) {
        const yOf = (m) => (f && typeof f.yOf === 'function') ? f.yOf(m) : mcapToYUtil(m, mcaps, RH)
        const yLive = yOf(liveMcap)
        const yLim = yOf(hMcap)
        if (Number.isFinite(yLive) && Number.isFinite(yLim)) {
          const buy = hMcap < liveMcap
          const bg   = buy ? 'rgba(0, 255, 13, 0.151)' : 'rgba(255, 0, 21, 0.158)'
          const edge = buy ? 'rgba(0, 174, 255, 0.5)'  : 'rgba(255, 68, 85, 0.5)'
          const y0 = Math.max(0, Math.min(yLive, yLim))
          const y1 = Math.min(H, Math.max(yLive, yLim))
          if (y1 > y0) {
            ctx.fillStyle = bg;   ctx.fillRect(0, y0, W, y1 - y0)
            ctx.fillStyle = edge; ctx.fillRect(0, y0, 2, y1 - y0)
          }
        }
      }
    }

    // ── current row in an INACTIVE host: the solid chip the DOM row drew ──
    if (!isActiveChart && hlIdx >= 0) {
      ctx.fillStyle = '#3a3f4c'
      ctx.fillRect(0, hlIdx * RH, W, RH)
    }

    // ── labels ──
    // ctx.font IS NOT A CHEAP ASSIGNMENT. It parses the shorthand and resolves
    // it against the document's font set — Chrome bills it as Recalculate
    // Style, and this painter re-runs on every tick and every hover change.
    // Setting it twice a paint (14px labels, then 9px for the tick glyph)
    // profiled at 351.4ms of self time on this line. Canvas keeps its state
    // across paints — setTransform and clearRect don't reset it — so caching
    // what we last set makes this ONE assignment for the life of the canvas.
    if (yAxisFontRef.current !== AXIS_LABEL_FONT) {
      ctx.font = AXIS_LABEL_FONT
      if ('letterSpacing' in ctx) ctx.letterSpacing = `${AXIS_LETTER_SPACING_PX}px`
      yAxisFontRef.current = AXIS_LABEL_FONT
    }
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    const cx = W / 2
    let anyTick = false
    // Gold .y-axis-current-overlay owns the live price label. Forcing the
    // current row to 'major' here printed a second number (e.g. 2520.0) one
    // row off a real major (2520.1) — the axis bunching.
    for (let i = 0; i < mcaps.length; i++) {
      if (i === hlIdx) continue
      const tier = tierOf(mcaps[i], i)
      if (tier === 'tick') { anyTick = true; continue }
      if (tier === 'blank') continue
      ctx.globalAlpha = tier === 'minor' ? 0.3 : 1
      ctx.fillStyle = '#646464'
      const rowStep = i + 1 < mcaps.length
        ? Math.abs(mcaps[i] - mcaps[i + 1])
        : (i > 0 ? Math.abs(mcaps[i - 1] - mcaps[i]) : null)
      // Centred on the ROW: the label names the bucket [mcaps[i], +step),
      // whose cell fills this row. Prices plot at the row's bottom edge.
      ctx.fillText(formatYAxis(mcaps[i], rowStep), cx, i * RH + RH / 2)
    }
    // The tick is a 9px en-dash — which rasterises to a short horizontal bar
    // and nothing else. Drawing the bar directly keeps it identical to the
    // eye and avoids a SECOND font assignment (and its switch back on the
    // next paint), which is the whole cost this block was carrying. 4.5px is
    // the en-dash advance at 9px; 1px is its stem.
    if (anyTick && RH >= Y_TICK_MIN_ROW_PX) {
      ctx.globalAlpha = 0.3
      ctx.fillStyle = '#646464'
      for (let i = 0; i < mcaps.length; i++) {
        if (i === hlIdx) continue
        if (tierOf(mcaps[i], i) === 'tick') {
          ctx.fillRect(cx - 2.25, Math.round(i * RH + RH / 2), 4.5, 1)
        }
      }
    }
    ctx.globalAlpha = 1
    }
    axisBandArmedRef.current = !!(interaction.magnifier.visible && interaction.magnifier.mcap > 0)
    yAxisPaintRef.current = paint
    paint()
  }, [visibleSortedMcaps, dynamicRowHeight, axisColW, canvasHeight,
      axisHlRow, interaction.magnifier.visible, interaction.magnifier.mcap,
      interaction.hoverState.rows, isActiveChart, formatYAxis])

  // Fade strips (.y-axis-region::before/::after): 5% of the chart BODY at
  // top and bottom. containerHeight is the body's measured clientHeight
  // (ResizeObserver above) — the SAME box CSS `height: 5%` resolves against —
  // so publishing 5% × containerHeight as the px var makes the fade a true 5%
  // whether the live CSS sizes the strips by var(--axis-fade-h) OR by height:5%.
  // Recomputes on every resize. Change 0.05 to tune (0.08 ≈ a deeper fade).
  useEffect(() => {
    const el = wrapperRef.current
    if (!el || !(containerHeight > 0)) return
    el.style.setProperty('--axis-fade-h', `${Math.round(containerHeight * 0.05)}px`)
  }, [containerHeight])

  // ============================================
  // Scroll hook
  // ============================================

  // Pinch zoom state — shared with useChartScroll so pan is suppressed
  // while two fingers are down. True from the moment a second pointer
  // lands until ALL pointers lift (prevents a stale one-finger pan from
  // resuming with old dragStart coords after the pinch ends).
  const pinchActiveRef = useRef(false)

  // Crosshair MODE (phone): long-press ENTERS a persistent crosshair mode —
  // finger can lift, drags scrub the crosshair, the chart doesn't pan. A
  // single quick tap EXITS back to normal swiping. A long-press held still
  // inside the mode still sets an alert on lift (hook behavior unchanged).
  // Declared HERE (above useChartScroll) so the scroll hook can gate its
  // pointer pan on them — the document-level touchmove blocker only stops
  // NATIVE scrolling; the drag pan is pointer-event driven and kept moving
  // the chart under a scrubbing finger until the hook learned to check.
  // (phone-xhair) The MODE is now owned by the header crosshair button, not a
  // gesture. Persisted per device; phone only (desktop never turns it on).
  const PHONE_XHAIR_KEY = 'solcheddar:phoneCrosshair'
  const [xhairOn, setXhairOn] = useState(() => {
    try { return isPhoneDevice() && localStorage.getItem(PHONE_XHAIR_KEY) === '1' } catch { return false }
  })
  const crosshairModeRef = useRef(xhairOn)
  // True only mid-gesture while a long-press has armed and the finger is
  // still down scrubbing (the arm callback sets it, pointerup clears it).
  const crosshairArmedRef = useRef(false)

  // (scrollperf) scheduleCanvasDrawRef is declared above useChartInteraction
  // (chart-perf: AVWAP hover schedules through it) and filled right after
  // scheduleCanvasDraw is defined (below).
  const scroll = useChartScroll({
    setAutoScrollEnabled, autoScrollRef, chartData,
    visibleSortedMcaps, visibleSortedMcapsRef, dynamicRowHeight,
    containerHeight, bodyScrollRef, yAxisRef, viewWindowSizeRef,
    containerWidthRef, viewEndIndex, setViewEndIndex, viewEndIndexRef,
    pinchActiveRef,
    crosshairModeRef, crosshairArmedRef,
    drawCanvasRef,
    scheduleDrawRef: scheduleCanvasDrawRef,
    // Same ref as slot xWin.fracPxRef when present — pan writes the one owner.
    fractionalOffsetPxRef,
    wheelParkRef: xWin?.wheelParkRef,
    isDraggingChartRef,
    freezeXGlideRef,
    onDragEdgeRef: kickYFollowRef,
    onYDragRef: yBodyDragRef,
  })

  // Sync the ref read by the bucket auto-adjust effect above. The FLAG is
  // already true from pointerdown (see useChartScroll); this render only
  // mirrors hook state for the glide half of the gesture, where isGliding
  // flips without another pointerdown. Seed and settle are NOT called here.
  // They used to be, after the grid memo, so the memo stamp-fit and the
  // kick's flushSync was overwritten by the render already in progress.
  {
    const prevDrag = isDraggingChartRef.current
    isDraggingChartRef.current = scroll.isDragging
    if (scroll.isDragging) {
      beginChartInteractRef.current()
    } else if (prevDrag) {
      endChartInteractRef.current()
    }
  }

  // Sync isDraggingMirror (state, declared above the bucket effect) to
  // scroll.isDragging (state on the hook below). The roundtrip costs one
  // extra render per drag start and per drag end; it's the price of
  // working around the declaration-order constraint where the bucket
  // effect lives above useChartScroll. See the mirror's declaration for
  // why this exists.
  useEffect(() => {
    setIsDraggingMirror(scroll.isDragging)
    // Pan-drag start: redraw so heat + cells track the new pan offset together.
    if (scroll.isDragging) scheduleCanvasDraw()
  }, [scroll.isDragging])

  // ── (xzoom) Persist the user's X view (xZoomStore) ─────────────────────
  // Trailing write once X has been still XZOOM_SAVE_MS — wheel glide landed,
  // footer / pinch released, pan settled — never per frame. While a gesture
  // or a TF switch is in flight it re-arms instead of writing. Stores:
  //   win — only after a manual zoom (auto-fit = default = not stored);
  //   gap — only while the live candle is on screen and the end is concrete;
  //         scrolled into history keeps the previous gap. Default = absent.
  // The treadmill re-runs this every new column with the same values; the
  // save is a no-op when nothing changed.
  const xSaveTimerRef = useRef(0)
  useEffect(() => {
    const XZOOM_SAVE_MS = 600
    const run = () => {
      xSaveTimerRef.current = 0
      const busy = !!(scroll?.isDraggingRef?.current || zoomGlideRef.current || zoomGlideRafRef.current
        || footerDragRef.current || pinchActiveRef.current || tfLiveAnchorRef.current
        || (xWin?.wheelParkRef?.current && viewEndIndexRef.current === -1))
      if (busy) { xSaveTimerRef.current = setTimeout(run, XZOOM_SAVE_MS); return }
      const total = chartDataRef.current?.candles?.length || 0
      if (total < 2) return
      const prev = loadXView(chartId) || {}
      const win = Math.max(1, Math.round(+viewWindowSizeRef.current || 1))
      const next = { win: xAutoFitRef.current ? null : win, gap: prev.gap ?? null }
      const endRaw = viewEndIndexRef.current
      if (endRaw !== -1) {
        const gap = resolveEndIdx(endRaw, total) - (total - 1)
        if (gap >= 0 && gap <= win - 1) next.gap = gap === AUTO_PADDING ? null : gap
      }
      saveXView(chartId, next)
    }
    clearTimeout(xSaveTimerRef.current)
    xSaveTimerRef.current = setTimeout(run, XZOOM_SAVE_MS)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewWindowSize, viewEndIndex, chartId])
  useEffect(() => () => clearTimeout(xSaveTimerRef.current), [])

  // ─── Pan redraw: rAF-coalesced, and skipped when the output cannot differ ───
  // pointermove fires at the pointer's rate, not the display's — 120Hz+ on a
  // modern phone, and every event used to run a SYNCHRONOUS full drawCanvas.
  // At best half those frames were painted over before anyone saw them; the
  // cost scaled with candle count, which is why panning degraded as a chart
  // filled rather than being uniformly slow. Hover was coalesced for exactly
  // this reason (see useChartInteraction); the pan never was.
  //
  // Two guards, in cost order:
  //   1. COALESCE. At most one draw per animation frame. Events arriving
  //      inside a frame collapse; the draw reads the ref, so it always
  //      renders the LATEST offset rather than the one that scheduled it.
  //   2. IDENTICAL-OUTPUT SKIP. drawCanvas quantizes the pan offset to the
  //      device-pixel grid (see panOffsetPx). Two moves landing in the same
  //      device pixel produce a bit-identical frame, so the second is pure
  //      waste. Only applied MID-DRAG: outside a drag any number of other
  //      things can have changed, and a stale frame is far worse than a
  //      redundant one.
  const panLastRef = useRef({ q: NaN, end: NaN })
  // `scroll` through a REF, and this effect mounts ONCE.
  //
  // useChartScroll returns a bare object literal, so `scroll` is a fresh
  // identity on every render — the same fact that keeps it out of the
  // drawCanvas deps. With [scroll] here, the effect re-subscribed on every
  // render and its cleanup ran cancelAnimationFrame. A drag renders at every
  // index tick (setViewEndIndex, once per candle-width), so at every candle
  // boundary the MAIN chart's pending draw was cancelled and it skipped a
  // frame — while DVP, CVD and the trail, on their own uninterrupted rAF
  // loops, drew the new position. Once per candle the subgraphs moved and
  // the chart didn't: the horizontal, drag-only, all-three, once-per-candle
  // bounce. The old synchronous handler had nothing pending to cancel; the
  // deferral is what made the dependency load-bearing.
  const scrollRef = useRef(scroll)
  scrollRef.current = scroll
  useEffect(() => {
    const handler = (e) => {
      // (chart2-bounce) This is a window event and every mounted chart
      // listens. Unscoped, a drag on chart 1 re-ran chart 2's pan sync, its
      // Y-follow kick and a full redraw on every pointermove (and the
      // reverse). Only the chart whose scroll fired it acts; an event with no
      // src (any other caller) still redraws everyone, as before.
      const _src = e && e.detail && e.detail.src
      if (_src && _src !== scrollRef.current?.redrawSrcRef?.current) return
      // SYNC THE MIRROR HERE, synchronously, before anything is scheduled.
      // There are two refs named fractionalOffsetPxRef: the real one inside
      // useChartScroll that the pan handler writes, and this component's
      // mirror, which is what DVP, CVD and the trail are handed. The subgraphs
      // draw on their own rAF loops, registered at the END of their previous
      // frame — so they fire BEFORE any rAF scheduled during this frame's
      // pointermove. With the sync inside paint(), they read the mirror one
      // frame stale while the chart drew current: chart smooth, subgraphs
      // one step behind, every frame. Syncing in the handler restores the
      // guarantee the old synchronous version had by accident — by the time
      // any rAF callback runs, the mirror already holds this frame's value.
      fractionalOffsetPxRef.current = scrollRef.current.fractionalOffsetPxRef.current
      const sc = scrollRef.current
      if (sc.isDraggingRef?.current) {
        const dprQ = window.devicePixelRatio || 1
        const q = Math.round(fractionalOffsetPxRef.current * dprQ) / dprQ
        const end = viewEndIndexRef.current
        if (q === panLastRef.current.q && end === panLastRef.current.end) return
        panLastRef.current.q = q; panLastRef.current.end = end
      } else {
        panLastRef.current.q = NaN; panLastRef.current.end = NaN
        panGeomRef.current.key = ''; panGeomRef.current.fill = null
      }
      // The drag dispatches this on every move. Columns entering and leaving
      // the window step the fit; Y eases toward that window. x is untouched.
      kickYFollowRef.current()
      // Shared rAF with scheduleCanvasDraw — one draw per frame max.
      scheduleCanvasDraw()
    }
    window.addEventListener('solcheddar-redraw', handler)
    // Cleanup is now UNMOUNT-only. Cancelling a pending frame is right for a
    // teardown and was the whole bug for a re-subscribe.
    return () => {
      window.removeEventListener('solcheddar-redraw', handler)
    }
  }, [])

  // (ydragslide) X-drag Y fit: one visible hi+lo window. Centre and half
  // both ease toward it (Y_DRAG_FIT_TAU_MS). Half is NOT snapped out to
  // cover the destination — that snap was the zoom-out pop; the edges ease
  // with the same tau, so a new extreme slides into frame instead of
  // appearing full-size. Kick is gated to drag / release so live redraws
  // do not move Y. One rAF loop; release just lets that loop land.
  // Seed runs from pointerdown (before the grid memo's next pass), not
  // from this component's render.
  useEffect(() => {
    const knownPrice = () => {
      const d = chartDataRef.current
      const hl = +d?.highlightMcap || 0
      if (hl > 0) return hl
      const cs = d?.candles
      if (cs) {
        for (let i = cs.length - 1; i >= 0; i--) {
          const v = +(cs[i]?.close_mcap ?? cs[i]?.close) || 0
          if (v > 0) return v
        }
      }
      return 0
    }

    // (dragfit-edge) Edge columns count toward the drag fit only once at least
    // this fraction of their width is on screen. The left overdraw column
    // (startIdx-1) shows frac px; the rightmost column (endIdx) shows cw-frac
    // px (the rest is under the axis). At 0.5 exactly one of the two is in at
    // any moment, swapping at the half-way point, so a new low/high starts
    // pulling Y when half its candle is visible, not at 0px.
    // fullEdges (seed / release): old rule, both edges in, so the release
    // lands on the same range the resting fit scans (no pop on let-go).
    const DRAG_FIT_EDGE_VIS = 0.5
    const planDragFitY = (fullEdges = false) => {
      const cs = chartDataRef.current?.candles
      const total = cs?.length || 0
      if (!total) return null
      const w = Math.max(1, Math.round(+viewWindowSizeRef.current || 1))
      const endRaw = resolveEndIdx(viewEndIndexRef.current, total)
      const endIdx = clampEndIdx(endRaw, total, w)
      if (endIdx === -1) return null
      const startIdx = startIdxFor(endIdx, w)
      let lo = startIdx - 1, hi = endIdx
      if (!fullEdges) {
        const plotW = Math.max(1, containerWidthRef.current - RIGHT_PADDING - FIXED_OVERLAY_WIDTH)
        const cwPx = plotW / Math.max(1, +viewWindowSizeRef.current || 1)
        if (cwPx > 0) {
          const vis = Math.min(1, Math.max(0, (+fractionalOffsetPxRef.current || 0) / cwPx))
          if (vis < DRAG_FIT_EDGE_VIS) lo = startIdx                                   // left sliver too thin
          if (1 - vis < DRAG_FIT_EDGE_VIS && hi - 1 >= startIdx && hi < total) hi = endIdx - 1   // right column mostly under the axis
        }
      }
      const slice = cs.slice(Math.max(0, lo), Math.min(total, hi + 1))
      if (!slice.length) return null
      const fit = scanFitRange(slice, logScaleRef.current, knownPrice, cs)
      if (!fit) return null
      const bucket = fit.log ? Math.log(mcapRatioRef.current) : mcapBucketSizeRef.current
      if (!(bucket > 0)) return null
      const dataRange = fit.max - fit.min
      let half = (dataRange / 2) + computeRangeBuffer(dataRange, bucket)
      const minQuietRows = 24
      const minHalf = (minQuietRows * bucket) / 2
      if (half < minHalf) half = minHalf
      return {
        half,
        centre: (fit.max + fit.min) / 2 - bucket,
        min: fit.min,
        max: fit.max,
      }
    }

    const yScaleLive = () => !!(
      yGlideRafRef.current
      || yHalfTargetRef.current != null
      || yAxisZoomDragActiveRef.current
      || yCam.isGesturing()
    )

    // (drag-shimmer) The ease stopped without its own landing paint: repaint
    // once so candles drawn unsnapped mid-ease re-snap.
    const resnapAfterEase = () => {
      if (!yUnsnappedPaintRef.current) return
      requestFrame(() => {
        if (!yDragFitRafRef.current && yUnsnappedPaintRef.current && drawCanvasRef.current) drawCanvasRef.current()
      }, FRAME_ANIM)
    }

    const stopDragFitEase = () => {
      yDragFitSettleRef.current = false
      yDragFitLastTsRef.current = 0
      if (yDragFitRafRef.current) {
        cancelFrame(yDragFitRafRef.current)
        yDragFitRafRef.current = 0
      }
      resnapAfterEase()
    }

    const releaseDragFit = () => {
      stopDragFitEase()
      yDragFitDestRef.current = null
      yCam.clearFitHold('drag-release')
      if (!zoomGlideRef.current?.cone) {
        glideHalfRef.current = null
        glideCenterRef.current = null
      }
    }

    // One ease. Dest is live (retargeted by kick). Release does not start a
    // second timer — it lets this loop land, then drops the drag window.
    const stepDragFit = (ts) => {
      const dest = yDragFitDestRef.current
      if (!dest || !(dest.half > 0)) {
        yDragFitRafRef.current = 0
        resnapAfterEase()
        return
      }
      if (yCam.isPinned() || yScaleLive() || zoomGlideRef.current?.cone) {
        yDragFitDestRef.current = null
        yDragFitSettleRef.current = false
        yDragFitRafRef.current = 0
        resnapAfterEase()
        return
      }
      const destC = dest.centre
      const destH = dest.half
      const curH = glideHalfRef.current
      const curC = glideCenterRef.current
      if (!Number.isFinite(curH) || !Number.isFinite(curC)) {
        yDragFitRafRef.current = 0
        resnapAfterEase()
        return
      }
      const last = yDragFitLastTsRef.current
      const dt = (last && ts > last) ? (ts - last) : 16.67
      yDragFitLastTsRef.current = ts || 0
      // Same tau on both, so each edge approaches the destination edge on
      // its own exponential. No cover snap, no late collapse.
      let c = easeToward(curC, destC, dt, Y_DRAG_FIT_TAU_MS)
      let h = easeToward(curH, destH, dt, Y_DRAG_FIT_TAU_MS)
      const base = Math.max(1e-9, destH)
      const arrived = Math.max(Math.abs(destH - h) / base, Math.abs(destC - c) / base) < 2e-3
      if (arrived) {
        h = destH
        c = destC
      }
      glideHalfRef.current = h
      glideCenterRef.current = c
      yfitC('dragFit ease step', { c, h, extra: `dest c=${destC} h=${destH}` })
      yCam.clearFitHold('drag-ease')
      const settleNow = yDragFitSettleRef.current && !isDraggingChartRef.current
      if (arrived) yDragFitRafRef.current = 0
      // Paint while the drag window is still armed. Release after, matching
      // the old land: the committed frame is the destination, then idle
      // framing resumes from that stamp.
      // (drag-smooth) Same rows -> update in place (no React render), like the
      // Y glide. Rows/bucket change, or the landing frame -> render path.
      if (arrived || !dragFitInPlaceRef.current()) flushSync(() => setYFitTick((t) => t + 1))
      if (arrived && settleNow) releaseDragFit()
      else if (!arrived) yDragFitRafRef.current = requestFrame(stepDragFit, FRAME_ANIM)
    }

    kickYFollowRef.current = (opts = {}) => {
      const seed = !!opts.seed
      const settle = !!opts.settle
      if (opts.abort) {
        releaseDragFit()
        return
      }
      const dragging = !!isDraggingChartRef.current
      if (seed) {
        yDragFitSettleRef.current = false
        // (wheel-drag) a drag seed owns Y now - no stale wheel landing snap-back.
        pendingLandYRef.current = null
        yLandExactRef.current = null
      }
      if (settle) yDragFitSettleRef.current = true
      // Live solcheddar-redraw must not move Y — only an X-drag, its release,
      // or a dest still held from the current drag.
      if (!dragging && !settle && !seed && !yDragFitDestRef.current) return
      if (yCam.isPinned() || yScaleLive() || zoomGlideRef.current?.cone) {
        // Pin / Y-scale / wheel cone own framing. Drop the drag window.
        if (yDragFitDestRef.current) yDragFitDestRef.current = null
        stopDragFitEase()
        return
      }

      const dest = planDragFitY(seed || settle)   // (dragfit-edge) seed/release: full edges
      if (!dest || !(dest.half > 0)) {
        if (settle) {
          releaseDragFit()
          flushSync(() => setYFitTick((t) => t + 1))
        }
        return
      }
      // (dragfit-catch) Same plan as the one already armed -> nothing to do.
      // The drag fires this on EVERY pointermove (often 2-3 per frame) and
      // each one used to re-arm the same dest and clearFitHold('drag-kick').
      // The running ease (or the landed glide) already owns that dest.
      {
        const live = yDragFitDestRef.current
        if (!seed && !settle && live
            && live.min === dest.min && live.max === dest.max
            && live.half === dest.half && live.centre === dest.centre) return
      }

      const m = visibleSortedMcapsRef.current
      let half = glideHalfRef.current
      let centre = glideCenterRef.current
      if (!Number.isFinite(half)) {
        half = Number.isFinite(m?.halfRange) ? m.halfRange : null
      }
      if (!Number.isFinite(centre)) {
        centre = Number.isFinite(m?.fitCenter) ? m.fitCenter : null
      }
      if (!Number.isFinite(half) || !Number.isFinite(centre)) return

      // Seed/freeze: publish current as glide so dragFitLive owns framing and
      // yCam.frame cannot stamp-jump on the first column that enters.
      if (!Number.isFinite(glideHalfRef.current)) {
        glideHalfRef.current = half
        glideCenterRef.current = centre
      }

      const curH = glideHalfRef.current
      const curC = glideCenterRef.current
      const baseH = Math.max(1e-9, curH)
      const needMove = Math.max(
        Math.abs(dest.half - curH) / baseH,
        Math.abs(dest.centre - curC) / baseH,
      ) > 2e-3

      if (seed && !needMove) {
        // Freeze at the current window for the drag; dest tracks so the live path stays on.
        stopDragFitEase()
        yDragFitDestRef.current = {
          half: curH,
          centre: curC,
          min: dest.min,
          max: dest.max,
        }
        return
      }

      if (!needMove) {
        // Already on this window. If a loop is still aimed at an OLD
        // destination, adopting that plan is what stops the ease walking
        // away from the columns now on screen. Comparing needMove to the
        // glide (not to yDragFitDestRef) used to return here and leave the
        // stale dest in place.
        const live = yDragFitDestRef.current
        const stale = !!(yDragFitRafRef.current && live && (
          Math.abs(live.half - dest.half) / baseH > 2e-3
          || Math.abs(live.centre - dest.centre) / baseH > 2e-3
        ))
        if (yDragFitRafRef.current) {
          cancelFrame(yDragFitRafRef.current)
          yDragFitRafRef.current = 0
          resnapAfterEase()
        }
        if (stale) {
          glideHalfRef.current = dest.half
          glideCenterRef.current = dest.centre
        }
        yDragFitDestRef.current = dest
        if (settle) {
          releaseDragFit()
          flushSync(() => setYFitTick((t) => t + 1))
        }
        return
      }

      // Retarget. Centre and half ease together on the rAF loop. No
      // immediate half expand — that was a one-frame zoom-out pop.
      yDragFitDestRef.current = dest
      yfitC('planDragFitY dest', { lo: dest.min, hi: dest.max, c: dest.centre, h: dest.half })
      yCam.clearFitHold('drag-kick')
      if (!yDragFitRafRef.current) {
        yDragFitLastTsRef.current = 0
        yDragFitRafRef.current = requestFrame(stepDragFit, FRAME_ANIM)
      }
    }

    return () => {
      if (yDragFitRafRef.current) { cancelFrame(yDragFitRafRef.current); yDragFitRafRef.current = 0 }
      yDragFitSettleRef.current = false
      yDragFitDestRef.current = null
      if (!zoomGlideRef.current?.cone) {
        glideHalfRef.current = null
        glideCenterRef.current = null
      }
      kickYFollowRef.current = () => {}
    }
  }, [yCam])

  // (yexplore) Autoscroll re-arm follows the live edge in X only. The Y span
  // / explore are the user's until the Y-axis (or footer) double-click.
  useEffect(() => {
    if (autoScrollEnabled) scroll.scrollToLatest()
  }, [autoScrollEnabled, scroll.scrollToLatest])

  // Delta panes' wheel -> the main body wheel handler (see bodyWheelHandlerRef).
  // Native non-passive listeners so preventDefault actually stops the page
  // scroll (React's onWheel is passive). Phone has no wheel: unaffected.
  useEffect(() => {
    const els = [dvpPaneEl, cvdPaneEl].filter(Boolean)
    if (!els.length) return
    const onWheel = (e) => {
      e.preventDefault()
      const h = bodyWheelHandlerRef.current
      if (h) h(e)
    }
    for (const el of els) el.addEventListener('wheel', onWheel, { passive: false })
    return () => { for (const el of els) el.removeEventListener('wheel', onWheel) }
  }, [dvpPaneEl, cvdPaneEl])

  // ============================================
  // Volume profile
  // ============================================

  // ============================================
  // Swing high/low detection (full candle array for global structure context)
  // ============================================

  // Keyed on structuralVersion, not the candles reference: swing pivots are
  // confirmed only by N *closed* bars on each side, so the live bar (and the
  // most recent N) can never be a new pivot between closes. Recomputing this
  // O(candles × levels) scan on every publish was pure waste — now it runs once
  // per structural change (close/trim/rebuild/switch), bit-identical output.
  const swings = useMemo(() => {
    const candles = chartData.candles
    if (candles.length < 6) return { swingHighs: [], swingLows: [], structureEvents: [] }
    const n = computeAdaptiveLookback(candles)
    const detected = detectSwings(candles, n)
    return { ...detected, structureEvents: detectStructure(detected) }
  }, [chartData.structuralVersion])

  // Incremental: recomputes only the LIVE bar each publish and rebuilds the
  // closed portion only on a structural change — O(1)/tick vs O(candles×levels).
  // The live value still updates every tick, so the bot's ATR trail ratchets
  // intra-bar exactly as before. Bit-identical to the full recompute (verified).
  const atrStop = useMemo(() => {
    const candles = chartData.candles
    if (candles.length < 16) return []
    return computeATRTrailingStopLive(
      candles,
      // config folds into the version key: any knob change reads as a
      // structural change to the cache and forces the full rebuild
      `${chartData.structuralVersion}|${atrTrailCfg.atrPeriod}|${atrTrailCfg.hhvPeriod}|${atrTrailCfg.mult}`,
      atrCacheRef.current,
      atrTrailCfg.atrPeriod, atrTrailCfg.hhvPeriod, atrTrailCfg.mult)
  }, [chartData.candles, chartData.structuralVersion, atrTrailCfg])

  // Churn zones: closed candles only -> once per structural change, and only when on.
  const churnZones = useMemo(() => (showChurnZones ? computeChurnZones(chartData.candles, churnCfg) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chartData.structuralVersion, showChurnZones, churnCfg])
  churnZonesRef.current = churnZones
  // Console check: window.__churnZones['main.fut'|'main.sol'|...] -> { on, count, zones, stats, style }
  try { if (typeof window !== 'undefined') { const w = (window.__churnZones = window.__churnZones || {}); w[settingsScope] = { on: !!showChurnZones, token: effectiveToken, structuralVersion: chartData.structuralVersion, count: churnZones.length, zones: churnZones, stats: churnZones.stats || null, style: churnCfg.style, candles: chartData.candles?.length || 0 } } } catch {}
  // Background mode: idx -> tinted plate colour, read by the cell plate pass.
  const churnBg = useMemo(() => (showChurnZones && churnCfg.style === 'background' ? buildChurnBgMap(churnZones, churnCfg) : null),
    [showChurnZones, churnZones, churnCfg])
  churnBgRef.current = churnBg
  showChurnZonesRef.current = showChurnZones

  // Sync inline — no extra render cycle
  swingsRef.current      = swings
  showSwingsRef.current  = showSwings
  atrStopRef.current     = atrStop
  showATRStopRef.current = showATRStop
  // MA knob changes: one full repaint (values rebuild lazily in the pass).
  useEffect(() => {
    if (!maConfigs.some(c => c.on) && maHoverIdRef.current != null) maHoverIdRef.current = null
    scheduleCanvasDrawRef.current?.('full')
  }, [maConfigs])
  showFlipZonesRef.current = showFlipZones
  showMeteoraRef.current = showMeteora
  showValueAreaRef.current = showValueArea
  vaConfigRef.current = vaConfig

  // Close-cadence: processCandles does a full reset()+reingest (O(candles ×
  // levels), the heaviest of the indicators). Keyed on structuralVersion, it
  // runs once per close/trim/rebuild/switch instead of every publish. Rendered
  // output is effectively unchanged — a zone only renders after >= minTouches
  // (2) touches, which accrue on closed candles; the live bar can add at most
  // one provisional touch that a close would confirm anyway.
  const flipZones = useMemo(() => {
    return flipDetectorRef.current.processCandles(chartData.candles)
  }, [chartData.structuralVersion])

  flipZonesRef.current = flipZones

  // Rebucket de-spike: VP rebuilds one frame behind a *committed* ladder
  // bucket to split the phone rescale hitch. Always read the latest value
  // from the ref inside rAF — the old deps-on-vpBucket + early-return path
  // could cancel the in-flight frame and never catch up ("missed rebucket").
  const [vpBucket, setVpBucket] = useState(mcapBucketSize)
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const next = mcapBucketSizeRef.current
      setVpBucket(prev => (prev === next ? prev : next))
    })
    return () => cancelAnimationFrame(id)   // raw rAF id — see the deferral above
  }, [mcapBucketSize])

  // (scrollperf) The profile reads the grid ONLY for its step / ratio — two
  // primitives. Keyed on the lattice ARRAY, it re-binned every visible candle
  // (forEachLevel / logStepIdx) on every remint — every few frames of a Y-wheel
  // glide as the row COUNT changes — while the step, and so the profile, had
  // not moved. Same derivation, hoisted; deps are now the derived numbers.
  let vpGridRatio = null, vpGridStep = null
  {
    const grid = visibleSortedMcaps
    if (grid?.length >= 2) {
      if (grid.log && grid[0] > 0 && grid[1] > 0) {
        // (va) The lattice's own step, exp(L.step) — the ratio the cells bin
        // with (dispRatio). The 1e8-rounded spacing was a slightly different
        // log ladder, so VP rows drifted off grid rows far from the anchor.
        // Still one stable double per step, so the memo deps stay quiet.
        vpGridRatio = grid.step > 0 ? Math.exp(grid.step) : Math.round((grid[0] / grid[1]) * 1e8) / 1e8
      } else {
        const st = Math.round((grid[0] - grid[1]) * 1e8) / 1e8
        if (st > 0) vpGridStep = st
      }
    }
  }
  const volumeProfile = useMemo(() => {
    // INTEGER-KEYED builder: Map<stepIdx, [buy, sell, total]> where
    // step = stepIdxOf(mcap, bucket). The old shape paid a float-precision
    // snapBucket + String() alloc + object-property churn PER LEVEL — the
    // bulk of the rebucket spike after the DOM. Same math, several times
    // cheaper, and immune to the too-many-properties class by construction.
    const profile = new Map()
    let maxVol = 0
    // maxSide: largest SINGLE-SIDED print in view — the footprint cells'
    // scale. candleMaxLevelVolume takes buy and sell separately, not the
    // level total, so a footprint-mode VP scaled by maxVol would draw every
    // bar at roughly half the width of the cells beside it. Stays 0 when
    // every visible candle is smeared history (no split anywhere), which
    // the draw pass uses as its "nothing to ladder" signal.
    let maxSide = 0
    // GLOBAL ROW CAP — the per-candle poison guard below bounds ONE candle,
    // but the UNION of keys across candles is span/bucket: a degenerate
    // bucket (sized off a 1-candle view sliver) against the full visible
    // span produced 100K+ unique keys → 100K+ VP bar DOM nodes → the phone
    // lockup. Mirror the grid's emergency coarsen: profile at a coarsened
    // bucket whenever span/bucket exceeds what a screen could ever show.
    // The VP bar rows are grid-row sized at render anyway, so a profile
    // finer than ~1K rows is invisible by construction.
    // Prefer the LIVE grid lattice (same ground truth drawCanvas uses).
    // Emergency coarsen / land install can move the painted step off the
    // committed ladder while vpBucket still lags — binning only on vpBucket
    // then "misses" the rebucket and stacks bars on wrong rows.
    let bucketSize = vpBucket
    let ratio = logScale && mcapRatio > 1 ? mcapRatio : null
    if (vpGridRatio != null) ratio = vpGridRatio          // (scrollperf) hoisted
    else if (vpGridStep != null) bucketSize = vpGridStep
    // LOG: profile bins on the same global lattice the cells do —
    // logStepIdx(mcap, ratio) — so a VP row and a grid row are the same row.
    // If we only have the lagged ladder (no grid yet), keep ratio/mcapRatio.
    // Walk coarser MCAP_RATIO_STEPS below when the span exceeds the row cap.
    {
      const MAX_VP_ROWS = 1024
      const r = scanCandlesMcapRange(visibleCandles)
      if (Number.isFinite(r.min) && Number.isFinite(r.max) && r.max > r.min) {
        if (ratio) {
          if (!(r.min > 0)) {
            ratio = null            // non-positive price: fall back to linear
          } else {
            let need = Math.log(r.max / r.min) / Math.log(ratio)
            if (need > MAX_VP_ROWS) {
              let rCur = snapRatio(ratio)
              while (need > MAX_VP_ROWS) {
                const i = MCAP_RATIO_STEPS.indexOf(rCur)
                if (i < 0 || i >= MCAP_RATIO_STEPS.length - 1) break
                rCur = MCAP_RATIO_STEPS[i + 1]
                need = Math.log(r.max / r.min) / Math.log(rCur)
              }
              ratio = rCur
            }
          }
        }
        if (!ratio && bucketSize > 0) {
          const need = (r.max - r.min) / bucketSize
          if (need > MAX_VP_ROWS) bucketSize = (r.max - r.min) / MAX_VP_ROWS
        }
      }
    }
    if (!ratio && !(bucketSize > 0)) return { profile, maxVol: 0, maxSide: 0, bucket: 0, ratio: null }
    const stepOf = ratio ? (m) => (m > 0 ? logStepIdx(m, ratio) : NaN) : (m) => stepIdxOf(m, bucketSize)
    // Each level tracks buy/sell separately (for dominance coloring) plus a
    // total (for bar width). Live candles supply the buy/sell split via
    // forEachLevel; historical candles carry only aggregate volume, so it
    // lands in `total` with buy/sell left at 0 (those levels render neutral).
    const bump = (step, buy, sell, vol) => {
      if (!Number.isFinite(step)) return       // stepOf(NaN) for a non-positive price
      let e = profile.get(step)
      if (!e) { e = [0, 0, 0]; profile.set(step, e) }
      e[0] += buy
      e[1] += sell
      e[2] += vol
      if (e[2] > maxVol) maxVol = e[2]
      if (e[0] > maxSide) maxSide = e[0]
      if (e[1] > maxSide) maxSide = e[1]
    }
    for (const candle of visibleCandles) {
      if (!candle.isHistorical || candle._omFootprint) {
        // _omFootprint history has REAL per-level data — profile it exactly
        // like live candles instead of the flat historical smear below.
        // null,null = lattice-keyed miss → skip any stale _display; bin from
        // native/live at the grid ratio only (no every-Nth from coarse cache).
        // (perf3) Packed candles: cached binning, same order and values.
        const lv = vpBinnedLevels(candle, ratio, bucketSize)
        if (lv) {
          for (let i = 0; i < lv.length; i += 3) bump(lv[i], lv[i + 1], lv[i + 2], lv[i + 1] + lv[i + 2])
        } else {
          forEachLevel(candle, (mcap, buy, sell) => {
            bump(stepOf(mcap), buy, sell, buy + sell)
          }, null, null)
        }
      } else if (candle.volume_tokens > 0 && candle.high_mcap != null && candle.low_mcap != null) {
        // POISON GUARD (integer form): one garbage historical candle with an
        // absurd high/low span must not detonate the loop/Map. No real candle
        // needs more synthetic levels than the lattice can display; poisoned
        // candles get their volume attributed to a single level at the close.
        const loStep = stepOf(candle.low_mcap)
        const hiStep = stepOf(candle.high_mcap)
        const bucketCount = hiStep - loStep + 1
        if (!Number.isFinite(bucketCount) || bucketCount < 1 || bucketCount > 4096) {
          const anchor = candle.close_mcap ?? candle.low_mcap
          if (Number.isFinite(anchor) && anchor > 0) {
            bump(stepOf(anchor), 0, 0, candle.volume_tokens)
          }
          continue
        }
        const volPerBucket = candle.volume_tokens / bucketCount
        for (let s = loStep; s <= hiStep; s++) {
          bump(s, 0, 0, volPerBucket)
        }
      }
    }
    return { profile, maxVol, maxSide, bucket: bucketSize, ratio }
  }, [visibleCandles, vpBucket, logScale, mcapRatio, vpGridRatio, vpGridStep])

  // ─── Absorption detection ───
  // Keyed by CANDLE OBJECT, not index: the draw loop already holds the candle
  // it is painting, and an index would desync the instant the visible window
  // shifts between this memo running and the next paint.
  //
  // Skips candles with no buy/sell split (plain historical). Their volume is
  // smeared evenly across the high-low range by the profile builder, so a
  // "level" there is an artefact of that smear, not a place trades happened.
  // Marking one would be a claim the data cannot support.
  const absorptionCells = useMemo(() => {
    if (!showAbsorption) return null
    const cells = new Map()
    // Median of the visible cell population, capped so a very wide view can't
    // turn one memo into a 100k-element sort.
    const vols = []
    const MAX_SAMPLE = 20000
    for (const candle of visibleCandles) {
      if (candle.isHistorical && !candle._omFootprint) continue
      forEachLevel(candle, (m, buy, sell) => {
        const t = buy + sell
        if (t > 0 && vols.length < MAX_SAMPLE) vols.push(t)
      })
    }
    if (vols.length < 8) return cells
    vols.sort((a, b) => a - b)
    const median = vols[vols.length >> 1] || 0
    if (!(median > 0)) return cells
    const minVol = median * (absConfig.minMult || 3)
    // liq-repaint-20261023: a closed candle's cell is picked ONCE and kept by
    // startTime. The pick uses the visible-window median, so without this the
    // past cells (and their Liq labels) moved on every zoom/scroll/new candle.
    const cc = absCellCacheRef.current
    const ck = `${data.currentToken || ''}|${timeInterval}|${absConfig.minMult || 3}`
    if (cc.key !== ck) { cc.key = ck; cc.map = new Map() }
    const _allC = chartData.candles || []
    const _last = _allC[_allC.length - 1]

    for (const candle of visibleCandles) {
      if (candle.isHistorical && !candle._omFootprint) continue
      const forming = candle === _last && !candle.isHistorical
      if (!forming && cc.map.has(candle.startTime)) {
        const k = cc.map.get(candle.startTime)
        if (k) cells.set(candle, { mcap: k.mcap, side: k.side, liq: null })
        continue
      }
      const close = candle.close_mcap
      let hi = candle.high_mcap, lo = candle.low_mcap
      if (!Number.isFinite(hi) || !Number.isFinite(lo)) {
        hi = -Infinity; lo = Infinity
        forEachLevel(candle, (m, buy, sell) => {
          if (buy + sell === 0) return
          if (m > hi) hi = m
          if (m < lo) lo = m
        })
      }
      const range = hi - lo
      if (!Number.isFinite(close) || !Number.isFinite(range) || range <= 0) continue

      let bestScore = 0, bestMcap = null, bestSide = null
      forEachLevel(candle, (m, buy, sell) => {
        // Rejection measured from the level to the CLOSE: the bar's verdict on
        // whether that level held. Using high/low instead would mark every
        // extreme regardless of whether it was defended.
        if (sell >= minVol && close > m) {
          const sc = sell * ((close - m) / range)
          if (sc > bestScore) { bestScore = sc; bestMcap = m; bestSide = 'bid' }
        }
        if (buy >= minVol && close < m) {
          const sc = buy * ((m - close) / range)
          if (sc > bestScore) { bestScore = sc; bestMcap = m; bestSide = 'ask' }
        }
      })
      if (bestMcap != null) cells.set(candle, { mcap: bestMcap, side: bestSide, liq: null })
      if (!forming) cc.map.set(candle.startTime, bestMcap != null ? { mcap: bestMcap, side: bestSide } : null)
    }
    // abs-liq-20261017: liquidity reaction per cell, cached by candle time.
    // absorb-liq4: 'Liq' toggle off -> no computation at all.
    // Final results (hit or choppy) are computed once; pending / not-covered
    // cells (undefined) are retried on the next memo run.
    if (absConfig.showLiq !== false) try {
      const lc = absLiqCacheRef.current
      const lk = `v2|${data.currentToken || ''}|${timeInterval}`
      if (lc.key !== lk) { lc.key = lk; lc.map = new Map(); lc.emitted = new Set() }
      const buf = data.rawTradesRef?.current
      const all = chartData.candles
      const base = viewportInfo.startIdx || 0
      for (let vi = 0; vi < visibleCandles.length; vi++) {
        const candle = visibleCandles[vi]
        const cell = cells.get(candle)
        if (!cell) continue
        const hit = lc.map.get(candle.startTime)
        if (hit && hit.mcap === cell.mcap) { cell.liq = hit.res; continue }
        const idx = all[base + vi] === candle ? base + vi : all.indexOf(candle)
        if (idx < 0) continue
        const res = absLiqReaction(buf, all, idx, cell.mcap, timeIntervalRef?.current || timeInterval || 60)
        if (res === undefined) continue
        // absorb-liq7: the forming candle is never cached (its row's last print can still move)
        if (!(idx === all.length - 1 && !candle.isHistorical)) lc.map.set(candle.startTime, { mcap: cell.mcap, res })
        cell.liq = res
        // liq3s-anytf-20261019: a freshly resolved Liq label drives the armed
        // BUY/SELL, on any TF. Once per cell; WalletHoldings ignores labels
        // whose reaction print predates the arm (history loads, cached cells).
        if (res && res.at != null) {
          const ek = `${candle.startTime}|${cell.mcap}`
          if (!lc.emitted) lc.emitted = new Set()
          if (!lc.emitted.has(ek)) {
            lc.emitted.add(ek)
            emitLiqReaction({ dir: res.dir, sec: res.sec, at: res.at, now: data.serverNowRef?.current?.() ?? Date.now(), token: data.currentToken })
          }
        }
      }
    } catch {}
    return cells
  }, [showAbsorption, visibleCandles, absConfig.minMult, absConfig.showLiq, data.currentToken, timeInterval])
  useEffect(() => { absorptionRef.current = absorptionCells }, [absorptionCells])
  // absorb-zones-20261019: strong past absorption levels as zones (drawing only).
  // Recomputed only when a candle is added / history changes, not per tick.
  const _azC = chartData.candles
  const absZonesKeepRef = useRef(null)   // abszone-keep-20261031
  const absZones = useMemo(() => {
    if (!showAbsorption || !absConfig.zones) return null
    // absorb-zone-load-20261023: not while history is still loading; the
    // partial set gave zones from candle 0 to the edge until a toggle.
    // abszone-keep-20261031: keep last zones during a background history load (was null -> all zones vanished at once).
    const _azK = (data.currentToken || '') + '|' + timeInterval
    if (absZonesKeepRef.current?.k !== _azK) absZonesKeepRef.current = { k: _azK, z: null }
    if (data.historyPending) return absZonesKeepRef.current.z
    try { return (absZonesKeepRef.current.z = computeAbsorbZones(_azC, forEachLevel, absConfig.minMult)) } catch { return absZonesKeepRef.current.z }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAbsorption, absConfig.zones, absConfig.minMult, _azC?.length, _azC?.[0]?.startTime, chartData.structuralVersion, data.currentToken, timeInterval, data.historyPending, _azC?.[(_azC?.length || 0) - 2]?.startTime])
  const absZonesRef = useRef(null)
  absZonesRef.current = absZones

  // Identity-stable read for drawCanvas (same pattern as chartDataRef):
  // the VP paint lives inside the draw pass and must not add deps.
  // ─── Cell paint batching (full pass only) ───
  // The profile's verdict on a dense TF switch: fillRect 1.49s self,
  // drawImage 610ms, getTextSprite 230ms — raw per-cell painting, with React
  // a bystander. The canvas cost is not the rects, it is the STATE CHANGES
  // between them: fillStyle alternates buy/sell color per half-cell, which
  // defeats every batching layer the browser has. The palette, though, is
  // tiny — colors come from two 256-entry LUTs — so bucketing rects by color
  // and issuing ONE fill() per color turns thousands of styled ops into
  // dozens. Same pixels by construction: cell rects never overlap each
  // other, so paint order between fills is irrelevant, and text draws after
  // ALL fills instead of after ITS fill, which is identical because a
  // sprite only ever sits over its own cell.
  //
  // Live pass keeps the immediate path: animated cells need their transform
  // (they cannot batch), and the live candle is a few dozen cells at most.
  // Pooled in refs — Maps and arrays are reused across frames, cleared by
  // length=0, so steady-state adds zero GC pressure (Minor GC was already
  // showing up in the profile).
  const cellFillBatchRef = useRef(new Map())    // color -> flat [x,y,w,h,...]
  // Slot plates only (under trail). Same shape as fill batch; flushed to plateLayer.
  const cellPlateBatchRef = useRef(new Map())   // color -> flat [x,y,w,h,...]
  // ─── Wallet overlay (derived, cached) ───
  // Rebuilt from the trade buffer when (wallet, display bucket, buffer
  // length) changes, throttled to 200ms so a busy tape doesn't rebuild per
  // trade. Nothing here is stored on candles — see buildWalletOverlay.
  const walletOverlayRef = useRef({ key: '', map: null, at: 0, len: 0 })
  useEffect(() => {
    const off = onChartWalletChange(() => {
      walletOverlayRef.current = { key: '', map: null, at: 0, len: 0 }
      if (drawCanvasRef.current) drawCanvasRef.current()
    })
    const onKey = (e) => { if (e.key === 'Escape' && getChartWallet()) selectChartWallet(null) }
    window.addEventListener('keydown', onKey)
    return () => { off(); window.removeEventListener('keydown', onKey) }
  }, [])

  const cellSpriteBatchRef = useRef([])         // flat [sprite,bx,by,...]
  const cellStrokeBatchRef = useRef([])         // absorption outlines
  // Candle-mode wicks/bodies, batched so the pan replay below is complete.
  // Stride 9: [wxH, yHigh, yLow, wickCol, bodyX, bodyTop, bodyW, bodyH, bodyColor]
  const cellWickBatchRef   = useRef([])
  // ─── Pan geometry cache ───
  // The cell loop's OUTPUT is the batches — that is the geometry. On a
  // drag frame where only the pan offset moved, the batches from the last
  // full draw are still correct up to a horizontal translation, so they're
  // replayed with x += dx and the loop is skipped entirely: no forEachLevel,
  // no mcapToRow, no formatCellVol, no sprite lookups, no luminance math.
  // The same idea as WebGL's matrix transform, without a new renderer.
  //
  // Correctness rests on two things. The KEY must cover every input the
  // loop reads except panOffsetPx; it is checked only mid-drag, and a drag
  // freezes zoom/scroll/theme by construction, so the inputs that can
  // change under it are exactly structuralVersion (a candle closing) and
  // the wallet overlay (a trade landing) — both in the key. And the frame
  // that gets cached is drawn with ONE candle of overdraw each side, so a
  // shift of up to cw (panOffsetPx is bounded to [0, cw) by the drag
  // handler) never exposes a blank column at the edge.
  const panGeomRef = useRef({ key: '', pan: 0, fill: null, sprite: null, stroke: null, wick: null, overlay: null, badges: null })
  const volumeProfileRef = useRef(volumeProfile)
  volumeProfileRef.current = volumeProfile

  // Value Area (POC / VAH / VAL) over the visible-range volume profile. Only
  // computed while the overlay is on. Recomputes as the profile (scroll/zoom)
  // or the VA% changes; synced to a ref for the identity-stable draw.
  const valueArea = useMemo(
    () => (showValueArea ? computeValueArea(volumeProfile.profile, volumeProfile.bucket, vaConfig.vaPct, volumeProfile.ratio) : null),
    [showValueArea, volumeProfile, vaConfig.vaPct],
  )
  valueAreaRef.current = valueArea

  const maxTotalVolume = useMemo(() => {
    let max = 0
    for (const candle of visibleCandles) {
      const totalVol = candleTotalVolume(candle)
      if (totalVol > max) max = totalVol
    }
    return max
  }, [visibleCandles])

  const filteredTrades = useMemo(() => {
    if (tradeFilterMin <= 0) return data.recentTrades
    return data.recentTrades.filter(t => t.amount >= tradeFilterMin)
  }, [data.recentTrades, tradeFilterMin])

  // maxTradeAmount moved to TapePanel (only the trade rows used it).

  // Grouped header/footer cells — merge when pxPerCandle is too narrow for
  // text. Pure logic lives in utils/chartGeometry.js (buildGroupedCells).
  // Same column range as the footprint (one extra each side); see there.
  const groupedCells = useMemo(() => buildGroupedCells({
    candles: chartData.candles,
    startIdx: viewportInfo.startIdx,
    endIdx: viewportInfo.endIdx,
    pxPerCandle: viewportInfo.pxPerCandle,
    timeInterval,
  }), [chartData.candles, viewportInfo.startIdx, viewportInfo.endIdx, viewportInfo.pxPerCandle, timeInterval])
  // New cells land on commit; place them against the frame on screen before
  // paint. drawCanvas re-places on every full pass after that.
  useLayoutEffect(() => { placeEdgeRows(lastFrameRef.current) })

  // ============================================
  // Canvas draw function
  // ============================================


  const drawCanvas = useCallback((target) => {
    // target: undefined/'full' = the structural pass (everything except the
    // forming candle); 'live' = the forming candle + its animations ONLY,
    // onto the dedicated live canvas. Exclusivity is enforced at liveIdx.
    const liveOnly = target === 'live'
    const canvas = liveOnly ? liveLayerRef.current : canvasRef.current
    if (!canvas) return
    // Freeze-frame during a token switch: skip the draw so the canvas keeps the
    // OUTGOING token's last painted pixels. The blurred overlay sits on top;
    // when the switch disarms, the ref-sync above has already flipped false, so
    // the very next draw-trigger repaints the new token under the lifting blur.
    if (isSwitchingRef.current) return
    fdCount(liveOnly ? 'live' : 'full')   // (perf4) debug counter
    // Nudge guard tracks FULL draws only: a live frame repaints one candle,
    // and must not convince the nudge that a toggle's full repaint happened.
    if (!liveOnly) lastDrawTsRef.current = Date.now()
    // Read latest data from the ref (see chartDataRef above) so this callback
    // doesn't depend on chartData identity and stays stable across ticks.
    const chartData = chartDataRef.current
    const candles = chartData.candles
    const committedX = committedXRef.current
    if (liveOnly && committedX && committedX.candles && committedX.candles !== candles) return

    // Apply sub-bucket pixel residual (canvas + y-axis-body) in lockstep
    // with the pixel repaint below. Doing this in a separate effect would
    // open a 1-paint window where the transform is new but the canvas
    // pixels are stale — visible as a flash on bucket-boundary crosses.
    //
    // The same px value is also published as a CSS variable on the
    // footprint-body-wrapper so overlay layers (alert lines, position
    // tags, limit-order tags) can ride the same transform via CSS without
    // each needing its own ref/imperative assignment. Anything new that
    // should track bucket positions just adds itself to the CSS rule.
    const containerW = containerWidthRef.current
    // Grid selection. FULL draws take the newest grid and commit it; LIVE
    // frames reuse what was committed, so the forming candle is always
    // mapped onto the same rows as the pixels around it.
    //
    // Why this shows up in FOLLOW mode specifically: the centre glides every
    // frame under FOLLOW_DAMP_K, so rangeMax re-snaps continuously and the
    // grid (and its sub-bucket residual) is different on almost every frame.
    // On a fast token live ticks far outnumber full draws, so most frames
    // were drawing the live candle on a grid the rest of the chart had not
    // been repainted onto yet. At rest the grid is stable and nothing moves,
    // which is why it only appears on recentre.
    const committed = committedGridRef.current
    const useCommitted = liveOnly && committed !== null
    let sortedMcaps = useCommitted ? committed.mcaps    : visibleSortedMcapsRef.current
    let RH          = useCommitted ? committed.RH       : dynamicRowHeightRef.current
    const residualPx  = useCommitted ? committed.residual : yResidualPxRef.current
    // NOT published here: the zoom-freeze block below reassigns sortedMcaps
    // and RH to the FROM lattice. Publish is `frame`, after proj.

    // (yaudit) Dead zoomPaint/Freeze Y-camera transform removed (never armed).
    // Y framing mid X-wheel = cone → glideHalf/Centre → latticeGeomFor remint.
    // cameraScale/ty stay identity; X sub-candle stretch is cameraScaleX below.
    let cameraScale = 1, cameraTy = 0, cameraActive = false
    // Live candle stays solid on phone pinch/footer because it never uses
    // shared GL. History must match for the whole X-zoom gesture (not only
    // while cameraActive) — Android still composites WebGL over 2D.
    // Camera is final here — publish before anything can read it. drawCanvas
    // runs at FRAME_DRAW and the layers at FRAME_LAYER, so a layer paint
    // always sees this frame's value rather than last frame's.
    // X ease camera: FROM freeze + live window → scaleX about ax (follow = right edge).
    // WHEEL EASE ONLY — drag is 1:1 panOffset + geom replay (no second camera).
    // Computed here (before grid/main) so every paint surface shares one transform.
    // X CAMERA — glide only, sub-candle. The glide eases W as a float, but
    // the window the paint uses is a whole candle count (the slot rounds), so
    // column width plotW/W moved in whole-candle STEPS (~3% a step near 30
    // candles). During a glide the window is committed as ceil(W) and this
    // stretches the drawn mesh by ceil(W)/W about the plot's RIGHT edge (the
    // window's anchor — endIdx/live edge stays put, candles enter at the
    // left), so on-screen column width is exactly plotW/W, continuous.
    // ceil → scale ≥ 1: the extra part clips off the left, never a gap.
    // At landing W is whole, the scale is exactly 1 and this switches off.
    // The whole-candle side of the ratio MUST be the window this frame's
    // mesh is actually painted with — the same pick as winSizeF below
    // (committed X for a live-only pass, else the lattice's own window).
    // The slot ref moves the instant a glide frame commits, a render before
    // the mesh does; using it stretched the wrong mesh for a frame (jumpy,
    // over-sensitive) and gave the live-only pass a different scale than the
    // full pass (live candle lagging the rest).
    const _xg = zoomGlideRef.current
    const _cX = committedXRef.current
    const _xwInt = +((liveOnly && _cX && (!_cX.candles || _cX.candles === candles))
      ? _cX.winSizeF
      : (Number.isFinite(sortedMcaps?.winSizeF)
          ? sortedMcaps.winSizeF
          : ((xWin && typeof xWin.snap === 'function') ? xWin.snap().win : viewWindowSizeRef.current))) || 0
    const _xwF = _xg ? +_xg.win : 0
    let xCameraActive = !!(_xg && _xwF > 0 && _xwInt > 0 && Math.abs(_xwInt - _xwF) > 1e-4)
    let cameraScaleX = xCameraActive ? _xwInt / _xwF : 1
    // Body-wheel glide stretches about the cursor (anchorX). Pinch, footer,
    // and every other glide keep the plot's right edge.
    let cameraAx = 0
    if (xCameraActive) {
      const plotRight = Math.max(1, (containerWidthRef.current || 0) - RIGHT_PADDING - FIXED_OVERLAY_WIDTH)
      const axA = (_xg && _xg.wheelAnchor && Number.isFinite(_xg.anchorX)) ? _xg.anchorX : plotRight
      // Wall frames (anchor solution clamped at the soft left edge — oldest
      // candle at the right edge — or at the forward wall): X stays on the
      // integer step. When the price range moves with the width (diag),
      // stretch about the plot's RIGHT edge on the same float the Y cone
      // samples. A cursor pivot on a clamped frame is what bounced.
      const _nEdge = candles?.length || 0
      const onForwardWall = !!(_xg && _xg.wheelAnchor && !_xg.pastRight
        && Number.isFinite(_xg.placedEnd) && _nEdge > 0
        && _xg.placedEnd >= _nEdge - 1 + FUTURE_PADDING - 1e-6)
      const onStartWall = !!(_xg && _xg.wheelAnchor && _xg.hitStart)
      if (_xg && _xg.wheelAnchor && (onStartWall || onForwardWall)) {
        if (_xg.diag && _xwF > 0 && _xwInt > 0) {
          xCameraActive = true
          cameraScaleX = _xwInt / _xwF
          cameraAx = plotRight
        } else {
          xCameraActive = false
          cameraScaleX = 1
          cameraAx = 0
        }
      } else {
        cameraAx = Math.max(0, Math.min(plotRight, axA))
      }
    }
    // ONE stretch per mesh. The main canvas is only re-stretched when a FULL
    // pass repaints it; a live-only pass in between computed the stretch from
    // the glide's CURRENT width, so the live candle + its cells ran ahead of
    // the (not yet repainted) rest. Live-only passes now reuse the stretch the
    // last full pass painted with; full passes publish theirs.
    if (liveOnly) {
      const f = xCamFullRef.current
      if (_xg) { xCameraActive = f.active; cameraScaleX = f.scale; cameraAx = f.ax }
      else { xCameraActive = false; cameraScaleX = 1; cameraAx = 0 }
    } else {
      xCamFullRef.current = { active: xCameraActive, scale: cameraScaleX, ax: cameraAx }
    }
    cameraRef.current.scale = cameraScale
    cameraRef.current.ty = cameraTy
    cameraRef.current.active = cameraActive
    cameraRef.current.scaleX = cameraScaleX
    cameraRef.current.ax = cameraAx

    // while cameraActive) — Android still composites WebGL over 2D. X window
    // / days-on-screen is unchanged; only the paint path.
    // Footer drag forces 2D only on PHONES (the Android WebGL stacking issue
    // these 2D paths exist for). On desktop the footer stays on GL: switching
    // renderers the moment the footer was pressed is what shifted the wicks
    // on click, and the 2D cells drifted off the GL-free layers mid-drag. GL
    // batches get the X camera applied in JS (_xcamBatch) instead.
    const xZoomGesture = !!(cameraActive || pinchActiveRef.current || (footerDragRef.current && isPhoneDevice()))
    if (!liveOnly) {
      // Derived id — not independent: same array.latticeId across idle frames
      // that return the cached prev, with :cN marking each committed write.
      const arrId = sortedMcaps?.latticeId
      committedGridRef.current = {
        mcaps: sortedMcaps,
        RH,
        residual: yResidualPxRef.current,
        // Stamped by the grid memo for the current grid geometry.
        halfRange: Number.isFinite(sortedMcaps?.halfRange) ? sortedMcaps.halfRange : null,
        latticeId: arrId != null ? `${arrId}:c${++commitSeqRef.current}` : null,
      }
    }
    const totalHeight = sortedMcaps.length * RH
    // (y1) The six lattice-height layers follow the PAINTED lattice. Same
    // number the JSX used (rows x RH), from this pass's sortedMcaps / RH.
    if (!liveOnly) writeLayerHeights(`${totalHeight}px`)
    // ── ONE PAINT SURFACE. Never swapped. ────────────────────────────────
    // The three paint surfaces (main canvas, grid layer, volume lane) each
    // used `cameraActive ? containerHeight : totalHeight`, so every camera
    // arm and every land resized all three. cssW tracks containerW and only
    // moves on a real resize; cssH tracked paintH and moved on every
    // recentre — which is why a profile showed `set height` at 1,059ms /
    // 14.2% against `set width` at 31.5ms. The caching was working; the
    // value really was changing that often. That is the "it adjusts at the
    // end" you can see: the land flipping the surface back.
    //
    // It was also wrong, not just expensive. The camera maps content into
    // [0, H_fit], and H_fit = fromNativeH + (containerHeight - fromNativeH)*p
    // — so at the START of a glide H_fit is the FROM lattice's native
    // height, which when zoomed in EXCEEDS containerHeight. A viewport-tall
    // surface clipped the bottom of the lattice. That is the "doesn't size
    // to the correct height when zoomed in".
    //
    // ── THE SURFACE IS THE VIEWPORT. ONE AUTHORITY, NO RATCHET. ─────────
    // surfaceH was max(containerHCss, totalHCss) behind a 3-branch ratchet.
    // That made the BITMAP track the LATTICE — and the two are the same
    // quantity: the grid memo sizes rows continuously as
    // RH = H·B / (2·half + 2·B), so visCount × RH reduces to the viewport
    // height. totalHeight differs from containerHeight only by the ceil'd
    // overhang (clipped by overflow:hidden anyway) and the MIN_ROW_PX floor.
    // That is ROUNDING NOISE on a value that is mathematically H.
    //
    // So the ratchet was damping noise in a number that should never have
    // moved. A [GLRESIZE] probe caught it flipping 1234↔1235↔1239↔1242 —
    // ~30 reallocations in 300 calls, each one a ~5MB drawing buffer AND an
    // implicit canvas clear. A clear landing mid-draw wipes everything
    // painted before it: that is the one-frame split where half the
    // footprint rendered differently from the other half.
    //
    // TWO SIZING AUTHORITIES WERE FIGHTING. The bitmap grew with the
    // lattice, and the camera ALSO mapped the lattice into H_fit. Both
    // half-applied. The camera is the glide (p: FROM → DEST) and is identity
    // at rest — it is not a second sizer. Giving it the job outright removes
    // the conflict rather than arbitrating it.
    //
    // MIN_ROW_PX coarsens the lattice; it does not license a multi-kpx
    // bitmap. There is no "below the fold" mode here — a lattice taller than
    // the viewport is the sizer leaking, not a product state.
    //
    // Consequences, accepted deliberately:
    //   · fromNativeH > H at p≈0 clips or letterboxes inside the viewport.
    //     If that framing is wrong, fix the CAMERA, not the surface.
    //   · Bitmap size now changes only on a real window resize.
    //   · The phone-OOM note below is moot: this is the smallest the surface
    //     has ever been, not the largest.
    const surfaceH = Math.max(1, Math.round((containerHeightRef.current || containerHeight) + SURFACE_PAD_PX))
    const containerWCss = Math.max(1, Math.round(containerW))

    // Transform (sub-bucket y-residual + live y-drag). Only write when the
    // pixel offset actually changed — re-assigning an identical transform and
    // CSS var every frame forces a full-subtree reflow before paint.
    {
      // pinPx: PINNED-layer translate (never add scrollTop).
      // regionPx: IN-FLOW translate = pinPx + scrollTop (cancels physical scroll).
      const px = -residualPx  // == pinPx
      const pinPx = px
      // MEASURED (alignment probe, two rounds): the CANVAS is the pinned
      // element — position:absolute against the body-WRAPPER (the scroller
      // is position:static, so it isn't the containing block) — and the
      // y-axis region is in-flow INSIDE the scroller, so it physically
      // scrolls while the canvas never moves. Probe arithmetic: drift was
      // exactly -scr with transform px (region slid, canvas didn't) and
      // exactly -2*scr with px - scr (first fix pushed it further). So the
      // region transform must ADD scrollTop, canceling its physical scroll
      // against the pinned canvas. Corollary for future cleanup: vertical
      // scrollTop has NEVER moved the canvas — the auto-scroll-to-highlight
      // effect's only visible result was sliding the axis out of alignment;
      // the machinery is vestigial. The y-axis-body scrollTop syncs are
      // separate no-ops (overflow:visible cannot scroll).
      // Cached, not read from the DOM. `el.scrollTop` is a layout read, and
      // the five transform writes immediately below dirty layout again — so
      // reading it here made every draw force a synchronous reflow off the
      // previous draw's writes. The profile named this exact line for both
      // `Recalculate style` and `Layout`. The cache is written by every
      // scrollTop writer in useChartScroll in the same statement as the DOM
      // write, so it is never behind — see the note on its declaration.
      //
      // NOT the same call as the y-axis-body scrollTop that was deleted from
      // useChartInteraction: that element is overflow:visible and provably
      // cannot scroll, so its read added zero. THIS one is load-bearing
      // whenever the body actually scrolls (the -scr / -2*scr drift probe
      // above is what established it), so it is cached rather than removed.
      //
      // `scroll` is deliberately NOT in drawCanvas's dependency array, and a
      // lint autofix that adds it would be a real regression: useChartScroll
      // returns a fresh object literal every render, so drawCanvas would get
      // a new identity every render, and the useLayoutEffect below that lists
      // drawCanvas as a dep would fire a full synchronous redraw on every
      // render. Omitting it is safe because bodyScrollTopRef is a useRef —
      // the same object on every render, so even a stale `scroll` closure
      // reads the live value.
      const scr = scroll.bodyScrollTopRef.current || 0
      const regionPx = px + scr
      // Phone Y-axis drag: keep the axis GUTTER planted. Mid-zoom residual /
      // liveYDrag would otherwise rewrite translateY every frame and the whole
      // y layer slides with the finger (the "Y bounce" feel). Cancel scroll
      // only; resume full regionPx when the drag ends.
      const axisSolid = !!yAxisZoomDragActiveRef.current
      const axisPx = axisSolid ? (scr - residualPx) : regionPx
      if (
        px !== lastStyleRef.current.px
        || regionPx !== lastStyleRef.current.regionPx
        || axisSolid !== lastStyleRef.current.axisSolid
      ) {
        canvas.style.transform = `translateY(${px}px)`
        // Grid background rides the SAME px as the canvas (it's pinned the same
        // way), so the dashed lines stay locked to the cell boundaries.
        if (gridLayerRef.current) gridLayerRef.current.style.transform = `translateY(${px}px)`
        if (plateLayerRef.current) plateLayerRef.current.style.transform = `translateY(${px}px)`
        // cellGl transform set below (may include Y-camera scale).
        if (liveLayerRef.current) liveLayerRef.current.style.transform = `translateY(${px}px)`
        if (zoneLabelLayerRef.current) zoneLabelLayerRef.current.style.transform = `translateY(${px}px)`   // hold-label-front-20261016
        if (zAboveLayerRef.current) zAboveLayerRef.current.style.transform = `translateY(${px}px)`   // zindex-20261102
        if (zBelowLayerRef.current) zBelowLayerRef.current.style.transform = `translateY(${px}px)`
        const tRegion = `translateY(${axisPx}px)`
        if (yAxisRef.current)              yAxisRef.current.style.transform              = tRegion
        // The PROFILE rides `px`, not tRegion. regionPx adds scrollTop to
        // cancel the physical scroll of in-flow elements (the y-axis region
        // is one). But this canvas is position:absolute against the
        // body-WRAPPER — pinned exactly like the main canvas, it never
        // physically scrolls — so adding scrollTop over-corrected it by
        // exactly `scr` and slid the profile vertically against the bars
        // whenever the body was scrolled. Same probe arithmetic as the
        // comment above, mirrored: pinned elements take px.
        if (volumeProfileLayerRef.current) volumeProfileLayerRef.current.style.transform = `translateY(${px}px)`
        // (perf2) IN-FLOW layers (alert / position / limit tags, magnifier
        // marker) take regionPx (= pinPx + scrollTop) as a DIRECT inline
        // transform. It used to be the --chart-y-residual custom property on
        // .footprint-body-wrapper: an inherited var changing every glide frame
        // restyled the wrapper's whole subtree. Same value, same consumers;
        // the chip adds it in its own block below. Pinned layers still read
        // lastStyleRef.pinPx.
        const tTags = yTagTf(regionPx)
        const _tagLayers = [alertLayerElRef.current, posTagsLayerElRef.current,
          limitTagsLayerElRef.current, magLayerElRef.current]
        for (const el of _tagLayers) if (el) writeTf(el, tTags)
        lastStyleRef.current.px = pinPx       // legacy alias
        lastStyleRef.current.pinPx = pinPx     // SoT name for pinned consumers
        lastStyleRef.current.regionPx = regionPx
        lastStyleRef.current.axisSolid = axisSolid
      }
      // Scroll for the y-axis fade strips: pseudo-elements of .y-axis-region,
      // whose BOX physically scrolls (its children counter-translate via
      // regionPx, but pseudo-elements pin to the box itself). The strips
      // cancel exactly the scroll — not regionPx, which also carries the
      // residual. (perf2) Scoped to the region itself (was the wrapper) and
      // written only when the scroll actually changes.
      {
        const regionEl = yAxisRef.current?.parentElement
        if (regionEl && regionEl.__yScr !== scr) {
          regionEl.style.setProperty('--chart-y-scroll', `${scr}px`)
          regionEl.__yScr = scr
        }
      }
    }

    // Never CSS-scale WebGL (Android blanks it). During pinch/footer/wheel
    // X-zoom, match the live layer: pure 2D, shared GL out of the stack
    // (visibility:hidden — opacity alone still let Android composite it).
    //
    // WRITE-ON-CHANGE, same discipline as the transform block above and for a
    // sharper reason. Those five properties were re-assigned on EVERY draw,
    // and opacity / visibility / z-index are LAYER-INDUCING: touching them
    // makes the compositor redo layer assignment even when the value is
    // identical. A 35s profile put `Layerize` at 2,690ms — 23.9% of the
    // trace, second only to the layout-shift synthetic, and roughly SIX TIMES
    // the ~423ms spent in every 2D raster primitive combined (fillRect 126.6,
    // drawImage 187.4, stroke 37.5, fill 26.3, rect 25.1, clearRect 20.0).
    // Nothing about the drawing was ever the cost; the per-frame style
    // invalidation was.
    const _ls = lastStyleRef.current
    {
      const chartPx = -residualPx
      const glEl = cellGlCanvasRef.current
      if (glEl) {
        const tf = `translateY(${chartPx}px)`
        if (tf !== _ls.glTf) {
          glEl.style.transformOrigin = ''
          glEl.style.transform = tf
          _ls.glTf = tf
        }
        // Never visibility:hidden. The original reason was that heat lived
        // on this canvas and hiding it blanked heat on scroll; heat has since
        // moved back to LiquidityHeatLayer's own canvas (see the shared-scene
        // skip in the cell flush), so this layer is genuinely EMPTY during
        // xZoomGesture — beginFrame still clears it, but every draw call is
        // gated on !xZoomGesture. It may well be hideable now, and that would
        // save a full-size empty composite on exactly the frames with the
        // least budget. This note is the only record of why it wasn't, so it
        // needs a real Android retest before anyone flips it, not a reading
        // of this comment.
        //
        // These two clear inline overrides that nothing sets any more. Once
        // cleared they stay cleared, so after the first draw this is a no-op
        // that used to cost a relayerize per frame.
        if (_ls.glOp !== '') { glEl.style.opacity = ''; _ls.glOp = '' }
        if (_ls.glVis !== '') { glEl.style.visibility = ''; _ls.glVis = '' }
      }
      if (!liveOnly && canvas.style) {
        // Raise history above GL during X-zoom so Android WebGL stacking
        // cannot cover 2D cells; heat still shows in transparent GL gaps.
        //
        // '' AND NOT '2.5'. z-index takes `auto | <integer>` — 2.5 is invalid,
        // so CSSOM rejected it and the assignment silently did nothing. On
        // load that was harmless (the canvas sat at z-index auto, ordered by
        // DOM position, which is what ships today). But it also meant the
        // raise could never be UNDONE: '4' applied, then the '2.5' meant to
        // restore it was rejected and the canvas stayed at 4 — above the live
        // layer's z3 — for the rest of the session after the very first wheel
        // or pinch. '' removes the property, which reproduces the load-time
        // stacking exactly and makes the toggle reversible.
        //
        // NOTE the JSX still declares zIndex: 2.5 on this canvas and it is
        // rejected there the same way, so the intended GL(2) < main < live(3)
        // order has never actually held. Fixing that needs the whole stack
        // re-spaced (there is no integer between 2 and 3) plus an Android
        // retest of the raise, so it is deliberately NOT bundled here.
        const z = xZoomGesture ? '4' : ''
        if (z !== _ls.zIdx) { canvas.style.zIndex = z; _ls.zIdx = z }
      }
    }

    // ── Grid lines on the dedicated grid canvas (z0, behind the trail) ──
    // Crisp 1px dashed lines on every 3rd row boundary, matching the old
    // (rowIdx+1)*RH placement. Pixel-snapped (Math.round+0.5) so 1px stays 1px
    // even at fractional RH — the reason this is a canvas and not a CSS
    // background. The element's translateY(px) (set in the transform block
    // above) keeps it phase-locked to the cells, so we draw in UNTRANSFORMED
    // content space here: y = (rowIdx+1)*RH, same coordinate space as the main
    // canvas's cells.
    if (!liveOnly) {
      const gridCanvas = gridLayerRef.current
      if (gridCanvas && RH > 0) {
        const gdpr = window.devicePixelRatio || 1
        // plotWidth is declared later in drawCanvas (TDZ if referenced here);
        // recompute from the same expression. Matches line ~3185 exactly.
        const gridPlotWidth = Math.max(50, containerW - RIGHT_PADDING - FIXED_OVERLAY_WIDTH)
        const gw = Math.max(1, gridPlotWidth)
        // Viewport-sized while Y-camera (same OOM guard as main canvas).
        const gh = surfaceH
        // SIGNATURE SKIP — every input the stroke below reads. Identical sig
        // ⇒ identical bitmap ⇒ skip the full-surface clear + dashed stroke.
        // During animation-pumped frames (60fps while a cell flips) none of
        // these change, so the grid costs zero there; any real change (RH
        // drift on a tick, resize, zoom, dpr) redraws as before. Safe
        // because the resize assignments below (the only thing that wipes
        // the bitmap) only run when the sig has changed too.
        const gridSig = `${gdpr}|${gw}|${gh}|${RH}|${sortedMcaps.length}`
        // sizedOk guards element remounts: a FRESH canvas node arrives blank
        // at the default 300×150 — if the sig happened to match the old
        // node's, the skip would leave it blank forever.
        const sizedOk = gridCanvas.width === Math.round(gw * gdpr) && gridCanvas.height === Math.round(gh * gdpr)
        if (gridSig !== gridSigRef.current || !sizedOk) {
        gridSigRef.current = gridSig
        if (gridCanvas.width !== Math.round(gw * gdpr) || gridCanvas.height !== Math.round(gh * gdpr)) {
          gridCanvas.width = Math.round(gw * gdpr)
          gridCanvas.height = Math.round(gh * gdpr)
        }
        // CSS height must match content height (large y-drag can translate it
        // far; sizing to the viewport would expose an ungridded gap).
        const hpx = `${gh}px`
        if (gridCanvas.style.height !== hpx) gridCanvas.style.height = hpx
        // CSS width must match the drawing surface (plotWidth) or the canvas
        // stretches to the wrapper's right edge and distorts. left:0 + explicit
        // width keeps 1:1 device pixels.
        const wpx = `${gw}px`
        if (gridCanvas.style.width !== wpx) gridCanvas.style.width = wpx
        const gctx = gridCanvas.getContext('2d')
        gctx.setTransform(gdpr, 0, 0, gdpr, 0, 0)
        gctx.clearRect(0, 0, gw, gh)
        if (xCameraActive) {
          gctx.translate(cameraAx, 0)
          gctx.scale(cameraScaleX, 1)
          gctx.translate(-cameraAx, 0)
        }
        if (cameraActive) gctx.transform(1, 0, 0, cameraScale, 0, cameraTy)
        gctx.strokeStyle = '#252525'
        gctx.lineWidth = 1
        gctx.setLineDash([4, 4])
        gctx.beginPath()
        for (let rowIdx = 0; rowIdx < sortedMcaps.length; rowIdx++) {
          if ((rowIdx + 1) % 7 !== 0) continue
          // +0.5 puts the 1px stroke on a single physical pixel row (crisp).
          const y = Math.round((rowIdx + 1) * RH) + 0.5
          gctx.moveTo(0, y)
          gctx.lineTo(gw, y)
        }
        gctx.stroke()
        gctx.setLineDash([])
        }   // end signature-skip gate
      }
    }

    // ── ALIGNMENT PROBE (debug; localStorage.cheddar_debug_align='1') ──
    // Measures, per draw: the canvas band's real on-screen center for the
    // highlight row vs the chip / axis row / volume bar element rects.
    // getBoundingClientRect reflects transforms AND scroll, so these are
    // ground truth — whatever term is wrong shows as a nonzero delta,
    // named, in pixels. Remove once the saga is closed.
    try {
      if (!liveOnly && localStorage.getItem('cheddar_debug_align') === '1') {
        // Rects reflect the LAST committed paint, so probe against that
        // frame — live refs made this tool lie during a freeze.
        const _pf = paintedGeomRef.current
        const mc = _pf?.mcaps || visibleSortedMcapsRef.current
        const RHp = (_pf?.RH > 0) ? _pf.RH : dynamicRowHeightRef.current
        const hl = chartDataRef.current?.highlightMcap
        const idx = bucketRowIndex(mc, hl)
        const wrapper = canvas.closest('.footprint-body-wrapper')
        let probeEl = wrapper?.querySelector('.align-probe')
        if (wrapper && !probeEl) {
          probeEl = document.createElement('div')
          probeEl.className = 'align-probe'
          probeEl.style.cssText = 'position:absolute;top:4px;left:8px;z-index:99;font:10px JetBrains Mono,monospace;color:#ffb020;background:rgba(0,0,0,0.7);padding:4px 6px;pointer-events:none;white-space:pre;direction:ltr;'
          wrapper.appendChild(probeEl)
        }
        if (probeEl && idx >= 0 && idx < mc.length) {
          const cRect = canvas.getBoundingClientRect()
          const bandC = cRect.top + (idx + 0.5) * RHp
          const fmt = (label, el, center) => {
            if (!el) return `${label}: —`
            const r = el.getBoundingClientRect()
            const c = center ?? (r.top + r.height / 2)
            const d = (c - bandC)
            return `${label}: ${d >= 0 ? '+' : ''}${d.toFixed(1)}px`
          }
          const chip = wrapper.querySelector('.y-axis-current-overlay')
          // The axis rows are painted on a canvas inside .y-axis-body at
          // i * RH (pass two removed the row divs), so "where does the axis
          // think row idx is" = body.top + (idx + 0.5) * RH. Same measurement
          // as before — the element it came from just changed.
          const axisBody = wrapper.querySelector('.y-axis-body')
          const rowCenter = axisBody ? axisBody.getBoundingClientRect().top + (idx + 0.5) * RHp : null
          const vp = wrapper.querySelector(`.volume-profile-bar[data-mcap="${mc[idx]}"]`)
          // FADE STRIP probe: measure the bottom fade's real bottom edge
          // vs the y-axis-region's real bottom edge. If the strip is pinned
          // to the axis floor (correct) this delta is ~0 at every scroll.
          // If it rides a transform, the delta tracks that transform.
          const region = wrapper.querySelector('.y-axis-region')
          const after = region ? getComputedStyle(region, '::after') : null
          let stripLine = 'strip: —'
          if (region) {
            const rr = region.getBoundingClientRect()
            // The ::after strip is bottom:0 of the region; its rendered
            // bottom edge = region bottom + whatever transform it inherits.
            // We can't rect a pseudo-element, so report the region's own
            // transform and bottom instead — that IS what the strip rides.
            const cs = getComputedStyle(region)
            stripLine = `region.bottom ${rr.bottom.toFixed(1)}  region.tf ${cs.transform === 'none' ? '0' : cs.transform}`
          }
          probeEl.textContent =
            `idx ${idx}  RH ${RHp.toFixed(2)}  px ${(-yResidualPxRef.current).toFixed(1)}  scr ${bodyScrollRef.current?.scrollTop ?? 0}\n` +
            `${fmt('chip', chip)}  ${fmt('row', axisBody, rowCenter)}  ${fmt('vp', vp)}\n` +
            stripLine
        }
      }
    } catch {}

    const ctx = canvas.getContext('2d')
    const dpr = window.devicePixelRatio || 1

    // Mid Y-camera: DEST lattice can be many× viewport tall. Sizing the
    // 2D/GL bitmaps to that on phone OOM'd / black-screened on pinch.
    // Camera maps lattice → viewport, so paint surface is viewport-sized.
    const paintW = containerWCss
    const paintH = surfaceH
    const needW = Math.max(1, Math.round(paintW * dpr))
    const needH = Math.max(1, Math.round(paintH * dpr))
    // Size caches are PER-CANVAS: sharing one cache between the main and
    // live canvases would let whichever sized first satisfy the check and
    // leave the other at the blank 300x150 default forever. Mutate fields
    // (never replace the object) so the alias stays the live cache.
    const sizeCache = liveOnly ? liveCanvasSizeRef.current : lastCanvasSizeRef.current
    if (sizeCache.w !== needW || sizeCache.h !== needH) {
      canvas.width = needW; canvas.height = needH
      sizeCache.w = needW; sizeCache.h = needH
    }
    // CSS width/height only when changed (same reflow concern).
    const cssW = `${paintW}px`, cssH = `${paintH}px`
    const cssCache = liveOnly ? liveCssRef.current : lastStyleRef.current
    if (cssW !== cssCache.w) { canvas.style.width = cssW; cssCache.w = cssW }
    if (cssH !== cssCache.h) { canvas.style.height = cssH; cssCache.h = cssH }

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, paintW, paintH)
    if (xCameraActive || cameraActive) {
      // Keep magnified DEST rows / X-scaled columns inside the viewport.
      ctx.beginPath()
      ctx.rect(0, 0, paintW, paintH)
      ctx.clip()
      if (xCameraActive) {
        ctx.translate(cameraAx, 0)
        ctx.scale(cameraScaleX, 1)
        ctx.translate(-cameraAx, 0)
      }
      if (cameraActive) ctx.transform(1, 0, 0, cameraScale, 0, cameraTy)
    }

    // Shared chart GL scene (S0–S2) — create once, size with main (full pass only).
    // Clear is deferred to beginFrame() immediately before cell draws.
    // Skip resize while camera-hidden — avoiding a multi-MB GL alloc on pinch.
    if (!liveOnly) {
      if (isFootprintCellGLEnabled() && cellGlCanvasRef.current && cellGlRef.current === null) {
        // Was createChartGLScene, a wrapper whose only non-forwarding job was
        // this `|| false` guard. Its other half — beginHeatLayer /
        // commitHeatLayer / blitHeatLayer / stampHeatOverlay / drawHeatBatches
        // — was the frozen-heat FBO the x camera scaled between arm and land.
        // No camera, no freeze, no blit: zero call sites. The scene never left
        // this file either (cellGlRef is local; LiquidityHeatLayer runs its
        // own canvas), so nothing downstream can miss it.
        const _c = createFootprintCellGL(cellGlCanvasRef.current)
        cellGlRef.current = (_c && _c.ok) ? _c : false
      }
      const _gl = cellGlRef.current
      if (!xZoomGesture && _gl && _gl.ok) {
        // Same integer surface as main/grid/VP (paintW/paintH are already
        // Math.round'd from integer clientWidth/clientHeight). (chart-perf)
        // Skip both calls outright when nothing changed for THIS controller —
        // they ran on every full paint, ~60x a second, for a size that only
        // moves on a real window/seam resize.
        const _gs = glSizeMemoRef.current
        if (_gs.gl !== _gl || _gs.w !== paintW || _gs.h !== paintH || _gs.dpr !== dpr) {
          _gl.resize(paintW, paintH, dpr)
          _gl.setCssSize(paintW, paintH)
          _gs.gl = _gl; _gs.w = paintW; _gs.h = paintH; _gs.dpr = dpr
        }
      }
    }

    // ─── Device-pixel-quantized pan offset (shimmer fix) ───
    // Panning slowly, the raw offset creeps by fractions of a pixel and every
    // element that rounds to whole pixels flips at a slightly DIFFERENT
    // sub-pixel threshold — wicks snap on one frame, bodies the next, cells
    // and heat antialias differently throughout. Reads as shimmer, worst on
    // phones where DPR is ~3 so one CSS pixel is a coarse jump.
    // Quantize the offset ONCE per frame to the device-pixel grid and let
    // every column derive from it: elements land on boundaries TOGETHER, so
    // nothing shifts relative to anything else. Motion still advances in
    // 1/dpr steps — finer than the eye — so it stays smooth AND crisp.
    const _dprQ = window.devicePixelRatio || 1
    const currentPanOffsetPx = Math.round(fractionalOffsetPxRef.current * _dprQ) / _dprQ

    if (candles.length === 0 || sortedMcaps.length === 0) {
      // Token switch/reset publishes an empty set: the full pass just cleared
      // the MAIN canvas, but with no anims there is no pump — the live layer
      // would keep the outgoing token's forming candle forever. Recurse once
      // (depth 1: live never calls full) so it clears too.
      if (!liveOnly) {
        const _glE = cellGlRef.current
        if (_glE && _glE.ok) _glE.clear()
        // live-clear2-20261017: the recursion below never cleared anything on a
        // switch - committedXRef still holds the OLD coin's candles (it is only
        // written further down, past this return), so the live pass bails at
        // its "committedX.candles !== candles" guard before its clearRect.
        // Clear the live layer here directly. The live column's background
        // plate is on plateLayer (full pass only), so it stays like history.
        const _LL = liveLayerRef.current
        if (_LL && _LL.width && _LL.height) {
          const _lc = _LL.getContext('2d')
          _lc.setTransform(1, 0, 0, 1, 0, 0)
          _lc.clearRect(0, 0, _LL.width, _LL.height)
        }
        zLayerClear(zAboveLayerRef.current); zLayerClear(zBelowLayerRef.current)   // zindex-20261102
        zLayerDirtyRef.current.a = false; zLayerDirtyRef.current.b = false
        drawCanvasRef.current?.('live')
      }
      return
    }

    // ─── B3 pack pass ───────────────────────────────────────────────
    // Compact closed candles' _liveLevels Map into a Float64Array
    // (_native) so forEachLevel / candleMaxLevelVolume can iterate a
    // typed array instead of walking a Map forever. addTickToLive on
    // an already-packed candle (rare — late tick after pack) unpacks
    // back into _liveLevels via candleHelpers, so the check is safe.
    // Skips the last PACK_BUFFER_CANDLES so the normal stream of late
    // ticks (within the buffer) doesn't churn pack/unpack each frame.
    // Uses a high-water-mark so we don't re-walk the array each frame.
    // Previously this checked `c.mcapLevels` which was a legacy field
    // name — the live data goes into `_liveLevels`, so the check never
    // passed and the pack pass was effectively dead.
    if (!liveOnly) {   // data-shape work — every data change triggers a FULL draw, so live frames never need it
      const pc = packCacheRef.current
      if (pc.candlesRef !== candles) { pc.candlesRef = candles; pc.lastPackedIdx = -1 }
      const packLimit = candles.length - 1 - PACK_BUFFER_CANDLES
      for (let i = pc.lastPackedIdx + 1; i <= packLimit; i++) {
        const c = candles[i]
        // Already packed → _native exists, _liveLevels is null. Skip.
        // Historical bars never had levels → skip too.
        if (c && !c.isHistorical && !c._native && c._liveLevels && c._liveLevels.size > 0) {
          packCandle(c, mcapBucketSizeRef.current)
        }
      }
      if (packLimit > pc.lastPackedIdx) pc.lastPackedIdx = packLimit
    }
    // ────────────────────────────────────────────────────────────────

    // The forming candle — the ONE thing the live pass paints and the ONE
    // thing the full pass skips. Exclusivity by construction: no overlap, no
    // double-paint, no seam at candle close (close bumps structuralVersion →
    // chartData identity → the data-tick effect schedules a FULL draw, which
    // paints the just-closed bar; the live layer repaints the NEW forming one).
    const liveIdx = !candles[candles.length - 1].isHistorical ? candles.length - 1 : -1

    const total = candles.length
    const useCommittedX = liveOnly && committedX && (!committedX.candles || committedX.candles === candles)
    // drawCanvas line ~1370 — FIXED 160px OVERLAY
    // Float win only drives candle WIDTH (pxPerCandle). Index loops MUST use
    // the rounded int — candles[100.7] is undefined (prior float-ease blank cells).
    // Inclusive SoT: winSize = N on-screen candles → startIdx = endIdx - N + 1.
    // Pan overdraw is SEPARATE: `_loopLo = startIdx - 1` (not baked into winSize).
    // ── PAINT THE WINDOW THE LATTICE WAS FIT FOR. ──────────────────────
    // Not viewWindowSizeRef. The ref is published by a layout effect AFTER
    // the render that built this lattice, so during a wheel it trails the
    // committed window by a full notch — the [fit] probe shows exactly that
    // (`win=50 ref=67.00`, then `win=37 ref=50.00`). A draw that landed in
    // that gap painted startIdx = endIdx - 67 + 1 while the fit had only
    // scanned 50 candles, so the extra columns on the left were candles
    // whose lows the lattice never covered — and they rendered BELOW the
    // viewport. A drag pan cannot do this: it never changes the window, so
    // ref and state can never disagree.
    //
    // sortedMcaps carries its own window now, so the two cannot come from
    // different renders. One object, one generation, ordering-proof.
    // Frame geom X from one owner when the slot handle is present.
    const xSnap = (!useCommittedX && xWin && typeof xWin.snap === 'function')
      ? xWin.snap()
      : null
    const winSizeF = useCommittedX
      ? committedX.winSizeF
      : (Number.isFinite(sortedMcaps?.winSizeF)
          ? sortedMcaps.winSizeF
          : (xSnap ? xSnap.win : viewWindowSizeRef.current))
    // ONE ROUNDING RULE. The mid-ease ceil was the second overrun: the fit
    // scans viewportInfo's window, which is Math.round(_winLive), so a ceil
    // here painted one column MORE than fit had measured — at winSizeF 44.3
    // the memo sliced 44 candles and the painter looped 45. Zooming out, the
    // newly revealed candle was drawn before fit had ever seen it, which is
    // the visible form of "the wheel runs ahead of the fit".
    //
    // The ceil was added to stop rim-candle pop at .5 back when painter and
    // memo were reading different values anyway (live ref vs committed int).
    // They read the same value now, so both round and both pop on the same
    // frame — which is correct, not a glitch.
    const winSize = Math.max(1, Math.round(+winSizeF || 1))
    const mainPlotWidth = Math.max(50, containerW - RIGHT_PADDING - FIXED_OVERLAY_WIDTH)
    const plotWidth = mainPlotWidth                     // main chart area only
    const pxPerCandle = plotWidth / Math.max(1, (+winSizeF > 0 ? +winSizeF : winSize))
    const cw = pxPerCandle
    // LOD: zoomed out past the point where per-level cells are readable,
    // render candles the way candle MODE already does — cells skipped,
    // candlesticks from the body/wick pass. This is why "2-3 big footprints"
    // is butter and 200 candles isn't: the cell loop's cost is
    // candles × levels × (anim maps + LUT + transforms + 2-4 fills) per
    // frame, and below ~7px/candle all of it paints invisible detail.
    // One boolean, consumed at the cell skip and the three body/wick style
    // points, so LOD candles look EXACTLY like candle-mode candles.
    // ─── LOD crossfade ───
    // Text gate (FP_LOD_TEXT_PX) is independent and earlier. Cell→candle
    // morph uses FADE_START..CELLS so volume bars linger after labels die.
    // Candles style underneath for the whole fade; cells use 1-blend² alpha.
    // ── NO CROSSFADE. THREE DISCRETE STATES. ──────────────────────────
    //   cw > FP_LOD_TEXT_PX (32)  → full footprint: cells + text
    //   cw > FP_LOD_SWITCH        → cells, no text
    //   cw <= FP_LOD_SWITCH       → candle mode: cells gone
    //
    // This was a continuous ramp, (20 - cw) / 10, and everything downstream
    // read it as an ALPHA. That meant a whole band of zoom where cells AND
    // candles both drew, every rect alpha-blended — and under the wheel's
    // scaleX camera those rects are also non-axis-aligned, so each one takes
    // the antialiased transformed-and-blended path instead of a blit. Fine
    // zoomed tight, fine zoomed wide, sluggish in the middle: the band was
    // paying for two layers at once on the slow path.
    //
    // Binary keeps every downstream expression working unchanged — 1-b², the
    // candle alpha ramp, the _cellsGone test — they just evaluate to exactly
    // 0 or 1 now. Nothing renders at a partial alpha, so nothing is ever
    // drawn only to be blended away.
    //
    // Switch at the midpoint of the old ramp: cells stop being readable
    // around there, and it is where the crossfade used to be half done.
    //
    // Latched, with a dead zone between 15 and 18. A bare `cw <= 15` is a
    // step, and with a 25%-per-notch wheel and no interpolation you cross it
    // in one frame — the whole chart changes render mode between two
    // consecutive notches, and a notch back the other way changes it again.
    // The latch means the boundary you leave is not the boundary you return
    // through, so the mode can only change on a deliberate move.
    if (cw <= FP_LOD_SWITCH) lodCandlesRef.current = true
    else if (cw >= FP_LOD_RESTORE) lodCandlesRef.current = false
    const lodBlend = lodCandlesRef.current ? 1 : 0
    const lodCandles = lodBlend >= 1
    const _tpo = chartModeRef.current === 'tpo'
    const asCandle = chartModeRef.current === 'candle' || lodCandles || _tpo
    // Wicks and bodies use the cell rows in both modes (candleY). A
    // continuous price sits off the cell it belongs to, so the switch
    // between candles and footprints jumped by up to a row.
    // Styling switch below is COLOR + WIDTH only.
    const candleStyled = chartModeRef.current === 'candle' || lodBlend > 0
    // Vela candle LOD tiers (see ../utils/candleLod). Aggregate only when
    // cells are not the primary view; localStorage.candleAggLod=0 disables.
    const _aggLodOff = (typeof localStorage !== 'undefined' && localStorage.getItem('candleAggLod') === '0')
    const _candleTier = _aggLodOff ? 'full' : candleTier(cw)
    // ── THE SLUGGISH BAND ─────────────────────────────────────────────
    // A [lod] probe walked the whole zoom range:
    //   cw=45..146  blend=0.000  cellsGone=false   cols=5..29   → cells only
    //   cw=11..19   blend=0.4..0.9 cellsGone=false cols=53..65  → BOTH
    //   cw=9.61     blend=1.000  cellsGone=true    cols=76      → candles only
    //
    // Fine tight, fine wide, sluggish in between — because the middle drew
    // ~6500 cell rects AND a full body+wick per column, all alpha-blended.
    // A per-frame cost rises with column count; a U-shape means a BAND where
    // an extra layer switches on. This was that band.
    //
    // Cells now go at 0.82 instead of 0.999. With the front-loaded fade above
    // ((1-b)^2), cell alpha at b=0.82 is 0.032 — 3% opaque, under full-
    // strength candles. Invisible, and it was the entire cost. The old curve
    // held them at 26% opacity at b=0.86, which is why cutting them earlier
    // was not an option before the curve changed: the two edits only work
    // together.
    const _cellsGone = asCandle || lodCandles || lodBlend >= 0.82
    const _useCandleAgg = !_aggLodOff && _candleTier === 'aggregate' && _cellsGone && !liveOnly && !_tpo
    const _useWickOnly = !_aggLodOff && _candleTier === 'wick' && _cellsGone
    const _wg = zoomGlideRef.current
    const _wheelPlaced = !!(_wg && _wg.wheelAnchor && Number.isFinite(_wg.placedEnd))
    // Placed end wins over follow -1 for the whole body-wheel glide.
    const endIdx = _wheelPlaced
      ? _wg.placedEnd
      : (useCommittedX
        ? committedX.endIdx
        : resolveEndIdx(xSnap ? xSnap.end : viewEndIndexRef.current, total, !(xWin?.wheelParkRef?.current)))
    const startIdx = startIdxFor(endIdx, winSize)
    // The slot's frac is rescaled the instant a glide frame commits a new
    // whole-candle window (commitWindow -> rescaleFracPx), but cw here comes
    // from the LATTICE's window, which the React render rebuilds a frame
    // later. Mixing them put the mesh at the new window's sub-candle offset
    // with the old window's column width for that frame - a sideways
    // shimmy at every whole-candle step. Express frac in the window this
    // pass actually paints with. A body-wheel place already solved frac for
    // this W; rescaling it (frozen-end) slides the cursor candle.
    let panOffsetPx = _wheelPlaced
      ? (Number.isFinite(_wg.placedFrac) ? _wg.placedFrac : 0)
      : (useCommittedX
        ? committedX.panOffsetPx
        : (xSnap ? xSnap.frac : currentPanOffsetPx))
    if (!_wheelPlaced && !useCommittedX && xSnap && Number.isFinite(xSnap.win) && Number.isFinite(winSizeF)
        && Math.round(xSnap.win) !== Math.round(winSizeF) && plotWidth > 0) {
      panOffsetPx = rescaleFracPx(panOffsetPx, xSnap.win, winSizeF, plotWidth)
    }

    // after const cw = pxPerCandle
    const visibleCount = endIdx - startIdx + 1 || 1
    // ── PUBLISH WHAT WAS ACTUALLY PAINTED. ────────────────────────────────
    // Everything a layer needs, so no layer ever re-derives geometry from
    // refs. Written here, at the end of the full pass, read at FRAME_LAYER.
    // X fields also carry xWin.snap() so geom consumers share one owner.
    if (!liveOnly) committedXRef.current = {
      candles, total, winSizeF, winSize, endIdx, startIdx, visibleCount,
      plotWidth, cw, panOffsetPx,
      xWin: xSnap || (xWin && typeof xWin.snap === 'function' ? xWin.snap() : null),
      frac: panOffsetPx,
    }
    const firstVisibleRow = 0
    const lastVisibleRow = sortedMcaps.length - 1

    if (endIdx < 0) return

    // ─── Liquidity heat: paint BEFORE candles so the footprint stays on
    // top. Derived here from the raw snapshot ring: for each visible
    // column, the snapshots inside its slot are aggregated per price row
    // (max resting size), then one translucent cell per row — alpha
    // log-scaled against the session max so a 10× wall reads clearly
    // without blowing out the field. ~20 levels × snaps × columns folds
    // to at most rows × columns fills; trivially cheap.
    const heatSnaps = hlBookRef.current.snaps
    // FULL DRAWS ONLY. The live pump reruns drawCanvas with liveOnly at
    // 30-40fps repainting just the forming region — without this gate the
    // heat pass repainted the WHOLE field over the standing historical
    // cells every pump frame, while only the live candle's cells were
    // redrawn on top of it. That was every "liquidity is over the
    // footprint (except the current one)" report; the exclusions and
    // backings were treating its symptoms.
    const cbSnaps = cbSnapsRef.current
    // `hctx` gates the whole section: no heat canvas, no heat render. It is
    // part of the CONDITION, never an early return — this block sits inside
    // drawCanvas, so a bare `return` here would abort candles, footprint and
    // everything after it on any frame without the canvas.
    // The slot fold ADVANCES regardless of who renders it — the layer reads
    // book.slot on its own clock, so this must sit OUTSIDE the render gate
    // below. (It lived inside; with the flag on, the fold froze and the
    // layer read a stale state — caught by the audit, not by esbuild.)
    if (!liveOnly && heatSnaps.length > 0) {
      const tfMs = (candles.length >= 2 ? candles[1].startTime - candles[0].startTime : 0) || 60000
      hlBookRef.current.advanceSlots(tfMs, `${hlPair?.base || ''}|${tfMs}`)
    }
    // ─── Heat render: MOVED to LiquidityHeatLayer (the merge) ───
    // One canvas carries history + flow + CB there; this file keeps only
    // the slot-fold advance above (the layer reads book.slot) and the CB
    // data effect. The ~470-line block that lived here — HL columns,
    // normalizer fold, CB columns, CB projection — was ported, verified on
    // live tape behind the cheddar_heat_in_layer flag, then cut.

    // Derive bucketSize from the array spacing rather than mcapBucketSizeRef
    // OR chartData.gridBucket — both can lag behind the renderer's actual
    // grid for one frame (state update from the auto-adjust effect hasn't
    // landed yet, or data layer's bucket differs from the inline-picked
    // bucket inside the visibleSortedMcaps memo). Cells, swing markers,
    // volume bars all draw against this — using a stale bucket makes them
    // flash to wrong rows. Array spacing is the ground truth.
    const bucketSize = sortedMcaps.length >= 2
      ? Math.round((sortedMcaps[0] - sortedMcaps[1]) * 1e8) / 1e8
      : (chartData.gridBucket > 0 ? chartData.gridBucket : mcapBucketSizeRef.current)
    // LOG: the display cache is keyed on (bucket, ratio). In log the lattice
    // spacing above is the TOP row's absolute width, which drifts as the range
    // moves — keyed on that, every frame would miss. So the key bucket is the
    // (stale, stable) linear one and the ratio carries the real identity.
    // (ycells) The ratio is the lattice's own canonical step, exp(L.step) —
    // the SAME double on every lattice of a gesture (rows-changed commits
    // included) and exact, so it is both the cache key AND what the
    // aggregator bins with. (A 1e8-rounded ratio would be a different
    // geometric ladder: rows drift off the grid far from the anchor.)
    // Linear keeps its existing key exactly (rounded spacing = bucketSize).
    // (ycells) No latticeId: a display depends only on (bucket, ratio), so a
    // coarser display can never match a finer key anyway.
    const dispRatio = sortedMcaps.log && sortedMcaps.length >= 2
      ? (sortedMcaps.step > 0 ? Math.exp(sortedMcaps.step) : sortedMcaps[0] / sortedMcaps[1])
      : null
    const dispKeyBucket = dispRatio ? mcapBucketSizeRef.current : bucketSize
    const forEachDispLevel = (c, fn) => forEachLevel(c, fn, dispKeyBucket, dispRatio)
    const topMcap = sortedMcaps[0]
    const bottomMcap = sortedMcaps[sortedMcaps.length - 1]
    const mcapRange = topMcap - bottomMcap
    // Into emits on exact array spacing; nearest ties a level to an axis
    // row without the Math.round rebase that grouped in-view cells.
    // Highlight/chip keep bucketRowIndex for continuous prices.
    const _lnStep = dispRatio ? Math.log(sortedMcaps[0] / sortedMcaps[1]) : 0
    // MUST NOT use nearestLatticeRow here — that clamps out-of-window prices
    // onto row 0 / row n-1, so Y zoom-in stacks cells at the edges instead of
    // letting them leave the viewport. latticeRowIndex + bounds → -1 skip.
    // ── THE PROJECTION. One price->row map for this frame. ──────────────
    // Built from the PAINTED lattice and the display key, so the cell path
    // and the candle path can no longer answer the same question from two
    // helpers. rowOfPrice is bit-identical to latticeRowIndex (unclamped —
    // an above-grid high must stay negative so its wick clips off the top
    // rather than snapping to row 0); rowOfLevel adds the range clamp that
    // mcapToRow already applied.
    const proj = createProjection({
      mcaps: sortedMcaps,
      rowHeight: RH,
      keyBucket: dispKeyBucket,
      keyRatio: dispRatio,
    })

    // THE FRAME. Everything below reads this; nothing re-reads
    // visibleSortedMcapsRef / dynamicRowHeightRef / viewEndIndexRef /
    // fractionalOffsetPxRef. rowOfPrice takes a price, rowOfLevel a display
    // key (band lower edge) — different inputs, not a duplicate mapping.
    let _framePriceForY = chartData.highlightMcap
    if (liveIdx >= 0 && candles[liveIdx]) {
      _framePriceForY = resolveLivePriceForY({
        highlightMcap: chartData.highlightMcap,
        closeMcap: candles[liveIdx].close_mcap,
        sortedMcaps,
        bucketSize,
        lnStep: _lnStep,
      })
    }
    livePriceForYRef.current = _framePriceForY
    const frame = {
      mcaps: sortedMcaps, RH, residualPx, proj,
      pinPx: -residualPx,
      livePrice: _framePriceForY,
      candles,   // (subx) subgraphs index the array this frame painted
      total, plotWidth, cw, winSizeF, winSize,
      startIdx, endIdx, visibleCount, panOffsetPx,
      camScale: cameraScale, camTy: cameraTy,
      camScaleX: cameraScaleX, camAx: cameraAx,
      rowOfPrice: (m) => proj.rowOfPrice(m),
      rowOfLevel: (m) => proj.rowOfLevel(m),
      yOf: (m) => proj.yOfPrice(m),
      // No Math.round in the mapper; snap at the fill.
      xLeft: (i) => plotWidth - (endIdx - i + 1) * cw + panOffsetPx,
      xMid: (i) => plotWidth - (endIdx - i + 1) * cw + panOffsetPx + cw / 2,
    }
    paintedGeomRef.current = frame   // same object, old keys, old readers work
    lastFrameRef.current = frame
    if (!liveOnly) repositionYOverlays(frame)   // (y2b) tags / marker / dlmm rows
    if (!liveOnly) absRectsRef.current = new Map()   // full repaint: drop stale outline rects
    if (!liveOnly) placeEdgeRows(frame)          // header/footer slide with the columns
    placeSyncLineRef.current(frame)              // (xhair-sync) remote time line rides this chart's columns

    // ── THE CHIP, on the painted projection. ────────────────────────────
    // Written here rather than in JSX because JSX runs at commit against
    // React state, and on a frozen frame the canvas is painting an older
    // lattice. drawCanvas is after the commit, so this is the last writer
    // and the chip lands on the row the canvas actually drew. Same shape as
    // the JSX version — yOfPrice is mcapToY — so the first paint does not
    // move it; it only stops it drifting.
    {
      const chipEl = currentChipRef.current
      if (chipEl) {
        const chipPrice = livePriceForYRef.current > 0
          ? livePriceForYRef.current
          : (chartDataRef.current?.highlightMcap || 0)
        const chipY = chipPrice > 0 ? proj.yOfPrice(chipPrice) : NaN
        if (Number.isFinite(chipY)) {
          const chipH = Math.max(RH, 20)   // readable minimum, as in the JSX
          const chipTop = chipY - chipH / 2
          // (scrollperf) Position rides TRANSFORM, not `top`. top is a layout
          // property: writing it every zoom frame re-laid-out the axis region
          // and logged a layout shift per frame. The CSS rule's
          // translateY(var(--chart-y-residual)) is folded into the same inline
          // transform, so the on-screen geometry is identical:
          //   top:0 + translateY(residual + chipTop) == top:chipTop + translateY(residual)
          // Cached PER ELEMENT (expando / inline style), so a remounted chip
          // node is always written — the shared _ls cache could skip a fresh node.
          if (chipEl.style.top !== '0px') chipEl.style.top = '0px'
          // (perf2) regionPx folded in directly (the wrapper var is gone).
          const _rp = Number.isFinite(_ls.regionPx) ? _ls.regionPx : 0
          const chipTf = yTagTf(_rp + chipTop)
          if (chipEl.__chipTf !== chipTf) { chipEl.style.transform = chipTf; chipEl.__chipTf = chipTf }
          const chipHpx = `${chipH}px`
          if (chipEl.style.height !== chipHpx) chipEl.style.height = chipHpx
          _ls.chipTop = chipTop
          _ls.chipH = chipH
        }
      }
    }
    // Overlay draw Y — shared with the hover hit-test in useChartInteraction.
    // Snaps to lattice row center so SR / AVWAP / trendline draw matches
    // useChartInteraction. Continuous mcapToYUtil stays for wicks/bodies.
    const mcapToY = frame.yOf

    // ── Volume profile layer (canvas) ────────────────────────────────
    // Replaces the VolumeProfileBars DOM layer 1:1. Painted on FULL passes
    // only — data ticks already schedule a full draw, so the profile stays
    // exactly as fresh as before at a few hundred fillRects instead of a
    // few hundred divs' style+layout+paint per tick. Reads the profile via
    // ref (no new drawCanvas deps); rides the same tRegion transform the
    // old div did (the upstream style write is untouched). Bars right-align
    // to the canvas edge, which sits at the old anchor line
    // (right:profileRightPx), so pixels land where the DOM bars did.
    // Labels are NOT ported: .volume-profile-bar-label shipped
    // color #00000000 with no reveal rule anywhere in styles.css —
    // permanently invisible DOM; dropping them is pixel-identical.
    if (!liveOnly) {
      const vpCanvas = volumeProfileLayerRef.current
      if (vpCanvas) {
        const vp = volumeProfileRef.current
        // Blank on empty, same rule as the y-axis column. Without this the
        // canvas keeps its LAST painted frame — so switching tokens leaves the
        // previous token's profile hanging on screen until the new one folds
        // in. On a heavy mint that fold takes seconds, and for all of them the
        // chart is asserting a distribution that belongs to a different coin.
        if (!vp || vp.profile.size === 0) {
          const vctx0 = vpCanvas.getContext('2d')
          if (vctx0) vctx0.clearRect(0, 0, vpCanvas.width, vpCanvas.height)
        } else {
        const VP_W = 200                       // max bar width — matches (total/maxVol)*200
        const vdpr = window.devicePixelRatio || 1
        const vw = Math.round(VP_W * vdpr)
        const vpH = surfaceH
        const vh = Math.max(1, Math.round(vpH * vdpr))
        if (vpCanvas.width !== vw || vpCanvas.height !== vh) {
          vpCanvas.width = vw
          vpCanvas.height = vh
          vpCanvas.style.width = `${VP_W}px`
          vpCanvas.style.height = `${vpH}px`
        }
        const vctx = vpCanvas.getContext('2d')
        vctx.setTransform(vdpr, 0, 0, vdpr, 0, 0)
        vctx.clearRect(0, 0, VP_W, vpH)
        // VP is a fixed side lane — never take X scaleX (that slid bars with zoom).
        // Y camera only, same as before the X-freeze work.
        if (cameraActive) vctx.transform(1, 0, 0, cameraScale, 0, cameraTy)
        if (vp && vp.maxVol > 0 && RH > 2) {
          const vc = vaConfigRef.current
          const THV = themeRef.current?.volumeProfile
          const vpSideNow = vc.vpSide === 'left' ? 'left' : 'right'
          const buyC  = vc.vpBuy  || THV?.buy  || '#6ea8ff'
          const sellC = vc.vpSell || THV?.sell || '#ff7b7b'
          const h = RH - 2
          // ─── FOOTPRINT MODE ───
          // The visible-range aggregate drawn as a footprint ladder: buy bar
          // growing left from a centre line, sell bar growing right, numbers
          // either side. Same rows, same RH, same profile Map — only the
          // render differs. Gated on vp.maxSide, the cells' scale: it is 0
          // whenever every visible candle is smeared history, and there is
          // nothing to put either side of a centre line in that case, so the
          // frame falls through to the profile render below rather than
          // dividing by zero. Futures never trip this; Solana does the moment
          // you zoom past the live window.
          const useFp = vc.vpMode === 'footprint' && vp.maxSide > 0
          if (useFp) {
            const halfW = VP_W / 2
            const cx = VP_W / 2
            const showText = vc.vpText !== false
            // The cells' font formula with VP_W as the column width — the
            // `VP_W * 0.20 = 40` term never binds below RH 61, so this is
            // clamp(floor(RH * 0.65), 8, 16). Computed HERE, not borrowed:
            // fontSize / font / TEXT_* are declared after this block in the
            // same component and are in TDZ at this line.
            const fpx = Math.max(8, Math.min(CELL_ATLAS_PX, Math.min(RH * 0.65, VP_W * 0.20)))
            const fpScale = fpx / CELL_ATLAS_PX
            const fpFont = CELL_ATLAS_FONT
            // Text contrast against a FLAT fill, once per frame. The cells
            // read luminance per intensity index off the LUT; the VP paints
            // vpBuy/vpSell as-is, so derive it from the two colours directly
            // with buildLUT's own weights. Numbers sit at cx±4, which is ON
            // the bar for anything but the shortest rows, so this matters:
            // both default VP colours are above the threshold and white text
            // would vanish on them.
            const THc = themeRef.current?.cell
            const lightAt = THc?.textLightThreshold ?? 135
            const onDark  = THc?.textOnDark  || '#ffffff'
            const onLight = THc?.textOnLight || '#1a1a1a'
            const lumOf = (c) => { const [r, g, b] = hexToRgb(c); return 0.299 * r + 0.587 * g + 0.114 * b }
            const buyTxt  = lumOf(buyC)  > lightAt ? onLight : onDark
            const sellTxt = lumOf(sellC) > lightAt ? onLight : onDark
            // Buckets, same reason as the cell loop's: resolve (font, colour)
            // once rather than rebuilding a ~45-char atlas key per row.
            const _bkVpBuy  = atlasBucket(buyTxt)
            const _bkVpSell = atlasBucket(sellTxt)
            for (const [step, e] of vp.profile) {
              const mcap = vp.ratio ? logStepEdge(step, vp.ratio) : step * vp.bucket
              const rowIdx = findBucketIndex(sortedMcaps, mcap, bucketSize)
              if (rowIdx === -1) continue
              const buy = e[0], sell = e[1], total = e[2]
              const y = rowIdx * RH + 1
              if (buy === 0 && sell === 0) {
                // Smeared history. The profile paints these neutral; so does
                // the ladder. Width is the ink a split row of the same total
                // would paint across both halves — (total/maxSide)*halfW —
                // centred on the line and clamped to the lane. No numbers:
                // there is no split to claim, and the absence is itself the
                // read of where real per-level data exists in the range.
                const w = Math.min(VP_W, (total / vp.maxSide) * halfW)
                if (!(w > 0)) continue
                vctx.fillStyle = '#36383d'
                vctx.fillRect(cx - w / 2, y, w, h)
                continue
              }
              // Cells' geometry (buyBarWidth / sellBarWidth against halfWidth):
              // buy grows LEFT from centre, sell grows RIGHT.
              const buyW  = (buy  / vp.maxSide) * halfW
              const sellW = (sell / vp.maxSide) * halfW
              if (buyW > 0) {
                vctx.fillStyle = buyC
                vctx.fillRect(cx - buyW, y, buyW, h)
              }
              if (sellW > 0) {
                vctx.fillStyle = sellC
                vctx.fillRect(cx, y, sellW, h)
              }
              if (showText) {
                const cy = y + h / 2
                // Cells' placement: buy right-aligned ending at cx-4, sell
                // left-aligned from cx+4. Same formatter, same atlas — so
                // whatever the columns print, the ladder prints, and the
                // blits share the cells' sprite pages instead of minting
                // per-string canvases for a lane that can carry ~100 rows.
                if (buy > 0) {
                  const sp = atlasText(_bkVpBuy, _fcv(buy))
                  const dw = sp._cssW * fpScale, dh = sp._cssH * fpScale
                  vctx.drawImage(sp.img, sp.sx, sp.sy, sp.sw, sp.sh,
                    Math.round(cx - 4 - dw), Math.round(cy - dh / 2), dw, dh)
                }
                if (sell > 0) {
                  const sp = atlasText(_bkVpSell, _fcv(sell))
                  const dw = sp._cssW * fpScale, dh = sp._cssH * fpScale
                  vctx.drawImage(sp.img, sp.sx, sp.sy, sp.sw, sp.sh,
                    Math.round(cx + 4), Math.round(cy - dh / 2), dw, dh)
                }
              }
            }
            // Centre line last, over the bars, full lane height.
            vctx.fillStyle = '#4a4a55'
            vctx.fillRect(cx - 0.5, 0, 1, totalHeight)
          } else {
          // ─── PROFILE MODE (unchanged) ───
          vctx.setLineDash([3, 3])
          vctx.lineWidth = 1
          vctx.strokeStyle = '#2b2b2b'         // the DOM bars' dashed border
          for (const [step, e] of vp.profile) {
            const mcap = vp.ratio ? logStepEdge(step, vp.ratio) : step * vp.bucket
            const rowIdx = findBucketIndex(sortedMcaps, mcap, bucketSize)
            if (rowIdx === -1) continue
            const buy = e[0], sell = e[1], total = e[2]
            const barW = (total / vp.maxVol) * VP_W
            if (!(barW > 0)) continue
            // Bars grow AWAY from their anchored edge: right-anchored means
            // right-aligned and growing left; left-anchored is the mirror.
            // Right-aligning on the left edge would leave every bar detached
            // from the edge it is supposed to hang off.
            const x = vpSideNow === 'left' ? 0 : VP_W - barW
            const y = rowIdx * RH + 1
            vctx.fillStyle = buy > sell ? buyC : sell > buy ? sellC : '#36383d'
            vctx.fillRect(x, y, barW, h)
            if (barW >= 2) vctx.strokeRect(x + 0.5, y + 0.5, barW - 1, h - 1)
          }
          vctx.setLineDash([])
          }   // end: mode branch
        }
        }   // end: profile has data
      }
    }

    // ─── Display pass ───────────────────────────────────────────────
    // Coarsen each visible candle's _native (or _liveLevels for the
    // rightmost) into a _display Float64Array at the renderer's actual
    // grid bucket. forEachLevel hits the _display branch, so the cell
    // loop iterates O(display_levels) instead of O(native_levels) and
    // adjacent native levels in the same display row are pre-aggregated
    // instead of painted on top of each other. Idempotent: short-circuits
    // when (_displayBucket, _dataVersion) match what's already cached.
    // candleMaxLevelVolume's _display branch picks up the aggregated max
    // for free, fixing the buyBarWidth proportion at coarse buckets.
    // ────────────────────────────────────────────────────────────────
    // Visible display rebuild: sync on lattice flip; budget otherwise.
    //
    // forEachLevel (lattice-keyed) no longer soft-bins native when cold —
    // that was the solid R/G flash. Cold leftovers skip cells for a frame
    // instead. So we must NOT unbounded-sync every scroll-in candle (that
    // hitch reads as binding/input lag). Lattice flip still syncs the
    // whole window (blank beat worse than one hitch). Steady-state uses
    // 4ms; idle warm (below) keeps most newcomers hot.
    //
    // X motion-scroll: panGeom can hit; do NOT Infinity-rebuild every ease
    // tick. Lattice flip still syncs the whole window. Drag still slides via
    // panGeom. (yaudit) xPaintFreeze note retired — freeze never armed.
    // The warm check mirrors buildDisplay's own cache guard
    // (candleHelpers) — if that key ever changes, change this with it.
    {
      const paintKey = dispRatio != null ? `${dispKeyBucket}|${dispRatio}` : String(dispKeyBucket)
      const latticeFlipped = dispPaintKeyRef.current !== paintKey
      if (latticeFlipped) dispPaintKeyRef.current = paintKey
      // ROW HEIGHT INVALIDATES THE WHOLE MESH, SO IT CANNOT BE BUDGETED.
      // fontSize is min(RH*0.5, cw*0.20) and RH is in the mesh key, so a
      // changed RH means every cell's geometry AND every sprite is stale. A
      // partial rebuild of wholly-stale geometry is guaranteed to show holes —
      // that is the blank-then-fill on a y zoom.
      //
      // This never fired before because RH was containerHeight / visCount, so
      // it only moved when visCount moved, and that IS a lattice flip. Now RH
      // moves with the Y span independently of count, so it needs saying out
      // loud. Same treatment as a flip: rebuild whole, one frame, no holes.
      const rhChanged = meshRHRef.current !== RH
      meshRHRef.current = RH
      // (yview) X-cone: candles enter as ceil(W) grows — never budget-skip their
      // displays. Arm also pre-warms the path union; this is the safety net.
      const coneLiveDisp = !!(zoomGlideRef.current?.cone
        && Number.isFinite(glideHalfRef.current)
        && Number.isFinite(glideCenterRef.current))
      const REBUILD_BUDGET_MS = 4
      const deadline = (latticeFlipped || rhChanged || coneLiveDisp)
        ? Infinity
        : (performance.now() + REBUILD_BUDGET_MS)
      // Include ±1 pan overdraw on full passes (matches _loopLo/_loopHi).
      const lo = liveOnly ? Math.max(startIdx, liveIdx) : Math.max(0, startIdx - 1)
      const hi = liveOnly ? Math.min(endIdx, liveIdx) : endIdx + 1
      const prevR = dispRangeRef.current
      // First paint / lattice flip: no prior range — treat none as "entered"
      // (full sync already covers via Infinity deadline on flip).
      const hadRange = prevR.lo >= 0 && prevR.hi >= prevR.lo && !latticeFlipped
      for (let ci = hi; ci >= lo; ci--) {
        const c = candles[ci]
        // _omFootprint historical candles (OpenMarket futures history) carry
        // packed levels and render cells — build their displays too.
        if (!c || (c.isHistorical && !c._omFootprint)) continue
        const warm = !!(c._display && c._displayBucket === dispKeyBucket && c._displayRatio === dispRatio
          && c._displayVersion === (c._dataVersion | 0))
        if (warm) continue
        // Newly scrolled/scaled into the window: always sync — budget skip
        // was blank footprints at the rim (cells+text missing until warm).
        const entered = hadRange && (ci < prevR.lo || ci > prevR.hi)
        if (entered || performance.now() < deadline) { buildDisplay(c, dispKeyBucket, dispRatio); fdCount('dispBuild') }
        // else: skip cells this frame (forEachLevel won't native-flash)
      }
      if (!liveOnly) dispRangeRef.current = { lo, hi }
    }

    // ── Off-screen display warming (drag/scale smoothness) ──
    // buildDisplay caches per candle by (_displayBucket, _displayVersion): a
    // warm candle returns on its first line, a cold one walks its whole native
    // level map. That is the entire reason a drag is smooth when everything is
    // already on screen and clunky when candles arrive from off-screen — each
    // newcomer builds COLD, mid-drag, on the pre-paint path. A bucket change is
    // the same cost at its worst: it invalidates every cached display at once.
    //
    // The data is already loaded, so the work can be done BEFORE it is needed.
    // On idle (never during the drag itself, never while animations are
    // running) build the displays for a margin of candles just outside each
    // edge, at the CURRENT bucket. Scrolling into them then hits the warm path.
    //
    // Deliberately conservative: idle-callback only (falls back to a timeout
    // where rIC is missing — iOS Safari), a hard cap on candles per pass so a
    // long chain can never block a frame, and it re-arms on the next draw so
    // continued scrolling keeps the margin ahead of the viewport. Purely a
    // cache warm — identical output, no visual effect, safe to skip entirely.
    if (!liveOnly && !warmIdleRef.current && !isDraggingChartRef.current) {
      const warm = () => {
        warmIdleRef.current = 0
        if (isDraggingChartRef.current) return   // never compete with a live drag
        const MARGIN = 60, CAP = 40
        let built = 0
        for (let ci = Math.max(0, startIdx - MARGIN); ci < startIdx && built < CAP; ci++) {
          const c = candles[ci]
          if (c && (!c.isHistorical || c._omFootprint)) { buildDisplay(c, dispKeyBucket, dispRatio); built++ }
        }
        for (let ci = endIdx + 1; ci <= Math.min(total - 1, endIdx + MARGIN) && built < CAP; ci++) {
          const c = candles[ci]
          if (c && (!c.isHistorical || c._omFootprint)) { buildDisplay(c, dispKeyBucket, dispRatio); built++ }
        }
      }
      warmIdleRef.current = (typeof requestIdleCallback === 'function')
        ? requestIdleCallback(warm, { timeout: 400 })
        : setTimeout(warm, 120)
    }

    // 1. Grid lines — drawn on the dedicated grid canvas (.footprint-grid-layer,
    // z0) behind the trail (z1) and this cells canvas (z2), in the block just
    // above the transform write. Nothing to draw here.


    // 7a. Flip Zones
    if (!liveOnly && showFlipZonesRef.current && flipZonesRef.current.length > 0) {
      // Use the shared mcapToYUtil (same as the rest of the chart) so zones
      // stay locked to bucket centers across timeframe changes.

      // Build timeToX: given a zone's birthTime, return the X pixel of the
      // candle that birthed it. Lines/bands start at that X (Pine-style).
      const candleArr = chartData.candles || []
      const timeToIdx = new Map()
      for (let i = 0; i < candleArr.length; i++) {
        const t = candleArr[i]?.startTime
        if (t != null) timeToIdx.set(t, i)
      }
      const timeToX = (birthTime) => {
        const idx = timeToIdx.get(birthTime)
        if (idx == null) return null
        if (idx < startIdx || idx > endIdx) return null
        return frame.xMid(idx)
      }

      drawFlipZones(ctx, flipZonesRef.current, frame.yOf, plotWidth, totalHeight, bucketSize, timeToX)
    }

    // 1a2. Volume Profile Value Area — POC / VAH / VAL + band, behind the
    //      footprint cells. Off the visible-range profile (valueAreaRef).
    if (!liveOnly && showValueAreaRef.current && valueAreaRef.current) {
      const va = valueAreaRef.current
      const vc = vaConfigRef.current
      const yPOC = frame.yOf(va.poc), yVAH = frame.yOf(va.vah), yVAL = frame.yOf(va.val)
      const onScreen = (yy) => Number.isFinite(yy) && yy >= -1 && yy <= totalHeight + 1
      ctx.save()
      if (vc.showBand && Number.isFinite(yVAH) && Number.isFinite(yVAL)) {
        ctx.fillStyle = vc.bandColor
        ctx.globalAlpha = 0.07
        ctx.fillRect(0, Math.min(yVAH, yVAL), plotWidth, Math.abs(yVAL - yVAH) || 1)
      }
      const baseVaW = vc.weight || 1
      setCtxFont(ctx, '10px "JetBrains Mono", monospace')   // (perf3)
      ctx.textBaseline = 'middle'
      const drawVaLine = (yy, color, label, dashed, key) => {
        if (!onScreen(yy)) return
        const hot = hoveredVaLineRef.current === key
        // Subtle hover: same colour, same line style (dashed stays dashed, same
        // weight) — just a touch brighter so you can tell which line the
        // right-click will open. No glow, no thickening.
        const baseAlpha = dashed ? 0.6 : 0.9
        ctx.strokeStyle = color
        ctx.lineWidth = baseVaW
        ctx.globalAlpha = hot ? Math.min(1, baseAlpha + 0.25) : baseAlpha
        ctx.setLineDash(dashed ? [4, 4] : [])
        ctx.beginPath(); ctx.moveTo(0, yy); ctx.lineTo(plotWidth, yy); ctx.stroke()
        ctx.setLineDash([])
        ctx.globalAlpha = hot ? 1 : 0.9
        ctx.fillStyle = color
        const tw = ctx.measureText(label).width
        ctx.fillText(label, plotWidth - tw - 6, Math.max(6, Math.min(totalHeight - 6, yy - 6)))
      }
      if (vc.showBounds) { drawVaLine(yVAH, vc.bandColor, 'VAH', true, 'vah'); drawVaLine(yVAL, vc.bandColor, 'VAL', true, 'val') }
      if (vc.showPOC)    { drawVaLine(yPOC, vc.pocColor, 'POC', false, 'poc') }
      ctx.restore()
    }

    // optvol-20261103: Opt Vol Levels — calls green, puts red; weight/alpha by volume.
    if (!liveOnly && optVolRef.current) {
      const ov = optVolRef.current
      const rows = [...(ov.calls || []).map(o => ({ ...o, c: 1 })), ...(ov.puts || []).map(o => ({ ...o, c: 0 }))]
      const maxV = Math.max(1, ...rows.map(o => o.volume || 0))
      ctx.save()
      setCtxFont(ctx, '10px "JetBrains Mono", monospace')
      ctx.textBaseline = 'middle'
      for (const o of rows) {
        const yy = frame.yOf(o.strike)
        if (!Number.isFinite(yy) || yy < -1 || yy > totalHeight + 1) continue
        const k = (o.volume || 0) / maxV
        const color = o.c ? '#22c55e' : '#ef4444'
        ctx.strokeStyle = color; ctx.fillStyle = color
        ctx.globalAlpha = 0.3 + 0.6 * k
        ctx.lineWidth = 1 + 2.5 * k
        ctx.beginPath(); ctx.moveTo(0, yy); ctx.lineTo(plotWidth, yy); ctx.stroke()
        ctx.globalAlpha = 0.95
        ctx.fillText(`${o.strike} ${o.c ? 'C' : 'P'} ${formatCompact(o.volume || 0)}`, 6, Math.max(6, Math.min(totalHeight - 6, yy - 7)))
      }
      ctx.restore()
    }

    // 1a. Churn zones — light boxes behind the footprint.
    if (!liveOnly && showChurnZonesRef.current && churnCfgRef.current.style !== 'background') {
      try {
        // hold-hover-alpha-20261016: the mouse-hovered zone: fill + label at half alpha, border normal.
        const _hk = zoneHoverKeyRef.current
        const _cs = chartData.candles
        const _ivMs = (timeIntervalRef?.current || timeInterval || 60) * 1000
        const _dimFill = _hk ? (z) => {
          if (z.dir !== 'up' && z.dir !== 'down') return false
          const c0 = _cs?.[z.start], c1 = _cs?.[z.end], cn = _cs?.[z.end + 1]
          if (!c0 || !c1) return false
          const t1 = (cn && cn.startTime > c1.startTime ? cn.startTime : c1.startTime + _ivMs) - 1
          return `${c0.startTime}:${t1}:${z.dir === 'up' ? 'b' : 's'}` === _hk
        } : null
        drawChurnZones(ctx, churnZonesRef.current, { startIdx, endIdx, xMid: frame.xMid, yOf: mcapToY, cw, lastIdx: endIdx }, churnCfgRef.current, _dimFill)
      } catch {}
    }
    // hold-pct-big-20261016: which zones are on screen + where their label goes
    // (device px, captured under the zone's own transform). Green (up) zone =
    // span buyers, red (down) zone = span sellers; unresolved zones get none.
    const _zoneLbl = []
    const _absLiqLbl = []   // abs-liq-20261017: [text, x, y, alpha, color, align, maxWidth] device px
    let _absLiqSy = 1
    let _absLiqSx = 1   // absorb-liq6: device px per CSS px in X, for the pan replay shift
    let _zoneSy = 1
    if (!liveOnly && showChurnZonesRef.current && churnCfgRef.current.style !== 'background'
        && dataSource !== 'hyperliquid' && chartData.candles?.length) {
      try {
        const T = ctx.getTransform()
        _zoneSy = Math.abs(T.d) || 1
        const cs = chartData.candles
        const cz = churnCfgRef.current
        const ivMs = (timeIntervalRef?.current || timeInterval || 60) * 1000
        const vis = []
        const rects = []   // hold-label-style-20261016
        const Hm = holdPctRef.current.map
        for (const z of churnZonesRef.current) {
          if (z.dir !== 'up' && z.dir !== 'down') continue
          const stop = z.resolvedIdx ?? (z.expired ? z.lastChurn + (cz.expireBars || 40) : endIdx)
          if (stop < startIdx || z.start > endIdx) continue
          const c0 = cs[z.start], c1 = cs[z.end], cn = cs[z.end + 1]
          if (!c0 || !c1) continue
          const t0 = c0.startTime
          const t1 = (cn && cn.startTime > c1.startTime ? cn.startTime : c1.startTime + ivMs) - 1
          if (!(t1 > t0)) continue
          const side = z.dir === 'up' ? 'b' : 's'
          const key = `${t0}:${t1}:${side}`
          vis.push({ t0, t1, side, key })
          const x0 = frame.xMid(z.start) - cw / 2, x1 = frame.xMid(stop) + cw / 2
          const yA = mcapToY(z.hi), yB = mcapToY(z.lo)
          {
            const ax = T.a * x0 + T.e, bx = T.a * x1 + T.e, ay = T.d * yA + T.f, by = T.d * yB + T.f
            rects.push({ key, x0: Math.min(ax, bx), x1: Math.max(ax, bx), y0: Math.min(ay, by), y1: Math.max(ay, by) })
          }
          const v = Hm.get(key)
          if (!v || !(v.n > 0) || v.hold == null) continue
          const ym = (yA + yB) / 2
          const xm = (x0 + x1) / 2
          _zoneLbl.push(`${v.hold}%`, T.a * xm + T.c * ym + T.e, T.b * xm + T.d * ym + T.f,
                        key === zoneHoverKeyRef.current ? 0.5 : 1)   // hold-hover-alpha-20261016: alpha
        }
        zoneHoldVisRef.current = vis
        zoneHoldRectsRef.current = rects
      } catch {}
    }

    // 1b. ATR Trailing Stop line — drawn BEFORE the footprint cells so the
    //     candles/footprint paint over it (ATR sits behind). Same geometry as
    //     before; only the draw order changed.
    if (!liveOnly && showATRStopRef.current) {
      const stops = atrStopRef.current
      if (stops.length > 0 && sortedMcaps.length > 1) {
        let segBull  = null
        const points = []

        const flushSegment = () => {
          if (points.length < 2) { points.length = 0; return }
          ctx.save()
          ctx.lineWidth   = atrStyleRef.current.weight || 2.5
          ctx.strokeStyle = segBull ? atrStyleRef.current.bullCol : atrStyleRef.current.bearCol
          ctx.shadowColor = segBull ? atrStyleRef.current.bullCol : atrStyleRef.current.bearCol
          ctx.shadowBlur  = 4
          ctx.beginPath()
          ctx.moveTo(points[0].x, points[0].y)
          for (let p = 1; p < points.length; p++) ctx.lineTo(points[p].x, points[p].y)
          ctx.stroke()
          ctx.restore()
          points.length = 0
        }

        for (let i = startIdx; i <= endIdx; i++) {
          const entry = stops[i]
          if (!entry) continue
          const x = frame.xMid(i)
          const y = mcapToY(entry.ts)

          if (segBull !== null && entry.bull !== segBull) {
            points.push({ x, y }) // shared endpoint for continuity
            flushSegment()
            segBull = entry.bull
          }
          if (segBull === null) segBull = entry.bull
          points.push({ x, y })
        }
        flushSegment()
      }
    }

    // 2. Footprint cells
    // Continuous desired size — atlas is fixed at CELL_ATLAS_PX; blit scales.
    // Floor 10 keeps the small-cell look (old 8px floor was too tiny once the
    // column term bound). Soften growth for large cells: RH*0.75 / cw*0.28
    // filled ~¾ of the row before the atlas cap — readable but oversized.
    // Half-row / ~⅕ column leaves padding; atlas still hard-caps at 16.
    const _fsBase = Math.max(10, Math.min(CELL_ATLAS_PX, RH * 0.5, cw * 0.2))
    // cell-text-scale-20261019: up to the 16px cap nothing changes. Once both
    // terms clear the cap (RH >= 32, cw >= 80), keep growing at RH*0.5 up to
    // 24px, bounded so a 3-digit label still fits its half-column; whole px
    // so the Y glide steps cleanly instead of shimmering. Measured widths are
    // cached per text in atlasCssWidth, so this is one map hit per frame.
    let fontSize = _fsBase
    if (_fsBase >= CELL_ATLAS_PX) {
      const _fitW = frame.cw / 2 - 4
      const _fit = CELL_ATLAS_PX_LARGE * _fitW / atlasCssWidth(CELL_ATLAS_FONT_LARGE, '888')
      const _big = Math.floor(Math.min(CELL_ATLAS_PX_LARGE, RH * 0.5, _fit))
      if (_big > CELL_ATLAS_PX) fontSize = _big
    }
    const _cellLarge = fontSize > CELL_ATLAS_PX
    const fontScale = fontSize / (_cellLarge ? CELL_ATLAS_PX_LARGE : CELL_ATLAS_PX)
    const font = CELL_ATLAS_FONT
    // (ycells) ONE Y quantizer for the cell lattice, the Y twin of the pan
    // offset quantizer above. Row edges snap to the device grid ONCE; cell
    // rect, plate, text, outlines and badges all derive from the same two
    // edges, on the same grid snapY puts the candles on. Before: GL cells
    // (antialias:false) snapped each edge on their own, text rounded in CSS
    // px, candles in device px, the forming column didn't snap at all — so
    // during a Y glide each hopped a pixel on a DIFFERENT frame (stutter).
    // Gap: 1 CSS px (≥1 device px) between rows, same as the old
    // rowY+0.5 .. rowY+RH-0.5 extent; plate/outline inset 1 CSS px as before.
    const _yRow = (r) => Math.round(r * RH * dpr) / dpr
    const _gapDev = Math.max(1, Math.round(dpr))
    const _cellGapT = Math.floor(_gapDev / 2) / dpr
    const _cellGapB = (_gapDev - Math.floor(_gapDev / 2)) / dpr
    const _slotInset = _gapDev / dpr
    // Candles are a price scale, not a footprint row. High, low, open and
    // close land on the continuous price→Y map. Row top, row bottom and row
    // centre are bucket edges and are not the print.
    const candleY = (price) => proj.yOfPrice(price)
    // (perf3) Skip when the context already holds it — the assignment forced
    // a style recalc every draw (see utils/canvasFont).
    setCtxFont(ctx, font); ctx.textBaseline = 'middle'

    // Theme — hoisted once per draw frame to avoid re-reading inside the
    // hot per-cell loop. Values mirror themes.js fields. The LUT is a
    // 256-entry RGB lookup built from the theme's gradient (multi-stop
    // or legacy 2-stop grayscale) and includes pre-stringified
    // fillStyles to skip per-cell `rgb(r,g,b)` template concatenation.
    const TH = themeRef.current
    const CELL_LUT_BUY  = cellLutRef.current.buy
    const CELL_LUT_SELL = cellLutRef.current.sell
    const TEXT_LIGHT_THRESHOLD = TH.cell.textLightThreshold
    const TEXT_ON_DARK   = TH.cell.textOnDark
    const TEXT_ON_LIGHT  = TH.cell.textOnLight
    const IMBALANCE_BUY  = TH.imbalance.buy
    const IMBALANCE_SELL = TH.imbalance.sell
    // Atlas buckets. Weight 500 only at the 10px floor; 11-16px stays 200.
    // Same colours (imbalance green/red included). Scale at blit, no outline.
    const _cellAtlasFont = _cellLarge ? CELL_ATLAS_FONT_LARGE : fontSize === 10 ? CELL_ATLAS_FONT_HEAVY : CELL_ATLAS_FONT
    const _bkText     = atlasBucket(_cellAtlasFont, TEXT_ON_DARK)
    const _bkTextLite = atlasBucket(_cellAtlasFont, TEXT_ON_LIGHT)
    const _bkImbBuy   = atlasBucket(_cellAtlasFont, IMBALANCE_BUY)
    const _bkImbSell  = atlasBucket(_cellAtlasFont, IMBALANCE_SELL)
    // Half-column minus the 4px the blit insets from the centre. That span
    // is the label content width: buy grows left from center-4, sell right
    // from center+4. Compared width is the blit width (atlas cssW * scale).
    // Wider than the cell: first glyph plus one dot per remaining digit
    // (234 -> 2.., 45 -> 4.). The atlas font is monospace, so a short
    // string of the same length measures the same as the full number and
    // atlasCssWidth(short) <= max used to skip every placeholder. Dots
    // are packed to half an em. That run stays until it itself is wider.
    const _cellTextMaxW = frame.cw / 2 - 4
    const _cellLabelW = (t) => atlasCssWidth(_cellAtlasFont, t) * fontScale
    const _advPx = (ch) => {
      const w = atlasCssWidth(_cellAtlasFont, ch) - 8
      return (w > 0 ? w : 1) * fontScale
    }
    const _dotPx = _advPx('.') * 0.5
    const _padDev = 4 * dpr
    const _glyphCache = new Map()
    const _glyphOf = (bk, ch) => {
      const k = bk.color + '\n' + ch
      let v = _glyphCache.get(k)
      if (v) return v
      const sp = atlasText(bk, ch)
      v = {
        img: sp.img,
        sx: sp.sx + _padDev,
        sy: sp.sy,
        sw: Math.max(1, sp.sw - _padDev * 2),
        sh: sp.sh,
        _cssH: sp._cssH,
      }
      _glyphCache.set(k, v)
      return v
    }
    const _dotCache = new Map()
    const _dotOf = (bk) => {
      let v = _dotCache.get(bk.color)
      if (v) return v
      const g = _glyphOf(bk, '.')
      const keep = Math.max(1, g.sw * 0.5)
      v = {
        img: g.img,
        sx: g.sx + (g.sw - keep) * 0.5,
        sy: g.sy,
        sw: keep,
        sh: g.sh,
        _cssH: g._cssH,
      }
      _dotCache.set(bk.color, v)
      return v
    }
    const _paintSprite = (sp, x, y, dw, dh, animating) => {
      if (!(dw > 0) || !(dh > 0)) return
      if (batchCells && !animating) {
        _spriteBatch.push(sp, x, y, dw, dh)
        return
      }
      let dx = x, w = dw
      if (xCameraActive && Math.abs(cameraScaleX - 1) > 1e-6) {
        w = dw / cameraScaleX
        dx = x + dw * 0.5 - w * 0.5
      }
      ctx.drawImage(sp.img, sp.sx, sp.sy, sp.sw, sp.sh, dx, y, w, dh)
    }
    // side < 0: label's right edge at anchorX (buy). side > 0: left edge (sell).
    const _paintCellLabel = (bk, raw, anchorX, cellY, cellH, side, animating) => {
      if (!raw) return
      if (_cellLabelW(raw) <= _cellTextMaxW) {
        const sp = atlasText(bk, raw)
        const dw = sp._cssW * fontScale
        const dh = sp._cssH * fontScale
        const x = side < 0 ? _xr(anchorX - dw) : _xr(anchorX)
        const y = cellY + Math.round((cellH - dh) * 0.5 * dpr) / dpr
        _paintSprite(sp, x, y, dw, dh, animating)
        return
      }
      if (cmeExactRef.current) {
        // cmeexact: shrink the whole number to fit, never abbreviate.
        const sp = atlasText(bk, raw)
        const k = _cellTextMaxW / (sp._cssW * fontScale)
        if (!(k > 0)) return
        const dw = sp._cssW * fontScale * k
        const dh = sp._cssH * fontScale * k
        const x = side < 0 ? _xr(anchorX - dw) : _xr(anchorX)
        const y = cellY + Math.round((cellH - dh) * 0.5 * dpr) / dpr
        _paintSprite(sp, x, y, dw, dh, animating)
        return
      }
      const nDots = raw.length - 1
      if (nDots <= 0) return
      const head = raw.charAt(0)
      const digW = _advPx(head)
      const total = digW + nDots * _dotPx
      if (total > _cellTextMaxW) return
      const dig = _glyphOf(bk, head)
      const dh = dig._cssH * fontScale
      const y = cellY + Math.round((cellH - dh) * 0.5 * dpr) / dpr
      let x = side < 0 ? _xr(anchorX) - total : _xr(anchorX)
      _paintSprite(dig, x, y, digW, dh, animating)
      x += digW
      const dot = _dotOf(bk)
      for (let i = 0; i < nDots; i++) {
        _paintSprite(dot, x, y, _dotPx, dh, animating)
        x += _dotPx
      }
    }

    // De-dup fillStyle assignments: within a cell the buy and sell halves
    // share the same intensity color, and runs of equal-volume cells repeat
    // it — so skip redundant ctx.fillStyle writes (each one re-parses the
    // color string, a real cost at thousands of cells/frame). Animated live
    // cells set fillStyle directly inside their own save/restore; curFill is
    // reset after each restore so the guard can't drift out of sync.
    let curFill = ''
    const setFill = (s) => { if (s !== curFill) { ctx.fillStyle = s; curFill = s } }
    // Full pass batches; live pass paints immediately (see the batching note
    // at the refs). setFill's dedupe becomes irrelevant on the batched path —
    // collection never touches ctx state at all.
    const batchCells = !liveOnly
    const _fillBatch = cellFillBatchRef.current
    const pushFill = (color, x, y, w, h) => {
      let q = _fillBatch.get(color)
      if (!q) { q = []; _fillBatch.set(color, q) }
      q.push(x, y, w, h)
    }
    // Plates go to their own batch → plateLayer (z0), under the trail.
    const _plateBatch = cellPlateBatchRef.current
    for (const q of _plateBatch.values()) q.length = 0
    const pushPlate = (color, x, y, w, h) => {
      let q = _plateBatch.get(color)
      if (!q) { q = []; _plateBatch.set(color, q) }
      q.push(x, y, w, h)
    }
    const _spriteBatch = cellSpriteBatchRef.current
    const _strokeBatch = cellStrokeBatchRef.current
    const _wickBatch   = cellWickBatchRef.current

    // Skip the per-cell volume numbers when rows are too short to render them
    // legibly: the 8px-floor font overflows into neighboring rows and reads as
    // a smear, while still paying getTextSprite + drawImage per cell. This is
    // exactly the dense grid that lags; readable zooms (RH ≥ 10) keep the text.
    const cellTextFits = RH >= 8
    // Visual cw for text LOD; plate is on for every footprint cell level.
    const lodCw = cw * (xCameraActive ? cameraScaleX : 1)
    // (wick) Tight spacing → one opaque stick per candle (see wickColorOf).
    // The stick rides the stride-9 wick batch as a BODY with a zero-length
    // wick, so GL, the 2D fallbacks and the pan replay all draw it as-is.
    // X snapped to the device grid; 1 CSS px wide (≥1 device px).
    const _wickTight = lodCw < WICK_TIGHT_CW
    const _wickTightCol = wickColorOf(TH, true)
    // ── NO X PIXEL-SNAP WHILE GLIDING. ─────────────────────────────────────
    // Snapping rounds column positions in MESH space, and the X camera then
    // stretches them. The mesh changes every frame or two of a glide (scale
    // swinging ~1.04–1.20), so each column's rounding landed differently
    // frame to frame — columns hopping up to a pixel independently: the X
    // glitch. Anything drawn at float positions (the dashed lines) was
    // smooth. While the camera is on, draw X at exact positions; at rest
    // everything snaps exactly as before (crisp when still).
    const _xSnapOn = !xCameraActive
    const _xr = (v) => (_xSnapOn ? Math.round(v) : v)
    const _stickW = Math.max(1, Math.round(dpr)) / dpr
    const _stickL = (x) => (_xSnapOn ? Math.round(x * dpr - _stickW * dpr / 2) / dpr : x - _stickW / 2)
    const pushWickStick = (batch, x, yA, yB, col) => {
      const L = _stickL(x)
      const t = Math.min(yA, yB)
      batch.push(L, t, t, col, L, t, _stickW, Math.max(1 / dpr, Math.abs(yB - yA)), col)
    }
    const cellSlotBg = !asCandle
    // Text stays on through a wheel now: nothing is stretched, so there is no
    // unstretch fight and no remesh size pop to hide.
    const cellTextOn = lodCw > FP_LOD_TEXT_PX && cellTextFits
    // ── LOD BAND PROBE. Delete once answered. ─────────────────────────────
    // Symptom: fine zoomed tight, fine zoomed far out, SLUGGISH in between.
    // A per-frame cost gets monotonically worse as columns multiply — a
    // U-shape means a BAND where something expensive switches on.
    //
    // Three thresholds live in that middle:
    //   cw vs FP_LOD_TEXT_PX (32)  → cell text appears
    //   lodBlend 0..1              → cells fade to candles; anything strictly
    //                                between 0 and 1 means EVERY cell and
    //                                candle draws alpha-blended
    //   candleTier                 → full → wick → aggregate
    //
    // Throttled to 400ms and printed only when a value actually changes, so
    // a slow wheel from tight to wide gives a readable ladder rather than a
    // flood. Wheel until it feels worst and read lodBlend on those lines.
    if (typeof window !== 'undefined' && !liveOnly) {
      const _lp = window.__lod || (window.__lod = { at: 0, k: '' })
      const _k = `${lodBlend.toFixed(2)}|${_candleTier}|${cellTextOn}|${_cellsGone}`
      const _now = performance.now()
      if (_k !== _lp.k && _now - _lp.at > 400) {
        _lp.k = _k; _lp.at = _now
        if (dbgOn()) dbg(`[lod] cw=${cw.toFixed(2)} lodCw=${lodCw.toFixed(2)} ` +
          `blend=${lodBlend.toFixed(3)} tier=${_candleTier} text=${cellTextOn} ` +
          `cellsGone=${_cellsGone} RH=${RH.toFixed(2)} cols=${visibleCount}`)
      }
    }

    // ─── Wallet overlay for this frame ───
    // Badges are COLLECTED here and painted after the batch flush below;
    // painted inline they land under the batched cell fills (same reason
    // the absorption outlines are deferred). Empty unless a wallet is
    // selected, so the unselected path costs one null check per frame.
    let _badges = null
    {
      const _selAddr = getChartWallet()
      const st = walletOverlayRef.current
      if (_selAddr && data.rawTradesRef?.current) {
        const buf = data.rawTradesRef.current
        const wid = walletIdFor(_selAddr)
        // Key on THIS WALLET's print count, not the buffer's length. The
        // buffer grows on every trade on the token, so buf.length made the
        // key change constantly and forced a full O(n) rescan every 200ms
        // even while the selected wallet sat idle — most of the cost, none
        // of the benefit. The registry's buys+sells changes only when they
        // actually trade, which is exactly when the overlay is stale.
        const _wm = getWalletStats(data.currentToken)?.get(_selAddr)
        const _prints = _wm ? (_wm.buys + _wm.sells) : 0
        // Lattice identity in the key: the ratio in log (bucketSize is the top
        // row's absolute width there and moves with rangeMax), the bucket in linear.
        const key = `${wid}|${dispRatio || bucketSize}|${_prints}|${chartWalletVersion()}`
        const now = performance.now()
        // A SHRINKING buffer is the other staleness source: trimBefore
        // prunes old trades at the retention floor, and the registry is a
        // running fold that never decreases — so the print count alone
        // would keep serving badges for trades no longer in the buffer.
        const _pruned = buf.length < (st.len || 0)
        if ((st.key !== key || _pruned) && (st.map === null || now - st.at > 200)) {
          st.map = buildWalletOverlay(buf, candles, wid, bucketSize, dispRatio)
          st.key = key; st.at = now; st.len = buf.length
        }
        if (st.map) _badges = []
      } else if (st.map) {
        walletOverlayRef.current = { key: '', map: null, at: 0, len: 0 }
      }
    }
    const _ovMap = _badges ? walletOverlayRef.current.map : null

    // ─── Pan geometry cache: replay, or fall through to the loop ───
    // Every input the loop reads, except panOffsetPx. y inputs are
    // topMcap/bucketSize/RH (the DOM scrolls the canvas, so scroll is not
    // one); x inputs are the window and the candle width; content inputs
    // are the data version, the excluded live index, text visibility, and
    // the wallet overlay. Checked ONLY mid-drag.
    let _geomHit = false
    let _overlayReplay = null
    if (batchCells) {
      const gk = `${chartData.structuralVersion}|${startIdx}|${endIdx}|${cw}|${pxPerCandle}|${plotWidth}`
               + `|${topMcap}|${bucketSize}|${RH}|${liveIdx}|${cellTextOn ? 1 : 0}|${fontSize}|${asCandle ? 1 : 0}|${cellSlotBg ? 1 : 0}|${churnBgRef.current ? churnBgRef.current.key : ''}`
               + `|${containerW}|${totalHeight}|${walletOverlayRef.current.key}|${_candleTier}|${_useCandleAgg ? 1 : 0}|${absHoverKeyRef.current ?? ''}`
      const g = panGeomRef.current
      // Drag OR mid X-ease FROM freeze: membership/cw stable → replay mesh.
      const _geomReplay = !!(scrollRef.current?.isDraggingRef?.current)
      if (_geomReplay && g.key === gk && g.fill) {
        const dx = panOffsetPx - g.pan
        for (const [color, q] of g.fill) {
          let dst = _fillBatch.get(color)
          if (!dst) { dst = []; _fillBatch.set(color, dst) }
          for (let i = 0; i < q.length; i += 4) dst.push(q[i] + dx, q[i + 1], q[i + 2], q[i + 3])
        }
        if (g.plates) {
          for (const [color, q] of g.plates) {
            let dst = _plateBatch.get(color)
            if (!dst) { dst = []; _plateBatch.set(color, dst) }
            for (let i = 0; i < q.length; i += 4) dst.push(q[i] + dx, q[i + 1], q[i + 2], q[i + 3])
          }
        }
        const sp = g.sprite
        if (sp) for (let i = 0; i < sp.length; i += 5) _spriteBatch.push(sp[i], sp[i + 1] + dx, sp[i + 2], sp[i + 3], sp[i + 4])
        const st = g.stroke
        for (let i = 0; i < st.length; i += 6) _strokeBatch.push(st[i], st[i + 1], st[i + 2] + dx, st[i + 3], st[i + 4], st[i + 5])
        const wk = g.wick
        for (let i = 0; i < wk.length; i += 9) _wickBatch.push(wk[i] + dx, wk[i + 1], wk[i + 2], wk[i + 3], wk[i + 4] + dx, wk[i + 5], wk[i + 6], wk[i + 7], wk[i + 8])
        // absorb-liq6: Liq labels ride the pan replay too (device px, so dx * sx).
        if (g.liq && g.liq.length) {
          const ldx = dx * (g.liqSx || 1)
          for (let i = 0; i < g.liq.length; i += 7) _absLiqLbl.push(g.liq[i], g.liq[i + 1] + ldx, g.liq[i + 2], g.liq[i + 3], g.liq[i + 4], g.liq[i + 5], g.liq[i + 6])
          _absLiqSy = g.liqSy || 1
        }
        if (g.badges && g.badges.length) {
          if (!_badges) _badges = []
          const bd = g.badges
          for (let i = 0; i < bd.length; i += 7) _badges.push(bd[i] + dx, bd[i + 1], bd[i + 2], bd[i + 3], bd[i + 4], bd[i + 5], bd[i + 6])
        }
        // Footprint OHLC overlays (slim center candles) were snapshotted AFTER
        // the cell loop and never replayed — cells slid on the geom cache while
        // overlays rebuilt with Math.round(cx) every drag frame (center candle pop).
        if (g.overlay && g.overlay.length) {
          _overlayReplay = []
          const ov = g.overlay
          for (let i = 0; i < ov.length; i += 9) {
            _overlayReplay.push(ov[i] + dx, ov[i + 1], ov[i + 2], ov[i + 3], ov[i + 4] + dx, ov[i + 5], ov[i + 6], ov[i + 7], ov[i + 8])
          }
        }
        _geomHit = true
      } else {
        g.key = gk; g.pan = panOffsetPx; g.fill = null; g.plates = null; g.overlay = null
      }
    }

    // ─── Vela aggregate candle LOD (sub-pixel column sticks) ───
    // When cw < 1 and cells are gone, collapse bars that share a pixel
    // column into high-low sticks so draw cost tracks screen width.
    // Excludes liveIdx (forming candle stays on the live layer).
    if (_useCandleAgg && !_geomHit && topMcap && sortedMcaps.length > 1) {
      const _dprLod = window.devicePixelRatio || 1
      // Same ±1 pan overdraw as the main column loop (inclusive startIdx).
      const aLo = Math.max(0, startIdx - 1)
      const aHi = Math.min(total - 1, endIdx + 1)
      const pushAggRanges = []
      if (liveIdx < aLo || liveIdx > aHi) {
        pushAggRanges.push([aLo, aHi])
      } else {
        if (aLo <= liveIdx - 1) pushAggRanges.push([aLo, liveIdx - 1])
        if (liveIdx + 1 <= aHi) pushAggRanges.push([liveIdx + 1, aHi])
      }
      for (let ri = 0; ri < pushAggRanges.length; ri++) {
        const [a0, a1] = pushAggRanges[ri]
        const sticks = aggregateCandleColumns(candles, a0, a1, frame.xMid, frame.yOf)
        for (let si = 0; si < sticks.length; si++) {
          const s = sticks[si]
          const yH = snapY(frame.yOf(s.hi), _dprLod)
          const yL = snapY(frame.yOf(s.lo), _dprLod)
          if (yH > totalHeight && yL > totalHeight) continue
          if (yH < 0 && yL < 0) continue
          // (wick) Sub-pixel columns are always tight: one opaque stick per
          // column in the theme wick colour. The old 2px bull/bear body
          // covered the neighbour's grey core → pink/purple stripes.
          if (batchCells) {
            pushWickStick(_wickBatch, s.x, yH, yL, _wickTightCol)
          } else {
            ctx.globalAlpha = 1
            ctx.fillStyle = _wickTightCol
            ctx.fillRect(_stickL(s.x), Math.min(yH, yL), _stickW, Math.max(1 / dpr, Math.abs(yL - yH)))
          }
        }
      }
    }

    // Named pan/replay OVERDRAW (±1 column) — separate from inclusive winSize.
    // startIdx already means "first of N on-screen candles"; these neighbors
    // are usually off-canvas/clipped, but panOffsetPx ∈ [0, cw) slides one
    // column into view. `!candle` handles startIdx-1 < 0 and endIdx+1 >= total.
    // MUST be on every full miss pass (not only batchCells): inclusive
    // startIdx = endIdx-N+1 no longer silently supplies the extra column the
    // old endIdx-N formula did, so candle/wick paths without overdraw stopped
    // at the viewport edge and popped instead of sliding in.
    // absorb-liq7: Liq label placement, shared by the outline pass and the live
    // column. Colour AND side both follow the move direction: up = buy colour on
    // the RIGHT edge, down = sell colour on the LEFT edge. The rightmost (live)
    // candle puts both on the right (open space). Clear-spot check: a right-edge
    // label sits over the right neighbour's BUY numbers (its left half), a
    // left-edge label over the left neighbour's SELL numbers (its right half).
    // If the default spot has a number there, try the other edge, then the row
    // above/below on the default edge; nothing free -> default spot. Neighbour
    // rows are looked up only for candles that carry a label (a handful).
    const _liqNb = new Map()
    const _liqBusyRows = (idx, wantBuy) => {
      const k = idx * 2 + (wantBuy ? 1 : 0)
      let s = _liqNb.get(k)
      if (s) return s
      s = new Set()
      const c = candles[idx]
      if (c && !(c.isHistorical && (!c._omFootprint || asCandle))) {
        forEachDispLevel(c, (m, bv, sv) => { if (wantBuy ? bv > 0 : sv > 0) s.add(frame.rowOfLevel(m)) })
      }
      _liqNb.set(k, s)
      return s
    }
    const _pushAbsLiq = (cIdx, hit, T, slotY, slotH, centerX, halfWidth, isLast) => {
      const lq = hit.liq
      const sy = Math.abs(T.d) || 1
      _absLiqSy = sy
      _absLiqSx = T.a || 1
      const up = lq.dir === 'up'
      const sec = lq.sec < 1 ? '<1' : String(Math.round(lq.sec))
      const gap = 2 * sy
      const row = frame.rowOfLevel(hit.mcap)
      const def = (up || isLast) ? 1 : -1
      const busy = (side, r) => {
        if (r < 0 || r >= sortedMcaps.length) return true
        if (side > 0 && isLast) return false   // nothing to the right of the live candle
        return _liqBusyRows(cIdx + side, side > 0).has(r)
      }
      let side = def, r = row
      if (busy(def, row)) {
        if (!isLast && !busy(-def, row)) side = -def
        else if (!busy(def, row - 1)) r = row - 1
        else if (!busy(def, row + 1)) r = row + 1
      }
      const cy = r === row ? slotY + slotH / 2 : (_yRow(r) + _yRow(r + 1)) / 2
      const xL = T.a * (centerX - halfWidth) + T.c * cy + T.e
      const xR = T.a * (centerX + halfWidth) + T.c * cy + T.e
      const cfg = absConfigRef.current
      _absLiqLbl.push(`Liq ${up ? '\u2191' : '\u2193'}${sec}s`,
        side > 0 ? Math.max(xL, xR) + gap : Math.min(xL, xR) - gap,
        T.b * centerX + T.d * cy + T.f,
        absHoverKeyRef.current != null && candles[cIdx]?.startTime === absHoverKeyRef.current ? 0.5 : 1,
        up ? cfg.buyColor : cfg.sellColor,
        side > 0 ? 'left' : 'right',
        isLast && side > 0 ? Infinity : Math.abs(xR - xL))   // open space right of the live candle
    }
    const _churnBgMap = churnBgRef.current ? churnBgRef.current.map : null
    // ── TPO render pass (tpo-20261103) ─────────────────────────────────
    // Same frame.xLeft / candleY projection as candles, so pan, zoom, glide
    // and the right price axis are the chart's own. Per daily session: 30m
    // periods lettered from the session's first bar, rows = auto tick group.
    if (_tpo && !liveOnly && candles.length) {
      const kind = tpoKindRef.current
      const _pxRow = 12
      const _tpoLo = Math.max(0, startIdx - 1), _tpoHi = Math.min(candles.length - 1, endIdx + 1)
      // Walk back to the first visible session's start (bounded).
      let s0 = _tpoLo
      while (s0 < _tpoHi && !candles[s0]) s0++
      if (candles[s0]) {
        const k0 = tpoSessionKey(candles[s0].startTime, kind)
        for (let b = 0; s0 > 0 && b < 6000 && candles[s0 - 1] && tpoSessionKey(candles[s0 - 1].startTime, kind) === k0; b++) s0--
      }
      const W = containerW || canvas.clientWidth || 0
      const lw = Math.max(5, Math.min(10, frame.cw))
      ctx.save(); ctx.globalAlpha = 1; ctx.setLineDash([])
      ctx.textBaseline = 'middle'
      let i = s0
      while (i <= _tpoHi) {
        const c0 = candles[i]; if (!c0) { i++; continue }
        const key = tpoSessionKey(c0.startTime, kind)
        let j = i
        while (j + 1 < candles.length && candles[j + 1] && tpoSessionKey(candles[j + 1].startTime, kind) === key) j++
        // tick grouping from this session's on-screen price scale
        let hi = -Infinity, lo = Infinity
        for (let q = i; q <= j; q++) { const c = candles[q]; if (c.high_mcap > hi) hi = c.high_mcap; if (c.low_mcap < lo) lo = c.low_mcap }
        const spanPx = Math.abs(candleY(lo) - candleY(hi))
        const rs = tpoNiceStep(spanPx > 0 ? (hi - lo) * _pxRow / spanPx : hi * 0.001)
        const prof = rs > 0 ? tpoBuildProfile(candles, i, j, rs) : null
        if (prof) {
          // tpofix-20261103: anchor at the session's first column, but a
          // session spans many bars on short timeframes (1m = 1440), so its
          // first column is usually far off-screen left while the profile is
          // only ~48 letters wide - that drew everything out of view (blank
          // chart). Pin the profile inside [left edge, session end].
          let maxLen = 0
          for (const L of prof.rows.values()) if (L.length > maxLen) maxLen = L.length
          const xEnd = frame.xLeft(j) + frame.cw
          const x0 = Math.min(Math.max(frame.xLeft(i), 0), Math.max(frame.xLeft(i), xEnd - maxLen * lw))
          const yTop = (r) => candleY((r + 1) * rs), yBot = (r) => candleY(r * rs)
          const rh = Math.abs(yBot(prof.poc) - yTop(prof.poc))
          const useLetters = tpoLettersRef.current && rh >= 7 && lw >= 6
          ctx.font = `${Math.max(7, Math.min(11, rh - 1))}px ui-monospace, Menlo, Consolas, monospace`
          for (const r of prof.keys) {
            const L = prof.rows.get(r), yt = yTop(r), yb = yBot(r)
            const col = r === prof.poc ? '#ef4444' : (r >= prof.vaLo && r <= prof.vaHi) ? '#14b8a6' : null
            for (let k = 0; k < L.length; k++) {
              const cx = x0 + k * lw
              if (cx > xEnd || cx > W) break
              if (useLetters) { ctx.fillStyle = col || '#8a90a0'; ctx.fillText(TPO_LETTERS[L[k]], cx, (yt + yb) / 2) }
              else { ctx.fillStyle = col || '#4b5160'; ctx.fillRect(cx, Math.min(yt, yb) + 0.5, lw - 1, Math.max(1, rh - 1)) }
            }
          }
          // TVAH / TVAL dotted lines extending right
          ctx.setLineDash([2, 3]); ctx.lineWidth = 1; ctx.strokeStyle = '#14b8a6'
          for (const y of [yTop(prof.vaHi), yBot(prof.vaLo)]) {
            const yy = Math.round(y) + 0.5
            ctx.beginPath(); ctx.moveTo(x0, yy); ctx.lineTo(W, yy); ctx.stroke()
          }
          ctx.strokeStyle = '#ef4444'
          const yp = Math.round((yTop(prof.poc) + yBot(prof.poc)) / 2) + 0.5
          ctx.beginPath(); ctx.moveTo(x0, yp); ctx.lineTo(Math.min(xEnd, W), yp); ctx.stroke()
          ctx.setLineDash([])
          // session stats under the profile
          const dp = rs >= 1 ? 0 : Math.min(6, Math.max(0, Math.round(-Math.log10(rs)) + 1))
          const fm = (v) => v.toFixed(dp)
          // tpostats-20261103: under the profile when it fits; otherwise at the
          // front (left) of the profile, clamped inside the visible chart.
          const _vH = canvas.clientHeight || 0, _bH = 33
          const _under = candleY(prof.lo) + 12
          const _fits = _under + _bH - 11 <= _vH
          const sy = _fits ? _under : Math.max(10, Math.min(_vH - _bH + 8, candleY(prof.hi) + 10))
          if (xEnd > 0 && x0 < W) {
            ctx.font = '9px ui-monospace, Menlo, Consolas, monospace'
            const lines = [[`POC ${fm((prof.poc + 0.5) * rs)}`, '#ef4444'], [`VAH ${fm((prof.vaHi + 1) * rs)}  VAL ${fm(prof.vaLo * rs)}`, '#14b8a6'],
              [`RNG ${fm(prof.hi - prof.lo)}  IB ${fm(prof.ibHi - prof.ibLo)}  TPO ${prof.tot}`, '#8a90a0']]
            const _bw = Math.max(...lines.map(([t]) => ctx.measureText(t).width))
            const sx = _fits ? Math.max(x0, 2) : Math.max(2, x0 - 10 - _bw)
            lines.forEach(([t, cl], n) => { ctx.fillStyle = cl; ctx.fillText(t, sx, sy + n * 11) })
          }
        }
        i = j + 1
      }
      ctx.restore()
    }
    const _loopLo = _geomHit ? startIdx : startIdx - 1
    const _loopHi = _geomHit ? endIdx   : endIdx + 1
    for (let candleIdx = _geomHit ? _loopHi + 1 : _loopLo; candleIdx <= _loopHi; candleIdx++) {
      const candle = candles[candleIdx]
      // Live-layer exclusivity: volume bars/text for the forming candle paint
      // ONLY on the live layer. Full pass still lays its SLOT PLATES so the
      // order trail can sit between plate (z0) and cells (z2+).
      const isLiveCol = candleIdx === liveIdx
      if (liveOnly && !isLiveCol) continue
      const platesOnly = !liveOnly && isLiveCol
      const logicalIdx = candleIdx - startIdx
      const idxFromRight = endIdx - candleIdx
      const candleX = frame.xLeft(candleIdx)
      const centerX = frame.xMid(candleIdx)
      const halfWidth = frame.cw / 2

      // Ghost cell - blank space
      if (!candle) continue
      if (_tpo) continue   // tpo-20261103: profiles replace candles (drawn below)

      // Historical OHLCV candlestick. _omFootprint history candles fall
      // through to the CELL path below instead — except in candle/LOD mode,
      // where cells are skipped chart-wide and they'd otherwise vanish; there
      // they draw this candlestick like any other historical bar.
      if (candle.isHistorical && (!candle._omFootprint || asCandle) && topMcap) {
        if (platesOnly) continue
        // Aggregate LOD already painted column sticks for this zoom.
        if (_useCandleAgg) continue
        // Price Y. Not the bucket row that contains the print.
        ctx.globalAlpha = 1                     // bodies/wicks at full strength
        const yHigh  = candleY(candle.high_mcap)
        const yLow   = candleY(candle.low_mcap)
        const yOpen  = candleY(candle.open_mcap)
        const yClose = candleY(candle.close_mcap)
        if (yHigh > totalHeight && yLow > totalHeight) continue
        if (yHigh < 0 && yLow < 0) continue
        const isBullish = candle.close_mcap >= candle.open_mcap
        // SAME palette as the live candlestick pass below — history and live
        // read as one continuous chart, no color seam at the boundary. (The
        // themes' historical* fields are retained as legacy fallbacks only.)
        const bodyColor = (isBullish ? TH.candle.bullBody : TH.candle.bearBody)
          || (isBullish ? TH.candle.historicalBullBody : TH.candle.historicalBearBody)
        const strokeColor = (isBullish ? TH.candle.bullStroke : TH.candle.bearStroke)
          || TH.candle.historicalStroke
        // Wick — black halo at +2px, then colored stroke on top. Same wick
        // color the live pass picks for this mode, so the historical/live
        // boundary doesn't show a styling seam.
        const wickCol = wickColorOf(TH, false, candleStyled, strokeColor)
        // Both strokes on ONE half-pixel center. Odd line widths (1 and 3)
        // straddle their center, so a fractional centerX antialiases the
        // 3px halo and the 1px core across different pixel boundaries —
        // the black then sits asymmetrically around the colored wick and
        // reads as an outline following its own geometry. Snapping makes
        // them concentric and crisp.
        // Black halo restored (per Levi). Both strokes share ONE snapped
        // half-pixel center (wxH) and identical endpoints, so the 3px black
        // sits symmetrically around the 1px core instead of leaning to one
        // side as it did pre-snap.
        const _dprH = window.devicePixelRatio || 1
        // (drag-shimmer) No Y snap mid drag-fit ease: per-edge rounding against
        // a moving scale flips edges 1 device px per frame. Crisp on arrival.
        const _ySnapOn = !yDragFitRafRef.current
        yUnsnappedPaintRef.current = !_ySnapOn
        const _sy = (v) => (_ySnapOn ? snapY(v, _dprH) : v)
        const yHighS = _sy(yHigh)
        const yLowS = _sy(yLow)
        const yOpenS = _sy(yOpen)
        const yCloseS = _sy(yClose)
        if (_wickTight) {
          // (wick) Tight: one opaque stick, no halo, no body.
          if (batchCells) { pushWickStick(_wickBatch, centerX, yHighS, yLowS, _wickTightCol); continue }
          ctx.fillStyle = _wickTightCol
          ctx.fillRect(_stickL(centerX), Math.min(yHighS, yLowS), _stickW, Math.max(1 / dpr, Math.abs(yLowS - yHighS)))
          continue
        }
        const wxH = _xSnapOn ? Math.round(centerX) + 0.5 : centerX
        const bodyTop = Math.min(yOpenS, yCloseS)
        const bodyH = _useWickOnly ? 0 : Math.max(2, Math.abs(yCloseS - yOpenS))
        const bodyW = _useWickOnly ? 0 : Math.max(2, cw * 0.65)
        if (batchCells) {
          // Deferred like the cells, so the pan replay (see panGeomRef)
          // covers candle-mode columns too — without this a replayed frame
          // would show footprints and drop every historical candle.
          _wickBatch.push(wxH, yHighS, yLowS, wickCol, _xr(wxH - bodyW / 2), bodyTop, _xr(bodyW), bodyH, bodyColor)
          continue
        }
        ctx.strokeStyle = '#000'
        ctx.lineWidth   = 3
        ctx.beginPath(); ctx.moveTo(wxH, yHighS); ctx.lineTo(wxH, yLowS); ctx.stroke()
        ctx.strokeStyle = wickCol
        ctx.lineWidth   = 1
        ctx.beginPath(); ctx.moveTo(wxH, yHighS); ctx.lineTo(wxH, yLowS); ctx.stroke()
        if (!_useWickOnly) {
          ctx.fillStyle = bodyColor
          // Body is centered on the SAME snapped axis as the wick (wxH), not
          // the raw centerX — otherwise the crisp half-pixel wick sat up to
          // 0.5px off the body's center, and which way it leaned changed with
          // pan offset. One vertical axis per candle.
          // Body: FILL ONLY. The stroked outline that used to follow made
          // candle mode (and the historical/futures branch) look different
          // from the candles the footprint fades into, which draw via the
          // overlay pass with no body stroke. One renderer's look now.
          ctx.fillRect(Math.round(wxH - bodyW / 2), bodyTop, Math.round(bodyW), bodyH)
        }
        continue
      }

      // Live footprint candle
      // In 'candle' mode — and under the LOD threshold — we skip per-row
      // buy/sell cells entirely; the OHLC overlay below draws a proper
      // candlestick instead.
      if (asCandle) continue
      // LOD fade: FRONT-loaded (1-b)^2. Cells are ~6500 rects; candles are ~65.
      // The old 1-b^2 kept cells near-opaque deep into the fade (b=0.86 still
      // drew them at alpha 0.26), so the whole band paid for BOTH layers.
      if (!platesOnly) ctx.globalAlpha = (1 - lodBlend) * (1 - lodBlend)

      const candleMaxVolume = candleMaxLevelVolume(candle, dispKeyBucket, dispRatio)
      if (candleMaxVolume === 0) continue

      // ─── Live-cell animation bookkeeping ───
      // Only the rightmost candle is "live" (still receiving trades).
      // Detect when the live bar rolls forward and reset all animation
      // state — we only animate updates within the current bar.
      const isLiveCandle = candleIdx === total - 1
      const nowAnim = isLiveCandle ? performance.now() : 0
      if (isLiveCandle) {
        if (lastLiveCandleStartRef.current !== candle.startTime) {
          lastLiveCandleStartRef.current = candle.startTime
          cellAnimsBuyRef.current.clear()
          cellAnimsSellRef.current.clear()
          prevLiveBuyRef.current.clear()
          prevLiveSellRef.current.clear()
        }
      }

      // Hoisted: one Map lookup per CANDLE, not per level. At ~20 levels a
      // candle this is the difference between 500 and 10,000 gets per frame.
      const absCfg = absConfigRef.current
      const absHit = showAbsorptionRef.current ? absorptionRef.current?.get(candle) : null
      // Point of control: the level with the most TOTAL volume in this bar.
      // Tracked before the off-lattice skip below, so a POC that sits outside
      // the visible rows can't hand the crown to the biggest cell on screen.
      let pocVol = 0, pocMcap = 0
      forEachDispLevel(candle, (mcap, buyVol, sellVol) => {
        const _tv = (buyVol || 0) + (sellVol || 0)
        if (_tv > pocVol) { pocVol = _tv; pocMcap = mcap }
        const rowIdx = frame.rowOfLevel(mcap)
        if (rowIdx < 0 || rowIdx >= sortedMcaps.length) return
        if (buyVol === 0 && sellVol === 0) return

        // Detect changes vs last frame and arm animations. We re-arm
        // (overwrite startTime) on every change — a cell hit twice in
        // 100ms restarts from "now", which keeps the visual responsive
        // during heavy flow rather than queuing up animations.
        // Numeric mcap key — no string allocation per level per frame.
        if (isLiveCandle && showCellFlipRef.current) {
          const prevB = prevLiveBuyRef.current
          // Only animate when the key EXISTED previously and value changed.
          // First-appearance keys (e.g., cells revealed by wheel zoom widening
          // the visible mcap range) just get stored without an animation —
          // otherwise off-screen→on-screen reveals look like flashes.
          if (prevB.has(mcap)) {
            if (buyVol !== prevB.get(mcap)) {
              cellAnimsBuyRef.current.set(mcap, nowAnim)
            }
          }
          prevB.set(mcap, buyVol)
          const prevS = prevLiveSellRef.current
          if (prevS.has(mcap)) {
            if (sellVol !== prevS.get(mcap)) {
              cellAnimsSellRef.current.set(mcap, nowAnim)
            }
          }
          prevS.set(mcap, sellVol)
        }
        const rowY = _yRow(rowIdx)
        const rowY1 = _yRow(rowIdx + 1)
        const cellY = rowY + _cellGapT
        const cellH = Math.max(0, rowY1 - _cellGapB - cellY)
        const slotY = rowY + _slotInset
        const slotH = Math.max(0, rowY1 - _slotInset - slotY)
        const buyBarWidth = (buyVol / candleMaxVolume) * halfWidth
        const sellBarWidth = (sellVol / candleMaxVolume) * halfWidth
        // Full-width slot plate under volume bars. Painted on plateLayer (z0)
        // so the order trail (z1) sits BETWEEN plate and cells (z2+). Inset so
        // neighboring columns don't share an edge (old cw+1 made them kiss).
        // Full pass only (incl. live column); live layer never paints plates.
        if (cellSlotBg && batchCells) {
          const pad = 1
          const colW = Math.max(0, cw - pad * 2)
          const colX = centerX - colW / 2
          const colC = (_churnBgMap && _churnBgMap.get(candleIdx)) || 'rgba(48, 52, 64, 0.92)'
          pushPlate(colC, colX, slotY, colW, slotH)
        }
        if (platesOnly && absHit && absHit.mcap === mcap && absHit.liq && absCfg.showLiq !== false && cellTextOn) {   // absorb-liq7
          try { _pushAbsLiq(candleIdx, absHit, ctx.getTransform(), slotY, slotH, centerX, halfWidth, true) } catch {}
        }
        if (platesOnly) return

        let isBuyImbalance = false, isSellImbalance = false
        if (buyVol > 0 && sellVol > 0) {
          if (buyVol / sellVol >= IMBALANCE_RATIO) isBuyImbalance = true
          else if (sellVol / buyVol >= IMBALANCE_RATIO) isSellImbalance = true
        } else if (buyVol > 0) isBuyImbalance = true
        else if (sellVol > 0) isSellImbalance = true

        const totalVol = (buyVol || 0) + (sellVol || 0);
        const intensity = (candleMaxVolume > 0 && totalVol > 0) ? Math.min(totalVol / candleMaxVolume, 1) : 0;

        if (buyVol > 0) {
          // LUT index: clamp intensity to [0,1] then map to 0..255
          const lutIdx = intensity >= 1 ? 255 : (intensity * 255 | 0)
          const buyColor = CELL_LUT_BUY.fillStyles[lutIdx]
          const cellLumV = CELL_LUT_BUY.lum[lutIdx]

          // Animation transform for live cells. Two-axis transform:
          //  (a) symmetric pop scale: 1.0 → 1+popMax → 1.0 over 300ms (sin-curve)
          //  (b) Y-flip illusion: scaleY swings 1 → -1 → 1 (a full 360° flip
          //      around the X-axis as if the cell were a card). cos(2π·t)
          //      gives that swing; combined with the pop scale, it reads
          //      as a 3D card flip with a magnified peak.
          // White flash overlay at peak amplifies the pop without changing
          // the underlying gradient color.
          const cellX = centerX - buyBarWidth
          const cellW = buyBarWidth
          const animStart = isLiveCandle ? cellAnimsBuyRef.current.get(mcap) : undefined
          let popScale = 1, flipScaleY = 1, flashAlpha = 0
          if (animStart !== undefined) {
            const t = (nowAnim - animStart) / 300
            if (t >= 1) {
              cellAnimsBuyRef.current.delete(mcap)
            } else {
              popScale = 1 + 0.6 * Math.sin(t * Math.PI)
              flipScaleY = Math.cos(t * 2 * Math.PI)
              flashAlpha = Math.sin(t * Math.PI) * 0.45
            }
          }
          const animating = popScale !== 1 || flipScaleY !== 1
          if (animating) {
            ctx.save()
            // Pivot at cell center for both axes so the cell scales
            // outward and flips around its own midline.
            const pivotX = cellX + cellW / 2
            const pivotY = cellY + cellH / 2
            ctx.translate(pivotX, pivotY)
            ctx.scale(popScale, popScale * flipScaleY)
            ctx.translate(-pivotX, -pivotY)
            ctx.fillStyle = buyColor
            // Animated cells cannot batch — the rect must render inside THIS
            // transform. Inflated fillRect rationale unchanged (see history).
            ctx.fillRect(cellX - 0.5, cellY, cellW + 1, cellH)
          } else if (batchCells) {
            pushFill(buyColor, cellX - 0.5, cellY, cellW + 1, cellH)
          } else {
            setFill(buyColor)
            ctx.fillRect(cellX - 0.5, cellY, cellW + 1, cellH)
          }
          if (cellTextOn) {
            // Auto text contrast: cell brightness `v` is 32 (dark) to 210
            // (bright). Above ~135 the cell is light enough that white
            // text stops reading — flip to a dark grey. Imbalance colors
            // (green for buy, red for sell) override since those are
            // signal hues that should always be visible.
            const cellLight = cellLumV > TEXT_LIGHT_THRESHOLD
            // Same three-way choice as before — it just selects a BUCKET now
            // rather than a colour string, so the lookup below is keyed on the
            // short volume text alone. Colours are identical.
            const bk = isBuyImbalance ? _bkImbBuy : (cellLight ? _bkTextLite : _bkText)
            // Atlas entry, not a per-string canvas: every blit below is
            // same-source, which is what lets the browser batch them (see
            // getTextSpriteAtlas for the why). Shorten before the lookup
            // so the placeholder is the glyph run that gets drawn.
            _paintCellLabel(bk, _fcv(buyVol), centerX - 4, cellY, cellH, -1, animating)
          }
          if (animating && flashAlpha > 0) {
            ctx.fillStyle = `rgba(255, 255, 255, ${flashAlpha})`
            ctx.fillRect(cellX, cellY, cellW, cellH)
          }
          if (animating) { ctx.restore(); curFill = '' }
        }
        if (sellVol > 0) {
          const lutIdx = intensity >= 1 ? 255 : (intensity * 255 | 0)
          const sellColor = CELL_LUT_SELL.fillStyles[lutIdx]
          const cellLumV = CELL_LUT_SELL.lum[lutIdx]

          const cellX = centerX
          const cellW = sellBarWidth
          const animStart = isLiveCandle ? cellAnimsSellRef.current.get(mcap) : undefined
          let popScale = 1, flipScaleY = 1, flashAlpha = 0
          if (animStart !== undefined) {
            const t = (nowAnim - animStart) / 300
            if (t >= 1) {
              cellAnimsSellRef.current.delete(mcap)
            } else {
              popScale = 1 + 0.6 * Math.sin(t * Math.PI)
              flipScaleY = Math.cos(t * 2 * Math.PI)
              flashAlpha = Math.sin(t * Math.PI) * 0.45
            }
          }
          const animating = popScale !== 1 || flipScaleY !== 1
          if (animating) {
            ctx.save()
            const pivotX = cellX + cellW / 2
            const pivotY = cellY + cellH / 2
            ctx.translate(pivotX, pivotY)
            ctx.scale(popScale, popScale * flipScaleY)
            ctx.translate(-pivotX, -pivotY)
            ctx.fillStyle = sellColor
            ctx.fillRect(cellX - 0.5, cellY, cellW + 1, cellH)
          } else if (batchCells) {
            pushFill(sellColor, cellX - 0.5, cellY, cellW + 1, cellH)
          } else {
            setFill(sellColor)
            ctx.fillRect(cellX - 0.5, cellY, cellW + 1, cellH)
          }
          if (cellTextOn) {
            const cellLight = cellLumV > TEXT_LIGHT_THRESHOLD
            const bk = isSellImbalance ? _bkImbSell : (cellLight ? _bkTextLite : _bkText)
            _paintCellLabel(bk, _fcv(sellVol), centerX + 4, cellY, cellH, 1, animating)
          }
          if (animating && flashAlpha > 0) {
            ctx.fillStyle = `rgba(255, 255, 255, ${flashAlpha})`
            ctx.fillRect(cellX, cellY, cellW, cellH)
          }
          if (animating) { ctx.restore(); curFill = '' }
        }
        // ─── Absorption outline ───
        // Drawn last in the level callback so it sits over BOTH halves. The
        // rect spans the drawn extent of the level (buy half + sell half), not
        // the full column: the outline is a claim about where volume actually
        // printed at this price, and padding it to the column would overstate
        // that. Half-pixel offset + integer sizing keeps a 1px stroke crisp
        // rather than smeared across two device rows.
        if (absHit && absHit.mcap === mcap) {
          const w = absCfg.weight || 2
          const x0 = centerX - buyBarWidth
          const x1 = centerX + sellBarWidth
          const rx = _xr(x0) - w / 2
          const ry = slotY - w / 2
          const rw = _xr(x1 - x0) + w
          const rh = slotH + w
          try {
            const T = ctx.getTransform()
            const ax = T.a * rx + T.c * ry + T.e, ay = T.b * rx + T.d * ry + T.f
            absRectsRef.current.set(candle.startTime, {
              el: canvas, candle, side: absHit.side, mcap: absHit.mcap,
              x: ax, y: ay, w: T.a * rw, h: T.d * rh,
            })
            // abs-liq-20261017 (liq3): 'Liq ↑6s' just outside the RIGHT edge of
            // this candle's column (moved up, buy colour) / 'Liq ↓6s' just
            // outside its LEFT edge (moved down, sell colour), centred on the
            // cell's row, in the cell's OUTLINE colour (absorb-liq4). liq5: full size,
            // never squeezed; hidden when wider than the column or cell numbers are off.
            // Half alpha while hovered.
            // absorb-liq7: placement lives in _pushAbsLiq (shared with the live column).
            if (absHit.liq && absCfg.showLiq !== false && cellTextOn && !liveOnly) {   // absorb-liq5: only where cell numbers show
              _pushAbsLiq(candleIdx, absHit, T, slotY, slotH, centerX, halfWidth, isLiveCandle)
            }
          } catch {}
          // abs-cell-hover-20261017: mouse-hovered cell = thicker outline in its
          // own colour plus a thin white line on top. Only that one cell.
          const _absCol = absHit.side === 'bid' ? absCfg.buyColor : absCfg.sellColor
          const _absHot = absHoverKeyRef.current != null && candle.startTime === absHoverKeyRef.current
          const _absW = _absHot ? w + 1.5 : w
          if (batchCells) {
            // Deferred with the rest: drawn during the loop it would land
            // UNDER the batched fills that flush afterwards.
            _strokeBatch.push(_absCol, _absW, rx, ry, rw, rh)
            if (_absHot) _strokeBatch.push('rgba(255, 255, 255, 0.85)', 1, rx, ry, rw, rh)
          } else {
            ctx.save()
            ctx.strokeStyle = _absCol
            ctx.lineWidth = _absW
            ctx.strokeRect(rx, ry, rw, rh)
            if (_absHot) {
              ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)'
              ctx.lineWidth = 1
              ctx.strokeRect(rx, ry, rw, rh)
            }
            ctx.restore()
          }
        }
      })

      // ─── POC outline ───────────────────────────────────────────────────
      // Full column slot, edge to edge — NOT the bar extents, so the box is
      // the same width on every bar and marks the ROW, not how the volume
      // split buy/sell. Deferred into the stroke batch for the same reason
      // absorption is: drawn inline it would land UNDER the fills that flush
      // afterwards.
      if (!platesOnly && pocVol > 0 && candleMaxVolume > 0 && showPocRef.current) {
        const pRow = frame.rowOfLevel(pocMcap)
        if (pRow >= 0 && pRow < sortedMcaps.length) {
          const w = 1
          const x0 = centerX - halfWidth
          const x1 = centerX + halfWidth
          const rx = _xr(x0) - w / 2
          const pTop = _yRow(pRow) + _slotInset
          const ry = pTop - w / 2
          const rw = _xr(x1 - x0) + w
          const rh = Math.max(0, _yRow(pRow + 1) - _slotInset - pTop) + w
          if (batchCells) {
            _strokeBatch.push(POC_COLOR, w, rx, ry, rw, rh)
          } else {
            ctx.save()
            ctx.strokeStyle = POC_COLOR
            ctx.lineWidth = w
            ctx.strokeRect(rx, ry, rw, rh)
            ctx.restore()
          }
        }
      }

      // Wallet badges: a second, tiny pass over the SAME levels forEachLevel
      // just drew, so a badge can only ever sit on a row a cell occupies.
      // Lookup key is stepIdxOf(levelMcap, bucketSize) — the display bucket
      // the overlay was built with — so both sides agree by construction.
      if (_ovMap && cellTextFits) {
        const _ovC = _ovMap.get(candle.startTime)
        if (_ovC) {
          const r = Math.max(5, Math.min(11, RH * 0.45))
          const fpx = Math.max(7, Math.min(fontSize, r * 1.35))
          forEachDispLevel(candle, (mcap, bv, sv) => {
            const cell = _ovC.get(dispRatio ? logStepIdx(mcap, dispRatio) : stepIdxOf(mcap, bucketSize))
            if (!cell) return
            const rowIdx = frame.rowOfLevel(mcap)
            if (rowIdx < 0 || rowIdx >= sortedMcaps.length) return
            const cy = Math.round((_yRow(rowIdx) + _yRow(rowIdx + 1)) * 0.5 * dpr) / dpr
            // Outer edge of each column, just past the widest bar this
            // candle can draw — the mock's placement, and the only spot
            // with room when cells are dense.
            // Anchored ON the column edge, grown outward — a pill can never
            // reach into the neighbour however many digits it carries.
            if (cell.buy  > 0) _badges.push(centerX - halfWidth, cy, r, cell.buy,  cell.nBuy,  1, fpx)
            if (cell.sell > 0) _badges.push(centerX + halfWidth, cy, r, cell.sell, cell.nSell, -1, fpx)
          })
        }
      }
    }

    // ─── Snapshot the batches for the pan replay (full pass, loop ran) ───
    // Copies, because the flush below empties the live arrays in place.
    // Only mid-drag: outside one the snapshot can never be replayed, and
    // copying every batch per frame would be pure cost. The first drag
    // frame therefore misses, draws with overdraw, and snapshots; every
    // frame after that replays until the integer index moves.
    if (batchCells && !_geomHit && scrollRef.current?.isDraggingRef?.current) {
      const g = panGeomRef.current
      const fill = new Map()
      for (const [color, q] of _fillBatch) if (q.length) fill.set(color, q.slice())
      g.fill = fill
      const plates = new Map()
      for (const [color, q] of _plateBatch) if (q.length) plates.set(color, q.slice())
      g.plates = plates
      g.sprite = _spriteBatch.slice()
      g.stroke = _strokeBatch.slice()
      g.wick   = _wickBatch.slice()
      g.badges = _badges ? _badges.slice() : null
      g.liq = _absLiqLbl.slice(); g.liqSy = _absLiqSy; g.liqSx = _absLiqSx   // absorb-liq6
    }

    // ─── Flush the cell batches (full pass only) ───
    // Order is the contract: wicks → fills → sprites → outlines. Wicks
    // first because candle-mode columns never share a cell with a
    // footprint column; then the within-cell order of the old immediate
    // path, applied globally. Legal because cell rects never overlap other
    // cells and a sprite only covers its own cell.
    if (batchCells) {
      // Wicks (historical OHLCV + candle-mode bodies) must paint at full
      // strength. The cell loop leaves globalAlpha at the LOD fade
      // (1 - lodBlend²) so footprint dissolves under the morph — and the
      // wick flush used to inherit that, so futures history faded and
      // popped back when lodBlend crossed 1. Force alpha 1 for wicks;
      // restore the cell fade only for the fill/sprite/stroke flush.
      const cellAlpha = (lodBlend > 0 && !asCandle) ? ((1 - lodBlend) * (1 - lodBlend)) : 1
      const _cellGl = cellGlRef.current
      // Clear GL only when we will redraw into it. xZoomGesture forces 2D but
      // still used to beginFrame-clear — empty GL over cells while candles
      // (same 2D pass) looked fine on some stacks; skip clear when not using GL.
      if (!xZoomGesture && _cellGl && _cellGl.ok && _cellGl.beginFrame) {
        _cellGl.beginFrame()
      }
      // Draw order (cell GL): fills → volume text → wicks/bodies. Heat is own-canvas.
      // Wicks used to flush BEFORE fills, so on phone LOD (cells+candles both
      // live) opaque volume cells painted over candle bodies.
      // Pinch/footer/wheel X-zoom: force 2D like the live layer (xZoomGesture).
      // set localStorage footprintCellGL=0 forces 2D always.
      // X camera for the GL path. ctx.scale() stretches the 2D fallbacks, but
      // WebGL is never CSS/ctx-scaled, so stretch the batch coordinates about
      // cameraAx in place before the GL draw — and undo it if the GL draw
      // declines and the 2D fallback (which applies ctx.scale) runs instead.
      const _xcs = (xCameraActive && Math.abs(cameraScaleX - 1) > 1e-6) ? cameraScaleX : 1
      const _xcam = (q, stride, xi, wi, inv) => {
        if (_xcs === 1) return
        const s = inv ? 1 / _xcs : _xcs
        for (let i = 0; i < q.length; i += stride) {
          q[i + xi] = cameraAx + (q[i + xi] - cameraAx) * s
          if (wi >= 0) q[i + wi] *= s
        }
      }
      if (!xZoomGesture && _cellGl && _cellGl.ok) for (const [, q] of _fillBatch) _xcam(q, 4, 0, 2, false)
      const _fillGl = !xZoomGesture && _cellGl && _cellGl.ok && _cellGl.drawColorBatches(_fillBatch, cellAlpha)
      if (!_fillGl && !xZoomGesture && _cellGl && _cellGl.ok) for (const [, q] of _fillBatch) _xcam(q, 4, 0, 2, true)
      if (_fillGl) {
        for (const [, q] of _fillBatch) q.length = 0   // keep the array — pooled across frames
      } else {
        ctx.globalAlpha = cellAlpha
        for (const [color, q] of _fillBatch) {
          const n = q.length
          if (n === 0) continue
          ctx.fillStyle = color
          ctx.beginPath()
          for (let i = 0; i < n; i += 4) ctx.rect(q[i], q[i + 1], q[i + 2], q[i + 3])
          ctx.fill()
          q.length = 0   // keep the array — pooled across frames
        }
      }
      // Column plates → plateLayer (z0), under order trail (z1). Same camera as cells.
      {
        const plateCanvas = plateLayerRef.current
        if (plateCanvas) {
          const pdpr = window.devicePixelRatio || 1
          const pw = Math.max(1, plotWidth)
          const ph = surfaceH
          if (plateCanvas.width !== Math.round(pw * pdpr) || plateCanvas.height !== Math.round(ph * pdpr)) {
            plateCanvas.width = Math.round(pw * pdpr)
            plateCanvas.height = Math.round(ph * pdpr)
          }
          const hpx = `${ph}px`
          if (plateCanvas.style.height !== hpx) plateCanvas.style.height = hpx
          const wpx = `${pw}px`
          if (plateCanvas.style.width !== wpx) plateCanvas.style.width = wpx
          const pctx = plateCanvas.getContext('2d')
          pctx.setTransform(pdpr, 0, 0, pdpr, 0, 0)
          pctx.clearRect(0, 0, pw, ph)
          if (xCameraActive) {
            pctx.translate(cameraAx, 0)
            pctx.scale(cameraScaleX, 1)
            pctx.translate(-cameraAx, 0)
          }
          if (cameraActive) pctx.transform(1, 0, 0, cameraScale, 0, cameraTy)
          pctx.globalAlpha = cellAlpha
          for (const [color, q] of _plateBatch) {
            const n = q.length
            if (n === 0) continue
            pctx.fillStyle = color
            pctx.beginPath()
            for (let i = 0; i < n; i += 4) pctx.rect(q[i], q[i + 1], q[i + 2], q[i + 3])
            pctx.fill()
            q.length = 0
          }
          pctx.globalAlpha = 1
        } else {
          for (const [, q] of _plateBatch) q.length = 0
        }
      }

      curFill = ''     // ctx.fillStyle no longer matches setFill's tracker
      // M3 WebGL atlas text; set localStorage footprintCellGL=0 to force 2D.
      const sb = _spriteBatch
      if (sb.length) {
        // Keep glyph size constant under anisotropic scaleX: shrink dw in
        // FROM space about sprite center so after ctx.scale(s,1) visual
        // width ≈ FROM font size (tracks column, does not stretch letters).
        if (xCameraActive && Math.abs(cameraScaleX - 1) > 1e-6) {
          const s = cameraScaleX
          for (let i = 0; i < sb.length; i += 5) {
            const dw = sb[i + 3]
            const dw2 = dw / s
            sb[i + 1] = sb[i + 1] + dw * 0.5 - dw2 * 0.5
            sb[i + 3] = dw2
          }
        }
        // GL sprites: position stretches with the column, glyph width stays
        // the (already pre-shrunk ×s → original) font width.
        const _spGl0 = !xZoomGesture && _cellGl && _cellGl.ok
        if (_spGl0) _xcam(sb, 5, 1, 3, false)
        const _spGl = _spGl0 && _cellGl.drawSpriteBatch(sb, cellAlpha)
        if (_spGl0 && !_spGl) _xcam(sb, 5, 1, 3, true)
        if (_spGl) {
          sb.length = 0
        } else {
          ctx.globalAlpha = cellAlpha
          for (let i = 0; i < sb.length; i += 5) {
            const sp = sb[i]
            ctx.drawImage(sp.img, sp.sx, sp.sy, sp.sw, sp.sh, sb[i + 1], sb[i + 2], sb[i + 3], sb[i + 4])
          }
          sb.length = 0
        }
      }
      // M2 WebGL wicks/bodies LAST so candles sit above volume cells.
      // Wicks paint at full strength — never inherit cell LOD alpha.
      const wb = _wickBatch
      if (wb.length) {
        const _wkGl0 = !xZoomGesture && _cellGl && _cellGl.ok
        if (_wkGl0) { _xcam(wb, 9, 0, -1, false); _xcam(wb, 9, 4, 6, false) }
        const _wkGl = _wkGl0 && _cellGl.drawWickBatch(wb)
        if (_wkGl0 && !_wkGl) { _xcam(wb, 9, 0, -1, true); _xcam(wb, 9, 4, 6, true) }
        if (_wkGl) {
          wb.length = 0
        } else {
          // 2D fallback (x-zoom gestures, incl. the footer drag, bypass GL).
          // Rasterize the SAME quads the GL pass draws — halo [wx-1.5, wx+1.5],
          // core [wx-0.5, wx+0.5] — with edges snapped to device pixels, which
          // is what the non-antialiased GL surface does. An antialiased
          // stroke here lands up to one device pixel off GL, so wicks
          // visibly shifted the moment a footer drag switched renderers.
          const _dq = (v) => Math.round(v * dpr) / dpr
          ctx.save()
          ctx.globalAlpha = 1
          for (let i = 0; i < wb.length; i += 9) {
            const wx = wb[i]
            if (wb[i + 1] !== wb[i + 2]) {   // (wick) zero-length = tight stick (body only)
              const y0 = _dq(Math.min(wb[i + 1], wb[i + 2]))
              const y1 = _dq(Math.max(wb[i + 1], wb[i + 2]))
              const hx0 = _dq(wx - 1.5), hx1 = _dq(wx + 1.5)
              const cx0 = _dq(wx - 0.5), cx1 = _dq(wx + 0.5)
              ctx.fillStyle = '#000'
              ctx.fillRect(hx0, y0, hx1 - hx0, y1 - y0)
              ctx.fillStyle = wb[i + 3]
              ctx.fillRect(cx0, y0, cx1 - cx0, y1 - y0)
            }
            ctx.fillStyle = wb[i + 8]
            ctx.fillRect(wb[i + 4], wb[i + 5], wb[i + 6], wb[i + 7])
          }
          ctx.restore()
          wb.length = 0
        }
      }
      const kb = _strokeBatch
      if (kb.length) {
        ctx.save()
        for (let i = 0; i < kb.length; i += 6) {
          ctx.strokeStyle = kb[i]
          ctx.lineWidth = kb[i + 1]
          ctx.strokeRect(kb[i + 2], kb[i + 3], kb[i + 4], kb[i + 5])
        }
        ctx.restore()
        kb.length = 0
      }
      ctx.globalAlpha = 1
    }

    // ─── Wallet badges (on top of every cell, both passes) ───
    if (_badges && _badges.length) {
      for (let i = 0; i < _badges.length; i += 7) {
        // _badges[i+5]: +1 buy (green, grows LEFT off the bid edge),
        // -1 sell (red, grows RIGHT off the ask edge).
        const _sd = _badges[i + 5]
        drawWalletBadge(ctx, _badges[i], _badges[i + 1], _badges[i + 2], _badges[i + 3], _badges[i + 4],
                        _sd > 0 ? '#22c55e' : '#ef4444', _badges[i + 6], _sd > 0 ? -1 : 1, cmeExactRef.current)
      }
    }

    // hold-label-front-20261016: labels go on their own topmost canvas (z 6,
    // above GL cells / main / live layers), sized + translated exactly like the
    // main canvas so the recorded device px land in the same place. Cleared on
    // every full pass so stale labels never linger.
    if (!liveOnly) {
      const LL = zoneLabelLayerRef.current
      if (LL) {
        try {
          if (LL.width !== canvas.width) LL.width = canvas.width
          if (LL.height !== canvas.height) LL.height = canvas.height
          if (LL.style.width !== canvas.style.width) LL.style.width = canvas.style.width
          if (LL.style.height !== canvas.style.height) LL.style.height = canvas.style.height
          if (LL.style.transform !== canvas.style.transform) LL.style.transform = canvas.style.transform
          const lctx = LL.getContext('2d')
          lctx.setTransform(1, 0, 0, 1, 0, 0)
          lctx.clearRect(0, 0, LL.width, LL.height)
          if (_zoneLbl.length) drawZoneHoldLabels(lctx, _zoneLbl, _zoneSy)
          if (_absLiqLbl.length) drawAbsLiqLabels(lctx, _absLiqLbl, _absLiqSy, fontSize)   // abs-liq-20261017
        } catch {}
      } else {
        if (_zoneLbl.length) drawZoneHoldLabels(ctx, _zoneLbl, _zoneSy)
        if (_absLiqLbl.length) drawAbsLiqLabels(ctx, _absLiqLbl, _absLiqSy, fontSize)   // abs-liq-20261017
      }
    }

    // 2b. OHLC candlestick overlay (centered in each live footprint column)
    // Aggregate LOD already painted sticks for the full pass.
    // Phone often composites the WebGL cell layer ABOVE the sibling 2D
    // canvas despite z-index — so footprint overlays that stay on ctx end
    // up under volume cells. When the shared GL scene is live, batch these
    // wicks/bodies and drawWickBatch after the cell flush (same FB).
    // Mid Y-camera: shared GL is opacity-hidden on phone — force overlay
    // onto ctx with the camera transform or center candles vanish.
    const _glOverlay = (!xZoomGesture && batchCells && cellGlRef.current && cellGlRef.current.ok)
      ? cellGlRef.current
      : null
    // Always batch footprint overlays (stride 9) so pan geom can replay them
    // with the cells. Immediate ctx path still flushes below when !_glOverlay.
    const _overlayWick = _overlayReplay ? _overlayReplay : []
    if (sortedMcaps.length > 1 && !_useCandleAgg && !_overlayReplay) {
      // Match main-loop pan overdraw so the rim slim candle slides, not pops.
      const _ovLo = startIdx - 1
      const _ovHi = endIdx + 1
      for (let ci = _ovLo; ci <= _ovHi; ci++) {
        const candle = candles[ci]
        // Live-layer exclusivity — the forming candle's wick/body belongs to
        // the live layer; everything else to the full pass (see liveIdx).
        if (liveOnly ? ci !== liveIdx : ci === liveIdx) continue
        // Skip ghosts & historical (they draw their own candlestick above) —
        // EXCEPT _omFootprint history, which renders cells and takes this
        // live-style wick/body pass… unless candle/LOD mode already drew it
        // as a candlestick (drawing a second body would double-paint).
        if (_tpo || !candle || (candle.isHistorical && (!candle._omFootprint || asCandle))) continue
        ctx.globalAlpha = (1 - lodBlend) * (1 - lodBlend)   // front-loaded, see :7594

        // Wick high / low: read from the candle's raw stored prices, NOT
        // from bucket-snapped mcapLevels keys. Body open/close already use
        // raw candle.open_mcap / close_mcap, so the wick must match the
        // same precision or the two slip by up to a row of FP rounding.
        // Falls back to bucket-key scan only if the raw fields aren't
        // populated yet (very early in a live candle's life).
        let hiMcap = candle.high_mcap ?? -Infinity
        let loMcap = candle.low_mcap  ??  Infinity
        if (!Number.isFinite(hiMcap) || !Number.isFinite(loMcap)) {
          hiMcap = -Infinity; loMcap = Infinity
          forEachDispLevel(candle, (m, buy, sell) => {
            if (buy + sell === 0) return
            if (m > hiMcap) hiMcap = m
            if (m < loMcap) loMcap = m
          })
        }
        if (!Number.isFinite(hiMcap) || !Number.isFinite(loMcap)) continue

        // Open / close: use candle properties if tracked, otherwise fall back
        const openM  = candle.open_mcap  ?? loMcap
        const closeM = candle.close_mcap ?? hiMcap

        // Same column X as the cell loop (centerX). visibleCount algebra
        // equals endIdx-ci today, but a second formula is a second SoT.
        const colX = frame.xLeft(ci)
        const cx   = colX + cw / 2

        // Price Y. Not the bucket row that contains the print.
        // For the CURRENT live candle only (rightmost, ci === last index),
        // the close uses chartData.highlightMcap so its leading edge sits
        // inside the highlight row. Other non-historical candles keep raw.
        const isCurrentLive = ci === candles.length - 1
        // Leading edge tracks highlight (last trade) when it's near close.
        // Bound must be lattice-native: ±2 LOG rows (ratio), not ±2 * linear
        // top-row width — on futures LOG that linear gate fired constantly,
        // so the body stayed on close_mcap while the y-axis chip / dashed
        // line sat on highlightMcap (visible vertical slip).
        // Shared gate (chartProjector) — same price as dashed line / chip.
        const closeForY = isCurrentLive
          ? resolveLivePriceForY({
              highlightMcap: chartData.highlightMcap,
              closeMcap: closeM,
              sortedMcaps,
              bucketSize,
              lnStep: _lnStep,
            })
          : closeM
        // RAW prices straight into priceToRow — NO pre-snap. priceToRow is
        // lattice-relative membership (row i covers [mcaps[i], mcaps[i]+B))
        // and returns the row CONTAINING the price by construction, on any
        // lattice. The old snapBucket pre-snap here was a bandaid for the
        // era when findBucketIndex did nearest-row rounding; snapBucket
        // floors to ABSOLUTE bucket multiples, which is a second, different
        // geometry — whenever the rendered lattice isn't anchored to
        // absolute multiples (HL fractional buckets, stretch states), the
        // snap moved hi/lo across a row boundary BEFORE the correct mapping
        // saw it, leaving wick stubs poking past the cell stack. One
        // mapping only.
        // Same row edges as the cells (candleY), candle mode included.
        ctx.globalAlpha = 1                     // bodies/wicks at full strength
        const yH = candleY(hiMcap)
        const yL = candleY(loMcap)
        const yO = candleY(openM)
        const yC = candleY(closeForY)

        const bull    = closeM >= openM
        const bodyTop = Math.min(yO, yC)
        const bodyH   = Math.max(2, Math.abs(yC - yO))
        // In candle mode, body fills most of the column like a real candlestick.
        // In footprint mode, stays a slim ~10px overlay so footprint cells show through.
        // (wick) Capped at floor(cw)-1 so a body can never cover its neighbours.
        const bodyW   = Math.min(candleStyled
          ? Math.max(2, cw * 0.7)
          : Math.min(10, Math.max(4, cw * 0.12)), Math.max(1, Math.floor(cw) - 1))

        // Wick — thicker + colored in candle mode, thin overlay in footprint
        // mode. Black halo drawn first at +2px width so 1px of black peeks
        // out on each side of the colored center stroke. Two-pass cost is
        // modest (~visibleCount extra strokes per frame) and only the live
        // candle path; closed candles in this loop are skipped above.
        const wickColor = wickColorOf(TH, false, candleStyled)
        const wickWidth = 1
        const _dprO = window.devicePixelRatio || 1
        const yHs = snapY(yH, _dprO)
        const yLs = snapY(yL, _dprO)
        const bodyTopS = snapY(bodyTop, _dprO)
        const bodyHS = _useWickOnly ? 0 : Math.max(2, Math.abs(snapY(yC, _dprO) - snapY(yO, _dprO)))
        const bodyWS = _useWickOnly ? 0 : bodyW
        if (_wickTight) { pushWickStick(_overlayWick, cx, yHs, yLs, _wickTightCol); continue }
        // Cells live at raw centerX. Independently rounding the slim overlay
        // (Math.round(cx)+0.5 + rounded body) makes every footprint candle
        // jump 1px vs its cells while cw eases — motion-scroll everything-pops.
        // Candle-only has no cells underneath; keep the crisp half-pixel snap.
        // Halo + core still share ONE wx so they stay concentric.
        // (wick) Also snapped once cells are gone (small cw, nothing to track).
        const _ovSnap = (asCandle || _cellsGone) && _xSnapOn
        const wx = _ovSnap ? (Math.round(cx) + 0.5) : cx
        const bodyX = _ovSnap ? Math.round(wx - bodyWS / 2) : (wx - bodyWS / 2)
        const bodyWW = _ovSnap ? Math.round(bodyWS) : bodyWS
        const bodyColor = bull ? TH.candle.bullBody : TH.candle.bearBody
        // Same stride as historical M2 / pan-geom overlay replay.
        _overlayWick.push(
          wx, yHs, yLs, wickColor,
          bodyX, bodyTopS, bodyWW, bodyHS,
          bodyColor,
        )
        // In candle mode, add a subtle outline so adjacent same-direction candles
        // are visually separated
        // NO body outline, any mode. The stroke used to apply in TRUE
        // candle mode only (skipped at LOD widths), which is exactly why
        // toggling to candle mode looked different from the candles the
        // footprint fades into. Fill-only everywhere: one look.
      }
    }

    // Pan geom: snapshot overlays with the cells (same dx replay).
    // Flush is OUTSIDE the build `!_overlayReplay` if — geom-hit must still draw.
    if (!_overlayReplay && batchCells && scrollRef.current?.isDraggingRef?.current) {
      const g = panGeomRef.current
      if (g.fill) g.overlay = _overlayWick.slice()
    }
    if (_overlayWick.length) {
      if (_glOverlay && _glOverlay.drawWickBatch) {
        // The footprint centre candles are their OWN GL batch, drawn here
        // after the cell flush — so the X camera stretch applied to the cell
        // batches never reached them. Mid-glide the cells stretched and the
        // candles didn't (up to ~3% of the distance from the right edge —
        // the "off by 2/3 of a column, snaps back on release"). Same stretch
        // here: wick x [0], body x [4], body width [6], about cameraAx.
        if (xCameraActive && Math.abs(cameraScaleX - 1) > 1e-6) {
          const s = cameraScaleX
          for (let i = 0; i < _overlayWick.length; i += 9) {
            _overlayWick[i]     = cameraAx + (_overlayWick[i]     - cameraAx) * s
            _overlayWick[i + 4] = cameraAx + (_overlayWick[i + 4] - cameraAx) * s
            _overlayWick[i + 6] *= s
          }
        }
        _glOverlay.drawWickBatch(_overlayWick)
      } else {
        ctx.save()
        ctx.globalAlpha = 1
        for (let i = 0; i < _overlayWick.length; i += 9) {
          const wx = _overlayWick[i]
          if (_overlayWick[i + 1] !== _overlayWick[i + 2]) {   // (wick) zero-length = tight stick
            ctx.strokeStyle = '#000'; ctx.lineWidth = 3
            ctx.beginPath(); ctx.moveTo(wx, _overlayWick[i + 1]); ctx.lineTo(wx, _overlayWick[i + 2]); ctx.stroke()
            ctx.strokeStyle = _overlayWick[i + 3]; ctx.lineWidth = 1
            ctx.beginPath(); ctx.moveTo(wx, _overlayWick[i + 1]); ctx.lineTo(wx, _overlayWick[i + 2]); ctx.stroke()
          }
          if (_overlayWick[i + 6] > 0 && _overlayWick[i + 7] > 0) {
            ctx.fillStyle = _overlayWick[i + 8]
            ctx.fillRect(_overlayWick[i + 4], _overlayWick[i + 5], _overlayWick[i + 6], _overlayWick[i + 7])
          }
        }
        ctx.restore()
      }
    }

    // 2b2. Absorption zones (absorb-zones-20261019) - shaded band from the
    //      forming candle right until a close breaks through. Full pass only.
    if (!liveOnly && showAbsorptionRef.current && absConfigRef.current?.zones && absZonesRef.current?.length) {
      try {
        const col = absConfigRef.current.zoneColor || '#ffd166'
        ctx.save()
        ctx.beginPath(); ctx.rect(0, 0, plotWidth, totalHeight); ctx.clip()
        ctx.fillStyle = hexA(col, 0.16); ctx.strokeStyle = hexA(col, 0.6); ctx.lineWidth = 1
        for (const z of absZonesRef.current) {
          const e = z.end == null ? Infinity : z.end
          if (e < startIdx - 1 || z.start > endIdx + 1) continue
          const x0 = frame.xLeft(z.start)
          const x1 = z.end == null ? plotWidth : frame.xLeft(z.end)
          const ya = mcapToY(z.hi), yb = mcapToY(z.lo)
          if (!Number.isFinite(x0) || !Number.isFinite(x1) || !Number.isFinite(ya) || !Number.isFinite(yb)) continue
          const y0 = Math.min(ya, yb), h = Math.max(1, Math.abs(yb - ya))
          ctx.fillRect(x0, y0, x1 - x0, h)
          ctx.strokeRect(Math.round(x0) + 0.5, Math.round(y0) + 0.5, Math.round(x1 - x0), Math.round(h))
        }
        ctx.restore()
      } catch {}
    }

    // 2c. Protraction (protraction-20261018) - one leg per candle: open ->
    //     the wick extreme made AGAINST the close direction (bull = low,
    //     bear = high), x = time in candle, dot at the extreme. Doji = none.
    //     Above cells/candles, below labels. Full pass = closed candles (rides
    //     the drag cache with the rest of the main canvas); live pass = the
    //     forming candle only. Needs the tape buffer (~3h); older = no line.
    if (showProtractionRef.current && cw >= protCfgRef.current.minCandlePx && data.rawTradesRef?.current) {
      try {
        const pc = protCfgRef.current
        const buf = data.rawTradesRef.current
        const ivMs = (timeIntervalRef?.current || timeInterval || 60) * 1000
        const cache = protCacheRef.current
        const ck = `${data.currentToken || ''}|${dataSource}|${ivMs}`
        if (cache.key !== ck) { cache.key = ck; cache.map = new Map() }
        const lo = liveOnly ? liveIdx : Math.max(0, startIdx - 1)
        const hi = liveOnly ? liveIdx : Math.min(candles.length - 1, endIdx + 1)
        if (lo >= 0 && buf.length) {
          const dots = pc.dots ? [] : null
          ctx.save()
          ctx.beginPath(); ctx.rect(0, 0, plotWidth, totalHeight); ctx.clip()
          ctx.strokeStyle = pc.color; ctx.globalAlpha = pc.opacity
          ctx.lineWidth = pc.lineWidth; ctx.lineCap = 'round'; ctx.setLineDash([])
          ctx.beginPath()
          for (let i = lo; i <= hi; i++) {
            if (!liveOnly && i === liveIdx) continue
            const c = candles[i]
            if (!c || !Number.isFinite(c.startTime)) continue
            const r = protractionFor(cache, buf, c.startTime, ivMs, i === liveIdx)
            if (!r) continue
            const xl = frame.xLeft(i)
            drawProtraction(ctx, r, c.startTime, ivMs, xl + cw * 0.1, xl + cw * 0.9, mcapToY, dots)
          }
          ctx.stroke()
          if (dots && dots.length) {
            ctx.fillStyle = pc.color
            ctx.beginPath()
            for (let k = 0; k < dots.length; k += 2) { ctx.moveTo(dots[k] + 2, dots[k + 1]); ctx.arc(dots[k], dots[k + 1], 2, 0, Math.PI * 2) }
            ctx.fill()
          }
          ctx.restore()
        }
      } catch {}
    }

    // 3. Highlight current price row — priceToRow never tolerance-misses
    // (always nearest row by geometry), so the band can't flicker out when
    // price falls between two row mcaps.
    // ─── VOLUME LANE (canvas) ───
    // Same column geometry as everything else: right-anchored, half-centered,
    // pan-fraction aware — so it tracks the chart exactly instead of trailing
    // it. Full draws only; the live pump leaves it alone (its own candle's bar
    // updates on the next data commit, same cadence the DOM lane had).
    // Volume alerts: check every full draw (data commits), lane shown or not.
    if (!liveOnly) {
      try { volAlertsRef.current?.check(candles, (timeIntervalRef.current || 60) * 1000, candleTotalVolume) } catch {}
    }
    // trailvol-20261103: order-trail bubble scale = MEDIAN volume of the
    // visible COMPLETED candles at this timeframe (same candleTotalVolume as
    // the volume lane). Live candle excluded: it is still filling.
    if (!liveOnly) {
      const _vs = []
      for (let i = Math.max(0, startIdx); i <= Math.min(endIdx, candles.length - 1); i++) {
        const c = candles[i]; if (!c || !c.isHistorical) continue
        const v = candleTotalVolume(c); if (v > 0) _vs.push(v)
      }
      if (_vs.length) { _vs.sort((x, y) => x - y); trailCandleVolRef.current = _vs[_vs.length >> 1] }
    }
    if (!liveOnly && volLaneRef.current) {
      const vc = volLaneRef.current
      // Lane height: fixed 120px (original) or heightPct% of the chart body.
      const _vcfg = volConfigRef.current || VOL_DEFAULTS
      const _vpct = Number(_vcfg.heightPct)
      const _bodyH = containerHeightRef.current || 0
      const LANE_H = (_vcfg.heightPct != null && Number.isFinite(_vpct) && _bodyH > 0)
        ? Math.max(8, Math.round(_bodyH * Math.min(VOL_PCT_MAX, Math.max(VOL_PCT_MIN, _vpct)) / 100))
        : 120
      if (vc.style.height !== `${LANE_H}px`) vc.style.height = `${LANE_H}px`
      const dpr = window.devicePixelRatio || 1
      if (vc.width !== Math.round(containerW * dpr) || vc.height !== Math.round(LANE_H * dpr)) {
        vc.width = Math.round(containerW * dpr); vc.height = Math.round(LANE_H * dpr)
      }
      const lctx = vc.getContext('2d')
      lctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      lctx.clearRect(0, 0, containerW, LANE_H)
      // X camera: the volume lane is its own canvas, so the stretch applied to
      // the chart never reached it — its bars re-laid-out a whole candle at a
      // time while the columns above glided (the lane "expanding" in steps).
      if (xCameraActive && Math.abs(cameraScaleX - 1) > 1e-6) {
        lctx.translate(cameraAx, 0)
        lctx.scale(cameraScaleX, 1)
        lctx.translate(-cameraAx, 0)
      }
      let liveMax = 0
      for (let i = startIdx; i <= Math.min(endIdx, total - 1); i++) {
        const v = candleTotalVolume(candles[i]); if (v > liveMax) liveMax = v
      }
      // Rest: live window max. Interact / X-ease / zoom-paint: peak-hold so
      // integer window-edge snaps cannot collapse the scale mid-gesture.
      const holdScale = chartInteractingRef.current
      let maxVol = liveMax
      if (holdScale) {
        maxVol = Math.max(liveMax, volMaxHoldRef.current || 0)
        volMaxHoldRef.current = maxVol
      } else {
        volMaxHoldRef.current = liveMax
      }
      const _barsMax = maxVol
      maxVol = volLaneScale(maxVol)   // volalert2: alerts above the bars stay on-pane
      if (maxVol > 0) {
        const _hexA = (hex, a, fb) => {
          const m = /^#?([0-9a-f]{6})$/i.exec(hex || '')
          const al = Number.isFinite(Number(a)) ? Math.min(1, Math.max(0, Number(a))) : 1
          if (!m) return fb
          const n = parseInt(m[1], 16)
          return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${al})`
        }
        const _volOutline = _vcfg.outline !== false
        // voldelta-20261027: bar colour by that bar's delta (buy - sell), same
        // flow source as the CVD hook (plain OHLCV history = no flow = neutral).
        const _volFillZero = _hexA(_vcfg.fillColor, _vcfg.fillOpacity, 'rgba(43, 43, 43, 0.699)')
        const _volFillPos = _hexA(_vcfg.posColor, _vcfg.fillOpacity, 'rgba(38, 166, 154, 0.7)')
        const _volFillNeg = _hexA(_vcfg.negColor, _vcfg.fillOpacity, 'rgba(239, 83, 80, 0.7)')
        lctx.strokeStyle = _hexA(_vcfg.lineColor, _vcfg.lineOpacity, '#00ff2a27')
        lctx.lineWidth = 1
        lctx.setLineDash([2, 2])
        // Same +-1 pan overdraw as the footprint column loop (_loopLo/_loopHi):
        // the edge bar slides off and the canvas edge clips it, instead of the
        // bar vanishing whole the moment it leaves [startIdx, endIdx].
        for (let i = Math.max(0, startIdx - 1); i <= Math.min(endIdx + 1, total - 1); i++) {
          const v = candleTotalVolume(candles[i])
          if (v <= 0) continue
          const h = Math.min(LANE_H, (v / maxVol) * LANE_H)
          const x = frame.xLeft(i)
          const w = Math.max(1, Math.round(cw) - 1)
          const _c = candles[i]
          const _d = (_c.isHistorical && !_c._omFootprint) ? 0 : candleDelta(_c)
          lctx.fillStyle = _d > 0 ? _volFillPos : _d < 0 ? _volFillNeg : _volFillZero
          lctx.fillRect(x, LANE_H - h, w, h)
          if (!_volOutline) continue
          lctx.beginPath()                       // dashed cap + sides, no bottom
          lctx.moveTo(x + 0.5, LANE_H)
          lctx.lineTo(x + 0.5, LANE_H - h + 0.5)
          lctx.lineTo(x + w - 0.5, LANE_H - h + 0.5)
          lctx.lineTo(x + w - 0.5, LANE_H)
          lctx.stroke()
        }
      }
      // volalert-20261028: remember the painted columns (camera-mapped, CSS
      // px of the lane) for the right-click hit test and the alert nodes.
      {
        const _cam = xCameraActive && Math.abs(cameraScaleX - 1) > 1e-6
        const _X = (xx) => (_cam ? cameraAx + (xx - cameraAx) * cameraScaleX : xx)
        const _w = Math.max(1, Math.round(cw) - 1)
        const _cols = []
        for (let i = Math.max(0, startIdx - 1); i <= Math.min(endIdx + 1, total - 1); i++) {
          const x = frame.xLeft(i)
          _cols.push({ i, t: candles[i]?.startTime, x0: _X(x), x1: _X(x + _w), v: candleTotalVolume(candles[i]) })
        }
        volLaneGeomRef.current = { LANE_H, maxVol, barsMax: _barsMax, cols: _cols, lineEnd: containerW - FIXED_OVERLAY_WIDTH }
        placeVolAlerts()
      }
    }

    // Current price: a DASHED LINE AT THE EXACT TRADED PRICE — not a row
    // band. The filled band marked the whole bucket ROW, so it read as a
    // range and its thickness/position quantized with the display bucket;
    // the line uses continuous y (same mapping as candle wicks/bodies), so
    // it sits on the actual last-trade price at every scale.
    // Full-only: highlightMcap changes ONLY on data publishes, and every
    // publish schedules a full draw — freshness by construction.
    // Prefer the SAME price the live body tip uses (closeForY rules), so the
    // dashed line, chip, and candle close cannot disagree on LOG futures.
    const priceForLine = frame.livePrice   // resolved at the frame install
    const highlightIdx = frame.rowOfPrice(priceForLine)
    if (!liveOnly && priceForLine > 0 && highlightIdx >= 0 && highlightIdx <= lastVisibleRow) {
      // Armed chart: gold. Unarmed: neutral gray — matches the dimmed
      // y-axis chip so every focus signal agrees on where orders land.
      const yExact = frame.yOf(priceForLine)
      ctx.save()
      ctx.strokeStyle = isActiveChartRef.current
        ? 'rgba(255, 166, 0, 0.85)'
        : 'rgba(140, 150, 170, 0.55)'
      ctx.lineWidth = 1
      ctx.setLineDash([4, 4])
      ctx.beginPath()
      ctx.moveTo(0, yExact + 0.5)
      ctx.lineTo(containerW, yExact + 0.5)
      ctx.stroke()
      ctx.restore()
    }

    // 4. (removed) Live/historical divider — Solana history now comes from
    // capture-derived footprint, so there are no isHistorical price candles
    // and thus no live/historical boundary to mark.

    // 5-8: AVWAP / SR lines / swing labels — structural-cadence pixels.
    // Painted on full draws only; they persist on the main canvas between
    // full draws (it no longer clears per frame), so nothing strobes.
    if (!liveOnly) {
    // zindex-20261102: per-item target canvas by its `index` (see zLayerPrep).
    let _zA = null, _zB = null
    const _zDirty = zLayerDirtyRef.current
    const layerCtx = (ix) => {
      if (ix > 0) {
        if (!_zA) { const L = zAboveLayerRef.current; if (!L) return ctx; _zA = zLayerPrep(L, canvas, ctx); _zDirty.a = true }
        return _zA
      }
      if (ix < 0) {
        if (!_zB) { const L = zBelowLayerRef.current; if (!L) return ctx; _zB = zLayerPrep(L, canvas, ctx); _zDirty.b = true }
        return _zB
      }
      return ctx
    }
    // 5. AVWAP lines
    const activeAvwaps = interaction.avwapsRef.current
    const hovId = interaction.hoveredAvwapIdRef.current
    const dragItem = interaction.draggingItemRef.current
    const newPointsCache = []

    // Helper: derive (price, vol) for a candle under a given mode. For
    // historical candles this is O(1); for the live candle it walks
    // mcapLevels once per frame (only one candle, so cheap).
    const _avwapCandlePV = (c, mode) => {
      if (c.isHistorical) {
        const vol = c.volume_tokens || 0
        let price
        if      (mode === 'high') price = c.high_mcap
        else if (mode === 'low')  price = c.low_mcap
        else                      price = (c.open_mcap + c.high_mcap + c.low_mcap + c.close_mcap) / 4
        return { price, vol }
      }
      // NATIVE levels, not display levels. forEachDispLevel groups by
      // dispKeyBucket, which is derived from the VISIBLE lattice — so the
      // average price of a candle changed whenever you scrolled or zoomed,
      // and the whole study drifted with the viewport. Display keys are also
      // a bucket's LOWER EDGE, which biased every price down by up to a
      // bucket. An anchored study must depend only on the trades. This also
      // matches useChartInteraction's computeAvwapPointsLive, which already
      // walks native levels — the two used to disagree.
      let highMcap = -Infinity, lowMcap = Infinity, weightedMcap = 0, vol = 0
      forEachLevel(c, (mcap, buy, sell) => {
        const levelVol = buy + sell
        if (levelVol === 0) return
        if (mcap > highMcap) highMcap = mcap
        if (mcap < lowMcap)  lowMcap  = mcap
        weightedMcap += mcap * levelVol
        vol += levelVol
      })
      let price
      if      (mode === 'high') price = highMcap === -Infinity ? 0 : highMcap
      else if (mode === 'low')  price = lowMcap  === Infinity  ? 0 : lowMcap
      else                      price = vol > 0 ? weightedMcap / vol : 0
      return { price, vol }
    }

    if (activeAvwaps.length > 0 && sortedMcaps.length > 1) {
      const liveIdx = candles.length - 1
      // Sweep dead cache entries (AVWAPs that were deleted)
      const activeIdSet = new Set(activeAvwaps.map(v => v.id))
      for (const id of avwapAccumCacheRef.current.keys()) {
        if (!activeIdSet.has(id)) avwapAccumCacheRef.current.delete(id)
      }

      for (const vwap of activeAvwaps) {
        // Resolve anchor index (cheap; small loop)
        let anchorIdx = -1
        for (let i = 0; i < candles.length; i++) {
          if (candles[i].startTime >= vwap.anchorTime) { anchorIdx = i; break }
        }
        if (anchorIdx === -1) continue

        // Cache lookup. Invalidate on candles-array swap (token change),
        // anchor change, or mode change. Also rebuild if the cached
        // arrays are too small (candle count grew past capacity).
        let cache = avwapAccumCacheRef.current.get(vwap.id)
        const cacheValid = cache
          && cache.candlesRef === candles
          && cache.anchorTime === vwap.anchorTime
          && cache.mode       === vwap.mode
          && cache.anchorIdx  === anchorIdx
          && cache.cumPVAt.length >= candles.length

        if (!cacheValid) {
          // Allocate with headroom so we don't realloc on every new candle.
          const cap = Math.max(candles.length, 64) * 2
          cache = {
            anchorTime: vwap.anchorTime,
            mode:       vwap.mode,
            anchorIdx,
            candlesRef: candles,
            cumPVAt:    new Float64Array(cap),
            cumVAt:     new Float64Array(cap),
            lastClosedIdx: anchorIdx - 1, // nothing closed-and-cached yet
          }
          avwapAccumCacheRef.current.set(vwap.id, cache)
        } else if (cache.cumPVAt.length < candles.length) {
          // Grow buffers; copy existing values
          const cap = candles.length * 2
          const newPV = new Float64Array(cap), newV = new Float64Array(cap)
          newPV.set(cache.cumPVAt); newV.set(cache.cumVAt)
          cache.cumPVAt = newPV; cache.cumVAt = newV
        }

        // Extend cache through any newly-closed candles (everything up to
        // but not including liveIdx). Each runs at most once per close.
        const cumPVAt = cache.cumPVAt
        const cumVAt  = cache.cumVAt
        let prevPV = cache.lastClosedIdx >= anchorIdx ? cumPVAt[cache.lastClosedIdx] : 0
        let prevV  = cache.lastClosedIdx >= anchorIdx ? cumVAt [cache.lastClosedIdx] : 0
        const extendFrom = Math.max(anchorIdx, cache.lastClosedIdx + 1)
        for (let i = extendFrom; i < liveIdx; i++) {
          const { price, vol } = _avwapCandlePV(candles[i], vwap.mode)
          if (vol > 0 && price > 0) {
            prevPV += price * vol
            prevV  += vol
          }
          cumPVAt[i] = prevPV
          cumVAt[i]  = prevV
        }
        cache.lastClosedIdx = liveIdx - 1

        // Live candle (recomputed every frame — only one candle)
        let livePV = prevPV, liveV = prevV
        if (liveIdx >= anchorIdx) {
          const { price, vol } = _avwapCandlePV(candles[liveIdx], vwap.mode)
          if (vol > 0 && price > 0) {
            livePV = prevPV + price * vol
            liveV  = prevV  + vol
          }
        }

        // Build from the ANCHOR, not the viewport. Clamping to startIdx put
        // points[0] — which IS the anchor handle below — on the left edge
        // whenever the anchor scrolled off, so the line read as if the AVWAP
        // started at the edge and the handle could be grabbed somewhere the
        // anchor isn't. Everything left of the plot collapses to two points:
        // the anchor, and the bar that crosses the edge. The stroke meets
        // the border on its real local slope instead of cutting a shortcut,
        // and the walk stays bounded by viewWindowSize, not candle count.
        const points = []
        const visEnd   = Math.min(endIdx, liveIdx)
        const visStart = Math.max(startIdx - 1, anchorIdx)
        // The LINE starts at the anchor, and so does the handle (points[0],
        // desktop dot and phone square alike). It used to sit on the first
        // bar still inside the viewport (i >= startIdx), which made the dot
        // slide down the line toward the left edge as the anchor scrolled
        // off. Off-screen anchor = off-screen handle, clipped like the line.
        const pushAt = (i) => {
          const curPV = (i === liveIdx) ? livePV : cumPVAt[i]
          const curV  = (i === liveIdx) ? liveV  : cumVAt [i]
          if (curV <= 0) return
          points.push({ x: frame.xMid(i), y: mcapToY(curPV / curV) })
        }
        if (anchorIdx < visStart) pushAt(anchorIdx)
        for (let i = visStart; i <= visEnd; i++) pushAt(i)
        if (points.length < 2) continue
        newPointsCache.push({ id: vwap.id, points })

        const isHovered = hovId === vwap.id
        const isDragging = dragItem?.type === 'avwap' && dragItem.id === vwap.id
        const lc = layerCtx(vwap.index)   // zindex-20261102
        lc.save()
        if (isHovered || isDragging) {
          lc.shadowColor = vwap.color; lc.shadowBlur = 10
          lc.strokeStyle = vwap.color; lc.lineWidth = vwap.weight + 2; lc.globalAlpha = 0.5; lc.setLineDash([])
          lc.beginPath(); lc.moveTo(points[0].x, points[0].y)
          for (let p = 1; p < points.length; p++) lc.lineTo(points[p].x, points[p].y)
          lc.stroke(); lc.shadowBlur = 0; lc.globalAlpha = 1.0
        }
        lc.strokeStyle = vwap.color
        lc.lineWidth = isHovered || isDragging ? vwap.weight + 1 : vwap.weight
        lc.setLineDash(isDragging ? [6, 4] : [])
        lc.beginPath(); lc.moveTo(points[0].x, points[0].y)
        for (let p = 1; p < points.length; p++) lc.lineTo(points[p].x, points[p].y)
        lc.stroke()
        // One square, same size as trendline / S/R. Phone shows it only
        // while this AVWAP is highlighted. Desktop keeps the small dot.
        // Both sit on the curve start (points[0], the anchor via
        // frame.xMid / mcapToY) — never clamped to the viewport.
        const _hp = points[0]
        lc.fillStyle = vwap.color
        if (isPhone) {
          if (isHovered || isDragging) {
            const h = PHONE_HANDLE_HALF
            const _start = points[0]
            lc.fillRect(_start.x - h, _start.y - h, h * 2, h * 2)
          }
        } else {
          const _anchorR = (isHovered || isDragging) ? 6 : 4
          lc.beginPath()
          lc.arc(_hp.x, _hp.y, _anchorR, 0, Math.PI * 2)
          lc.fill()
        }
        lc.restore()
      }
    }
    interaction.avwapPointsCacheRef.current = newPointsCache

    // 5b. Moving averages — same pass, same mapper as AVWAP (frame.xMid =
    //     xLeft + cw/2, mcapToY = the candles' Y projection), so it rides the
    //     wheel glide, drag and Y camera exactly as the candles do. One bar
    //     past each edge is included and the plot is clipped, so the line
    //     slides off the edges instead of stopping short. Values come from
    //     the incremental cache: closed bars once, forming bar per pass.
    {
      const maCfgs = maConfigsRef.current
      const maOut = []
      const liveIdxMa = candles.length - 1
      const structVer = chartData.structuralVersion
      const caches = maCacheRef.current
      for (const id of caches.keys()) if (!maCfgs.some(c => c.id === id && c.on)) caches.delete(id)
      if (liveIdxMa >= 0 && sortedMcaps.length > 1) {
        for (const ma of maCfgs) {
          if (!ma.on) continue
          const cache = updateMaCache(caches.get(ma.id), candles, ma, structVer)
          caches.set(ma.id, cache)
          const lo = Math.max(0, startIdx - 1)
          const hi = Math.min(liveIdxMa, endIdx + 1)
          const pts = []
          for (let i = lo; i <= hi; i++) {
            const v = maValueAt(cache, i, liveIdxMa)
            if (!(v > 0)) continue
            const yv = mcapToY(v)
            if (!Number.isFinite(yv)) continue
            pts.push({ x: frame.xMid(i), y: yv })
          }
          if (pts.length < 2) continue
          maOut.push({ id: ma.id, points: pts })
          const lc = layerCtx(ma.index)   // zindex-20261102
          const hot = maHoverIdRef.current === ma.id
          lc.save()
          lc.beginPath(); lc.rect(0, 0, plotWidth, totalHeight); lc.clip()
          lc.lineJoin = 'round'; lc.lineCap = 'round'; lc.setLineDash([])
          if (hot) {
            lc.shadowColor = ma.color; lc.shadowBlur = 10
            lc.strokeStyle = ma.color; lc.lineWidth = ma.weight + 2; lc.globalAlpha = 0.5
            lc.beginPath(); lc.moveTo(pts[0].x, pts[0].y)
            for (let p = 1; p < pts.length; p++) lc.lineTo(pts[p].x, pts[p].y)
            lc.stroke(); lc.shadowBlur = 0; lc.globalAlpha = 1
          }
          lc.strokeStyle = ma.color
          lc.lineWidth = hot ? ma.weight + 1 : ma.weight
          lc.beginPath(); lc.moveTo(pts[0].x, pts[0].y)
          for (let p = 1; p < pts.length; p++) lc.lineTo(pts[p].x, pts[p].y)
          lc.stroke()
          lc.restore()
        }
      }
      maPointsRef.current = maOut
    }

    // 6. S/R lines
    // Trendlines paint under S/R. The painter returns its screen segments,
    // which feed hover + endpoint-grab hit-testing — measured against what
    // was actually drawn, so the highlight can't disagree with the eye.
    {
      const _ivMs = (timeIntervalRef.current || 60) * 1000
      const _g = { plotWidth, panOffsetPx, cw, startIdx, visibleCount, intervalMs: _ivMs }
      const _cp = trend.cursorPxRef.current
      let _cursor = null
      if (_cp.x >= 0 && sortedMcaps.length > 1) {
        // Continuous on BOTH axes: a fractional index interpolated to a
        // timestamp (extrapolating past the live candle into the future
        // padding), and a fractional row already given by yToMcap. Nothing
        // snaps, so an endpoint tracks the cursor exactly and can be placed
        // anywhere on the plot.
        const _idxF = indexOfX(_cp.x, _g)
        const _t = timeOfIndexF(_idxF, chartData.candles, _ivMs)
        const _m = yToMcapUtil(_cp.y, frame.mcaps, frame.RH || 12)
        if (_t > 0 && _m > 0) _cursor = { t: _t, mcap: _m, x: _cp.x, y: _cp.y }
      }
      const _segs = drawTrendlines(ctx, {
        lines: trend.linesRef.current,
        pending: trend.pendingRef.current,
        cursor: _cursor,
        hoveredId: trend.hoveredRef.current,
        dragging: trend.dragRef.current,
        candles: chartData.candles,
        geom: _g,
        mcapToY,
        phone: isPhone,
        layerCtx,   // zindex-20261102
      })
      // px -> {t, mcap} with the exact math used for _cursor above, so the
      // phone double-tap anchor lands where a desktop click would.
      const _mcapsF = frame.mcaps, _rhF = frame.RH || 12, _candlesF = chartData.candles
      const _toData = (x, y) => {
        if (!(_mcapsF?.length > 1)) return null
        const t = timeOfIndexF(indexOfX(x, _g), _candlesF, _ivMs)
        const m = yToMcapUtil(y, _mcapsF, _rhF)
        return (t > 0 && m > 0) ? { t, mcap: m } : null
      }
      trend.syncFrame(_cursor, _segs, _toData)
    }

    const activeSRLines = interaction.srLinesRef.current
    const hovSRId = interaction.hoveredSRLineIdRef.current
    if (activeSRLines.length > 0 && sortedMcaps.length > 1) {
      for (const line of activeSRLines) {
        const lineY = mcapToY(line.mcap)
        if (lineY < -2 || lineY > totalHeight + 2) continue
        const isHovered = hovSRId === line.id
        const isDraggingSR = dragItem?.type === 'sr' && dragItem.id === line.id
        let _sx = 0
        if ((line.ray || line.style === 'ray') && line.anchorTime) {
          let ai = -1
          for (let ci = candles.length - 1; ci >= 0; ci--) {
            if ((candles[ci]?.startTime ?? 0) <= line.anchorTime) { ai = ci; break }
          }
          if (ai >= 0) {
            const _cw = frame.cw
            _sx = Math.max(0, Math.min(plotWidth, frame.xLeft(ai) + (line.anchorFrac || 0) * _cw))
          }
        }
        const lc = layerCtx(line.index)   // zindex-20261102
        // Glow uses _sx too, so a ray only highlights its drawn part.
        lc.save()
        if (isHovered || isDraggingSR) {
          lc.strokeStyle = line.color; lc.lineWidth = line.weight + 6; lc.globalAlpha = 0.18
          lc.shadowColor = line.color; lc.shadowBlur = isDraggingSR ? 18 : 12; lc.setLineDash([])
          lc.beginPath(); lc.moveTo(_sx, lineY); lc.lineTo(plotWidth, lineY); lc.stroke(); lc.shadowBlur = 0
        }
        lc.globalAlpha = (isHovered || isDraggingSR) ? 1.0 : 0.85; lc.strokeStyle = line.color
        lc.lineWidth = (isHovered || isDraggingSR) ? line.weight + 1 : line.weight
        // Stroke pattern independent of ray flag (legacy style==='ray' → solid).
        if (line.style === 'dashed') lc.setLineDash([8, 5])
        else if (line.style === 'dotted') lc.setLineDash([2, 4])
        else lc.setLineDash([])
        // Ray: from the click point, rightward. Dedicated `ray` boolean (or
        // legacy style==='ray') so dashed/dotted can also extend. THE bug
        // behind "ray draws same as solid": this renderer always drew
        // 0 -> plotWidth and 'ray' fell through to the solid dash. (Earlier
        // fixes went to the TRENDLINE system - a different component sharing
        // the settings popover. Two line systems, one menu.) Every SR line
        // carries anchorTime - the clicked candle - and the drag handler
        // already moves it, so the ray starts at that candle's x and runs
        // right. Anchor scrolled off-left clamps to 0: still a ray, just
        // past its start.
        lc.beginPath(); lc.moveTo(_sx, lineY); lc.lineTo(plotWidth, lineY); lc.stroke(); lc.setLineDash([])
        // Same square as AVWAP / trendline. Phone only, and only while highlighted.
        // Ray: it sits on the start so a left/right drag can move the anchor.
        if (isPhone && (isHovered || isDraggingSR)) {
          const h = PHONE_HANDLE_HALF
          const hx = clampHandleX(_sx, plotWidth, h)
          lc.fillStyle = line.color
          lc.fillRect(hx - h, lineY - h, h * 2, h * 2)
        }
        if (line.label) {
          const labelFont = `500 11px "IBM Plex Sans KR", sans-serif`
          setCtxFont(lc, labelFont); lc.textBaseline = 'bottom'   // (perf3)
          lc.globalAlpha = (isHovered || isDraggingSR) ? 1.0 : 0.9; lc.fillStyle = line.color
          lc.fillText(line.label, plotWidth - lc.measureText(line.label).width - 4, lineY - 2)
        }
        lc.restore()
      }
    }

    // zindex-20261102: wipe a layer that had items last pass but none now.
    if (!_zA && _zDirty.a) { zLayerClear(zAboveLayerRef.current); _zDirty.a = false }
    if (!_zB && _zDirty.b) { zLayerClear(zBelowLayerRef.current); _zDirty.b = false }
    // 7. Swing highs / lows + market structure labels
    }   // end !liveOnly wrapper (AVWAP / SR)

    if (!liveOnly && showSwingsRef.current) {
      const { swingHighs, swingLows, structureEvents } = swingsRef.current
      if (sortedMcaps.length > 1) {
        const topMcapVal = sortedMcaps[0]

        // Snap to the nearest bucket row — same coordinate system as candle rendering
        // Snap to the SAME coordinate the wick tips use (historical candle
        // draw: yHigh = priceToRow(snapBucket(high))*RH, yLow =
        // (priceToRow(snapBucket(low)) + 1)*RH). The old version used
        // Math.round on raw mcap with no low-edge offset, so swing labels —
        // especially lows on historical/wick candles — landed up to ~1.5 rows
        // off the actual wick tip. `edge` picks top vs bottom.
        // Wick tips are CONTINUOUS (see candle pass), so labels anchor on the
        // same mapping. snapBucket was a second lattice; the row snap put
        // labels up to a row off the tip they point at.
        const mcapToRowY = (mcap) => frame.yOf(mcap)

        const swingCandleToX = (idx) => {
          if (idx < startIdx || idx > endIdx) return null
          return frame.xMid(idx)
        }

        // Build a lookup of structure events by candle index for O(1) access
        const structureByIdx = new Map()
        for (const ev of structureEvents) {
          structureByIdx.set(ev.index, ev)
        }

        ctx.save()
        setCtxFont(ctx, '500 9px "JetBrains Mono", monospace')   // (perf3)
        ctx.lineWidth = 1.5

        const OFFSET  = 8  // px above/below the candle extreme
        const TICK_H  = 5  // half-height of the vertical tick
        const ARM_LEN = 28 // horizontal arm length in px
        const GAP     = 4  // gap between arm end and text

        // Swing highs — marker sits ABOVE the high row
        for (const sh of swingHighs) {
          const x = swingCandleToX(sh.index)
          if (x === null) continue
          const y   = mcapToRowY(sh.mcap) - OFFSET  // above
          const ev  = structureByIdx.get(sh.index)
          const col = sh.exhaustion ? '#ff3a3a' : 'rgba(220,80,80,0.5)'

          ctx.strokeStyle = col
          ctx.fillStyle   = col

          // Vertical tick at candle center
          ctx.beginPath()
          ctx.moveTo(x, y - TICK_H)
          ctx.lineTo(x, y + TICK_H)
          ctx.stroke()

          // Horizontal arm extending left
          ctx.beginPath()
          ctx.moveTo(x, y)
          ctx.lineTo(x - ARM_LEN, y)
          ctx.stroke()

          // Label: structure event if confirmed, otherwise price level
          ctx.textBaseline = 'middle'
          ctx.textAlign    = 'right'
          if (ev) {
            const label = ev.type === 'BOS_HIGH' ? 'bos high' : 'choch bear'
            ctx.fillText(label, x - ARM_LEN - GAP, y)
          } else {
            ctx.globalAlpha = 0.55
            ctx.fillText(dataSourceRef.current === 'hyperliquid' ? formatPrice(sh.mcap) : formatMcap(sh.mcap), x - ARM_LEN - GAP, y)
            ctx.globalAlpha = 1
          }
        }

        // Swing lows — marker sits BELOW the low row
        for (const sl of swingLows) {
          const x = swingCandleToX(sl.index)
          if (x === null) continue
          const y   = mcapToRowY(sl.mcap) + OFFSET  // below the wick-low tip
          const ev  = structureByIdx.get(sl.index)
          const col = sl.exhaustion ? '#00e676' : 'rgba(60,200,120,0.5)'

          ctx.strokeStyle = col
          ctx.fillStyle   = col

          // Vertical tick
          ctx.beginPath()
          ctx.moveTo(x, y - TICK_H)
          ctx.lineTo(x, y + TICK_H)
          ctx.stroke()

          // Horizontal arm extending right
          ctx.beginPath()
          ctx.moveTo(x, y)
          ctx.lineTo(x + ARM_LEN, y)
          ctx.stroke()

          // Label: structure event if confirmed, otherwise price level
          ctx.textBaseline = 'middle'
          ctx.textAlign    = 'left'
          if (ev) {
            const label = ev.type === 'BOS_LOW' ? 'bos low' : 'choch bull'
            ctx.fillText(label, x + ARM_LEN + GAP, y)
          } else {
            ctx.globalAlpha = 0.55
            ctx.fillText(dataSourceRef.current === 'hyperliquid' ? formatPrice(sl.mcap) : formatMcap(sl.mcap), x + ARM_LEN + GAP, y)
            ctx.globalAlpha = 1
          }
        }

        ctx.restore()
      }
    }

    // 8. (ATR Trailing Stop moved earlier — now drawn before footprint cells
    //     so it sits BEHIND the candles/footprint instead of on top.)

    // 9. Orderflow Bot — metrics on the 4Hz strategy cadence, overlay drawn
    //    only when enabled. `_strategyTickDue` is shared with the bundle
    //    detector below so both tick on the same beat.
    const _strategyNow = performance.now()
    const _strategyTickDue = _strategyNow - strategyTickAtRef.current >= 250
    if (_strategyTickDue) strategyTickAtRef.current = _strategyNow
    // Tape clock for strategy consumers: SERVER-aligned app time for BOTH
    // Solana and futures. HL/CEX trades carry exchange timestamps; this clock
    // is only for "now" windows (never client Date.now() on the HL path).
    const _tapeNow = data.serverNowRef?.current?.() ?? Date.now()
    const bot = botRef.current
    if (bot && _strategyTickDue) {
      // Liq reaction live hook (longpress-liq-20261017). Re-attached every
      // tick because botRef can be swapped for a fresh instance. The bot's
      // liq tracker runs on every update, enabled or not, so this works
      // with the bot off. WalletHoldings' long-press arm listens for it.
      // liq3s-anytf-20261019: the arm fires off the chart's Liq labels now
      // (absorption memo), so the bot's own tracker no longer emits.
      bot.onLiqReaction = null
      bot.update({
        now: _tapeNow,
        candles,
        highlightMcap: chartData.highlightMcap,
        flipZones: flipZonesRef.current,
        atrStop: atrStopRef.current,
        swings: swingsRef.current,
        mcapBucketSize: bucketSize,
        meteoraBins: meteoraBinsRef.current,
        timeInterval: timeIntervalRef.current,
        token: data.currentToken,
        // Raw swap buffer (rolling, kept by useChartData). Bot can do its
        // own time-window aggregation independent of the chart's TF — used
        // for flow metrics (reactivity, heat, churn) and per-stage windowing.
        // Shape: [{time, side, amount, tradeMcap}, ...]. consumerView() returns
        // a stable, bounded real Array (recent window) — the columnar buffer's
        // Array.isArray-compatible face for the strategy consumers.
        rawSwaps: data.rawTradesRef?.current?.consumerView() || null,
      })
    }
    // Overlay DRAW is per-frame, deliberately outside the 4Hz gate above.
    // It used to live inside it — but drawCanvas clears and repaints the
    // whole canvas on every frame (data ticks, the cell-anim rAF pump, the
    // render nudge), so a band painted only on tick frames survived ~1 frame
    // in 15 and the range strobed on and off. Same split the bot markers
    // already use below: think on the strategy beat, paint every frame.
    //
    // SOURCE = activeBotRef, not the local `bot`. `bot` is the local
    // strategy instance that update() has to drive; the range/PILOT/BASIS/
    // STOP lines belong to whichever brain is actually holding the position,
    // which in server mode is the runner (RemoteBot mirrors the runner's
    // getStatus(), so activeBase/pilot/load arrive already shaped).
    const overlayBot = activeBotRef.current || bot
    if (!liveOnly && overlayBot && showBotOverlayRef.current) {
      drawBotOverlay(ctx, overlayBot, {
        plotWidth,
        totalHeight,
        sortedMcaps,
        mcapToY: frame.yOf,
        RH,
        bucketSize,
      })
    }

    // 9b. Bundle detector — SHADOW. Same rawSwaps buffer the bot reads;
    //     gated to the same 4Hz strategy cadence, logs would-fires, touches
    //     no pixels. Independent of whether the bot is enabled — it's a
    //     market observer, not a trader.
    const bundle = bundleRef.current
    if (bundle && _strategyTickDue) {
      bundle.update({
        now: _tapeNow,
        swaps: data.rawTradesRef?.current?.consumerView() || null,
        mcap: chartData.highlightMcap,
        token: data.currentToken,
      })
    }

    if (!liveOnly) {   // 9c is pixels + journal->marker rebuild; markers persist between full draws
    // 9c. BOT TRADE MARKERS — ◄ arrows at the bot's executed trades.
    //     Green = buys (pilot entry + load add), red = sells (exits).
    //     Source: the bot JOURNAL — one code path for BOTH modes, because
    //     the local OrderflowBot and RemoteBot expose the same
    //     getJournal() shape. In server mode the entries come from the
    //     runner over the app WS (RemoteBot sends {cmd:'journal'} and
    //     refreshes on every EXIT it sees in bot_status), so the server
    //     is the store and the chart is the viewer. Entries are keyed to
    //     candle startTime buckets at the CURRENT timeframe, so markers
    //     survive TF switches; trades older than the loaded candle
    //     history simply find no candle and skip. Rebuild is 4Hz-gated;
    //     the DRAW runs every frame so arrows ride pan/zoom/rebucket
    //     exactly like cells. (Open positions aren't in the journal until
    //     they close — the live PILOT/BASIS overlay lines cover those.)
    //     SOURCE = activeBotRef (local OrderflowBot in local mode, RemoteBot
    //     in server mode) — NOT the local `bot` above. `bot` is the local
    //     strategy instance that update()/drawBotOverlay need; the journal
    //     must come from whichever brain actually took the trades.
    const journalBot = activeBotRef.current || bot
    if (journalBot && _strategyTickDue && showBotTradesRef.current) {
      const tok = data.currentToken
      // On a futures chart the y-axis is a perp price, not a memecoin market
      // cap, so a runner entry tagged with a mint would map to a meaningless
      // y (and `tok` is empty in futures mode, so the token filter below
      // can't catch it). Drop tagged rows there and keep only untagged ones,
      // which is exactly the shape a local HL trade journals as.
      const isFut = dataSourceRef.current === 'hyperliquid'
      const intervalMs = (timeIntervalRef.current || 60) * 1000
      const out = []
      try {
        const journal = journalBot.getJournal ? journalBot.getJournal() : []
        botMarkerDiagRef.current.journalLen = journal.length
        let _tokFiltered = 0
        for (const j of journal) {
          if (!j) continue
          // Tagged rows must match the charted token; untagged rows
          // (pre-tagging local entries) are shown only when tokens can't
          // be compared — better to under-draw than to paint another
          // coin's trades on this chart.
          if (isFut) {
            if (j.token) { _tokFiltered++; continue }
          } else {
            if (j.token && tok && j.token !== tok) { _tokFiltered++; continue }
            if (!j.token && tok && j.adopted) { _tokFiltered++; continue }
          }
          if (!j.adopted) {
            if (j.pilotEntry?.mcap > 0 && j.openedAt) {
              out.push({ t: Math.floor(j.openedAt / intervalMs) * intervalMs, mcap: j.pilotEntry.mcap, side: 'buy' })
            }
            if (j.load?.addMcap > 0 && j.load.addedAt) {
              out.push({ t: Math.floor(j.load.addedAt / intervalMs) * intervalMs, mcap: j.load.addMcap, side: 'buy' })
            }
          }
          if (j.exit?.mcap > 0 && j.closedAt) {
            out.push({ t: Math.floor(j.closedAt / intervalMs) * intervalMs, mcap: j.exit.mcap, side: 'sell' })
          }
        }
        const d = botMarkerDiagRef.current
        d.mode = journalBot === botRef.current ? 'local' : 'server'
        d.token = tok || null
        d.tokenFiltered = _tokFiltered
        d.built = out.length
      } catch {}
      botMarkersRef.current = out
    }
    const _ownTags = qtPaperRef.current ? paperMarksRef.current.list : ownTagsRef.current.list
    if (sortedMcaps.length > 1 && ((showBotTradesRef.current && botMarkersRef.current.length > 0) || _ownTags.length > 0)) {
      const cArr = chartData.candles || []
      const mTimeToIdx = new Map()
      for (let i = 0; i < cArr.length; i++) {
        const t = cArr[i]?.startTime
        if (t != null) mTimeToIdx.set(t, i)
      }
      let _mapped = 0, _drawn = 0
      // bot-tag-20261025: tags go on the topmost canvas (zoneLabelLayer, z 6)
      // so neither the main cells nor the live layer can cover them. That
      // layer is sized/translated/cleared by the hold-label pass above on
      // this same full draw, so we just paint with the main ctx's transform.
      const _LL = zoneLabelLayerRef.current
      let tctx = ctx
      if (_LL) { try { tctx = _LL.getContext('2d'); tctx.setTransform(ctx.getTransform()) } catch { tctx = ctx } }
      tctx.save()
      for (const m of (showBotTradesRef.current ? botMarkersRef.current : [])) {
        const idx = mTimeToIdx.get(m.t)
        if (idx != null) _mapped++
        if (idx == null || idx < startIdx || idx > endIdx) continue
        _drawn++
        const y = frame.yOf(m.mcap)
        if (y < -20 || y > totalHeight + 20) continue
        const isBuy = m.side === 'buy'
        const c = cArr[idx]
        const tip = tradeTagTip(frame, idx, m.mcap, isBuy, RH, _yRow, sortedMcaps.length,
          asCandle || !c || (c.isHistorical && !c._omFootprint))
        const R = Math.max(7, Math.min(13, pxPerCandle * 0.38) * 0.85)
        drawTradeTag(tctx, tip.x, tip.y, isBuy, R, isBuy ? '#00cc6a' : '#ff4455', 'bot')
      }
      // own-tag-20261025: your own fills, same tag, yellow ring + order size.
      if (_ownTags.length) {
        const _iMs = (timeIntervalRef.current || 60) * 1000
        for (const m of _ownTags) {
          const idx = mTimeToIdx.get(Math.floor(m.ts / _iMs) * _iMs)
          if (idx == null || idx < startIdx || idx > endIdx) continue
          const y = frame.yOf(m.mcap)
          if (y < -20 || y > totalHeight + 20) continue
          const isBuy = m.side === 'buy'
          const c = cArr[idx]
          const tip = tradeTagTip(frame, idx, m.mcap, isBuy, RH, _yRow, sortedMcaps.length,
            asCandle || !c || (c.isHistorical && !c._omFootprint))
          const R = Math.max(7, Math.min(13, pxPerCandle * 0.38) * 0.85)
          drawTradeTag(tctx, tip.x, tip.y, isBuy, R, '#ffd000', fmtTagSize(m.amount))
        }
      }
      tctx.restore()
      botMarkerDiagRef.current.mapped = _mapped
      botMarkerDiagRef.current.drawn = _drawn
    }

    }   // end !liveOnly wrapper (9c)

    // 9d. ARD SHADOW — Aggression-Response Divergence detector, client-side.
    //     Observer only (never trades). Fed the same rawSwaps buffer as the
    //     bundle detector; its would-fires collect in ardMarkersRef and paint
    //     below as HOLLOW arrows — long = green ▲ under the bar, protect =
    //     red ▼ over the bar — so the would-have-beens sit beside the solid ◄
    //     fills for eyeball validation. Reset on token switch so token A's
    //     pending events never fire against token B.
    {
      const ard = ardRef.current
      if (ard && _strategyTickDue && (showArdRef.current || ardArmedRef.current)) {
        const tok = data.currentToken
        if (tok !== ardTokenRef.current) {
          ard.reset()
          ardMarkersRef.current = []
          ardSeenRef.current.clear()
          ardTokenRef.current = tok
        }
        // Nearest ask wall above price from the DLMM bins (best-effort; the
        // detector normalizes number|object and treats null as "no wall").
        let wallAbove = null
        const bins = meteoraBinsRef.current
        if (Array.isArray(bins) && chartData.highlightMcap > 0) {
          let best = Infinity
          for (const b of bins) {
            if (b && b.mcap > chartData.highlightMcap && (b.notionalSol || 0) >= 5 && b.mcap < best) best = b.mcap
          }
          if (best < Infinity) wallAbove = best
        }
        ard.update({
          now: _tapeNow,
          swaps: data.rawTradesRef?.current?.consumerView() || null,
          mcap: chartData.highlightMcap,
          token: tok,
          wallAbove,
        })
      }
    }
    if (!liveOnly && (showArdRef.current || ardArmedRef.current) && ardMarkersRef.current.length > 0 && sortedMcaps.length > 1) {
      const cArr = chartData.candles || []
      const aTimeToIdx = new Map()
      for (let i = 0; i < cArr.length; i++) {
        const t = cArr[i]?.startTime
        if (t != null) aTimeToIdx.set(t, i)
      }
      ctx.save()
      ctx.lineWidth = 1.5
      for (const m of ardMarkersRef.current) {
        const idx = aTimeToIdx.get(m.t)
        if (idx == null || idx < startIdx || idx > endIdx) continue
        const x = frame.xMid(idx)
        const y = frame.yOf(m.mcap)
        if (y < -20 || y > totalHeight + 20) continue
        const s = Math.max(5, Math.min(10, pxPerCandle * 0.30))
        // HOLLOW (stroked) triangle so shadow reads distinct from the solid
        // ◄ fills. LONG → green ▲ just BELOW price; PROTECT → red ▼ ABOVE.
        const isLong = m.dir === 'LONG'
        ctx.strokeStyle = isLong ? '#00e08a' : '#ff5a6a'
        const gap = 6
        ctx.beginPath()
        if (isLong) {
          const ty = y + gap            // apex points up toward price
          ctx.moveTo(x, ty)
          ctx.lineTo(x - s * 0.7, ty + s)
          ctx.lineTo(x + s * 0.7, ty + s)
        } else {
          const ty = y - gap            // apex points down toward price
          ctx.moveTo(x, ty)
          ctx.lineTo(x - s * 0.7, ty - s)
          ctx.lineTo(x + s * 0.7, ty - s)
        }
        ctx.closePath()
        ctx.stroke()
      }
      ctx.restore()
    }

    // =============================================
    // AXIS BACKDROP — dynamic width, matched to the DOM y-axis column
    // =============================================
    // Was a FIXED 60px strip while the DOM column is text-sized: the two
    // surfaces disagreed, so candle cells / trail showed through under
    // wider columns, and under narrower ones the solid strip jutted past
    // the labels and buried the volume profile's bar tips (the strip
    // paints at z2, ABOVE the z0 profile layer) — "the text area adjusts
    // but the canvas and profile are under it." PAINT ONLY: the plot math
    // still reserves FIXED_OVERLAY_WIDTH everywhere (candle x-mapping,
    // trail, CVD, AVWAP untouched); only where the solid ink goes changed.
    if (!liveOnly) {   // axis backing + separator — static ink, full-only
    const axisW = axisWRef.current > 0 ? axisWRef.current : FIXED_OVERLAY_WIDTH
    const overlayX = containerW - RIGHT_PADDING - axisW

    ctx.save()
    // Screen-space strip: never take X scaleX (same bug as VP). Y camera only
    // so the gutter still tracks the lattice glide.
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    if (cameraActive) ctx.transform(1, 0, 0, cameraScale, 0, cameraTy)

    // Background
    ctx.fillStyle = 'rgba(15, 15, 25, 0.96)'
    ctx.fillRect(overlayX, 0, axisW, totalHeight)

    // Left separator line
    ctx.strokeStyle = '#222'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(overlayX, 0)
    ctx.lineTo(overlayX, totalHeight)
    ctx.stroke()

    ctx.restore()

    // Phone WebKit: WebGL cell layer often composites ABOVE this 2D axis
    // backing. Punch the same strip transparent on the GL canvas so closed
    // footprint cells cannot cover the DOM y-axis (live layer already
    // clearRect's its own canvas below).
    {
      const _glAxis = cellGlRef.current
      if (_glAxis && _glAxis.ok && _glAxis.clearCssRect) {
        _glAxis.clearCssRect(overlayX, 0, containerW - overlayX, totalHeight)
      }
    }
    }   // end !liveOnly wrapper (axis backing)

    // ── Live layer: keep the forming candle out of the y-axis strip ──
    // The axis backing is painted at the END of the FULL pass, so it used to
    // mask any cell that overflowed into the axis. The live canvas sits ABOVE
    // that backing (z3 vs z2), so nothing masks it and the forming candle drew
    // over the axis while every closed candle stayed clipped underneath.
    // Erasing the strip after drawing is the whole fix: no clip state to leak
    // across frames, no save/restore to unbalance, and the rect is tiny.
    // Uses the same axisW fallback as the backing itself, so the two edges
    // cannot disagree.
    if (liveOnly) {
      const axisWLive = axisWRef.current > 0 ? axisWRef.current : FIXED_OVERLAY_WIDTH
      const axisXLive = containerW - RIGHT_PADDING - axisWLive
      // Match axis backing: clear in screen space (no scaleX), Y camera only.
      ctx.save()
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      if (cameraActive) ctx.transform(1, 0, 0, cameraScale, 0, cameraTy)
      ctx.clearRect(axisXLive, 0, containerW - axisXLive, totalHeight)
      ctx.restore()
    }

    // Pump RAF while any cell animation is still running, so the flip
    // continues smoothly even if no new trades arrive between frames.
    // Self-throttling: once all anims finish (entries deleted by their
    // own draw blocks at t>=1), both maps hit 0 and we stop pumping.
    // STAGE-1 SPLIT: the pump now repaints the LIVE LAYER ONLY — the whole
    // point of the split. Runs from both passes (a full frame with anims
    // starts the pumping; each live frame re-arms it), on its OWN flag so it
    // can never suppress a scheduled full draw.
    const animsActive = cellAnimsBuyRef.current.size > 0 || cellAnimsSellRef.current.size > 0
    if (animsActive && !liveDrawScheduledRef.current && !document.hidden) {
      liveDrawScheduledRef.current = true
      // (perf2) The pump goes through the shared frame driver
      // (scheduleCanvasDraw, Light level) instead of its own rAF. A glide
      // frame that already has a Full draw pending absorbs it (the Full pass
      // ends with the live pass), so it is ONE draw per frame, not two.
      const pump = () => {
        liveDrawScheduledRef.current = false
        if (scheduleCanvasDrawRef.current) scheduleCanvasDrawRef.current('live')
        else if (drawCanvasRef.current) drawCanvasRef.current('live')
      }
      // Phones pump the flip animation at ~30fps instead of 60. On a busy
      // tape anims are near-continuously active, so this loop IS the frame
      // rate — and the full draw pass is the phone's biggest per-frame
      // cost. A 300ms scale flip reads identically at half rate; the 17ms
      // timeout + rAF lands each pump on the next-but-one vsync. Desktop
      // keeps the straight rAF chain.
      if (COARSE_POINTER) {
        setTimeout(pump, 17)   // pump arms the driver's next rAF
      } else {
        pump()
      }
    }
    // chartData intentionally omitted — read via chartDataRef so this callback
    // stays identity-stable across data ticks (prevents per-tick synchronous
    // redraws). visibleSortedMcaps/dynamicRowHeight/botEnabled/chartMode are
    // view/mode values that genuinely require a fresh draw closure.
    // Y-AXIS IN THE SAME PASS. It reads lastFrameRef, which this pass just
    // published, so ticks and cells can no longer be one frame apart.
    if (!liveOnly) {
      const _ar = bucketRowIndex(frame.mcaps, frame.livePrice)
      const k = axisPaintKeyRef.current
      // Armed limit band is continuous. The row index does not move while
      // price walks inside a step, so keying on the row left the edge parked.
      const band = axisBandArmedRef.current ? frame.livePrice : 0
      if (k.mcaps !== frame.mcaps || k.RH !== frame.RH || k.row !== _ar || k.band !== band) {
        axisPaintKeyRef.current = { mcaps: frame.mcaps, RH: frame.RH, row: _ar, band }
        yAxisPaintRef.current?.()
      }
    }
    // Full pass complete → repaint the live layer against the fresh geometry
    // (rebucket / zoom / theme / close all land here). Live never calls full,
    // so recursion depth is exactly 1.
    if (!liveOnly) drawCanvasRef.current?.('live')
    // Own-canvas heat: force paint after full geom draw (SoT for heat catch-up).
    // Wheel glide moves the footprint centre/half. Heat takes the same force
    // path as a Y glide so paintNow re-projects with this frame's rowOfPrice
    // and rowHeight (lastFrameRef) instead of freezing on translateY(pinPx).
    // Trail stays unforced mid X-glide — a full trail rebuild every frame was
    // the xflash. Land still forces both once (endChartInteract).
    if (!liveOnly) {
      const xGliding = !!(zoomGlideRef.current || zoomGlideRafRef.current)
      if (xGliding) {
        heatLayerPaintRef.current?.({ mode: 'force' })
        trailLayerPaintRef.current?.()
      } else {
        heatLayerPaintRef.current?.({ mode: 'force' })
        trailLayerPaintRef.current?.({ mode: 'force' })
      }
    }
  }, [visibleSortedMcaps, dynamicRowHeight, botEnabled, chartMode, tpoLetters])

  drawCanvasRef.current = drawCanvas

  // UNIVERSAL RENDER-NUDGE. The chart is event-drawn (data ticks and
  // interactions call drawCanvas); mode toggles and indicator flags change
  // WHAT the next paint shows but don't CAUSE a paint, so on a quiet token a
  // toggle sits invisible until the next trade arrives. Instead of enrolling
  // every flag in a dependency list (a ritual every future indicator would
  // have to remember), catch it at the chart: ANY state change re-renders
  // this component, and this no-deps effect runs after every commit —
  // rAF-coalesced, and skipped when a draw already happened this frame
  // (data-driven renders drew already, so ticks cost nothing). The switch
  // gate keeps sole ownership of draws mid-switch, so no stale-epoch paints.
  // A nudge draw is byte-identical to a tick draw: one synchronous pass,
  // presented whole — no flash.
  const renderNudgeRef = useRef(false)
  useEffect(() => {                     // no dep array — after every commit
    if (renderNudgeRef.current) return
    renderNudgeRef.current = true
    requestAnimationFrame(() => {
      renderNudgeRef.current = false
      if (isSwitchingRef.current) return
      if (Date.now() - lastDrawTsRef.current > 16) {
        // (scrollperf) Mid-gesture, fold into the shared frame-driver draw
        // instead of a second synchronous full pass in its own rAF. At rest,
        // unchanged.
        if (chartInteractingRef.current) scheduleCanvasDraw()
        else drawCanvasRef.current?.()
      }
    })
  })

  // Sync overlays + redraw synchronously before paint
  useLayoutEffect(() => {
    interaction.avwapsRef.current = interaction.avwaps
    interaction.srLinesRef.current = interaction.srLines
    // hoveredAvwapIdRef is the hover source of truth now (chart-perf): hover
    // writes it and schedules a draw, with no React state, so nothing to sync.
    interaction.hoveredSRLineIdRef.current = interaction.hoveredSRLineId
    if (drawCanvasRef.current) drawCanvasRef.current()
  }, [interaction.avwaps, interaction.srLines, interaction.hoveredSRLineId])

  // Single shared rAF for high-frequency canvas draws (pan, live ticks,
  // mid-interact layout). Vela Scheduler pattern: invalidate levels coalesce
  // to one rAF; highest wins. Full subsumes Live/Light if both pending.
  // Levels: None=0, Cursor=1, Light=2, Full=3. Cursor skipped for now
  // (crosshair is DOM/imperative). Static chart = no armed rAF.
  const InvalidateLevel = { None: 0, Cursor: 1, Light: 2, Full: 3 }
  const drawRafRef = useRef(0)
  const pendingInvalidateRef = useRef(0) // InvalidateLevel; was pendingDrawModeRef
  const scheduleCanvasDraw = useCallback((mode) => {
    // API: scheduleCanvasDraw('full'|'live'|'light') or numeric level.
    // undefined / 'full' / other → Full; 'live'|'light' → Light.
    let level = InvalidateLevel.Full
    if (mode === 'live' || mode === 'light') level = InvalidateLevel.Light
    else if (mode === 'cursor') level = InvalidateLevel.Cursor
    else if (typeof mode === 'number') level = mode
    if (level > pendingInvalidateRef.current) pendingInvalidateRef.current = level
    if (drawRafRef.current) return
    // Keep boolean flag in sync for any legacy readers / comments.
    canvasDrawScheduledRef.current = true
    drawRafRef.current = requestFrame(() => {
      drawRafRef.current = 0
      canvasDrawScheduledRef.current = false
      const lvl = pendingInvalidateRef.current
      pendingInvalidateRef.current = InvalidateLevel.None
      if (lvl <= InvalidateLevel.None) return
      if (lvl <= InvalidateLevel.Cursor) return // Cursor: no canvas path yet
      // Light → existing live-only forming-candle path; Full → full drawCanvas
      drawCanvasRef.current?.(lvl >= InvalidateLevel.Full ? undefined : 'live')
      // (perf4) THE Y GLIDE'S DRAW FOR THE NEXT FRAME IS ARMED HERE, AFTER
      // this frame's draw. The glide tick (FRAME_ANIM) runs BEFORE this
      // callback in the same frame, so its scheduleCanvasDraw found this
      // request still pending and merged into it — correct for THIS frame,
      // but it left nothing queued for the next one. Frames alternated:
      // draw (two glide steps' worth of movement), no draw, draw… — a 30Hz
      // judder at 60Hz, unless something unrelated (cell anims, data) kept a
      // draw queued. While the walker is armed, keep exactly one draw queued
      // per frame; it runs after that frame's step, so every step is painted
      // in the frame it happens. Stops by itself when the glide lands.
      // Same for the X ZOOM glide (wheel / footer / pinch): it had the exact
      // same alternation — [xglide] showed LIVE-only frames with no full
      // pass while win kept moving, then a full pass two steps later: the
      // "double call". One full draw queued per frame while it runs.
      if (yGlideRafRef.current || zoomGlideRafRef.current) scheduleCanvasDrawRef.current?.()
    }, FRAME_DRAW)
  }, [])
  scheduleCanvasDrawRef.current = scheduleCanvasDraw   // (scrollperf)

  // (y2b / yexplore) ONE Y-CAMERA FRAME WITHOUT A REACT RENDER. Runs the
  // camera exactly once against the last pass's inputs (lattice.yPass) — the
  // camera reads its own user span / explore centre — then the SAME pure
  // geometry the memo uses (latticeGeomFor). If the rows come out identical to
  // the current lattice, this is the memo's reuse branch done in place:
  // residual / halfRange / rowHalf / fitCenter stamped on the same array, RH +
  // residual refs recomputed with the same functions the dynamicRowHeight memo
  // and residual effect use, one coalesced draw. Returns false (caller bumps
  // yFitTick; the memo reuses this frame's camera through yCamHandoffRef) when
  // the rows change, or when ANY pass input has moved or a mode the memo
  // short-circuits is active — those keep the React path.
  // (drag-smooth) Body-drag fit, in place. The memo's dragFitLive branch
  // frames from the glide refs (no yCam.frame) with pinned geometry; this
  // runs that SAME geometry against the last pass. Same rows -> stamp the
  // reuse fields + RH/residual and draw. Anything else -> false (render).
  dragFitInPlaceRef.current = () => {
    const L = visibleSortedMcapsRef.current
    const p = L && L.yPass
    if (!p || L !== prevMcapsRef.current || !(L.length > 0)) return false
    if (isSwitchingRef.current) return false
    if (!yDragFitDestRef.current || yCam.isPinned()) return false
    if (zoomGlideRef.current != null || zoomTargetWinRef.current != null) return false
    if (yGlideRafRef.current || yHalfTargetRef.current != null || yAxisZoomDragActiveRef.current || yCam.isGesturing()) return false
    if (tfRebucketPendingRef.current || tfWinRef.current) return false
    if (lastLogModeRef.current !== p.LOG) return false
    const cd = chartDataRef.current
    if (!cd || cd.candles !== p.candles || cd.highlightMcap !== p.highlight) return false
    if (viewWindowSizeRef.current !== p.win) return false
    if (containerHeightRef.current !== p.H || containerWidthRef.current !== p.containerW) return false
    if (logScaleRef.current !== p.logScale
        || mcapBucketSizeRef.current !== p.mcapBucketSize
        || mcapRatioRef.current !== p.mcapRatio) return false
    if ((targetRowHRef.current || 20) !== p.targetPx) return false
    const half = glideHalfRef.current, centre = glideCenterRef.current
    if (!Number.isFinite(half) || !Number.isFinite(centre) || !(half > 0)) return false
    const g = latticeGeomFor({
      center: centre, halfRange: half, bucketSize: p.bucketSize,
      pinned: true, holdQuiet: false,
      containerHeight: p.H, viewWindowSize: p.win,
      containerW: p.containerW,
      log: p.LOG, minStep: 0,
      refinePx: Y_REFINE_ROW_MULT * (p.targetPx || 20),
      targetPx: p.targetPx || 20,
    })
    if (!g) return false
    const LOGR = p.LOG && !g.lin
    const extCount = g.count + 1
    const first = LOGR ? Math.exp(g.rangeMax) : g.rangeMax
    const lastI = extCount - 1
    const last = LOGR ? Math.exp(g.rangeMax - lastI * g.gridBucket) : g.rangeMax - lastI * g.gridBucket
    if (!(L.log === LOGR && L.length === extCount && L.visibleCount === g.count
          && L.step === g.gridBucket && L[0] === first && L[L.length - 1] === last)) return false
    lastRangeMaxRef.current = { value: g.rangeMax, bucket: p.bucketSize }
    L.residualBuckets = yCam.slide(g.slideTop, g.gridBucket)
    L.halfRange = g.halfRange
    L.floorRaised = g.halfRange > half
    L.rowHalf = g.lin ? g.linHalf : undefined
    L.fitCenter = centre
    const RH = rowHeightFor(L, p.H, L.visibleCount ?? L.length)
    dynamicRowHeightRef.current = RH
    yResidualPxRef.current = yPinResidualPx(L, RH, p.H)
    scheduleCanvasDrawRef.current?.('full')
    return true
  }
  const yCamInPlaceRef = useRef(() => false)
  yCamInPlaceRef.current = () => {
    const L = visibleSortedMcapsRef.current
    const p = L && L.yPass
    if (!p || L !== prevMcapsRef.current || !(L.length > 0)) return false
    if (isSwitchingRef.current) return false
    // An unpinned drag belongs to the X-drag fit (glide refs). With a user
    // span the drag is explore, and Y-only moves land here.
    if (isDraggingChartRef.current && !yCam.isPinned()) return false
    if (zoomGlideRef.current?.cone
        && Number.isFinite(glideHalfRef.current)
        && Number.isFinite(glideCenterRef.current)
        && !yCam.isPinned()
        && yHalfTargetRef.current == null
        && !yAxisZoomDragActiveRef.current
        && !yCam.isGesturing()) return false
    if ((zoomGlideRef.current != null || zoomTargetWinRef.current != null)
        && yHalfTargetRef.current == null
        && !yAxisZoomDragActiveRef.current
        && !yCam.isGesturing()) return false
    if (tfRebucketPendingRef.current || tfWinRef.current) return false
    if (lastLogModeRef.current !== p.LOG) return false
    const cd = chartDataRef.current
    if (!cd || cd.candles !== p.candles || cd.highlightMcap !== p.highlight) return false
    // (diag) Exploring = the camera ignores the visible window (pan clamp is the
    // full data set), so an X candle step mid diagonal drag must not bounce Y
    // to the next React pass: that one-frame Y hold was the stair step.
    if (viewWindowSizeRef.current !== p.win) return false
    if (viewEndIndexRef.current !== p.end && !yCam.isExploring()) return false
    if (containerHeightRef.current !== p.H || containerWidthRef.current !== p.containerW) return false
    if (logScaleRef.current !== p.logScale
        || mcapBucketSizeRef.current !== p.mcapBucketSize
        || mcapRatioRef.current !== p.mcapRatio) return false
    if ((targetRowHRef.current || 20) !== p.targetPx) return false
    // The camera, once. xMoving is what the memo computes right now (same
    // expression), so the hand-off matches; logChanged is guarded above.
    const xMoving = !!(
      isDraggingChartRef.current
      || zoomGlideRef.current != null
      || zoomTargetWinRef.current != null
    )
    const camIn = {
      min: p.min, max: p.max, step: p.bucketSize,
      highlight: p.highlight, log: p.LOG, xMoving,
      liveInView: p.liveInView, logChanged: false,
      liveLo: p.liveLo, liveHi: p.liveHi,
      clampMin: p.fullLo, clampMax: p.fullHi,
      minHalfLin: p.minHalfLin,
    }
    const cam = yCam.frame(camIn)
    fdCount('camInPlace')   // (perf4) debug counter
    yCamHandoffRef.current = {
      inputs: camIn, cam, at: performance.now(),
      holdAfter: yCam.peekFitHold(), pinnedAfter: yCam.isPinned(),
    }
    const userSpan = yCam.isPinned()
    const minStep = userSpan ? (p.minStep || 0) : 0
    const g = latticeGeomFor({
      center: cam.centre, halfRange: cam.halfSpan, bucketSize: p.bucketSize,
      pinned: userSpan, holdQuiet: !!cam.holdQuiet,
      containerHeight: p.H, viewWindowSize: p.win,
      containerW: p.containerW,
      log: p.LOG, minStep,
      refinePx: Y_REFINE_ROW_MULT * (p.targetPx || 20),
      targetPx: p.targetPx || 20,
    })
    if (!g) return false
    // Row identity, with the memo's own arithmetic (array build + reuse test).
    const LOGR = p.LOG && !g.lin
    const extCount = g.count + 1
    const first = LOGR ? Math.exp(g.rangeMax - 0 * g.gridBucket) : g.rangeMax - 0 * g.gridBucket
    const lastI = extCount - 1
    const last = LOGR ? Math.exp(g.rangeMax - lastI * g.gridBucket) : g.rangeMax - lastI * g.gridBucket
    if (!(L.log === LOGR && L.length === extCount && L.visibleCount === g.count
          && L.step === g.gridBucket && L[0] === first && L[L.length - 1] === last)) {
      return false   // rows change → React rebuilds them with THIS camera
    }
    // Same rows — the memo's reuse branch, in place.
    lastRangeMaxRef.current = { value: g.rangeMax, bucket: p.bucketSize }
    L.residualBuckets = yCam.slide(g.slideTop, g.gridBucket)
    L.halfRange = g.halfRange
    L.floorRaised = g.halfRange > cam.halfSpan
    L.rowHalf = g.lin ? g.linHalf : undefined
    L.fitCenter = cam.fitCentre
    L.winSizeF = p.win
    lastViewWindowSizeRef.current = p.win
    // Viewport layer: dynamicRowHeight memo + residual layout effect.
    const RH = rowHeightFor(L, p.H, L.visibleCount ?? L.length)
    dynamicRowHeightRef.current = RH
    yResidualPxRef.current = yPinResidualPx(L, RH, p.H)
    scheduleCanvasDraw('full')
    fdCount('inPlaceOk')   // (perf4) debug counter
    return true
  }

  // (yexplore) THE USER Y SPAN. Armed by the first Y-axis wheel / phone axis
  // drag from what is painted (lattice half + centre), so arming moves
  // nothing. The X cone / drag fit stand down on their own once pinned.
  const ensureUserSpanRef = useRef(() => false)
  ensureUserSpanRef.current = () => {
    if (yCam.isPinned()) return true
    const L = visibleSortedMcapsRef.current
    if (!L || !(L.halfRange > 0) || !Number.isFinite(L.fitCenter)) return false
    yCam.armSpan(L.halfRange, L.fitCenter)
    return yCam.isPinned()
  }
  // Span limits, working space. (ytickcap) Floor: the camera's one-tick floor
  // — one tick per row at the TARGET row height, the same number frame()
  // enforces, so a target below it can never fight the camera. No tick
  // known: the old 0.2x-of-range floor. Ceiling: 5x the default fit span.
  const yHalfLimitsRef = useRef(() => ({ lo: 0, hi: Infinity }))
  yHalfLimitsRef.current = () => {
    const L = visibleSortedMcapsRef.current
    const p = L && L.yPass
    const H = containerHeightRef.current || 0
    if (!p || !(H > 0)) return { lo: Math.max(1e-12, yCam.floorHalf()), hi: Infinity }
    const step = p.bucketSize > 0 ? p.bucketSize : 0
    const range = Math.max(0, p.max - p.min)
    const fitHalf = Math.max(range / 2 + computeRangeBuffer(range, step), (24 * step) / 2)
    let lo = yCam.floorHalf()
    if (!(lo > 0)) lo = range * 0.1
    if (!(lo > 0)) lo = step > 0 ? step : 1e-12
    return { lo, hi: Math.max(fitHalf * 5, lo) }
  }

  // (yexplore) BODY DRAG Y = EXPLORE. Only with a user span (default fit keeps
  // the X-drag fit). The first move with Y_EXPLORE_ARM_PX of vertical travel
  // enters explore at the painted centre (nothing jumps); follow and auto-fit
  // stop. After that every move pans the centre by dy at the current scale,
  // in step with X. No coast. Clamp lives in yCam.frame().
  const yBodyDragStateRef = useRef(null)
  yBodyDragRef.current = (phase, clientY) => {
    if (phase === 'start') {
      yBodyDragStateRef.current = { y0: clientY, last: clientY, on: false, moved: false }
      return
    }
    const st = yBodyDragStateRef.current
    if (!st) return
    if (phase === 'end') {
      yBodyDragStateRef.current = null
      // In-place frames skipped React; one pass syncs the memos.
      if (st.moved) setYFitTick((t) => t + 1)
      return
    }
    if (!Number.isFinite(clientY) || !yCam.isPinned()) return
    if (!st.on && yCam.isExploring()) {
      st.on = true          // already exploring: no arm travel this drag
      st.last = st.y0
    }
    if (!st.on) {
      if (Math.abs(clientY - st.y0) < Y_EXPLORE_ARM_PX) return
      st.on = true
      st.last = clientY
      const L = visibleSortedMcapsRef.current
      yCam.enterExplore(Number.isFinite(L?.fitCenter) ? L.fitCenter : null)
      return
    }
    const d = clientY - st.last
    st.last = clientY
    if (!d) return
    const H = containerHeightRef.current || 0
    const h = yCam.peekHalf()
    if (!(H > 0) || !(h > 0)) return
    // Drag down = content down = higher prices into view = centre up.
    yCam.panBy(d * (2 * h) / H)
    st.moved = true
    // The pan only writes the camera; it must ask for its own frame. Rows
    // reused → paint in place. Rows changed → one React pass per frame.
    if (yCamInPlaceRef.current()) scheduleCanvasDraw()
    else if (!st.tickRaf) st.tickRaf = requestAnimationFrame(() => { st.tickRaf = 0; setYFitTick((t) => t + 1) })
  }

  // View/layout changes (zoom, pan, row-height, visible range, theme) need a
  // SYNCHRONOUS pre-paint redraw so the canvas is correct before the browser
  // paints — otherwise zoom/pan flickers. During interaction, coalesce via
  // rAF instead to cut double work with pan/wheel schedulers.
  useLayoutEffect(() => {
    if (chartInteractingRef.current) scheduleCanvasDraw()
    else drawCanvas()
  }, [viewWindowSize, viewEndIndex, visibleSortedMcaps, dynamicRowHeight, drawCanvas, theme])

  // Live data ticks (chartData identity changes on every trade/candle/liquidity
  // push from the parent) are HIGH frequency — on an active token this fired a
  // full synchronous redraw many times per frame. Route these through the RAF
  // scheduler so a burst of ticks coalesces into a single draw per frame
  // (60fps cap) instead of N draws per frame. This is the main canvas-lag fix.
  useEffect(() => { scheduleCanvasDraw() }, [chartData, scheduleCanvasDraw])

  // --- Y-axis click trading ---
  // LIMIT ORDERS ONLY, ladder semantics: left-click above current price =
  // sell-limit at that row, below = buy-limit at that row. The click IS the
  // price — placed directly, no modal (the order tag is the feedback, with
  // drag-to-reprice / click-to-edit / × already built). Market entry lives
  // on the WalletHoldings buttons (MKT mode), never on the axis: the old
  // above-price instant market-buy was a mis-click landmine and inverted
  // ladder convention.
  const handleRowClick = useStableCallback((mcap, e) => {
    e.preventDefault(); e.stopPropagation()
    if (snapBucket(mcap, mcapBucketSize) === snapBucket(chartData.highlightMcap, mcapBucketSize)) return
    if (dataSource === 'hyperliquid' && hlWallet.isLocked) { showToast('Connect first', 'sell'); return }
    if (dataSource === 'solana' && !publicKey) { showToast('Connect wallet first', 'sell'); return }

    const side = mcap > chartData.highlightMcap ? 'sell' : 'buy'
    trading.placeLimitDirect(side, mcap)
  })

  // --- Y-axis limit entry: long-press on TOUCH, instant on MOUSE ---
  // On the phone an instant tap placed a limit order — a mis-tap landmine
  // while scrolling the axis with a finger. Touch now REQUIRES a deliberate
  // long-press (hold still ~450ms) before the order is placed; a quick tap or
  // any finger movement cancels. Mouse is untouched (placed on pointerdown as
  // before). The overlay uses onPointerDown so pointerType is available — a
  // touch pointerdown would otherwise also synthesize a mouse event, double-
  // ─── Phone y-axis magnifier mode ───
  // Desktop drives the magnifier from mouse move over the y-axis (in
  // useChartInteraction, untouched). Phone had no path to it AND the y-axis
  // owns a vertical-drag-to-zoom gesture, so there was nowhere to read a price
  // off the axis.
  //
  // ONE MAGNIFIER SYSTEM. The phone no longer runs a parallel tooltip/band —
  // the long-press gesture DRIVES THE DESKTOP MAGNIFIER: each finger move is
  // clamped to the y-axis rect and fed into interaction.handleYAxisMouseMove,
  // the exact function the mouse calls. Tooltip = the desktop tooltip (the
  // CSS class is position:fixed and expects viewport clientY — which is what
  // we feed). Band = the desktop React band (hMcap via magnifier.visible) —
  // so it "tracks like desktop" by construction. Release places at the hook's
  // own precise mcap; exit calls handleYAxisMouseLeave, same as mouse-out.
  // 500, not 380: this hold is the ONLY thing standing between a finger
  // brushing the axis and trading.placeLimitDirect() sending a real order,
  // and it now also has to clear the double-tap window below (300ms) so a
  // reset gesture can never arm the magnifier on its way past.
  const YAXIS_MAG_LONGPRESS_MS = 500   // hold before magnifier engages
  const YAXIS_MAG_FINGER_OFFSET_PX = 50 // magnifier reads 30px ABOVE the fingertip —
                                        // the finger covers the exact row otherwise.
                                        // Applied in the feed, so tooltip, band, and
                                        // the placed price all agree: what you SEE
                                        // above your finger is what a release places.
  const YAXIS_MAG_ARM_TOL_PX   = 12    // pre-arm finger travel that cancels (a scroll, not a hold)
  const phoneMagActiveRef = useRef(false)   // gates the zoom-drag handler below
  const yAxisTouchRef = useRef(false)       // last y-axis pointerdown was touch (gates native contextmenu)
  const yAxisTouchTsRef = useRef(0)         // ts of last y-axis touch — suppresses raw synthetic-mouse events
  // Pending press (pre-arm) + live gesture bookkeeping.
  const magPressRef = useRef(null) // { timer, x, y, pointerId } before arm; { armed, pointerId } after

  // Synthesize the mouse event the desktop hook expects, with the finger Y
  // CLAMPED to the axis rect first — this is the "magnifier stays on the
  // chart" guarantee, enforced before the hook ever sees a coordinate.
  const feedDesktopMagnifier = useStableCallback((clientY) => {
    const yEl = yAxisRef.current
    if (!yEl || !interaction.handleYAxisMouseMove) return null
    const r = yEl.getBoundingClientRect()
    const yc = Math.min(Math.max(clientY - YAXIS_MAG_FINGER_OFFSET_PX, r.top), r.bottom)
    interaction.handleYAxisMouseMove({
      clientY: yc,
      clientX: r.left + r.width / 2,
      target: yEl,
      currentTarget: yEl,
      preventDefault() {},
      stopPropagation() {},
    })
    return yc
  })

  // Read the mcap under a client Y using the SAME conversion the alert/crosshair
  // paths use (canvas rect + visibleSortedMcaps + row height), clamped to the
  // visible range so the finger can't read a price off the ends.
  const magMcapFromClientY = useStableCallback((clientY) => {
    const canvas = canvasRef.current
    const mcaps = visibleSortedMcapsRef.current
    const RH = dynamicRowHeightRef.current
    if (!canvas || !(mcaps?.length >= 2) || !(RH > 0)) return null
    const rect = canvas.getBoundingClientRect()
    let m = yToMcapUtil(clientY - rect.top, mcaps, RH)
    if (m > mcaps[0]) m = mcaps[0]
    else if (m < mcaps[mcaps.length - 1]) m = mcaps[mcaps.length - 1]
    // Snap to the ACTUAL grid mcap of the row under the finger — not
    // Math.round(m). The red/green band's inRange test compares against exact
    // visibleSortedMcaps values (usually non-integer, e.g. 468.3/463.1), so a
    // rounded integer never matches a grid row and the band misses. Returning
    // the real grid value matches what the desktop magnifier feeds hMcap, so
    // the band lights the same way. (Placement via handleRowClick wants a row
    // price too, so snapping is correct there as well.)
    const idx = bucketRowIndex(mcaps, m)
    if (idx >= 0 && idx < mcaps.length) return mcaps[idx]
    return m
  })

  // Paint the red/green band IMPERATIVELY — the same technique the crosshair
  // uses (direct DOM writes, no React state). The band was computed inside the
  // y-axis rows' .map(), so it only repainted on re-render; on Android the
  // touch pointer stream outpaces React commits, so the band froze while the
  // magnifier (imperative) kept up. This walks the actual .y-axis-row elements
  // and toggles hover-buy/hover-sell on those between the current price and the
  // finger — instant, every move, no render dependency. Desktop still uses the
  // React-class path via hover events; this only runs during a touch drag.
  const exitPhoneMag = useStableCallback(() => {
    phoneMagActiveRef.current = false
    magPressRef.current = null
    // Same teardown a real mouse-out performs: hides the desktop tooltip and
    // clears the hover/band state through the hook's own path.
    try { interaction.handleYAxisMouseLeave?.() } catch {}
    // And the ROW-hover half of the state, which mouse-leave does not own:
    // hoverState.rows set by per-row enters would otherwise survive the
    // release and leave a frozen band segment on screen.
    try { interaction.handleRowLeave?.() } catch {}
  })

  const yAxisLastTapRef = useRef({ t: 0, y: 0 })
  // Timestamp of the last double-tap reset. The y-axis zoom-drag checks it and
  // declines the gesture, so the finger still resting on the axis after a
  // reset can't be read as the start of a zoom.
  const yAxisResetTapRef = useRef(0)
  const handleYAxisRowPointerDown = useStableCallback((mcap, e) => {

    const type = e.pointerType || 'mouse'
    // "Is this a phone" is decided by the HARDWARE, not by what the browser
    // labels the event. Samsung Internet (and Chromium desktop-site mode on
    // any Android) can deliver touch as pointerType 'mouse' — which sent every
    // finger down the mouse branch: tap placed instantly, the long-press never
    // armed, the touch stamp never set, so the desktop hover magnifier ran
    // wild on a finger. matchMedia('(pointer: coarse)') reflects the primary
    // input device and stays true in desktop-site mode; touchscreen laptops
    // (primary = mouse/trackpad) stay false, so desktop behavior is untouched.
    const isTouchLike = type === 'touch' || (COARSE_POINTER && type !== 'pen')
    yAxisTouchRef.current = isTouchLike
    if (isTouchLike) yAxisTouchTsRef.current = Date.now()
    // ── Double-click / double-tap → reset the price scale (ALL POINTERS) ──
    // Previously touch-only, because a mouse pointerdown called handleRowClick
    // synchronously and a double-click therefore sent TWO live limit orders.
    // Mouse now takes the same long-press path as touch (below), so a quick
    // click places nothing and the second click is free to mean reset.
    {
      const now = Date.now()
      const last = yAxisLastTapRef.current
      if (now - last.t < 300 && Math.abs(e.clientY - last.y) < 24) {
        yAxisLastTapRef.current = { t: 0, y: 0 }
        // Tap #1 armed a long-press that is still counting; it must die here
        // or the magnifier engages ~200ms after the reset lands.
        if (magPressRef.current?.timer) clearTimeout(magPressRef.current.timer)
        magPressRef.current = null
        phoneMagActiveRef.current = false
        // Kill any in-flight Y-scale glide FIRST. glideYZoom is a rAF walk
        // toward yHalfTargetRef; resetting without clearing the target means
        // the very next tick walks the span right back — the reported
        // "flashes and returns".
        yHalfTargetRef.current = null
        if (yGlideRafRef.current) { cancelFrame(yGlideRafRef.current); yGlideRafRef.current = 0 }
        // Same hazard, same fix, for the X zoom glide: zoomTargetWinRef is the
        // truth ITS walker obeys, so clearing state without clearing the target
        // walks the window straight back on the next tick. glideHalfRef too —
        // it overrides the memo's half-range, so a reset that leaves it set is
        // reset in x and stale in y.
        zoomTargetWinRef.current = null
        zoomGlideRef.current = null
        glideHalfRef.current = null
        glideCenterRef.current = null
        yDragFitDestRef.current = null
        yCam.reset()
        if (zoomGlideRafRef.current) { cancelFrame(zoomGlideRafRef.current); zoomGlideRafRef.current = 0 }
        flushGlideWin()   // (xref) X glide cut here: commit the shown window
        // The FOLLOW DAMPER went down with yCam.reset() above: its trail glides
        // the scale over several frames, so leaving it set means the reset lands
        // and is then smoothly undone — the "flashes and returns".
        // Stand the y-axis ZOOM-DRAG down for this gesture. It is a separate
        // touch-only listener on the same element, so it receives this very
        // same pointerdown — and it yields only on `magPressRef ||
        // phoneMagActiveRef`, both of which were just cleared two lines up.
        // It therefore captures the pointer and snapshots the PRE-RESET span.
        // The first pixel of finger movement then writes that span * factor
        // and the old scale is back. That is the phone-only flash: desktop never
        // saw it because this listener ignores mouse.
        yAxisResetTapRef.current = Date.now()
        e.preventDefault(); e.stopPropagation()
        // yCam.reset() above cleared the user span + explore: back to the
        // default fit on this pass.
        setYFitTick((t) => t + 1)
        setForceBucketRecompute(c => c + 1)
        return
      }
      yAxisLastTapRef.current = { t: now, y: e.clientY }
    }
    // Mouse/pen: only left button participates; anything else (right-click for
    // the context menu, middle) falls through untouched.
    if (!isTouchLike && e.button !== 0) return
    // NOTE: mouse used to place the order instantly on pointerdown. It now
    // arms the same long-press as touch. A y-axis row click calls
    // trading.placeLimitDirect — a REAL order — so an instant mousedown made
    // every stray click on the axis a live trade, and double-click sent two.
    // One gesture, both platforms: hold to place, quick click does nothing,
    // double-click resets the scale.
    // Touch: arm a long-press. Don't preventDefault yet — a short tap should
    // still fall through to normal axis behavior; magnifier only engages on the
    // timer. Capture the pointer to the y-axis so the browser doesn't hand the
    // slide to native scrolling; the actual move/up handling lives on WINDOW
    // (see the effect below) because capture retargets events to the axis
    // element and they no longer reach this per-row overlay's React handlers.
    if (magPressRef.current?.timer) clearTimeout(magPressRef.current.timer)
    const startY = e.clientY, startX = e.clientX, pid = e.pointerId
    const yEl = yAxisRef.current
    try { yEl?.setPointerCapture?.(pid) } catch {}
    const timer = setTimeout(() => {
      const p = magPressRef.current
      if (!p || p.armed) return
      // Engage magnifier mode at the finger's CURRENT position (lastY, kept
      // current by the window move handler) — not the touch-down Y, which may
      // have drifted within tolerance during the hold.
      p.armed = true
      phoneMagActiveRef.current = true
      feedDesktopMagnifier(p.lastY)                       // desktop tooltip + band, clamped
      p.mcap = interaction.magnifierMcapRef.current ?? magMcapFromClientY(p.lastY)
      if (navigator.vibrate) { try { navigator.vibrate(12) } catch {} }  // "grabbed"
    }, YAXIS_MAG_LONGPRESS_MS)
    // `touch` is stamped from isTouchLike at press time, not re-derived in the
    // move handler: the move events during a press can arrive with a different
    // pointerType than the one that started it (synthetic mouse from touch),
    // and the cancel rule must follow the GESTURE, not the individual event.
    magPressRef.current = { timer, x: startX, y: startY, lastY: startY, mcap: null, pointerId: pid, armed: false, touch: isTouchLike }
  })

  // Window-level driver for the phone magnifier. Registered once; every handler
  // early-returns unless a magnifier press is pending, so it's inert otherwise.
  // Lives on WINDOW (not the overlay) so it keeps receiving the pointer stream
  // after setPointerCapture retargets events to the y-axis element — the exact
  // reason the magnifier used to freeze the instant the finger left its starting
  // row instead of following it like the desktop mouse magnifier does.
  useEffect(() => {
    const onMove = (e) => {
      const p = magPressRef.current
      if (!p || e.pointerId !== p.pointerId) return
      p.lastY = e.clientY
      if (!p.armed) {
        // Pre-arm cancel is a TOUCH rule, and only touch. On a finger, travel
        // means the user is scrolling or zoom-dragging the axis (that gesture
        // is registered touch-only — see the y-zoom drag effect's pointerType
        // guard), so a moving press is not a hold and must not arm.
        //
        // A mouse has no competing drag on this axis: desktop scales with the
        // wheel. So the 12px leash only made the user hold unnaturally still,
        // and small hand tremor silently cancelled the press. Mouse instead
        // has to stay ON THE AXIS — leaving it is the real "changed my mind"
        // signal, and it also keeps the armed magnifier's guarantee that it
        // can never be summoned from off-axis.
        if (p.touch) {
          if ((e.clientX - p.x) ** 2 + (e.clientY - p.y) ** 2 > YAXIS_MAG_ARM_TOL_PX * YAXIS_MAG_ARM_TOL_PX) {
            clearTimeout(p.timer)
            magPressRef.current = null
          }
        } else {
          const yEl = yAxisRef.current
          const r = yEl?.getBoundingClientRect()
          const off = !r || e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom
          if (off) {
            clearTimeout(p.timer)
            magPressRef.current = null
          }
        }
        return
      }
      // Armed: follow the finger THROUGH the desktop hook — clamped first, so
      // the magnifier physically cannot leave the axis. Chart does not scale
      // (zoom drag gated off while phoneMagActiveRef is set).
      feedDesktopMagnifier(e.clientY)
      p.mcap = interaction.magnifierMcapRef.current ?? magMcapFromClientY(e.clientY)
      if (e.cancelable) e.preventDefault()
    }
    const onUp = (e) => {
      const p = magPressRef.current
      if (!p || e.pointerId !== p.pointerId) return
      const yEl = yAxisRef.current
      if (p.pointerId != null) { try { yEl?.releasePointerCapture(p.pointerId) } catch {} }
      if (p.timer) clearTimeout(p.timer)
      if (p.armed) {
        // Release inside magnifier mode → place the limit at the SHOWN price:
        // the desktop hook's own precise mcap (what the tooltip displays),
        // exactly like a desktop click. Fallbacks only if the hook has none.
        const m = interaction.magnifierMcapRef.current ?? p.mcap ?? magMcapFromClientY(e.clientY - YAXIS_MAG_FINGER_OFFSET_PX)
        if (Number.isFinite(m)) {
          handleRowClick(m, { preventDefault() {}, stopPropagation() {} })
          if (navigator.vibrate) { try { navigator.vibrate(18) } catch {} }  // "placed"
        }
      }
      exitPhoneMag()
    }
    window.addEventListener('pointermove', onMove, { passive: false })
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Volume bar under (clientX, clientY)? Opens the Volume menu prefilled
  // for that bar and returns true. Shared by right-click / long-press on the
  // chart body and (volphone-20261028) long-press on the footer strip, which
  // sits over the bottom of the lane and catches most phone presses there.
  const openVolMenuAt = (clientX, clientY) => {
    const vc = volLaneRef.current, g = volLaneGeomRef.current
    if (!vc || !g || !g.cols || !g.cols.length || !(g.maxVol > 0)) return false
    const r = vc.getBoundingClientRect()
    const lx = clientX - r.left, ly = clientY - r.top
    if (!(lx >= 0 && ly >= 0 && lx < Math.min(r.width, g.lineEnd) && ly <= r.height)) return false
    const c = g.cols.find(c => lx >= c.x0 && lx < c.x1 + 1)
    if (!c || !(c.v > 0) || !Number.isFinite(c.t)) return false
    const barTop = g.LANE_H - Math.min(g.LANE_H, (c.v / g.maxVol) * g.LANE_H)
    if (ly < barTop - 60) return false
    setVolSettings({ show: true, x: clientX + 4, y: Math.max(6, Math.min(clientY, window.innerHeight - 240)), openLeft: clientX > window.innerWidth - 420, alert: { mode: 'new', t: c.t, level: roundVol(c.v) } })
    return true
  }

  // --- Chart context menu ---
  const handleChartContextMenu = useStableCallback((e) => {
    // Crosshair mode / armed long-press: contextmenu events here are the
    // browser reacting to the scrubbing finger, never an intent to open
    // the menu. Only the double-tap synthetic call (allow flag) passes.
    // allowTouchMenuRef is the double-tap's explicit "this one is intentional"
    // flag; it is the only way a touch contextmenu gets through, and it now
    // has to win in crosshair mode as well.
    if ((crosshairModeRef.current || crosshairArmedRef.current) && !allowTouchMenuRef.current) {
      e.preventDefault?.()
      return
    }
    // Phone: the browser fires contextmenu on long-press. Long-press is the
    // CROSSHAIR gesture now — only the double-tap path (which sets the allow
    // flag) may open the menu from touch.
    if (lastPointerTypeRef.current === 'touch' && !allowTouchMenuRef.current) {
      e.preventDefault?.()
      return
    }
    allowTouchMenuRef.current = false
    // Volume profile BARS: right-click on a painted bar opens the VP menu at
    // the cursor. The hit test is the lane's own pixels (alpha under the
    // cursor), so it follows both modes, the side, and the Y camera — and
    // empty lane space and the rest of the chart fall through to the normal
    // menus. The lane canvas is pointer-events:none, so the event lands here.
    {
      const vpc = volumeProfileLayerRef.current
      if (vpc && vpc.width > 0 && vpc.height > 0) {
        const r = vpc.getBoundingClientRect()
        const lx = e.clientX - r.left, ly = e.clientY - r.top
        if (lx >= 0 && ly >= 0 && lx < r.width && ly < r.height) {
          try {
            const sx = Math.floor(lx * vpc.width / r.width)
            const sy = Math.floor(ly * vpc.height / r.height)
            if (vpc.getContext('2d').getImageData(sx, sy, 1, 1).data[3] > 20) {
              e.preventDefault()
              setVpSettings({ show: true, x: e.clientX + 4, y: Math.max(6, Math.min(e.clientY, window.innerHeight - 200)), openLeft: e.clientX > window.innerWidth - 420 })
              return
            }
          } catch {}
        }
      }
    }
    // Volume BARS (volalert-20261028): right-click / phone long-press on a
    // bar (or up to 60px above its top) opens the Volume menu with a
    // "Set alert" row prefilled with that bar's volume.
    if (openVolMenuAt(e.clientX, e.clientY)) { e.preventDefault?.(); return }
    // Moving average line: right-click (or phone double-tap) ON the line
    // opens its settings, like AVWAP / ATR.
    {
      const maId = maHitAt(e.clientX, e.clientY)
      if (maId) {
        e.preventDefault?.()
        setMaSettings({ show: true, id: maId, x: e.clientX, y: Math.max(6, Math.min(e.clientY, window.innerHeight - 160)), openLeft: e.clientX > window.innerWidth - 300 })
        return
      }
    }
    // ATR trail line proximity: right-click ON the line opens its settings
    // directly (the AVWAP/S-R pattern), before any other menu resolves.
    if (showATRStopRef.current) {
      const canvas = canvasRef.current
      const stops = atrStopRef.current
      if (canvas && stops && stops.length) {
        const rect = canvas.getBoundingClientRect()
        const mxA = e.clientX - rect.left, myA = e.clientY - rect.top
        const plotW = Math.max(1, containerWidthRef.current - RIGHT_PADDING - FIXED_OVERLAY_WIDTH)
        const cwA = plotW / Math.max(1, viewWindowSizeRef.current)
        const totalA = stops.length
        const endA = resolveEndIdx(viewEndIndexRef.current, totalA)
        const idxA = Math.max(0, Math.min(totalA - 1, endA - Math.round((plotW + fractionalOffsetPxRef.current - mxA) / cwA) + 1))
        const sv = stops[idxA] ?? stops[Math.max(0, idxA - 1)]
        const svVal = sv != null && typeof sv === 'object' ? (sv.ts ?? sv.stop ?? sv.value ?? null) : sv   // entries are {ts, bull}: ts IS the trail value
        if (svVal != null) {
          const yA = priceToY(svVal)
          if (Number.isFinite(yA) && Math.abs(yA - myA) < 12) {
            e.preventDefault()
            setAtrSettings({ show: true, x: e.clientX, y: Math.max(6, Math.min(e.clientY, window.innerHeight - 280)), openLeft: e.clientX > window.innerWidth - 240 })
            return
          }
        }
      }
    }
    // If the cursor is over the smoketrail's live-edge handle, open the little
    // flow-direction menu (AVWAP-line pattern) instead of the chart menu.
    if (showOrderflowTrail && trailHoveredRef.current) {
      e.preventDefault()
      setTrailFlowMenu({ x: e.clientX, y: e.clientY })
      return
    }
    // If a Value Area line is under the cursor, right-click opens ITS settings
    // to the LEFT of the click — same paradigm as right-clicking an AVWAP /
    // S-R line. hoveredVaLineRef is maintained by the desktop hover path.
    if (hoveredVaLineRef.current) {
      e.preventDefault()
      const openLeft = e.clientX > 340
      setVaSettings({
        show: true,
        x: openLeft ? e.clientX - 8 : e.clientX + 8,
        y: Math.max(6, Math.min(e.clientY - 12, window.innerHeight - 180)),
        openLeft,
      })
      return
    }
    // A hovered trendline right-clicks into ITS settings, same paradigm as
    // AVWAP / S-R / Value-Area lines above.
    if (trend.handleContextMenu(e)) return
    interaction.handleChartContextMenu(e)
  })

  // ─── Phone LONG-PRESS opens every menu ───
  // Hold ~450ms (the app's long-press threshold, same as the alert hook and
  // the S/R / AVWAP / trendline holds) without moving >12px:
  //   - S/R, AVWAP, trendline: their own grab code already opens their menu
  //     on a still hold (lpClaimRef marks those fingers so this skips them);
  //   - DVP pane -> DVP settings;
  //   - smoketrail handle -> its menu (hit-tested here: touch has no hover);
  //   - anything else (MA, ATR, Value Area, VP bars, empty chart, CVD pane)
  //     goes through handleChartContextMenu, which hit-tests each item and
  //     falls back to the main context menu.
  // Replaces the old double-tap opener. The press point is also the phone
  // trendline's point 1 and the '🔔 Set Alert Here' price.
  const PHONE_LONG_PRESS_MS = 450
  const lpRef = useRef(null)             // { timer, x, y, kind, target }
  const lpClaimRef = useRef(false)       // finger already owned by a line grab / placement
  const pressAlertMcapRef = useRef(0)    // price for the menu's Set Alert item
  const lpLiftGuardRef = useRef({ armed: false, until: 0 })
  const cancelLongPress = () => {
    const lp = lpRef.current
    if (lp?.timer) clearTimeout(lp.timer)
    lpRef.current = null
  }
  const mcapAtClientY = (clientY) => {
    const canvas = canvasRef.current
    const mcaps = visibleSortedMcapsRef.current
    const RH = dynamicRowHeightRef.current
    if (!canvas || !mcaps || mcaps.length < 2 || !(RH > 0)) return 0
    const r = canvas.getBoundingClientRect()
    let m = yToMcapUtil(clientY - r.top, mcaps, RH)
    const top = yToMcapUtil(0, mcaps, RH)
    if (m > top) m = top
    else if (m < mcaps[mcaps.length - 1]) m = mcaps[mcaps.length - 1]
    return Math.round(m)
  }
  const fireLongPress = useStableCallback(() => {
    const lp = lpRef.current
    lpRef.current = null
    if (!lp || lpClaimRef.current) return
    if (interaction.draggingItemRef.current || trend.modeRef.current === 'phonePlacing') return
    // Footer strip (volphone-20261028): only a volume bar under the press opens.
    if (lp.kind === 'footer') {
      if (!openVolMenuAt(lp.x, lp.y)) return
      if (navigator.vibrate) { try { navigator.vibrate(12) } catch {} }
      lpLiftGuardRef.current = { armed: true, until: 0 }
      return
    }
    if (navigator.vibrate) { try { navigator.vibrate(12) } catch {} }
    // The finger is still down on the menu that is about to open; swallow the
    // click its lift produces (see the lift-guard effect).
    lpLiftGuardRef.current = { armed: true, until: 0 }
    if (lp.kind === 'dvp') {
      setDvpSettings({ show: true, x: lp.x + 4, y: Math.max(6, Math.min(lp.y, window.innerHeight - 380)), openLeft: lp.x > window.innerWidth - 250 })
      return
    }
    if (lp.kind === 'body') {
      const canvas = canvasRef.current
      // Smoketrail live-edge handle (desktop opens it from hover state).
      if (showOrderflowTrail && canvas) {
        const handle = getTrailHandle()
        if (handle) {
          const r = canvas.getBoundingClientRect()
          const RHh = dynamicRowHeightRef.current
          if (Math.abs(lp.x - r.left - handle.x) <= 28 && Math.abs(lp.y - r.top - handle.y) <= Math.max(10, RHh * 0.8)) {
            setTrailFlowMenu({ x: lp.x, y: lp.y })
            return
          }
        }
      }
      // Run the hover hit-tests at the press point (Value Area lines etc. are
      // resolved from hover refs), then put the crosshair back the way it was.
      applyBodyHover({ clientX: lp.x, clientY: lp.y, target: lp.target, _touchScrub: true })
      if (!crosshairModeRef.current) {
        updateCrosshair(-1)
        if (cursorLabelRef.current) cursorLabelRef.current.style.display = 'none'
        placeXCrossLabels(-1)
        if (hHighlightRef.current) hHighlightRef.current.style.display = 'none'
      }
    }
    pressAlertMcapRef.current = crosshairModeRef.current && crosshairMcapRef.current > 0
      ? Math.round(crosshairMcapRef.current)
      : mcapAtClientY(lp.y)
    // Phone trendline: this press point becomes point 1 if 'Trendline' is picked.
    trend.notePhoneMenuPoint(lp.x, lp.y)
    allowTouchMenuRef.current = true
    handleChartContextMenu({
      preventDefault: () => {},
      clientX: lp.x,
      clientY: lp.y,
      target: lp.target,
    })
    allowTouchMenuRef.current = false
  })
  // Lift guard: the click synthesized by lifting the finger that long-pressed
  // must not activate whatever menu item opened under it. Document capture,
  // one lift only, 600ms window.
  useEffect(() => {
    const onEnd = () => {
      const g = lpLiftGuardRef.current
      if (g.armed) { g.armed = false; g.until = Date.now() + 600 }
    }
    const onClick = (ev) => {
      if (Date.now() < lpLiftGuardRef.current.until) {
        lpLiftGuardRef.current.until = 0
        ev.preventDefault(); ev.stopPropagation()
      }
    }
    document.addEventListener('touchend', onEnd, true)
    document.addEventListener('touchcancel', onEnd, true)
    document.addEventListener('click', onClick, true)
    return () => {
      document.removeEventListener('touchend', onEnd, true)
      document.removeEventListener('touchcancel', onEnd, true)
      document.removeEventListener('click', onClick, true)
    }
  }, [])
  useEffect(() => () => cancelLongPress(), [])   // eslint-disable-line react-hooks/exhaustive-deps

  // Header crosshair button (phone). On: one-finger drag scrubs the
  // crosshair (relative, see handleChartTouchMove) and it stays after lift.
  // Off: normal pan / zoom, crosshair hidden.
  const togglePhoneCrosshair = useStableCallback(() => {
    const next = !crosshairModeRef.current
    try { localStorage.setItem(PHONE_XHAIR_KEY, next ? '1' : '0') } catch {}
    setXhairOn(next)
    scrubRef.current = null
    if (!next) { exitCrosshairMode(); return }
    crosshairModeRef.current = true
    placePhoneCrosshair()
  })
  // Show it straight away, mid-plot (or where it last was).
  const placePhoneCrosshair = useStableCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const plotEnd = containerWidthRef.current - RIGHT_PADDING - FIXED_OVERLAY_WIDTH
    const bodyR = bodyScrollRef.current?.getBoundingClientRect()
    const x = crosshairXRef.current >= 0 ? crosshairXRef.current : Math.max(0, plotEnd / 2)
    const y = crosshairYRef.current >= 0
      ? crosshairYRef.current
      : Math.max(0, (bodyR ? bodyR.top + bodyR.height / 2 : rect.top + rect.height / 2) - rect.top)
    crosshairYRef.current = y
    lastTouchTsRef.current = Date.now()
    handleBodyMouseMove({ clientX: rect.left + x, clientY: rect.top + y, target: canvas, _touchScrub: true })
  })
  // Restored ON from storage: place it once the chart has rows to map.
  useEffect(() => {
    if (!xhairOn || !hasAxisValues || crosshairXRef.current >= 0) return
    const id = requestAnimationFrame(() => { if (crosshairModeRef.current) placePhoneCrosshair() })
    return () => cancelAnimationFrame(id)
  }, [xhairOn, hasAxisValues])   // eslint-disable-line react-hooks/exhaustive-deps
  // Set when a touch is consumed by phone trendline placement, so the same
  // finger's touchstart/move/end don't scrub, pan, or count toward a
  // double-tap. Cleared on that finger's touchend.
  const trendPlaceTouchRef = useRef(false)
  const handleChartTouchStart = useStableCallback((e) => {
    lastTouchTsRef.current = Date.now()
    if (trendPlaceTouchRef.current || trend.modeRef.current === 'phonePlacing') return
    // An AVWAP anchor grab already claimed this finger. pointerdown fires
    // BEFORE touchstart, so handleOverlayDragStart has run and armed the
    // drag by now — without this bail the same finger would also seed a pan
    // anchor, and dragging an anchor would scroll the chart underneath it.
    if (interaction.draggingItemRef.current) return
    const t = e.touches && e.touches[0]
    if (!t) return
    touchStartRef.current = { t: Date.now(), x: t.clientX, y: t.clientY, moved: false }
    // Long-press: one finger only, not on the y-axis (it owns its own hold —
    // the magnifier), not a finger a line grab already claimed.
    cancelLongPress()
    if (e.touches.length !== 1 || lpClaimRef.current) return
    if (e.target.closest && (e.target.closest('.y-axis-body') || e.target.closest('.y-axis-region'))) return
    const kind = e.currentTarget === dvpPaneEl ? 'dvp' : (e.currentTarget === cvdPaneEl ? 'cvd' : (e.currentTarget === footerVpRef.current ? 'footer' : 'body'))
    lpRef.current = {
      x: t.clientX, y: t.clientY, kind, target: e.target,
      timer: setTimeout(fireLongPress, PHONE_LONG_PRESS_MS),
    }
  })

  const handleChartTouchEndForDblTap = useStableCallback((e) => {
    lastTouchTsRef.current = Date.now()
    cancelLongPress()
    // Read before it's cleared: a finger a line/AVWAP grab owned never
    // counts toward the crosshair double-tap.
    const fingerWasClaimed = lpClaimRef.current
    if (!e.touches || e.touches.length === 0) lpClaimRef.current = false
    if (trendPlaceTouchRef.current) {
      trendPlaceTouchRef.current = false
      scrubRef.current = null
      return
    }
    // A lift ends the current scrub. Clearing the anchor means the NEXT
    // finger-down re-anchors from wherever the crosshair now sits — so putting
    // your finger down somewhere else moves it RELATIVELY from its held
    // position, it never jumps to the new touch. This is the crux of the
    // TradingView feel and it belongs before any early return below.
    scrubRef.current = null

    const touch = e.changedTouches && e.changedTouches[0]
    if (!touch) return
    // ─── Crosshair-as-cursor ───
    // A still tap in crosshair mode forwards a click to whatever sits under
    // the CROSSHAIR, not under the finger. On a phone the two are different
    // points — the scrub is relative, so the line is wherever you drove it —
    // and that is exactly what makes this worth having: the finger never
    // occludes the target, so a 14px alert × becomes hittable.
    //
    // WHITELISTED. A blanket redirect would happily click anything the line
    // happens to cross, which is how you get "why did my chart do that".
    // Only overlay controls that are already pointerEvents:auto and already
    // have real handlers are eligible.
    //
    // Position is read from the rendered elements rather than tracked in a
    // ref: the crosshair line owns its own x and the highlight strip owns the
    // y, so their rects ARE the cursor position by definition and cannot
    // drift out of sync with what is painted.
    if (crosshairModeRef.current) {
      const st0 = touchStartRef.current
      const still = st0 && !st0.moved && (Date.now() - st0.t) < 280
      if (still) {
        const chEl = crosshairRef.current
        const hlEl = hHighlightRef.current
        if (chEl && hlEl && chEl.style.display !== 'none' && hlEl.style.display !== 'none') {
          const cr = chEl.getBoundingClientRect()
          const hr = hlEl.getBoundingClientRect()
          const px = cr.left + cr.width / 2
          const py = hr.top + hr.height / 2
          // The crosshair layers sit above the targets, so hide them for the
          // hit-test or elementFromPoint just returns the crosshair itself.
          const chVis = chEl.style.visibility, hlVis = hlEl.style.visibility
          chEl.style.visibility = 'hidden'
          hlEl.style.visibility = 'hidden'
          const hit = document.elementFromPoint(px, py)
          chEl.style.visibility = chVis
          hlEl.style.visibility = hlVis
          const target = hit?.closest?.(CROSSHAIR_CLICK_TARGETS)
          if (target) {
            // Dispatch on the FOUND element, not a wrapper — a React handler
            // bound to the × never sees a click aimed at its parent. Real
            // MouseEvent with coordinates so handlers reading clientX/Y work.
            target.dispatchEvent(new MouseEvent('click', {
              bubbles: true, cancelable: true, clientX: px, clientY: py,
            }))
            if (navigator.vibrate) { try { navigator.vibrate(8) } catch {} }
            return
          }
        }
      }
    }

    // Menus on touch open from LONG-PRESS (fireLongPress). Double-tap on the
    // chart BODY toggles phone crosshair mode (replaces the header button).
    // Two quick still taps (<280ms each, <300ms apart, within 30px), one
    // finger, body pane only (not DVP/CVD), not the y-axis (it owns its own
    // double-tap reset), not a finger a line/AVWAP grab claimed, not while an
    // item drag or phone trendline placement is live, not on a button/input.
    const st = touchStartRef.current
    const now = Date.now()
    const isStillTap = st && !st.moved && (now - st.t) < 280
    const tgt = e.target
    const onControl = !!(tgt && tgt.closest && tgt.closest('button, input, select, textarea, a, .y-axis-body, .y-axis-region'))
    const isBody = e.currentTarget !== dvpPaneEl && e.currentTarget !== cvdPaneEl
    const eligible = isPhone && isStillTap && isBody && !onControl && !fingerWasClaimed
      && (!e.touches || e.touches.length === 0)
      && !interaction.draggingItemRef.current
      && trend.modeRef.current !== 'phonePlacing'
    if (!eligible) { phoneLastTapRef.current = { t: 0, x: 0, y: 0 }; return }
    const last = phoneLastTapRef.current
    if (last.t && now - last.t < 300
        && Math.abs(touch.clientX - last.x) < 30 && Math.abs(touch.clientY - last.y) < 30) {
      phoneLastTapRef.current = { t: 0, x: 0, y: 0 }
      togglePhoneCrosshair()
      if (navigator.vibrate) { try { navigator.vibrate(8) } catch {} }
      return
    }
    phoneLastTapRef.current = { t: now, x: touch.clientX, y: touch.clientY }
  })
  // Previous still tap on the chart body (crosshair double-tap detection).
  const phoneLastTapRef = useRef({ t: 0, x: 0, y: 0 })

  // While a touch long-press is armed, the finger scrubs the crosshair. This
  // runs on touchmove but only acts once armed (the hook sets crosshairArmedRef
  // via the arm callback), so ordinary scroll/pan before arming is untouched.
  // (crosshairModeRef / crosshairArmedRef are declared above useChartScroll
  //  so the scroll hook can gate its pointer pan on them.)
  const touchStartRef = useRef({ t: 0, x: 0, y: 0, moved: false })
  // Mobile browsers fire a NATIVE contextmenu on long-press, which raced our
  // long-press-crosshair flow and opened the menu anyway. Track the pointer
  // type so touch-originated contextmenu can be suppressed; the double-tap
  // path sets the allow flag to pass its synthetic call through the gate.
  const lastPointerTypeRef = useRef('mouse')
  const allowTouchMenuRef = useRef(false)
  const handleChartTouchMove = useStableCallback((e) => {
    if (trendPlaceTouchRef.current) return
    if (e.touches && e.touches[0]) {
      const t0 = touchStartRef.current
      const t = e.touches[0]
      if ((t.clientX - t0.x) ** 2 + (t.clientY - t0.y) ** 2 > 12 * 12) { t0.moved = true; cancelLongPress() }
      if (e.touches.length > 1) cancelLongPress()
    }
    if (!crosshairModeRef.current && !crosshairArmedRef.current) return
    const t = e.touches && e.touches[0]
    if (!t) return
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const mainPlotEnd = containerWidthRef.current - RIGHT_PADDING - FIXED_OVERLAY_WIDTH

    // RELATIVE SCRUB. Absolute tracking (crosshair = finger x) buried the exact
    // point you're reading under your thumb. Instead: anchor on the first move,
    // then move the crosshair by the finger's DELTA from that anchor. It keeps
    // its place and follows your motion — TradingView's phone feel.
    let sc = scrubRef.current
    if (!sc) {
      // First move of this scrub — anchor BOTH axes to where the crosshair
      // already is. X: the arm placed it at the press point. Y: seeded from the
      // last synthesized y if we have one (a prior scrub this session), else
      // the finger's current y — at that instant the thumb IS at the press
      // point, so it reads as "placed at press" either way.
      const baseX = crosshairXRef.current >= 0
        ? crosshairXRef.current
        : Math.max(0, Math.min(t.clientX - rect.left, mainPlotEnd - 1))
      const baseY = crosshairYRef.current >= 0
        ? crosshairYRef.current
        : Math.max(0, Math.min(t.clientY - rect.top, rect.height - 1))
      sc = scrubRef.current = { fingerX0: t.clientX, crossX0: baseX, fingerY0: t.clientY, crossY0: baseY }
    }
    const rawX = sc.crossX0 + (t.clientX - sc.fingerX0)
    const rawY = sc.crossY0 + (t.clientY - sc.fingerY0)
    // Clamp both axes to the plot so the crosshair pins at the edges instead of
    // walking onto the y-axis / fixed overlay / off the body. (TradingView
    // clamps too.)
    const clampedX = Math.max(0, Math.min(rawX, mainPlotEnd - 1))
    const clampedY = Math.max(0, Math.min(rawY, rect.height - 1))
    crosshairYRef.current = clampedY   // remembered → next scrub session resumes from here
    // Drive the FULL desktop cursor path (vertical line + horizontal highlight +
    // price label + sibling sync) with a synthetic event that is RELATIVE on
    // BOTH axes now. Y was left absolute at first on the theory that vertical
    // isn't occluded — in practice the price row you're reading sits exactly
    // under the thumb, same disease as X.
    lastTouchTsRef.current = Date.now()
    handleBodyMouseMove({
      clientX: rect.left + clampedX,
      clientY: rect.top + clampedY,
      target: canvas,
      _touchScrub: true,   // our own synthetic event — exempt from the ghost-mouse guard
    })
    // edgescroll-20261102 (phone only): crosshair held near a side edge
    // slowly scrolls the chart; deeper into the edge zone = a bit faster.
    if (isPhone) {
      const EDGE = 32
      const plotEnd = mainPlotEnd - 1
      let dir = 0, depth = 0
      if (clampedX <= EDGE) { dir = 1; depth = (EDGE - clampedX) / EDGE }
      else if (clampedX >= plotEnd - EDGE) { dir = -1; depth = (clampedX - (plotEnd - EDGE)) / EDGE }
      const es = edgeScrollRef.current
      es.x = clampedX; es.y = clampedY
      es.dir = dir; es.depth = Math.max(0, Math.min(1, depth))
      if (dir && !es.raf) es.raf = requestAnimationFrame(edgeScrollTick)
      else if (!dir) stopEdgeScroll()
    }
    if (e.cancelable) e.preventDefault()
  })
  const edgeScrollRef = useRef({ raf: 0, dir: 0, depth: 0, x: 0, y: 0 })
  const stopEdgeScroll = () => {
    const es = edgeScrollRef.current
    if (es.raf) cancelAnimationFrame(es.raf)
    es.raf = 0; es.dir = 0
  }
  const edgeScrollTick = useStableCallback(() => {
    const es = edgeScrollRef.current
    es.raf = 0
    if (!es.dir || !(crosshairModeRef.current || crosshairArmedRef.current)) { es.dir = 0; return }
    const pan = scrollRef.current?.panByPx
    if (typeof pan !== 'function') { es.dir = 0; return }
    pan(es.dir * (1.5 + 4.5 * es.depth))   // px per frame
    const canvas = canvasRef.current
    if (canvas) {
      const rect = canvas.getBoundingClientRect()
      handleBodyMouseMove({ clientX: rect.left + es.x, clientY: rect.top + es.y, target: canvas, _touchScrub: true })
    }
    es.raf = requestAnimationFrame(edgeScrollTick)
  })
  useEffect(() => {
    const end = () => stopEdgeScroll()
    document.addEventListener('touchend', end, { capture: true })
    document.addEventListener('touchcancel', end, { capture: true })
    return () => {
      document.removeEventListener('touchend', end, { capture: true })
      document.removeEventListener('touchcancel', end, { capture: true })
      stopEdgeScroll()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // React attaches touchmove PASSIVELY, so preventDefault in the React
  // handler is ignored — the chart's body-scroll container kept native-
  // scrolling under the finger. First fix bound a non-passive listener to
  // bodyScrollRef, but that element mounts behind the reveal gate, so a
  // once-on-mount effect could attach to null and silently never block.
  // DOCUMENT-level capture has no element-timing dependency: while the
  // crosshair mode (or a long-press arm) is active, every touchmove is
  // cancelled before any scroll container sees it. Mode off = zero effect.
  useEffect(() => {
    const block = (ev) => {
      // Crosshair scrub OR an armed overlay drag (S/R / AVWAP) — both need
      // native body pan cancelled. React's touchmove is passive; this capture
      // listener is the one that actually sticks.
      const overlayDrag = !!interaction.draggingItemRef.current || trend.modeRef.current === 'phonePlacing'
      if ((crosshairModeRef.current || crosshairArmedRef.current || overlayDrag) && ev.cancelable) {
        ev.preventDefault()
      }
    }
    // Same document-capture reasoning for the NATIVE long-press contextmenu:
    // it can land on overlay children (bot button, tags, trail) that never
    // route through handleChartContextMenu's gate. While crosshair mode is
    // active, kill every real contextmenu at the door — the double-tap path
    // calls the handler directly with a synthetic object, so it's unaffected.
    const blockCtx = (ev) => {
      if (crosshairModeRef.current || crosshairArmedRef.current || trend.modeRef.current === 'phonePlacing') {
        ev.preventDefault()
        ev.stopPropagation()
      }
    }
    document.addEventListener('touchmove', block, { passive: false, capture: true })
    document.addEventListener('contextmenu', blockCtx, { capture: true })
    return () => {
      document.removeEventListener('touchmove', block, { capture: true })
      document.removeEventListener('contextmenu', blockCtx, { capture: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // --- Wheel x-zoom: column width only ---
  // ONE VALUE. A wheel notch resizes columns and commits. There is no
  // current/target pair, so there is no interpolator, no tau, no settle
  // threshold, no Animator lifecycle, and no "is an ease running" flag for
  // twenty-one other gates to branch on. Every bug in this path came from
  // holding two values for one window and letting them disagree.
  //
  // Smoothness comes from the wheel's own event rate, not from an ease: the
  // burst coalescer sums a frame's notches and applies them compounded, so a
  // fast spin travels the same distance as a slow one. If a discrete mouse
  // wheel reads as jumpy, lower X_STEP_FRAC (a per-notch ratio: 0.75 means
  // x1.75 out, /1.75 in) — a constant, not machinery.
  const setXWindowRef = useRef((_w) => {})
  // --- Y-zoom glide (wheel step smoothing, geometric) ---
  // Same idea as the x glide, but y-zoom is MULTIPLICATIVE (x1.15 / /1.15)
  // and continuous, so the walk must be geometric: step a fixed fraction of
  // the remaining LOG distance each frame. That gives equal *visual*
  // increments — a linear walk would crawl when zoomed in and lurch when
  // zoomed out, because the same numeric delta means different things at
  // different scales.
  //
  // min/max mirror the x stepper exactly: MIN_STEP keeps it converging
  // briskly instead of asymptotically creeping (an exponential approach never
  // actually arrives), and the `min(step, remaining)` clamp lands it EXACTLY
  // on target. ~5 frames for one wheel tick.
  const Y_GLIDE_RATE = 0.70              // half-time vs 0.45 — same distance, fewer frames
  const Y_GLIDE_MIN_STEP = Math.log(1.02) // never step less than ~2% — guarantees arrival
  // Wheel-only Y glide (wheelslow/wheelslow2): ~2x slower settle, eased all
  // the way to rest. Time-based exponential in log space: tau 28ms = 2x the
  // ~13.9ms time constant of the shared 0.70/frame rate at 60Hz. NO min-step
  // floor: the floor's constant-speed tail then dead stop (steps 17,10,10,0.5
  // x1e-3) was the catch. Instead land once the leftover is sub-pixel
  // (log 0.001 = 0.1% of span, < 0.5px at the axis edge on 1080p). dt-based,
  // so 60/120/144Hz settle in the same ms. Phone y-drag keeps Y_GLIDE_RATE /
  // Y_GLIDE_MIN_STEP (the glide trails the finger there).
  const Y_GLIDE_TAU_WHEEL_MS = 28
  const Y_GLIDE_LAND_WHEEL = 0.001
  const yGlideTauRef = useRef(0)       // 0 = legacy per-frame rate (phone drag)
  const yGlideLastTsRef = useRef(0)
  // tauMs optional; omitted = shared drag feel. Latest caller wins.
  // (yanchor) Wheel-over-Y-axis anchor: u = the pointer's offset from the view
  // centre in half-spans (+1 top, -1 bottom). While set, every glide step pans
  // the explore centre so the price under the pointer stays under it. Only the
  // wheel path sets it; drag / phone leave it null (centre-anchored, unchanged).
  const yWheelAnchorRef = useRef(null)
  const glideYZoom = useCallback((tauMs) => {
    const anchorPan = (from, to) => {
      const u = yWheelAnchorRef.current
      if (u != null && from > 0 && to > 0 && yCam.isExploring()) yCam.panBy((from - to) * u)
    }
    yGlideTauRef.current = (tauMs > 0) ? tauMs : 0
    if (yGlideRafRef.current) return   // already walking; target was updated in place
    const tick = (ts) => {
      yGlideRafRef.current = 0
      let target = yHalfTargetRef.current
      const cur = yCam.userHalf()
      if (target == null || !(cur > 0)) { yHalfTargetRef.current = null; endChartInteractRef.current(); return }
      // (ytickcap) The tick floor moves with price; clamp every tick so a
      // target under it lands on it instead of walking against the camera.
      {
        const lim = yHalfLimitsRef.current()
        target = Math.max(lim.lo, Math.min(lim.hi, target))
        yHalfTargetRef.current = target
      }
      // (yrows) No glide-owned step. latticeGeomFor picks the step from the
      // live halfRange every frame (coarsen under MAX_GRID_ROWS, refine to the
      // tick), so a fast spin cannot run count past the cap.
      const lr = Math.log(target / cur)
      if (!Number.isFinite(lr) || lr === 0) { yHalfTargetRef.current = null; endChartInteractRef.current(); return }
      // Keep the Y-scale gesture window alive for the whole glide.
      markYScaleGesture()
      beginChartInteractRef.current()
      const mag = Math.abs(lr)
      let step
      const tau = yGlideTauRef.current
      if (tau > 0) {
        // (wheelslow2) wheel: exponential ease, land when the rest is sub-pixel.
        const last = yGlideLastTsRef.current
        const dt = (last && ts > last) ? Math.min(64, ts - last) : 16.67
        yGlideLastTsRef.current = ts || 0
        step = mag * (1 - Math.exp(-dt / tau))
        if (mag - step < Y_GLIDE_LAND_WHEEL) step = mag
      } else {
        step = Math.min(Math.max(mag * Y_GLIDE_RATE, Y_GLIDE_MIN_STEP), mag)
      }
      fdCount('ystep')   // (perf4) debug counter
      if (step >= mag) {                       // final frame — land exactly on target
        anchorPan(cur, target)
        yCam.setHalf(target)
        yHalfTargetRef.current = null
        // Rows held → painted in place; one trailing pass syncs the memos.
        // Rows changed → render now so this frame paints the new lattice.
        if (yCamInPlaceRef.current()) setYFitTick((t) => t + 1)
        else { fdCount('rowsCommit'); flushSync(() => setYFitTick((t) => t + 1)) }
        endChartInteractRef.current()
        kickSizer()
        return
      }
      anchorPan(cur, cur * Math.exp(Math.sign(lr) * step))
      yCam.setHalf(cur * Math.exp(Math.sign(lr) * step))
      // Re-arm FIRST: the draw callback reads yGlideRafRef to keep the next
      // frame's draw queued (see scheduleCanvasDraw).
      yGlideRafRef.current = requestFrame(tick, FRAME_ANIM)
      if (!yCamInPlaceRef.current()) {
        // (perf4) ROWS CHANGED → COMMIT NOW, NOT AFTER THE FRAME (a plain
        // setState from rAF paints a frame late — the hitch). The memo
        // consumes this tick's camera through the hand-off.
        fdCount('rowsCommit')
        flushSync(() => setYFitTick((t) => t + 1))
      }
    }
    beginChartInteractRef.current()
    yGlideLastTsRef.current = 0   // (wheelslow2) fresh dt clock per glide
    yGlideRafRef.current = requestFrame(tick, FRAME_ANIM)
    // (perf4) Seed the draw chain: queued outside a tick, so it lands in the
    // SAME frame as the first step (and re-arms itself while the glide runs).
    scheduleCanvasDrawRef.current?.()
  }, [])

  // endChartInteract: clear flag only when NO interact source remains, then
  // rAF one own-canvas heat catch-up. Do NOT gate on shared-scene beginFrame —
  // heat lives on LiquidityHeatLayer's canvas; cell GL clear is unrelated.
  // Placed after x/y glide refs so the closure reads initialized bindings.
  endChartInteractRef.current = () => {
    if (isDraggingChartRef.current) return
    if (pinchActiveRef.current) return
    if (yAxisZoomDragActiveRef.current) return
    if (yGlideRafRef.current) return
    if (interactWheelTimerRef.current) return
    if (zoomGlideRafRef.current || zoomGlideRef.current != null) return
    if (!chartInteractingRef.current) return
    chartInteractingRef.current = false
    // Drop Y markGesture tail so kickSizer is not deferred by isGesturing()
    // for another 200ms after the glide already landed.
    yCam.clearGesture()
    // Land pass: commit any ideal that mid-gesture Y-scale freeze skipped.
    kickSizer()
    requestAnimationFrame(() => {
      heatLayerPaintRef.current?.({ mode: 'force' })
      trailLayerPaintRef.current?.({ mode: 'force' })
    })
  }
  beginChartInteractRef.current = () => {
    const was = chartInteractingRef.current
    chartInteractingRef.current = true
    // First frame of a gesture: redraw without heat.
    if (!was) scheduleCanvasDraw()
  }
  // (trail-wake) Android PWA: backgrounding mid-touch never delivers the
  // pointerup/touchend, so the drag/pinch flags and chartInteractingRef stay
  // TRUE after resume. The trail's own loop sleeps while "interacting" and
  // only paints when the chart repaints -> flashes, never flows, until a
  // restart. On hide/resume, drop stale input state and force a trail paint
  // (its hook re-arms the loop).
  useEffect(() => {
    const unstick = () => {
      isDraggingChartRef.current = false
      pinchActiveRef.current = false
      yAxisZoomDragActiveRef.current = false
      if (interactWheelTimerRef.current) { clearTimeout(interactWheelTimerRef.current); interactWheelTimerRef.current = null }
      chartInteractingRef.current = false
    }
    const onVis = () => {
      unstick()
      if (!document.hidden) {
        requestAnimationFrame(() => {
          heatLayerPaintRef.current?.({ mode: 'force' })
          trailLayerPaintRef.current?.({ mode: 'force' })
        })
      }
    }
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('pageshow', onVis)
    return () => {
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('pageshow', onVis)
    }
  }, [])

  // (perf5) Hold useChartData's live React commits (stats / tape / chartData)
  // while THIS chart's Y glide walks. On futures they arrived every frame: a
  // full App + chart render per glide frame, a new candles array (VP /
  // absorption / grid memo passes, a second yCam.frame()), and a hand-off
  // miss on the next rows-commit because highlight/candles moved. The forming
  // candle is mutated in place, so the per-frame draw still shows it; the
  // batch flushes the frame the glide lands (cap 1s). Predicate reads the
  // walker handle, so every exit path (land / cancel / unmount) releases.
  // (chart2-bounce) Scoped to THIS slot: a glide here must not hold the other
  // chart's data (see dataCommitGate).
  useEffect(() => registerDataHold(() => !!(
    yGlideRafRef.current || zoomGlideRafRef.current || zoomGlideRef.current
  ), chartId), [chartId])

  // Cancel on unmount only (NOT tied to the wheel effect's deps, which churn
  // on every candle close — a cleanup there would abort a glide mid-flick).
  useEffect(() => () => {
    if (hoverRafRef.current) cancelFrame(hoverRafRef.current)
    if (yGlideRafRef.current) cancelFrame(yGlideRafRef.current)
    if (interactWheelTimerRef.current) { clearTimeout(interactWheelTimerRef.current); interactWheelTimerRef.current = 0 }
    if (drawRafRef.current) { cancelFrame(drawRafRef.current); drawRafRef.current = 0 }
    if (warmIdleRef.current) {
      if (typeof cancelIdleCallback === 'function') cancelIdleCallback(warmIdleRef.current)
      clearTimeout(warmIdleRef.current)   // harmless if it was an idle handle
    }
  }, [])

  // ── WebGL scene teardown — UNMOUNT ONLY ───────────────────────────────
  // createFootprintCellGL (see the create at the top of drawCanvas) holds one
  // WebGL context on cellGlCanvasRef, sized containerW x totalHeight x dpr.
  // totalHeight tracks the viewport (RH = max(MIN_ROW_PX, containerHeight /
  // visCount)), so it is bounded — but on a 4K panel at dpr 2 that is still
  // ~18MB of GPU memory, and this component is per chart SLOT. Every slot
  // teardown and every parent remount orphaned one. Browsers cap live
  // contexts and evict the oldest; past the cap createFootprintCellGL returns
  // null, cellGlRef.current becomes false, and since `=== null` is the
  // "not tried" sentinel it never retries — that chart is 2D for the rest
  // of its life, silently.
  //
  // loseContext is the whole teardown: neither footprintCellGL nor the
  // No wrapper exposes a dispose, and losing the context drops
  // every program, buffer, texture and the heat FBO at once. getContext()
  // is forwarded by the scene and already returns null when the controller
  // is dead, so a context that was lost before unmount is skipped for free.
  //
  // The controller's own webglcontextlost listener will fire from this and
  // set its dead flag. That is fine and intended — the canvas is detached
  // by then and the listener goes with it.
  //
  // DEPS ARE [] — this is unmount, not a reaction to any state. It must
  // never run on a dep change: cellGlRef.current would then hold a dead
  // controller, which is truthy, so the `=== null` create guard would never
  // rebuild it and every later draw would fall to 2D with nothing logged.
  useEffect(() => () => {
    const scene = cellGlRef.current
    cellGlRef.current = null
    if (!scene) return
    try {
      scene.getContext?.()?.getExtension('WEBGL_lose_context')?.loseContext()
    } catch {}
  }, [])

  // --- Wheel zoom ---
  useEffect(() => {
    const body = bodyScrollRef.current
    if (!body) return

    // Shared entry: body wheel, footer drag, phone pinch. Clamp and commit.
    // The layout effect on viewWindowSize publishes the ref and schedules the
    // draw, so fit has already run for this window before anything paints.
    // WHAT THE FIT WOULD SAY AT WINDOW `win`, WITHOUT MOVING ANYTHING.
    // Only reachable because scanFitRange is pure — this slices the candles
    // the painter WOULD paint at that window and runs the same scan the memo
    // runs, with none of the centre policy (stateful, would advance the held
    // centre as a side effect of asking a question).
    // (yknow) ONE planner for arm dest AND land remint. Candles already loaded.
    // Matches idle fit after clearFitHold: same end clamp as publishWin,
    // same rangeCandles slice (+leading sliver), wantHalf + quiet, fit centre
    // (mid - step). Pan-free centre — callers add yPan when painting.
    // Body-wheel only. Pinch/footer never set wheelAnchor, so they keep
    // the right-edge end and this returns null.
    const wheelPlace = (win) => {
      const g = zoomGlideRef.current
      if (!g || !g.wheelAnchor) return null
      const tot = chartDataRef.current?.candles?.length || 0
      if (!Number.isFinite(g.anchorA) || !Number.isFinite(g.anchorX)) return null
      const plotW = Math.max(1, (containerWidthRef.current || 0) - RIGHT_PADDING - FIXED_OVERLAY_WIDTH)
      return placeWheelAnchor({
        anchorA: g.anchorA,
        anchorX: g.anchorX,
        plotW,
        win,
        total: tot,
      })
    }
    const landFit = (win) => {
      const cs = chartDataRef.current?.candles
      const total = cs?.length || 0
      if (!total) return null
      const w = Math.max(1, Math.round(+win || 1))
      // Land publishWin → clampEndIdx / commitWin walls at target W.
      const placedY = wheelPlace(w)
      const endRaw = placedY ? placedY.end : resolveEndIdx(viewEndIndexRef.current, total)
      const endIdx = placedY
        ? clampEndIdx(placedY.end, total, w, !placedY.pastRight)
        : clampEndIdx(endRaw, total, w)
      if (endIdx === -1) return null
      const startIdx = startIdxFor(endIdx, w)
      const slice = cs.slice(Math.max(0, startIdx - 1), Math.min(total, endIdx + 1))
      if (!slice.length) return null

      const fit = scanFitRange(slice, logScaleRef.current, () => {
        const d = chartDataRef.current
        const hl = +d?.highlightMcap || 0
        if (hl > 0) return hl
        const cs2 = d?.candles
        if (cs2) {
          for (let i = cs2.length - 1; i >= 0; i--) {
            const v = +(cs2[i]?.close_mcap ?? cs2[i]?.close) || 0
            if (v > 0) return v
          }
        }
        return 0
      }, cs)
      if (!fit) return null
      return { fit, w, endIdx, n: slice.length }
    }
    // lf: a landFit(win) the caller already has (armXCone reuses its scan).
    const planLandY = (win, lf = landFit(win)) => {
      if (!lf) return null
      const { fit } = lf
      const bucket = fit.log ? Math.log(mcapRatioRef.current) : mcapBucketSizeRef.current
      if (!(bucket > 0)) return null
      const dataRange = fit.max - fit.min
      let half = (dataRange / 2) + computeRangeBuffer(dataRange, bucket)
      const minQuietRows = 24
      const minHalf = (minQuietRows * bucket) / 2
      if (half < minHalf) half = minHalf
      return {
        half,
        centre: (fit.max + fit.min) / 2 - bucket,
        min: fit.min,
        max: fit.max,
        w: lf.w,
        endIdx: lf.endIdx,
        n: lf.n,
      }
    }
    const publishWin = (win) => {
      const total = chartDataRef.current?.candles?.length || 0
      const prevW = Math.max(1, +viewWindowSizeRef.current || win)
      const nextW = Math.max(1, +win || prevW)
      const plotW = Math.max(1, (containerWidthRef.current || 0) - RIGHT_PADDING - FIXED_OVERLAY_WIDTH)
      const commitWin = (xWin && typeof xWin.commitWindow === 'function')
        ? xWin.commitWindow
        : (typeof commitWindow === 'function' ? commitWindow : null)
      // Same publish as W: end + frac for the cursor anchor. Null on pinch/footer.
      const placed = wheelPlace(nextW)
      const gAnchor = zoomGlideRef.current
      if (gAnchor && gAnchor.wheelAnchor && placed) {
        // Paint reads these. A follow latch that puts -1 back in the ref
        // must not move the candle under the cursor or the camera pivot.
        gAnchor.placedEnd = placed.end
        gAnchor.placedFrac = placed.frac
        gAnchor.hitStart = !!placed.hitStart
        gAnchor.pastRight = !!placed.pastRight
      }
      const anchorOpt = placed ? { end: placed.end, frac: placed.frac, pastRight: !!placed.pastRight } : null
      let legal
      if (commitWin) {
        // Slot owns W + end clamp. Anchor skips frac rescale (frozen-end rescale slides).
        // (xref) publishWin is glide-only: defer the slot's App state and hold
        // the int locally; stepGlide flushes it at landing.
        let heldW
        ;({ win: heldW, end: legal } = commitWin(nextW, total, plotW, { deferState: true, anchor: anchorOpt }))
        setGlideWinLive(Number.isFinite(heldW) ? heldW : Math.max(1, Math.round(nextW)))
        const sFrac = scrollRef.current?.fractionalOffsetPxRef
        if (sFrac) sFrac.current = fractionalOffsetPxRef.current
      } else if (anchorOpt) {
        legal = clampEndIdx(anchorOpt.end, total, nextW)
        if (legal !== viewEndIndexRef.current) {
          viewEndIndexRef.current = legal
          setViewEndIndex(legal)
        } else {
          viewEndIndexRef.current = legal
        }
        fractionalOffsetPxRef.current = anchorOpt.frac
        const sFrac = scrollRef.current?.fractionalOffsetPxRef
        if (sFrac) sFrac.current = anchorOpt.frac
        setViewWindowSize(prev => prev === nextW ? prev : nextW)
      } else {
        // Column width is plotWidth/W. A leftover pan offset in PIXELS is a
        // different number of columns after the wheel, so the block of cells
        // slides off the left edge until a drag recomputes frac into [0, cw).
        if (prevW !== nextW) {
          const newCw = plotW / nextW
          let frac = (fractionalOffsetPxRef.current || 0) * (prevW / nextW)
          if (newCw > 0) frac = ((frac % newCw) + newCw) % newCw
          fractionalOffsetPxRef.current = frac
          const sFrac = scrollRef.current?.fractionalOffsetPxRef
          if (sFrac) sFrac.current = frac
        }
        legal = clampEndIdx(viewEndIndexRef.current, total, nextW)
        if (legal !== viewEndIndexRef.current) {
          viewEndIndexRef.current = legal
          setViewEndIndex(legal)
        }
        setViewWindowSize(prev => prev === win ? prev : win)
      }
    }

    const inFollowY = () => {
      const cs = chartDataRef.current?.candles
      const total = cs?.length || 0
      const liveInView = isLiveInView(viewEndIndexRef.current, total)
      return liveInView && yCam.isPinned()
    }
    // First glide frame must start at the CURRENT view, not snap to the live
    // scan. Seeding from null half/centre made g.half = t.half instantly.
    const seedY = () => {
      const m = visibleSortedMcapsRef.current
      const h = yCam.peekFitHold()
      let half = glideHalfRef.current
      let centre = Number.isFinite(glideCenterRef.current)
        ? glideCenterRef.current
        : null
      if (!Number.isFinite(half)) half = Number.isFinite(m?.halfRange) ? m.halfRange : null
      if (!Number.isFinite(centre)) {
        if (Number.isFinite(m?.fitCenter)) centre = m.fitCenter
        else if (h) centre = (h.min + h.max) / 2
        else centre = null
      }
      return { half, centre }
    }

    // (xflash) ZOOM-OUT LEAD retired. flushSync in setGlideWinLive rebuilds
    // the lattice in the same anim tick, so ceil(W) already keeps X camera
    // scale = ceil(W)/W ≥ 1 without committing ahead. Lead + mesh clamp used
    // to hold the float and read as a step/flash.
    const glideLead = (_win, _target) => 0
    const stepGlide = (ts) => {
      zoomGlideRafRef.current = 0
      const target = zoomTargetWinRef.current
      const g = zoomGlideRef.current
      if (!g || target == null) {
        zoomGlideRef.current = null
        zoomTargetWinRef.current = null
        glideHalfRef.current = null
        glideCenterRef.current = null
        flushGlideWin()   // (xref) killed from outside: commit where it stands
        return
      }
      const dt = g.last ? Math.min(64, ts - g.last) : 16.67
      g.last = ts
      g.frameDt = dt
      const prevWin = g.win
      g.win = easeToward(g.win, target, dt, g.tau || ZOOM_GLIDE_TAU_MS)
      // (ease25) Wheel only: cruise at the peak speed this glide has reached
      // (or the minRate floor), then decelerate at a constant rate over the
      // last GLIDE_EASE_FRAC of the time and stop exactly on the target.
      // A stacked notch raises the eased speed and so the peak, and restarts
      // the distance the slow zone is sized from; a reversal resets the peak
      // so the turn is not taken at the old speed.
      if (g.minRate > 0) {
        const rem = target - prevWin
        const dir = Math.sign(rem)
        const left = Math.abs(rem)
        if (dir !== g.dir) { g.dir = dir; g.peak = 0 }
        if (target !== g.easeTgt) { g.easeTgt = target; g.easeFrom = prevWin }
        g.peak = Math.max(g.peak || 0, Math.abs(g.win - prevWin) / dt, g.minRate)
        const f = GLIDE_EASE_FRAC
        const slow = f > 0 ? (f / (2 - f)) * Math.abs(target - g.easeFrom) : 0
        // Constant decel from peak to 0 over `slow`: v = peak*sqrt(left/slow).
        const acc = slow > 0 ? (g.peak * g.peak) / (2 * slow) : 0
        let d
        if (left - g.peak * dt >= slow) {
          d = g.peak * dt
        } else if (left > slow) {
          // Zone entry mid-frame: cruise to the edge, decelerate the rest.
          const tc = (left - slow) / g.peak
          const td = Math.min(dt - tc, g.peak / acc)
          d = (left - slow) + g.peak * td - 0.5 * acc * td * td
        } else {
          const vNow = g.peak * Math.sqrt(left / slow)
          const vNext = Math.max(0, vNow - acc * dt)
          d = Math.max(((vNow + vNext) / 2) * dt, g.minRate * 0.25 * dt)
        }
        g.win = left <= d ? target : prevWin + dir * d
      }
      g.step = Math.abs(g.win - prevWin)
      const xDone = Math.abs(target - g.win) < (g.land > 0 ? g.land : 0.05)
      // Footer held: never LAND mid-drag. Landing switches the X camera off
      // and snaps to whole candles, so a drag that kept reaching its target
      // went step-snap-step. Sit on the (float) target with the camera on
      // until pointer-up, which hands in a whole-candle target to land on.
      if (xDone && footerDragRef.current) {
        g.win = target
        publishWin(Math.ceil(g.win - 1e-6) + glideLead(g.win, target))
        zoomGlideRafRef.current = requestFrame(stepGlide, FRAME_ANIM)
        return
      }
      if (xDone) {
        // (yknow2) Land = locked arm dest. Only planLandY if cone never armed
        // (pinned / failed seed). pendingLandYRef → memo skips frame().
        // Anchor lives on the glide. Publish before clearing it or the land
        // frame drops back to the right edge and the cursor candle slides.
        publishWin(target)
        const planned = (g.cone && Number.isFinite(g.destHalf) && Number.isFinite(g.destCentre)
            && g.destHalf > 0)
          ? { half: g.destHalf, centre: g.destCentre, min: g.destCentre - g.destHalf, max: g.destCentre + g.destHalf }
          : planLandY(target)
        // Apply locked dest via pendingLandY on the land remint — do NOT
        // applyXConeY here (that flushSyncs while cone is still live, then
        // we immediately clear cone refs).
        if (planned && Number.isFinite(planned.half)) {
          pendingLandYRef.current = planned
          yLandExactRef.current = {
            half: planned.half,
            centre: planned.centre,
            min: planned.min,
            max: planned.max,
          }
        }
        zoomGlideRef.current = null
        zoomTargetWinRef.current = null
        glideHalfRef.current = null
        glideCenterRef.current = null
        if (planned) yfitC('planLandY/cone land dest', { lo: planned.min, hi: planned.max, c: planned.centre, h: planned.half })
        yCam.clearFitHold('land')
        flushGlideWin()   // (xref) the ONE App-state commit of this glide
        endChartInteractRef.current()
        kickSizer()
        return
      }
      // (ycone / ydiag) Cone only while Y scale is idle. Diagonal Y-gesture
      // pauses cone (clears glide refs → memo uses frame/wantHalf); resume
      // from currently framed range when Y goes idle mid X-glide.
      if (g.cone) runXCone(g, target)
      // Whole candles UP (ceil), so the paint's X camera only ever stretches
      // (scale ≥ 1) between steps — see the camera in drawCanvas.
      publishWin(Math.ceil(g.win - 1e-6) + glideLead(g.win, target))
      zoomGlideRafRef.current = requestFrame(stepGlide, FRAME_ANIM)
    }

    // (ydragslide) kickYFollow is owned by the X-drag Y-fit effect below —
    // do not blank it here (wheel cone still wins inside that kick).

    // (yknow2) Cone Y: arm locks dest; applyXConeY publishes lerp + remints
    // geometry from that camera (latticeGeomFor). No live-window fit mid-glide.
    // (ydiag) User Y-scale owns halfSpan/centre while active. Cone yields.
    const yScaleLive = () => !!(
      yGlideRafRef.current
      || yHalfTargetRef.current != null
      || yAxisZoomDragActiveRef.current
      || yCam.isGesturing()
    )

    const pauseXCone = (g) => {
      glideHalfRef.current = null
      glideCenterRef.current = null
      if (g) g.conePaused = true
    }

    const applyXConeY = (half, centre) => {
      if (!(half > 0) || !Number.isFinite(centre)) return
      if (yCam.isPinned() || yScaleLive()) return
      // (yview) Publish precomputed camera for the painted W, remint via
      // latticeGeomFor (coneLive). Skip tick when already on this plan — X
      // camera still advances via publishWin between W steps.
      const paintC = centre
      if (glideHalfRef.current === half
          && glideCenterRef.current === paintC) return
      glideHalfRef.current = half
      glideCenterRef.current = paintC
      flushSync(() => setYFitTick((t) => t + 1))
    }

    // (yview) Land is planLandY(toWin) once, at arm. Mid-glide, centre and
    // half walk a straight line from the arm seed to that land (same p as
    // the eased X width — no second ease). Pre-warm _display for candles
    // that enter as the width changes. Do not retarget Y to each width.
    const preWarmConeDisplays = (wLo, wHi) => {
      const cs = chartDataRef.current?.candles
      const total = cs?.length || 0
      if (!total) return
      const wWide = Math.max(1, Math.max(wLo, wHi))
      const placedY = wheelPlace(wWide)
      const endRaw = placedY ? placedY.end : resolveEndIdx(viewEndIndexRef.current, total)
      const endIdx = placedY
        ? clampEndIdx(placedY.end, total, wWide, !placedY.pastRight)
        : clampEndIdx(endRaw, total, wWide)
      if (endIdx === -1) return
      const startIdx = Math.max(0, startIdxFor(endIdx, wWide) - 1)
      const hi = Math.min(total - 1, endIdx + 1)
      const L = visibleSortedMcapsRef.current
      let dispRatio = null
      let dispKeyBucket = mcapBucketSizeRef.current
      if (L && L.log && L.length >= 2) {
        dispRatio = Math.round((L[0] / L[1]) * 1e8) / 1e8
      } else if (L && L.length >= 2) {
        dispKeyBucket = Math.round((L[0] - L[1]) * 1e8) / 1e8
      }
      for (let ci = startIdx; ci <= hi; ci++) {
        const c = cs[ci]
        if (!c || (c.isHistorical && !c._omFootprint)) continue
        try { buildDisplay(c, dispKeyBucket, dispRatio) } catch {}
      }
    }

    const armXCone = (g, fromWin, toWin) => {
      // (ydragslide) Cone takes the glide refs — drop any X-drag Y window first.
      yDragFitDestRef.current = null
      yLandExactRef.current = null
      if (!g || yCam.isPinned() || yScaleLive()) {
        pauseXCone(g)
        if (g) {
          g.diag = false
          g.cone = false
          g.coneFromWin = fromWin
          g.startHalf = null
          g.startCentre = null
          g.destHalf = null
          g.destCentre = null
          g.yPath = null
        }
        return
      }
      const seed = seedY()
      // (glidebucket) Dest bucket first (every re-arm, newest toWin), then
      // plan the land on it so land == what the idle fit would paint.
      const lf = landFit(toWin)
      if (lf) {
        const ex = lf.fit.log ? Math.exp : (v) => v
        commitArmBucketRef.current?.(ex(lf.fit.min), ex(lf.fit.max))
      }
      const dest = planLandY(toWin, lf)
      if (!(Number.isFinite(seed.half) && Number.isFinite(seed.centre) && dest
            && Number.isFinite(dest.half) && Number.isFinite(dest.centre))) {
        // Can't know yet — hold Y (no cone morph of current window).
        pauseXCone(g)
        g.diag = false
        g.cone = false
        g.coneFromWin = fromWin
        g.startHalf = null
        g.startCentre = null
        g.destHalf = null
        g.destCentre = null
        g.yPath = null
        return
      }
      const wA = Math.max(1, Math.round(+fromWin || 1))
      const wB = Math.max(1, Math.round(+toWin || 1))
      const wLo = Math.min(wA, wB)
      const wHi = Math.max(wA, wB)
      // One land. Sampling each integer width's midpoint is the U dip.
      // diag is only whether this land moves Y off the arm seed.
      const baseH = Math.max(1e-9, seed.half)
      const diag = Math.max(
        Math.abs(dest.half - seed.half) / baseH,
        Math.abs(dest.centre - seed.centre) / baseH) > 2e-3
      g.diag = diag
      g.yPath = null
      g.yBiasHalf = 0
      g.yBiasCentre = 0
      g.coneFromWin = fromWin
      g.startHalf = seed.half
      g.startCentre = seed.centre
      g.destHalf = dest.half
      g.destCentre = dest.centre
      g.cone = true
      try {
        if (typeof localStorage !== 'undefined'
            && localStorage.getItem('cheddar_debug_yplan') === '1') {
          dbg('[yplan] arm',
            'fromW=' + (+fromWin).toFixed(2),
            'toW=' + dest.w,
            'straight=1',
            'end=' + dest.endIdx,
            'n=' + dest.n,
            'startH=' + seed.half.toFixed(5),
            'destH=' + dest.half.toFixed(5),
            'dH=' + (dest.half - seed.half).toFixed(5),
            'startC=' + seed.centre.toFixed(5),
            'destC=' + dest.centre.toFixed(5),
            'dC=' + (dest.centre - seed.centre).toFixed(5))
        }
      } catch {}
      // Pre-mint displays for every column that will enter before first RAF.
      preWarmConeDisplays(wLo, wHi)
      glideHalfRef.current = seed.half
      glideCenterRef.current = seed.centre
      g.conePaused = false
    }

    // One armed glide, one Y land (destHalf / destCentre). Centre and
    // half-span walk a straight line from the arm seed to that land.
    // p is the eased X width's progress — no second ease, no per-width
    // midpoint. Top and bottom each move in a straight line.
    const stepXCone = (g, target) => {
      if (!g || !(g.destHalf > 0) || !Number.isFinite(g.destCentre)) return
      if (!(g.startHalf > 0) || !Number.isFinite(g.startCentre)) return
      const span = target - g.coneFromWin
      const p = Math.abs(span) < 1e-9
        ? 1
        : Math.max(0, Math.min(1, (g.win - g.coneFromWin) / span))
      const goalH = g.startHalf * (1 - p) + g.destHalf * p
      const goalC = g.startCentre * (1 - p) + g.destCentre * p
      applyXConeY(goalH, goalC)
    }

    // (ydiag / yknow2) Pause while Y live. Resume keeps the LOCKED dest
    // (plan-once at arm / notch); only refresh start from current framing.
    // Never replan dest mid-glide unless the X target itself changed (arm on
    // new notch in setXWindowRef).
    const runXCone = (g, target) => {
      if (!g || !g.cone) return
      if (yCam.isPinned() || yScaleLive()) {
        pauseXCone(g)
        return
      }
      if (g.conePaused && Number.isFinite(g.destHalf) && Number.isFinite(g.destCentre)
          && g.destHalf > 0) {
        const seed = seedY()
        if (Number.isFinite(seed.half) && Number.isFinite(seed.centre)) {
          g.coneFromWin = g.win
          g.startHalf = seed.half
          g.startCentre = seed.centre
          glideHalfRef.current = seed.half
          glideCenterRef.current = seed.centre
          g.conePaused = false
        } else {
          armXCone(g, g.win, target)
        }
      } else if (!(g.startHalf > 0) || !(g.destHalf > 0)
          || !Number.isFinite(g.startCentre) || !Number.isFinite(g.destCentre)) {
        armXCone(g, g.win, target)
      }
      if (g.conePaused) return
      stepXCone(g, target)
    }

    // opts: optional { tau, minRate, land, cone } (wheelslow/wheelslow2 / ycone).
    // Omitted = shared ZOOM_GLIDE_TAU_MS, no floor, 0.05 land (pinch, footer).
    // Wheel passes its own set + cone:true. Latest caller wins.
    setXWindowRef.current = (w, opts) => {
      // Manual X zoom — stop resize from re-fitting under the user.
      xAutoFitRef.current = false
      // Body wheel only. Pinch and footer omit wheelAnchor and clear it,
      // so their glide stays right-edge anchored.
      const stampWheelAnchor = (g) => {
        if (!g) return
        if (opts && opts.wheelAnchor && Number.isFinite(opts.anchorX) && Number.isFinite(opts.anchorA)) {
          g.wheelAnchor = true
          g.anchorX = opts.anchorX
          g.anchorA = opts.anchorA
        } else {
          g.wheelAnchor = false
          g.anchorX = null
          g.anchorA = null
        }
      }
      // opts.float: keep a fractional target (footer drag — the X camera
      // renders it exactly). Everyone else targets whole candles.
      const _w = (opts && opts.float) ? +w : Math.round(+w)
      const next = Math.max(MIN_VIEW_WINDOW, Math.min(MAX_VIEW_WINDOW_X, _w || MIN_VIEW_WINDOW))
      const tau = (opts && opts.tau > 0) ? opts.tau : ZOOM_GLIDE_TAU_MS
      const minRate = (opts && opts.minRate > 0) ? opts.minRate : 0
      const land = (opts && opts.land > 0) ? opts.land : 0.05
      const wantCone = !!(opts && opts.cone)
      const prevTarget = zoomTargetWinRef.current
      zoomTargetWinRef.current = next
      if (!zoomGlideRef.current) {
        const from = Math.max(MIN_VIEW_WINDOW, +viewWindowSizeRef.current || next)
        zoomGlideRef.current = {
          win: from, last: 0, tau, minRate, land, step: 0, cone: wantCone,
          conePaused: false,
          coneFromWin: from,
          startHalf: null, startCentre: null,
          destHalf: null, destCentre: null,
          yBiasHalf: 0, yBiasCentre: 0,
          yPath: null,
          diag: false,
          wheelAnchor: false,
          anchorX: null,
          anchorA: null,
          hitStart: false,
          pastRight: false,
        }
        stampWheelAnchor(zoomGlideRef.current)
        // Plan dest FIRST (before interact draw / first RAF). Then move.
        // Anchor is already stamped so planLandY sees the cursor end.
        if (wantCone) armXCone(zoomGlideRef.current, from, next)
        beginChartInteractRef.current()
      } else {
        zoomGlideRef.current.tau = tau
        zoomGlideRef.current.minRate = minRate
        zoomGlideRef.current.land = land
        stampWheelAnchor(zoomGlideRef.current)
        // New notch mid-glide: restart cone from the currently framed range.
        if (wantCone && next !== prevTarget) {
          zoomGlideRef.current.cone = true
          armXCone(zoomGlideRef.current, zoomGlideRef.current.win, next)
        } else if (!wantCone) {
          zoomGlideRef.current.cone = false
          glideHalfRef.current = null
          glideCenterRef.current = null
        }
      }
      if (!zoomGlideRafRef.current) {
        zoomGlideRafRef.current = requestFrame(stepGlide, FRAME_ANIM)
      }
    }

    // PROPORTIONAL STEP: win x k, not win + n. A fixed candle COUNT cannot be
    // right at both ends of the range — 12 of 20 columns is a lurch, 12 of
    // 500 is imperceptible. The y axis already uses a ratio for the same
    // reason. Shared scope, not handler's: applyWheelX is a sibling.
    // Per-notch RATIO, not an additive fraction. Additive was reversible
    // enough to pass at 0.25 (out x1.25, in x0.75 — a 6% round-trip error)
    // but not at this size: out x1.75 against in x0.25 means one click out
    // and one back in lands at 0.44 of where you started. A ratio returns
    // exactly at any step size.
    const X_STEP_FRAC = 0.75
    const X_STEP_K = 1 + X_STEP_FRAC
    const xStepWin = (win, dir) => {
      const w0 = Math.max(MIN_VIEW_WINDOW, +win || MIN_VIEW_WINDOW)
      return Math.max(1, Math.round(dir > 0 ? w0 * X_STEP_K : w0 / X_STEP_K))
    }

    // One interact window for the whole spin, on a trailing timer — the same
    // mechanism the y-axis branch already used. It is what keeps the bucket
    // sizer from re-minting the lattice between notches; that used to be the
    // ease flag's job, and this is one mechanism instead of three.
    const markWheelInteract = () => {
      beginChartInteractRef.current()
      if (interactWheelTimerRef.current) clearTimeout(interactWheelTimerRef.current)
      interactWheelTimerRef.current = setTimeout(() => {
        interactWheelTimerRef.current = 0
        endChartInteractRef.current()
      }, 120)
    }

    // (ycone A) Continuous log map matching Y drag. One typical mouse notch
    // (deltaY ≈ 100) ≈ x1.25 span. Trackpad micro-deltas stay small.
    // (yexplore) Sets the user span (absolute half), clamped to the tick
    // floor / 5x fit ceiling. deltaY > 0 = zoom out (more range).
    const Y_WHEEL_DIV = 100 / Math.log(1.25)
    const applyWheelY = (deltaY, anchorU) => {
      if (!Number.isFinite(deltaY) || deltaY === 0) return
      if (!ensureUserSpanRef.current()) return
      // (yanchor) Zoom about the price under the mouse: explore holds a fixed
      // centre the glide can pan (follow / fit would re-centre it).
      if (Number.isFinite(anchorU)) {
        if (!yCam.isExploring()) {
          const L = visibleSortedMcapsRef.current
          yCam.enterExplore(Number.isFinite(L?.fitCenter) ? L.fitCenter : null)
        }
        yWheelAnchorRef.current = yCam.isExploring() ? anchorU : null
      } else yWheelAnchorRef.current = null
      markWheelInteract()
      const base = yHalfTargetRef.current ?? yCam.userHalf()
      const lim = yHalfLimitsRef.current()
      yHalfTargetRef.current = Math.max(lim.lo, Math.min(lim.hi, base * Math.exp(deltaY / Y_WHEEL_DIV)))
      markYScaleGesture()
      glideYZoom(Y_GLIDE_TAU_WHEEL_MS)
    }

    const handler = (e) => {
      e.preventDefault()

      // Over y-axis: y-zoom only. Coalesce summed deltaY like X notches so a
      // trackpad burst is one continuous target update per frame, not N×1.25.
      if (e.target.closest('.y-axis-body')) {
        e.stopPropagation()
        wheelBurstRef.current.yDelta += e.deltaY
        {
          const r = e.target.closest('.y-axis-body').getBoundingClientRect()
          wheelBurstRef.current.yU = (r.height > 0 && Number.isFinite(e.clientY))
            ? Math.max(-1, Math.min(1, 1 - 2 * (e.clientY - r.top) / r.height)) : null
        }
        if (wheelBurstRef.current.yRaf) return
        wheelBurstRef.current.yRaf = requestFrame(() => {
          const burst = wheelBurstRef.current
          burst.yRaf = 0
          const d = burst.yDelta
          burst.yDelta = 0
          if (d) applyWheelY(d, burst.yU)
        }, FRAME_INPUT)
        return
      }
      // (xflash) Length via ref — was chartData.candles.length in the effect
      // deps, so every candle close remounted this listener and aborted the
      // glide (cleanup cancelled zoomGlideRaf).
      if (!(chartDataRef.current?.candles?.length > 0)) return

      // ── COALESCE THE BURST. ONE HANDLER BODY PER FRAME. ────────────────
      // A wheel does not send one event per notch: a flick sends 10-30 in a
      // few hundred ms, delivered at the device's rate, not the display's.
      // pointermove is passive and browser-coalesced; wheel is neither, and
      // this listener calls preventDefault, so every event in the burst is a
      // hard synchronous block on the main thread. Sum the notches here, run
      // the real body once on the next frame.
      wheelBurstRef.current.notches += (e.deltaY > 0 ? 1 : -1)
      if (Number.isFinite(e.clientX)) wheelBurstRef.current.clientX = e.clientX
      if (wheelBurstRef.current.raf) return
      wheelBurstRef.current.raf = requestFrame(() => {
        const burst = wheelBurstRef.current
        burst.raf = 0
        const notches = burst.notches
        const clientX = burst.clientX
        burst.notches = 0
        if (notches) applyWheelX(notches, clientX)
      }, FRAME_INPUT)
    }

    // The real body, once per frame. `notches` is the summed burst — sign is
    // the direction, magnitude is how many clicks landed in that frame.
    const applyWheelX = (notches, clientX) => {
      if (!(chartDataRef.current?.candles?.length > 0)) return
      // Before the first glide frame. A trade latch queued on -1 must see
      // the park and not write the live pad over the cursor anchor.
      if (xWin?.wheelParkRef) xWin.wheelParkRef.current = true
      markWheelInteract()
      const direction = notches > 0 ? 1 : -1
      const steps = Math.min(8, Math.abs(notches))
      // Chain from where the glide is GOING, not where it is. Mid-glide the
      // committed window is a partial position (plus the zoom-out lead), so
      // basing a new notch on it re-aimed the glide short of / past the
      // previous notch's destination: every notch pulsed the zoom — the
      // wheel's double call. At rest the target is null → current window.
      const _base = zoomGlideRef.current && zoomTargetWinRef.current != null
        ? zoomTargetWinRef.current
        : viewWindowSizeRef.current
      let w = Math.max(MIN_VIEW_WINDOW, +_base || MIN_VIEW_WINDOW)
      // The step is a FRACTION of the window, so N notches must compound —
      // not multiply one step by N.
      for (let n = 0; n < steps; n++) {
        w = Math.max(MIN_VIEW_WINDOW, Math.min(MAX_VIEW_WINDOW_X, xStepWin(w, direction)))
      }
      const plotW = Math.max(1, (containerWidthRef.current || 0) - RIGHT_PADDING - FIXED_OVERLAY_WIDTH)
      let anchorX = plotW
      const canvas = canvasRef.current
      const host = canvas || body
      if (host && Number.isFinite(clientX)) {
        anchorX = clientX - host.getBoundingClientRect().left
      }
      if (!(anchorX >= 0)) anchorX = 0
      if (anchorX > plotW) anchorX = plotW
      const total = chartDataRef.current.candles.length
      const g0 = zoomGlideRef.current
      const winInt = Math.max(1, Math.round(+viewWindowSizeRef.current || 1))
      const winFloat = (g0 && +g0.win > 0) ? +g0.win : winInt
      // Match the paint. A wall frame is unstretched unless this notch is
      // diagonal, and then the camera is the plot's right edge — not the cursor.
      const onForwardWall = !!(g0 && !g0.pastRight
        && Number.isFinite(g0.placedEnd) && total > 0
        && g0.placedEnd >= total - 1 + FUTURE_PADDING - 1e-6)
      const wall = !!(g0 && g0.wheelAnchor && (g0.hitStart || onForwardWall))
      const camOn = !!(g0 && Math.abs(winInt - winFloat) > 1e-4 && (!wall || g0.diag))
      const camScale = camOn ? (winInt / winFloat) : 1
      const camAx = camOn
        ? ((wall && g0.diag) ? plotW
          : ((g0.wheelAnchor && Number.isFinite(g0.anchorX)) ? g0.anchorX : plotW))
        : null
      const endNow = (g0 && g0.wheelAnchor && Number.isFinite(g0.placedEnd))
        ? g0.placedEnd
        : resolveEndIdx(viewEndIndexRef.current, total, !(xWin?.wheelParkRef?.current))
      const fracNow = (g0 && g0.wheelAnchor && Number.isFinite(g0.placedFrac))
        ? g0.placedFrac
        : (fractionalOffsetPxRef.current || 0)
      const anchorA = wheelAnchorIndex({
        end: endNow,
        frac: fracNow,
        plotW,
        win: winInt,
        screenX: anchorX,
        camScale,
        camAx,
      })
      // Pure cursor anchor in both directions. The soft left edge sits at
      // the viewport (oldest candle at the right edge), so a cursor over
      // real data never reaches a wall; over the blank columns left of
      // history the anchor is still a legal X (negative index) and only
      // clamps once the oldest candle would leave the plot — then the paint
      // holds the right edge (hitStart). No span mode, no gutter special case.
      const tgt = zoomTargetWinRef.current
      const same = w === Math.max(MIN_VIEW_WINDOW, Math.round(+_base || w))
        && (tgt == null || Math.round(tgt) === w)
      if (same) return
      setXWindowRef.current(w, {
        tau: ZOOM_GLIDE_TAU_WHEEL_MS,
        minRate: ZOOM_GLIDE_MIN_RATE_WHEEL,
        land: ZOOM_GLIDE_LAND_WHEEL,
        cone: true,
        wheelAnchor: true,
        anchorX,
        anchorA,
      })
    }

    // (drag-jump) FREEZE THE X GLIDE WHERE IT IS ON SCREEN. A body drag
    // starting mid-glide used to leave the glide running: every step
    // re-published its cursor-anchored end over the drag's, and it then
    // landed on its TARGET window/end mid-drag. Here the glide stops at its
    // CURRENT eased width instead: the continuous candle under the pointer is
    // read through the same camera the last full pass painted with, then
    // re-placed at the nearest whole window so that candle stays under the
    // pointer. Y holds the cone frame currently painted. Returns the glide
    // state for the debug log, or null when nothing was gliding.
    freezeXGlideRef.current = (clientX) => {
      const pendingNotches = wheelBurstRef.current.raf ? wheelBurstRef.current.notches : 0
      if (wheelBurstRef.current.raf) { cancelFrame(wheelBurstRef.current.raf); wheelBurstRef.current.raf = 0 }
      wheelBurstRef.current.notches = 0
      const g = zoomGlideRef.current
      if (!g && !zoomGlideRafRef.current) return pendingNotches ? { pendingNotches } : null
      if (zoomGlideRafRef.current) { cancelFrame(zoomGlideRafRef.current); zoomGlideRafRef.current = 0 }
      const info = {
        pendingNotches,
        win: g ? +g.win : null, target: zoomTargetWinRef.current,
        placedEnd: g ? g.placedEnd : null, wheelAnchor: !!(g && g.wheelAnchor),
        cone: !!(g && g.cone),
      }
      const total = chartDataRef.current?.candles?.length || 0
      if (g && total > 0 && +g.win > 0) {
        const plotW = Math.max(1, (containerWidthRef.current || 0) - RIGHT_PADDING - FIXED_OVERLAY_WIDTH)
        let px = plotW
        const host = canvasRef.current || body
        if (host && Number.isFinite(clientX)) px = clientX - host.getBoundingClientRect().left
        px = Math.max(0, Math.min(plotW, px))
        const winInt = Math.max(1, Math.round(+viewWindowSizeRef.current || 1))
        const cam = xCamFullRef.current || { active: false, scale: 1, ax: 0 }
        const endNow = (g.wheelAnchor && Number.isFinite(g.placedEnd))
          ? g.placedEnd
          : resolveEndIdx(viewEndIndexRef.current, total, !(xWin?.wheelParkRef?.current))
        const fracNow = (g.wheelAnchor && Number.isFinite(g.placedFrac))
          ? g.placedFrac
          : (fractionalOffsetPxRef.current || 0)
        const a = wheelAnchorIndex({
          end: endNow, frac: fracNow, plotW, win: winInt, screenX: px,
          camScale: cam.active ? cam.scale : 1, camAx: cam.active ? cam.ax : null,
        })
        const W = Math.max(MIN_VIEW_WINDOW, Math.min(MAX_VIEW_WINDOW_X, Math.round(+g.win)))
        if (Number.isFinite(a)) {
          g.wheelAnchor = true
          g.anchorA = a
          g.anchorX = px
        }
        publishWin(W)   // wheelPlace(W) -> commitWindow anchor: end + frac at W
        info.frozenWin = W
        info.frozenEnd = viewEndIndexRef.current
        // (wheel-drag) Commit the frozen window's bucket NOW (same rule as the
        // wheel arm) and seed the sizer gate with it, so the first drag frame
        // doesn't jump 2+ rungs from a stale range and remint the grid.
        const lf = landFit(W)
        if (lf) {
          const ex = lf.fit.log ? Math.exp : (v) => v
          const lo = ex(lf.fit.min), hi = ex(lf.fit.max)
          commitArmBucketRef.current?.(lo, hi)
          if (Number.isFinite(lo) && Number.isFinite(hi) && hi > lo) sizerLastRangeRef.current = { lo, hi }
        }
        lastSizerRunAtRef.current = performance.now()
        dragFreezeAtRef.current = performance.now()
      }
      // Y: keep the cone frame that is on screen as the glide frame (drag fit
      // eases from it). No landing refs - they snapped Y back on release.
      pendingLandYRef.current = null
      yLandExactRef.current = null
      zoomGlideRef.current = null
      zoomTargetWinRef.current = null
      const h = glideHalfRef.current, c = glideCenterRef.current
      if (!(g && g.cone && h > 0 && Number.isFinite(c))) {
        glideHalfRef.current = null
        glideCenterRef.current = null
      }
      flushGlideWin()   // one slot commit of the frozen window
      return info
    }

    body.addEventListener('wheel', handler, { passive: false })
    // Delta subgraph panes forward their wheel here (same function, same anchor).
    bodyWheelHandlerRef.current = handler
    return () => {
      freezeXGlideRef.current = null
      if (bodyWheelHandlerRef.current === handler) bodyWheelHandlerRef.current = null
      body.removeEventListener('wheel', handler)
      if (zoomGlideRafRef.current) { cancelFrame(zoomGlideRafRef.current); zoomGlideRafRef.current = 0 }
      flushGlideWin()   // (xref) aborted mid-glide: slot keeps the shown window
      zoomGlideRef.current = null
      zoomTargetWinRef.current = null
      glideHalfRef.current = null
      glideCenterRef.current = null
      if (wheelBurstRef.current.raf) {
        cancelFrame(wheelBurstRef.current.raf)
        wheelBurstRef.current.raf = 0
      }
      if (wheelBurstRef.current.yRaf) {
        cancelFrame(wheelBurstRef.current.yRaf)
        wheelBurstRef.current.yRaf = 0
      }
      wheelBurstRef.current.notches = 0
      wheelBurstRef.current.yDelta = 0
    }
  }, [glideYZoom, markYScaleGesture, setViewWindowSize, setViewEndIndex, yCam])

  // --- Pinch zoom (touch) ---
  // Two-finger pinch over the chart body drives the same x-zoom the wheel
  // does: setViewWindowSize, clamped to [MIN_VIEW_WINDOW, MAX_VIEW_WINDOW_X].
  // Native listeners on the body element fire BEFORE React's root-delegated
  // handlers, so pinchActiveRef is already true when the scroll hook's
  // handlePointerDown sees the second finger — pan never starts mid-pinch.
  // Mouse never produces two concurrent pointers, so desktop is untouched.
  useEffect(() => {
    const body = bodyScrollRef.current
    if (!body) return
    const pointers = new Map()   // id -> { x, y, t } — t for ghost purging
    let pinchStart = null // { dist, windowSize }
    let pinchRaf = 0      // rAF-coalesced commit (same reasoning as the footer drag)
    let pinchPendingW = 0

    // ── Ghost-pointer self-heal ──
    // Mobile browsers sometimes EAT pointerup/pointercancel (gesture
    // handoff, pull-to-refresh, app-switch mid-touch). One missed up left a
    // ghost finger in the map forever: the next one-finger swipe counted as
    // a 2-finger pinch (pan suppressed + windowSize thrashed against the
    // ghost's stale coords), and on lift size stayed 1 so pinchActiveRef
    // NEVER cleared — every swipe silently ignored, chart "frozen" while
    // everything else ran, until a full reload. Two defenses:
    //   1. purge entries not seen for 3s on every pointerdown (a real pinch
    //      is continuous motion; a 3s-silent pointer is a ghost)
    //   2. clearAll on visibilitychange/pagehide — gesture state must never
    //      survive a backgrounding, which is exactly when ups get eaten.
    const clearAll = () => {
      pointers.clear()
      pinchStart = null
      pinchActiveRef.current = false
      endChartInteractRef.current()
    }
    const purgeStale = (now) => {
      for (const [id, p] of pointers) {
        if (now - p.t > 3000) pointers.delete(id)
      }
      if (pointers.size < 2) {
        pinchStart = null
        if (pointers.size === 0) {
          pinchActiveRef.current = false
          endChartInteractRef.current()
        }
      }
    }
    const onVis = () => { if (document.hidden) clearAll() }

    const dist = () => {
      const [a, b] = [...pointers.values()]
      return Math.hypot(a.x - b.x, a.y - b.y)
    }

    const onDown = (e) => {
      if (!(e.pointerType === 'touch' || (COARSE_POINTER && e.pointerType !== 'pen'))) return
      purgeStale(performance.now())
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, t: performance.now() })
      if (pointers.size === 2) {
        pinchActiveRef.current = true
        beginChartInteractRef.current()
          pinchStart = { dist: dist(), windowSize: viewWindowSizeRef.current }
        // Pinch implies the user is reshaping the viewport — leave autoscroll
        // the same way wheel x-zoom does NOT, so don't touch it here. Zoom
        // in autoscroll keeps following live candles, matching wheel behavior.
      }
    }

    const onMove = (e) => {
      if (!pinchStart || !pointers.has(e.pointerId)) return
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, t: performance.now() })
      if (pointers.size < 2) return
      const d = dist()
      if (d < 10) return // degenerate — fingers basically touching
      const nextF = pinchStart.windowSize * (pinchStart.dist / d)
      const next = Math.max(MIN_VIEW_WINDOW, Math.min(MAX_VIEW_WINDOW_X, nextF))
      // Phone: same shared entry as wheel (setXWindowRef). Desktop: keep
      // snappy rAF-int commits — mouse users don't pinch.
      if (isPhoneDevice()) {
        setXWindowRef.current(next)
        return
      }
      // Desktop / non-phone: own the window; cancel any wheel ease and commit int.
      pinchPendingW = Math.round(next)
      xAutoFitRef.current = false
      if (pinchRaf) return
      pinchRaf = requestFrame(() => {
        pinchRaf = 0
        setViewWindowSize(prev => prev === pinchPendingW ? prev : pinchPendingW)
      }, FRAME_INPUT)
    }

    const onUp = (e) => {
      if (!(e.pointerType === 'touch' || (COARSE_POINTER && e.pointerType !== 'pen'))) return
      pointers.delete(e.pointerId)
      if (pointers.size < 2) pinchStart = null
      // Keep pan suppressed until every finger is gone — a leftover single
      // finger resuming the pan would jump to its stale dragStart coords.
      if (pointers.size === 0) {
        pinchActiveRef.current = false
        endChartInteractRef.current()
      }
    }

    body.addEventListener('pointerdown', onDown)
    body.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('pagehide', clearAll)
    return () => {
      body.removeEventListener('pointerdown', onDown)
      body.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('pagehide', clearAll)
      cancelFrame(pinchRaf)
      pinchActiveRef.current = false
      endChartInteractRef.current()
    }
  }, [])

  // --- Y-axis touch-drag to scale price (the user span) ---
  // The y-axis zoom was WHEEL-ONLY (no touch path), so on a phone price
  // couldn't be scaled. A vertical finger drag on the y-axis now drives the
  // same user span the wheel does: drag down = zoom out (see more range),
  // drag up = zoom in, matching the wheel's deltaY sign. Continuous from drag
  // distance rather than stepped. Mirrors the wheel's span limits and its
  // autoscroll-only guard (in drag/frozen mode the y-axis auto-fits, so manual
  // y-zoom stays disabled there too). touch-action:none on the element (set in
  // JSX) keeps the browser from treating the vertical drag as a scroll.
  //
  // Phone feel: drive the SAME glideYZoom path as desktop wheel — set
  // yHalfTargetRef + glideYZoom each move (no hard span writes / no nulling
  // the target mid-drag). Hard factor writes reminted halfRange through the
  // power-of-two lattice quantizer and caused visible Y steps; cancelling the
  // glide each move fought the smooth rAF walk. Divisor 480 (was 360) softens
  // coarse touch further. yCam.setDragActive keeps gesturing=true for the
  // whole finger so sparse touch moves cannot expire the 200ms window and
  // flicker pin/mode (bounce).
  useEffect(() => {
    const yEl = yAxisRef.current
    if (!yEl) return
    let drag = null  // { y, half }
    const Y_DRAG_DIV = 480
    const abortDrag = () => {
      // Stop any in-flight glide so mag / dbltap-reset own the scale.
      yHalfTargetRef.current = null
      drag = null
      yCam.setDragActive(false)
      yAxisZoomDragActiveRef.current = false
      endChartInteractRef.current()
    }
    const applyDragTarget = (clientY) => {
      if (!drag) return
      const dy = clientY - drag.y
      // Downward (positive dy) zooms out (larger span = more visible range),
      // matching wheel deltaY>0. Absolute from drag-start span + total dy.
      if (drag.half == null) {
        if (Math.abs(dy) < 2 || !ensureUserSpanRef.current()) return
        drag.half = yCam.userHalf()
        if (!(drag.half > 0)) return
      }
      const lim = yHalfLimitsRef.current()
      const next = Math.max(lim.lo, Math.min(lim.hi, drag.half * Math.exp(dy / Y_DRAG_DIV)))
      yWheelAnchorRef.current = null   // (yanchor) drag zooms about the centre
      yHalfTargetRef.current = next
      // Long mark as belt-and-suspenders; setDragActive is the primary keep-alive.
      markYScaleGesture(900)
      glideYZoom()
    }
    const onDown = (e) => {
      if (!(e.pointerType === 'touch' || (COARSE_POINTER && e.pointerType !== 'pen'))) return
      // A magnifier long-press is pending or active on this axis — that gesture
      // owns the finger; a zoom drag here would scale the chart out from under
      // the magnifier. Let the magnifier handlers have it.
      if (magPressRef.current || phoneMagActiveRef.current) return
      // A double-tap reset just fired on this axis. The second tap's finger is
      // still down and its pointerdown reaches here too — starting a zoom now
      // would snapshot the pre-reset zoom and hand it back on the first move.
      // 400ms comfortably covers the lift after a 300ms double-tap window.
      if (Date.now() - yAxisResetTapRef.current < 400) return
      // half: null until the finger actually moves — a tap must not arm the
      // user span (applyDragTarget arms it).
      drag = { y: e.clientY, half: null }
      yAxisZoomDragActiveRef.current = true
      yCam.setDragActive(true)
      markYScaleGesture(900)
      beginChartInteractRef.current()
      yEl.setPointerCapture?.(e.pointerId)
    }
    const onMove = (e) => {
      if (!drag) return
      // If magnifier engaged mid-drag (the hold timer fired), stop zooming.
      if (phoneMagActiveRef.current) {
        abortDrag()
        return
      }
      // A double-tap reset landed AFTER this drag armed. The onDown guard
      // can't catch this case: this listener is NATIVE on the axis element
      // and fires before React's delegated pointerdown, so on tap #2 the
      // drag arms with drag.zoom = the PRE-RESET zoom, and only then does
      // the React handler run the reset and stamp the ref. Overlay trace
      // showed exactly that: reset on axis-dbltap, then 50ms later the old
      // scale from axis-zoomdrag — this drag replaying its stale snapshot on the last
      // pixel of finger movement. Kill it here, where the drag is already
      // live and the stamp is finally visible.
      if (Date.now() - yAxisResetTapRef.current < 400) {
        abortDrag()
        return
      }
      applyDragTarget(e.clientY)
    }
    const onUp = (e) => {
      if (!drag) return
      // Final target from last finger Y, then hand off to glide (same path as
      // wheel). Clear drag-active after land is scheduled; glide ticks keep
      // markGesture alive, and endChartInteract no-ops while yGlideRaf walks.
      applyDragTarget(e.clientY)
      drag = null
      yCam.setDragActive(false)
      yAxisZoomDragActiveRef.current = false
      endChartInteractRef.current()
      try { yEl.releasePointerCapture(e.pointerId) } catch {}
    }
    yEl.addEventListener('pointerdown', onDown)
    yEl.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      yEl.removeEventListener('pointerdown', onDown)
      yEl.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      yCam.setDragActive(false)
    }
  }, [glideYZoom, markYScaleGesture, yCam])

  // --- Token subscription handler (called when selecting tokens from lists) ---
  const subscribeToToken = useCallback((address) => {
    if (!address) return
    // Switch back to Solana mode if in futures
    if (dataSourceRef.current === 'hyperliquid') {
      setHlPair(null)
      setDataSource('solana')
      data.tokenSupplyRef.current = 1000000000
      // Unsubscribe from futures streams
      const ws = data.wsRef?.current
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ action: 'hl_unsubscribe' }))
      }
    }
    data.handleSubscribe(address)
  }, [data])

  // Add/remove the currently-charted token to the watchlist. Extracted from
  // the old trades-footer +/★ button so the context menu (and any future
  // panel) can drive it. Optimistic insert + parallel v2/spark enrichment,
  // same as before; persistence handled by the existing localStorage effect.
  const isCurrentWatched = futWatchKey
    ? tokenWatchlist.some(t => t.address === futWatchKey)
    : (!!data.currentToken && tokenWatchlist.some(t => t.address === data.currentToken))
  const toggleWatchlist = useCallback(() => {
    // Futures: the star toggles the PAIR. address = `fut:<coin>` — a stable
    // pseudo-address so every existing address-keyed path (dedupe, removal,
    // persistence) works unchanged; fetchedAt/chartFetchedAt pre-stamped so
    // the sol enrichment effects skip these rows entirely.
    if (futWatchKey) {
      const coin = hlPair.base
      if (isCurrentWatched) {
        setTokenWatchlist(prev => prev.filter(t => t.address !== futWatchKey))
        showToast('Removed from watchlist', 'sell')
      } else if (tokenWatchlist.length >= 100) {
        showToast('Watchlist group full (100)', 'sell')
      } else {
        setTokenWatchlist(prev => [{ kind: 'fut', address: futWatchKey, coin, name: (cmeInfo(coin) || stkInfo(coin)) ? coin : `${coin}/USDC`, symbol: (cmeInfo(coin) || stkInfo(coin)) ? coin : `${coin}/USDC`, fetchedAt: Date.now(), chartFetchedAt: Date.now() }, ...prev])
        showToast('Added to watchlist', 'buy')
      }
      return
    }
    const addr = data.currentToken
    if (!addr) return
    const already = tokenWatchlist.some(t => t.address === addr)
    if (already) {
      setTokenWatchlist(prev => prev.filter(t => t.address !== addr))
      showToast('Removed from watchlist', 'sell')
    } else if (tokenWatchlist.length >= 100) {
      showToast('Watchlist group full (100)', 'sell')
    } else {
      const fallbackName = data.status.startsWith('token:') ? data.status.slice(6) : addr.slice(0, 6)
      setTokenWatchlist(prev => [{ address: addr, name: fallbackName, fetchedAt: 0 }, ...prev])
      showToast('Added to watchlist', 'buy')
      fetchTokenV2Data(addr).then(v2 => {
        if (!v2) return
        setTokenWatchlist(prev => prev.map(t =>
          t.address === addr
            ? { ...v2, address: addr, name: v2.symbol || v2.name || fallbackName, fetchedAt: Date.now() }
            : t
        ))
      }).catch(() => {})
    }
  }, [data.currentToken, data.status, tokenWatchlist, showToast, futWatchKey, hlPair, isCurrentWatched, setTokenWatchlist])

  // Record the current view into the unified recents — both modes, the
  // moment they render. Names: futures use PAIR/USDC; sol prefers the
  // resolved token name from data.status, falling back to the short mint.
  useEffect(() => {
    if (dataSource === 'hyperliquid' && hlPair?.base) {
      pushRecentView({ kind: 'fut', coin: hlPair.base, name: ((cmeInfo(hlPair.base) || stkInfo(hlPair.base)) ? hlPair.base : `${hlPair.base}/USDC`) })
    } else if (data.currentToken) {
      const nm = data.status?.startsWith('token:')
        ? data.status.slice(6)
        : `${data.currentToken.slice(0, 4)}…${data.currentToken.slice(-4)}`
      pushRecentView({ kind: 'sol', address: data.currentToken, name: nm })
    }
  }, [dataSource, hlPair?.base, data.currentToken, data.status])

  // ─── Price alerts ───
  // Server-backed: backend tracks the firehose of all decoded swaps and
  // fires cross detection regardless of which token is currently charted.
  // Alerts persist on disk (alerts.json) keyed by wallet pubkey.
  // Adding alerts requires a wallet to be set on the server first
  // (set_wallet WS action, sent by useChartData when publicKey resolves).
  const priceAlerts = usePriceAlerts({
    currentToken:  data.currentToken,
    currentWallet: dataSource === 'hyperliquid'
      ? (hlWallet.accountAddress || null)
      : (publicKey ? publicKey.toString() : null),
    wsRef:         data.wsRef,
    onSwitchToken: subscribeToToken,
    market:        dataSource === 'hyperliquid' ? 'hl' : 'sol',
    currentSymbol: dataSource === 'hyperliquid' ? hlPair?.base : null,
    status:        data.status,
  })
  // An alert belongs to an S/R line when it sits at the line's exact price
  // (the bell sets it at line.mcap). The server alert has no line id, so the
  // price is the link; moving the line re-arms the alert at the new price.
  srLineMovedRef.current = (oldMcap, newMcap) => {
    if (!Number.isFinite(oldMcap) || !Number.isFinite(newMcap)) return
    const tol = Math.abs(oldMcap) * 1e-9
    const cur = chartData.highlightMcap || data.lastBucketMcapRef?.current
    for (const a of priceAlerts.alertsForCurrentToken) {
      if (Math.abs(a.mcap - oldMcap) <= tol) priceAlerts.updateAlert(a, newMcap, cur)
    }
  }

  // Phone: when a touch long-press arms (crosshair mode), start driving the
  // crosshair from the touch position. The finger then scrubs it; lifting
  // without moving commits the alert (handled in the hook), moving cancels it.
  // (phone-xhair) Phone long-press OPENS MENUS now (see the chart touch
  // long-press below); crosshair mode is the header button. So the alert
  // hook's touch path is neutralised: its arm is a no-op and every touch
  // press counts as an "entry" press (suppressCommit), so lifting never sets
  // an alert. Phone alerts: long-press -> '🔔 Set Alert Here' in the menu.
  // Desktop never takes the hook's touch path, so it is unchanged.
  useEffect(() => {
    if (!priceAlerts.setOnLongPressArm) return
    priceAlerts.setIsCrosshairActive?.(() => false)
    priceAlerts.setCommitMcapResolver?.(() => null)
    priceAlerts.setOnLongPressArm(() => {})
  }, [priceAlerts])

  // Mirror triggered price alerts into the messages log. The hook surfaces
  // them as transient notifications keyed by id; we record each one once.
  const seenAlertNotifsRef = useRef(new Set())
  useEffect(() => {
    const notifs = priceAlerts.notifications
    if (!notifs || notifs.length === 0) return
    const seen = seenAlertNotifsRef.current
    for (const n of notifs) {
      if (seen.has(n.id)) continue
      seen.add(n.id)
      if (seen.size > 500) seen.clear()
      const arrow = n.direction === 'above' || n.direction === 'up' ? '▲' : '▼'
      // Prefer a real remembered name (keyed by mint) over the hook's
      // resolved symbol, which can degrade to a mint-slice at trigger time.
      const name = resolveTokenName(n.mint, n.symbol)
      // Pass mint+name so the log row renders the token as a clickable link
      // (switches charts to it). Solana mints only; HL alerts have no mint.
      addMessage(`🔔 ${arrow} crossed ${formatMcap(n.threshold)}`, 'alert', { mint: n.mint, name })
    }
  }, [priceAlerts.notifications, addMessage, resolveTokenName])

  // --- Alert tag vertical drag (move an alert's price) ---
  // Pointer-down on a tag starts a drag; moving updates a live preview mcap;
  // pointer-up commits via priceAlerts.updateAlert (remove old + add new at
  // the dragged price). The X button cancels/removes instead. Triangles on
  // the tag blink only while dragging.
  const [alertDrag, setAlertDrag] = useState(null) // { id, mcap } | null
  const alertDragRef = useRef(null)
  const beginAlertDrag = useStableCallback((alert, e) => {
    e.preventDefault(); e.stopPropagation()
    const startInfo = { alert, id: alert.id, mcap: alert.mcap, pixelY: null, moved: false }
    alertDragRef.current = startInfo
    setAlertDrag({ id: alert.id, mcap: alert.mcap, pixelY: null })
    const onMove = (ev) => {
      const d = alertDragRef.current
      if (!d) return
      const canvas = canvasRef.current
      const mcaps = visibleSortedMcapsRef.current
      const RH = dynamicRowHeightRef.current
      if (!canvas || !(mcaps?.length >= 2) || !(RH > 0)) return
      const rect = canvas.getBoundingClientRect()
      // Raw pixel Y in the canvas coordinate space (same base the alert-line
      // layer uses for `top`). Used to make the tag GLIDE with the cursor
      // while dragging, instead of snapping row-to-row.
      let py = ev.clientY - rect.top
      const maxY = (mcaps.length) * RH
      if (py < 0) py = 0
      else if (py > maxY) py = maxY
      let m = yToMcapUtil(ev.clientY - rect.top, mcaps, RH)
      const _mTop = yToMcapUtil(0, mcaps, RH)   // top edge of row 0
      if (m > _mTop) m = _mTop
      else if (m < mcaps[mcaps.length - 1]) m = mcaps[mcaps.length - 1]
      m = Math.round(m)
      d.mcap = m; d.pixelY = py; d.moved = true
      setAlertDrag({ id: d.id, mcap: m, pixelY: py })
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      const d = alertDragRef.current
      alertDragRef.current = null
      setAlertDrag(null)
      // Commit only if actually moved to a new price.
      if (d && d.moved && d.mcap !== d.alert.mcap) {
        priceAlerts.updateAlert(d.alert, d.mcap, chartData.highlightMcap)
      }
    }
    window.addEventListener('pointermove', onMove, { passive: true })
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
  })

  // --- Footer drag to scale x-axis ---

  // Live-edge "handle" for the smoketrail: the (x,y) of the flat EKG segment
  // at the forming candle / current price. Hovering near it glows the trail
  // and right-clicking it opens the flow menu (AVWAP-line pattern). Replicates
  // the trail's own getOriginX so the hit-test matches what's drawn.
  const getTrailHandle = useStableCallback(() => {
    if (!showOrderflowTrail) return null
    const edge = containerWidthRef.current - RIGHT_PADDING - FIXED_OVERLAY_WIDTH
    const f = lastFrameRef.current
    if (!f) return null
    // What the chart painted — not a rebuild from live refs.
    const y = f.yOf(f.livePrice) + f.pinPx
    if (!Number.isFinite(y)) return null
    return { x: f.xMid(f.total - 1), y }
  })


  // Cache wrapper/body/canvas rects across pointermoves — three gBCRs per
  // move were Recalculate+Layout in the hover path (~350ms combined in profiles).
  // Invalidate on resize via containerWidth / when transform pin changes.
  const hoverLayoutCacheRef = useRef({ canvas: null, body: null, wrapper: null, at: 0, pinPx: null, cw: 0 })
  // Moving-average line hit test against the polyline as last painted
  // (maPointsRef, canvas px) — the same canvas-rect convention as the AVWAP
  // hover and the ATR right-click. Whole-line, like AVWAP.
  const maHitAt = useStableCallback((clientX, clientY) => {
    const canvas = canvasRef.current
    const list = maPointsRef.current
    if (!canvas || !list || !list.length) return null
    const r = canvas.getBoundingClientRect()
    const mx = clientX - r.left, my = clientY - r.top
    let best = null
    let bd = isPhone ? 14 : 6
    for (const { id, points } of list) {
      for (let i = 1; i < points.length; i++) {
        const a = points[i - 1], b = points[i]
        if (Math.max(a.x, b.x) < mx - bd || Math.min(a.x, b.x) > mx + bd) continue
        const d = distToSegment(mx, my, a.x, a.y, b.x, b.y)
        if (d < bd) { bd = d; best = id }
      }
    }
    return best
  })
  // Hover: ref + one scheduled draw on change, coalesced to a frame — no
  // React state (same contract as the AVWAP hover).
  const queueMaHover = (e) => {
    maHoverPtRef.current = { clientX: e.clientX, clientY: e.clientY }
    if (maHoverRafRef.current) return
    if (!maPointsRef.current.length && maHoverIdRef.current == null) return
    maHoverRafRef.current = requestAnimationFrame(() => {
      maHoverRafRef.current = 0
      const p = maHoverPtRef.current
      const id = p && !interaction.draggingItemRef.current ? maHitAt(p.clientX, p.clientY) : null
      if (id !== maHoverIdRef.current) {
        maHoverIdRef.current = id
        scheduleCanvasDrawRef.current?.('full')
      }
    })
  }
  useEffect(() => () => { if (maHoverRafRef.current) cancelAnimationFrame(maHoverRafRef.current) }, [])
  const applyBodyHover = useStableCallback((e) => {
    // GHOST-MOUSE GUARD. Touchscreens dispatch synthetic mouse events ~300ms
    // after a tap, AT THE TAP POINT — and this is the desktop hover path, which
    // places the crosshair absolutely on any mousemove. That was the "crosshair
    // jumps to my finger when I tap" bug: the touch code went fully relative,
    // then the browser's fake mousemove re-placed it absolutely — including
    // right after the exit tap, which made exiting look broken too. Any mouse
    // event within 700ms of touch activity is a ghost, unless it's our own
    // tagged synthetic scrub event. Desktop never stamps the ref → guard inert.
    //
    // Still correct under coalescing: the guard reads lastTouchTsRef at APPLY
    // time, one frame later than the event, so a ghost is if anything more
    // clearly inside the 700ms window, never less.
    if (!e._touchScrub && Date.now() - lastTouchTsRef.current < 700) return
    // During overlay drag, skip hover detection (window listeners handle it)
    if (interaction.draggingItemRef.current) return
    interaction.handleAvwapHover(e)
    queueMaHover(e)
    const canvas = canvasRef.current
    if (!canvas) return
    const _hlc = hoverLayoutCacheRef.current
    const _pin = lastStyleRef.current?.pinPx
    const _cw = containerWidthRef.current || 0
    // pinPx IS CORRECTED, NOT RE-MEASURED.
    // This cache used to invalidate on `_hlc.pinPx !== _pin` — and pinPx is
    // the canvas transform, the one value that changes on EVERY frame of a
    // scroll or pan. So the cache missed every frame of the gesture it exists
    // for, and each miss cost THREE forced getBoundingClientRect calls. A
    // profile put Recalculate style at 82–247ms and Layout at 78–100ms on
    // exactly this line, with getBoundingClientRect at 112ms total.
    //
    // But pinPx is a translateY of a KNOWN number. The measured rect does not
    // have to be re-read to account for it — it has to be OFFSET by it. So
    // cache at whatever pin the measurement was taken at, and add the delta
    // for every frame after. Same answer, no layout.
    //
    // Only top moves: a vertical translate cannot change left/width/height,
    // and body/wrapper are not transformed at all, so neither of those ever
    // needed pinPx in the key.
    //
    // The 250ms staleness sweep stays — it covers everything a transform
    // delta cannot describe (page scroll, panel reflow, zoom).
    const _stale = !_hlc.canvas || _hlc.cw !== _cw
      || (performance.now() - _hlc.at > 250)
    if (_stale) {
      const cr = canvas.getBoundingClientRect()
      // Store plain numbers, not the live DOMRect — the corrected copy below
      // has to be a fresh object each frame or a consumer holding `rect`
      // would see a top that mutates under it.
      _hlc.canvas = { left: cr.left, top: cr.top, width: cr.width, height: cr.height }
      const bodyEl = bodyScrollRef.current
      _hlc.body = bodyEl ? bodyEl.getBoundingClientRect() : null
      const wrapEl = bodyEl?.closest?.('.footprint-body-wrapper') || wrapperRef.current
      _hlc.wrapper = wrapEl ? wrapEl.getBoundingClientRect() : null
      _hlc.at = performance.now()
      _hlc.pinPx = _pin
      _hlc.cw = _cw
    }
    const _pinDelta = (Number.isFinite(_pin) && Number.isFinite(_hlc.pinPx))
      ? (_pin - _hlc.pinPx) : 0
    const rect = _pinDelta === 0
      ? _hlc.canvas
      : { left: _hlc.canvas.left, top: _hlc.canvas.top + _pinDelta,
          width: _hlc.canvas.width, height: _hlc.canvas.height }
    const mouseX = e.clientX - rect.left

    // Only show line on the main chart area (exclude the new 160px fixed overlay)
    const mainPlotEnd = containerWidthRef.current - RIGHT_PADDING - FIXED_OVERLAY_WIDTH

    // Single consolidated "cursor is off the chart" check. Three indicators
    // follow the cursor on the chart body: the dashed vertical crosshair,
    // the cursor-mcap price label, and the horizontal highlight bar — all
    // imperative DOM updates. Previously each indicator had its own early
    // return: the mouseX-vs-mainPlotEnd check (above) hid only the
    // crosshair, the e.target-vs-y-axis check (below) hid only the label
    // and h-highlight. Whichever exit path the cursor happened to trip
    // first left the OTHER indicators stuck on screen — symptom: when the
    // cursor crossed from the chart onto the y-axis (or onto the
    // y-axis-magnifier overlay, which has pointer-events:none and so
    // delegates the event to whatever's underneath), one indicator
    // would visibly stick. Combining both conditions into a single exit
    // hides everything together, every time.
    const isOverYAxis = e.target.closest('.y-axis-body') || e.target.closest('.y-axis-region')
    const outOfChart  = mouseX < 0 || mouseX > mainPlotEnd || isOverYAxis
    if (outOfChart) {
      if (mouseXRef.current !== -1) {
        mouseXRef.current = -1
        updateCrosshair(-1)
      }
      if (cursorLabelRef.current) cursorLabelRef.current.style.display = 'none'; placeXCrossLabels(-1)
      if (hHighlightRef.current)  hHighlightRef.current.style.display = 'none'
      // Drop any VA-line hover when the cursor leaves the plot (y-axis included)
      // so the highlight doesn't stick.
      if (hoveredVaLineRef.current) {
        hoveredVaLineRef.current = null
        if (drawCanvasRef.current) drawCanvasRef.current()
      }
      return
    }

    mouseXRef.current = mouseX
    updateCrosshair(mouseX)

    // ── Trendlines: cursor tracking + hover ──
    // Canvas-space cursor feeds the rubber band (draw converts it to data
    // space) and the hover hit-test below.
    {
      const my = e.clientY - rect.top
      if (trend.handleMove(mouseX, my) && drawCanvasRef.current) drawCanvasRef.current()
    }

    // Value Area line hover — recompute each enabled line's y the same way
    // the S/R hover does (shared mcapToYUtil → canvas-rect space) and pick the
    // closest within a thin band. Yields to AVWAP / S-R so overlapping lines
    // don't double-highlight. hoveredVaLineRef drives the draw highlight + the
    // click-to-open in onPointerDown; the cursor flips to a pointer over a hit.
    {
      let vaHit = null
      if (showValueAreaRef.current && valueAreaRef.current
          && interaction.hoveredAvwapIdRef.current == null
          && interaction.hoveredSRLineIdRef.current == null) {
        const va = valueAreaRef.current
        const vc = vaConfigRef.current
        const mcaps = visibleSortedMcapsRef.current
        const RH = dynamicRowHeightRef.current
        if (mcaps.length > 1 && RH > 0) {
          const myc = e.clientY - rect.top
          const cands = []
          if (vc.showPOC)    cands.push(['poc', va.poc])
          if (vc.showBounds) { cands.push(['vah', va.vah], ['val', va.val]) }
          let best = 7
          for (const [key, m] of cands) {
            const ly = priceToY(m)
            if (Number.isFinite(ly) && Math.abs(myc - ly) < best) { best = Math.abs(myc - ly); vaHit = key }
          }
        }
      }
      if (vaHit !== hoveredVaLineRef.current) {
        hoveredVaLineRef.current = vaHit
        if (drawCanvasRef.current) drawCanvasRef.current()
      }
    }

    // Optional: still update column for other logic if you need it
    const pxPerCandle = mainPlotEnd / Math.max(1, viewWindowSizeRef.current)
    const col = Math.floor(mouseX / pxPerCandle)
    if (col !== hoverColRef.current) {
      hoverColRef.current = col
    }

    // Broadcast the hovered candle's startTime so the sibling chart can
    // mirror the vertical line at the SAME point in time (its own x).
    // Projects mouseX → candle index with the exact inverse of the canvas
    // x formula (x = plotWidth - (idxFromRight+1)*pxPerCandle + frac).
    {
      const candleArr = chartDataRef.current?.candles
      if (candleArr && candleArr.length) {
        const winSize = viewWindowSizeRef.current
        const total = candleArr.length
        const endIdx = resolveEndIdx(viewEndIndexRef.current, total, !(xWin?.wheelParkRef?.current))
        // (xhair-sync) floor, not round(… - 1): candle k (from the right)
        // covers (plotEnd + frac - x) / ppc in [k, k+1). round(v - 1) picked
        // the right-hand neighbour for half of every column, so the broadcast
        // time flipped between two candles as the mouse crossed one.
        const idxFromRight = Math.floor(
          (mainPlotEnd + fractionalOffsetPxRef.current - mouseX) / pxPerCandle
        )
        const idx = endIdx - idxFromRight
        const c = candleArr[idx]
        if (c && c.startTime != null) {
          crosshairBus.emit({ from: chartInstanceId, startTime: c.startTime, ivSec: timeIntervalRef?.current || timeInterval || 60 })
        } else {
          // Future pad / before the first candle: nothing to mirror.
          crosshairBus.emit({ from: chartInstanceId, startTime: null })
        }
      }
    }

    // Cursor mcap label — DOM-based for 60fps. Reuses `canvas` and `rect`
    // from the crosshair block above (rect IS canvas.getBoundingClientRect()).
    const label = cursorLabelRef.current
    const body = bodyScrollRef.current
    if (label && body && canvas) {
      // Canvas rect (`rect`) is position:absolute top:0 inside body-scroll
      // AND carries the translateY(-yResidualPxRef)
      // transform. So `e.clientY - rect.top` in screen coords already
      // reflects body.scrollTop (absolute children scroll with their
      // overflow parent) AND the live transform — giving the canvas-
      // internal Y where the mouse points, exactly matching the pixel
      // grid the cells were drawn on. The earlier code used body.rect +
      // body.scrollTop only, which left the cursor mcap off by
      // yResidualPxRef (static, up to one row).
      const bodyRect = _hlc.body || body.getBoundingClientRect()
      const relY = e.clientY - rect.top
      const mcaps = visibleSortedMcapsRef.current
      const RH = dynamicRowHeightRef.current
      if (mcaps.length >= 2 && RH > 0) {
        let mcap = yToMcapUtil(relY, mcaps, RH)
        // Clamp to the lattice: top edge of row 0 .. bottom row's key
        // (row-edge mapping — y in [0, n*RH] is exactly that price span).
        const _mTop = yToMcapUtil(0, mcaps, RH)
        if (mcap > _mTop) mcap = _mTop
        else if (mcap < mcaps[mcaps.length - 1]) mcap = mcaps[mcaps.length - 1]
        crosshairMcapRef.current = mcap   // the line's price — alert commits read this
        // ── ALL LAYOUT READS FIRST, THEN ALL WRITES ──
        // This block used to interleave them: write textContent, read
        // getBoundingClientRect, write style.top, read offsetWidth, write
        // five more styles, read offsetWidth AGAIN. Every read after a write
        // forces a synchronous layout of the whole document, so ONE
        // pointermove cost THREE full reflows — at pointer rate, on a page
        // whose DOM carries the y-axis rows and ~400 volume-bar divs.
        // Profiled at 287.6ms of self time in Layout attributed to this
        // function, with pointerover totalling 297.9ms.
        //
        // Nothing below depends on a value written below, so hoisting the
        // reads is behaviour-preserving: same geometry, one layout earlier.
        //
        // KEEP THIS ORDERING. Any read added below the writes silently
        // restores the reflow, and it will not show in the code — only in
        // a profile.
        //
        // SECOND PASS: hoisting cut three reflows to one, but a later profile
        // still put 449ms of Layout self time here (plus 272ms in
        // getBoundingClientRect). Hoisting only moved the reflow; it did not
        // remove the reason for one. The writes below were `top`, `left`,
        // `width` and `height` — LAYOUT properties — so every pointermove
        // re-dirtied layout and guaranteed the next move's reads would reflow
        // again. Two changes fix the cause rather than its ordering:
        //   1. y now rides `transform`, which is compositor-only and dirties
        //      nothing. `top` is pinned to 0 once so the layout box stops
        //      moving at all.
        //   2. left/width/height/display change with LAYOUT — a resize, the
        //      axis column growing a digit, a bucket change — never with the
        //      mouse. They are compared against the element's own inline
        //      style before writing. Reading `el.style.x` parses the inline
        //      declaration and does NOT touch layout, so the guard is free,
        //      and unlike a mirrored ref it cannot desync from the several
        //      places that set display:none on these elements.
        const wrapperRect = _hlc.wrapper
        const yAxis = yAxisRef.current
        // axisWRef is maintained by the y-axis resize effect and is already
        // the source for the three other consumers of this width. offsetWidth
        // is a LAYOUT read and was the third one in this handler; it now runs
        // only before that effect has measured once, so steady-state costs
        // nothing. Fallback chain is unchanged, so the pre-measure value is
        // the same 0 it always was.
        const axisW = axisWRef.current > 0 ? axisWRef.current : (yAxis ? yAxis.offsetWidth : 0)
        // Crosshair follow label shows the FULL value — same source mcap the
        // alert is set to (both from yToMcap on e.clientY - rect.top), just
        // not run through the compact M/K/B axis formatter. Solana: full
        // integer mcap with separators; Hyperliquid: full price.
        // Write into an inner span (created once, then reused) rather than the
        // label itself. The outer label spans the full y-axis width for
        // positioning/centering and stays transparent; the span carries the
        // solid background + border so it hugs the TEXT only, not the whole
        // y-axis column.
        let textEl = label.firstElementChild
        if (!textEl || textEl.className !== 'price-label-text') {
          label.textContent = ''
          textEl = document.createElement('span')
          textEl.className = 'price-label-text'
          label.appendChild(textEl)
        }
        // (scrollperf) Write only on change: textContent replaces the text node
        // (style + layout for the label) and .title is a setAttribute — both
        // ran every hover frame even when the value had not changed.
        const _lblTxt = formatYAxis(mcap)   // same width behavior as the scale
        if (textEl.textContent !== _lblTxt) textEl.textContent = _lblTxt
        const _lblTitle = dataSource === 'hyperliquid' ? String(mcap) : Math.round(mcap).toLocaleString('en-US')
        if (textEl.title !== _lblTitle) textEl.title = _lblTitle
        // Position relative to .footprint-body-wrapper (has position:relative).
        // wrapperRect / yAxis / axisW were all read ABOVE, before this frame's
        // first write — do not re-read them here.
        if (wrapperRect) {
          const wy = e.clientY - wrapperRect.top
          // The -50% centering used to sit in the JSX inline transform. It is
          // composed here instead so the two translateYs stack to exactly the
          // old geometry; the JSX value is overwritten on the first move.
          const tf = `translateY(${wy}px) translateY(-50%)`
          label.style.transform = tf
          if (label.style.top !== '0px') label.style.top = '0px'
          // Horizontal highlight — same Y as the price label, but spans the
          // chart body width (not the y-axis). One row tall, positioned in
          // the same wrapper so it stays put relative to the mouse rather
          // than scrolling with chart content.
          const hl = hHighlightRef.current
          if (hl) {
            // Strip ENDS at the gutter — it does not continue behind the tag.
            // bodyRect is .footprint-body-scroll, which CONTAINS .y-axis-region
            // as a flex child (wrapper is direction:rtl, so the axis sits
            // physically right), meaning its width already includes the axis
            // column. Subtract the LIVE axis width — the column grows with
            // mcap digits, so the constant would drift.
            hl.style.transform = tf
            if (hl.style.top !== '0px') hl.style.top = '0px'
            const hlLeft = `${bodyRect.left - wrapperRect.left}px`
            if (hl.style.left !== hlLeft) hl.style.left = hlLeft
            const hlWidth = `${Math.max(0, bodyRect.width - axisW)}px`
            if (hl.style.width !== hlWidth) hl.style.width = hlWidth
            const hlHeight = '0px'
            if (hl.style.height !== hlHeight) hl.style.height = hlHeight
            if (hl.style.display !== 'block') hl.style.display = 'block'
          }
        }
        // Match y-axis width
        if (yAxis) {
          const labelWidth = `${axisW}px`
          if (label.style.width !== labelWidth) label.style.width = labelWidth
        }
        if (label.style.display !== 'flex') label.style.display = 'flex'
      }
    }

    // ── Smoketrail handle hover (AVWAP-style glow) ──
    // The handle is the flat live-edge segment at current price. Glow when the
    // cursor is within a small box of it. Uses the same canvas-internal coords
    // as the cursor label (e.clientY - rect.top reflects scroll + transform).
    {
      const handle = getTrailHandle()
      let over = false
      if (handle) {
        const cy = e.clientY - rect.top
        // x near the live edge (generous, the segment is short), y within ~1 row.
        const RHh = dynamicRowHeightRef.current
        over = Math.abs(mouseX - handle.x) <= 28 && Math.abs(cy - handle.y) <= Math.max(8, RHh * 0.6)
      }
      if (over !== trailHoveredRef.current) setTrailHovered(over)
    }
  })

  // ─── Hover: rAF-coalesced ───
  // Bound with onMouseMove, and mousemove is NOT rAF-aligned the way
  // pointermove is — it fires at the DEVICE rate, 125Hz to 1000Hz on a
  // gaming mouse. Every one of those ran the whole body, and its first act
  // is canvas.getBoundingClientRect(). A profile put 171.7ms of Recalculate
  // style — 4.3% of the trace — on that single read.
  //
  // The read itself is not the bug; the frequency is. Nothing it measures
  // (canvas transform, container size, page scroll) can change faster than
  // a frame, so re-reading it per EVENT buys nothing. The pan path and the
  // AVWAP hover in useChartInteraction were both coalesced for exactly this
  // reason — see the pan's note — and this one never was.
  //
  // Coalescing, not caching: the rect is still read fresh every time it is
  // used, so there is no staleness window and no invalidation surface to get
  // wrong. It is read once per frame instead of up to sixteen times.
  //
  // Four fields are copied rather than the event held, so this does not
  // depend on React's pooling behaviour. The body calls no preventDefault or
  // stopPropagation — verified — so deferring it changes nothing observable.
  const handleBodyMouseMove = useStableCallback((e) => {
    hoverEvRef.current = {
      clientX: e.clientX,
      clientY: e.clientY,
      target: e.target,
      _touchScrub: e._touchScrub,
    }
    if (hoverRafRef.current) return
    hoverRafRef.current = requestFrame(() => {
      hoverRafRef.current = 0
      const ev = hoverEvRef.current
      if (ev) applyBodyHover(ev)
    }, FRAME_INPUT)
  })

  const handleBodyMouseLeave = useStableCallback(() => {
    // DROP THE PENDING HOVER FRAME FIRST. Hover is rAF-coalesced, so a move
    // that arrived in this frame is still queued when leave fires. Left
    // alone it would run after everything below and re-show the label and
    // highlight at the last inside-the-chart position — cursor gone, ghost
    // crosshair stuck until the next entry. Clearing the payload as well as
    // cancelling means a frame already dispatched finds nothing to apply.
    if (hoverRafRef.current) {
      cancelFrame(hoverRafRef.current)
      hoverRafRef.current = 0
    }
    hoverEvRef.current = null
    crosshairBus.emit({ from: chartInstanceId, startTime: null })
    // hoverColRef is tracked but not read by drawCanvas — no redraw needed
    if (hoverColRef.current !== -1) hoverColRef.current = -1
    if (mouseXRef.current !== -1) {
      mouseXRef.current = -1
      updateCrosshair(-1)
    }
    if (cursorLabelRef.current) cursorLabelRef.current.style.display = 'none'; placeXCrossLabels(-1)
    if (hHighlightRef.current) hHighlightRef.current.style.display = 'none'
    if (trailHoveredRef.current) setTrailHovered(false)
  })

  const [isFooterDragging, setIsFooterDragging] = useState(false)
  // Footer double-click/double-tap → reset viewport (moved OFF the chart
  // surface — the chart's own dblclick handler is gone). Detected manually
  // from pointerdown pairs instead of onDoubleClick: preventDefault on
  // pointerdown (needed for the drag) can suppress the browser's synthetic
  // dblclick, and manual detection also gives phones double-tap for free.
  const footerLastTapRef = useRef({ t: 0, x: 0 })
  const handleFooterPointerDown = useStableCallback((e) => {
    e.preventDefault()
    const now = Date.now()
    const last = footerLastTapRef.current
    if (now - last.t < 300 && Math.abs(e.clientX - last.x) < 24) {
      footerLastTapRef.current = { t: 0, x: 0 }
      footerDragRef.current = null
      // A coalesced commit from the drag that ENDED with tap #1 may still be
      // queued — let it land now and it overwrites this reset one frame later.
      cancelFrame(footerRafRef.current)
      footerRafRef.current = 0
      // Same stale-walker hazard as the axis double-tap, on BOTH axes: a
      // wheel flick's x-window glide or a pinch's y-zoom glide still walking
      // would overwrite this reset a frame later. Clear the targets, not just
      // the animation frames — a cleared frame with a live target re-arms on
      // the next glide call.
      yHalfTargetRef.current = null
      if (yGlideRafRef.current) { cancelFrame(yGlideRafRef.current); yGlideRafRef.current = 0 }
      zoomTargetWinRef.current = null
      zoomGlideRef.current = null
      glideHalfRef.current = null
      glideCenterRef.current = null
      yDragFitDestRef.current = null
      yCam.reset()
      if (zoomGlideRafRef.current) { cancelFrame(zoomGlideRafRef.current); zoomGlideRafRef.current = 0 }
      if (interactWheelTimerRef.current) { clearTimeout(interactWheelTimerRef.current); interactWheelTimerRef.current = 0 }
      endChartInteractRef.current()
      // Follow damper too — it went down with yCam.reset() above.
      setIsFooterDragging(false)
      setAutoScrollEnabled(true)
      setYFitTick((t) => t + 1)   // yCam.reset() above: default Y fit
      // ── RESET POSITION, NOT JUST SCALE ────────────────────────────────
      // setViewWindowSize says HOW MANY columns; nothing here said WHERE.
      // viewEndIndex and the sub-pixel remainder kept whatever the last pan
      // left them at, so a double-click landed on a different candle
      // depending on where you had scrolled to. -1 is the follow sentinel —
      // the same value the token-switch reset uses — and the fractional
      // offset has to go with it or the window sits a fraction of a column
      // off the live edge.
      //
      // Refs as well as state: the draw reads the refs, and it can run
      // before React commits.
      setViewEndIndex(-1)
      viewEndIndexRef.current = -1
      fractionalOffsetPxRef.current = 0
      clearXView(chartId)             // (xzoom) forget the stored zoom + live gap
      fitFootprintXRef.current(true)  // width-derived footprint columns
      setForceBucketRecompute(c => c + 1)
      return
    }
    footerLastTapRef.current = { t: now, x: e.clientX }
    // (wheel-drag) stop a running wheel glide first (commits its bucket), so
    // the footer drag starts from the painted window, not the glide target.
    freezeXGlideRef.current?.(e.clientX)
    footerDragRef.current = { x: e.clientX, windowSize: viewWindowSizeRef.current }
    footerPendingWRef.current = viewWindowSizeRef.current   // direction baseline
    footerDirRef.current = 0
    setIsFooterDragging(true)
  })
  // rAF-coalesced: pointermove can outpace the display (>60Hz on phones), and
  // every viewWindowSize commit re-slices visibleCandles, re-scans the grid,
  // re-runs the bucket sizer, and regroups the footprint — per EVENT that was
  // the footer-drag jank/freeze on phones. Latest value wins, one commit per
  // frame.
  const footerRafRef = useRef(0)
  const footerPendingWRef = useRef(0)
  const footerDirRef = useRef(0)         // +1 zooming out, -1 zooming in
  const handleFooterPointerMove = useStableCallback((e) => {
    if (!footerDragRef.current) return
    const dx = e.clientX - footerDragRef.current.x
    const sensitivity = 0.5
    // Continuous (no rounding): the X camera draws fractional windows, so
    // the zoom tracks the pointer instead of jumping a whole candle at a time.
    const delta = dx * sensitivity
    const nextW = Math.max(MIN_VIEW_WINDOW, Math.min(MAX_VIEW_WINDOW_X, footerDragRef.current.windowSize + delta))
    // Remember which way the zoom was last moving — release lands in THAT
    // direction (see pointer-up), never backwards.
    if (nextW !== footerPendingWRef.current) footerDirRef.current = nextW > footerPendingWRef.current ? 1 : -1
    footerPendingWRef.current = nextW
    if (footerRafRef.current) return
    footerRafRef.current = requestFrame(() => {
      footerRafRef.current = 0
      const w = footerPendingWRef.current
        // Reuse the wheel's shared entry: footer targets remain integer/fine
      // grained, while the displayed window eases between those targets.
      // Short tau: follow the pointer nearly 1:1 (was the wheel's ease, which
      // trailed the finger — the footer felt out of sync with the hand).
      setXWindowRef.current(w, { float: true, tau: 12, land: 0.01 })
    }, FRAME_INPUT)
  })
  useEffect(() => () => cancelFrame(footerRafRef.current), [])
  const handleFooterPointerUp = useStableCallback(() => {
    footerDragRef.current = null
    setIsFooterDragging(false)
    // Land on whole candles (the glide was held open on a float target) —
    // the NEXT whole candle in the direction of travel, not the nearest:
    // nearest could be behind the hand, and easing back to it read as a
    // second X move ("double call"). This only ever coasts forward.
    const t = zoomTargetWinRef.current
    if (t != null && setXWindowRef.current) {
      const dir = footerDirRef.current
      const land = dir > 0 ? Math.ceil(t - 1e-6) : dir < 0 ? Math.floor(t + 1e-6) : Math.round(t)
      setXWindowRef.current(land, { tau: 40 })
    }
    footerDirRef.current = 0
  })
  useEffect(() => {
    if (!isFooterDragging) return
    window.addEventListener('pointermove', handleFooterPointerMove)
    window.addEventListener('pointerup', handleFooterPointerUp)
    window.addEventListener('pointercancel', handleFooterPointerUp)
    return () => {
      window.removeEventListener('pointermove', handleFooterPointerMove)
      window.removeEventListener('pointerup', handleFooterPointerUp)
      window.removeEventListener('pointercancel', handleFooterPointerUp)
    }
  }, [isFooterDragging, handleFooterPointerMove, handleFooterPointerUp])

  // ============================================
  // JSX
  // ============================================

  return (
    <div className="footprint-container">
      {/* Bottom scan ticker bar styles */}
      <style>{`
        /* ─── Messages popup (alerts / DEX / orders) ─── */
        .footprint-container { position: relative; }
        /* Messages/chat popup styles moved to styles.css (popup now app-level) */
        .chart-mode-toggle {
          position: absolute;
          top: 10px;
          left: 80px;
          display: flex;
          gap: 1px;
          background: rgba(15, 15, 25, 0.85);
          border: 1px solid #2a2a35;
          padding: 2px;
          z-index: 10;
        }
        .chart-mode-btn {
          background: none;
          border: none;
          color: #666;
          width: 24px;
          height: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          border-radius: 3px;
          transition: all 0.1s;
          padding: 0;
        }
        .chart-mode-btn:hover { color: #ccc; background: #1d1d24; }
        .chart-mode-btn.active {
          color: #ffb300;
          background: #1f1f28;
        }
        /* Flat density square (F/N/C). The 3D keycap below is parked: its
           .chart-density-key / -face rules and /density-keycap.png are kept
           for reuse elsewhere, just not rendered on the rail. */
        /* Text state toggles (density, LOG/LIN): flat, thin sharp outline. */
        .chart-rail .chart-mode-btn.chart-text-toggle {
          box-sizing: border-box;
          height: 20px;
          padding: 0 3px;
          border: 1px solid #3a3a44;
          border-radius: 2px;
          background: none;
          font-size: 11px;
          font-weight: 700;
          font-family: monospace;
          line-height: 1;
        }
        .chart-rail .chart-mode-btn.chart-text-toggle:hover { border-color: #5a5a66; background: #1d1d24; }
        .chart-rail .chart-mode-btn.chart-density-flat { width: 20px; padding: 0; }
        .chart-rail .chart-mode-btn.chart-density-key {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          padding: 0;
          margin: 0 1px 3px;
          box-sizing: border-box;
          color: #ffb300 !important;
          /* Exact key plate from user's reference (A removed) — shadows/highlights baked in */
          background:
            url('/density-keycap.png') center / 96% 96% no-repeat !important;
          border: none;
          border-radius: 3px; /* less round than prior 6px chiclet */
          box-shadow:
            2px 3px 0 #08080c,
            2px 4px 5px rgba(0,0,0,0.5);
          transition: transform 0.07s ease, box-shadow 0.07s ease;
          overflow: hidden;
        }

        .chart-rail .chart-mode-btn.chart-density-key .chart-density-key-face {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          border-radius: 2px;
          font-size: 10px;
          font-weight: 700;
          font-family: monospace;
          line-height: 1;
          background: transparent;
          box-shadow: none;
          text-shadow:
            0 1px 1px rgba(0,0,0,0.9),
            0 0 2px rgba(0,0,0,0.6);
          color: #ffb300;
          /* sit the letter on the recessed dish of the plate */
          transform: translateY(-0.5px);
        }

        .chart-rail .chart-mode-btn.chart-density-key:hover {
          filter: brightness(1.06);
          background:
            url('/density-keycap.png') center / 96% 96% no-repeat !important;
        }
        .chart-rail .chart-mode-btn.chart-density-key:hover .chart-density-key-face {
          background: transparent;
        }

        .chart-rail .chart-mode-btn.chart-density-key:active,
        .chart-rail .chart-mode-btn.chart-density-key:active:hover {
          transform: translateY(2px);
          filter: brightness(0.92);
          box-shadow: 2px 1px 0 #08080c;
        }
        .chart-rail .chart-mode-btn.chart-density-key:active .chart-density-key-face {
          background: transparent;
          box-shadow: none;
          transform: translateY(0);
        }        .scan-ticker-bar {
          position: fixed;
          bottom: 25px;   /* sit directly above the 25px .app-footer instead of
                             overlapping it — both were pinned to the screen
                             bottom (ticker fixed bottom:0 z50 over footer z30). */
          left: 0;
          right: 0;
          overflow: hidden;
          background: #0a0a0f;
          border-top: 1px solid #1a1a25;
          height: 28px;
          display: flex;
          align-items: center;
          z-index: 50;
        }
        .scan-ticker-belt {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 0;
          width: max-content;
          animation: scanTickerScroll var(--ticker-duration, 30s) linear infinite;
          cursor: grab;
          touch-action: pan-y;
        }
        .scan-ticker-belt:hover {
          animation-play-state: paused;
        }
        .scan-ticker-belt:active {
          cursor: grabbing;
        }
        .scan-ticker-chip {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 3px 12px;
          white-space: nowrap;
          cursor: pointer;
          border-right: 1px solid rgba(255,255,255,0.06);
          font-size: 12px;
          transition: background 0.15s;
        }
        .scan-ticker-chip:hover {
          background: rgba(255,255,255,0.07);
        }
        .scan-ticker-chip .scan-rank {
          color: rgba(255,255,255,0.3);
          font-size: 10px;
          min-width: 14px;
        }
        .scan-ticker-chip .scan-logo {
          width: 16px;
          height: 16px;
          border-radius: 50%;
        }
        .scan-ticker-chip .scan-name {
          color: #ccc;
          font-weight: 600;
          margin-right: 2px;
        }
        .scan-ticker-chip .scan-volbuy {
          color: #4caf50;
          font-size: 11px;
        }
        .scan-ticker-chip .scan-volsell {
          color: #f44336;
          font-size: 11px;
        }
        @keyframes scanTickerScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        /* NEW: clean toggle switch for ticker tape — NO JUMP on tab switch */
        .ticker-toggle {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-left: 10px;
          font-size: 12px;
          color: #aaa;
          user-select: none;
          cursor: pointer;
        }

        .ticker-toggle input[type="checkbox"] {
          position: absolute;
          opacity: 0;
          width: 0;
          height: 0;
        }

        /* Switch track */
        .ticker-toggle .switch {
          position: relative;
          display: inline-block;
          width: 36px;
          height: 18px;
          background: #222;
          border-radius: 9999px;
          transition: background 0.2s;
        }

        /* Turn on when checked */
        .ticker-toggle input:checked + .switch {
          background: #00c853;
        }

        /* Slider knob — aggressive initial state to stop jump */
        .ticker-toggle .slider {
          position: absolute;
          top: 2px;
          left: 2px;
          width: 14px;
          height: 14px;
          background: #fff;
          border-radius: 50%;
          transition: transform 0.2s;
          transform: translateX(0) !important;   /* force OFF immediately */
          will-change: transform;                /* GPU hint */
        }

        /* Move ONLY when checked */
        .ticker-toggle input:checked + .switch .slider {
          transform: translateX(18px) !important;
        }
      `}</style>
      <div className="split-container chart-only" style={{ overflow: 'visible', position: 'relative', width: '100%', height: '100%' }}>
        {/* Token-switch freeze-frame. The chart stays VISIBLE and holds the
            outgoing token's last canvas frame (draws are suppressed while
            switching); we blur + slightly dim it and drop the GateLoader label
            in the bottom-left. When the switch disarms, the blur lifts to
            reveal the freshly-painted new token — TradingView-style, no blank
            flash, no spinner-over-void. */}
        <div
          ref={wrapperRef}
          className={`footprint-wrapper${chartFocus && isPhone ? ' chart-focus-target' : ''}`}
          style={{
            width: '100%',
            height: '100%',
            position: 'relative',
            pointerEvents: showSwitchCover ? 'none' : 'auto',
          }}
        >
          {/* Token-switch loader. ABSOLUTE inside the wrapper (not fixed to the
              viewport), shaped to the measured visible chart body — so it rides
              with the chart when the phone scrolls instead of staying welded to
              the screen while the chart leaves without it.
              Sibling of the blurred canvas div, not a child of it, so it stays
              sharp. zIndex 60 clears it: the wrapper is position:relative with
              z-index auto, so it isn't a stacking context, and the blurred div's
              filter makes that div atomic without lifting it above us. */}
          {showSwitchCover && switchBodyRect && (
            <div style={{
              position: 'absolute',
              left: switchBodyRect.left,
              top: switchBodyRect.top,
              width: switchBodyRect.width,
              height: switchBodyRect.height,
              pointerEvents: 'none',
              zIndex: 12,   // in-panel chrome band — under bot menu (20) + popovers (30+)
            }}>
              <GateLoader />
            </div>
          )}

          {/* Chart rail — the chart's header. The NON-primary chart in a
              dual-chart cell renders no rail (headerless); the primary chart's
              rail carries both symbol chips and the shared controls. */}
          {!headerless && (() => {
            const tfValue   = timeInterval
            const tfSet     = setTimeInterval
            const modeValue = chartMode
            const modeSet   = setChartMode
            const densValue = bucketDensity
            const densSet   = setBucketDensity
            // Parked: F/N/C density header button hidden (logic kept). true = show.
            const SHOW_DENSITY_BTN = false
            return (
          <div className="chart-rail panel-rail" style={{ pointerEvents: 'auto' /* stays usable under the load cover's wrapper lock */ }}>
            <div className="chart-rail-left">
              {/* Sibling chip on the LEFT (so chart 1's identity is always
                  leftmost, even when chart 2 is the visible/active one). */}
              {secondChart && coLocated && secondChart.siblingSide === 'left' && (
                <>
                  <span
                    className={`chart-rail-symbol chart-rail-symbol-second${secondChart.isActive ? '' : ' chart-rail-symbol-dim'}`}
                    onClick={() => { cellSwitch?.(secondChart.siblingKey); secondChart.onActivate?.() }}
                    title={`Show ${secondChart.label}`}
                  >
                    {secondChart.label}
                  </span>
                  <span className="chart-rail-symbol-divider">|</span>
                </>
              )}
              {(() => {
                const isFut = dataSource === 'hyperliquid' && hlPair
                // Unified recents (sol + fut) replace sol-only tokenHistory,
                // and the menu now opens in BOTH modes.
                const recents = chipHistoryOpen ? readRecentViews() : []
                // ALWAYS openable. The old gate counted `recents`, which is
                // only READ while the menu is open — so a fresh futures
                // session (refresh-restore straight into a pair, empty
                // watchlist, no sol history) computed hasMenu=false and the
                // chip went inert even though localStorage held recents.
                // The record effect pushes every rendered view, so by the
                // time anyone can click this chip there is content; both
                // tabs render graceful empties for the first-ever boot.
                const hasMenu = true
                const label = isFut
                  ? ((cmeInfo(hlPair.base) || stkInfo(hlPair.base)) ? hlPair.base : `${hlPair.base}/USDC`)
                  : (data.currentToken ? `${data.currentToken.slice(0, 4)}…${data.currentToken.slice(-4)}` : 'Cheddar')
                const pick = (entry) => {
                  setChipHistoryOpen(false)
                  onHistorySelect?.(entry)
                }
                const row = (entry) => {
                  const key = entry.kind === 'fut' ? `fut:${entry.coin}` : entry.address
                  return (
                    <div key={key} className="token-history-item" onClick={() => pick(entry)}>
                      <span className="token-history-name">{entry.name}</span>
                      <span className="token-history-addr" style={entry.kind === 'fut' ? { color: '#3b82f6' } : undefined}>
                        {entry.kind === 'fut' ? (stkInfo(entry.coin) ? 'STK' : cmeInfo(entry.coin) ? 'CME' : 'PERP') : `${entry.address.slice(0, 6)}...`}
                      </span>
                      {entry.kind === 'fut' && stkInfo(entry.coin) && (
                        <span className="token-history-addr" style={{ cursor: 'pointer', textDecoration: 'underline', marginLeft: 6 }}
                          onClick={(e) => { e.stopPropagation(); setChipHistoryOpen(false); window.dispatchEvent(new CustomEvent('cheddar-open-options', { detail: { symbol: entry.coin } })) }}>options</span>
                      )}
                    </div>
                  )
                }
                const wlSol = tokenWatchlist.filter(t => t.kind !== 'fut')
                const wlFut = tokenWatchlist.filter(t => t.kind === 'fut')
                const sect = (text, colr) => (
                  <div className="dropdown-section-header" style={{
                    borderBottom: `2px solid ${colr}`,
                    paddingBottom: 3,
                    marginBottom: 2,
                  }}>{text}</div>
                )
                return (
                  <span className="chart-rail-symbol-wrap" ref={chipHistoryRef} style={{ position: 'relative' }} onDoubleClick={(e) => e.stopPropagation()}>
                    <span
                      className={`chart-rail-symbol${hasMenu ? ' chart-rail-symbol-clickable' : ''}`}
                      onClick={hasMenu ? toggleChipHistory : undefined}
                      title={hasMenu ? 'Recents & watchlist' : undefined}
                    >
                      {label}
                      {hasMenu && <span className="chart-rail-symbol-caret">▾</span>}
                    </span>
                    {chipHistoryOpen && hasMenu && (
                      <div
                        ref={chipMenuRef}
                        className="token-history-dropdown chart-chip-history"
                        style={chipMenuStyle || undefined}
                      >
                        <div className="dropdown-section-header" style={{ display: 'flex', gap: 6, userSelect: 'none' }}>
                          <span
                            onClick={(e) => { e.stopPropagation(); setChipTab('recent') }}
                            style={{ cursor: 'pointer', opacity: chipTab === 'recent' ? 1 : 0.45, fontWeight: chipTab === 'recent' ? 700 : 500 }}
                          >Recent</span>
                          <span style={{ opacity: 0.3 }}>|</span>
                          <span
                            onClick={(e) => { e.stopPropagation(); setChipTab('watch') }}
                            style={{ cursor: 'pointer', opacity: chipTab === 'watch' ? 1 : 0.45, fontWeight: chipTab === 'watch' ? 700 : 500 }}
                          >Watchlist</span>
                        </div>
                        {chipTab === 'recent' ? (
                          recents.length > 0
                            ? recents.map(row)
                            : (data.tokenHistory?.length
                                ? data.tokenHistory.map(e => row({ kind: 'sol', ...e }))
                                : <div className="token-history-item" style={{ opacity: 0.5, cursor: 'default' }}>no recents yet</div>)
                        ) : (
                          <>
                            {wlSol.length > 0 && sect('Tokens', '#b8860b')}
                            {wlSol.map(t => row({ kind: 'sol', address: t.address, name: t.symbol || t.name || `${t.address.slice(0, 4)}…${t.address.slice(-4)}` }))}
                            {wlFut.length > 0 && sect('Futures', '#3b82f6')}
                            {wlFut.map(t => row({ kind: 'fut', coin: t.coin, name: t.name }))}
                            {wlSol.length === 0 && wlFut.length === 0 && (
                              <div className="token-history-item" style={{ opacity: 0.5, cursor: 'default' }}>watchlist empty — ★ a chart</div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </span>
                )
              })()}
              {hlPair && (
                <span className="chart-rail-lev">{stkInfo(hlPair.base) ? 'STK · est. footprint' : cmeInfo(hlPair.base) ? 'CME · live' : `${hlPair.maxLeverage}x`}</span>
              )}
              {/* Sibling chip on the RIGHT (chart 2 always rightmost). Clicking
                  switches the visible body (cellSwitch) + arms it. */}
              {secondChart && coLocated && secondChart.siblingSide === 'right' && (
                <>
                  <span className="chart-rail-symbol-divider">|</span>
                  <span
                    className={`chart-rail-symbol chart-rail-symbol-second${secondChart.isActive ? '' : ' chart-rail-symbol-dim'}`}
                    onClick={() => { cellSwitch?.(secondChart.siblingKey); secondChart.onActivate?.() }}
                    title={`Show ${secondChart.label}`}
                  >
                    {secondChart.label}
                  </span>
                </>
              )}
              <TimeframeSelect value={tfValue} onChange={(v) => tfSet(v)} chartId={chartId} cmeRoot={(dataSource === 'hyperliquid' && hlPair?.base && cmeInfo(hlPair.base)) ? String(hlPair.base).toUpperCase() : null} />
            </div>
            <div className="chart-rail-right">
              {/* Quick-trade: leftmost of the right rail (beaker-toggled). */}
              {showQuickTrade && (
                <div className="chart-rail-group" style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                     onDoubleClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => fireQuick('buy')}
                    disabled={!!qtPending}
                    title={dataSource === 'hyperliquid' ? 'Market buy (futures defaults sizing)' : `Market buy ${quickQty} SOL`}
                    style={{ height: 20, padding: '0 8px', fontSize: 10, fontWeight: 700, fontFamily: 'monospace',
                             color: '#22c55e', background: 'transparent', border: '1px solid #22c55e55',
                             borderRadius: 4, cursor: qtPending ? 'wait' : 'pointer',
                             opacity: qtPending === 'sell' ? 0.4 : 1 }}
                  >{qtPending === 'buy' ? '…' : 'BUY'}</button>
                  {dataSource !== 'hyperliquid' && (
                    <input
                      type="text" inputMode="decimal" enterKeyHint="done"
                      value={quickQty}
                      onChange={(e) => setQuickQty(e.target.value.replace(/[^0-9.]/g, ''))}
                      onClick={(e) => e.stopPropagation()}
                      onPointerDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      title="SOL amount for market buys"
                      style={{ width: '4.5ch', height: 18, fontSize: 10, fontWeight: 600, fontFamily: 'monospace',
                               textAlign: 'center', color: '#ccc', background: 'transparent',
                               border: 'none', borderBottom: '1px solid #444', outline: 'none', padding: 0 }}
                    />
                  )}
                  <button
                    onClick={() => fireQuick('sell')}
                    disabled={!!qtPending}
                    title={dataSource === 'hyperliquid' ? 'Market sell / close (futures defaults sizing)' : 'Market sell (default sell %)'}
                    style={{ height: 20, padding: '0 8px', fontSize: 10, fontWeight: 700, fontFamily: 'monospace',
                             color: '#ef4444', background: 'transparent', border: '1px solid #ef444455',
                             borderRadius: 4, cursor: qtPending ? 'wait' : 'pointer',
                             opacity: qtPending === 'buy' ? 0.4 : 1 }}
                  >{qtPending === 'sell' ? '…' : 'SELL'}</button>
                  {qtPaper && dataSource !== 'hyperliquid' && (() => {
                    const pp = paperPosition(data.currentToken)
                    const sm = paperSummary()
                    const tip = `Paper (no fees)\nClosed: ${sm.sells} · wins ${sm.wins} · net ${sm.net >= 0 ? '+' : ''}${sm.net.toFixed(4)} SOL`
                    if (!pp) return <span title={tip} style={{ fontSize: 10, fontFamily: 'monospace', color: '#888' }}>flat</span>
                    const o = paperOpenPnl(pp, data.rawTradesRef?.current?.lastMcapOf?.('sell') || chartData.highlightMcap || data.lastBucketMcapRef?.current)   // marked at the bid (what a sell fills at)
                    const c = !o ? '#888' : o.pnl >= 0 ? '#22c55e' : '#ef4444'
                    return (
                      <span title={`${tip}\nOpen: ${pp.costSol.toFixed(3)} SOL in`} style={{ fontSize: 10, fontWeight: 700, fontFamily: 'monospace', color: c, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {pp.costSol.toFixed(2)}◎ {o ? `${o.pnl >= 0 ? '+' : ''}${o.pnl.toFixed(3)} (${o.pct >= 0 ? '+' : ''}${o.pct.toFixed(1)}%)` : '…'}
                        {/* paperclose-20261027: close 100% regardless of the default sell % */}
                        <button
                          onClick={() => { trading.executeMarketOrder('sell', { sellFraction: 1 }).catch(() => {}) }}
                          title="Close paper position (100%)"
                          style={{ height: 16, padding: '0 4px', fontSize: 10, fontWeight: 700, fontFamily: 'monospace',
                                   color: '#ef4444', background: 'transparent', border: '1px solid #ef444455',
                                   borderRadius: 3, cursor: 'pointer', lineHeight: 1 }}
                        >X</button>
                      </span>
                    )
                  })()}
                </div>
              )}
              {/* Footprint/Candle, LOG/LIN and the phone crosshair button moved out
                  of the header: chart type + scale live in the context menu's 'Chart type'
                  section, crosshair is a double-tap on the chart body (phone). */}
              {/* F/N/C density button - PARKED for later (SHOW_DENSITY_BTN). Code and
                  density state kept intact; flip the flag to bring it back. */}
              {SHOW_DENSITY_BTN && (
              <div className="chart-mode-toggle chart-rail-group" title="Bucket size density">
                {(() => {
                  const densityCycle = ['fine', 'normal', 'coarse']
                  const densityLabels = { fine: 'F', normal: 'N', coarse: 'C' }
                  const densityNames = { fine: 'Fine', normal: 'Normal', coarse: 'Coarse' }
                  const currentDensity = densityCycle.includes(densValue) ? densValue : 'normal'
                  const nextDensity = densityCycle[(densityCycle.indexOf(currentDensity) + 1) % densityCycle.length]
                  return (
                    <button
                      type="button"
                      className="chart-mode-btn active chart-density-flat chart-text-toggle"
                      onClick={() => densSet(nextDensity)}
                      title={`Density: ${densityNames[currentDensity]} — click to cycle Fine / Normal / Coarse`}
                    >
                      {densityLabels[currentDensity]}
                    </button>
                  )
                })()}
              </div>
              )}
              {/* Indicators — the beaker. Toggles moved here from the
                  right-click menu, which keeps only positional actions
                  (copy/paste, S/R here, AVWAP here). */}
              <div className={`chart-mode-toggle chart-rail-group beaker-wrap${indicatorMenuPos ? ' beaker-open' : ''}${showQuickTrade ? ' beaker-qt' : ''}`} ref={indicatorMenuRef}>
                {/* beaker-label-20261022: "Indicator" slides out from behind the beaker on hover.
                    Touch: slides out while the Indicators menu is open; hidden
                    when the BUY/SELL buttons sit left of the beaker (no overlap). */}
                <style>{`.chart-rail .beaker-wrap,.beaker-wrap{position:relative;overflow:visible}.beaker-wrap>button{position:relative;z-index:2}.beaker-clip{position:absolute;right:100%;top:0;bottom:0;display:flex;align-items:center;overflow:hidden;pointer-events:none}.beaker-label{display:block;padding-right:3px;font-size:10px;line-height:14px;white-space:nowrap;color:#ccc;opacity:0;transform:translateX(100%);transition:transform .18s ease-out,opacity .14s ease-out}@media (hover:hover){.beaker-wrap:hover .beaker-label{opacity:1;transform:translateX(0)}}@media (hover:none),(pointer:coarse){.beaker-wrap.beaker-open:not(.beaker-qt) .beaker-label{opacity:1;transform:translateX(0)}.beaker-wrap.beaker-qt .beaker-clip{display:none}}`}</style>
                <span className="beaker-clip"><span className="beaker-label">Indicator</span></span>
                <button
                  className={`chart-mode-btn ${indicatorMenuPos ? 'active' : ''}`}
                  onClick={(e) => {
                    const r = e.currentTarget.getBoundingClientRect()
                    // Phone: open toward the side with more room and cap to it,
                    // so a mid-screen rail never gets a full-screen menu that
                    // covers the beaker (no way to tap out). Desktop unchanged.
                    const below = window.innerHeight - r.bottom
                    const up = isPhone ? r.top > below : r.top > window.innerHeight / 2
                    // Anchor only — ChartMenu measures, clamps and flips.
                    setIndicatorMenuPos(prev => prev ? null : {
                      x: r.left,
                      y: up ? r.top - 6 : r.bottom + 6,
                      up,
                      maxH: up ? Math.max(140, r.top - 14) : (isPhone ? Math.max(140, below - 14) : undefined),
                    })
                  }}
                >
                  <img src="/beaker.svg" alt="Indicators" width="14" height="14" draggable="false"
                       style={{ display: 'block', objectFit: 'contain' }} />
                </button>
                {indicatorMenuPos && createPortal(
                  <ChartMenu panelRef={indicatorMenuPanelRef}
                    anchor={indicatorMenuPos}
                    maxHeight={indicatorMenuPos.maxH}
                    ignoreRefs={[indicatorMenuRef]}
                    onClose={() => setIndicatorMenuPos(null)}
                    closeOnTouchOutside={isPhone}
                    wide className="cm-list" title="Indicators">
                    <MenuItemRow>
                      <MenuItem
                        onContextMenu={(e) => {
                          e.preventDefault()
                          const r = e.currentTarget.getBoundingClientRect()
                          const openLeft = r.left > 240
                          setAbsSettings({ show: true, x: openLeft ? r.left - 6 : r.right + 6, y: Math.max(6, Math.min(r.top, window.innerHeight - 260)), openLeft })
                          setIndicatorMenuPos(null)
                        }}
                        onClick={() => setShowAbsorption(v => !v)}>
                        {showAbsorption ? '✓' : '○'} Absorption
                      </MenuItem>
                      <MenuItem gear
                        title="Absorption settings"
                        onClick={(e) => {
                          const r = e.currentTarget.getBoundingClientRect()
                          const openLeft = r.left > 240
                          setAbsSettings({ show: true, x: openLeft ? r.left - 6 : r.right + 6, y: Math.max(6, Math.min(r.top, window.innerHeight - 260)), openLeft })
                          setIndicatorMenuPos(null)
                        }}>
                        ⚙
                      </MenuItem>
                    </MenuItemRow>
                    <MenuItemRow>
                      <MenuItem
                        onContextMenu={(e) => {
                          // right-click the row = open its gear menu directly
                          e.preventDefault()
                          const r = e.currentTarget.getBoundingClientRect()
                          const openLeft = r.left > 240
                          setArdSettings({ show: true, x: openLeft ? r.left - 6 : r.right + 6, y: Math.max(6, Math.min(r.top, window.innerHeight - 340)), openLeft })
                          setIndicatorMenuPos(null)
                        }}
                        onClick={() => setShowArd(v => !v)}>
                        {showArd ? '✓' : '○'} ARD Shadow
                      </MenuItem>
                      <MenuItem gear
                        title="ARD shadow settings"
                        onClick={(e) => {
                          const r = e.currentTarget.getBoundingClientRect()
                          const openLeft = r.left > 240
                          setArdSettings({ show: true, x: openLeft ? r.left - 6 : r.right + 6, y: Math.max(6, Math.min(r.top, window.innerHeight - 340)), openLeft })
                          setIndicatorMenuPos(null)
                        }}>
                        ⚙
                      </MenuItem>
                    </MenuItemRow>
                    <MenuItemRow>
                      <MenuItem
                        onContextMenu={(e) => {
                          e.preventDefault()
                          const r = e.currentTarget.getBoundingClientRect()
                          const openLeft = r.left > 240
                          setAtrSettings({ show: true, x: openLeft ? r.left - 6 : r.right + 6, y: Math.max(6, Math.min(r.top, window.innerHeight - 280)), openLeft })
                          setIndicatorMenuPos(null)
                        }}
                        onClick={() => setShowATRStop(v => !v)}>
                        {showATRStop ? '✓' : '○'} ATR Trailing Stop
                      </MenuItem>
                      <MenuItem gear
                        title="ATR Trail settings"
                        onClick={(e) => {
                          const r = e.currentTarget.getBoundingClientRect()
                          const openLeft = r.left > 240
                          setAtrSettings({ show: true, x: openLeft ? r.left - 6 : r.right + 6, y: Math.max(6, Math.min(r.top, window.innerHeight - 280)), openLeft })
                          setIndicatorMenuPos(null)
                        }}>
                        ⚙
                      </MenuItem>
                    </MenuItemRow>
                    <MenuItem onClick={() => setShowBotTrades(v => !v)}>
                      {showBotTrades ? '✓' : '○'} Bot Markers
                    </MenuItem>
                    {/* Display only: the active bot's range band + lines. */}
                    <MenuItem onClick={() => setShowBotOverlay(v => !v)}>
                      {showBotOverlay ? '✓' : '○'} Bot Overlay
                    </MenuItem>
                    {/* ARMS the local bot — real orders. Guarded, so it can't
                        stack a second brain on top of a live server bot. */}
                    <MenuItem onClick={toggleLocalBotArm}>
                      {botEnabled ? '✓' : '○'} Bot Trade
                    </MenuItem>
                    <MenuItem onClick={() => setShowQuickTrade(v => !v)}>
                      {showQuickTrade ? '✓' : '○'} Buy/Sell Buttons
                    </MenuItem>
                    {(() => {
                      const openChurn = (e) => {
                        const r = e.currentTarget.getBoundingClientRect()
                        const openLeft = r.left > 240
                        setChurnSettings({ show: true, x: openLeft ? r.left - 6 : r.right + 6, y: Math.max(6, Math.min(r.top, window.innerHeight - 320)), openLeft })
                        setIndicatorMenuPos(null)
                      }
                      return (
                        <MenuItemRow>
                          <MenuItem
                            onContextMenu={(e) => { e.preventDefault(); openChurn(e) }}
                            onClick={() => setShowChurnZones(v => !v)}>
                            {showChurnZones ? '✓' : '○'} Churn Zones
                          </MenuItem>
                          <MenuItem gear title="Churn Zones settings" onClick={openChurn}>
                            ⚙
                          </MenuItem>
                        </MenuItemRow>
                      )
                    })()}
                    <MenuItemRow>
                      <MenuItem
                        onContextMenu={(e) => {
                          // right-click the row = open its gear menu directly
                          e.preventDefault()
                          const r = e.currentTarget.getBoundingClientRect()
                          const openLeft = r.left > 240
                          setCvdSettings({ show: true, x: openLeft ? r.left - 6 : r.right + 6, y: Math.max(6, Math.min(r.top, window.innerHeight - 200)), openLeft })
                          setIndicatorMenuPos(null)
                        }}
                        onClick={() => setShowCvdSubgraph(v => !v)}>
                        {showCvdSubgraph ? '✓' : '○'} Cumulative Delta
                      </MenuItem>
                      <MenuItem gear
                        title="Cumulative Delta settings"
                        onClick={(e) => {
                          const r = e.currentTarget.getBoundingClientRect()
                          const openLeft = r.left > 240
                          setCvdSettings({ show: true, x: openLeft ? r.left - 6 : r.right + 6, y: Math.max(6, Math.min(r.top, window.innerHeight - 200)), openLeft })
                          setIndicatorMenuPos(null)
                        }}>
                        ⚙
                      </MenuItem>
                    </MenuItemRow>
                    <MenuItemRow>
                      <MenuItem onClick={() => setShowBodyVol(v => !v)}>
                        {showBodyVol ? '✓' : '○'} Wick Volume (rel)
                      </MenuItem>
                    </MenuItemRow>
                    <MenuItemRow>
                      <MenuItem
                        onContextMenu={(e) => {
                          // right-click the row = open its gear menu directly
                          e.preventDefault()
                          const r = e.currentTarget.getBoundingClientRect()
                          const openLeft = r.left > 240
                          setDvpSettings({ show: true, x: openLeft ? r.left - 6 : r.right + 6, y: Math.max(6, Math.min(r.top, window.innerHeight - 380)), openLeft })
                          setIndicatorMenuPos(null)
                        }}
                        onClick={() => setShowDvp(v => !v)}>
                        {showDvp ? '✓' : '○'} Delta Volume Pro
                      </MenuItem>
                      <MenuItem gear
                        title="Delta Volume Pro settings"
                        onClick={(e) => {
                          const r = e.currentTarget.getBoundingClientRect()
                          const openLeft = r.left > 240
                          setDvpSettings({ show: true, x: openLeft ? r.left - 6 : r.right + 6, y: Math.max(6, Math.min(r.top, window.innerHeight - 380)), openLeft })
                          setIndicatorMenuPos(null)
                        }}>
                        ⚙
                      </MenuItem>
                    </MenuItemRow>
                    <MenuItem onClick={() => setShowFlipZones(v => !v)}>
                      {showFlipZones ? '✓' : '○'} Flip Zones
                    </MenuItem>
                    <MenuItem onClick={() => setShowCellFlip(v => !v)}>
                      {showCellFlip ? '✓' : '○'} Flip Animation
                    </MenuItem>
                    <MenuItem
                      onClick={() => setShowPoc(v => !v)}>
                      {showPoc ? '✓' : '○'} Footprint POC
                    </MenuItem>
                    {anyHeatSource && (<MenuItemRow>
                      <MenuItem
                        onContextMenu={(e) => {
                          // right-click the row = open its gear menu directly
                          e.preventDefault()
                          const r = e.currentTarget.getBoundingClientRect()
                          const openLeft = r.left > 240
                          setHeatSettings({ show: true, x: openLeft ? r.left - 6 : r.right + 6, y: Math.max(6, Math.min(r.top, window.innerHeight - 260)), openLeft })
                          setIndicatorMenuPos(null)
                        }}
                        onClick={() => {
                          // master: any on -> all off; all off -> HL on
                          if (showBookHeat || showCbHeat) { setShowBookHeat(false); setShowCbHeat(false) }
                          else setShowBookHeat(true)
                        }}>
                        {(showBookHeat || showCbHeat) ? '✓' : '○'} Heatmap
                      </MenuItem>
                      <MenuItem gear
                        title="Heatmap"
                        onClick={(e) => {
                          const r = e.currentTarget.getBoundingClientRect()
                          const openLeft = r.left > 240
                          setHeatSettings({ show: true, x: openLeft ? r.left - 6 : r.right + 6, y: Math.max(6, Math.min(r.top, window.innerHeight - 260)), openLeft })
                          setIndicatorMenuPos(null)
                        }}>
                        ⚙
                      </MenuItem>
                    </MenuItemRow>)}
                    <MenuItem onClick={() => setShowMeteora(v => !v)}>
                      {showMeteora ? '✓' : '○'} Meteora DLMM
                    </MenuItem>
                    {/* Moving Average — toggle + ⚙, same pattern as ATR / DVP.
                        Right-click the row opens the settings directly. */}
                    {(() => {
                      const ma0 = maConfigs[0]
                      const openMa = (e) => {
                        const r = e.currentTarget.getBoundingClientRect()
                        const openLeft = r.left > 240
                        setMaSettings({ show: true, id: ma0.id, x: openLeft ? r.left - 6 : r.right + 6, y: Math.max(6, Math.min(r.top, window.innerHeight - 160)), openLeft })
                        setIndicatorMenuPos(null)
                      }
                      return (
                        <MenuItemRow>
                          <MenuItem
                            onContextMenu={(e) => { e.preventDefault(); openMa(e) }}
                            onClick={() => updateMa(ma0.id, { on: !ma0.on })}>
                            {ma0.on ? '✓' : '○'} Moving Average
                          </MenuItem>
                          <MenuItem gear title="Moving Average settings" onClick={openMa}>
                            ⚙
                          </MenuItem>
                        </MenuItemRow>
                      )
                    })()}
                    {(() => {
                      const openProt = (e) => {
                        const r = e.currentTarget.getBoundingClientRect()
                        const openLeft = r.left > 240
                        setProtSettings({ show: true, x: openLeft ? r.left - 6 : r.right + 6, y: Math.max(6, Math.min(r.top, window.innerHeight - 200)), openLeft })
                        setIndicatorMenuPos(null)
                      }
                      return (
                        <MenuItemRow>
                          <MenuItem
                            onContextMenu={(e) => { e.preventDefault(); openProt(e) }}
                            onClick={() => setShowProtraction(v => !v)}>
                            {showProtraction ? '✓' : '○'} Protraction
                          </MenuItem>
                          <MenuItem gear title="Protraction settings" onClick={openProt}>
                            ⚙
                          </MenuItem>
                        </MenuItemRow>
                      )
                    })()}
                    <MenuItemRow>
                      <MenuItem
                        onContextMenu={(e) => {
                          // right-click the row = open its gear menu directly
                          e.preventDefault()
                          const r = e.currentTarget.getBoundingClientRect()
                          const openLeft = r.left > 240
                          setTrailFlowMenu({ x: openLeft ? r.left - 6 : r.right + 6, y: Math.max(6, Math.min(r.top, window.innerHeight - 200)), openLeft })
                          setIndicatorMenuPos(null)
                        }}
                        onClick={() => setShowOrderflowTrail(v => !v)}>
                        {showOrderflowTrail ? '✓' : '○'} Smoketrail
                      </MenuItem>
                      {/* Gear, not the old flow arrow: the Smoketrail menu (flow
                          direction + buy/sell/outline colours) lives here now,
                          same pattern as ATR / Heatmap / DVP. Flow flipping is
                          inside it, so the arrow went. */}
                      <MenuItem gear
                        title="Smoketrail settings"
                        onClick={(e) => {
                          const r = e.currentTarget.getBoundingClientRect()
                          const openLeft = r.left > 240
                          setTrailFlowMenu({ x: openLeft ? r.left - 6 : r.right + 6, y: Math.max(6, Math.min(r.top, window.innerHeight - 200)), openLeft })
                          setIndicatorMenuPos(null)
                        }}>
                        ⚙
                      </MenuItem>
                    </MenuItemRow>
                    <MenuItem onClick={() => setShowSwings(v => !v)}>
                      {showSwings ? '✓' : '○'} Swings
                    </MenuItem>
                    <MenuItem onClick={() => setShowValueArea(v => !v)}>
                      {showValueArea ? '✓' : '○'} Value Area
                    </MenuItem>
                    {optVolSym && (
                      <MenuItem onClick={() => setShowOptVol(v => !v)}>
                        {showOptVol ? '✓' : '○'} Opt Vol Levels
                      </MenuItem>
                    )}
                    <MenuItemRow>
                      <MenuItem
                        onContextMenu={(e) => {
                          // right-click / long-press the row = open its gear menu
                          e.preventDefault()
                          const r = e.currentTarget.getBoundingClientRect()
                          const openLeft = r.left > 240
                          setVolSettings({ show: true, x: openLeft ? r.left - 6 : r.right + 6, y: Math.max(6, Math.min(r.top, window.innerHeight - 200)), openLeft })
                          setIndicatorMenuPos(null)
                        }}
                        onClick={() => setShowVolumeBars(v => !v)}>
                        {showVolumeBars ? '✓' : '○'} Volume
                      </MenuItem>
                      <MenuItem gear
                        title="Volume settings"
                        onClick={(e) => {
                          const r = e.currentTarget.getBoundingClientRect()
                          const openLeft = r.left > 240
                          setVolSettings({ show: true, x: openLeft ? r.left - 6 : r.right + 6, y: Math.max(6, Math.min(r.top, window.innerHeight - 200)), openLeft })
                          setIndicatorMenuPos(null)
                        }}>
                        ⚙
                      </MenuItem>
                    </MenuItemRow>
                    <MenuItemRow>
                      <MenuItem
                        onClick={() => setShowVolumeProfile(v => !v)}>
                        {showVolumeProfile ? '✓' : '○'} Volume Profile
                      </MenuItem>
                      <MenuItem gear
                        title="Volume profile settings"
                        onClick={(e) => {
                          const r = e.currentTarget.getBoundingClientRect()
                          const openLeft = r.left > 240
                          setVpSettings({ show: true, x: openLeft ? r.left - 6 : r.right + 6, y: Math.max(6, Math.min(r.top, window.innerHeight - 200)), openLeft })
                          setIndicatorMenuPos(null)
                        }}>
                        ⚙
                      </MenuItem>
                    </MenuItemRow>
                  </ChartMenu>,
                  document.body
                )}
              </div>
            </div>
          </div>
            )
          })()}

          {/* Header */}
          <div className="footprint-header-row">
            <div className="y-axis-header-cell" style={{ width: axisColW }}>{hasAxisValues ? 'MCap' : ''}</div>
            <div className="header-viewport" ref={headerVpRef} data-xe={groupedCells.endIdx} data-xpad={groupedCells.rightPadPx}>
              {groupedCells.groups.map((g, i) => (
                <div key={i} className="candle-header-cell" style={{ width: `${g.widthPx}px`, minWidth: `${g.widthPx}px` }}>
                  {g.label}
                </div>
              ))}
            </div>
            {/* Crosshair time chip — on the row (outside the masked viewport). */}
            <div ref={headerXLabelRef} className="x-cross-label" style={{ display: 'none' }}><span className="price-label-text" /></div>
          </div>

          {/* Body */}
          <div className="footprint-body-wrapper" style={{
            position: 'relative',
            // Freeze-frame blur lives HERE (the canvas + overlay layers), not on
            // the whole wrapper — so the rail (new symbol name, TF/mode controls)
            // and footer stay sharp and usable while only the chart body blurs
            // and unblurs into the new token. The canvas underneath holds the
            // old frame because draws are suppressed while switching.
            filter: showSwitchCover ? 'blur(5px) brightness(0.9) saturate(0.9)' : 'none',
            transition: 'filter 260ms ease-out',
            willChange: showSwitchCover ? 'filter' : 'auto',
          }}>
              {/* Watermark — current symbol behind footprints */}
              <div className="chart-watermark">
                {dataSource === 'hyperliquid' && hlPair
                  ? ((cmeInfo(hlPair.base) || stkInfo(hlPair.base)) ? hlPair.base : `${hlPair.base}/USDC`)
                  : data.currentToken?.slice(0, 8) || 'Cheddar'}
              </div>
            {/* Cursor mcap label — DOM ref, chart body hover only */}
            {/* Horizontal cursor highlight — aligned in Y with the price
                label, width matches the chart body cells. DOM-based so
                cursor movement doesn't trigger a canvas redraw. */}
            <div ref={hHighlightRef} className="chart-h-highlight" style={{ display: 'none', transform: 'translateY(-50%)' }} />
            {/* Cursor mcap label — DOM ref, chart body hover only */}
            <div ref={cursorLabelRef} className="price-label" style={{ display: 'none', transform: 'translateY(-50%)' }} />

            {/* Timeframe, chart-mode, density moved to .chart-rail (chart header). */}

            {/* Bot HUD badge */}
            <BotBadge
              bot={activeBot}
              waiting={botWaiting}
              walletConnected={dataSource === 'hyperliquid' || !!publicKey}
              onContextMenu={(e) => setBotMenu({ show: true, x: e.clientX, y: e.clientY })}
              dataSource={dataSource}
              hlPair={hlPair}
            />

            {/* Volume bars — CANVAS lane (was VolumeBarsLayer, DOM divs).
                DOM could only move on a React commit, so during a drag the
                chart canvas glided at rAF rate while the lane arrived a
                commit later — the lag Levi spotted. Drawn now from the same
                draw pass, on the same clock, with the same column math.
                Revert = restore <VolumeBarsLayer/> here and drop the two
                blocks marked VOLUME LANE below. */}
            {/* Gated on hasAxisValues, not just cleared. A canvas holds its
                last painted frame until something overwrites it, and on a
                blank load NOTHING triggers a full draw — so a clearRect in
                the draw pass never runs and the previous token's bars stay
                on screen. Unmounting removes the pixels with the element,
                which is the only version that cannot go stale. */}
            {/* (gridvol-20261027) The lane canvas now mounts INSIDE the scroll
                container, right after the grid canvas, so it paints over the
                grid lines (and plates) but stays under trail/cells. */}

            {/* Scroll container */}
            <div
              className={`footprint-body-scroll ${scroll.isDragging ? 'dragging' : ''} ${interaction.isDraggingOverlay ? 'overlay-dragging' : ''}`}
              ref={bodyScrollRef}
              style={{
                // Visibility is handled by the parent footprint-wrapper's
                // opacity fade (driven by isSwitching). No need to also hide
                // here — that would prevent the canvas from being part of the
                // fade-in animation.
                ...(interaction.isDraggingOverlay
                  ? { cursor: interaction.draggingItemRef.current?.type === 'sr' ? 'ns-resize' : 'ew-resize' }
                  : null),
              }}
              onPointerDown={(e) => {
                if (dbgOn()) dbg('[Absorbers] body pointerdown reached ' + JSON.stringify({ type: e.pointerType, button: e.button, target: e.target?.tagName + '.' + (e.target?.className || '') }))
                lastPointerTypeRef.current = e.pointerType || 'mouse'
                // Absorbers: remember the press; pointerup decides if it was a still tap.
                absTapRef.current = (e.isPrimary && (e.button === 0 || e.pointerType === 'touch') && showAbsorptionRef.current
                  && dataSource !== 'hyperliquid' && !e.target.closest('.y-axis-body, .y-axis-region'))
                  ? { x: e.clientX, y: e.clientY, t: e.timeStamp, id: e.pointerId, onAbs: tryOpenAbsorbers(e.clientX, e.clientY, true) } : null
                if (dbgOn()) dbg('[Absorbers] down ' + JSON.stringify(absTapRef.current
                  ? { onOutline: absTapRef.current.onAbs, type: e.pointerType, id: e.pointerId }
                  : { tracked: false, primary: e.isPrimary, button: e.button, type: e.pointerType, absOn: showAbsorptionRef.current,
                      ds: dataSource, onYAxis: !!e.target.closest('.y-axis-body, .y-axis-region'), target: String(e.target?.className || '') }))
                if (e.isPrimary) lpClaimRef.current = false
                // Phone trendline placement owns every touch until it ends,
                // ahead of AVWAP / S/R grabs, which would otherwise steal a
                // finger that lands near their lines.
                if (e.pointerType === 'touch' && trend.modeRef.current === 'phonePlacing') {
                  const _r = canvasRef.current?.getBoundingClientRect()
                  if (_r && trend.handlePointerDown(e.clientX - _r.left, e.clientY - _r.top, e.pointerType, e.clientX, e.clientY)) {
                    trendPlaceTouchRef.current = true
                    lpClaimRef.current = true
                    if (e.cancelable) e.preventDefault()
                    return
                  }
                }
                // AVWAP / S/R line drag takes priority: if a line is hovered,
                // arm the overlay drag and skip alerts + chart pan entirely.
                if (interaction.handleOverlayDragStart(e)) {
                  // S/R / AVWAP grab owns this finger, incl. its own hold-to-menu.
                  if (e.pointerType === 'touch') lpClaimRef.current = true
                  if (e.pointerType === 'touch' && trend.hoveredRef.current) {
                    trend.hoveredRef.current = null
                    if (drawCanvasRef.current) drawCanvasRef.current()
                  }
                  // Own the gesture immediately. Phone body is touch-action:none
                  // now, but still preventDefault so any residual native scroll
                  // cannot move Y under an S/R drag.
                  if (e.cancelable) e.preventDefault()
                  return
                }
                // Drawing a trendline owns the click: anchor start, or finish.
                if (e.button === 0) {
                  const _r = canvasRef.current?.getBoundingClientRect()
                  if (_r && trend.handlePointerDown(e.clientX - _r.left, e.clientY - _r.top, e.pointerType, e.clientX, e.clientY)) {
                    // Trendline grab owns it (its own hold opens its menu).
                    if (e.pointerType === 'touch') lpClaimRef.current = true
                    e.preventDefault(); return
                  }
                }
                // Compute mcap at cursor to arm long-press for alerts.
                // Movement > tolerance cancels (it's a drag/pan, not a press).
                // Skip if pointer is over the y-axis region — that's not a chart click.
                // A press on an Absorption outline is the absorbers tap, never an alert.
                if (e.button === 0 && !absTapRef.current?.onAbs && !e.target.closest('.y-axis-body') && !e.target.closest('.y-axis-region')) {
                  const body = bodyScrollRef.current
                  const canvas = canvasRef.current
                  const mcaps = visibleSortedMcapsRef.current
                  const RH = dynamicRowHeightRef.current
                  if (body && canvas && mcaps?.length >= 2 && RH > 0) {
                    // canvas rect already includes body.scrollTop AND the
                    // translateY transform — same canvas-internal coord
                    // space the cells are drawn in. Avoids the off-by-drag
                    // bug that pinning to bodyRect + scrollTop introduces.
                    const canvasRect = canvas.getBoundingClientRect()
                    const relY = e.clientY - canvasRect.top
                    let mcap = yToMcapUtil(relY, mcaps, RH)
                    const _mTop = yToMcapUtil(0, mcaps, RH)   // top edge of row 0
                    if (mcap > _mTop) mcap = _mTop
                    else if (mcap < mcaps[mcaps.length - 1]) mcap = mcaps[mcaps.length - 1]
                    // Store a whole-number mcap (matches the crosshair label,
                    // no decimals) and pass the current price so the alert
                    // direction is set by where the press lands: above the
                    // live price → cross-above, below → cross-below.
                    priceAlerts.handlePointerDown(e, Math.round(mcap), chartData.highlightMcap)
                  }
                }
                scroll.handlePointerDown(e)
              }}
              onPointerMove={priceAlerts.handlePointerMove}
              onPointerUp={(e) => {
                {
                  // Still click / tap on an Absorption outline -> absorbers panel.
                  // Not a drag (<6px), not a hold (<350ms: long-press menus stay
                  // theirs), not a finger a line grab / trendline placement owns.
                  const _at = absTapRef.current
                  absTapRef.current = null
                  const _lim = _at?.onAbs ? 600 : 350
                  const _dur = _at ? e.timeStamp - _at.t : null
                  const _mv = _at ? Math.hypot(e.clientX - _at.x, e.clientY - _at.y) : null
                  const _why = !_at ? 'no press tracked'
                    : _at.id !== e.pointerId ? 'different pointer'
                    : !(_dur < _lim) ? `held too long (${Math.round(_dur)}ms >= ${_lim}ms)`
                    : !(_mv < 6) ? `moved ${_mv.toFixed(1)}px (>= 6)`
                    : lpClaimRef.current ? 'claimed by long-press'
                    : interaction.draggingItemRef.current ? 'claimed by line drag'
                    : trend.modeRef.current === 'phonePlacing' ? 'claimed by trendline placement'
                    : null
                  if (dbgOn()) dbg('[Absorbers] up ' + JSON.stringify({ durMs: _dur != null ? Math.round(_dur) : null, limitMs: _lim, movePx: _mv != null ? +_mv.toFixed(1) : null,
                    onOutline: _at?.onAbs ?? null, callsOpen: !_why, why: _why }))
                  if (!_why) {
                    tryOpenAbsorbers(e.clientX, e.clientY)
                  }
                }
                if (trend.handlePointerUp() && drawCanvasRef.current) drawCanvasRef.current()
                priceAlerts.handlePointerUp(e)
                crosshairArmedRef.current = false
                // MODE persists across lifts — crosshair stays until tap-exit.
                if (!crosshairModeRef.current) updateCrosshair(-1)
              }}
              onPointerCancel={(e) => {
                absTapRef.current = null
                priceAlerts.handlePointerUp(e)
                crosshairArmedRef.current = false
                if (!crosshairModeRef.current) updateCrosshair(-1)
              }}
              onTouchStart={handleChartTouchStart}
              onTouchMove={handleChartTouchMove}
              onTouchEnd={handleChartTouchEndForDblTap}
              onMouseMove={handleBodyMouseMove}
              onMouseEnter={interaction.handleChartMouseEnter}
              onMouseLeave={(e) => { interaction.handleChartMouseLeave(e); if (cursorLabelRef.current) cursorLabelRef.current.style.display = 'none'; placeXCrossLabels(-1); if (hHighlightRef.current) hHighlightRef.current.style.display = 'none' }}
              onContextMenu={handleChartContextMenu}
            >
              {/* Y-Axis region */}
              <div className="y-axis-region" style={{ width: axisColW, flexShrink: 0 }}>
                {/* Price alert lines — DOM overlay so we don't fight the
                    canvas render loop. Same row-index math as position
                    tags. Off-viewport alerts clamp to top/bottom edge. */}
                <div className="alert-lines-layer" ref={alertLayerRefCb} style={{ position: 'absolute', top: 0, left: 0, right: 0, pointerEvents: 'none', zIndex: 4 }}>
                  {priceAlerts.alertsForCurrentToken.map(a => {
                    if (visibleSortedMcaps.length === 0) return null
                    // While this alert is being dragged, preview at the drag
                    // mcap; otherwise use its stored mcap.
                    const dragging = alertDrag && alertDrag.id === a.id
                    const effMcap = dragging ? alertDrag.mcap : a.mcap
                    // Bounds MUST come from the same lattice as the y above.
                    // priceToY reads the PAINTED frame; pairing it with React's
                    // row height and row count meant that during any freeze or
                    // zoom ease the clamp stopped bounding the value, and a tag
                    // could be placed far outside the layer — which grows the
                    // scroll container and jumps the chart.
                    const _fA = lastFrameRef.current
                    const RHa = (_fA && _fA.RH > 0) ? _fA.RH : dynamicRowHeight
                    const nA = (_fA && _fA.mcaps) ? _fA.mcaps.length : visibleSortedMcaps.length
                    let yA = priceToY(effMcap)
                    let offScreen = null
                    if (!Number.isFinite(yA) || yA < 0) { yA = RHa / 2; offScreen = 'above' }
                    else if (yA > nA * RHa) { yA = (nA - 0.5) * RHa; offScreen = 'below' }
                    // While dragging, ride the raw cursor pixel Y so the tag
                    // GLIDES like the crosshair; otherwise sit on the exact price.
                    const top = (dragging && Number.isFinite(alertDrag.pixelY))
                      ? alertDrag.pixelY
                      : yA
                    const color = a.direction === 'above' ? '#22c55e' : '#ef4444'
                    const valueText = dataSource === 'hyperliquid'
                      ? formatPrice(effMcap)
                      : Math.round(effMcap).toLocaleString('en-US')
                    return (
                      <div key={a.id}
                        data-ytag="a" data-ym={effMcap} data-yoff={offScreen || ''}
                        data-ydrag={(dragging && Number.isFinite(alertDrag.pixelY)) ? '1' : undefined}
                        style={{
                        // No borderTop: the dashed rule is gone by request. The
                        // div stays as the positioning anchor for the tag — it
                        // is what carries `top` — so it keeps zero height and
                        // simply draws nothing.
                        // (perf2) Positioned by transform (compositor), top pinned
                        // at 0. Drag still rides alertDrag.pixelY through `top`
                        // the variable, not the property.
                        position: 'absolute', left: 0, right: 0, top: 0,
                        transform: yTagTf(top),
                        height: 0,
                        opacity: offScreen ? 0.35 : (dragging ? 1 : 0.7),
                      }}>
                        {/* Draggable tag. Drag vertically to move the alert
                            price; the X cancels. Triangles blink while dragging. */}
                        <div style={{
                          // left:0 is the y-axis's LEFT edge: .y-axis-region is
                          // position:sticky, so it — not the chart wrapper — is
                          // this layer's containing block. Group reads
                          // value → bell → ×, so the bell is the middle element
                          // and the × keeps the outer slot it already had.
                          // Shifted left by one × (glyph ~7px + 10px padding +
                          // 4px gap ≈ 22) so the × clears the axis onto the
                          // chart and the bell lands against the axis's inside
                          // left edge. This is the nudge knob: more negative
                          // moves the whole group further onto the chart.
                          position: 'absolute', left: -22, top: 0,
                          // Centered on the alert price — same as position tags.
                          transform: 'translateY(-50%)',
                          display: 'flex', alignItems: 'center', gap: 4,
                          pointerEvents: 'auto',
                        }}>
                          {/* blink triangles (only while dragging this tag) */}
                          {dragging && (
                            <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 0 }}>
                              <span className="alert-tag-tri" style={{
                                width: 0, height: 0, borderLeft: '4px solid transparent',
                                borderRight: '4px solid transparent', borderBottom: `5px solid ${color}`, marginBottom: 1,
                              }} />
                              <span className="alert-tag-tri" style={{
                                width: 0, height: 0, borderLeft: '4px solid transparent',
                                borderRight: '4px solid transparent', borderTop: `5px solid ${color}`,
                              }} />
                            </span>
                          )}
                          <span
                            onPointerDown={(e) => beginAlertDrag(a, e)}
                            title={`Alert ${a.direction} ${valueText}${offScreen ? ` — off screen ${offScreen}` : ''}\nDrag to move`}
                            style={{
                              fontSize: 12, background: '#363636',
                              padding: '0 4px', borderRadius: 2,
                              cursor: dragging ? 'grabbing' : 'grab',
                              fontFamily: 'monospace', userSelect: 'none', touchAction: 'none',
                              display: 'inline-flex', alignItems: 'center', gap: 3,
                            }}>
                            {/* Just the bell. The dashed line carries the level,
                                its colour carries the direction, and the y-axis
                                is right there for the number — printing the mcap
                                on the tag repeated what the position already
                                said. Exact value moved to the tooltip.

                                GREYED WITH filter, NOT color. U+1F514 is
                                Emoji_Presentation=Yes, so it renders from the
                                colour font and ignores `color` outright — the
                                previous `color: '#9aa0a6'` here was a no-op and
                                the bell stayed gold on every platform. grayscale
                                desaturates the glyph itself; brightness lifts it
                                off the #363636 chip. Tune brightness to taste —
                                1 is mid-grey, higher is lighter. */}
                            <span style={{ filter: 'grayscale(1) brightness(1.25)' }}>🔔</span>
                            {offScreen && (
                              // The one case position CANNOT convey: the tag is
                              // clamped to the viewport edge, so without this an
                              // off-screen alert is indistinguishable from one
                              // sitting exactly at the edge.
                              <span style={{ color: '#9aa0a6', fontSize: 10 }}>{offScreen === 'above' ? '↑' : '↓'}</span>
                            )}
                          </span>
                          {/* Cancel LAST in flex order — lands chart-side of the chip. */}
                          <span
                            data-xh="alert-cancel"
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); priceAlerts.removeAlert(a.id) }}
                            title="Cancel alert"
                            style={{
                              fontSize: 12, color: '#aaa', background: '#363636',
                              padding: '0 5px', borderRadius: 2, cursor: 'pointer',
                              fontFamily: 'monospace', userSelect: 'none', fontWeight: 700,
                            }}>×</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
                {/* Position tags */}
                <div className="position-tags-layer" ref={posTagsLayerRefCb}>
                  {renderedPositions.filter(pos =>
                    // Each chart renders its OWN token's positions: the armed chart from
                    // the live shared context, a non-armed chart from its own-token
                    // localStorage load (renderedPositions). The token check stays as a
                    // safety so a stale entry can never cross to the other chart.
                    pos.futuresPosition ? pos.token === hlPair?.base : pos.token === data.currentToken
                  ).map((pos) => {
                    // Defensive: coerce to number, reject obviously bad entries
                    // positions-20261017: Solana entry is stored as SOL per token;
                    // the chart's real supply turns it into mcap here.
                    const _supNow = pos.futuresPosition ? 0 : (Number(data.tokenSupplyRef?.current) || 0)
                    const _usePs = !pos.futuresPosition && Number(pos.entryPriceSol) > 0 && _supNow > 0
                    const entryMcapNum = _usePs ? Number(pos.entryPriceSol) * _supNow : Number(pos.entryMcap)
                    if (!Number.isFinite(entryMcapNum) || entryMcapNum <= 0) {
                      console.warn('[position-tag] rejecting pos with bad entryMcap', { id: pos.id, entryMcap: pos.entryMcap, type: typeof pos.entryMcap, pos })
                      return null
                    }
                    if (visibleSortedMcaps.length === 0) return null
                    // Bounds MUST come from the same lattice as the y above.
                    // priceToY reads the PAINTED frame; pairing it with React's
                    // row height and row count meant that during any freeze or
                    // zoom ease the clamp stopped bounding the value, and a tag
                    // could be placed far outside the layer — which grows the
                    // scroll container and jumps the chart.
                    const _fP = lastFrameRef.current
                    const RHp = (_fP && _fP.RH > 0) ? _fP.RH : dynamicRowHeight
                    const nP = (_fP && _fP.mcaps) ? _fP.mcaps.length : visibleSortedMcaps.length
                    let yP = priceToY(entryMcapNum)
                    let offScreen = null  // null | 'above' | 'below'
                    const _pb = posTagBounds(RHp, nP)
                    if (!Number.isFinite(yP) || yP < _pb[0]) { yP = _pb[0]; offScreen = 'above' }
                    else if (yP > _pb[1]) { yP = _pb[1]; offScreen = 'below' }
                    const currentMcap = chartData.highlightMcap || data.lastBucketMcapRef?.current || pos.entryMcap

                    let pnlUsd, pnlPositive
                    if (pos.futuresPosition) {
                      // Futures P/L — shared helper (identical math to WalletHoldings)
                      const { pnlUsd: fp } = futuresPositionPnl({
                        entryPx: pos.avgOpenPrice || pos.entryMcap,
                        currentPx: currentMcap,
                        qty: pos.tokenAmount,
                        side: pos.side,
                        fallbackUsd: pos.unrealizedPNL,
                      })
                      pnlUsd = fp || 0
                      pnlPositive = pnlUsd >= 0
                    } else if (pos.paper) {
                      // paper P/L, straight trade, no fees (same as the chip).
                      const po = paperOpenPnl(_paperPos, data.rawTradesRef?.current?.lastMcapOf?.('sell') || currentMcap)   // marked at the bid
                      pnlUsd = po ? po.pnl * (Number(data.solPriceRef.current) || 0) : 0
                      pnlPositive = pnlUsd >= 0
                    } else {
                      // Solana P/L — shared helper (SOL-based, current SOL price).
                      // Single source of truth: WalletHoldings calls the same fn.
                      const supply = _usePs ? _supNow : (pos.tokenSupply > 0 ? pos.tokenSupply : data.tokenSupplyRef.current)
                      const { pnlUsd: sp } = solPositionPnl({
                        entryMcap: entryMcapNum,
                        currentMcap,
                        tokenAmount: pos.tokenAmount,
                        tokenSupply: supply,
                        solPriceUsd: data.solPriceRef.current,
                      })
                      pnlUsd = sp || 0
                      pnlPositive = pnlUsd >= 0
                    }

                    const sideLabel = pos.futuresPosition
                      ? (pos.side === 'SHORT' ? 'S' : 'L')
                      : null

                    return (
                      <div key={pos.id} data-ytag="p" data-ym={entryMcapNum} data-yoff={offScreen || ''} className={`position-tag${offScreen ? ' position-tag-offscreen-' + offScreen : ''}`} style={{ top: 0, transform: yTagTfC(yP) }}>
                        <button className="position-tag-close" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); trading.handleClosePosition(pos) }}>X</button>
                        {offScreen === 'above' && <span className="position-tag-arrow">▲</span>}
                        {offScreen === 'below' && <span className="position-tag-arrow">▼</span>}
                        {sideLabel && <span className={`position-tag-side ${pos.side === 'SHORT' ? 'short' : 'long'}`}>{sideLabel}</span>}
                        {pos.futuresPosition && pos.leverage && <span style={{ color: '#888', fontSize: '10px' }}>{pos.leverage}x</span>}
                        {pos.detected && <span className="position-tag-detected" title="Entry detected from on-chain history">@</span>}
                        <span className="position-tag-amount">{formatCompact(pos.tokenAmount)}{pos.futuresPosition && hlPair ? ' ' + hlPair.base : ''}</span>
                        <span className={`position-tag-pnl ${pnlPositive ? 'positive' : 'negative'}`}>
                          ${Math.abs(pnlUsd) >= 1000 ? (Math.abs(pnlUsd) / 1000).toFixed(1) + 'K' : Math.abs(pnlUsd).toFixed(2)}{pnlPositive ? '+' : pnlUsd < 0 ? '-' : ''}
                        </span>
                      </div>
                    )
                  })}
                  {_cmePos && _cmePos.qty > 0 && _cmePos.avg > 0 && visibleSortedMcaps.length > 0 && (() => {
                    // cmetags-20261101: CME paper position - same tag as HL futures.
                    const _fC = lastFrameRef.current
                    const RHc = (_fC && _fC.RH > 0) ? _fC.RH : dynamicRowHeight
                    const nC = (_fC && _fC.mcaps) ? _fC.mcaps.length : visibleSortedMcaps.length
                    let yC = priceToY(_cmePos.avg)
                    let offC = null
                    const _pbC = posTagBounds(RHc, nC)
                    if (!Number.isFinite(yC) || yC < _pbC[0]) { yC = _pbC[0]; offC = 'above' }
                    else if (yC > _pbC[1]) { yC = _pbC[1]; offC = 'below' }
                    const pxC = Number(chartData.highlightMcap || data.lastBucketMcapRef?.current) || _cmePos.avg
                    const pnlC = cmeUnrealized({ ..._cmePos, sym: _cmePos.sym || _cmeRoot }, pxC) || 0
                    const posC = pnlC >= 0
                    const shortC = _cmePos.side === 'SHORT'
                    return (
                      <div key={`cmepaper:${_cmeRoot}`} data-ytag="p" data-ym={_cmePos.avg} data-yoff={offC || ''} className={`position-tag${offC ? ' position-tag-offscreen-' + offC : ''}`} style={{ top: 0, transform: yTagTfC(yC) }} title={`PAPER ${_cmePos.side} ${_cmePos.qty} ${_cmeRoot} @ ${_cmePos.avg}`}>
                        <button className="position-tag-close" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); try { cmeFlatten(_cmeRoot, pxC) } catch (err) { console.warn('[cme-paper] flatten failed', err) } }}>X</button>
                        {offC === 'above' && <span className="position-tag-arrow">▲</span>}
                        {offC === 'below' && <span className="position-tag-arrow">▼</span>}
                        <span style={{ color: '#888', fontSize: '9px', letterSpacing: '0.5px', opacity: 0.8 }}>PAPER</span>
                        <span className="position-tag-amount">{_cmePos.qty} {_cmeRoot}</span>
                        <span style={{ position: 'relative', alignSelf: 'stretch', display: 'flex', alignItems: 'center' }}>
                        <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', ...(shortC ? { top: '100%', marginTop: '1px' } : { bottom: '100%', marginBottom: '1px' }), color: shortC ? '#ef5350' : '#26a69a', fontSize: '8px', lineHeight: 1, pointerEvents: 'none' }}>{shortC ? '▼' : '▲'}</span>
                        <span className={`position-tag-pnl ${posC ? 'positive' : 'negative'}`}>
                          ${Math.abs(pnlC) >= 1000 ? (Math.abs(pnlC) / 1000).toFixed(1) + 'K' : Math.abs(pnlC).toFixed(2)}{posC ? '+' : '-'}
                        </span>
                        </span>
                      </div>
                    )
                  })()}
                </div>
                {/* Limit order tags */}
                <div className="limit-order-tags-layer" ref={limitTagsLayerRefCb}>
                  {trading.limitOrders.filter(lo =>
                    // Same as position tags: limit-order tags belong to the armed chart.
                    isActiveChart && (lo.futuresOrder ? (lo.symbol || lo.token) === hlPair?.base : lo.token === data.currentToken)
                  ).map((lo) => {
                    if (visibleSortedMcaps.length === 0) return null
                    // Bounds MUST come from the same lattice as the y above.
                    // priceToY reads the PAINTED frame; pairing it with React's
                    // row height and row count meant that during any freeze or
                    // zoom ease the clamp stopped bounding the value, and a tag
                    // could be placed far outside the layer — which grows the
                    // scroll container and jumps the chart.
                    const _fL = lastFrameRef.current
                    const RHl = (_fL && _fL.RH > 0) ? _fL.RH : dynamicRowHeight
                    const nL = (_fL && _fL.mcaps) ? _fL.mcaps.length : visibleSortedMcaps.length
                    let yL = priceToY(lo.targetMcap)
                    if (!Number.isFinite(yL)) return null
                    if (yL < 0) yL = RHl / 2
                    else if (yL > nL * RHl) yL = (nL - 0.5) * RHl
                    return (
                      <div key={lo.id} data-ytag="l" data-ym={lo.targetMcap} className={`limit-order-tag ${lo.side}`} style={{ top: 0, transform: yTagTfC(yL), cursor: 'grab' }}
                        onPointerDown={(e) => handleLimitDragStart(e, lo)}>
                        <button className="limit-order-tag-close" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (dataSource === 'hyperliquid' && hlWallet.isLocked) { showToast('Connect first', 'sell'); return }; trading.handleCancelLimitOrder(lo) }}>X</button>
                        <span className="limit-order-tag-side">{lo.side.toUpperCase()}</span>
                        <span className="limit-order-tag-mcap">{formatYAxis(lo.targetMcap)}</span>
                        <span className="limit-order-tag-amount">{lo.amount ? `${lo.amount} ${dataSource === 'hyperliquid' && hlPair ? hlPair.base : 'SOL'}` : `${lo.percentage}%`}</span>
                      </div>
                    )
                  })}
                </div>

                {/* Y-Axis */}
                <div className="y-axis-body" ref={yAxisBodyRefCb}
                  style={{ width: axisColW, minWidth: axisColW, touchAction: 'none' }}
                  // (y1) height is written by drawCanvas from the painted frame.
                  // ^ Must match canvasHeight, NOT containerHeight. Canvas
                  // renders visibleSortedMcaps.length rows (= visibleCount
                  // + 1, the +1 being the bottom-pad row used for sub-bucket
                  // y-residual scrolling). Without an explicit height the
                  // y-axis-body falls back to its parent's container height
                  // — sized to visibleCount rows, not length — so the canvas
                  // paints a bottom-pad row with no matching y-axis label.
                  // Matching canvasHeight (same as alert-lines-layer,
                  // position-tags-layer, limit-order-tags-layer, the canvas
                  // itself) keeps the y-axis aligned through the full
                  // rendered area including the bottom-pad row.
                  onMouseMove={(e) => {
                    // Coarse-pointer device (phone/tablet): the desktop hover
                    // magnifier NEVER runs — the long-press magnifier owns the
                    // axis. Hardware check, so it holds even when the browser
                    // (Samsung Internet, desktop-site mode) mislabels touch as
                    // mouse and the timestamp guard below never got stamped.
                    if (COARSE_POINTER) return
                    // Suppress the DESKTOP (mouse-driven) magnifier when the
                    // event is a synthetic mouse event the browser fired from a
                    // touch — otherwise a phone finger spawns BOTH the desktop
                    // tooltip and the touch one (the "double magnifier"). Any
                    // recent y-axis touch, or an active phone magnifier, means
                    // this mousemove is touch-derived; skip it. Real desktop
                    // mice never set the touch stamp, so they're unaffected.
                    if (phoneMagActiveRef.current || (Date.now() - yAxisTouchTsRef.current) < 700) return
                    // HOVER NO LONGER DRIVES THE MAGNIFIER. Desktop now uses
                    // the same contract as touch: the magnifier exists only
                    // while a long-press is armed. During that hold, movement
                    // is fed by the press machinery's window pointermove via
                    // feedDesktopMagnifier — not by this handler — so gating
                    // here removes hover-summoning without starving the held
                    // state. This also gates the hover band/row highlight,
                    // which is part of the same hook path: the axis is now
                    // visually inert until you commit to a hold.
                    if (!magPressRef.current?.armed) return
                    interaction.handleYAxisMouseMove(e)
                    // ROW HOVER, folded in. Was onMouseEnter per row; there are no rows.
                    // Same gate as the enters had (COARSE_POINTER / armed — both already
                    // returned above), fired only when the row under the pointer changes:
                    // handleRowHover REPLACES hoverState, so change-only is the exact
                    // end state the old leave→enter pair produced.
                    const hit = axisRowAt(e)
                    const rowNow = hit ? hit.i : -1
                    if (rowNow !== yAxisHoverRowRef.current) {
                      yAxisHoverRowRef.current = rowNow
                      if (hit) interaction.handleRowHover(hit.mcap)
                      else interaction.handleRowLeave()
                    }
                  }} onMouseLeave={(e) => {
                    interaction.handleYAxisMouseLeave(e)
                    // The rows' own leave, ungated ("so any residue always clears").
                    yAxisHoverRowRef.current = -1
                    if (!COARSE_POINTER) interaction.handleRowLeave(e)
                  }}
                  onPointerDown={(e) => {
                    // Was .y-axis-click-overlay per row. The handler reads only
                    // e.clientY, never the row element, so it moves unchanged.
                    const hit = axisRowAt(e)
                    if (hit) handleYAxisRowPointerDown(hit.mcap, e)
                  }}
                  onContextMenu={(e) => {
                    // The order path, VERBATIM from the row overlay — only the
                    // source of `mcap` / `isCurrent` changed (hit-test, not closure).
                    const hit = axisRowAt(e)
                    if (!hit) return
                    const { mcap, isCurrent } = hit
                  e.preventDefault(); e.stopPropagation()
                  // Phone: native long-press fires contextmenu, which
                  // would collide with the magnifier gesture. Touch
                  // presses own the axis — bail so only the magnifier
                  // (release-to-place) handles touch.
                  if (yAxisTouchRef.current) return
                  const preciseMcap = interaction.magnifierMcapRef.current || mcap
                  if (isCurrent) return
                  if (dataSource === 'hyperliquid' && hlWallet.isLocked) { showToast('Connect first', 'sell'); return }
                  if (dataSource === 'solana' && !publicKey) { showToast('Connect wallet first', 'sell'); return }

                  if (dataSource === 'hyperliquid') {
                    // ONE_WAY: right-above = limit sell (direct, no modal), right-below = market sell
                    if (preciseMcap > chartData.highlightMcap) trading.placeLimitDirect('sell', preciseMcap)
                    else trading.executeMarketOrder('sell').catch(() => {})
                    return
                  }

                  // Solana: original
                  if (preciseMcap > chartData.highlightMcap) trading.setOrderModal({ show: true, side: 'sell', mcap: preciseMcap })
                  else trading.executeMarketOrder('sell').catch(() => {})
                  }}>
                  {/* The axis surface. Absolute within .y-axis-body — the
                      body is transformed every frame (residual), which makes
                      it a containing block, so this rides that transform with
                      no second alignment term. z7 clears the rows (z6) so
                      nothing a row paints can cover a label; pointer-events:
                      none so every click and hover still lands on the row
                      hit-targets beneath. Bitmap is sized by the painter
                      effect in device pixels; the CSS size is the box. */}
                  <canvas ref={yAxisCanvasRef} className="y-axis-canvas" aria-hidden="true"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                             pointerEvents: 'none', zIndex: 7 }} />
                  {/* No rows. The surface is the body itself: hover, press and
                      contextmenu above hit-test y → row → mcap via axisRowAt. */}
                </div>
                {/* Current-price chip OVERLAY — escapes the dial fade.
                    The in-body .current row is trapped in y-axis-body's
                    stacking context (the residual transform creates one),
                    so the fade strips (z 3) always paint over it near the
                    edges. This sibling renders the same chip at z 5 —
                    above the strips, below position/limit tags (40) —
                    using the SAME bucketRowIndex mapping and the same
                    --chart-y-residual translate as the other tag layers
                    (shared CSS rule). pointer-events:none so hover/clicks
                    fall through to the real row beneath. */}
                {/* Magnifier (limit-order style). Replaces the old fixed
                    orange .y-axis-magnifier-tooltip. Line across the plot,
                    solid value chip ON the y-axis column — same red/green as
                    limit-order tags (sell above live, buy below). */}
                {interaction.magnifier.visible && visibleSortedMcaps.length > 0 && (() => {
                  const m = interaction.magnifier.mcap
                  const live = chartData.highlightMcap
                  if (!(m > 0)) return null
                  // Same ladder rule as handleRowClick: above = sell, below = buy.
                  const side = (live > 0 && m > live) ? 'sell' : 'buy'
                  // Same lattice for the value and its bounds (see the tag
                  // layers above) — a clamp read off a different lattice than
                  // the y it clamps is not a clamp.
                  const _fM = lastFrameRef.current
                  const RH = (_fM && _fM.RH > 0) ? _fM.RH : dynamicRowHeight
                  let y = priceToY(m)
                  if (!Number.isFinite(y)) return null
                  const n = (_fM && _fM.mcaps) ? _fM.mcaps.length : visibleSortedMcaps.length
                  if (y < 0) y = RH / 2
                  else if (y > n * RH) y = (n - 0.5) * RH
                  return (
                    <div className="y-axis-mag-marker-layer" ref={magLayerRefCb} aria-hidden>
                      <div data-ytag="l" data-ym={m} className={`y-axis-mag-marker ${side}`} style={{ top: 0, transform: yTagTfC(y) }}>
                        <div className="y-axis-mag-marker-line" />
                        <div className="y-axis-mag-marker-wall" />
                        <div className="y-axis-mag-marker-value">{formatYAxis(m)}</div>
                      </div>
                    </div>
                  )
                })()}
                {(() => {
                  const RHv = dynamicRowHeight
                  // Same price the live body tip / dashed line use (ref stamped
                  // in drawCanvas). Fallback to highlight before first draw.
                  const priceY = (livePriceForYRef.current > 0)
                    ? livePriceForYRef.current
                    : chartData.highlightMcap
                  // POSITION IS THE CANVAS'S JOB (drawCanvas chip block).
                  // JSX mounts and writes text only — no style.top here.
                  // Same lattice for the value and the visibility test. This
                  // one only decides whether the chip MOUNTS (the canvas sets
                  // its top), so a mismatch unmounted it mid-freeze.
                  const _fC = lastFrameRef.current
                  const _rh = (_fC && _fC.RH > 0) ? _fC.RH : RHv
                  const _n  = (_fC && _fC.mcaps) ? _fC.mcaps.length : visibleSortedMcaps.length
                  const y = priceToY(priceY)
                  if (!(priceY > 0) || !Number.isFinite(y) || y < -_rh || y > _n * _rh + _rh) return null
                  const chipH = Math.max(RHv, 20)
                  return (
                    <div className="y-axis-current-overlay" ref={currentChipRef}
                      // Full column width (matches the y-axis body), text
                      // centered like the rows. width:auto + minWidth keeps
                      // the TradingView overhang: chip text wider than the
                      // column grows leftward over the plot instead of
                      // resizing the axis.
                      style={{ height: `${chipH}px`, right: 0, left: 'auto', width: 'auto', minWidth: yAxisWidth, whiteSpace: 'nowrap' }}>
                      {/* TEXT stays last-trade (highlight). POSITION uses the
                          shared livePriceForY so the chip center matches the
                          candle body tip / dashed line on LOG futures. */}
                      {formatYAxis(chartData.highlightMcap > 0 ? chartData.highlightMcap : priceY)}
                    </div>
                  )
                })()}
              </div>

              {/* DLMM bin liquidity bars — chart-area layer, sits BEHIND the
                  volume profile (rendered just before it in DOM order, lower
                  in z-stack). Bars grow leftward from the right edge of the
                  plot area, width = log-normalized intensity vs the largest
                  visible bin. pointerEvents: none so chart interactions work. */}
              {dataSource === 'solana' && showMeteora && meteoraLiquidity.bins && meteoraLiquidity.bins.length > 0 && visibleSortedMcaps.length > 0 && (() => {
                const rowTotals = new Map()
                let maxTotal = 0
                for (const b of meteoraLiquidity.bins) {
                  const bucketMcap = snapBucket(b.mcap, mcapBucketSize)
                  const prev = rowTotals.get(bucketMcap) || 0
                  const next = prev + b.notionalSol
                  rowTotals.set(bucketMcap, next)
                  if (next > maxTotal) maxTotal = next
                }
                if (maxTotal <= 0) return null
                const logMax = Math.log1p(maxTotal)
                const topMcap    = visibleSortedMcaps[0]
                const bottomMcap = visibleSortedMcaps[visibleSortedMcaps.length - 1]
                const BAR_MAX_WIDTH = 240   // px — max leftward extent of the longest bar
                const out = []
                for (const [bucketMcap, total] of rowTotals) {
                  if (bucketMcap > topMcap || bucketMcap < bottomMcap) continue
                  const idx = findBucketIndex(visibleSortedMcaps, bucketMcap, mcapBucketSize)
                  if (idx === -1) continue
                  const intensity = Math.min(1, Math.log1p(total) / logMax)
                  const widthPx   = Math.max(2, intensity * BAR_MAX_WIDTH)
                  const alpha     = 0.05 + intensity * 0.85
                  out.push(
                    <div key={bucketMcap} data-yi={idx}
                      className="dlmm-bar"
                      style={{
                        position: 'absolute',
                        right: 0,
                        // (perf2) 1px box translated + scaled to the row: no
                        // top/height (layout) writes when RH moves.
                        top: 0,
                        height: '1px',
                        transformOrigin: '0 0',
                        transform: yBarTf(idx * dynamicRowHeight, dynamicRowHeight),
                        width: `${widthPx}px`,
                        background: `rgba(90, 100, 130, ${alpha})`,
                        pointerEvents: 'none',
                      }} />
                  )
                }
                return (
                  <div className="dlmm-bars-layer" ref={dlmmLayerRefCb}
                    style={{
                      position: 'absolute',
                      top: 0, right: `${profileRightPx}px`,   // same glyph-edge anchor as the profile
                      width: '100%',
                      pointerEvents: 'none',   // (y1) height written by drawCanvas
                    }}>
                    {out}
                  </div>
                )
              })()}

              {/* Volume Profile — canvas layer; bars painted in drawCanvas
                  ("Volume profile layer" block). Sizing is owned by the draw
                  (same as the grid layer); the right edge anchors at the same
                  profileRightPx line the old DOM layer used, so right-aligned
                  bars land on identical pixels. z0 + tRegion transform
                  unchanged. */}
              {/* Same reason as the volume lane: the in-draw clearRect only
                  fires if a full draw happens, and a blank load triggers
                  none — so the previous token's profile persisted. Unmount
                  instead of clear. */}
              {hasAxisValues && showVolumeProfile && (
                <canvas ref={volumeProfileLayerRef} className="volume-profile-layer-canvas" style={{
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  // RIGHT anchors at profileRightPx — clear of the widest axis
                  // label. LEFT anchors at 0, the chart's own edge, where
                  // there is nothing to clear. Only one of the two is ever
                  // set: leaving both would stretch the canvas across the
                  // whole chart and the bar math assumes a VP_W-wide box.
                  ...(vaConfig.vpSide === 'left'
                    ? { left: 0 }
                    : { right: `${profileRightPx}px` }),
                  pointerEvents: 'none',
                  zIndex: 0,
                }} />
              )}

              {/* Smoke Trail — mounted INSIDE the scroll container, like the
                  canvas, so it inherits the footprint's clipping and
                  coordinate space (scrolls under the y-axis, clipped at the
                  chart-area boundary — no width math, no z-fighting). */}
              {/* Heat under footprint: LiquidityHeatLayer zIndex 0; cell plates
                  z0; trail z1; footprint GL z2 / main z2.5 / live z3.
                  Plates under trail so smoke runs between bg and cells. */}
              {(showBookHeatLive || showCbHeatLive) && dataSource === 'hyperliquid' && (() => {
                const liveEdge = containerWidthRef.current - RIGHT_PADDING - FIXED_OVERLAY_WIDTH
                // X CAMERA FOR HEAT. Mid X-glide the canvas (and the trail)
                // stretch the mesh by frame.camScaleX about frame.camAx; heat
                // read the raw frame.xLeft and ignored it, so it sat up to a
                // column off and stepped a whole candle at a time. Wheel glides
                // land on whole candles and the camera switches off in a few
                // frames; a footer drag holds a fractional window with the
                // camera on for the whole drag, so there the gap stayed visible.
                // Same transform as the canvas, from the same painted frame.
                // Identity at rest (scale 1).
                const heatCam = (g) => {
                  const k = (g && g.camScaleX > 0) ? g.camScaleX : 1
                  const ax = (g && Number.isFinite(g.camAx)) ? g.camAx : 0
                  return { k, x: (px) => ax + (px - ax) * k }
                }
                const getGeom = () => ({
                  // PAINTED row height, matching the pinPx published below
                  // (lastStyleRef, written by drawCanvas from the committed
                  // residual). Reading the live ref here paired a live RH
                  // with a committed translate for the length of a freeze.
                  rowHeight: lastFrameRef.current?.RH || dynamicRowHeightRef.current,
                  // The chart's own row membership — same frame as rowHeight and
                  // latticeId, so heat rows are the cells' rows. No `this` in
                  // rowOfPrice, so it is safe to hand over unbound.
                  rowOfPrice: lastFrameRef.current ? lastFrameRef.current.proj.rowOfPrice : null,
                  // Row step of that same projection (working space). Heat keys
                  // its cached rows on it: same step ⇒ a lattice change is a
                  // pure integer row shift it can adopt in place; a new step
                  // (rebucket / refine) ⇒ re-project.
                  rowStep: lastFrameRef.current ? lastFrameRef.current.proj.step : null,
                  // Paint camera — applied at row→pixel, the same place the
                  // canvas applies it in gctx.transform. Identity when idle.
                  camScale: cameraRef.current.scale,
                  camTy: cameraRef.current.ty,
                  // X ease SoT: FROM freeze + scaleX about ax (same as canvas).
                  // cols/xOfCol below publish VISUAL (scaled) coords so heat
                  // tracks the chart without reading live win membership.
                  camScaleX: cameraRef.current.scaleX,
                  camAx: cameraRef.current.ax,
                  // Owner id of the lattice array (minted in the grid memo's
                  // stability cache / buildZoomDestMcaps). Heat caches its
                  // price→row projection per flow entry and invalidates on
                  // this — profiled at 51% of frame time re-projecting every
                  // entry every paint. Same cached prev → same id → no idle
                  // flip; structural rebuild / dest mint → new id.
                  latticeId: (() => {
                    const m = lastFrameRef.current?.mcaps || visibleSortedMcapsRef.current
                    if (!m || !m.length) return null
                    // Fallback for foreign arrays (e.g. chartData.sortedMcaps
                    // early return) that heat may briefly read — stamp once
                    // at publish, do not force ids on every early return.
                    if (m.latticeId == null) m.latticeId = ++latticeIdSeqRef.current
                    return m.latticeId
                  })(),
                  // cw in the SAME atomic read as rowHeight — a zoom frame
                  // must not pair a new column width with a stale row height.
                  // Sizes the ONE-COLUMN flow strip (layer's pxPerMs note).
                  // Painted cw, not a re-derived one — otherwise the strip
                  // can be a notch wide while the column beneath it is not.
                  cw: (lastFrameRef.current?.cw && lastFrameRef.current.cw * heatCam(lastFrameRef.current).k)
                    || (Math.max(50, containerWidthRef.current - RIGHT_PADDING - FIXED_OVERLAY_WIDTH)
                        / Math.max(1, viewWindowSizeRef.current)),
                  // ── COLUMN GEOMETRY (merge step 1) ──
                  // The history heat is moving into the layer, and it needs
                  // the column x-mapping + each column's slot time. Published
                  // HERE rather than re-derived there: one mapping, one owner
                  // — the same rule getSeamX and priceToY already follow, and
                  // the reason the x-seam saga ended. Recomputed from refs on
                  // every call, exactly like the seam, so a 30fps layer paint
                  // between chart draws still reads current values.
                  cols: (() => {
                    const cd = chartDataRef.current
                    const cs = cd?.candles
                    if (!cs || cs.length === 0) return null
                    // FLOAT WIDTH, INT INDEX — drawCanvas's own split, verbatim
                    // (see its winSizeF/winSize pair: "Float win only drives
                    // candle WIDTH (pxPerCandle). Index loops MUST use the
                    // rounded int — candles[100.7] is undefined").
                    //
                    // This published startIdx IS an index: LiquidityHeatLayer's
                    // history pass seeds `colTime(cols.startIdx)` and then walks
                    // `for (let i = cols.startIdx; i <= lastI; i++)`. Mid x-zoom
                    // ease, applyXAnchor writes the FLOAT ease value into
                    // viewWindowSizeRef, so `eIdx - win` came out fractional and
                    // colTime's `(i >= 0 && i < tot)` guard passed on 152.68 —
                    // then `cs[152.68].startTime` threw, INSIDE the history
                    // block, AFTER its clearRect + save/clip. Region cleared,
                    // nothing painted back, restore() never reached: the heat
                    // map vanished on every wheel zoom and the leaked clip rode
                    // into later frames. Chart pan never touches
                    // viewWindowSizeRef, which is why pan alone was clean.
                    //
                    // Rounding does NOT move any column: vis - (i - sIdx)
                    // reduces to eIdx - i + 1, so sIdx cancels out of xOfCol
                    // entirely. It only makes the index an index again — and
                    // takes the raw float out of the layer's histKey, which
                    // could never report clean while it changed every tick.
                    //
                    // NOT re-derived from refs. This is the geometry the chart
                    // painted this frame; sIdx is therefore guaranteed integer
                    // and in range, which is what the walk below needs.
                    const g = lastFrameRef.current
                    if (!g) return null
                    const cam = heatCam(g)
                    const cwv = g.cw * cam.k
                    const tot = cs.length
                    const eIdx = g.endIdx
                    const sIdx = g.startIdx
                    const vis = g.visibleCount
                    const dq = window.devicePixelRatio || 1
                    const panFrom = Math.round(g.panOffsetPx * dq) / dq
                    return {
                      startIdx: sIdx, endIdx: eIdx, total: tot, visibleCount: vis,
                      cw: cwv, plotWidth: g.plotWidth, panOffsetPx: panFrom,
                      // Column left edge, X camera applied. Device-grid snap at FILL site.
                      xOfCol: (i) => cam.x(g.xLeft(i)),
                      // Highlight price's row — the CB projection's crossed-
                      // level guard (ask below price / bid above = impossible
                      // present). Same floor convention as every row site.
                      priceRow: g.livePrice > 0 ? g.rowOfPrice(g.livePrice) : null,
                      // Slot time per column; null outside the real array
                      // (future-padding columns have no candle).
                      // ?. IS LOAD-BEARING. This threw ~24x/second in
                      // production: `cs[i].startTime` on an undefined element,
                      // inside LiquidityHeatLayer's history pass, AFTER its
                      // clearRect and save()/clip(). Region cleared, nothing
                      // painted back, restore() never reached — one bad index
                      // wiped the whole layer and leaked a clip into every
                      // later frame.
                      //
                      // The `i >= 0 && i < tot` guard was written for exactly
                      // this and is incomplete: it passes for a FRACTIONAL i
                      // (152.68 < 400 is true) and for a hole in a sparse
                      // array, and cs[i] is undefined in both. This makes the
                      // accessor total — a column with no time reports null,
                      // which every caller already handles.
                      //
                      // THIS IS A GUARD, NOT THE ROOT CAUSE. Anything reaching
                      // here with a non-integer index is still a bug upstream;
                      // it just no longer costs the entire layer.
                      colTime: (i) => (i >= 0 && i < tot) ? (cs[i]?.startTime ?? null) : null,
                    }
                  })(),
                  // HEAT IS PINNED — pinPx from lastStyleRef (committed paint).
                  // Never re-derive; never add scrollTop. regionPx alias = pinPx
                  // (NOT the in-flow pinPx+scrollTop); LHL still reads REG for
                  // element translateY.
                  pinPx: (Number.isFinite(lastStyleRef.current.pinPx)
                    ? lastStyleRef.current.pinPx
                    : (Number.isFinite(lastStyleRef.current.px)
                      ? lastStyleRef.current.px
                      : -yResidualPxRef.current)),
                  // Alias of pinPx while heat is pinned (misnamed historically).
                  regionPx: (Number.isFinite(lastStyleRef.current.pinPx)
                    ? lastStyleRef.current.pinPx
                    : (Number.isFinite(lastStyleRef.current.px)
                      ? lastStyleRef.current.px
                      : -yResidualPxRef.current)),
                })
                // The seam — where the canvas slot heat's LAST column ends —
                // computed with the canvas heat pass's formulas VERBATIM
                // (its endIdx/startIdx clamps, its max(50) plot floor, its
                // dpr-quantized panOffset). A re-derived origin was one
                // clamp different and the error scaled with cw: overlap
                // zoomed out, gap zoomed in. If the draw's view math ever
                // changes, this changes with it — phase 2 should hoist ONE
                // shared function both call.
                const getSeamX = () => {
                  const total = chartData.candles.length
                  // Same SoT as cols / drawCanvas — FROM freeze + visual scale
                  // The chart's own painted geometry — the seam cannot race
                  // what it is drawn against if it is read from it.
                  const g = lastFrameRef.current
                  if (!g) return containerWidthRef.current - RIGHT_PADDING - FIXED_OVERLAY_WIDTH
                  return heatCam(g).x(g.xLeft(total - 1) + g.cw)
                }
                return (
                  <LiquidityHeatLayer
                    book={hlBookRef.current}
                    cbSnapsRef={cbSnapsRef}
                    cbMaxSzRef={cbMaxSzRef}
                    normRef={heatNormRef}
                    extendRef={heatExtendRef}
                    loRef={heatLoRef}
                    hiRef={heatHiRef}
                    rampRef={heatRampRef}
                    gammaRef={heatGammaRef}
                    showValuesRef={showHeatValuesRef}
                    priceToY={priceToY}
                    getGeom={getGeom}
                    getSeamX={getSeamX}
                    paintHookRef={heatLayerPaintRef}
                    // Freeze fills while interacting; force hook still paints so
                    // heat stays live on pan. Idle clock sleeps during interact.
                    interactingRef={chartInteractingRef}
                    // Own-canvas heat only (no sceneGlRef / no shared FBO).
                    // getGeom/getSeamX already bake panOffset — heat stays live
                    // during pan; interact catch-up is heatLayerPaintRef force.
                    flowDir={trailFlowDir}
                    width={liveEdge}
                    height={containerHeight}
                    active={showBookHeatLive && dataSource === 'hyperliquid'}
                  />
                )
              })()}

              {showOrderflowTrail && (() => {
                // Box width uses the LIVE axis width; the geometry below keeps
                // the constant. Those are two different jobs and only one of
                // them is a guess.
                //
                // FIXED_OVERLAY_WIDTH is 60, but the axis column is text-sized
                // (Math.max(48, min(140, textW + pad)) — 63-64 on a typical
                // mcap ladder, wider on long labels, and it never narrows
                // in-session). Sizing the trail's box to 60 ended it 3-4px
                // short of the axis, which is the visible seam: the trail
                // stopped before the price labels instead of running under
                // them like the footprint does.
                //
                // Safe because liveEdgeX is ONLY the container's style width —
                // it is stored in the trail's stateRef but never read by the
                // draw loop, so widening the box extends the clip and moves no
                // content. getOriginX below deliberately keeps the constant:
                // that IS the geometry, and it must stay identical to the
                // footprint's plotWidth or the trail slides off the candles.
                const axisWNow = axisWRef.current > 0 ? axisWRef.current : FIXED_OVERLAY_WIDTH
                const liveEdge = containerWidthRef.current - RIGHT_PADDING - axisWNow
                const getOriginX = () => {
                  const f = lastFrameRef.current
                  if (f) return f.xMid(f.total - 1)
                  return containerWidthRef.current - RIGHT_PADDING - FIXED_OVERLAY_WIDTH
                }
                // Atomic per-frame geometry. Trail is PINNED like the canvas —
                // pinPx from lastStyleRef (committed paint truth), same phase-
                // lock as rowHeight. NEVER re-derive from live residual refs;
                // NEVER add scrollTop.
                // PAINTED RH, not the live ref — trail must match heat.
                const getGeom = () => {
                  const f = lastFrameRef.current
                  if (f) return {
                    rowHeight: f.RH, pinPx: f.pinPx, regionPx: f.pinPx,
                    // Identity of the painted lattice: the trail shifts its
                    // pixels during a drag only while this is unchanged.
                    latticeId: f.mcaps?.latticeId ?? null,
                    camScale: f.camScale, camTy: f.camTy,
                    camScaleX: f.camScaleX, camAx: f.camAx,
                    // The one live price — same number the dashed line, chip
                    // and body tip use. Read at PAINT, never a React prop.
                    livePrice: f.livePrice,
                  }
                  // Pre-first-paint ONLY. NEVER null — the consumer reads
                  // geom.rowHeight with no guard (OrderflowTrail ~214).
                  const pinPx = Number.isFinite(lastStyleRef.current.pinPx)
                    ? lastStyleRef.current.pinPx
                    : -yResidualPxRef.current
                  return {
                    rowHeight: dynamicRowHeightRef.current, pinPx, regionPx: pinPx,
                    latticeId: visibleSortedMcapsRef.current?.latticeId ?? null,
                    camScale: cameraRef.current.scale, camTy: cameraRef.current.ty,
                    camScaleX: cameraRef.current.scaleX, camAx: cameraRef.current.ax,
                    livePrice: livePriceForYRef.current || 0,
                  }
                }
                return (
                  <OrderflowTrail
                    trades={data.recentTrades}
                    rowHeight={dynamicRowHeightRef.current}
                    priceToY={priceToY}
                    getGeom={getGeom}
                    flowDir={trailFlowDir}
                    hovered={trailHovered}
                    liveEdgeX={liveEdge}
                    getOriginX={getOriginX}
                    width={liveEdge}
                    height={containerHeight}
                    windowMs={(trailColors?.lenSec || 30) * 1000}
                    tailPx={trailColors?.tailPx || 2000}
                    theme={{
                      up:   trailColors?.up   || themeRef.current?.candle?.up   || '#26a69a',
                      down: trailColors?.down || themeRef.current?.candle?.down || '#ef5350',
                      outline: trailColors?.outline || '#a0a0a0',
                    }}
                    interactingRef={chartInteractingRef}
                    paintHookRef={trailLayerPaintRef}
                    showLabels={!!trailColors?.labels}
                    sizeCurve={trailColors?.sizeCurve || 0.85}
                    candleVolRef={trailCandleVolRef}
                    active={showOrderflowTrail}
                  />
                )
              })()}

              {/* Horizontal grid lines — a CSS background layer BEHIND the
                  trail (z1) and canvas (z2), so the stack is grid → trail →
                  cells. Dashed line on every 3rd row boundary, matching the
                  old canvas grid. Driven imperatively (backgroundSize from RH,
                  backgroundPosition + transform from the canvas's px) in
                  drawCanvas so it tracks scroll/zoom/rebucket with the cells.
                  Pinned like the canvas (absolute vs body-wrapper) so it rides
                  px, NOT regionPx. */}
              {/* Slot plates (cellSlotBg) — z0 under the order trail. Grid
                  dashes share z0 and mount after so lines sit on top of plates. */}
              <canvas ref={plateLayerRef} className="footprint-plate-layer" style={{
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 0,
                pointerEvents: 'none',
                zIndex: 0,
              }} />

              {/* Horizontal grid lines — dedicated canvas BEHIND the trail
                  (z1) and main canvas (z2). Canvas (not a CSS background)
                  because crisp 1px + dashed at fractional row offsets needs
                  pixel snapping (Math.round(y)+0.5) that backgrounds can't do.
                  Drawn in drawCanvas; rides the same translateY(px) as the main
                  canvas so lines stay locked to cell boundaries. */}
              <canvas ref={gridLayerRef} className="footprint-grid-layer" style={{
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 0,
                pointerEvents: 'none',
                zIndex: -3,   // vollayer-20261027: bottom of the stack (grid → volume → everything)
              }} />

              {/* Volume lane — after the grid at the same z0, so bars paint
                  OVER the grid lines (gridvol-20261027). Still absolute vs
                  body-wrapper, so geometry is unchanged. */}
              {hasAxisValues && showVolumeBars && (
                <canvas
                  ref={volLaneRef}
                  className="volume-layer-canvas"
                  style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 120, pointerEvents: 'none', zIndex: -2 }}   // vollayer-20261027: above grid (-3), under heat (-1), plates/VP (0), trail (1), cells (2+)
                />
              )}
              {/* Volume alerts (volalert-20261028): bell chip at the level,
                  centred on the anchor bar, dashed line forward to the right
                  edge. Same chip / drag / x as the price-alert tags. Nodes are
                  positioned imperatively by placeVolAlerts (draw geometry). */}
              {hasAxisValues && showVolumeBars && (
                <div ref={volAlertLayerRef} className="vol-alert-layer" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 120, pointerEvents: 'none', zIndex: 4 }}>
                  {volAlerts.alerts.map(a => {
                    const dragging = volAlertDragId === a.id
                    const color = '#22c55e'
                    const stop = (e) => e.stopPropagation()
                    return (
                      <div key={a.id} data-vaid={a.id} style={{ position: 'absolute', left: 0, top: 0, width: 0, height: 0, display: 'none', opacity: 0.7 }}>
                        {/* volalert2: no dashed line; the bell carries the level.
                            ↑ and × show only on hover / while touched (styles.css .va-bell). */}
                        <div className="va-bell"
                          onPointerEnter={(e) => { if (e.pointerType === 'mouse') vaShowCtl(e.currentTarget) }}
                          onPointerLeave={(e) => { if (e.pointerType === 'mouse') vaHideCtl(e.currentTarget) }}
                          style={{ position: 'absolute', left: 0, top: 0, transform: 'translate(-50%, -50%)', pointerEvents: 'auto' }}>
                          {/* invisible bridge: keeps the hover area continuous from the bell to ↑ / × */}
                          <span className="va-ctl" aria-hidden="true" style={{ position: 'absolute', left: '100%', top: -4, width: 40, bottom: -4, pointerEvents: 'auto' }} />
                          {dragging && (
                            <span style={{ position: 'absolute', right: 'calc(100% + 3px)', top: '50%', transform: 'translateY(-50%)', display: 'inline-flex', flexDirection: 'column', lineHeight: 0 }}>
                              <span className="alert-tag-tri" style={{ width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderBottom: `5px solid ${color}`, marginBottom: 1 }} />
                              <span className="alert-tag-tri" style={{ width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: `5px solid ${color}` }} />
                            </span>
                          )}
                          <span
                            data-va-chip="1"
                            onPointerDown={(e) => beginVolAlertDrag(a, e)}
                            onContextMenu={(e) => openVolAlertMenu(a, e)}
                            onTouchStart={stop}
                            title={`Volume alert ≥ ${fmtVol(a.level)}\nDrag to move · right-click to edit`}
                            style={{
                              fontSize: 12, background: '#363636', color: '#ddd',
                              padding: '0 4px', borderRadius: 2,
                              cursor: dragging ? 'grabbing' : 'grab',
                              fontFamily: 'monospace', userSelect: 'none', touchAction: 'none',
                              display: 'inline-flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap',
                            }}>
                            <span style={{ filter: 'grayscale(1) brightness(1.25)' }}>🔔</span>
                          </span>
                          {/* ↑, value and × sit OUTSIDE the chip (absolute) so the bell stays centred on the column */}
                          <span className="va-ctl" style={{ position: 'absolute', left: 'calc(100% + 26px)', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                            <span data-va-up="1" style={{ color: '#9aa0a6', fontSize: 10, display: 'none' }}>↑</span>
                          </span>
                          <span data-va-val="1" style={{ position: 'absolute', left: '50%', top: 'calc(100% + 2px)', transform: 'translateX(-50%)', fontSize: 10, color: '#ddd', background: '#363636', padding: '0 3px', borderRadius: 2, fontFamily: 'monospace', whiteSpace: 'nowrap', display: 'none', pointerEvents: 'none' }} />
                          <span
                            className="va-ctl"
                            data-xh="alert-cancel"
                            onPointerDown={stop}
                            onTouchStart={stop}
                            onClick={(e) => {
                              e.stopPropagation()
                              const b = e.currentTarget.closest('.va-bell')
                              // deliberate: only once × has been visible a moment (never the bell's own click)
                              if (!b || !(performance.now() - (b._vaShownAt || Infinity) >= 250)) return
                              if (volAlertSelRef.current === a.id) setVolAlertSel(null)
                              volAlerts.remove(a.id)
                            }}
                            title="Cancel volume alert"
                            style={{
                              position: 'absolute', left: 'calc(100% + 4px)', top: '50%', transform: 'translateY(-50%)',
                              fontSize: 12, color: '#aaa', background: '#363636',
                              padding: '0 5px', borderRadius: 2, cursor: 'pointer',
                              fontFamily: 'monospace', userSelect: 'none', fontWeight: 700,
                            }}>×</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* zindex-20261102: Index < 0 items (under the footprint cells, over the trail). */}
              <canvas ref={zBelowLayerRef} className="footprint-zbelow-layer" style={{
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                pointerEvents: 'none',
                zIndex: 0,   // zindex2: CSS z-index must be an integer (1.5 was ignored); z0 + DOM order = under main/GL cells
              }} />

              {/* M1 WebGL cell-fill underlay (batched fills). Text/wicks/strokes stay on main 2D canvas above. */}
              <canvas ref={cellGlCanvasRef} className="footprint-cell-gl-layer" style={{
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                pointerEvents: 'none',
                zIndex: 2,
              }} />

              {/* Canvas */}
              <canvas ref={canvasRef} className="footprint-canvas" style={{
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                pointerEvents: 'none',
                zIndex: 2.5,   // above GL cell fills (z2); live layer stays z3
              }} />

              {/* LIVE LAYER — forming candle + flip animations only (z3,
                  above the main cells canvas). The anim pump repaints THIS
                  canvas at ~30-40fps; the main canvas repaints only on
                  structural events (data publish, zoom, rebucket, theme).
                  drawCanvas sizes it and writes the same translateY as the
                  main canvas, so the two can never drift apart. */}
              <canvas ref={liveLayerRef} className="footprint-live-layer" style={{
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                pointerEvents: 'none',
                zIndex: 3,
              }} />

              {/* zindex-20261102: Index > 0 items (over the cells and the forming candle). */}
              <canvas ref={zAboveLayerRef} className="footprint-zabove-layer" style={{
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                pointerEvents: 'none',
                zIndex: 3,   // zindex2: integer; same z as live layer but later in DOM = above it
              }} />

              {/* hold-label-front-20261016: churn-zone hold % labels, topmost. */}
              <canvas ref={zoneLabelLayerRef} className="footprint-zone-label-layer" style={{
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                pointerEvents: 'none',
                zIndex: 6,
              }} />

              {/* Cursor crosshair — DOM-based so mouse movement doesn't
                  trigger a canvas redraw. Position updated imperatively
                  via translateX in updateCrosshair (no React state). */}
              <div ref={crosshairRef} className="chart-crosshair" style={{
                display: 'none',
                position: 'absolute',
                top: 0,
                left: 0,
                width: 0,
                height: '100%',
                ...CROSSHAIR_DOM_STYLE,   // shared with CVD / DVP canvas lines
                pointerEvents: 'none',
                zIndex: 3,
              }} />
              {/* Synced time line mirrored from the OTHER chart. Dimmer and
                  thinner so it reads as "the other cursor", not your own. */}
              <div ref={syncCrosshairRef} className="chart-crosshair-sync" style={{
                display: 'none',
                position: 'absolute',
                top: 0,
                left: 0,
                width: 0,
                height: '100%',
                borderLeft: '1px dashed rgba(255, 176, 32, 0.30)',
                pointerEvents: 'none',
                zIndex: 3,
              }} />
            </div>

            {/* Delta footer — absolute bottom:0, anchored to the BODY WRAPPER
                (position:relative) so it hugs the chart's bottom edge no matter
                how many subgraphs (CVD, DVP) stack below in the flex column.
                Previously a sibling of the subgraphs, anchored to the outer
                .footprint-wrapper — every enabled subgraph pushed it down.
                Side effect (intended): it now sits inside the freeze-frame
                blur during token switches, matching the stale chart it labels. */}
            <ChartFooter
              yAxisWidth={yAxisWidth}
              groupedCells={groupedCells}
              viewportRef={footerVpRef}
              onPointerDown={handleFooterPointerDown}
              onTouchStart={handleChartTouchStart}
              onTouchMove={(e) => {
                const t0 = touchStartRef.current, t = e.touches && e.touches[0]
                if (!t || e.touches.length > 1 || (t.clientX - t0.x) ** 2 + (t.clientY - t0.y) ** 2 > 144) cancelLongPress()
              }}
              onTouchEnd={() => cancelLongPress()}
              onTouchCancel={() => cancelLongPress()}
              showDelta={showFooterDelta}
              onContextMenu={(e) => { e.preventDefault(); setFooterMenuPos({ x: e.clientX, y: e.clientY - 6, up: true }) }}
            />
            {footerMenuPos && createPortal(
              <ChartMenu anchor={footerMenuPos} onClose={() => setFooterMenuPos(null)}
                closeOnTouchOutside={isPhone} className="cm-list" title="Footer">
                <MenuItem onClick={() => setShowFooterDelta(v => !v)}>
                  {showFooterDelta ? '✓' : '○'} Delta text
                </MenuItem>
              </ChartMenu>, document.body)}
          </div>

          {/* CVD subgraph — sits below the chart body (and its delta footer).
              Hidden when toggle is off; hook still runs to accumulate wicks.
              IMPORTANT: receives REFS for viewport state (viewEndIndexRef,
              viewWindowSizeRef, containerWidthRef, fractionalOffsetPxRef) so
              its rAF loop matches the main canvas exactly — never derive
              from state-based props for positioning math or candles will
              pop one bar at a time at scroll boundaries. */}
          {showCvdSubgraph && (
            <>
              {/* Resize handle — drag up to grow subgraph, down to shrink.
                  Sits flush between the chart body and the subgraph; the
                  ResizeObserver on bodyScrollRef picks up the resulting
                  containerHeight change and triggers bucket recompute via
                  the standard pipeline. */}
              <div
                className={`cvd-resize-handle ${isCvdResizing ? 'active' : ''}`}
                onPointerDown={onCvdResizeStart}
                style={{
                  height: '4px',
                  cursor: 'row-resize',
                  background: isCvdResizing ? 'rgba(120, 140, 180, 0.5)' : 'rgba(60, 70, 90, 0.4)',
                  borderTop: '1px solid #1a1d24',
                  borderBottom: '1px solid #1a1d24',
                  flexShrink: 0,
                  transition: isCvdResizing ? 'none' : 'background 120ms ease',
                  position: 'relative',
                  zIndex: 5,
                }}
                title="Drag to resize CVD subgraph"
              />
              <div ref={setCvdPaneEl} style={{ touchAction: 'none', flexShrink: 0, order: isPhone ? 4 : undefined }}
                onPointerDown={(e) => scroll.handlePointerDown(e)}
                onTouchStart={handleChartTouchStart}
                onTouchMove={handleChartTouchMove}
                onTouchEnd={handleChartTouchEndForDblTap}
                onContextMenu={(e) => {
                  // right-click the CVD pane = its settings, at the cursor (as DVP)
                  e.preventDefault()
                  // Phone: long-press keeps its existing behaviour; ignore the
                  // browser's own long-press contextmenu.
                  if (Date.now() - lastTouchTsRef.current < 1500) return
                  setCvdSettings({ show: true, x: e.clientX + 4, y: Math.max(6, Math.min(e.clientY, window.innerHeight - 200)), openLeft: e.clientX > window.innerWidth - 250 })
                }}>
              <CumulativeDeltaSubgraph
                axisWidth={yAxisWidth}
                bars={cvd.bars}
                candles={chartData.candles}
                viewEndIndexRef={viewEndIndexRef}
                viewWindowSizeRef={viewWindowSizeRef}
                paintNowRef={cvdPaintNowRef}
                containerWidthRef={containerWidthRef}
                fractionalOffsetPxRef={fractionalOffsetPxRef}
                frameRef={lastFrameRef}
                mouseXRef={mouseXRef}
                scheduleMainCanvasDraw={scheduleCanvasDraw}
                onCursorXChange={updateCrosshair}
                setViewWindowSize={setViewWindowSize}
                startIndex={cvd.startIndex}
                height={cvdHeight}
                config={cvdConfig}
              />
              </div>
            </>
          )}

          {showDvp && hasAxisValues && (
            <div ref={setDvpPaneEl} style={{ touchAction: 'none', flexShrink: 0, order: isPhone ? 5 : undefined }}
              onPointerDown={(e) => scroll.handlePointerDown(e)}
              onTouchStart={handleChartTouchStart}
              onTouchMove={handleChartTouchMove}
              onTouchEnd={handleChartTouchEndForDblTap}
              onContextMenu={(e) => {
                // right-click the subgraph itself = its settings, at the cursor
                e.preventDefault()
                // Phone: the touch long-press opens it (fireLongPress); ignore
                // the browser's own long-press contextmenu.
                if (Date.now() - lastTouchTsRef.current < 1500) return
                setDvpSettings({ show: true, x: e.clientX + 4, y: Math.max(6, Math.min(e.clientY, window.innerHeight - 380)), openLeft: e.clientX > window.innerWidth - 250 })
              }}>
              <div
                onPointerDown={(e) => {
                  // CVD-style drag resize: capture, track dy, clamp, persist scoped.
                  e.preventDefault()
                  e.stopPropagation()   // the pan-forwarding wrapper must not also fire
                  const startY = e.clientY, startH = dvpHeight
                  const move = (ev) => setDvpHeight(Math.max(60, Math.min(320, startH + (startY - ev.clientY))))
                  const up = () => {
                    window.removeEventListener('pointermove', move)
                    window.removeEventListener('pointerup', up)
                    window.removeEventListener('pointercancel', up)
                  }
                  window.addEventListener('pointermove', move)
                  window.addEventListener('pointerup', up)
                  window.addEventListener('pointercancel', up)
                }}
                style={{ height: 6, cursor: 'ns-resize', background: '#12151c', borderTop: '1px solid #1a1d24', flexShrink: 0, zIndex: 5, touchAction: 'none', order: isPhone ? 5 : undefined }}
                title="Drag to resize Delta Volume Pro"
              />
            <DeltaVolumePro
              axisWidth={yAxisWidth}
              candles={chartData.candles}
              viewEndIndexRef={viewEndIndexRef}
              viewWindowSizeRef={viewWindowSizeRef}
              paintNowRef={dvpPaintNowRef}
              containerWidthRef={containerWidthRef}
              fractionalOffsetPxRef={fractionalOffsetPxRef}
              frameRef={lastFrameRef}
              mouseXRef={mouseXRef}
              onCursorXChange={updateCrosshair}
              rowHeightRef={dynamicRowHeightRef}
              setViewWindowSize={setViewWindowSize}
              config={dvpConfig}
              height={dvpHeight}
            />
            </div>
          )}

          {showBodyVol && (
            <div style={{ touchAction: 'none', flexShrink: 0, order: isPhone ? 6 : undefined }}
              onPointerDown={(e) => scroll.handlePointerDown(e)}
              onTouchStart={handleChartTouchStart}
              onTouchMove={handleChartTouchMove}
              onTouchEnd={handleChartTouchEndForDblTap}>
              <BodyVolSubgraph
                axisWidth={yAxisWidth}
                candles={chartData.candles}
                containerWidthRef={containerWidthRef}
                frameRef={lastFrameRef}
                mouseXRef={mouseXRef}
                onCursorXChange={updateCrosshair}
                height={70}
              />
            </div>
          )}

        </div>

        {/* Resize bar removed — chart is canvas-only inside its grid cell.
            The tape (recent-trades) below is temporarily hidden; it will be
            lifted into its own Workspace cell in the next step. Kept mounted
            and wired (display:none) so its state/effects keep running. */}
        {/* Trade Tape (hidden pending move to its own grid cell) */}
        <div className="recent-trades" style={{ display: 'none' }}>
          <div className="wrap-h3s">
            <h3 className={tapeTab === 'trades' ? 'active-tab' : ''} onClick={() => setTapeTab('trades')}>Trade Tape
              {/* Filter + wallet-watchlist controls moved to TapePanel */}
            </h3>
            <h3 className={tapeTab === 'tokens' ? 'active-tab' : ''} onClick={() => setTapeTab('tokens')}>★WL</h3>
            <h3 className={tapeTab === 'wallets' ? 'active-tab' : ''} onClick={() => setTapeTab('wallets')}>Wallets

            </h3>
            <h3 className={tapeTab === 'history' ? 'active-tab' : ''} onClick={() => setTapeTab('history')}>History</h3>
          </div>
                  
          {/* Trade Tape body moved to TapePanel (its own grid cell) */}

          {/* ★WL tab moved to WatchlistPanel (its own grid cell) */}

          {/* Wallets tab moved to WalletsPanel (its own grid cell) */}

          {/* Scan tab moved to ScanPanel (its own grid cell) */}

          {tapeTab === 'history' && dataSource !== 'hyperliquid' && (() => {
            // ─── Bot journal view ───
            if (historyView === 'bot') {
              // ACTIVE bot, not the local one — in server mode the trades
              // live in RemoteBot's cached server journal.
              const journal = activeBot?.getJournal?.() || []
              // Aggregate stats — pull from j.exit (new schema places peakR/pnlR there)
              const closed = journal.filter(j => j.exit)
              const wins = closed.filter(j => reportR(j.exit) > 0)
              const losses = closed.filter(j => reportR(j.exit) <= 0)
              const winRate = closed.length > 0 ? (wins.length / closed.length) * 100 : 0
              const avgWinR = wins.length > 0 ? wins.reduce((s, j) => s + reportR(j.exit), 0) / wins.length : 0
              const avgLossR = losses.length > 0 ? losses.reduce((s, j) => s + reportR(j.exit), 0) / losses.length : 0
              const profitFactor = avgLossR < 0 && wins.length > 0
                ? Math.abs((wins.reduce((s, j) => s + reportR(j.exit), 0)) / (losses.reduce((s, j) => s + reportR(j.exit), 0)))
                : (wins.length > 0 ? Infinity : 0)
              const totalR = closed.reduce((s, j) => s + reportR(j.exit), 0)
              const avgDurationMs = closed.length > 0
                ? closed.reduce((s, j) => s + (j.durationMs || 0), 0) / closed.length
                : 0
              // Exit reason breakdown — categorize by the new bot's reason codes
              const exitReasonCount = {}
              for (const j of closed) {
                const r = String(j.exit?.reason || 'unknown')
                const cat = r === 'PILOT_STOP'         ? 'pilot stop'
                          : r === 'ADOPTED_STOP'       ? 'adopt stop'
                          : r === 'INITIAL_STOP'       ? 'init stop'
                          : r === 'ATR_TRAIL'          ? 'ATR trail'
                          : r === 'PEAK_GIVEBACK'      ? 'giveback'
                          : r === 'RUNNER_GIVEBACK'    ? 'runner GB'
                          : r === 'BE_LOCK'            ? 'BE lock'
                          : r === 'LOCK_STEP'          ? 'lock step'
                          : r === 'PILOT_TIME_CAP'     ? 'time cap'
                          : r === 'BASE_DECAY'         ? 'base decay'
                          : r === 'TOKEN_SWITCH'       ? 'switch'
                          : r === 'CONDITION_DEGRADED' ? 'thesis dead'
                          : 'other'
                exitReasonCount[cat] = (exitReasonCount[cat] || 0) + 1
              }

              return (
                <div className="trades-container" style={{ padding: 0, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  {/* Toggle */}
                  <div style={{ display: 'flex', gap: 4, padding: '4px 6px', borderBottom: '1px solid #1a1a25', alignItems: 'center', fontSize: 10 }}>
                    <button
                      onClick={() => setHistoryView('wallet')}
                      style={{ background: 'none', border: '1px solid #2a2a35', color: '#666', padding: '2px 8px', borderRadius: 3, cursor: 'pointer' }}
                    >Wallet</button>
                    <button
                      onClick={() => setHistoryView('bot')}
                      style={{ background: '#1f1f28', border: '1px solid #ffb300', color: '#ffb300', padding: '2px 8px', borderRadius: 3, cursor: 'pointer' }}
                    >Bot</button>
                    <button
                      onClick={() => {
                        const n = openBotJournalReport(activeBot?.getJournal?.() || [])
                        if (n === 0) showToast('No completed bot trades to analyze', 'sell')
                      }}
                      title="Open full expectancy analysis in a new tab"
                      style={{ background: 'none', border: '1px solid #46d41f', color: '#46d41f', padding: '2px 8px', borderRadius: 3, cursor: 'pointer' }}
                    >Analyze</button>
                    <button
                      onClick={() => {
                        const j = activeBot?.getJournal?.() || []
                        if (!j.length) { showToast('Journal is empty', 'sell'); return }
                        exportBotJournal(j)
                        showToast(`Exported ${j.length} trades`, 'buy')
                      }}
                      title="Download the raw journal as JSON"
                      style={{ background: 'none', border: '1px solid #2a2a35', color: '#888', padding: '2px 8px', borderRadius: 3, cursor: 'pointer' }}
                    >Export</button>
                    <span style={{ flex: 1 }} />
                    <button
                      onClick={() => {
                        if (window.confirm(`Reset bot journal? This deletes ${journal.length} trade record(s).`)) {
                          const n = activeBot?.resetJournal?.() || 0
                          showToast(`Cleared ${n} bot trades`, 'sell')
                          setBotJournalTick(t => t + 1)
                        }
                      }}
                      style={{ background: 'none', border: '1px solid #ff5252', color: '#ff5252', padding: '2px 8px', borderRadius: 3, cursor: 'pointer', fontSize: 9 }}
                    >Reset</button>
                  </div>

                  {/* Aggregate stats */}
                  {closed.length > 0 && (
                    <div style={{ padding: '6px 8px', borderBottom: '1px solid #1a1a25', fontSize: 10, lineHeight: 1.5, color: '#bbb' }}>
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <span>Trades: <b style={{ color: '#fff' }}>{closed.length}</b></span>
                        <span>Win: <b style={{ color: winRate >= 50 ? '#00c853' : '#ff5252' }}>{winRate.toFixed(0)}%</b></span>
                        <span>Total: <b style={{ color: totalR >= 0 ? '#00c853' : '#ff5252' }}>{totalR >= 0 ? '+' : ''}{totalR.toFixed(1)}R</b></span>
                        <span>PF: <b style={{ color: '#fff' }}>{profitFactor === Infinity ? '∞' : profitFactor.toFixed(2)}</b></span>
                      </div>
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', color: '#666', fontSize: 9, marginTop: 2 }}>
                        <span>avg win {avgWinR >= 0 ? '+' : ''}{avgWinR.toFixed(2)}R</span>
                        <span>avg loss {avgLossR.toFixed(2)}R</span>
                        <span>avg dur {(avgDurationMs / 1000).toFixed(0)}s</span>
                      </div>
                      {Object.keys(exitReasonCount).length > 0 && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', color: '#666', fontSize: 9, marginTop: 2 }}>
                          {Object.entries(exitReasonCount)
                            .sort((a, b) => b[1] - a[1])
                            .map(([k, v]) => (
                              <span key={k}>{k}: {v}</span>
                            ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Trade list — schema-aware: pilotEntry for organic, adoption for auto-pilot */}
                  <div className="no-scrollbar" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none' }}>
                    {journal.length === 0 && (
                      <div className="watchlist-empty" style={{ padding: 20 }}>No bot trades yet</div>
                    )}
                    {journal.map(j => {
                      const isClosed = !!j.exit
                      const r = reportR(j.exit)
                      const peakR = j.exit?.peakR ?? 0
                      const dur = j.durationMs ? `${(j.durationMs / 1000).toFixed(0)}s` : '...'
                      const dirColor = j.pilotDirection === 'SHORT' ? '#ff5252' : '#00c853'
                      const tokenShort = j.token ? `${j.token.slice(0, 4)}…${j.token.slice(-4)}` : ''
                      // Entry reference — adopted positions store it under `adoption`,
                      // organic ones under `pilotEntry`. Either may be null defensively.
                      const entryRef = j.adoption || j.pilotEntry || null
                      const entryMcap = entryRef?.mcap
                      const entryStop = entryRef?.stopMcap
                      const tooltip = [
                        j.adopted ? 'Type: AUTO-PILOT' : (j.load ? 'Type: BASE → LOAD' : 'Type: PILOT only'),
                        entryMcap ? `Entry: ${formatMcap(entryMcap)}${entryStop ? ` (stop ${formatMcap(entryStop)})` : ''}` : '',
                        j.load ? `Loaded @ ${formatMcap(j.load.addMcap)} · vol ${j.load.triggerSpikeMult?.toFixed?.(1) || '-'}× · delta ${((j.load.triggerDelta || 0) * 100).toFixed(0)}%` : '',
                        j.exit ? `Exit: ${formatMcap(j.exit.mcap)} — ${j.exit.reason}` : 'Open',
                        `Peak R: ${peakR.toFixed(2)}`,
                        entryRef
                          ? `H ${(entryRef.h ?? 0).toFixed(2)} C ${(entryRef.c ?? 0).toFixed(2)} R ${(entryRef.r ?? 0).toFixed(2)}`
                          : '',
                      ].filter(Boolean).join('\n')

                      return (
                        <div key={j.id}
                          style={{
                            padding: '4px 8px', borderBottom: '1px solid #15151c',
                            cursor: j.token ? 'pointer' : 'default',
                            fontSize: 10, lineHeight: 1.4,
                          }}
                          onClick={() => j.token && subscribeToToken(j.token)}
                          title={tooltip}
                        >
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <span style={{ color: dirColor, fontWeight: 600, minWidth: 36 }}>
                              {j.pilotDirection === 'SHORT' ? 'SHORT' : 'LONG'}
                            </span>
                            <span style={{ color: isClosed ? (r >= 0 ? '#00c853' : '#ff5252') : '#ffb300', fontWeight: 600, minWidth: 50 }}>
                              {isClosed ? `${r >= 0 ? '+' : ''}${r.toFixed(2)}R` : 'OPEN'}
                            </span>
                            <span style={{ color: '#888', fontSize: 9 }}>peak {peakR.toFixed(1)}</span>
                            {j.adopted ? (
                              <span style={{ color: '#8eb6ff', fontSize: 9, padding: '0 4px', border: '1px solid #448aff66', borderRadius: 2 }}>AUTO</span>
                            ) : j.load ? (
                              <span style={{ color: '#00c853', fontSize: 9, padding: '0 4px', border: '1px solid #00c85344', borderRadius: 2 }}>LOAD</span>
                            ) : (
                              <span style={{ color: '#ffb300', fontSize: 9, padding: '0 4px', border: '1px solid #ffb30044', borderRadius: 2 }}>PILOT</span>
                            )}
                            <span style={{ color: '#666', fontSize: 9 }}>{dur}</span>
                            <span style={{ flex: 1 }} />
                            {tokenShort && <span style={{ color: '#555', fontSize: 9, fontFamily: 'monospace' }}>{tokenShort}</span>}
                          </div>
                          {j.exit?.reason && (
                            <div style={{ color: '#666', fontSize: 9, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {j.exit.reason}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            }

            // ─── Wallet view moved to JournalPanel (its own grid cell) ───
            return (
              <div className="watchlist-empty" style={{ padding: 16 }}>
                Wallet history is now in the Journal panel.
              </div>
            )
          })()}

          {/* Futures (HL) history moved to JournalPanel */}
        </div>
      </div>
        {/* trades-footer deleted — org badge dropped; watchlist moved to context menu */}
      {/* Messages popup moved to App (app-level MessagesPanel) */}
      {/* Bottom scan ticker bar */}
      {(tickerTokens?.length ?? 0) > 0 && (
        <div className="scan-ticker-bar">
          <div
            ref={tickerBeltRef}
            className="scan-ticker-belt"
            style={{ '--ticker-duration': `${Math.max(20, tickerTokens.length * 3)}s` }}
            onMouseDown={(e) => {
              if (e.button !== 0) return
              const belt = tickerBeltRef.current
              if (!belt) return
              const matrix = new DOMMatrix(getComputedStyle(belt).transform)
              belt.style.animation = 'none'
              belt.style.transform = `translateX(${matrix.m41}px)`
              const drag = tickerDragRef.current
              drag.active = true
              drag.startX = e.clientX
              drag.startOffset = matrix.m41
              drag.moved = false
              clearTimeout(drag._timer)

              const onMove = (ev) => {
                if (!drag.active) return
                const dx = ev.clientX - drag.startX
                if (Math.abs(dx) > 5) drag.moved = true
                belt.style.transform = `translateX(${drag.startOffset + dx}px)`
              }
              const onUp = () => {
                drag.active = false
                window.removeEventListener('mousemove', onMove)
                window.removeEventListener('mouseup', onUp)
                clearTimeout(drag._timer)
                drag._timer = setTimeout(() => {
                  belt.style.animation = ''
                  belt.style.transform = ''
                }, 3000)
              }
              window.addEventListener('mousemove', onMove)
              window.addEventListener('mouseup', onUp)
            }}
          >
            {[...tickerTokens, ...tickerTokens].map((t, i) => {
              const addr = t.id || t.address || ''
              return (
                <div key={`${addr}-${i}`} className="scan-ticker-chip" onClick={() => { if (tickerDragRef.current.moved) return; subscribeToToken(addr) }}>
                  <span className="scan-rank">{(i % tickerTokens.length) + 1}</span>
                  <img className="scan-logo" src={t.icon || t.logoURI || ''} alt="" onError={(e) => { e.target.style.display = 'none' }} />
                  <span className="scan-name" title={addr}>{t.symbol || t.name || `${addr.slice(0, 6)}...`}</span>
                  {t._fresh ? (
                    // Fresh scan: CHDR score · AUTH ✓ · 🚀 boost only.
                    <>
                      {(() => {
                        const sc = t._raw?.chdr
                        const ok = typeof sc === 'number' && Number.isFinite(sc)
                        const tier = !ok ? 'tier-low' : sc >= 60 ? 'tier-high' : sc >= 30 ? 'tier-mid' : 'tier-low'
                        return (
                          <span className={`jup-scan-organic ${tier}`} title={ok ? `CHDR ${Math.round(sc)}/100` : 'CHDR pending'}
                                style={{ display: 'inline-flex', flexDirection: 'row', alignItems: 'center', gap: 2,
                                         minWidth: 0, padding: '0 3px', height: 12, borderRadius: 3, opacity: ok ? 1 : 0.35 }}>
                            <span className="jup-scan-organic-value" style={{ fontSize: 9, lineHeight: 1 }}>{ok ? Math.round(sc) : '–'}</span>
                            <span className="jup-scan-organic-label" style={{ fontSize: 7, marginTop: 0 }}>CHDR</span>
                          </span>
                        )
                      })()}
                      {t._raw?.auth === 'ok' && (
                        <span className="jup-scan-organic tier-high" title="Mint + freeze authority revoked"
                              style={{ display: 'inline-flex', alignItems: 'center', minWidth: 0, padding: '0 3px', height: 12, borderRadius: 3, opacity: 0.75 }}>
                          <span className="jup-scan-organic-value" style={{ fontSize: 9, lineHeight: 1 }}>✓</span>
                        </span>
                      )}
                      {t._boosted && <span title="Active DexScreener boosts" style={{ fontSize: 10 }}>🚀</span>}
                    </>
                  ) : t._raw ? (
                    <>
                      <span className="scan-volbuy">{t._raw.buyVolume >= 1000 ? `${(t._raw.buyVolume / 1000).toFixed(1)}K` : t._raw.buyVolume.toFixed(1)}</span>
                      <span className="scan-volsell">-{t._raw.sellVolume >= 1000 ? `${(t._raw.sellVolume / 1000).toFixed(1)}K` : t._raw.sellVolume.toFixed(1)}</span>
                      <span style={{ color: t._raw.delta >= 0 ? '#4caf50' : '#f44336', fontSize: 10 }}>Δ{t._raw.delta >= 0 ? '+' : ''}{t._raw.delta >= 100 ? `${(t._raw.delta / 1000).toFixed(1)}K` : t._raw.delta.toFixed(1)}</span>
                      {t._raw.organic != null && <span style={{ color: t._raw.organic >= 8 ? '#4caf50' : t._raw.organic >= 4 ? '#ffb300' : '#666', fontSize: 10, fontWeight: 600 }}>{t._raw.organic.toFixed(1)}✦</span>}
                    </>
                  ) : (
                    <>
                      <span className="scan-volbuy">{(() => { const v = t.stats24h?.buyVolume || 0; return v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}K` : v > 0 ? `$${v.toFixed(0)}` : '-' })()}</span>
                      {/* Organic score chip (replaces 24h sell volume, per Levi).
                          Same tier classes as ScanPanel's cards — colors stay
                          consistent; v2 scale thresholds (70/40). Inline
                          overrides flatten the card block into ticker height. */}
                      {(() => {
                        const score = t.organicScore || 0
                        const tier = score >= 70 ? 'tier-high' : score >= 40 ? 'tier-mid' : 'tier-low'
                        return (
                          <span className={`jup-scan-organic ${tier}`} title={`organic score ${Math.round(score)}`}
                                style={{ display: 'inline-flex', flexDirection: 'row', alignItems: 'center', gap: 2,
                                         minWidth: 0, padding: '0 3px', height: 12, borderRadius: 3 }}>
                            {/* child classes carry the tier colors — the cascade is
                                `.tier-X .jup-scan-organic-value { color: ... }`, so bare
                                text stayed default-colored. Inline only re-sizes. */}
                            <span className="jup-scan-organic-value" style={{ fontSize: 9, lineHeight: 1 }}>{Math.round(score)}</span>
                            <span className="jup-scan-organic-label" style={{ fontSize: 7, marginTop: 0 }}>ORG</span>
                          </span>
                        )
                      })()}
                    </>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Modals & Menus */}
      <OrderModal show={trading.orderModal.show} side={trading.orderModal.side} mcap={trading.orderModal.mcap} formatMcap={formatYAxis}
        onClose={() => trading.setOrderModal({ show: false, side: 'buy', mcap: 0 })} onConfirm={trading.handleOrderConfirm} showToast={showToast}
        tokenBalance={trading.tokenBalance} currentMcap={chartData.highlightMcap}
        dataSource={dataSource} hlPair={hlPair} defaults={defaults} positions={trading.positions} />
      <Toast message={toast.message} type={toast.type} show={toast.show} />
      <PriceAlertMenu
        menuState={priceAlerts.menuState}
        alertsForCurrentToken={priceAlerts.alertsForCurrentToken}
        onAdd={priceAlerts.addAlert}
        onRemove={priceAlerts.removeAlert}
        onClose={priceAlerts.closeMenu}
        formatMcap={priceAlerts.formatMcap}
        currentMcap={chartData.highlightMcap || data.lastBucketMcapRef?.current}
      />
      <PriceAlertNotifications
        notifications={volAlerts.notifications.length ? [...priceAlerts.notifications, ...volAlerts.notifications] : priceAlerts.notifications}
        onClick={(n) => (n.kind === 'vol' ? volAlerts.dismiss(n.id) : priceAlerts.handleNotificationClick(n))}
        onDismiss={(id) => (String(id).startsWith('vol_') ? volAlerts.dismiss(id) : priceAlerts.dismissNotification(id))}
        formatMcap={priceAlerts.formatMcap}
      />
      {/* DEX Paid live alerts — bottom-left so they don't fight price alert toasts. */}
      {boostToasts.length > 0 && (
        <div style={{
          position: 'fixed', bottom: 16, left: 16, zIndex: 9100,
          display: 'flex', flexDirection: 'column-reverse', gap: 8, pointerEvents: 'none',
        }}>
          {boostToasts.map(t => (
            <div key={t.id}
                 onClick={() => { subscribeToToken(t.mint); setBoostToasts(prev => prev.filter(x => x.id !== t.id)) }}
                 style={{
                   pointerEvents: 'auto',
                   background: '#1a1c22',
                   border: '1px solid #4a3a1a',
                   borderLeft: '4px solid #ffb300',
                   borderRadius: 6, padding: '10px 12px',
                   minWidth: 240, maxWidth: 320,
                   cursor: 'pointer', color: '#e8eaed',
                   boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
                   display: 'flex', alignItems: 'center', gap: 10,
                 }}>
              {t.icon
                ? <img src={t.icon} alt="" style={{ width: 32, height: 32, borderRadius: '50%' }} onError={(e) => { e.target.style.display = 'none' }} />
                : <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#2a2d35' }} />
              }
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: '#9aa0a6', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  DEX Paid 🚀
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.mint.slice(0, 6)}...{t.mint.slice(-4)} · <span style={{ color: '#ffb300' }}>+{t.amount}</span>
                </div>
                <div style={{ fontSize: 10, color: '#6b7280' }}>click to view chart</div>
              </div>
              <span onClick={(e) => { e.stopPropagation(); setBoostToasts(prev => prev.filter(x => x.id !== t.id)) }}
                    style={{ cursor: 'pointer', color: '#666', fontSize: 14, padding: '0 4px' }}>×</span>
            </div>
          ))}
        </div>
      )}
      {/* Phone trendline placement hint. Press sets point 1, the swipe
          previews point 2 and lifting sets it (a still tap draws nothing). */}
      {isPhone && trend.mode === 'phonePlacing' && (
        <div
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', left: '50%', top: 'calc(env(safe-area-inset-top, 0px) + 64px)',
            transform: 'translateX(-50%)', zIndex: 1500,
            display: 'flex', alignItems: 'center', gap: 10,
            background: '#16181c', border: '1px solid #2a2d33', borderRadius: 6,
            boxShadow: '0 6px 20px rgba(0,0,0,0.5)', padding: '6px 6px 6px 12px',
            fontSize: 12, color: '#ddd', whiteSpace: 'nowrap',
          }}
        >
          <span style={{ color: '#ffaa00' }}>{'\u2571'}</span>
          <span>{'Press, swipe, release to draw \u00b7 \u2715 to cancel'}</span>
          <button
            onClick={(e) => { e.stopPropagation(); trend.cancel() }}
            aria-label="Cancel trendline"
            style={{ background: '#22252b', border: '1px solid #2a2d33', borderRadius: 4, color: '#ddd',
                     fontSize: 14, lineHeight: 1, width: 30, height: 30, cursor: 'pointer' }}>
            {'\u2715'}
          </button>
        </div>
      )}
      <ChartContextMenu show={interaction.contextMenu.show} x={interaction.contextMenu.x} y={interaction.contextMenu.y}
        onAddAVWAP={interaction.handleAddAVWAP} onAddSRLine={interaction.handleAddSRLine}
        onAddTrendline={isPhone ? () => trend.armPhone() : () => trend.arm()} trendPending={!!trend.pending && trend.mode !== 'phonePlacing'}
        onAddRect={isPhone ? () => trend.armPhone('rect') : () => trend.arm('rect')} rectPending={!!trend.pending && trend.pending.shape === 'rect' && trend.mode !== 'phonePlacing'}
        chartMode={chartMode} onChartModeChange={setChartMode}
        tpoLetters={tpoLetters} onToggleTpoLetters={() => setTpoLetters(v => { const n = !v; try { localStorage.setItem('cheddar_tpo_letters', n ? '1' : '0') } catch {} return n })}
        volBars={volOn} onToggleVolBars={() => setVolBars(chartId, { type: volOn ? 'time' : 'vol' })}
        logScale={logScale} onToggleLogScale={() => toggleLogScale()}
        isWatched={isCurrentWatched}
        canWatch={/* fut-capable since the pseudo-address star: futWatchKey is
                     non-null exactly when a pair is charted, so the star works
                     in both modes */ !!futWatchKey || !!data.currentToken}
        onToggleWatchlist={() => { toggleWatchlist(); }}
        currentTokenAddress={data.currentToken}
        onTokenAddressChange={(addr) => {
          setTokenInput(addr)
          subscribeToToken(addr)
        }}
        extraItems={[
          // Phone Focus — elevate chart+Y, dim the rest. Desktop never sees it.
          // Phone: alerts used to be a second long-press in crosshair mode;
          // long-press opens this menu now, so the alert lives here — at the
          // press point (or the crosshair, when crosshair mode is on).
          ...(isPhone && pressAlertMcapRef.current > 0 ? [{
            label: '🔔 Set Alert Here',
            onClick: () => {
              const m = pressAlertMcapRef.current
              const cur = chartData.highlightMcap || data.lastBucketMcapRef?.current
              if (!Number.isFinite(m) || !(m > 0) || !Number.isFinite(cur)) return
              const futures = dataSource === 'hyperliquid'
              priceAlerts.addAlert({
                market: futures ? 'hl' : 'sol',
                mint:   futures ? null : data.currentToken,
                symbol: futures ? (hlPair?.base || null) : null,
                mcap:   m,
                direction: m >= cur ? 'above' : 'below',
              })
              showToast('🔔 Alert set', 'buy')
            },
          }] : []),
          ...(FOCUS_ENABLED && isPhone ? [{
            label: chartFocus ? '☑ Focus' : '☐ Focus',
            onClick: () => toggleChartFocus(),
          }] : []),
          // Price-scale mode. Label names the state you would be switching
          // TO, matching '☆ Add / ★ Remove Favorite' above it. Per token,
          // persisted — see toggleLogScale. Flipping back is a cache hit: the
          // display cache is keyed on (bucket, ratio), so both lattices stay
          // resident for the visible window.
          { label: logScale ? '≡ Linear Scale' : '∿ Log Scale', onClick: () => toggleLogScale() },
          // Data dump: this coin's captured swaps (server swaps.db) as CSV.
          // Same menu on phone (double-tap opens it), so no separate button.
          ...((futWatchKey || data.currentToken) ? [{
            label: '↓ Download CSV',
            onClick: () => {
              const key = futWatchKey || data.currentToken
              showToast('↓ Preparing CSV…', 'buy')
              downloadSwapsCsv(key)
                .then(({ name }) => showToast(`↓ ${name}`, 'buy'))
                .catch(e => showToast(String(e?.message || e), 'sell'))
            },
          }] : []),
          ...(trend.mode ? [{ label: trend.pending?.shape === 'rect' || trend.shapeRef?.current === 'rect' ? '✕ Cancel Rectangle' : '✕ Cancel Trendline', onClick: trend.cancel }] : []),
          ...((showBookHeatLive || showCbHeatLive) ? [{
            label: '⚙ Heatmap',
            onClick: () => {
              const cx = interaction.contextMenu.x
              setHeatSettings({ show: true, x: cx, y: Math.max(6, Math.min(interaction.contextMenu.y, window.innerHeight - 260)), openLeft: cx > window.innerWidth - 240 })
            },
          }] : []),
          ...(showArd ? [{
            label: '⚙ ARD Shadow settings',
            onClick: () => {
              const cx = interaction.contextMenu.x
              setArdSettings({ show: true, x: cx, y: Math.max(6, Math.min(interaction.contextMenu.y, window.innerHeight - 340)), openLeft: cx > window.innerWidth - 240 })
            },
          }] : []),
        ]}
        onClose={() => interaction.setContextMenu({ show: false, x: 0, y: 0, candleIdx: -1, clickMcap: 0 })} />

      {/* Smoketrail flow-direction mini-menu — opened by right-clicking the
          trail's live-edge handle. Styled like WalletHoldings' control menu. */}
      {trailFlowMenu && (
        <ChartMenu anchor={trailFlowMenu} openLeft={!!trailFlowMenu.openLeft}
          onClose={() => setTrailFlowMenu(null)} closeOnContextMenu stopMouseDown
          title="Smoketrail Flow">
          <MenuSection>
            {[{ k: 'left', label: '← Left' }, { k: 'right', label: 'Right →' }].map(({ k, label }) => (
              <MenuItem key={k} className={trailFlowDir === k ? 'cm-item-on' : undefined}
                onClick={() => { setTrailFlowDir(k); setTrailFlowMenu(null) }}>
                <span style={{ flex: 1 }}>{label}</span>
                {trailFlowDir === k && <span className="cm-check">✓</span>}
              </MenuItem>
            ))}
          </MenuSection>
          <MenuSection title="Colors">
            <MenuRow>
              <MenuSwatch
                value={trailColors?.up || themeRef.current?.candle?.up || '#26a69a'}
                onChange={(c) => setTrailColors(p => ({ ...(p || {}), up: c }))}
                title="Buy trail" />
              <MenuDivider />
              <MenuSwatch
                value={trailColors?.down || themeRef.current?.candle?.down || '#ef5350'}
                onChange={(c) => setTrailColors(p => ({ ...(p || {}), down: c }))}
                title="Sell trail" />
              <MenuDivider />
              <MenuSwatch
                value={trailColors?.outline || '#a0a0a0'}
                onChange={(c) => setTrailColors(p => ({ ...(p || {}), outline: c }))}
                title="Bubble outline" />
              <MenuDivider />
              <MenuButton variant="ghost" title="Follow the theme's candle colours"
                onClick={() => setTrailColors(p => ({ ...(p || {}), up: null, down: null }))}>
                THEME
              </MenuButton>
            </MenuRow>
          </MenuSection>
          <MenuSection title="Bubbles">
            {(() => {
              const STEPS = [0.5, 0.65, 0.85, 1, 1.25, 1.5]
              const cur = trailColors?.sizeCurve || 0.85
              const ix = Math.max(0, STEPS.findIndex(v => v >= cur - 1e-6))
              const step = (d) => setTrailColors(p => ({ ...(p || {}), sizeCurve: STEPS[Math.max(0, Math.min(STEPS.length - 1, ix + d))] }))
              const on = !!trailColors?.labels
              return (
                <MenuRow>
                  <MenuButton on={on} title="Show each bubble's volume" onClick={() => setTrailColors(p => ({ ...(p || {}), labels: !p?.labels }))}>{on ? 'NUMBERS ON' : 'NUMBERS OFF'}</MenuButton>
                  <MenuDivider />
                  <MenuButton variant="ghost" title="Less size contrast" onClick={() => step(-1)}>-</MenuButton>
                  <span style={{ flex: 1, textAlign: 'center', minWidth: 56 }} title="Size contrast (curve exponent; 0.5 = sqrt, 1 = linear)">contrast {cur}</span>
                  <MenuButton variant="ghost" title="More size contrast" onClick={() => step(1)}>+</MenuButton>
                </MenuRow>
              )
            })()}
          </MenuSection>
          <MenuSection title="Speed">
            {(() => {
              const STEPS = [5, 10, 15, 20, 30, 45, 60, 90, 120, 180, 300]
              const cur = trailColors?.lenSec || 30
              const ix = Math.max(0, STEPS.findIndex(v => v >= cur))
              const step = (d) => {
                const n = STEPS[Math.max(0, Math.min(STEPS.length - 1, ix + d))]
                setTrailColors(p => ({ ...(p || {}), lenSec: n }))
              }
              return (
                <MenuRow>
                  <MenuButton variant="ghost" title="Faster" onClick={() => step(-1)}>-</MenuButton>
                  <span style={{ flex: 1, textAlign: 'center', minWidth: 48 }}>{cur >= 60 ? (cur / 60) + 'm' : cur + 's'}</span>
                  <MenuButton variant="ghost" title="Slower" onClick={() => step(1)}>+</MenuButton>
                  <MenuDivider />
                  <MenuButton variant="ghost" title="Default 30s" onClick={() => setTrailColors(p => ({ ...(p || {}), lenSec: 30 }))}>RESET</MenuButton>
                </MenuRow>
              )
            })()}
          </MenuSection>
          <MenuSection title="Length">
            {(() => {
              const cur = trailColors?.tailPx || 2000
              const set = (v) => { const n = Math.max(1, Math.round(Number(v) || 0)); setTrailColors(p => ({ ...(p || {}), tailPx: n })) }
              const step = (d) => set(d > 0 ? Math.floor(cur / 1000) * 1000 + 1000 : Math.max(1000, Math.ceil(cur / 1000) * 1000 - 1000))
              return (
                <MenuRow>
                  <MenuButton variant="ghost" title="Shorter tail (-1000px)" onClick={() => step(-1)}>-</MenuButton>
                  <input type="number" min={1} step={1000} defaultValue={cur} key={cur}
                    onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') set(e.currentTarget.value) }}
                    onBlur={e => set(e.currentTarget.value)}
                    style={{ flex: 1, width: 64, minWidth: 48, background: 'transparent', color: 'inherit', border: '1px solid #555', borderRadius: 3, padding: '2px 4px', textAlign: 'center' }} />
                  <span style={{ marginLeft: 2, marginRight: 4 }}>px</span>
                  <MenuButton variant="ghost" title="Longer tail (+1000px)" onClick={() => step(1)}>+</MenuButton>
                  <MenuDivider />
                  <MenuButton variant="ghost" title="Default 2000px" onClick={() => set(2000)}>RESET</MenuButton>
                </MenuRow>
              )
            })()}
          </MenuSection>
        </ChartMenu>
      )}

      <BotSettingsMenu
        show={botMenu.show} x={botMenu.x} y={botMenu.y}
        bot={activeBot}
        mode={botMode}
        onDiscoverySettings={(settings) => {
          remoteBotRef.current.setDiscoverySettings(settings)
          const [k, v] = Object.entries(settings)[0] || []
          showToast(`Discovery: ${k} → ${v}`, 'buy')
        }}
        onAnalysisChange={(cfg) => {
          remoteBotRef.current.setAnalysis(cfg)
          showToast(`Bot bars: ${cfg.intervalSec}s`, 'buy')
        }}
        onDryRunToggle={(v) => {
          remoteBotRef.current.setDryRun(v)
          showToast(v ? '🧪 Dry run — paper only' : '💰 LIVE — real execution armed', v ? 'buy' : 'sell')
        }}
        onDiscoverToggle={(v) => {
          remoteBotRef.current.setDiscovery(v)
          showToast(v ? '🔎 Discovery ON — hunting trending coins' : 'Discovery OFF', 'buy')
        }}
        onModeChange={(m) => {
          // MODE IS A VIEW. It arms nothing and it disarms nothing.
          //
          // This used to call remoteBotRef.disable() on the way out of 'server',
          // which sends {cmd:'disable'} down the socket — and the runner's
          // 'disable' case sets coordEnabled = false AND disables every slot. So
          // toggling a view in a browser tab was killing the entire droplet
          // coordinator. The one thing a cloud bot exists to survive is you
          // closing the tab.
          //
          // The old comment ("leaving a mode disarms that mode's bot so only one
          // acts") was defending a real invariant — two bots, one wallet, double
          // size. But it enforced it at the wrong moment. Navigating is not
          // arming. The invariant now lives in onToggle, where the second bot
          // actually gets armed, so it survives being right.
          //
          // Note it isn't even live yet: the runner's executeSignal() is a stub
          // that throws 'live execution not wired yet'. The server bot cannot
          // currently collide with anything — it was paying full price for a
          // collision that can't happen.
          if (m === botMode) return
          setBotMode(m)
          showToast(`Bot view: ${m === 'server' ? 'SERVER' : 'LOCAL'}`, 'buy')
        }}
        onToggle={() => {
          const rb = remoteBotRef.current
          if (botMode === 'server') {
            if (!rb.online) { showToast('Server bot offline', 'sell'); return }
            if (rb.enabled) rb.disable()
            else {
              if (!data.currentToken) { showToast('Chart a token first', 'sell'); return }
              // No local-armed check: server + local bots run independently.
              rb.enable()
              showToast('☁️ Server bot armed on charted token', 'buy')
            }
            setBotMenu(m => ({ ...m }))   // nudge re-render
            return
          }
          // Arming LOCAL while the SERVER bot is live would put two bots on one
          // wallet. The rule itself lives in toggleLocalBotArm so this handler
          // and the Indicators menu item can't drift apart.
          toggleLocalBotArm()
        }}
        onUpdate={(cfg) => { activeBot?.updateConfig(cfg); showToast('Bot config updated', 'buy') }}
        onReset={() => {
          const bot = activeBot; if (!bot) return
          bot.tradeLog = []; bot._dailyPnL = 0; bot._dailyTradeCount = 0
          showToast('Bot stats reset', 'buy')
        }}
        onClose={() => setBotMenu({ show: false, x: 0, y: 0 })}
        dataSource={dataSource}
        hlPair={hlPair}
      />
      <SRLineSettingsMenu show={interaction.srLineSettings.show} x={interaction.srLineSettings.x} y={interaction.srLineSettings.y}
        line={interaction.srLineSettings.line} onUpdate={interaction.handleUpdateSRLine} onDelete={interaction.handleDeleteSRLine}
        onAlert={(m) => {
          // Direction is derived, not asked: an alert at a level ABOVE price
          // can only mean "tell me on the way up", and below means the reverse.
          // Same rule PriceAlertMenu uses for its default, so the bell and the
          // menu can never disagree about the same level.
          const cur = chartData.highlightMcap || data.lastBucketMcapRef?.current
          if (!Number.isFinite(m) || !Number.isFinite(cur)) return
          const futures = dataSource === 'hyperliquid'
          priceAlerts.addAlert({
            market: futures ? 'hl' : 'sol',
            mint:   futures ? null : data.currentToken,
            symbol: futures ? (hlPair?.base || null) : null,
            mcap:   m,
            direction: m >= cur ? 'above' : 'below',
          })
        }}
        onClose={() => interaction.setSrLineSettings({ show: false, x: 0, y: 0, line: null })} />
      <AVWAPSettingsMenu show={interaction.avwapSettings.show} x={interaction.avwapSettings.x} y={interaction.avwapSettings.y}
        vwap={interaction.avwapSettings.vwap} onUpdate={interaction.handleUpdateAVWAP} onDelete={interaction.handleDeleteAVWAP}
        onClose={() => interaction.setAvwapSettings({ show: false, x: 0, y: 0, vwap: null })} />
      <VolumeProfileSettingsMenu
        show={vpSettings.show} x={vpSettings.x} y={vpSettings.y} openLeft={vpSettings.openLeft}
        config={vaConfig}
        theme={theme}
        onUpdate={setVaConfig}
        onClose={() => setVpSettings(v => ({ ...v, show: false }))} />
      <VolumeSettingsMenu
        show={volSettings.show} x={volSettings.x} y={volSettings.y} openLeft={volSettings.openLeft}
        config={volConfig}
        onUpdate={setVolConfig}
        closeOnTouchOutside={isPhone}
        onClose={() => setVolSettings(v => ({ ...v, show: false }))}
        alert={(() => {
          const al = volSettings.show ? volSettings.alert : null
          if (!al) return null
          if (al.mode === 'new') return al
          const a = volAlerts.alerts.find(x => x.id === al.id)
          return a ? { mode: 'edit', id: a.id, level: a.level } : null
        })()}
        onAlertSet={(lv) => { const al = volSettings.alert; if (al && al.mode === 'new') volAlerts.add(al.t, lv) }}
        onAlertSave={(lv) => { const al = volSettings.alert; if (al && al.id) volAlerts.update(al.id, lv) }}
        onAlertRemove={() => { const al = volSettings.alert; if (al && al.id) volAlerts.remove(al.id) }} />
      <AbsorptionSettingsMenu
        show={absSettings.show} x={absSettings.x} y={absSettings.y} openLeft={absSettings.openLeft}
        config={absConfig}
        onUpdate={setAbsConfig}
        onClose={() => setAbsSettings(v => ({ ...v, show: false }))} />
      {dataSource !== 'hyperliquid' && <AbsorbersPanel at={absorbersAt} onClose={closeAbsorbers} />}
      <ValueAreaSettingsMenu
        show={vaSettings.show} x={vaSettings.x} y={vaSettings.y} openLeft={vaSettings.openLeft}
        config={vaConfig}
        themeVpBuy={theme?.volumeProfile?.buy || '#6ea8ff'}
        themeVpSell={theme?.volumeProfile?.sell || '#ff7b7b'}
        onUpdate={setVaConfig}
        onClose={() => setVaSettings(s => ({ ...s, show: false }))} />
      {/* Trendline styling reuses the S/R toolbar unchanged: a trendline
          carries the same color/weight/style/label contract, so onUpdate
          patches the matching entry and onDelete drops it. 'ray' extends
          the line past its second anchor, which is what that style means
          for a trendline. */}
      <SRLineSettingsMenu
        show={trend.menu.show}
        x={trend.menu.x}
        y={trend.menu.y}
        line={trend.menu.line}
        onUpdate={trend.update}
        onDelete={trend.remove}
        onClose={() => trend.setMenu(v => ({ ...v, show: false }))}
      />
      <ATRTrailSettingsMenu
        show={atrSettings.show}
        x={atrSettings.x}
        y={atrSettings.y}
        openLeft={!!atrSettings.openLeft}
        config={atrTrailCfg}
        onUpdate={(patch) => setAtrTrailCfg(prev => ({ ...(prev || {}), ...patch }))}
        onHide={() => setShowATRStop(false)}
        onClose={() => setAtrSettings(v => ({ ...v, show: false }))}
      />
      <ProtractionSettingsMenu
        show={protSettings.show}
        x={protSettings.x}
        y={protSettings.y}
        openLeft={!!protSettings.openLeft}
        enabled={showProtraction}
        onToggle={(v) => setShowProtraction(!!v)}
        config={protCfg}
        onUpdate={(patch) => setProtCfg(prev => ({ ...(prev || {}), ...patch }))}
        onClose={() => setProtSettings(v => ({ ...v, show: false }))}
      />
      <ChurnZonesSettingsMenu
        show={churnSettings.show}
        x={churnSettings.x}
        y={churnSettings.y}
        openLeft={!!churnSettings.openLeft}
        enabled={showChurnZones}
        onToggle={(v) => setShowChurnZones(!!v)}
        config={churnCfg}
        onUpdate={(patch) => setChurnCfg(prev => ({ ...(prev || {}), ...patch }))}
        onClose={() => setChurnSettings(v => ({ ...v, show: false }))}
      />
      <MovingAverageSettingsMenu
        show={maSettings.show}
        x={maSettings.x}
        y={maSettings.y}
        openLeft={!!maSettings.openLeft}
        config={maConfigs.find(c => c.id === maSettings.id) || maConfigs[0]}
        onUpdate={(patch) => updateMa(maSettings.id || maConfigs[0].id, patch)}
        onClose={() => setMaSettings(v => ({ ...v, show: false }))}
      />
      <CVDSettingsMenu
        show={cvdSettings.show}
        x={cvdSettings.x}
        y={cvdSettings.y}
        openLeft={!!cvdSettings.openLeft}
        config={cvdConfig}
        onUpdate={(patch) => setCvdConfig(prev => ({ ...CVD_DEFAULTS, ...(prev || {}), ...patch }))}
        onHide={() => setShowCvdSubgraph(false)}
        onClose={() => setCvdSettings(v => ({ ...v, show: false }))}
      />
      <DVPSettingsMenu
        show={dvpSettings.show}
        x={dvpSettings.x}
        y={dvpSettings.y}
        openLeft={!!dvpSettings.openLeft}
        config={dvpConfig}
        onUpdate={(patch) => setDvpConfig(prev => ({ ...DVP_DEFAULTS, ...(prev || {}), ...patch }))}
        onHide={() => setShowDvp(false)}
        onClose={() => setDvpSettings(v => ({ ...v, show: false }))}
      />
      <ChartMenu show={heatSettings.show} anchor={heatSettings} openLeft={!!heatSettings.openLeft}
        onClose={() => setHeatSettings(v => ({ ...v, show: false }))} stopMouseDown
        width={216} title="Heatmap" closeButton draggable>
        <MenuSection>
          {heatSrc.hl && (<MenuItem onClick={() => setShowBookHeat(v => !v)}>
            {showBookHeat ? '✓' : '○'} Hyperliquid <span style={{ color: '#6091ff' }}>■</span>
          </MenuItem>)}
          {heatSrc.cb && (<MenuItem onClick={() => setShowCbHeat(v => !v)}>
            {showCbHeat ? '✓' : '○'} Coinbase <span style={{ color: '#ffb347' }}>■</span>
          </MenuItem>)}
        </MenuSection>
        <div style={{ padding: '6px 10px 2px' }}>
              {/* Sensitivity — the fader owns this block entirely now. The old
                  markup (its own SENSITIVITY caption, the .fp-heat-range
                  absolutely-positioned track + gradient fill, and the two
                  floating percent labels) was left behind when the control was
                  swapped and rendered UNDERNEATH it: overlapping tracks, two
                  captions, numbers on top of numbers. All of it removed — the
                  component draws its own groove, fill, ticks and readouts. */}
              {(() => {
                // Labels map slider fractions through the SAME percentile
                // window the display uses (heatNormRef), so the readout
                // matches where the cut actually lands — the old 0..max
                // mapping would lie about it now.
                const hn = heatNormRef.current
                const szAt = (f) => Math.expm1(hn.lo + f * (hn.span || 20))
                const fmt = (v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v >= 10 ? Math.round(v) : v.toFixed(1)
                return (
                  <div style={{ padding: '2px 0 2px' }}>
                    <RangeSliderControl
                      lo={heatLo} hi={heatHi}
                      onChange={(nLo, nHi) => setHeatRange(nLo, nHi)}
                      min={0} max={1} step={0.005}
                      label="Sensitivity"
                      colorLo="#4a7fc9" colorHi="#ff2d1a"
                      width={196}
                    />
                    <div style={{ marginTop: 8 }}>
                      <RangeSliderControl
                        single
                        lo={0.3} hi={heatGamma}
                        onChange={(_, g) => setHeatGamma(Math.round(g * 100) / 100)}
                        min={0.3} max={2.5} step={0.05}
                        label="Gamma"
                        format={(g) => g.toFixed(2)}
                        colorLo="#4a7fc9" colorHi="#ff2d1a"
                        width={196}
                      />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 8, color: '#9aa8cc', padding: '2px 2px 0' }}>
                      <span>{fmt(szAt(heatLo))}</span>
                      <span style={{ color: '#e8eeff' }}>{fmt(szAt(heatHi))}</span>
                    </div>
                    {/* Heatmap ramp stops. The map is one continuous thermal
                        palette now — the book's two sides read from its shape,
                        not from hue — so the editable colors are the LEVELS of
                        that ramp, low -> high, replacing the old bid/ask pair. */}
                    {/* Cheddar's own ColorPicker, not the native input — the
                        native popup is white, unstylable, and theme-deaf. The
                        picker renders its own swatch trigger and portals its
                        panel to body, so it survives this popover's overflow
                        and outside-click handling (it stops propagation
                        itself, same as the native inputs it replaces). */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, font: "10px 'JetBrains Mono', monospace", color: '#8892aa' }}>
                      {heatRamp.map((hex, gi) => (
                        <ColorPicker key={gi} value={hex} size={14}
                          title={HEAT_STOP_LABELS[gi]}
                          onChange={(v) => setHeatRamp(prev => prev.map((p, j) => j === gi ? v : p))} />
                      ))}
                      <span style={{ marginLeft: 2 }}>LOW → HIGH</span>
                    </div>
                  </div>
                )
              })()}
        </div>
        <MenuSection>
          <MenuItem onClick={() => setShowHeatValues(v => !v)}>
            {showHeatValues ? '✓' : '○'} Show values
          </MenuItem>
          <MenuRow split label="Extend fwd"
            title="HL flow: seconds a band persists in the one-column strip (0 = off). CB projection: depth in columns. One knob, two units — by design for now">
            <MenuNumber value={heatExtend} min={0} max={40} width={44}
              onCommit={(n) => setHeatExtend(Math.max(0, Math.min(40, n)))} />
          </MenuRow>
        </MenuSection>
      </ChartMenu>
      <ArdSettingsMenu
        show={ardSettings.show} x={ardSettings.x} y={ardSettings.y} openLeft={ardSettings.openLeft}
        config={ardConfig}
        onUpdate={setArdConfig}
        onClose={() => setArdSettings(s => ({ ...s, show: false }))} />
    </div>
  )
}

// memo: the payoff for stabilising `data` (useChartData's return is now
// useMemo'd), `onHistorySelect` (useCallback) and `secondChart` (useMemo) in
// App. Without memo an unmemoized child re-renders whenever its parent does,
// no matter how stable the props are — App was re-rendering ~30x/s and each
// chart render costs ~25ms of React work, which was ~75% of the main thread
// and held the whole page at 18-27fps.
//
// Default shallow prop compare is correct here: every prop is either a
// primitive, a useState setter, a useRef object, a useCallback, or one of the
// three now-memoized values. Anything that genuinely changes still re-renders
// the chart — memo only skips renders where every prop is identical.
//
// The render-nudge inside the component is unaffected in intent: it exists so
// the chart's OWN toggles and indicator flags cause a paint, and those are
// local state, which still triggers a render and therefore still nudges.
export default memo(FootprintChart)
